// ZeroRiskPay Dashboard v2.0 - 2026-04-08
// ZeroRiskPay Dashboard v1.0
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Chart as ChartJS, ArcElement, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { G, curSym, fmt, fmtSymbol, fmtCode, fmtK, _initCurrency, getAppTz, fmtDay, fmtDayTime } from './theme';

ChartJS.register(ArcElement, LineElement, PointElement, LinearScale, CategoryScale, Filler, ChartTooltip, ChartLegend);
import { gatewaysApi, paymentsApi, subscriptionsApi, batchApi, batchHistoryApi, shopsApi, webhooksApi, pixelsApi, templatesApi, clubApi, cleanupApi, beneficiosApi, trackingApi, emailProviderApi, customersApi } from './api';
// Sección "Salud de pasarelas" — vive fuera de App.js. Se monta aquí para que
// el botón de la tabla de Pasarelas (goTo('gateway-health')) tenga destino.
import GatewayHealth from './sections/GatewayHealth';
import LiveFunnel from './sections/LiveFunnel';
import SmsTemplates from './sections/SmsTemplates';
import SmsCampaigns from './sections/SmsCampaigns';
import SmsHistory from './sections/SmsHistory';


// G, curSym, fmt* y helpers de zona horaria viven ahora en ./theme.js
// (los necesitan también las secciones nuevas: Live Funnel, Gateway Health, SMS).

// Símbolo de moneda por código ISO. Las tablas del panel admin mostraban "€"
// hardcodeado aunque el tenant cobre en otra divisa (p.ej. Carlos en GBP) —
// engañoso. 2026-06-23.

// ─────────────────────────────────────────────────────────────────────────────
// ZONA HORARIA DEL PANEL (por tenant) — 2026-06-25.
// El panel mostraba TODO en America/New_York (hardcodeado, resto del código de
// origen US). Ahora cada tenant elige su zona en Settings → settings.timezone,
// y todas las fechas/KPIs del panel la respetan vía esta variable APP_TZ.
// Se lee de localStorage al cargar (instantáneo, sin parpadeo) y se refresca
// desde el backend al montar la app. Al guardar una zona nueva se recarga la
// página para que todas las fechas se repinten (mismo patrón que la divisa).
// NOTA: desde 2026-09-02 Beneficios también usa APP_TZ (el backend bucketiza
// por settings.timezone del tenant, la misma fuente que sincroniza APP_TZ).
let APP_TZ = (() => { try { return localStorage.getItem('app_tz') || 'America/New_York'; } catch { return 'America/New_York'; } })();
function setAppTz(tz) {
  if (!tz || typeof tz !== 'string') return;
  APP_TZ = tz;
  try { localStorage.setItem('app_tz', tz); } catch {}
}

// Etiqueta corta (bandera + ciudad) de la zona horaria activa del panel.
// Antes se mostraba 🗽 "NY" fijo en el reloj y sufijos de hora aunque el tenant
// configurara otra zona (Madrid, Londres…). Ahora todo refleja settings.timezone.
const TZ_META = {
  'Europe/Madrid': { flag: '🇪🇸', city: 'Madrid' },
  'Europe/London': { flag: '🇬🇧', city: 'Londres' },
  'Europe/Paris': { flag: '🇫🇷', city: 'París' },
  'Europe/Berlin': { flag: '🇩🇪', city: 'Berlín' },
  'Europe/Rome': { flag: '🇮🇹', city: 'Roma' },
  'Europe/Lisbon': { flag: '🇵🇹', city: 'Lisboa' },
  'America/New_York': { flag: '🇺🇸', city: 'NY' },
  'America/Chicago': { flag: '🇺🇸', city: 'Chicago' },
  'America/Denver': { flag: '🇺🇸', city: 'Denver' },
  'America/Los_Angeles': { flag: '🇺🇸', city: 'LA' },
  'America/Mexico_City': { flag: '🇲🇽', city: 'CDMX' },
  'America/Sao_Paulo': { flag: '🇧🇷', city: 'São Paulo' },
  'America/Argentina/Buenos_Aires': { flag: '🇦🇷', city: 'Buenos Aires' },
  'Asia/Tokyo': { flag: '🇯🇵', city: 'Tokio' },
  'Asia/Shanghai': { flag: '🇨🇳', city: 'Shanghai' },
  'Australia/Sydney': { flag: '🇦🇺', city: 'Sídney' },
  'UTC': { flag: '🌐', city: 'UTC' },
};
function tzMeta(tz) {
  const key = tz || APP_TZ;
  return TZ_META[key] || { flag: '🌐', city: String(key || '').split('/').pop().replace(/_/g, ' ') };
}

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY CURRENCY — operator-facing toggle (EUR default, USD for US-based
// accounting). Display-only: all payments still charge and persist in EUR
// (and native card currencies). Only what the dashboard SHOWS changes.
//
// The store is a plain module-level object (not Context) because:
//   1. 40+ components across this file display money — passing a hook
//      everywhere would touch hundreds of lines for no benefit.
//   2. Changing the currency reloads the page (intentional UX: all numbers
//      flip at once, no mid-render flicker), so we don't need reactivity.
//   3. Rates are fetched once at mount and cached here for the session.
// ─────────────────────────────────────────────────────────────────────────────

const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --gold: ${G.gold};
  --gold-light: ${G.goldLight};
  --gold-bright: ${G.goldBright};
}

body {
  background: ${G.black};
  color: ${G.white};
  font-family: 'Outfit', sans-serif;
  overflow-x: hidden;
  color-scheme: dark;
}

::-webkit-scrollbar { width: 3px; }
::-webkit-scrollbar-track { background: ${G.black}; }
::-webkit-scrollbar-thumb { background: ${G.borderGold}; border-radius: 2px; }

.mono { font-family: 'DM Mono', monospace; }
.serif { font-family: 'Cormorant Garamond', serif; }

/* -- NOISE TEXTURE OVERLAY -- */
body::before {
  content: '';
  position: fixed;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none;
  z-index: 9999;
  opacity: 0.4;
}

/* -- GOLD SHIMMER ANIMATION -- */
@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes goldPulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 20px ${G.goldGlow}; }
  50% { opacity: 0.8; box-shadow: 0 0 40px ${G.gold}50; }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes slideIn {
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.8); }
}
@keyframes borderFlow {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* -- LAYOUT -- */
.layout { display: flex; height: 100vh; overflow: hidden; }

/* -- SIDEBAR -- */
.sidebar {
  width: 240px;
  min-width: 240px;
  background: ${G.card};
  border-right: 1px solid ${G.border};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
.sidebar::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, ${G.gold}, transparent);
  animation: borderFlow 3s ease-in-out infinite;
}
.sidebar-top {
  padding: 28px 20px 24px;
  border-bottom: 1px solid ${G.border};
}
.logo-wrap { display: flex; align-items: center; gap: 12px; }
.logo-icon {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${G.gold}, ${G.goldBright});
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px; font-weight: 700; color: #000;
  box-shadow: 0 4px 20px ${G.goldGlow};
  animation: goldPulse 4s ease-in-out infinite;
}
.logo-name {
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px; font-weight: 600;
  background: linear-gradient(135deg, ${G.goldLight}, ${G.goldBright});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.02em;
}
.logo-sub { font-size: 10px; color: ${G.muted}; letter-spacing: 0.15em; text-transform: uppercase; margin-top: 1px; }

.nav { padding: 16px 12px; flex: 1; overflow-y: auto; scrollbar-width: thin; scrollbar-color: ${G.border} transparent; }
.nav-section-label {
  font-size: 9px; color: ${G.muted}; letter-spacing: 0.18em;
  text-transform: uppercase; padding: 0 10px; margin: 16px 0 8px;
  font-family: 'DM Mono', monospace;
}
.nav-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 10px;
  cursor: pointer; font-size: 13px; font-weight: 500;
  color: ${G.muted}; transition: all 0.2s;
  margin-bottom: 2px; border: none; background: none;
  width: 100%; text-align: left; font-family: 'Outfit', sans-serif;
  position: relative;
}
.nav-item:hover { background: ${G.cardHover}; color: ${G.dim}; }
.nav-item.active {
  background: ${G.goldDim};
  color: ${G.goldLight};
  border: 1px solid ${G.borderGold};
}
.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0; top: 50%; transform: translateY(-50%);
  width: 3px; height: 60%;
  background: linear-gradient(180deg, ${G.gold}, ${G.goldBright});
  border-radius: 0 2px 2px 0;
}
.nav-item.locked { opacity: 0.45; }
.nav-item.locked:hover { opacity: 0.65; background: ${G.cardHover}; }
.nav-lock { margin-left: auto; font-size: 10px; opacity: 0.6; }
.nav-icon { font-size: 15px; width: 20px; text-align: center; opacity: 0.8; }
.nav-badge {
  margin-left: auto;
  background: linear-gradient(135deg, ${G.gold}, ${G.goldBright});
  color: #000; font-size: 9px; font-weight: 700;
  padding: 2px 7px; border-radius: 20px;
  font-family: 'DM Mono', monospace;
}

.sidebar-user {
  padding: 16px 20px;
  border-top: 1px solid ${G.border};
  display: flex; align-items: center; gap: 10px;
}
.user-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, ${G.gold}, ${G.goldBright});
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: #000;
  font-family: 'Cormorant Garamond', serif;
  box-shadow: 0 2px 12px ${G.goldGlow};
  flex-shrink: 0;
}
.user-name { font-size: 12px; font-weight: 600; color: ${G.white}; }
.user-role { font-size: 10px; color: ${G.muted}; margin-top: 1px; }
.logout-btn {
  margin-left: auto; background: none; border: none;
  color: ${G.muted}; cursor: pointer; font-size: 16px;
  padding: 4px; border-radius: 6px; transition: all 0.2s;
  display: flex; align-items: center;
}
.logout-btn:hover { color: ${G.red}; background: ${G.redDim}; }

/* -- MAIN -- */
.main { flex: 1; overflow-y: auto; display: flex; flex-direction: column; background: ${G.black}; }

.topbar {
  padding: 18px 32px;
  border-bottom: 1px solid ${G.border};
  display: flex; align-items: center; justify-content: space-between;
  position: sticky; top: 0; background: ${G.black}ee; z-index: 10;
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
}
.topbar-left { display: flex; align-items: center; gap: 14px; min-width: 0; flex: 1; }
.page-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 24px; font-weight: 600;
  background: linear-gradient(135deg, ${G.white}, ${G.dim});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.breadcrumb { font-size: 11px; color: ${G.muted}; margin-top: 2px; letter-spacing: 0.05em; }
.topbar-right { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }

/* NYClock — compact badge in the topbar */
.ny-clock {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  padding: 6px 10px;
  background: ${G.card};
  color: ${G.gold};
  border-radius: 8px;
  border: 1px solid ${G.border};
  line-height: 1;
  white-space: nowrap;
}
.ny-clock-icon { font-size: 12px; line-height: 1; }
.ny-clock-time { font-weight: 600; letter-spacing: 0.3px; }
.ny-clock-label { color: ${G.muted}; font-size: 10px; letter-spacing: 0.4px; }

/* Live-visitor + system-status badges in the topbar */
.topbar-badge {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; color: ${G.muted};
  font-family: 'DM Mono', monospace;
  padding: 6px 14px;
  background: ${G.card}; border-radius: 8px;
  border: 1px solid ${G.border};
  white-space: nowrap;
}

/* -- BUTTONS -- */
.btn {
  padding: 9px 20px; border-radius: 9px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  border: none; transition: all 0.25s;
  font-family: 'Outfit', sans-serif; letter-spacing: 0.03em;
  position: relative; overflow: hidden;
}
.btn-gold {
  background: linear-gradient(135deg, ${G.gold}, ${G.goldBright});
  color: #000; box-shadow: 0 4px 16px ${G.goldGlow};
}
.btn-gold:hover { box-shadow: 0 8px 28px ${G.gold}50; transform: translateY(-1px); }
.btn-gold:active { transform: translateY(0); box-shadow: 0 2px 8px ${G.goldGlow}; }
.btn-ghost {
  background: transparent; color: ${G.muted};
  border: 1px solid ${G.border};
}
.btn-ghost:hover { background: ${G.cardHover}; color: ${G.white}; border-color: ${G.borderGold}; }
.btn-danger {
  background: ${G.redDim}; color: ${G.red};
  border: 1px solid ${G.red}30;
}
.btn-danger:hover { background: ${G.red}25; }
.btn-sm { padding: 5px 12px; font-size: 11px; }
/* Un botón deshabilitado tiene que PARECERLO: en acciones de dinero (enviar una
   campaña, detener un lote) el operador debe ver de un vistazo que está
   bloqueado. Sin pointer-events:none para que el title siga explicando por qué. */
.btn:disabled {
  opacity: 0.42; cursor: not-allowed; filter: saturate(0.5);
  transform: none !important; box-shadow: none !important;
}

/* -- CONTENT -- */
.content { padding: 28px 32px; flex: 1; animation: fadeUp 0.3s ease; }
.site-down-banner { margin: 20px 32px 0; }
@media (max-width: 768px) { .site-down-banner { margin: 12px 12px 0; } }

/* -- KPI CARDS -- */
.kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 28px; }
.kpi-card {
  background: ${G.card};
  border: 1px solid ${G.border};
  border-radius: 14px; padding: 22px;
  position: relative; overflow: hidden;
  transition: all 0.3s;
  cursor: default;
}
.kpi-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, ${G.gold}60, transparent);
  opacity: 0; transition: opacity 0.3s;
}
.kpi-card:hover { border-color: ${G.borderGold}; transform: translateY(-2px); box-shadow: 0 8px 32px ${G.goldGlow}15; }
.kpi-card:hover::before { opacity: 1; }
.kpi-label {
  font-size: 10px; color: ${G.muted}; text-transform: uppercase;
  letter-spacing: 0.15em; margin-bottom: 12px;
  font-family: 'DM Mono', monospace;
}
.kpi-value {
  font-size: 30px; font-weight: 300;
  font-family: 'Cormorant Garamond', serif;
  background: linear-gradient(135deg, ${G.white}, ${G.goldLight});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
}
.kpi-delta { font-size: 11px; margin-top: 8px; display: flex; align-items: center; gap: 4px; font-family: 'DM Mono', monospace; }
.delta-up { color: ${G.green}; }
.delta-down { color: ${G.red}; }
.kpi-icon {
  position: absolute; right: 18px; top: 18px;
  font-size: 28px; opacity: 0.06;
}

/* -- CARDS -- */
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 28px; }
.grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 28px; }

.card {
  background: ${G.card};
  border: 1px solid ${G.border};
  border-radius: 14px; overflow: hidden;
  transition: border-color 0.3s;
}
.card:hover { border-color: ${G.borderGold}; }
.card-head {
  padding: 16px 22px;
  border-bottom: 1px solid ${G.border};
  display: flex; align-items: center; justify-content: space-between;
}
.card-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 16px; font-weight: 600; color: ${G.white};
  letter-spacing: 0.02em;
}
.card-body { padding: 22px; }

/* -- TABLES -- */
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th {
  padding: 10px 18px; text-align: left;
  font-size: 9px; font-weight: 500; color: ${G.muted};
  text-transform: uppercase; letter-spacing: 0.15em;
  border-bottom: 1px solid ${G.border};
  font-family: 'DM Mono', monospace;
}
td { padding: 14px 18px; border-bottom: 1px solid ${G.border}30; vertical-align: middle; }
tr:last-child td { border-bottom: none; }
tr { transition: background 0.15s; }
tr:hover td { background: ${G.cardHover}; }

/* -- BADGES -- */
.badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; border-radius: 20px;
  font-size: 10px; font-weight: 600;
  font-family: 'DM Mono', monospace; letter-spacing: 0.05em;
  white-space: nowrap;
}
.badge-gold { background: ${G.goldDim}; color: ${G.goldLight}; border: 1px solid ${G.borderGold}; }
.badge-green { background: ${G.greenDim}; color: ${G.green}; border: 1px solid ${G.green}25; }
.badge-red { background: ${G.redDim}; color: ${G.red}; border: 1px solid ${G.red}25; }
.badge-blue { background: ${G.blueDim}; color: ${G.blue}; border: 1px solid ${G.blue}25; }
.badge-orange { background: ${G.orangeDim}; color: ${G.orange}; border: 1px solid ${G.orange}25; }
.badge-gray { background: ${G.border}; color: ${G.muted}; border: 1px solid ${G.border}; }
.tag {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 5px;
  font-size: 10px; font-family: 'DM Mono', monospace;
  background: ${G.border}; color: ${G.dim};
}

/* -- PROGRESS -- */
.progress { height: 4px; background: ${G.border}; border-radius: 2px; overflow: hidden; }
.progress-fill {
  height: 100%; border-radius: 2px;
  background: linear-gradient(90deg, ${G.gold}, ${G.goldBright});
  transition: width 0.6s ease;
}

/* -- MINI CHART -- */
.mini-bars { display: flex; align-items: flex-end; gap: 3px; height: 120px; }
.mini-bar {
  flex: 1; border-radius: 3px 3px 0 0;
  background: ${G.gold}30;
  transition: all 0.4s ease; min-width: 4px;
}
.mini-bar:last-child { background: linear-gradient(180deg, ${G.goldBright}, ${G.gold}); }
.mini-bar:hover { background: ${G.gold}80; }

/* -- FORMS -- */
.form-group { margin-bottom: 16px; }
.form-label {
  font-size: 10px; color: ${G.muted}; margin-bottom: 7px;
  display: block; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.12em;
  font-family: 'DM Mono', monospace;
}
.form-input {
  width: 100%;
  background: ${G.black};
  border: 1px solid ${G.border};
  border-radius: 8px; padding: 10px 14px;
  font-size: 13px; color: ${G.white};
  font-family: 'Outfit', sans-serif; outline: none;
  transition: all 0.2s;
}
.form-input:focus { border-color: ${G.gold}60; box-shadow: 0 0 0 3px ${G.goldDim}; }
.form-input::placeholder { color: ${G.muted}; }
.form-input option { background: ${G.card}; color: ${G.white}; }
.form-input:-webkit-autofill,
.form-input:-webkit-autofill:hover,
.form-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px ${G.black} inset !important;
  -webkit-text-fill-color: ${G.white} !important;
  caret-color: ${G.white};
}
select.form-input { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B6580' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

/* -- SEARCH & FILTERS -- */
.filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 20px; }
.search {
  background: ${G.card}; border: 1px solid ${G.border};
  border-radius: 10px; padding: 10px 16px;
  font-size: 13px; color: ${G.white};
  font-family: 'Outfit', sans-serif; outline: none; width: 260px;
  transition: all 0.25s;
}
.search:focus { border-color: ${G.gold}60; box-shadow: 0 0 0 3px ${G.goldDim}; }
.search::placeholder { color: ${G.muted}; }
.pill-group {
  display: flex; background: ${G.card};
  border: 1px solid ${G.border}; border-radius: 8px; overflow: hidden;
}
.pill {
  padding: 8px 16px; font-size: 11px; font-weight: 600;
  cursor: pointer; border: none; background: none;
  color: ${G.muted}; font-family: 'Outfit', sans-serif;
  transition: all 0.2s; letter-spacing: 0.03em;
}
.pill:hover { color: ${G.dim}; background: ${G.cardHover}; }
.pill.active { background: ${G.goldDim}; color: ${G.goldLight}; }

/* -- MODAL -- */
.modal-bg {
  position: fixed; inset: 0; background: #00000090;
  display: flex; align-items: center; justify-content: center;
  z-index: 100; backdrop-filter: blur(6px);
}
.modal {
  background: ${G.card};
  border: 1px solid ${G.borderGold};
  border-radius: 16px; padding: 32px;
  width: 560px; max-width: 95vw; max-height: 85vh; overflow-y: auto;
  animation: fadeUp 0.25s ease;
  box-shadow: 0 24px 80px #00000080, 0 0 40px ${G.gold}10;
}
.modal-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px; font-weight: 600; margin-bottom: 24px;
  background: linear-gradient(135deg, ${G.white}, ${G.goldLight});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; }

/* -- TOAST -- */
.toast {
  position: fixed; bottom: 28px; right: 28px;
  background: ${G.card};
  border: 1px solid ${G.borderGold};
  border-radius: 12px; padding: 14px 20px;
  font-size: 13px; z-index: 200;
  display: flex; align-items: center; gap: 10px;
  animation: fadeUp 0.3s ease;
  box-shadow: 0 12px 40px #00000060, 0 0 0 1px ${G.gold}15;
}
.toast-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

/* -- SECTION TABS -- */
.section-tabs {
  display: flex; gap: 2px;
  background: ${G.card}; border: 1px solid ${G.border};
  border-radius: 10px; padding: 4px; margin-bottom: 22px;
  width: fit-content;
}
.section-tab {
  padding: 7px 18px; border-radius: 7px;
  font-size: 12px; font-weight: 500; cursor: pointer;
  border: none; background: none; color: ${G.muted};
  font-family: 'Outfit', sans-serif; transition: all 0.15s;
  letter-spacing: 0.03em;
}
.section-tab.active { background: ${G.goldDim}; color: ${G.goldLight}; }

/* -- EMPTY STATE -- */
.empty {
  text-align: center; padding: 48px 20px;
  color: ${G.muted}; font-size: 13px; line-height: 1.8;
}
.empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.4; }

/* -- STAT LINE -- */
.stat-line {
  display: flex; justify-content: space-between; align-items: center;
  padding: 11px 0; border-bottom: 1px solid ${G.border}20;
}
.stat-line:last-child { border-bottom: none; }

/* -- DOT PULSE -- */
.dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.pulse { animation: pulse 2s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

/* -- GATEWAY ROW -- */
.gateway-psp-icon {
  width: 36px; height: 36px; border-radius: 8px;
  background: ${G.goldDim}; border: 1px solid ${G.borderGold};
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; color: ${G.goldLight};
  font-family: 'Cormorant Garamond', serif;
}

/* -- CONNECT BANNER -- */
.connect-banner {
  background: linear-gradient(135deg, ${G.goldDim}, ${G.border}80);
  border: 1px solid ${G.borderGold};
  border-radius: 14px; padding: 22px 28px;
  margin-bottom: 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
}

/* -- HAMBURGER BUTTON -- */
.hamburger {
  display: none;
  flex-direction: column; gap: 5px;
  cursor: pointer; background: none; border: none;
  padding: 6px; border-radius: 8px;
  transition: background 0.2s;
}
.hamburger:hover { background: ${G.cardHover}; }
.hamburger span {
  display: block; width: 22px; height: 2px;
  background: ${G.dim}; border-radius: 2px;
  transition: all 0.3s;
}

/* -- OVERLAY -- */
.sidebar-overlay {
  display: none;
  position: fixed; inset: 0;
  background: #00000080;
  z-index: 40;
  backdrop-filter: blur(2px);
}

/* -- PSP / PLATFORM GRIDS -- */
.psp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
.platform-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
.psp-logo-img { width: 52px; height: 34px; object-fit: contain; filter: brightness(1.05); }
.platform-logo-img { width: 44px; height: 28px; object-fit: contain; }
.topbar-status { display: flex; align-items: center; gap: 8px; }

/* ── Trials próximos 5 días card — responsive layout ─────────────────
   Desktop (>700px): 2-column grid span (cabe los 5 días en fila ancha).
   Mobile  (<700px): 1 column span + 5 días en grid 5×1 con cells flex.
*/
.trials-upcoming-card { grid-column: span 2; }
@media (max-width: 700px) {
  .trials-upcoming-card { grid-column: span 1; padding: 14px !important; }
  .trials-upcoming-card .trials-day-grid { gap: 4px; }
  .trials-upcoming-card .trials-day-grid > div { padding: 6px 1px !important; }
}

/* ── Beneficios: mobile responsive tweaks ───────────────────────────── */
.beneficios-summary-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); margin-bottom: 24px; }
.beneficios-pl-table { width: 100%; font-size: 13px; border-collapse: collapse; }
.beneficios-tabs { display: flex; gap: 8px; overflow-x: auto; -webkit-overflow-scrolling: touch; max-width: 100%; }
.beneficios-tabs button { flex-shrink: 0; white-space: nowrap; }
.beneficios-filters { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 16px; }
.rent-scenarios { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); margin-bottom: 16px; }
.rent-kpis { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-bottom: 16px; }
.rent-data-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
.rent-window-selector { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rent-cpa-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
.cpa-shop-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
.campaigns-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }

@media (max-width: 768px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .grid-2 { grid-template-columns: 1fr; }
  .grid-3 { grid-template-columns: 1fr; }
  /* Beneficios mobile overrides */
  .beneficios-summary-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .rent-scenarios { grid-template-columns: 1fr; }
  .rent-kpis { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .rent-cpa-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .cpa-shop-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .rent-data-grid { grid-template-columns: 1fr 1fr; }
  .beneficios-filters { flex-direction: column; align-items: stretch; }
  .beneficios-filters > * { width: 100%; }
  .beneficios-pl-table { font-size: 11px; }
  .beneficios-pl-table th, .beneficios-pl-table td { padding: 6px 4px; }

  .hamburger { display: flex; }

  .sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    box-shadow: 4px 0 40px #00000080;
  }
  .sidebar.open { transform: translateX(0); }
  .sidebar-overlay.open { display: block; }

  .main { width: 100%; }

  .topbar { padding: 10px 12px; gap: 8px; }
  .page-title { font-size: 16px; }
  .breadcrumb { display: none; }
  .content { padding: 16px 12px; }
  .topbar-status { display: none; }
  .topbar-right { gap: 6px; }
  /* Compact NY clock on mobile — drop the "NY" label, keep icon + time */
  .ny-clock { padding: 5px 8px; font-size: 10px; gap: 4px; }
  .ny-clock-icon { font-size: 11px; }
  .ny-clock-label { display: none; }
  /* Visitor counter: shrink padding + font on mobile but keep the full
     "X visitantes en línea" label — operator wants to know at a glance
     how many shoppers are on their checkout right now. */
  .topbar-badge { padding: 5px 8px; font-size: 10px; gap: 5px; }
  /* On very narrow screens (<420px) the topbar already flex-wraps to a
     second row, so the label still fits naturally. */

  .kpi-value { font-size: 22px; }
  .kpi-card { padding: 14px; }

  .filters { flex-direction: column; align-items: flex-start; gap: 8px; }
  .search { width: 100% !important; min-width: unset !important; }
  .pill-group { overflow-x: auto; -webkit-overflow-scrolling: touch; max-width: 100%; flex-shrink: 0; }
  .section-tabs { overflow-x: auto; -webkit-overflow-scrolling: touch; max-width: 100%; }

  table { font-size: 12px; }
  th { padding: 8px 10px; }
  td { padding: 10px 10px; }

  .modal { padding: 20px 16px; }
  .modal-actions { flex-direction: column; gap: 8px; }
  .modal-actions .btn { width: 100%; text-align: center; justify-content: center; }
  .form-row { grid-template-columns: 1fr; }

  .connect-banner { flex-direction: column; align-items: flex-start; gap: 12px; }
  .connect-banner button { width: 100%; text-align: center; }

  .psp-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .psp-grid button { padding: 12px 6px !important; }
  .psp-logo-img { width: 40px; height: 26px; }
  .platform-grid { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .platform-logo-img { width: 36px; height: 22px; }

  .toast { bottom: 16px; right: 12px; left: 12px; max-width: calc(100vw - 24px); }
}

@media (max-width: 480px) {
  .kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
  .kpi-value { font-size: 20px; }
  .btn { padding: 7px 14px; font-size: 11px; }
  .content { padding: 12px 10px; }
  .psp-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; }
  .psp-grid button { padding: 10px 4px !important; }
  .psp-logo-img { width: 36px; height: 24px; }
  .platform-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; }
  .platform-grid button { padding: 8px 4px !important; }
  .platform-logo-img { width: 32px; height: 20px; }
  /* Beneficios very-narrow overrides */
  .beneficios-summary-grid { grid-template-columns: 1fr 1fr; gap: 6px; }
  .rent-kpis { grid-template-columns: 1fr 1fr; gap: 6px; }
  .rent-cpa-grid { grid-template-columns: 1fr; }
  .rent-data-grid { grid-template-columns: 1fr 1fr; }
}

/* ── Dashboard feature panels (gateway / products / revenue-over-time) ── */
.panel-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.panel-kpis > * { min-width: 0; }
/* grid items default to min-width:auto, so nowrap product titles widened the column past the screen */
.dash-pair > * { min-width: 0; }

@media (max-width: 900px) {
  .panel-kpis { grid-template-columns: repeat(2, 1fr); gap: 10px; }
}
@media (max-width: 768px) {
  .feature-panel { padding: 14px !important; }
  .gw-panel-body { grid-template-columns: 1fr !important; }
  .unit-row { gap: 9px !important; padding: 9px 10px !important; }
  .stat-card { padding: 11px 13px !important; gap: 10px !important; }
  .stat-val { font-size: 16px !important; }
  .stat-ic { width: 32px !important; height: 32px !important; font-size: 16px !important; }
  .complaint-row { gap: 8px !important; padding: 10px 11px !important; }
  .complaint-row > div:last-child { width: 100%; justify-content: flex-end; }
  .dash-pair { gap: 0; }
  .cr-title { font-size: 15px !important; }
  .cr-rate { padding: 6px 12px !important; }
  .cr-rate-val { font-size: 16px !important; }
}
@media (max-width: 480px) {
  .panel-kpis { gap: 8px; }
  .cr-ic { width: 34px !important; height: 34px !important; }
  .cr-head, .cr-head > div:first-child { gap: 10px !important; }
  .cr-rate { padding: 5px 10px !important; }
  .cr-rate-lbl { font-size: 8px !important; letter-spacing: 0.4px !important; }
  .cr-sub { font-size: 10.5px !important; }
  .po-ic { width: 34px !important; height: 34px !important; }
  .po-head, .po-head > div:first-child { gap: 10px !important; }
  .po-title { font-size: 15px !important; }
  .po-total { padding: 6px 12px !important; }
  .po-total-val { font-size: 17px !important; }
  .po-row { padding: 11px 12px !important; }
  .po-name { flex-basis: 100%; }
  .po-chip-inline { display: none !important; }
  .po-chip-stack { display: flex !important; }
  .po-paid { grid-template-columns: 1fr !important; }
  .po-days { grid-template-columns: repeat(3, 1fr) !important; }
  .po-day-amt { font-size: 15px !important; }
  .stat-ic { display: none !important; }
  .stat-lbl { white-space: normal !important; }
}
`;

// -- LOGIN SCREEN ------------------------------------------------------------

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email || !password) { setError('Completa todos los campos'); return; }
    setLoading(true); setError('');
    try {
      // 2026-06-05 — SaaS: use env-injected backend URL.
      // BACKEND is defined later in the file (hoisted, this runs after definition).
      const _saasBackend = (process.env.REACT_APP_API_URL || 'https://api.zeroriskpay.com').replace(/\/$/, '');
      const { data } = await axios.post(`${_saasBackend}/api/auth/login`, { email, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (e) {
      // 2026-06-11 — 423 Locked = tenant pausado. Inyectamos un user "fake"
      // con status='paused' para que el componente raíz pinte el overlay
      // de cuenta suspendida en lugar del dashboard.
      if (e.response?.status === 423 && e.response?.data?.paused) {
        const pausedUser = {
          id: 'paused',
          status: 'paused',
          name: e.response.data.userName || '',
          email: e.response.data.userEmail || email,
          role: 'user',
          plan: 'bronze',
        };
        onLogin(pausedUser);
        return;
      }
      setError(e.response?.data?.error || 'Error de conexión con el servidor');
    }
    setLoading(false);
  };

  const loginCss = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600&family=DM+Mono:wght@400&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${G.black}; }
    @keyframes floatUp { 0% { transform: translateY(0) scale(1); opacity: 0.3; } 100% { transform: translateY(-100vh) scale(0.5); opacity: 0; } }
    @keyframes shimmerText {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes goldPulse { 0%, 100% { box-shadow: 0 0 30px ${G.gold}40; } 50% { box-shadow: 0 0 60px ${G.gold}60; } }
    .particle {
      position: absolute; border-radius: 50%;
      background: ${G.gold}; animation: floatUp linear infinite;
    }
  `;

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: `${Math.random() * 4 + 2}px`,
    duration: `${Math.random() * 10 + 8}s`,
    delay: `${Math.random() * 10}s`,
    opacity: Math.random() * 0.4 + 0.1,
  }));

  return (
    <div style={{
      minHeight: '100vh', background: G.black,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Outfit, sans-serif', position: 'relative', overflow: 'hidden'
    }}>
      <style>{loginCss}</style>

      {/* Particles */}
      {particles.map(p => (
        <div key={p.id} className="particle" style={{
          left: p.left, width: p.size, height: p.size,
          animationDuration: p.duration, animationDelay: p.delay,
          opacity: p.opacity, bottom: '-10px',
        }} />
      ))}

      {/* Background glow */}
      <div style={{
        position: 'absolute', top: '30%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600, height: 600,
        background: `radial-gradient(circle, ${G.gold}08 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Card */}
      <div style={{
        background: G.card,
        border: `1px solid ${G.borderGold}`,
        borderRadius: 20, padding: '48px 44px',
        width: 420, maxWidth: '95vw',
        animation: 'fadeUp 0.5s ease',
        boxShadow: `0 32px 100px #00000080, 0 0 0 1px ${G.gold}10`,
        position: 'relative', zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {/* 2026-06-07 — dark-bg variant: transparent background +
              white wordmark + original gradient icon. Lives on the
              login card which sits on the dashboard's dark surface. */}
          <img src="/logo-horizontal-dark.png" alt="ZeroRiskPay" style={{ width: 220, height: 'auto', margin: '0 auto 8px', display: 'block', filter: 'drop-shadow(0 2px 12px rgba(75,159,232,0.3))' }} />
          <div style={{ fontSize: 11, color: G.muted, marginTop: 0, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'DM Mono, monospace' }}>
            Payment Orchestrator
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${G.gold}40, transparent)`, marginBottom: 32 }} />

        {/* Fields */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 10, color: G.muted, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'DM Mono, monospace' }}>Email</label>
          <input
            type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            style={{
              width: '100%', background: G.black,
              border: `1px solid ${G.border}`, borderRadius: 10,
              padding: '12px 16px', fontSize: 13, color: G.white,
              fontFamily: 'Outfit, sans-serif', outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = `${G.gold}60`}
            onBlur={e => e.target.style.borderColor = G.border}
            placeholder="ejemplo@tutienda.com"
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 10, color: G.muted, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'DM Mono, monospace' }}>Contraseña</label>
          <input
            type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            style={{
              width: '100%', background: G.black,
              border: `1px solid ${G.border}`, borderRadius: 10,
              padding: '12px 16px', fontSize: 13, color: G.white,
              fontFamily: 'Outfit, sans-serif', outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = `${G.gold}60`}
            onBlur={e => e.target.style.borderColor = G.border}
            placeholder="----------"
          />
        </div>

        {error && (
          <div style={{
            color: G.red, fontSize: 12, marginBottom: 16,
            textAlign: 'center', padding: '8px 12px',
            background: G.redDim, borderRadius: 8,
            border: `1px solid ${G.red}30`,
            fontFamily: 'DM Mono, monospace',
          }}>{error}</div>
        )}

        <button
          onClick={submit} disabled={loading}
          style={{
            width: '100%',
            background: loading ? G.border : `linear-gradient(135deg, ${G.gold}, ${G.goldBright})`,
            color: loading ? G.muted : '#000',
            border: 'none', borderRadius: 10,
            padding: '13px', fontSize: 13, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'Outfit, sans-serif',
            letterSpacing: '0.05em',
            boxShadow: loading ? 'none' : `0 6px 24px ${G.gold}40`,
            transition: 'all 0.2s',
          }}
        >
          {loading ? 'Verificando...' : 'Acceder al Dashboard'}
        </button>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace', letterSpacing: '0.1em' }}>
          ACCESO RESTRINGIDO · SOLO PERSONAL AUTORIZADO
        </div>
      </div>
    </div>
  );
}

// -- COMPONENTS ---------------------------------------------------------------

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  const colors = { success: G.green, error: G.red, info: G.blue, warning: G.gold };
  return (
    <div className="toast">
      <div className="toast-dot" style={{ background: colors[type] || G.gold }} />
      <span style={{ fontSize: 13 }}>{msg}</span>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    SUCCEEDED: ['badge-green', '● Pagado'],
    ACTIVE: ['badge-gold', '● Activo'],
    FAILED: ['badge-red', '● Error'],
    CANCELLED: ['badge-red', '● Cancelado'],
    PROCESSING: ['badge-blue', '● Procesando'],
    PENDING: ['badge-orange', '● Abandonado'],
    PAUSED: ['badge-gray', '⏸ Pausado'],
    CREATED: ['badge-gray', '● Creado'],
    INACTIVE: ['badge-gray', '○ Inactivo'],
  };
  const [cls, label] = map[status] || ['badge-gray', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

// ── LineChart ──────────────────────────────────────────────────────────────
// Professional SVG line chart for the "Actividad de pagos" card.
// Shows revenue per day by default, with a smooth line, gradient area fill,
// interactive hover points, grid lines, and nice Y-axis labels.
function LineChart({ chartData = [], metric = 'revenue', height = 240 }) {
  const [hover, setHover] = useState(null);
  const [mode, setMode] = useState(metric); // 'revenue' | 'ok'
  const containerRef = useRef(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 800);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Layout
  const padL = 44, padR = 16, padT = 18, padB = 30;
  const W = Math.max(320, width);
  const H = height;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // Guard against empty data
  const pts = chartData.length > 0 ? chartData : [{ date: '', ok: 0, fail: 0, total: 0, revenue: 0 }];
  const values = pts.map(d => (mode === 'revenue' ? (d.revenue || 0) : (d.ok || 0)));
  const maxV = Math.max(...values, mode === 'revenue' ? 10 : 1);
  // Nice max rounded up for Y-axis
  const niceMax = (() => {
    const pow = Math.pow(10, Math.floor(Math.log10(maxV || 1)));
    const normalized = maxV / pow;
    let nice;
    if (normalized <= 1) nice = 1;
    else if (normalized <= 2) nice = 2;
    else if (normalized <= 5) nice = 5;
    else nice = 10;
    return nice * pow;
  })();

  const xStep = pts.length > 1 ? plotW / (pts.length - 1) : plotW;
  const getX = (i) => padL + i * xStep;
  const getY = (v) => padT + plotH - (v / niceMax) * plotH;

  // Build smooth line path (Catmull-Rom spline → cubic bezier)
  const buildPath = () => {
    if (pts.length === 0) return '';
    if (pts.length === 1) {
      const x = getX(0), y = getY(values[0]);
      return `M ${x} ${y}`;
    }
    let d = `M ${getX(0)} ${getY(values[0])}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const x0 = getX(i - 1 < 0 ? i : i - 1);
      const y0 = getY(values[i - 1 < 0 ? i : i - 1]);
      const x1 = getX(i);
      const y1 = getY(values[i]);
      const x2 = getX(i + 1);
      const y2 = getY(values[i + 1]);
      const x3 = getX(i + 2 >= pts.length ? i + 1 : i + 2);
      const y3 = getY(values[i + 2 >= pts.length ? i + 1 : i + 2]);
      const cp1x = x1 + (x2 - x0) / 6;
      const cp1y = y1 + (y2 - y0) / 6;
      const cp2x = x2 - (x3 - x1) / 6;
      const cp2y = y2 - (y3 - y1) / 6;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    }
    return d;
  };

  const linePath = buildPath();
  const areaPath = `${linePath} L ${getX(pts.length - 1)} ${padT + plotH} L ${getX(0)} ${padT + plotH} Z`;

  // Y-axis ticks
  const yTicks = 4;
  const tickValues = [];
  for (let i = 0; i <= yTicks; i++) {
    tickValues.push((niceMax / yTicks) * i);
  }

  const fmtY = (v) => {
    if (mode === 'revenue') {
      return fmtK(v);
    }
    return Math.round(v);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-').map(Number);
      return `${d}/${m}`;
    } catch { return iso; }
  };

  // X-axis label density: show ~6 labels
  const labelEvery = Math.max(1, Math.ceil(pts.length / 6));

  // Hover handler on svg
  const handleMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (W / rect.width);
    const relX = x - padL;
    if (relX < 0 || relX > plotW) { setHover(null); return; }
    const idx = Math.round(relX / xStep);
    if (idx >= 0 && idx < pts.length) setHover(idx);
  };

  // Total revenue / success for the header
  const total = values.reduce((a, b) => a + b, 0);
  const totalLabel = mode === 'revenue' ? fmt(total) : total + ' pagos';

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Header: metric toggle + total */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: 3 }}>
          <button
            onClick={() => setMode('revenue')}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 600,
              background: mode === 'revenue' ? G.gold : 'transparent',
              color: mode === 'revenue' ? '#000' : G.muted,
              border: 'none', borderRadius: 6, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all .15s',
            }}
          >Ingresos</button>
          <button
            onClick={() => setMode('ok')}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 600,
              background: mode === 'ok' ? G.gold : 'transparent',
              color: mode === 'ok' ? '#000' : G.muted,
              border: 'none', borderRadius: 6, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all .15s',
            }}
          >Ventas</button>
        </div>
        <div style={{ fontSize: 12, color: G.muted }}>
          Total del periodo: <strong style={{ color: G.goldLight, fontFamily: 'DM Mono, monospace' }}>{totalLabel}</strong>
        </div>
      </div>

      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="lineChartArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={G.gold} stopOpacity="0.35" />
            <stop offset="100%" stopColor={G.gold} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lineChartStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={G.gold} />
            <stop offset="100%" stopColor={G.goldLight} />
          </linearGradient>
        </defs>

        {/* Horizontal grid + Y-axis labels */}
        {tickValues.map((tv, i) => {
          const y = getY(tv);
          return (
            <g key={`tick-${i}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke={G.border} strokeDasharray="3 4" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} fill={G.muted} fontSize="10" fontFamily="DM Mono, monospace" textAnchor="end">{fmtY(tv)}</text>
            </g>
          );
        })}

        {/* X-axis labels */}
        {pts.map((p, i) => {
          if (i % labelEvery !== 0 && i !== pts.length - 1) return null;
          const x = getX(i);
          return (
            <text key={`xl-${i}`} x={x} y={H - 10} fill={G.muted} fontSize="10" fontFamily="DM Mono, monospace" textAnchor="middle">{fmtDate(p.date)}</text>
          );
        })}

        {/* Area fill */}
        {pts.length > 0 && <path d={areaPath} fill="url(#lineChartArea)" />}

        {/* Main line */}
        {pts.length > 0 && (
          <path
            d={linePath}
            fill="none"
            stroke="url(#lineChartStroke)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Data points */}
        {pts.map((p, i) => {
          const x = getX(i);
          const y = getY(values[i]);
          const active = hover === i;
          return (
            <g key={`pt-${i}`}>
              {active && <circle cx={x} cy={y} r="8" fill={G.gold} fillOpacity="0.2" />}
              <circle
                cx={x}
                cy={y}
                r={active ? 5 : 3}
                fill={active ? G.goldLight : G.gold}
                stroke={G.bg}
                strokeWidth="2"
              />
            </g>
          );
        })}

        {/* Vertical hover line */}
        {hover !== null && (
          <line
            x1={getX(hover)}
            y1={padT}
            x2={getX(hover)}
            y2={padT + plotH}
            stroke={G.gold}
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.5"
          />
        )}
      </svg>

      {/* Tooltip */}
      {hover !== null && pts[hover] && (
        <div style={{
          position: 'absolute',
          left: `${(getX(hover) / W) * 100}%`,
          top: 46,
          transform: 'translateX(-50%)',
          background: G.surface,
          border: `1px solid ${G.gold}60`,
          borderRadius: 10,
          padding: '10px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,.6)',
          pointerEvents: 'none',
          zIndex: 20,
          whiteSpace: 'nowrap',
          minWidth: 150,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: G.goldLight, marginBottom: 6, fontFamily: 'DM Mono, monospace' }}>
            {pts[hover].date}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
            {fmt(pts[hover].revenue || 0)}
          </div>
          <div style={{ fontSize: 11, color: G.green, marginBottom: 2 }}>
            ✓ {pts[hover].ok || 0} exitosos
          </div>
          {(pts[hover].fail || 0) > 0 && (
            <div style={{ fontSize: 11, color: G.red }}>
              ✗ {pts[hover].fail} fallidos
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -- PAGES --------------------------------------------------------------------

// Helper: current Madrid day as YYYY-MM-DD
const madridTodayStr = () => new Date().toLocaleDateString('sv-SE', { timeZone: APP_TZ });
const madridDayStr = (iso) => { try { return new Date(iso).toLocaleDateString('sv-SE', { timeZone: APP_TZ }); } catch { return ''; } };
const madridDateLabel = (iso) => { try { return new Date(iso).toLocaleDateString('es-ES', { timeZone: APP_TZ, day: 'numeric', month: 'short' }); } catch { return ''; } };

// ══════════════════════════════════════════════════════════════════════════
// RENTABILITY CARD — "Am I profitable?" master component
// ══════════════════════════════════════════════════════════════════════════
// Shows the single most important answer the operator needs: is the business
// making money right now? Composed of:
//  - 🎯 Big semáforo verdict (green/yellow/red)
//  - 4 KPI sub-cards: Day-1 ROAS, MER, Payback, Cash 30d
//  - Inline numeric breakdown (LTV, CPA, margin)
//  - Expandable 30-day cash flow chart with break-even day marker
//  - Human-readable explanation (Spanish)
function RentabilityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(3);

  const load = useCallback(() => {
    setLoading(true);
    paymentsApi.getRentability({ windowDays, projectionDays: 30 })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const fmtMoney = (n) => {
    const v = Number(n) || 0;
    return (v < 0 ? '-$' : '$') + Math.abs(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: G.muted, fontSize: 13 }}>
        Calculando rentabilidad…
      </div>
    );
  }
  if (!data) return null;

  const v = data.verdict?.overall || 'unknown';
  const colorMap = {
    profitable:   { main: G.green, bg: `${G.green}15`, border: G.green, emoji: '🟢', label: 'RENTABLE' },
    borderline:   { main: G.gold,  bg: `${G.gold}15`,  border: G.gold,  emoji: '🟡', label: 'RENTABLE CON REBILLS' },
    unprofitable: { main: G.red,   bg: `${G.red}15`,   border: G.red,   emoji: '🔴', label: 'NO RENTABLE' },
    unknown:      { main: G.muted, bg: G.card,         border: G.border, emoji: '⚪', label: 'SIN DATOS' },
  };
  const col = colorMap[v] || colorMap.unknown;

  const d1 = data.day1 || {};
  const mer = data.mer || {};
  const pay = data.payback || {};
  const cf = data.cashFlow30d || {};
  const ltv = data.ltv || {};
  const inputs = data.inputs || {};
  const scenarios = data.scenarios || {};

  // Window selector
  const WindowSelector = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Ventana:</span>
      {[1, 3, 7, 30].map(d => (
        <button
          key={d}
          onClick={() => setWindowDays(d)}
          style={{
            padding: '6px 14px',
            background: windowDays === d ? G.gold : 'transparent',
            color: windowDays === d ? G.black : G.white,
            border: `1px solid ${windowDays === d ? G.gold : G.border}`,
            borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            fontFamily: 'DM Mono, monospace',
          }}
        >
          {d === 1 ? 'Hoy' : `${d}d`}
        </button>
      ))}
      <div style={{ fontSize: 10, color: G.dim, marginLeft: 4 }}>
        {inputs.totalCheckouts || 0} pedidos · {inputs.totalRebills || 0} rebills
      </div>
    </div>
  );

  const subCards = [
    {
      label: 'Day-1 ROAS',
      value: `${(d1.roas || 0).toFixed(2)}x`,
      sub: `mínimo ${(d1.breakEven || 0).toFixed(2)}x para break-even`,
      verdict: d1.verdict,
      hint: d1.verdict === 'profitable' ? '✓ Rentable solo con checkout'
        : d1.verdict === 'borderline' ? '≈ Cerca del break-even' : '✗ Pierdes en el primer cobro',
    },
    {
      label: `MER (${data.windowDays}d)`,
      value: `${(mer.value || 0).toFixed(2)}x`,
      sub: mer.verdict === 'healthy' ? '✓ ≥ 2.5x — saludable'
        : mer.verdict === 'tight' ? '≈ 1.5-2.5x — justo' : '✗ < 1.5x — peligroso',
      verdict: mer.verdict === 'healthy' ? 'profitable' : mer.verdict === 'tight' ? 'borderline' : 'unprofitable',
      hint: 'Revenue total / Ad spend',
    },
    {
      label: 'Payback',
      value: pay.days === null ? '∞' : pay.days === 0 ? 'Instantáneo' : `${pay.days}d`,
      sub: pay.verdict === 'fast' ? '✓ Recuperas rápido'
        : pay.verdict === 'ok' ? '≈ Aceptable'
        : pay.verdict === 'slow' ? '✗ Lento' : '✗ Nunca recuperas',
      verdict: pay.verdict === 'fast' ? 'profitable' : pay.verdict === 'ok' ? 'borderline' : 'unprofitable',
      hint: 'Días para recuperar CPA',
    },
    {
      label: 'Cash 30d',
      value: fmtMoney(cf.finalCash || 0),
      sub: cf.breakEvenDay !== null && cf.breakEvenDay !== undefined ? `Break-even día ${cf.breakEvenDay}` : 'No llega a break-even',
      verdict: (cf.finalCash || 0) > 0 ? 'profitable' : 'unprofitable',
      hint: 'Cash acumulado proyectado',
    },
  ];

  return (
    <div>
      {/* ═══════ HEADER GRANDE CON VEREDICTO ═══════ */}
      <div style={{
        background: `linear-gradient(135deg, ${col.bg} 0%, ${G.surface} 100%)`,
        border: `2px solid ${col.border}`, borderRadius: 14, padding: 24, marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 240, height: 240,
          background: `radial-gradient(circle, ${col.main}12 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 44 }}>{col.emoji}</div>
            <div>
              <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
                ¿Estás ganando dinero?
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: col.main, letterSpacing: 0.5, lineHeight: 1.1 }}>
                {col.label}
              </div>
            </div>
          </div>
          <WindowSelector />
        </div>

        {/* Headline explanation */}
        {data.verdict?.explanation?.headline && (
          <div style={{
            fontSize: 14, color: G.white, lineHeight: 1.5, padding: 14,
            background: `${col.main}0a`, borderLeft: `3px solid ${col.main}`, borderRadius: 4,
            marginBottom: 0,
          }}>
            {data.verdict.explanation.headline}
          </div>
        )}
      </div>

      {/* ═══════ TRES ESCENARIOS LADO A LADO ═══════ */}
      <div className="rent-scenarios">
        {/* Escenario A: Day-1 (solo checkout) */}
        <ScenarioCard
          title="ESCENARIO A"
          subtitle="Día 0 — Solo checkout"
          description="Si los rebills fallan o el cliente cancela."
          net={scenarios.day1?.net || 0}
          verdict={scenarios.day1?.verdict}
          detail={scenarios.day1?.description}
          lines={[
            { label: 'Revenue checkout', value: fmtMoney(inputs.aov), type: 'plus' },
            { label: 'COGS', value: `-${fmtMoney(inputs.cogs)}`, type: 'minus' },
            { label: `Fee pasarela (${inputs.feePct}% + $${(inputs.feeFlat || 0).toFixed(2)})`, value: `-${fmtMoney(inputs.aov * (inputs.feePct || 0) / 100 + (inputs.feeFlat || 0))}`, type: 'minus' },
            { label: 'CPA', value: `-${fmtMoney(inputs.cpa)}`, type: 'minus' },
          ]}
          fmtMoney={fmtMoney}
        />
        {/* Escenario B: 1 rebill (día 5) */}
        <ScenarioCard
          title="ESCENARIO B"
          subtitle="1 rebill — 5 días"
          description={`Checkout + primer rebill del día 5 (rate ${fmtPct(inputs.rebill1Rate).replace('.0', '')}).`}
          net={scenarios.ltv1?.net || 0}
          verdict={scenarios.ltv1?.verdict}
          detail={scenarios.ltv1?.description}
          lines={[
            { label: 'Checkout (día 0)', value: fmtMoney(ltv.month0), type: 'plus' },
            { label: 'Rebill 1 (día 5)', value: `+${fmtMoney(ltv.month1)}`, type: 'plus' },
            { label: 'COGS', value: `-${fmtMoney(inputs.cogs)}`, type: 'minus' },
            { label: 'Fees pasarela', value: `-${fmtMoney(inputs.aov * (inputs.feePct || 0) / 100 + (inputs.feeFlat || 0) + ltv.month1 * (inputs.feePct || 0) / 100 + ((ltv.month1 > 0 ? (inputs.feeFlat || 0) * (inputs.rebill1Rate / 100) : 0)))}`, type: 'minus' },
            { label: 'CPA', value: `-${fmtMoney(inputs.cpa)}`, type: 'minus' },
          ]}
          fmtMoney={fmtMoney}
        />
        {/* Escenario C: 2 rebills (35 días — día 5 + día 35) */}
        <ScenarioCard
          title="ESCENARIO C"
          subtitle="2 rebills — 35 días"
          description={`Checkout + rebill día 5 + rebill día 35 (con retención).`}
          net={scenarios.ltv?.net || 0}
          verdict={scenarios.ltv?.verdict}
          detail={scenarios.ltv?.description}
          lines={[
            { label: 'Checkout (día 0)', value: fmtMoney(ltv.month0), type: 'plus' },
            { label: 'Rebill 1 (día 5)', value: `+${fmtMoney(ltv.month1)}`, type: 'plus' },
            { label: 'Rebill 2 (día 35)', value: `+${fmtMoney(ltv.month2)}`, type: 'plus' },
            { label: 'COGS + Fees', value: `-${fmtMoney((ltv.totalCogs || 0) + (ltv.totalFees || 0))}`, type: 'minus' },
            { label: 'CPA', value: `-${fmtMoney(inputs.cpa)}`, type: 'minus' },
          ]}
          fmtMoney={fmtMoney}
        />
      </div>

      {/* ═══════ 4 KPI SUB-CARDS ═══════ */}
      <div className="rent-kpis">
        {subCards.map((c, i) => {
          const sc = colorMap[c.verdict] || colorMap.unknown;
          return (
            <div key={i} style={{
              background: G.card, border: `1px solid ${sc.border}50`, borderRadius: 10, padding: 14,
            }}>
              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{c.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: sc.main, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{c.value}</div>
              <div style={{ fontSize: 10, color: G.dim, marginTop: 5 }}>{c.hint}</div>
              <div style={{ fontSize: 11, color: sc.main, marginTop: 3, fontWeight: 600 }}>{c.sub}</div>
            </div>
          );
        })}
      </div>

      {/* ═══════ DATOS USADOS (transparencia) ═══════ */}
      <div style={{
        background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14, marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Datos usados en el cálculo (últimos {windowDays} días)
        </div>
        <div className="rent-data-grid" style={{ fontSize: 12, fontFamily: 'DM Mono, monospace' }}>
          <DataPoint label="AOV" value={fmtMoney(inputs.aov)} subtitle={`${inputs.totalCheckouts} pedidos`} />
          <DataPoint label="CPA real" value={fmtMoney(inputs.cpa)} subtitle={`Ads / clientes nuevos`} />
          <DataPoint label="Rebill rate" value={fmtPct(inputs.rebill1Rate)} subtitle="% que rebillea día 5" />
          <DataPoint label="Precio rebill" value={fmtMoney(inputs.rebillPrice)} subtitle="Mediana real" />
          <DataPoint label="COGS" value={fmtMoney(inputs.cogs)}
            subtitle={inputs.cogsSource === 'orders' ? `Promedio de ${inputs.cogsCountedOrders} pedidos`
              : inputs.cogsSource === 'catalog' ? 'Promedio del catálogo' : 'Valor por defecto'} />
          <DataPoint label="Ad spend/día" value={fmtMoney(inputs.dailyAdSpend)} subtitle="Media diaria" />
        </div>
      </div>

      {/* ═══════ GRÁFICA CASH FLOW 30 DÍAS ═══════ */}
      <CashFlowChart
        series={cf.series || []}
        breakEvenDay={cf.breakEvenDay}
        finalCash={cf.finalCash}
        fmtMoney={fmtMoney}
      />

      {/* ═══════ CPA MÁXIMO RECOMENDADO ═══════ */}
      <div style={{
        background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginTop: 16,
      }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
          ¿Cuánto puedo pagar por cliente?
        </div>
        <div className="rent-cpa-grid">
          <CpaBox label="Break-even" tooltip="Ni pierdes ni ganas" value={fmtMoney(ltv.cpaMax)} color={G.white} />
          <CpaBox label="20% de margen" tooltip="Profit saludable" value={fmtMoney(ltv.cpaFor20)} color={G.gold} />
          <CpaBox label="30% de margen" tooltip="Objetivo óptimo" value={fmtMoney(ltv.cpaFor30)} color={G.green} />
          <div style={{
            padding: 14, background: G.surface, borderRadius: 8,
            border: `2px solid ${inputs.cpa < (ltv.cpaFor20 || 0) ? G.green : inputs.cpa < (ltv.cpaMax || 0) ? G.gold : G.red}`,
          }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tu CPA actual</div>
            <div style={{
              fontSize: 22, fontWeight: 700,
              color: inputs.cpa < (ltv.cpaFor20 || 0) ? G.green : inputs.cpa < (ltv.cpaMax || 0) ? G.gold : G.red,
              fontFamily: 'DM Mono, monospace', marginTop: 2,
            }}>{fmtMoney(inputs.cpa)}</div>
            <div style={{ fontSize: 10, color: G.dim, marginTop: 3 }}>
              {inputs.cpa < (ltv.cpaFor30 || 0) ? '✓ Muy saludable'
                : inputs.cpa < (ltv.cpaFor20 || 0) ? '✓ Saludable'
                : inputs.cpa < (ltv.cpaMax || 0) ? '⚠ Margen justo'
                : '✗ Perdiendo dinero'}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════ EXPLICACIÓN + CONSEJOS ═══════ */}
      {data.verdict?.explanation && (
        <div style={{
          background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginTop: 16,
        }}>
          <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            Explicación detallada
          </div>
          <pre style={{
            fontFamily: 'DM Mono, monospace', fontSize: 11, color: G.white,
            whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6,
          }}>{data.verdict.explanation.breakdown}</pre>
          {Array.isArray(data.verdict.explanation.advice) && data.verdict.explanation.advice.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${G.border}` }}>
              <div style={{ fontSize: 11, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>
                💡 Consejos profesionales
              </div>
              {data.verdict.explanation.advice.map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: G.white, marginBottom: 6, lineHeight: 1.5 }}>• {a}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════ ANÁLISIS DE CAMPAÑAS (qué pausar / escalar) ═══════ */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: G.white, marginBottom: 4 }}>
          🎯 Análisis por campaña
        </div>
        <div style={{ fontSize: 12, color: G.dim, marginBottom: 12 }}>
          Estas son tus campañas de Meta Ads en los últimos {windowDays} día(s). Cada una lleva un veredicto profesional basado en su gasto real vs revenue atribuido.
        </div>
        <CampaignAnalysisPanel windowDays={windowDays} />
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────
function ScenarioCard({ title, subtitle, description, net, verdict, detail, lines, fmtMoney }) {
  const isOk = verdict === 'profitable';
  const color = isOk ? G.green : G.red;
  return (
    <div style={{
      background: G.card, border: `2px solid ${color}40`, borderRadius: 10, padding: 16,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: G.white, marginBottom: 3 }}>{subtitle}</div>
      <div style={{ fontSize: 11, color: G.dim, marginBottom: 12 }}>{description}</div>

      <div style={{ fontFamily: 'DM Mono, monospace', marginBottom: 12 }}>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: G.dim, padding: '3px 0' }}>
            <span>{l.label}</span>
            <span style={{ color: l.type === 'minus' ? G.red : G.white }}>{l.value}</span>
          </div>
        ))}
      </div>

      <div style={{
        padding: 12, background: `${color}12`, borderRadius: 6, borderLeft: `3px solid ${color}`,
      }}>
        <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
          Resultado por cliente
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>
          {net >= 0 ? '+' : ''}{fmtMoney(net)}
        </div>
        <div style={{ fontSize: 11, color: G.white, marginTop: 4 }}>{detail}</div>
      </div>
    </div>
  );
}

function DataPoint({ label, value, subtitle }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      <div style={{ fontSize: 10, color: G.dim, marginTop: 2 }}>{subtitle}</div>
    </div>
  );
}

function CpaBox({ label, tooltip, value, color }) {
  return (
    <div style={{ padding: 14, background: G.surface, borderRadius: 8, border: `1px solid ${color}40` }}>
      <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 10, color: G.dim, marginTop: 3 }}>{tooltip}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CAMPAIGN ANALYSIS — "which ads to pause / scale"
// ══════════════════════════════════════════════════════════════════════════
function CampaignAnalysisPanel({ windowDays }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    paymentsApi.getCampaignAnalysis({ windowDays })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [windowDays]);

  const fmtMoney = (n) => {
    const v = Number(n) || 0;
    return (v < 0 ? '-$' : '$') + Math.abs(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  if (loading) {
    return (
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20, textAlign: 'center', color: G.muted, fontSize: 12 }}>
        Analizando campañas…
      </div>
    );
  }
  if (!data) return null;
  if (data.reason) {
    return (
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20, textAlign: 'center', color: G.muted, fontSize: 12 }}>
        {data.reason}
      </div>
    );
  }
  if (!data.campaigns || data.campaigns.length === 0) {
    return (
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20, textAlign: 'center', color: G.muted, fontSize: 12 }}>
        No hay gasto en campañas en los últimos {windowDays} día(s).
      </div>
    );
  }

  const verdictColors = {
    scale:   { bg: `${G.green}15`,  border: G.green,       label: '✅ ESCALAR',    color: G.green },
    review:  { bg: `${G.gold}12`,   border: G.gold,        label: '🟡 MANTENER',   color: G.gold },
    warning: { bg: `${G.orange || '#f59e0b'}20`, border: G.orange || '#f59e0b', label: '🟠 DESACTIVAR SI NO MEJORA', color: G.orange || '#f59e0b' },
    pause:   { bg: `${G.red}18`,    border: G.red,         label: '❌ DESACTIVAR YA', color: G.red },
    monitor: { bg: G.surface,       border: G.border,      label: '👀 MONITOREAR', color: G.muted },
  };

  return (
    <div>
      {/* Executive summary */}
      {data.summary && (
        <>
          {/* Campañas a desactivar YA (pierdes dinero) */}
          {data.summary.toPause > 0 && (
            <div style={{
              background: `${G.red}15`, border: `2px solid ${G.red}`,
              borderRadius: 10, padding: 16, marginBottom: 14,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.red, marginBottom: 8 }}>
                ❌ DESACTIVAR YA — {data.summary.toPause} campaña{data.summary.toPause > 1 ? 's' : ''}
              </div>
              <div style={{ fontSize: 12, color: G.white, marginBottom: 10, lineHeight: 1.5 }}>
                Estás perdiendo <strong style={{ color: G.red }}>{fmtMoney(data.summary.monthlySavingsIfPause)}</strong> con estas campañas en los últimos {windowDays} días. Desactívalas ahora.
              </div>
              {data.summary.pauseCampaigns.map((c, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
                  background: G.card, borderRadius: 5, marginBottom: 4, fontSize: 12, fontFamily: 'DM Mono, monospace',
                  flexWrap: 'wrap', gap: 6,
                }}>
                  <span style={{ color: G.white, fontWeight: 600 }}>{c.name}</span>
                  <span style={{ color: G.red }}>
                    CPA {c.cpa === null ? '∞' : fmtMoney(c.cpa)} · profit/cliente {fmtMoney(c.profitPerCustomer)} · gasto {fmtMoney(c.spend)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Campañas en warning — desactivar si no mejoran */}
          {data.summary.toWarning > 0 && (
            <div style={{
              background: `${G.orange || '#f59e0b'}18`, border: `2px solid ${G.orange || '#f59e0b'}`,
              borderRadius: 10, padding: 16, marginBottom: 14,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.orange || '#f59e0b', marginBottom: 8 }}>
                🟠 REVISAR — {data.summary.toWarning} campaña{data.summary.toWarning > 1 ? 's' : ''} con poco margen
              </div>
              <div style={{ fontSize: 12, color: G.white, marginBottom: 10, lineHeight: 1.5 }}>
                Estas campañas ganan pero por debajo del objetivo mínimo de <strong>{fmtMoney(data.thresholds?.minProfitPerCustomer || 20)}/cliente</strong>. Dale 1-2 días para ver si mejoran; si no, desactivar.
              </div>
              {data.summary.warningCampaigns.map((c, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
                  background: G.card, borderRadius: 5, marginBottom: 4, fontSize: 12, fontFamily: 'DM Mono, monospace',
                  flexWrap: 'wrap', gap: 6,
                }}>
                  <span style={{ color: G.white, fontWeight: 600 }}>{c.name}</span>
                  <span style={{ color: G.orange || '#f59e0b' }}>
                    CPA {c.cpa === null ? '∞' : fmtMoney(c.cpa)} · profit/cliente {fmtMoney(c.profitPerCustomer)} · gasto {fmtMoney(c.spend)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* All good */}
          {data.summary.toPause === 0 && data.summary.toWarning === 0 && (
            <div style={{
              background: `${G.green}12`, border: `2px solid ${G.green}`,
              borderRadius: 10, padding: 16, marginBottom: 14,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.green }}>
                ✅ Todas las campañas tienen rentabilidad saludable
              </div>
              <div style={{ fontSize: 12, color: G.white, marginTop: 6 }}>
                Ninguna campaña está perdiendo dinero ni por debajo del umbral mínimo. Puedes mantener el gasto actual.
              </div>
            </div>
          )}
        </>
      )}

      {/* Full table */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14 }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Todas las campañas — últimos {windowDays} día(s)
        </div>
        <div className="campaigns-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 780 }}>
            <thead>
              <tr style={{ background: G.black }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Campaña</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Gasto</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Conv.</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>CPA</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Profit/cliente</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Profit total</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map((c, i) => {
                const vc = verdictColors[c.verdict] || verdictColors.monitor;
                const cpaColor = c.cpa === null ? G.red
                  : c.profitPerCustomer >= (data.thresholds?.healthyProfitPerCustomer || 30) ? G.green
                  : c.profitPerCustomer >= (data.thresholds?.minProfitPerCustomer || 20) ? G.gold
                  : G.red;
                const profitColor = c.profitPerCustomer >= (data.thresholds?.healthyProfitPerCustomer || 30) ? G.green
                  : c.profitPerCustomer >= 0 ? G.gold
                  : G.red;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${G.border}40`, background: vc.bg }}>
                    <td style={{ padding: '10px', color: G.white, fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.campaignName}>
                      <div style={{ fontWeight: 600 }}>{c.campaignName}</div>
                      <div style={{ fontSize: 10, color: G.dim }}>{c.shopName}</div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: G.white, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{fmtMoney(c.spend)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: G.dim, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
                      {Math.round(c.conversions)}
                      <div style={{ fontSize: 9, color: G.muted }}>Meta Pixel</div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: cpaColor }}>
                      {c.cpa === null ? '∞' : fmtMoney(c.cpa)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: profitColor }}>
                      {c.profitPerCustomer >= 0 ? '+' : ''}{fmtMoney(c.profitPerCustomer)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 700, color: c.netProfit >= 0 ? G.green : G.red }}>
                      {c.netProfit >= 0 ? '+' : ''}{fmtMoney(c.netProfit)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '5px 10px', borderRadius: 5,
                        background: vc.color, color: vc.color === G.gold || vc.color === G.green ? G.black : G.white,
                        letterSpacing: 0.5, whiteSpace: 'nowrap',
                      }}>{vc.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Thresholds context */}
        <div style={{
          fontSize: 11, color: G.white, marginTop: 12, padding: 12,
          background: G.surface, borderRadius: 8, lineHeight: 1.7,
        }}>
          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            Cómo se clasifica cada campaña
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong style={{ color: G.green }}>✅ ESCALAR</strong> — ganas ≥ {fmtMoney(data.thresholds?.healthyProfitPerCustomer || 30)}/cliente. Sube el presupuesto con seguridad.
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong style={{ color: G.gold }}>🟡 MANTENER</strong> — ganas entre {fmtMoney(data.thresholds?.minProfitPerCustomer || 20)} y {fmtMoney(data.thresholds?.healthyProfitPerCustomer || 30)}/cliente. Funciona pero no escalar aún.
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong style={{ color: G.orange || '#f59e0b' }}>🟠 DESACTIVAR SI NO MEJORA</strong> — ganas menos de {fmtMoney(data.thresholds?.minProfitPerCustomer || 20)}/cliente. Dale 1-2 días más; si no sube, desactivar.
          </div>
          <div>
            <strong style={{ color: G.red }}>❌ DESACTIVAR YA</strong> — pierdes dinero por cliente. Cortar el grifo inmediato.
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CASH FLOW CHART — SVG line chart with break-even day marker
// ══════════════════════════════════════════════════════════════════════════
function CashFlowChart({ series, breakEvenDay, finalCash, fmtMoney }) {
  if (!series || series.length === 0) return null;

  const W = 720, H = 180;
  const PAD_L = 60, PAD_R = 12, PAD_T = 14, PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const vals = series.map(s => s.cumulative);
  const minV = Math.min(0, ...vals);
  const maxV = Math.max(0, ...vals);
  const range = (maxV - minV) || 1;

  const x = (i) => PAD_L + (i / (series.length - 1)) * innerW;
  const y = (v) => PAD_T + (1 - (v - minV) / range) * innerH;

  // Build path
  const linePath = series.map((s, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(s.cumulative).toFixed(1)}`).join(' ');
  // Area under/over zero
  const areaPath = linePath + ` L ${x(series.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  const zeroY = y(0);

  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: G.white, marginBottom: 6 }}>
          📈 Proyección de cash acumulado — próximos 30 días
        </div>
        <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6, background: G.surface, padding: 10, borderRadius: 6 }}>
          <strong style={{ color: G.white }}>¿Qué muestra esta gráfica?</strong><br/>
          La línea representa el <strong style={{ color: G.white }}>dinero acumulado</strong> que tendrás en caja día a día si sigues gastando lo mismo en ads y tu rebill rate se mantiene.
          <br/><br/>
          • <strong style={{ color: G.red }}>Por debajo del cero</strong> = estás en pérdidas (pagaste ads pero los rebills aún no llegaron).<br/>
          • <strong style={{ color: G.gold }}>Línea dorada vertical</strong> = día en que pasas de pérdidas a beneficios (break-even).<br/>
          • <strong style={{ color: G.green }}>Por encima del cero</strong> = estás ganando dinero real en el bolsillo.
          <br/><br/>
          Cada cliente paga en <strong>día 0</strong> ({'$'}AOV checkout), en <strong>día 5</strong> (primer rebill $59.99 × tu rebill rate) y en <strong>día 35</strong> (segundo rebill × 50%). La curva refleja ese ciclo.
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 14, fontFamily: 'DM Mono, monospace', fontSize: 11, marginBottom: 4 }}>
        {breakEvenDay !== null && breakEvenDay !== undefined && (
          <div>Break-even: <span style={{ color: G.gold, fontWeight: 700 }}>día {breakEvenDay}</span></div>
        )}
        <div>Cash en 30 días: <span style={{ color: (finalCash || 0) >= 0 ? G.green : G.red, fontWeight: 700 }}>{fmtMoney(finalCash)}</span></div>
      </div>

      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const yy = PAD_T + t * innerH;
          return <line key={i} x1={PAD_L} y1={yy} x2={W - PAD_R} y2={yy} stroke={G.border} strokeWidth="0.5" strokeDasharray="3 4" opacity="0.5" />;
        })}

        {/* Y-axis labels */}
        {[0, 0.5, 1].map((t, i) => {
          const val = maxV - t * range;
          const yy = PAD_T + t * innerH;
          return (
            <text key={i} x={PAD_L - 6} y={yy + 3} textAnchor="end" fill={G.muted} fontSize="9" fontFamily="DM Mono, monospace">
              ${Math.round(val).toLocaleString()}
            </text>
          );
        })}

        {/* Zero line — emphasized */}
        <line x1={PAD_L} y1={zeroY} x2={W - PAD_R} y2={zeroY} stroke={G.muted} strokeWidth="1" strokeDasharray="5 5" />
        <text x={PAD_L - 6} y={zeroY + 3} textAnchor="end" fill={G.muted} fontSize="9" fontFamily="DM Mono, monospace" fontWeight="700">$0</text>

        {/* Area fill */}
        <path d={areaPath} fill={(finalCash || 0) >= 0 ? G.green : G.red} opacity="0.12" />

        {/* Main line */}
        <path d={linePath} fill="none" stroke={(finalCash || 0) >= 0 ? G.green : G.red} strokeWidth="2" />

        {/* Break-even marker */}
        {breakEvenDay !== null && breakEvenDay !== undefined && breakEvenDay < series.length && (
          <g>
            <line x1={x(breakEvenDay)} y1={PAD_T} x2={x(breakEvenDay)} y2={H - PAD_B} stroke={G.gold} strokeWidth="1" strokeDasharray="3 3" opacity="0.8" />
            <circle cx={x(breakEvenDay)} cy={y(series[breakEvenDay].cumulative)} r="4" fill={G.gold} stroke={G.black} strokeWidth="1.5" />
            <text x={x(breakEvenDay)} y={PAD_T - 4} textAnchor="middle" fill={G.gold} fontSize="10" fontFamily="DM Mono, monospace" fontWeight="700">
              Break-even
            </text>
          </g>
        )}

        {/* X-axis labels (every 5 days) */}
        {series.map((s, i) => {
          if (i === 0 || i === series.length - 1 || i % 5 === 0) {
            return (
              <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fill={G.muted} fontSize="9" fontFamily="DM Mono, monospace">
                {i === 0 ? 'HOY' : `+${i}d`}
              </text>
            );
          }
          return null;
        })}

        {/* Final point marker */}
        <circle cx={x(series.length - 1)} cy={y(series[series.length - 1].cumulative)} r="4" fill={(finalCash || 0) >= 0 ? G.green : G.red} stroke={G.black} strokeWidth="1.5" />
      </svg>
    </div>
  );
}

// ── Dashboard feature panels (HP-style) ─────────────────────────────────────
// Shared palette for gateway/product accents.
const PANEL_PALETTE = ['#8b5cf6', '#4B9FE8', '#4BE8A0', '#E8994B', '#E84B4B', '#22D3EE', '#EC4899', '#A3E635', '#F5C842', '#14B8A6', '#F472B6', '#38BDF8'];

// Decode HTML entities (&amp; &#39; …) coming from stored product titles.
function decodeEntities(s) {
  if (!s || typeof s !== 'string') return s || '';
  try {
    if (typeof document !== 'undefined') {
      const el = document.createElement('textarea');
      el.innerHTML = s;
      return el.value;
    }
  } catch { /* noop */ }
  return s.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

// Compact KPI stat card used by the gateway + revenue-over-time panels.
function StatCard({ emoji, color, label, value }) {
  return (
    <div className="stat-card" style={{ width: '100%', minWidth: 0, background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.border}`, borderRadius: 12, padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div className="stat-ic" style={{ width: 38, height: 38, borderRadius: 10, background: `${color}22`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{emoji}</div>
      <div style={{ minWidth: 0 }}>
        <div className="stat-lbl" style={{ fontSize: 9.5, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{label}</div>
        <div className="stat-val" style={{ fontSize: 20, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      </div>
    </div>
  );
}

// Donut chart (Chart.js). segments = [{ label, value, color }]. Empty center.
function DonutChart({ segments = [], size = 200 }) {
  const data = {
    labels: segments.map(s => s.label || ''),
    datasets: [{
      data: segments.map(s => Math.max(0, s.value || 0)),
      backgroundColor: segments.map(s => s.color),
      borderColor: G.card,
      borderWidth: 3,
      hoverOffset: 8,
      spacing: 2,
    }],
  };
  const options = {
    cutout: '68%',
    maintainAspectRatio: false,
    animation: { duration: 600 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)', borderColor: G.border, borderWidth: 1,
        padding: 10, cornerRadius: 8, titleColor: G.white, bodyColor: G.dim, displayColors: true,
        callbacks: { label: (ctx) => ` ${fmt(ctx.raw)}` },
      },
    },
  };
  return <div style={{ width: size, height: size, maxWidth: '100%' }}><Doughnut data={data} options={options} /></div>;
}

// Revenue over time (Chart.js). Current = gradient area; previous = dashed line.
function RevenueTimeChart({ points = [], height = 260 }) {
  const labels = points.map(p => p.label);
  const cur = points.map(p => p.revenue || 0);
  const prv = points.map(p => p.revenuePrev || 0);
  const data = {
    labels,
    datasets: [
      {
        label: 'Revenue', data: cur, borderColor: G.blue, borderWidth: 2.6,
        tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5,
        pointHoverBackgroundColor: G.blue, pointHoverBorderColor: '#fff', pointHoverBorderWidth: 2,
        backgroundColor: (ctx) => {
          const { chart } = ctx; const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(75,159,232,0.15)';
          const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          grad.addColorStop(0, 'rgba(75,159,232,0.40)');
          grad.addColorStop(1, 'rgba(75,159,232,0)');
          return grad;
        },
      },
      {
        label: 'Revenue prev.', data: prv, borderColor: G.dim, borderWidth: 1.6,
        borderDash: [6, 5], tension: 0.4, fill: false, pointRadius: 0, pointHoverRadius: 4,
      },
    ],
  };
  const options = {
    maintainAspectRatio: false,
    animation: { duration: 500 },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)', borderColor: G.border, borderWidth: 1,
        padding: 10, cornerRadius: 8, titleColor: G.white, bodyColor: G.dim,
        callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.raw)}` },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: G.muted, font: { size: 10, family: 'DM Mono, monospace' }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
      y: { grid: { color: G.border, drawTicks: false }, border: { display: false }, beginAtZero: true, ticks: { color: G.muted, font: { size: 10, family: 'DM Mono, monospace' }, callback: (v) => fmtK(v), maxTicksLimit: 5 } },
    },
  };
  return <div style={{ width: '100%', height }}><Line data={data} options={options} /></div>;
}

// Panel A — Revenue by gateway (donut + list + KPIs)
function GatewayRevenuePanel({ gatewayBreakdown = [] }) {
  const [view, setView] = useState('doughnut');
  const [selected, setSelected] = useState('all');
  const segs = (gatewayBreakdown || [])
    .filter(g => (g.revenue || 0) > 0 || (g.total || 0) > 0)
    .map((g, i) => ({ ...g, color: PANEL_PALETTE[i % PANEL_PALETTE.length] }));
  const grandRevenue = segs.reduce((s, g) => s + (g.revenue || 0), 0);
  const active = selected === 'all' ? segs : segs.filter(g => g.name === selected);
  const kRevenue = active.reduce((s, g) => s + (g.revenue || 0), 0);
  const kApproved = active.reduce((s, g) => s + (g.ok || 0), 0);
  const kFailed = active.reduce((s, g) => s + (g.fail || 0), 0);
  const kRate = (kApproved + kFailed) > 0 ? (kApproved / (kApproved + kFailed) * 100) : 0;
  const gwLabel = (g) => {
    const name = g.name || '';
    const psp = g.psp || '';
    if (!psp) return name;
    if (!name || name.toUpperCase() === psp.toUpperCase() || name.toUpperCase().includes(psp.toUpperCase())) return name || psp;
    return `${psp} - ${name}`;
  };
  return (
    <div className="feature-panel" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: '#8b5cf622', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💳</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: G.white }}>Revenue by gateway</div>
            <div style={{ fontSize: 11, color: G.muted }}>Distribución de ingresos y aprobación por pasarela</div>
          </div>
        </div>
        <div style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${G.blue}40`, background: `${G.blue}12`, textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Total revenue</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace' }}>{fmt(grandRevenue)}</div>
        </div>
      </div>
      {/* KPI cards */}
      <div className="panel-kpis" style={{ marginBottom: 16 }}>
        <StatCard emoji="💰" color="#8b5cf6" label="Selected revenue" value={fmt(kRevenue)} />
        <StatCard emoji="✅" color={G.green} label="Approved" value={kApproved} />
        <StatCard emoji="❌" color={G.red} label="Failed" value={kFailed} />
        <StatCard emoji="📊" color={G.orange} label="Approval rate" value={`${kRate.toFixed(2)}%`} />
      </div>
      {/* Chips */}
      <div style={{ fontSize: 9.5, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Gateway</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={() => setSelected('all')} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${selected === 'all' ? G.blue : G.border}`, background: selected === 'all' ? `${G.blue}20` : 'transparent', color: selected === 'all' ? G.white : G.dim, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>All {fmt(grandRevenue)}</button>
        {segs.map(g => (
          <button key={g.name} onClick={() => setSelected(selected === g.name ? 'all' : g.name)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 20, border: `1px solid ${selected === g.name ? g.color : G.border}`, background: selected === g.name ? `${g.color}22` : 'transparent', color: selected === g.name ? G.white : G.dim, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color }} />{gwLabel(g)} <span style={{ color: G.muted, fontFamily: 'DM Mono, monospace' }}>{fmt(g.revenue || 0)}</span>
          </button>
        ))}
      </div>
      {/* Toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <div style={{ display: 'inline-flex', border: `1px solid ${G.border}`, borderRadius: 9, overflow: 'hidden' }}>
          {[['doughnut', '◍ Doughnut'], ['list', '≣ Lista']].map(([v, lbl]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, border: 'none', background: view === v ? `${G.blue}20` : 'transparent', color: view === v ? G.blue : G.muted, cursor: 'pointer' }}>{lbl}</button>
          ))}
        </div>
      </div>
      {/* Body: donut + list */}
      <div style={{ display: 'grid', gridTemplateColumns: view === 'doughnut' ? 'minmax(240px, 380px) 1fr' : '1fr', gap: 18, alignItems: 'start' }} className="gw-panel-body">
        {view === 'doughnut' && (
          <div style={{ background: G.bg || G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <DonutChart segments={active.map(g => ({ label: gwLabel(g), value: g.revenue, color: g.color }))} size={210} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {active.map(g => (
                <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: G.dim }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: g.color }} />{gwLabel(g)}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {active.length === 0 && <div style={{ fontSize: 13, color: G.muted, padding: 20, textAlign: 'center' }}>Sin datos de pasarelas en el rango</div>}
          {active.map(g => {
            const share = grandRevenue > 0 ? (g.revenue / grandRevenue * 100) : 0;
            const rate = g.successRate || 0;
            return (
              <div key={g.name} style={{ background: G.bg || G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: g.color }} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: G.white }}>{gwLabel(g)}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: G.muted, background: G.border, padding: '2px 6px', borderRadius: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>{g.psp || 'PSP'}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace' }}>{fmt(g.revenue || 0)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, color: g.color, fontFamily: 'DM Mono, monospace', minWidth: 42 }}>{share.toFixed(1)}%</span>
                  <div style={{ flex: 1, height: 6, background: G.border, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max(2, share)}%`, height: '100%', background: g.color, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 10, color: rate >= 80 ? G.green : rate >= 50 ? G.orange : G.red, fontFamily: 'DM Mono, monospace', minWidth: 42, textAlign: 'right' }}>{rate.toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Panel B — Units sold per product (ranked list with images)
// Recuperación de carritos: emails de carrito abandonado del rango y cuántos
// acabaron en un pago del mismo cliente (ventana 7 días, ver /dashboard/cart-recovery).
function CartRecoveryPanel({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    fetch(`${BACKEND}/api/dashboard/cart-recovery?${params.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r && r.ok) ? r.json() : null).then(d => setData(d || { sent: 0, recovered: 0, recoveredRevenue: 0 })).catch(() => setData({ sent: 0, recovered: 0, recoveredRevenue: 0 }));
  }, [from, to]);
  const cr = data || { sent: 0, recovered: 0, recoveredRevenue: 0 };
  const rate = cr.sent > 0 ? (cr.recovered / cr.sent) * 100 : 0;
  const rateColor = cr.sent === 0 ? G.muted : rate >= 10 ? G.green : rate > 0 ? G.goldLight : G.red;
  const ico = (d) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;
  const tiles = [
    { label: 'Emails enviados', value: cr.sent, basis: 155, c1: '#F59E0B', c2: '#D97706', icon: ico(<><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>) },
    { label: 'Recuperados', value: cr.recovered, basis: 155, c1: '#34D399', c2: '#059669', icon: ico(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>) },
    { label: 'Ingresos recuperados', value: cr.recoveredRevenue > 0 ? fmt(cr.recoveredRevenue) : '—', basis: 200, c1: '#FBBF24', c2: '#B45309', icon: <span style={{ fontSize: 15, fontWeight: 800, lineHeight: 1 }}>€</span> },
  ];
  return (
    <div className="feature-panel" style={{ background: `linear-gradient(135deg, ${G.green}0C 0%, ${G.card} 55%)`, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      <div className="cr-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div className="cr-ic" style={{ width: 40, height: 40, borderRadius: 11, background: `${G.green}22`, color: G.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="cr-title" style={{ fontSize: 17, fontWeight: 700, color: G.white }}>Recuperación de carritos</div>
            <div className="cr-sub" style={{ fontSize: 11, color: G.muted }}>Carrito abandonado → venta recuperada</div>
          </div>
        </div>
        <div className="cr-rate" title={`Ventana de atribución: ${cr.windowDays || 7} días desde el email`} style={{ padding: '8px 18px', borderRadius: 22, border: `1.5px solid ${rateColor}55`, background: `${rateColor}12`, textAlign: 'center', flexShrink: 0 }}>
          <div className="cr-rate-val" style={{ fontSize: 18, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{rate.toFixed(2)}%</div>
          <div className="cr-rate-lbl" style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Recuperación</div>
        </div>
      </div>
      <div style={{ height: 6, background: G.border, borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ width: `${Math.max(0, Math.min(100, rate))}%`, height: '100%', background: `linear-gradient(90deg, ${G.green} 0%, #22D3EE 100%)`, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 10.5, color: G.dim, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: G.green }} />Recuperados</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: G.muted }} />Pendientes</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {tiles.map((t) => (
          <div key={t.label} style={{ flex: `1 1 ${t.basis}px`, display: 'flex', alignItems: 'center', gap: 10, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '10px 12px', minWidth: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: `linear-gradient(135deg, ${t.c1}, ${t.c2})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{t.icon}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9.5, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1.2 }}>{t.label}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{t.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Próximos payouts (pending / in_transit) de las cuentas Stripe del tenant, en su moneda nativa.
const PAYOUT_SYM = { GBP: '£', EUR: '€', USD: '$' };
function fmtPayout(n, cur) {
  const [int, dec] = Math.abs(n || 0).toFixed(2).split('.');
  const s = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
  const sym = PAYOUT_SYM[cur];
  return `${n < 0 ? '-' : ''}${sym ? sym + s : `${s} ${cur}`}`;
}

function PayoutsPanel() {
  // 2026-10-28 — Calendario de pagos: próximos 5 días (payout real de Stripe o,
  // si aún no existe, el saldo que Stripe libera ese día neto del saldo
  // negativo) + pagado ayer / hoy con los payouts realmente `paid` (los
  // cancelados se listan aparte y NO suman). Datos de GET /dashboard/payouts
  // (stripe-payouts.service.getPayoutCalendar). Se refresca cada 5 min y al
  // volver a la pestaña, así el "Hoy" avanza solo cuando cambia el día.
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/dashboard/payouts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r && r.ok) ? r.json() : null)
      .then(d => setData(d && d.daysAhead ? d : { accounts: 0 }))
      .catch(() => setData({ accounts: 0 }));
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  const days = (data && data.daysAhead) || [];
  useEffect(() => {
    if (!days.length) return;
    if (selected && days.some(d => d.date === selected)) return;
    const first = days.find(d => d.amount > 0) || days[0];
    setSelected(first.date);
  }, [days, selected]);

  if (data && !data.accounts) return null; // tenant sin cuentas Stripe
  const mono = 'DM Mono, monospace';
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  const ddmm = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  const STATUS = {
    paid: { label: 'Pagado', color: G.green, dashed: false },
    in_transit: { label: 'En camino', color: G.blue, dashed: false },
    pending: { label: 'Programado', color: G.goldLight, dashed: false },
    estimated: { label: 'Estimado', color: G.gold, dashed: true },
    weekend: { label: 'Fin de semana', color: G.muted, dashed: false },
    none: { label: 'Sin pago', color: G.muted, dashed: false },
  };
  const chip = (status, small) => {
    const s = STATUS[status] || STATUS.none;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: small ? 9.5 : 10.5, fontWeight: 700, color: s.color, padding: small ? '2px 7px' : '3px 9px', borderRadius: 20, background: `${s.color}14`, border: `1px ${s.dashed ? 'dashed' : 'solid'} ${s.color}55`, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>{s.label}</span>
    );
  };
  const others = (o) => Object.entries(o || {}).filter(([, v]) => v > 0).map(([c, v]) => (
    <div key={c} style={{ fontSize: 10.5, color: G.dim, fontFamily: mono, whiteSpace: 'nowrap' }}>+ {fmtPayout(v, c)}</div>
  ));

  const header = (
    <div className="po-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div className="po-ic" style={{ width: 40, height: 40, borderRadius: 11, background: `${G.green}22`, color: G.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10h18" /><path d="M12 3 3 8v2h18V8z" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 21h18" /></svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="po-title" style={{ fontSize: 17, fontWeight: 700, color: G.white }}>Próximos pagos</div>
          <div style={{ fontSize: 11, color: G.muted }}>
            {data ? `${data.accounts} cuentas Stripe · actualizado ${new Date(data.computedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}` : 'Consultando Stripe…'}
          </div>
        </div>
      </div>
      {data && (
        <div className="po-total" style={{ padding: '8px 16px', borderRadius: 14, border: `1.5px solid ${G.green}55`, background: `${G.green}12`, textAlign: 'right', flexShrink: 0 }}>
          <div className="po-total-val" style={{ fontSize: 20, fontWeight: 800, color: G.white, fontFamily: mono, lineHeight: 1.15, whiteSpace: 'nowrap' }}>{fmtPayout(data.totalNext, data.currency)}</div>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>Próximos {data.days} días</div>
        </div>
      )}
    </div>
  );

  if (!data) {
    return (
      <div className="feature-panel" style={{ background: `linear-gradient(135deg, ${G.green}14 0%, ${G.card} 60%)`, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
        {header}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }} className="po-days">
          {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ height: 92, borderRadius: 12, background: G.surface, border: `1px solid ${G.border}`, opacity: 0.6 }} />)}
        </div>
      </div>
    );
  }

  const paidTile = (p, title) => {
    const paid = p.amount > 0 || Object.keys(p.other || {}).length > 0;
    return (
      <div style={{ padding: '12px 14px', borderRadius: 12, background: G.surface, border: `1px solid ${G.border}`, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{title} · {cap(p.weekday)} {ddmm(p.date)}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: paid ? G.green : G.dim, fontFamily: mono, lineHeight: 1.1 }}>{paid ? fmtPayout(p.amount, p.currency) : '—'}</span>
          <span style={{ fontSize: 11, color: G.muted }}>{paid ? `${p.accounts.length} ${p.accounts.length === 1 ? 'cuenta' : 'cuentas'}` : 'sin pagos'}</span>
        </div>
        {others(p.other)}
        {p.notPaid.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {p.notPaid.map((n, i) => (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: G.red, background: G.redDim, border: `1px solid ${G.red}44`, borderRadius: 8, padding: '4px 8px', width: 'fit-content', maxWidth: '100%' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></svg>
                <span style={{ fontWeight: 700 }}>{n.status === 'failed' ? 'Fallido' : 'Cancelado'}</span>
                <span style={{ color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.gateway}</span>
                <span style={{ fontFamily: mono, fontWeight: 700, color: G.white, whiteSpace: 'nowrap' }}>{fmtPayout(n.amount, n.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sel = days.find(d => d.date === selected) || days[0];

  return (
    <div className="feature-panel" style={{ background: `linear-gradient(135deg, ${G.green}14 0%, ${G.card} 60%)`, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      {header}

      <div className="po-paid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        {paidTile(data.paidYesterday, 'Pagado ayer')}
        {paidTile(data.paidToday, 'Pagado hoy')}
      </div>

      <div className="po-days" style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8 }}>
        {days.map(d => {
          const active = sel && sel.date === d.date;
          const dimmed = d.amount <= 0 && !Object.keys(d.other || {}).length;
          return (
            <button key={d.date} type="button" onClick={() => setSelected(d.date)}
              style={{ textAlign: 'left', cursor: 'pointer', padding: '10px 12px', borderRadius: 12, background: active ? `${G.gold}12` : G.surface, border: `1px solid ${active ? G.gold + '88' : G.border}`, minWidth: 0, opacity: dimmed && !active ? 0.75 : 1, transition: 'border-color .15s, background .15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: active ? G.goldLight : G.dim, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' }}>{d.short} {ddmm(d.date)}</span>
                {d.label && <span style={{ fontSize: 9.5, fontWeight: 700, color: G.white, padding: '2px 7px', borderRadius: 20, background: `${G.white}14`, border: `1px solid ${G.white}22`, whiteSpace: 'nowrap' }}>{d.label}</span>}
              </div>
              <div className="po-day-amt" style={{ fontSize: 17, fontWeight: 800, color: dimmed ? G.muted : G.white, fontFamily: mono, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dimmed ? '—' : fmtPayout(d.amount, d.currency)}</div>
              {others(d.other)}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 7 }}>
                {chip(d.status, true)}
                {d.accounts.length > 0 && <span style={{ fontSize: 10, color: G.muted, whiteSpace: 'nowrap' }}>{d.accounts.length} {d.accounts.length === 1 ? 'cuenta' : 'cuentas'}</span>}
              </div>
            </button>
          );
        })}
      </div>

      {sel && (
        <div style={{ marginTop: 10, borderRadius: 12, background: G.surface, border: `1px solid ${G.border}`, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: sel.accounts.length ? 8 : 0 }}>
            <span style={{ fontSize: 11, color: G.dim }}><span style={{ color: G.white, fontWeight: 700 }}>{cap(sel.weekday)} {ddmm(sel.date)}</span>{sel.label ? ` · ${sel.label}` : ''} · por cuenta</span>
            {sel.accounts.length > 0 && <span style={{ fontSize: 13, fontWeight: 800, color: G.white, fontFamily: mono }}>{fmtPayout(sel.amount, sel.currency)}</span>}
          </div>
          {sel.accounts.length === 0 ? (
            <div style={{ fontSize: 12, color: G.muted }}>
              {sel.status === 'weekend' ? 'Stripe no hace pagos en fin de semana: lo que se libere pasa al siguiente día laborable.' : 'Sin saldo previsto para este día por ahora. Se actualiza según entran ventas.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sel.accounts.map((a, i) => (
                <div key={`${a.gateway}-${a.currency}-${i}`} className="po-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? `1px solid ${G.border}` : 'none' }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: G.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.gateway}</span>
                  {chip(a.status, true)}
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: G.white, fontFamily: mono, whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>{fmtPayout(a.amount, a.currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 10.5, color: G.muted, marginTop: 10, lineHeight: 1.5 }}>
        Importes netos: {data.negativeBalance < 0 ? `ya descontado el saldo negativo (${fmtPayout(data.negativeBalance, data.currency)}) que Stripe se cobra del siguiente pago. ` : 'sin saldo negativo pendiente. '}
        "Pagado" y "En camino" son pagos reales de Stripe; "Estimado" es lo que Stripe libera ese día más el saldo disponible actual (Stripe aún no ha creado ese pago: su "próximo pago" solo enseña el disponible de hoy, por eso puede salir en negativo).
        {data.failedAccounts > 0 ? ` ${data.failedAccounts === 1 ? '1 cuenta no ha respondido y no está incluida.' : `${data.failedAccounts} cuentas no han respondido y no están incluidas.`}` : ''}
        {Array.isArray(data.pausedAccounts) && data.pausedAccounts.length > 0 ? ` Pagos pausados en Stripe (no se estiman): ${data.pausedAccounts.map(p => p.gateway).join(', ')}.` : ''}
      </div>
    </div>
  );
}

// CPA por tienda — solo aparece cuando el tenant tiene 2+ tiendas activas.
// Datos de kpis.cpaByShop (ya calculados en el backend para el rango elegido).
function CpaByShopPanel({ shops }) {
  if (!Array.isArray(shops) || shops.length < 2) return null;
  const cpaColor = (c) => c == null ? G.muted : c <= 30 ? '#4ade80' : c <= 50 ? G.gold : '#f87171';
  return (
    <div className="feature-panel" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: `${G.blue}22`, color: G.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M4 9h16" /><path d="M9 20v-6h6v6" /></svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: G.white }}>CPA por tienda</div>
          <div style={{ fontSize: 11, color: G.muted }}>Coste por venta · gasto en ads ÷ ventas del periodo</div>
        </div>
      </div>
      <div className="cpa-shop-grid">
        {shops.map((s) => (
          <div key={s.shopId} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: G.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.shopName}>{s.shopName}</div>
            <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'DM Mono, monospace', color: cpaColor(s.cpa), marginTop: 6, lineHeight: 1.1 }}>{s.cpa == null ? '—' : fmtMoney(s.cpa)}</div>
            <div style={{ fontSize: 10.5, color: G.muted, marginTop: 4 }}>{s.orders} venta{s.orders === 1 ? '' : 's'} · {fmtMoney(s.adSpend)} ads</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UnitsSoldPanel({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('limit', '20');
    fetch(`${BACKEND}/api/dashboard/top-products?${params.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json()).then(d => setData(d || { products: [] })).catch(() => setData({ products: [] }));
  }, [from, to]);
  const products = (data && data.products) || [];
  const totalQty = (data && data.totalQuantity) || products.reduce((s, p) => s + (p.quantitySold || 0), 0);
  const maxQty = Math.max(1, ...products.map(p => p.quantitySold || 0));
  const medalBg = (rank) => rank === 1 ? '#F5C842' : rank === 2 ? '#B8C0CC' : rank === 3 ? '#CD7F32' : G.border;
  const medalFg = (rank) => rank <= 3 ? '#1a1520' : G.muted;
  return (
    <div className="feature-panel" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: '#22D3EE22', color: '#22D3EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📦</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: G.white }}>Unidades por producto</div>
            <div style={{ fontSize: 11, color: G.muted }}>Total de unidades por producto en el rango</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Total</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace' }}>{totalQty}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {!data && <div style={{ fontSize: 12, color: G.muted, padding: 20, textAlign: 'center' }}>Cargando…</div>}
        {data && products.length === 0 && <div style={{ fontSize: 13, color: G.muted, padding: 20, textAlign: 'center' }}>Sin ventas de productos en el rango</div>}
        {products.map(p => (
          <div key={p.key} className="unit-row" style={{ display: 'flex', alignItems: 'center', gap: 12, background: G.bg || G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '10px 14px' }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: medalBg(p.rank), color: medalFg(p.rank), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0, fontFamily: 'DM Mono, monospace' }}>{p.rank}</div>
            <div style={{ width: 46, height: 46, borderRadius: 8, background: G.border, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {p.image ? <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} /> : <span style={{ fontSize: 18 }}>📦</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: G.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 6 }}>{decodeEntities(p.title)}</div>
              <div style={{ height: 6, background: G.border, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(4, (p.quantitySold / maxQty) * 100)}%`, height: '100%', background: 'linear-gradient(90deg, #7C5CFF 0%, #22D3EE 100%)', borderRadius: 4 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{p.quantitySold}</div>
              <div style={{ fontSize: 9, color: G.muted }}>u.</div>
            </div>
            <div style={{ minWidth: 84, textAlign: 'right', flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>{fmt(p.revenueEUR || 0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Panel C — Revenue over time (2-line chart + KPIs + gamified badges)
function RevenueOverTimePanel({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    fetch(`${BACKEND}/api/dashboard/revenue-timeline?${params.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json()).then(d => setData(d || {})).catch(() => setData({ points: [] }));
  }, [from, to]);
  const points = (data && data.points) || [];
  const sales = (data && data.sales) || 0;
  const salesPrev = (data && data.salesPrev) || 0;
  const rebills = (data && data.rebills) || 0;
  const rebillRevenue = (data && data.rebillRevenue) || 0;
  const peak = (data && data.peak) || { label: '', value: 0 };
  const totalRev = points.reduce((s, p) => s + (p.revenue || 0), 0);
  const badges = [
    { label: 'First sale', earned: sales >= 1 },
    { label: '10 sales', earned: sales >= 10 },
    { label: '100 sales', earned: sales >= 100 },
    { label: 'Beat previous', earned: sales > salesPrev && salesPrev > 0 },
    { label: 'Converter', earned: sales > 0 },
    { label: 'Recurring', earned: rebills > 0 },
    { label: '$1K club', earned: totalRev >= 1000 },
  ];
  return (
    <div style={{ marginTop: 20 }}>
      {/* KPI cards */}
      <div className="panel-kpis" style={{ marginBottom: 12 }}>
        <StatCard emoji="🛒" color={G.green} label="Sales" value={sales} />
        <StatCard emoji="🕐" color={G.dim} label="Previous period" value={salesPrev} />
        <StatCard emoji="🔁" color="#8b5cf6" label="Rebills" value={rebills} />
        <StatCard emoji="🪙" color={G.orange} label="Revenue rebill" value={fmt(rebillRevenue)} />
      </div>
      {/* Badges */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
        {badges.map(b => (
          <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 16, fontSize: 10.5, fontWeight: 600, border: `1px solid ${b.earned ? G.orange + '55' : G.border}`, background: b.earned ? `${G.orange}18` : 'transparent', color: b.earned ? G.orange : G.muted }}>
            {b.earned ? '🏆' : '🔒'} {b.label}
          </span>
        ))}
      </div>
      {/* Chart card */}
      <div className="feature-panel" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: G.white, textTransform: 'uppercase', letterSpacing: 1 }}>Revenue over time</div>
            {peak && peak.value > 0 && <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>Pico {fmt(peak.value)} a las {peak.label}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 10.5 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: G.dim }}><span style={{ width: 16, height: 2.5, background: G.blue, borderRadius: 2, display: 'inline-block' }} />Revenue</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: G.muted }}><span style={{ width: 16, height: 0, borderTop: `2px dashed ${G.dim}`, display: 'inline-block' }} />Revenue prev.</span>
          </div>
        </div>
        {!data && <div style={{ fontSize: 12, color: G.muted, padding: 30, textAlign: 'center' }}>Cargando…</div>}
        {data && <RevenueTimeChart points={points} height={260} />}
      </div>
    </div>
  );
}

// Panel D — Clientes con quejas (early fraud warnings + disputas de las pasarelas)
function ComplaintsPanel({ showToast }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState('');

  const load = () => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/complaints`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => { setLoadError(false); setData(d && d.complaints ? d : { complaints: [], flagged: 0 }); })
      .catch(() => { setLoadError(true); setData({ complaints: [], flagged: 0 }); });
  };
  useEffect(load, []);

  const act = async (key, path, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND}/api/complaints${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      if (showToast) showToast(key.startsWith('refund') ? 'Reembolso procesado' : key.startsWith('exclude') ? 'Cliente fuera de rebills' : 'Hecho', 'success');
      load();
    } catch (e) {
      if (showToast) showToast(e.message, 'error');
    } finally {
      setBusy('');
    }
  };

  const all = (data && data.complaints) || [];
  const rows = showAll ? all : all.filter(c => !c.seen);
  const flagged = (data && data.flagged) || 0;
  // Total reembolsable DEDUPE por email: dos quejas del mismo cliente no
  // suman dos veces (el backend reembolsa por email, una vez).
  const uniqueEmails = new Map();
  rows.forEach(c => { if (c.email && !uniqueEmails.has(c.email)) uniqueEmails.set(c.email, c.refundableEur || 0); });
  const totalRefundable = Array.from(uniqueEmails.values()).reduce((s, v) => s + v, 0);
  const rowIds = rows.map(c => c.id);

  const pillBtn = (disabled) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 18,
    border: `1px solid ${G.border}`, background: 'transparent', color: disabled ? G.muted : G.dim,
    fontSize: 11, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
    whiteSpace: 'nowrap',
  });

  return (
    <div className="feature-panel" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 20, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: `${G.orange}22`, color: G.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🚩</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: G.white }}>Clientes con quejas</div>
            <div style={{ fontSize: 11, color: G.muted }}>Disputas y alertas de fraude recibidas de las pasarelas</div>
          </div>
        </div>
        <div style={{ padding: '8px 18px', borderRadius: 22, border: `1.5px solid ${G.orange}55`, background: `${G.orange}12`, textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{flagged}</div>
          <div style={{ fontSize: 8.5, color: G.orange, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>Flagged</div>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button style={pillBtn(busy !== '')} disabled={busy !== ''}
            onClick={() => act('refund-all', '/bulk', { action: 'refund', ids: rowIds }, `Reembolsar TODOS los pagos de ${uniqueEmails.size} cliente(s) visibles (${fmt(totalRefundable)}). Esta acción devuelve el dinero y no se puede deshacer. ¿Continuar?`)}>
            ↺ Reembolsar todo
          </button>
          <button style={pillBtn(busy !== '')} disabled={busy !== ''}
            onClick={() => act('exclude-all', '/bulk', { action: 'exclude', ids: rowIds }, `Excluir a los ${uniqueEmails.size} cliente(s) visibles de futuros rebills (cancela también sus suscripciones Stripe activas). ¿Continuar?`)}>
            ⊘ Excluir todo
          </button>
          <button style={pillBtn(busy !== '')} disabled={busy !== ''}
            onClick={() => act('seen-all', '/bulk', { action: 'seen', ids: rowIds })}>
            ✓ Marcar vistos
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {!data && <div style={{ fontSize: 12, color: G.muted, padding: 20, textAlign: 'center' }}>Cargando…</div>}
        {data && loadError && (
          <div style={{ fontSize: 13, color: G.red, padding: 20, textAlign: 'center' }}>
            Error cargando quejas — <button style={{ background: 'transparent', border: 'none', color: G.blue, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }} onClick={load}>reintentar</button>
          </div>
        )}
        {data && !loadError && rows.length === 0 && (
          <div style={{ fontSize: 13, color: G.muted, padding: 20, textAlign: 'center' }}>
            {all.length > 0 ? 'Sin quejas nuevas — todo visto' : 'Sin quejas ni alertas de las pasarelas'}
          </div>
        )}
        {rows.map(c => {
          const dt = fmtDayTime(c.flaggedAt);
          return (
            <div key={c.id} className="complaint-row" style={{ display: 'flex', alignItems: 'center', gap: 12, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '11px 14px', flexWrap: 'wrap', opacity: c.seen ? 0.6 : 1 }}>
              <span style={{ padding: '3px 9px', borderRadius: 7, background: `${G.orange}18`, color: G.orange, border: `1px solid ${G.orange}40`, fontSize: 9.5, fontWeight: 800, letterSpacing: 1, flexShrink: 0 }}>
                {c.kind === 'dispute' ? 'DISPUTA' : 'WARNING'}
              </span>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: G.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email || '(sin email)'}</div>
                <div style={{ fontSize: 10.5, color: G.muted, marginTop: 2 }}>
                  {c.fraudType}{dt.day !== '--' ? ` · ${dt.day} ${dt.time}` : ''}{c.country ? ` · ${c.country}` : ''}{c.gatewayName ? ` · ${c.gatewayName}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{fmt(c.collectedEur || 0)}</div>
                <div style={{ fontSize: 9, color: G.muted }}>cobrado</div>
              </div>
              <div style={{ display: 'flex', gap: 7, flexShrink: 0, alignItems: 'center' }}>
                <button style={pillBtn(c.excluded || busy !== '')} disabled={c.excluded || busy !== ''}
                  onClick={() => act(`exclude-${c.id}`, `/${c.id}/exclude`, null, `Excluir ${c.email} de futuros rebills (cancela también sus suscripciones Stripe activas). ¿Continuar?`)}>
                  ⊘ {c.excluded ? 'Fuera de rebills' : 'Excluir de rebills'}
                </button>
                {c.refundableEur > 0 ? (
                  <button style={pillBtn(busy !== '')} disabled={busy !== ''}
                    onClick={() => act(`refund-${c.id}`, `/${c.id}/refund`, null, `Reembolsar ${fmt(c.refundableEur)} a ${c.email}. Esta acción devuelve el dinero y no se puede deshacer. ¿Continuar?`)}>
                    ↺ Reembolsar {fmt(c.refundableEur)}
                  </button>
                ) : (
                  <span style={{ ...pillBtn(true), border: `1px solid ${G.green}30`, color: G.green }}>
                    {c.refundedAt || c.chargeRefunded ? '✓ Reembolsado' : 'Sin cargos'}
                  </span>
                )}
                <button title={c.seen ? 'Marcar como no visto' : 'Marcar como visto'} style={{ width: 30, height: 30, borderRadius: '50%', border: `1px solid ${c.seen ? G.green + '55' : G.border}`, background: c.seen ? `${G.green}15` : 'transparent', color: c.seen ? G.green : G.dim, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}
                  disabled={busy !== ''}
                  onClick={() => act(`seen-${c.id}`, `/${c.id}/seen`, { seen: !c.seen })}>
                  ✓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {all.some(c => c.seen) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button style={{ background: 'transparent', border: 'none', color: G.dim, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => setShowAll(s => !s)}>
            👥 {showAll ? 'Ocultar vistos' : 'Ver todos los clientes'}
          </button>
        </div>
      )}
    </div>
  );
}

function Dashboard({ showToast, onNavigate, isAdmin }) {
  const [kpis, setKpis] = useState(null);
  const [range, setRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [hasGateways, setHasGateways] = useState(true);
  const [chartRange, setChartRange] = useState('7d');
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // All ranges computed in Madrid timezone (Europe/Madrid). Midnight 00:00 = day cutoff.
  const getDateRange = useCallback((r) => {
    const to = madridTodayStr();
    let from;
    if (r === 'today') { from = to; }
    else if (r === 'yesterday') {
      const y = new Date(); y.setDate(y.getDate() - 1);
      const ys = y.toLocaleDateString('sv-SE', { timeZone: APP_TZ });
      return { from: ys, to: ys };
    }
    else if (r === '7d') { const d = new Date(); d.setDate(d.getDate() - 7); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); }
    else if (r === '30d') { const d = new Date(); d.setDate(d.getDate() - 30); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); }
    else if (r === '90d') { const d = new Date(); d.setDate(d.getDate() - 90); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); }
    else if (r === 'year') { const d = new Date(); d.setFullYear(d.getFullYear() - 1); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); }
    else if (r === 'last_month') {
      const now = new Date();
      const firstOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastOfLast = new Date(now.getFullYear(), now.getMonth(), 0);
      from = firstOfLast.toLocaleDateString('sv-SE', { timeZone: APP_TZ });
      return { from, to: lastOfLast.toLocaleDateString('sv-SE', { timeZone: APP_TZ }) };
    }
    else if (r === 'custom') { return { from: customFrom, to: customTo || customFrom || to }; }
    else { from = ''; }
    return { from, to };
  }, [customFrom, customTo]);

  const loadKpis = useCallback(() => {
    const { from, to } = getDateRange(range);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const url = `${BACKEND}/api/dashboard/kpis?${params.toString()}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => { if (r.status === 401) { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.reload(); return; } return r.json(); }).then(d => d && setKpis(d)).catch(() => setKpis({ totalRevenue:'0', todayRevenue:'0', todaySales:0, activeSubscriptions:0, conversionRate:'0', totalPayments:0, succeededPayments:0, funnel:{checkouts:0,initiated:0,completed:0}, chartData:[] }));
  }, [range, getDateRange]);

  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { gatewaysApi.getAll().then(g => setHasGateways(g.length > 0)).catch(() => {}); }, []);

  // Backend now pulls today's Meta spend LIVE inside /dashboard/kpis
  // (5-min cached). No need for frontend to trigger a separate sync.

  if (!kpis) return (
    <div className="content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ textAlign: 'center', color: G.muted }}>
        <div style={{ fontSize: 24, marginBottom: 8, animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>◌</div>
        <div style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', letterSpacing: '0.1em' }}>CARGANDO DATOS...</div>
      </div>
    </div>
  );

  const rangeLabels = { today: 'Hoy', yesterday: 'Ayer', '7d': '7 dias', '30d': '30 dias', '90d': '90 dias', year: 'Ultimo año', last_month: 'Mes pasado', custom: 'Personalizado' };
  const { from: panelFrom, to: panelTo } = getDateRange(range);
  const chartBars = (kpis.chartData || []).map(d => d.ok || 0);
  const funnel = kpis.funnel || { visits: 0, checkouts: 0, initiated: 0, submitted: 0, completed: 0, abandoned: 0 };
  const funnelBase = funnel.visits || funnel.checkouts || 1; // denominator for percentages
  const funnelPctCheckouts = funnel.visits ? ((funnel.checkouts / funnel.visits) * 100).toFixed(1) : '100';
  const funnelPctInit = funnelBase ? ((funnel.initiated / funnelBase) * 100).toFixed(1) : '0';
  const funnelPctSubmit = funnelBase ? (((funnel.submitted || funnel.initiated) / funnelBase) * 100).toFixed(1) : '0';
  const funnelPctDone = funnelBase ? ((funnel.completed / funnelBase) * 100).toFixed(1) : '0';
  const subsByStatus = kpis.subscriptionsByStatus || { trial: 0, active: 0, failed: 0, cancelled: 0, paused: 0, total: 0 };
  const newSubsToday = kpis.newSubscriptionsToday || 0;

  return (
    <div className="content">
      {/* Date range selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {Object.entries(rangeLabels).filter(([k]) => k !== 'custom').map(([k, v]) => (
          <button key={k} className={`pill ${range === k ? 'active' : ''}`} onClick={() => setRange(k)}>{v}</button>
        ))}
        <button className={`pill ${range === 'custom' ? 'active' : ''}`} onClick={() => setRange('custom')}>Personalizado</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
          {/* 2026-06-07 — neutral label until Deploy F adds the timezone
              selector (Settings → Preferencias). The actual TZ is the
              backend default (America/New_York) for now. */}
          Horario configurado en Settings · corte diario 00:00
        </div>
        {/* 2026-06-07 — Diagnostics toolbox is for the SaaS operator only.
            Surfaces cron status, force-fires, internal cache reload — none
            of which a merchant client should be poking. Gate by role. */}
        {isAdmin && (
          <button
            className="btn btn-ghost"
            style={{ fontWeight: 600, fontSize: 11, padding: '4px 10px' }}
            onClick={() => setShowDiagnostics(v => !v)}
          >
            {showDiagnostics ? '▾' : '▸'} 🔧 Diagnóstico
          </button>
        )}
      </div>

      {/* ── Profit countdown ──────────────────────────────────────────
          2026-06-11 — Antes el widget estaba oculto si profitGoal=0.
          Operator esperaba verlo siempre como "Camino al Millón" por
          defecto. Ahora: si el tenant no configura meta en Settings,
          usamos 1.000.000 € (clásico). Quien quiera ocultarlo guarda
          un valor < 0 (UI no expone esa opción, suficiente con poner 0
          en Settings = mostrar "Camino al Millón" por defecto). */}
      {(() => {
        const goal = (kpis.profitGoal && kpis.profitGoal > 0) ? kpis.profitGoal : 1_000_000;
        const profit = kpis.totalProfit || 0;
        // ¿En qué tramo del goal estamos? (1er, 2º, 3er …)
        const milestonesDone = Math.floor(Math.max(0, profit) / goal);
        const nextMilestone = (milestonesDone + 1) * goal;
        const remaining = Math.max(0, nextMilestone - profit);
        const pct = Math.max(0, Math.min(100, (profit - milestonesDone * goal) / goal * 100));
        const avg = kpis.last3DaysAvgProfit || 0;
        const daysLeft = avg > 0 ? Math.ceil(remaining / avg) : null;

        // 2026-06-10 — moneda dinámica del tenant (no más $ hardcoded).
        const cur = (kpis.displayCurrency || 'EUR').toUpperCase();
        const symMap = { EUR: '€', USD: '$', GBP: '£' };
        const locale = cur === 'EUR' ? 'es-ES' : cur === 'GBP' ? 'en-GB' : 'en-US';
        const sym = symMap[cur] || cur + ' ';
        const fmtBig = (n) => sym + Math.round(n).toLocaleString(locale);

        // 2026-06-10 — título dinámico. Si el tenant ha puesto su propio
        // customGoalTitle, se usa tal cual. Si no, se genera según el goal
        // ("Camino a los 100K", "Camino al Millón", "Camino a 5M", etc.).
        const autoTitle = (() => {
          if (goal >= 1_000_000) {
            const m = Math.round(goal / 1_000_000);
            if (m === 1) return 'Camino al Millón de Beneficio';
            return `Camino a los ${m}M de Beneficio`;
          }
          const k = Math.round(goal / 1000);
          return k > 0 ? `Camino a los ${k}K de Beneficio` : 'Camino al objetivo';
        })();
        const title = (typeof kpis.customGoalTitle === 'string' && kpis.customGoalTitle.trim())
          ? kpis.customGoalTitle.trim()
          : autoTitle;

        return (
          <div style={{
            background: `linear-gradient(135deg, ${G.gold}10 0%, ${G.surface} 70%)`,
            border: `1px solid ${G.gold}50`,
            borderRadius: 14,
            padding: 18,
            marginBottom: 18,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: `radial-gradient(circle, ${G.gold}25 0%, transparent 70%)`, borderRadius: '50%', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
                  {title}
                </div>
                <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>
                  Beneficio últimos 30 días: <strong style={{ color: G.green }}>{fmtBig(profit)}</strong>
                  {milestonesDone > 0 && (
                    <span style={{ color: G.gold, marginLeft: 6 }}>
                      · 🏆 {milestonesDone} {milestonesDone === 1 ? 'objetivo alcanzado' : 'objetivos alcanzados'}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Faltan</div>
                <div style={{ fontSize: 'clamp(22px, 5vw, 32px)', fontWeight: 800, color: G.gold, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>
                  {fmtBig(remaining)}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ background: G.black, borderRadius: 999, height: 10, overflow: 'hidden', border: `1px solid ${G.border}`, marginBottom: 10 }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${G.gold} 0%, ${G.goldLight} 100%)`,
                transition: 'width 0.6s ease',
                boxShadow: `0 0 8px ${G.gold}80`,
              }} />
            </div>

            {/* Bottom stats */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 12 }}>
              <div style={{ color: G.muted }}>
                <strong style={{ color: G.white, fontFamily: 'DM Mono, monospace', fontSize: 14 }}>{pct.toFixed(1)}%</strong>
                <span style={{ marginLeft: 6 }}>de {fmtBig(goal)}</span>
              </div>
              <div style={{ color: G.muted, textAlign: 'right' }}>
                {avg > 0 ? (
                  <>
                    Media Beneficio Diario: <strong style={{ color: avg >= 0 ? G.green : G.red, fontFamily: 'DM Mono, monospace' }}>{fmtBig(avg)}/día</strong>
                    {daysLeft !== null && (
                      <span style={{ marginLeft: 8, color: G.gold, fontWeight: 700 }}>
                        · {daysLeft.toLocaleString()} día{daysLeft === 1 ? '' : 's'} restantes
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ color: G.dim, fontStyle: 'italic' }}>Sin datos de 3 días para estimar</span>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Diagnostic buttons — collapsed by default, useful when something
          doesn't cuadra. Operator opens this section manually. */}
      {showDiagnostics && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap', padding: 10, background: G.card, border: `1px solid ${G.border}`, borderRadius: 8 }}>
        <span style={{ fontSize: 10, color: G.muted, marginRight: 6 }}>Auditar / Cron:</span>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 11, padding: '4px 10px' }} title="Muestra exactamente cuántas ventas tienes en la BD, por día y por source. Útil si el dashboard no cuadra." onClick={async () => {
          showToast('Auditando ventas...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/dashboard/sales-audit`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const t = d.totals || {};
            const bs = d.bySource || {};
            const drs = d.dailyRows || [];
            const bySrcLines = Object.entries(bs).map(([src, v]) =>
              `  ${src}: ${v.count} pagos · $${v.totalAmount.toFixed(2)}${v.phantomCount ? ` (${v.phantomCount} phantom)` : ''}`
            ).join('\n');
            const fbs = d.failedBySource || {};
            const failedBySrcLines = Object.entries(fbs).map(([src, v]) =>
              `  ${src}: ${v.count} fallos`
            ).join('\n');
            const tfr = d.topFailureReasons || [];
            const topFailLines = tfr.map(r => `  [${r.count}x] ${r.error}`).join('\n');
            const dailyLines = drs.slice(0, 15).map(r =>
              `  ${r.day}: ${r.count} ventas · $${r.totalAmount.toFixed(2)} · ${Object.entries(r.sources).map(([s, n]) => `${s}=${n}`).join(', ')}`
            ).join('\n');
            const orph = d.orphans || { count: 0 };
            const cross = d.crossUserSuspected || { count: 0 };
            const msg = `AUDITORÍA DE VENTAS (${d.tz})\n\n`
              + `📊 TOTALES:\n`
              + `  · SUCCEEDED: ${t.succeeded}\n`
              + `  · FAILED: ${t.failed}\n`
              + `  · REFUNDED: ${t.refunded}\n`
              + `  · PENDING: ${t.pending}\n`
              + `  · Revenue total: $${t.totalRevenue}\n\n`
              + `📦 POR SOURCE (solo SUCCEEDED):\n${bySrcLines || '  (vacío)'}\n\n`
              + `❌ FAILED POR SOURCE:\n${failedBySrcLines || '  (ninguno)'}\n\n`
              + `🔥 TOP 5 RAZONES DE FALLO:\n${topFailLines || '  (ninguno)'}\n\n`
              + `📅 ÚLTIMOS DÍAS (máx 15):\n${dailyLines || '  (vacío)'}\n\n`
              + (orph.count > 0 ? `⚠️ ${orph.count} HUÉRFANOS (gatewayId no existe) — no aparecen en dashboard\n\n` : '')
              + (cross.count > 0 ? `⚠️ ${cross.count} CROSS-USER (email tuyo, gateway de otro user) — invisibles\n` : '');
            window.alert(msg);
            console.log('[Sales Audit]', d);
            showToast(`Auditoría completa: ${t.succeeded} ventas · $${t.totalRevenue} · ver consola`, 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🔍 Auditar ventas</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 11, padding: '4px 10px' }} title="Desglosa el profit de hoy: revenue, fees, COGS, adSpend — para ver por qué no cuadra con Beneficios" onClick={async () => {
          showToast('Auditando profit de hoy...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/dashboard/profit-audit`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const s = d.summary || {};
            const cc = d.crossCheck || {};
            const ads = d.adSpendBreakdown || {};
            const cogsD = d.cogsDiagnostic || {};
            const feeD = d.feeDiagnostic || {};
            const msg = `AUDITORÍA DE PROFIT — ${d.day} (${d.tz})\n\n`
              + `🧮 FÓRMULA:\n  ${d.formula}\n\n`
              + `💰 SUMMARY:\n`
              + `  · Orders: ${s.orders}\n`
              + `  · Gross Revenue: $${s.grossRevenue}\n`
              + `  · Refunds: -$${s.refunds}\n`
              + `  · Net Revenue: $${s.netRevenue}\n`
              + `  · Fees (gateway): $${s.fees}\n`
              + `  · Fees Returned: $${s.feesReturned}\n`
              + `  · Net Fees: $${s.netFees}\n`
              + `  · COGS: $${s.cogs}\n`
              + `  · Ad Spend: $${s.adSpend}\n`
              + `  · ═════════════\n`
              + `  · PROFIT: $${s.profit}\n\n`
              + `🔬 CROSS-CHECK:\n`
              + `  · Suma directa (Dashboard ${cc.dashboardTz}): $${cc.naiveRevenue} (${cc.naiveOrders} orders)\n`
              + `  · computePnL en Dashboard TZ (${cc.dashboardTz}): $${cc.computePnlRevenue} (${cc.computePnlOrders} orders)\n`
              + `  · computePnL en BENEFICIOS TZ (${cc.beneficiosTz}): $${cc.beneficiosRevenue} (${cc.beneficiosOrders} orders)\n`
              + `  · Diff Dashboard vs Beneficios: $${cc.diffDashboardVsBeneficios}\n`
              + `  · ${cc.hint}\n\n`
              + `📣 AD SPEND:\n`
              + `  · Meta: $${ads.meta}, Google: $${ads.google}, Manual: $${ads.manual}, CSV: $${ads.csv}\n`
              + `  · Rows encontrados: ${ads.rowsFound}\n`
              + `  · ${ads.hint}\n\n`
              + `📦 COGS:\n`
              + `  · Productos configurados: ${cogsD.productCostsConfigured}\n`
              + `  · Items matcheados: ${cogsD.itemsMatched}\n`
              + `  · Items sin coste: ${cogsD.itemsUnmatched}\n`
              + `  · ${cogsD.hint}\n\n`
              + `💳 GATEWAYS:\n`
              + `  · Con fees custom: ${feeD.gatewaysWithFeeOverride?.length || 0}\n`
              + `  · Con fees default: ${feeD.gatewaysUsingDefaults?.length || 0}`;
            console.log('[Profit Audit]', d);
            window.alert(msg);
            showToast(`Profit: $${s.profit} · ver consola para detalle`, 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>📊 Auditar profit de hoy</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 11, padding: '4px 10px' }} title="Comprueba si el cron de cobros automáticos está corriendo y qué RCs están vencidos" onClick={async () => {
          showToast('Comprobando cron...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/dashboard/cron-status`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const last = d.lastRecurring;
            const lastLine = last
              ? `${last.email} · $${(last.amount/100).toFixed(2)} · hace ${last.ageHours}h (${last.createdAt})`
              : '(nunca)';
            const upcoming = (d.upcomingSample || []).slice(0, 5).map(r =>
              `  · ${r.email} (${r.status}) — en ${r.hoursFromNow}h ($${(r.amount/100).toFixed(2)})`
            ).join('\n');
            const due = (d.dueSample || []).slice(0, 10).map(r =>
              `  · ${r.email} (${r.status}) — vencido hace ${r.hoursOverdue}h ($${(r.amount/100).toFixed(2)}) ${r.hasVault ? '[vault]' : '[no-vault]'}`
            ).join('\n');
            const dd = d.dueDetail || {};
            const msg = `ESTADO DEL CRON DE COBROS (${d.nowIso})\n\n`
              + `📊 RCs cobrables: ${d.chargeable}\n`
              + `⏰ Vencidos ahora: ${d.dueNow}\n`
              + `  · Vencidos hace >24h: ${dd.over24h}\n`
              + `  · Vencidos hace >1h: ${dd.over1h}\n`
              + `  · Vencidos hace <1h: ${dd.underneath1h}\n\n`
              + `🕐 Último cobro automático:\n  ${lastLine}\n\n`
              + `${d.hint}\n\n`
              + (due ? `⚠️ VENCIDOS (máx 10):\n${due}\n\n` : '')
              + (upcoming ? `📅 PRÓXIMOS 5:\n${upcoming}\n` : '');
            console.log('[Cron Status]', d);
            window.alert(msg);
            if (d.cronLikelyStuck) {
              showToast('⚠️ Cron atascado — ve a Suscripciones y pulsa "Procesar atrasados"', 'error');
            } else {
              showToast(`Cron OK · ${d.dueNow} due · ${d.chargeable} total`, 'success');
            }
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>⏰ Estado del cron</button>
        <button className="btn btn-gold" style={{ fontWeight: 700, fontSize: 11, padding: '4px 10px' }} title="Fuerza al cron a correr AHORA (cobra los RCs vencidos) sin esperar a los 15 min del intervalo" onClick={async () => {
          if (!window.confirm('Forzar el cron a cobrar TODOS los RCs vencidos YA?\n\nEl cron normalmente corre cada 15 min. Este botón lo dispara inmediatamente.\n\n¿Continuar?')) return;
          showToast('Disparando cron...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/dashboard/force-cron-run`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const ageTxt = d.cronLastRunAgeMin !== null ? `hace ${d.cronLastRunAgeMin} min` : 'nunca';
            const msg = `✅ Cron disparado\n\n`
              + `· Último run antes: ${ageTxt}\n`
              + `· Tus RCs vencidos: ${d.dueForThisUser}\n\n`
              + `${d.note}\n\n`
              + `Espera 1-2 min y vuelve a pulsar "⏰ Estado del cron" para ver los cobros procesados.`;
            window.alert(msg);
            showToast(`Cron disparado · ${d.dueForThisUser} RCs tuyos vencidos`, 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>⚡ Forzar cron ahora</button>
      </div>
      )}
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '6px 10px', background: G.card, border: `1px solid ${G.border}`, borderRadius: 6, color: G.white, fontSize: 12 }} />
          <span style={{ color: G.dim, fontSize: 12 }}>hasta</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: '6px 10px', background: G.card, border: `1px solid ${G.border}`, borderRadius: 6, color: G.white, fontSize: 12 }} />
          <button className="btn btn-gold btn-sm" onClick={loadKpis}>Aplicar</button>
        </div>
      )}

      <div className="kpi-grid" style={{ marginBottom: 8 }}>
        {(() => {
          // 2026-06-07 — Make the headline KPI cards follow the date-range
          // selector. Before, 'Profit hoy' and 'Ad Spend hoy' stayed glued
          // to today no matter which pill the operator picked, which was
          // confusing — Revenue/CPA already updated but the first two
          // didn't. Now they read range-scoped values for any non-today
          // selection and fall back to today's values when the field
          // isn't present in the payload.
          const rangeLbl = rangeLabels[range] || range;
          const rangeLblLower = String(rangeLbl).toLowerCase();
          const isToday = range === 'today';
          // 2026-09-05 — La tarjeta "Profit <rango>" usaba kpis.totalProfit,
          // que es el ACUMULADO de 30 días (widget "Camino al Millón"), NO el
          // del rango → mostraba p.ej. 1531€ como "Profit ayer" cuando el
          // revenue de ayer era 921€ (imposible). El campo correcto del rango
          // es kpis.rangeProfit (= rangePL.profit), igual que rangeAdSpend.
          const profitVal = isToday
            ? (kpis.todayProfit || 0)
            : (kpis.rangeProfit != null ? kpis.rangeProfit : (kpis.todayProfit || 0));
          const adSpendVal = isToday
            ? (kpis.todayAdSpend || 0)
            : (kpis.rangeAdSpend != null ? kpis.rangeAdSpend : (kpis.todayAdSpend || 0));
          return [
            { label: `Profit ${rangeLblLower}`,   value: fmt(profitVal),  delta: 'neto despues de costes', up: profitVal >= 0, icon: '◆' },
            { label: `Ad Spend ${rangeLblLower}`, value: fmt(adSpendVal), delta: 'gasto publicitario',     up: true,            icon: '▲' },
            { label: 'Revenue', value: fmt(kpis.totalRevenue || 0), delta: rangeLbl, up: true, icon: '◈' },
            {
              label: 'ROAS',
              value: kpis.roas > 0 ? `${kpis.roas.toFixed(2)}x` : '—',
              delta: kpis.rangeAdSpend > 0
                ? `Revenue ${fmt(kpis.rangeRevenue)} / Ads ${fmt(kpis.rangeAdSpend)}`
                : 'Sin ad spend en el periodo',
              up: kpis.roas >= 1.5,
              icon: '📈',
            },
          ];
        })().map((k, i) => (
          <div key={i} className="kpi-card">
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value mono" style={
              k.label === 'Profit hoy' ? { color: (kpis.todayProfit || 0) >= 0 ? '#4ade80' : '#f87171' } :
              k.label === 'ROAS' ? { color: kpis.roas >= 1.5 ? '#4ade80' : kpis.roas >= 1 ? G.gold : '#f87171' } :
              {}}>{k.value}</div>
            <div className={`kpi-delta ${k.up ? 'delta-up' : 'delta-down'}`}>{k.delta}</div>
          </div>
        ))}
      </div>
      <div className="kpi-grid">
        {[
          // CPA moved here from the grid above (operator request 2026-05-13).
          // CPA  = adSpend / new customers in range (lower = better).
          {
            label: 'CPA',
            value: kpis.cpa > 0 ? fmt(kpis.cpa) : '—',
            delta: kpis.newCustomersInRange > 0
              ? `${kpis.newCustomersInRange} clientes nuevos · ${rangeLabels[range] || range}`
              : `${kpis.rangeOrders || 0} pedidos · ${rangeLabels[range] || range}`,
            up: kpis.cpa > 0 && kpis.cpa <= 30,
            icon: '👥',
          },
          { label: 'Pagos completados', value: kpis.succeededPayments || 0, delta: `de ${kpis.totalPayments || 0} intentos`, up: true, icon: '◎' },
          ...(kpis.autoSubscriptionEnabled ? [{ label: 'Nuevas suscripciones', value: newSubsToday, delta: 'hoy', up: true, icon: '↺' }] : [{ label: 'Clientes unicos', value: kpis.uniqueCustomers || 0, delta: 'en el periodo', up: true, icon: '◎' }]),
          { label: 'Conv. Rate (real)', value: `${kpis.conversionRate || 0}%`, delta: funnel.visits ? `${funnel.completed || 0}/${funnel.visits} visitas` : `${funnel.completed || 0}/${funnel.checkouts || 0} checkouts`, up: parseFloat(kpis.conversionRate || 0) > 5, icon: '◉' },
        ].map((k, i) => (
          <div key={i} className="kpi-card">
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value mono" style={
              k.label === 'CPA' && kpis.cpa > 0 ? { color: kpis.cpa <= 30 ? '#4ade80' : kpis.cpa <= 50 ? G.gold : '#f87171' } :
              {}}>{k.value}</div>
            <div className={`kpi-delta ${k.up ? 'delta-up' : 'delta-down'}`}>{k.delta}</div>
          </div>
        ))}
      </div>

      {/* Subscription status breakdown — ALWAYS visible, with on/off indicator */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="card-title">Estado de suscripciones</span>
            <span
              title={kpis.autoSubscriptionEnabled ? 'Auto-suscripciones ACTIVAS' : 'Auto-suscripciones DESACTIVADAS'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'DM Mono, monospace',
                background: kpis.autoSubscriptionEnabled ? `${G.green}20` : `${G.red}20`,
                color: kpis.autoSubscriptionEnabled ? G.green : G.red,
                border: `1px solid ${kpis.autoSubscriptionEnabled ? G.green : G.red}50`,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: kpis.autoSubscriptionEnabled ? G.green : G.red,
                  boxShadow: `0 0 6px ${kpis.autoSubscriptionEnabled ? G.green : G.red}`,
                  display: 'inline-block',
                }}
              />
              {kpis.autoSubscriptionEnabled ? 'Activas' : 'Desactivadas'}
            </span>
          </span>
          {kpis.autoSubscriptionEnabled && (
            <span style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>Total: {subsByStatus.total}</span>
          )}
        </div>
        <div className="card-body">
          {kpis.autoSubscriptionEnabled ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              {[
                { k: 'Trial', v: subsByStatus.trial, c: G.blue, d: 'En periodo de prueba', dot: G.blue },
                { k: 'Activo', v: subsByStatus.active, c: G.green, d: 'Cobrando recurrente', dot: G.green },
                { k: 'Fallidas', v: subsByStatus.failed, c: G.red, d: 'Retry agotado (3x)', dot: G.red },
                { k: 'Pausadas', v: subsByStatus.paused, c: G.goldLight, d: 'Pausa manual', dot: G.goldLight },
                { k: 'Canceladas', v: subsByStatus.cancelled, c: G.muted, d: 'Dadas de baja', dot: G.muted },
              ].map((s, i) => (
                <div key={i} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
                    <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.k}</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.c, fontFamily: 'DM Mono, monospace' }}>{s.v}</div>
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{s.d}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '18px 16px', background: G.black, border: `1px dashed ${G.red}40`, borderRadius: 10, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: G.red, fontWeight: 600, marginBottom: 4 }}>Auto-suscripciones desactivadas</div>
              <div style={{ fontSize: 11, color: G.muted }}>Actívalas en <strong>Ajustes → Auto-suscripción</strong> para empezar a cobrar recurrente.</div>
            </div>
          )}
        </div>
      </div>

      {/* ── CUSTOMER BEHAVIOR — last 10 min ───────────────────────── */}
      {(() => {
        const cb = kpis.customerBehavior || { activeCarts: 0, checkingOut: 0, purchased: 0 };
        const stages = [
          { label: 'Carritos activos', value: cb.activeCarts, color: '#818cf8', glow: '#818cf8' },
          { label: 'En checkout', value: cb.checkingOut, color: '#a78bfa', glow: '#a78bfa' },
          { label: 'Comprado', value: cb.purchased, color: '#22c55e', glow: '#22c55e' },
        ];
        const maxVal = Math.max(1, ...stages.map(s => s.value));
        return (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head">
              <span className="card-title">Comportamiento del cliente</span>
              <span style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '3px 10px' }}>10 min</span>
            </div>
            <div className="card-body" style={{ padding: '28px 24px 24px' }}>
              {/* Pipeline visual */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', marginBottom: 28, gap: 0 }}>
                {/* Connecting line behind the dots */}
                <div style={{ position: 'absolute', top: '50%', left: '15%', right: '15%', height: 3, background: `linear-gradient(90deg, ${stages[0].color}40, ${stages[1].color}40, ${stages[2].color}40)`, transform: 'translateY(-50%)', borderRadius: 2, zIndex: 0 }} />

                {stages.map((s, i) => {
                  const baseSize = 36;
                  const pulseSize = s.value > 0 ? Math.min(80, baseSize + (s.value / maxVal) * 44) : baseSize;
                  const isActive = s.value > 0;
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                      {/* Glow ring */}
                      {isActive && (
                        <div style={{
                          position: 'absolute', top: '50%', left: '50%',
                          width: pulseSize + 20, height: pulseSize + 20,
                          transform: 'translate(-50%, -50%)',
                          borderRadius: '50%',
                          background: `radial-gradient(circle, ${s.glow}20 0%, transparent 70%)`,
                          animation: 'pulse 2s ease-in-out infinite',
                        }} />
                      )}
                      {/* Dot */}
                      <div style={{
                        width: pulseSize, height: pulseSize, borderRadius: '50%',
                        background: isActive
                          ? `radial-gradient(circle at 35% 35%, ${s.color}90, ${s.color}50)`
                          : `${s.color}25`,
                        border: `2.5px solid ${isActive ? s.color : `${s.color}40`}`,
                        boxShadow: isActive ? `0 0 20px ${s.glow}30, 0 0 40px ${s.glow}15` : 'none',
                        transition: 'all 0.6s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isActive && <div style={{ width: pulseSize * 0.25, height: pulseSize * 0.25, borderRadius: '50%', background: '#fff', opacity: 0.6 }} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Numbers + labels */}
              <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                {stages.map((s, i) => (
                  <div key={i} style={{ flex: 1 }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.value > 0 ? G.white : G.dim, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: G.muted, marginTop: 6, fontWeight: 500 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid-2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-head">
            <span className="card-title">Actividad de pagos</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {['7d', '30d'].map(cr => (
                <button key={cr} onClick={() => setChartRange(cr)} style={{ padding: '3px 10px', fontSize: 10, fontFamily: 'DM Mono, monospace', fontWeight: 600, borderRadius: 6, border: `1px solid ${chartRange === cr ? G.gold : G.border}`, background: chartRange === cr ? `${G.gold}20` : 'transparent', color: chartRange === cr ? G.gold : G.muted, cursor: 'pointer', transition: 'all 0.2s' }}>{cr === '7d' ? '7 dias' : '30 dias'}</button>
              ))}
            </div>
          </div>
          <div className="card-body">
            <LineChart chartData={(() => { const data = kpis.chartData || []; return chartRange === '7d' ? data.slice(-7) : data.slice(-30); })()} metric="revenue" height={260} />
          </div>
        </div>

        {/* REAL Conversion Funnel */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Embudo de conversion real</span>
            <span style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>visita → pago</span>
          </div>
          <div className="card-body">
            {[
              { label: 'Visitas a la web', value: funnel.visits || 0, pct: '100', color: '#8b5cf6', width: 100, desc: (() => {
                const s = funnel.visitsBySource || {};
                const parts = [];
                if (s.meta) parts.push(`📘 Meta: ${s.meta}`);
                if (s.google) parts.push(`🔵 Google: ${s.google}`);
                if (s.tiktok) parts.push(`🎵 TikTok: ${s.tiktok}`);
                const known = (s.meta || 0) + (s.google || 0) + (s.tiktok || 0);
                const org = (funnel.visits || 0) - known;
                if (org > 0) parts.push(`🌐 Organico: ${org}`);
                return parts.length ? parts.join('  ·  ') : 'Sesiones unicas en la tienda';
              })() },
              { label: 'Checkouts iniciados', value: funnel.checkouts, pct: funnelPctCheckouts, color: G.blue || '#3b82f6', width: Math.max(6, parseFloat(funnelPctCheckouts)), desc: 'Carritos creados' },
              { label: 'Intentos de pago', value: funnel.initiated, pct: funnelPctInit, color: G.gold, width: Math.max(6, parseFloat(funnelPctInit)), desc: 'Con tarjeta/wallet' },
              { label: 'Pagos enviados', value: funnel.submitted || funnel.initiated, pct: funnelPctSubmit, color: G.orange || G.gold, width: Math.max(6, parseFloat(funnelPctSubmit)), desc: 'Procesados por la pasarela' },
              { label: 'Pagos completados', value: funnel.completed, pct: funnelPctDone, color: G.green, width: Math.max(6, parseFloat(funnelPctDone)), desc: 'Cobrados OK' },
            ].map((step, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: G.white }}>{step.label}</span>
                  <span className="mono" style={{ color: step.color, fontWeight: 600 }}>{step.value} ({step.pct}%)</span>
                </div>
                <div className="progress" style={{ height: 6 }}><div className="progress-fill" style={{ width: `${step.width}%`, background: step.color }} /></div>
                <div style={{ fontSize: 10, color: G.muted, marginTop: 2 }}>{step.desc}</div>
              </div>
            ))}
            {funnel.abandoned > 0 && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: `${G.red}15`, border: `1px solid ${G.red}40`, borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: G.red, marginBottom: 2 }}>Checkouts abandonados: {funnel.abandoned}</div>
                <div style={{ fontSize: 10, color: G.muted }}>Carts que no llegaron a pago exitoso</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PANELES: pasarela / productos / ventas en el tiempo ────────── */}
      <PayoutsPanel />
      <CpaByShopPanel shops={kpis.cpaByShop || []} />
      <GatewayRevenuePanel gatewayBreakdown={kpis.gatewayBreakdown || []} />
      <RevenueOverTimePanel from={panelFrom} to={panelTo} />
      <div className="grid-2 dash-pair" style={{ marginBottom: 0, alignItems: 'start' }}>
        <UnitsSoldPanel from={panelFrom} to={panelTo} />
        <CartRecoveryPanel from={panelFrom} to={panelTo} />
      </div>
      <ComplaintsPanel showToast={showToast} />

      {/* ── RESUMEN GLOBAL ───────────────────────────────────────────── */}
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: G.white, letterSpacing: 0.3 }}>Resumen global</div>
            <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>
              Indicadores avanzados de negocio · Zona horaria {APP_TZ} · Actualizado {new Date().toLocaleTimeString('en-US', { timeZone: APP_TZ, hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
          </div>
          <div style={{ fontSize: 10, color: G.muted, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
            <div style={{ color: G.gold, fontWeight: 600, letterSpacing: 1 }}>{kpis.today || ''}</div>
            <div>Corte 00:00 ({APP_TZ})</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {/* CARD 0 — Hoy vs Ayer */}
          {(() => {
            const tvy = kpis.todayVsYesterday || { today: {}, yesterday: {} };
            const t = tvy.today || {};
            const y = tvy.yesterday || {};
            const revDelta = y.revenue > 0 ? (((t.revenue - y.revenue) / y.revenue) * 100).toFixed(0) : (t.revenue > 0 ? '+100' : '0');
            const revUp = parseFloat(revDelta) >= 0;
            const ncDelta = y.newCustomers > 0
              ? (((t.newCustomers - y.newCustomers) / y.newCustomers) * 100).toFixed(0)
              : (t.newCustomers > 0 ? '+100' : '0');
            const rows = [
              { label: 'Revenue', today: fmt(t.revenue || 0), yesterday: fmt(y.revenue || 0), delta: revDelta, up: revUp },
              { label: 'Ventas', today: t.sales || 0, yesterday: y.sales || 0, delta: y.sales > 0 ? (((t.sales - y.sales) / y.sales) * 100).toFixed(0) : (t.sales > 0 ? '+100' : '0'), up: (t.sales || 0) >= (y.sales || 0) },
              // NEW: first-time paying customers only (not repeat orders, not subs).
              // Matches Meta Ads "New customers" metric.
              { label: 'Clientes nuevos', today: t.newCustomers || 0, yesterday: y.newCustomers || 0, delta: ncDelta, up: (t.newCustomers || 0) >= (y.newCustomers || 0) },
              { label: 'Intentos', today: t.attempts || 0, yesterday: y.attempts || 0, delta: null, up: true },
              { label: 'Fallidos', today: t.failed || 0, yesterday: y.failed || 0, delta: null, up: (t.failed || 0) <= (y.failed || 0) },
              ...(kpis.autoSubscriptionEnabled ? [{ label: 'Nuevas subs', today: t.newSubs || 0, yesterday: y.newSubs || 0, delta: null, up: (t.newSubs || 0) >= (y.newSubs || 0) }] : []),
              { label: 'Ticket medio', today: fmt(t.aov || 0), yesterday: fmt(y.aov || 0), delta: null, up: (t.aov || 0) >= (y.aov || 0) },
              { label: 'Ad Spend', today: fmt(t.adSpend || 0), yesterday: fmt(y.adSpend || 0), delta: y.adSpend > 0 ? (((t.adSpend - y.adSpend) / y.adSpend) * 100).toFixed(0) : (t.adSpend > 0 ? '+100' : '0'), up: (t.adSpend || 0) <= (y.adSpend || 0) },
              { label: 'Profit', today: fmt(t.profit || 0), yesterday: fmt(y.profit || 0), delta: y.profit > 0 ? (((t.profit - y.profit) / y.profit) * 100).toFixed(0) : (t.profit > 0 ? '+100' : '0'), up: (t.profit || 0) >= (y.profit || 0) },
            ];
            return (
              <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.gold}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden', gridColumn: 'span 1' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 32, height: 32, background: `${G.gold}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.gold, fontWeight: 700 }}>⚡</div>
                  <div>
                    <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Hoy vs Ayer</div>
                    <div style={{ fontSize: 11, color: G.dim }}>Comparativa diaria</div>
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontSize: 9, color: G.muted, fontWeight: 600, paddingBottom: 6, borderBottom: `1px solid ${G.border}`, letterSpacing: 1, textTransform: 'uppercase' }}></th>
                      <th style={{ textAlign: 'right', fontSize: 9, color: G.gold, fontWeight: 600, paddingBottom: 6, borderBottom: `1px solid ${G.border}`, letterSpacing: 1 }}>HOY</th>
                      <th style={{ textAlign: 'right', fontSize: 9, color: G.muted, fontWeight: 600, paddingBottom: 6, borderBottom: `1px solid ${G.border}`, letterSpacing: 1 }}>AYER</th>
                      <th style={{ textAlign: 'right', fontSize: 9, color: G.muted, fontWeight: 600, paddingBottom: 6, borderBottom: `1px solid ${G.border}`, letterSpacing: 1, width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 11, color: G.white, padding: '6px 0', borderBottom: i < rows.length - 1 ? `1px solid ${G.border}22` : 'none', fontWeight: 500 }}>{r.label}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, color: G.white, fontFamily: 'DM Mono, monospace', fontWeight: 700, padding: '6px 0', borderBottom: i < rows.length - 1 ? `1px solid ${G.border}22` : 'none' }}>{r.today}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, color: G.dim, fontFamily: 'DM Mono, monospace', padding: '6px 0', borderBottom: i < rows.length - 1 ? `1px solid ${G.border}22` : 'none' }}>{r.yesterday}</td>
                        <td style={{ textAlign: 'right', fontSize: 10, fontFamily: 'DM Mono, monospace', fontWeight: 700, padding: '6px 0', borderBottom: i < rows.length - 1 ? `1px solid ${G.border}22` : 'none', color: r.up ? G.green : G.red }}>{r.delta ? `${parseFloat(r.delta) >= 0 ? '+' : ''}${r.delta}%` : (r.up ? '↑' : '↓')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
          {/* CARD 1 — Ticket promedio (AOV) — 3 ventanas: hoy / 3d / global */}
          <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.gold}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${G.gold}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, background: `${G.gold}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.gold, fontWeight: 700 }}>◆</div>
              <div>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Ticket promedio</div>
                <div style={{ fontSize: 11, color: G.dim }}>Average Order Value por ventana</div>
              </div>
            </div>
            {/* 3 AOV values stacked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Hoy</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: G.goldLight, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{fmt(kpis.aovToday || 0)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Últimos 3d</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{fmt(kpis.aov3d || 0)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Global</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.dim, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{fmt(kpis.aovGlobal || kpis.aov || 0)}</div>
              </div>
            </div>
            <div style={{ fontSize: 9, color: G.muted, marginBottom: 14 }}>
              {kpis.aovToday_n || 0} hoy · {kpis.aov3d_n || 0} en 3d · {kpis.aovGlobal_n || 0} total
            </div>
            <div style={{ display: 'flex', gap: 12, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Clientes unicos</div>
                <div style={{ fontSize: 14, color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{kpis.uniqueCustomers || 0}</div>
              </div>
              <div style={{ width: 1, background: G.border }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>LTV estimado</div>
                <div style={{ fontSize: 14, color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(kpis.ltv || 0)}</div>
              </div>
            </div>
          </div>

          {/* CARD 2 — Tendencia 7 dias */}
          <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${parseFloat(kpis.weekChange || 0) >= 0 ? G.green : G.red}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${parseFloat(kpis.weekChange || 0) >= 0 ? G.green : G.red}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, background: `${parseFloat(kpis.weekChange || 0) >= 0 ? G.green : G.red}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: parseFloat(kpis.weekChange || 0) >= 0 ? G.green : G.red, fontWeight: 700 }}>{parseFloat(kpis.weekChange || 0) >= 0 ? '↑' : '↓'}</div>
              <div>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tendencia 7 dias</div>
                <div style={{ fontSize: 11, color: G.dim }}>vs semana anterior</div>
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: parseFloat(kpis.weekChange || 0) >= 0 ? G.green : G.red, fontFamily: 'DM Mono, monospace', lineHeight: 1, marginBottom: 4 }}>
              {parseFloat(kpis.weekChange || 0) >= 0 ? '+' : ''}{kpis.weekChange || '0'}%
            </div>
            <div style={{ fontSize: 10, color: G.muted, marginBottom: 14 }}>Variacion de revenue semanal</div>
            <div style={{ display: 'flex', gap: 12, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Ultimos 7d</div>
                <div style={{ fontSize: 14, color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(kpis.last7Revenue || 0)}</div>
              </div>
              <div style={{ width: 1, background: G.border }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Previos 7d</div>
                <div style={{ fontSize: 14, color: G.dim, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(kpis.prior7Revenue || 0)}</div>
              </div>
            </div>
          </div>

          {/* CARD 3 — Top pasarela */}
          <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.blue}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${G.blue}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, background: `${G.blue}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.blue, fontWeight: 700 }}>◈</div>
              <div>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Top pasarela</div>
                <div style={{ fontSize: 11, color: G.dim }}>Mejor rendimiento</div>
              </div>
            </div>
            {kpis.bestGateway ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {kpis.bestGateway.name}
                </div>
                <div style={{ fontSize: 11, color: G.blue, marginBottom: 14 }}>
                  {kpis.bestGateway.successRate}% success · {fmt(kpis.bestGateway.revenue || 0)}
                </div>
                <div style={{ paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
                  {(kpis.topGateways || []).slice(0, 3).map((gw, i) => (
                    <div key={gw.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 2 ? 6 : 0 }}>
                      <span style={{ fontSize: 10, color: G.muted, minWidth: 14, fontFamily: 'DM Mono, monospace' }}>#{i + 1}</span>
                      <span style={{ fontSize: 11, color: G.white, fontWeight: 500, flex: 1 }}>{gw.name}</span>
                      <span style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>{gw.ok} / {gw.total}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: G.muted, padding: '20px 0' }}>Sin datos de pasarelas</div>
            )}
          </div>

          {/* CARD 4 — Lealtad / fidelizacion (replaces Subs card if subs disabled) */}
          <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.green}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${G.green}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, background: `${G.green}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.green, fontWeight: 700 }}>♦</div>
              <div>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Fidelizacion</div>
                <div style={{ fontSize: 11, color: G.dim }}>Clientes recurrentes</div>
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace', lineHeight: 1, marginBottom: 4 }}>
              {kpis.repeatRate || '0'}%
            </div>
            <div style={{ fontSize: 10, color: G.muted, marginBottom: 14 }}>Clientes con &gt;1 compra</div>
            <div style={{ display: 'flex', gap: 12, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Recurrentes</div>
                <div style={{ fontSize: 14, color: G.green, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{kpis.repeatCustomers || 0}</div>
              </div>
              <div style={{ width: 1, background: G.border }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Nuevos</div>
                <div style={{ fontSize: 14, color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{Math.max(0, (kpis.uniqueCustomers || 0) - (kpis.repeatCustomers || 0))}</div>
              </div>
            </div>
          </div>

          {/* CARD 5 — MRR (only if autoSubscription enabled) */}
          {kpis.autoSubscriptionEnabled && (
            <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.gold}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${G.goldLight}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, background: `${G.gold}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.gold, fontWeight: 700 }}>↻</div>
                <div>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>MRR</div>
                  <div style={{ fontSize: 11, color: G.dim }}>Monthly Recurring Revenue</div>
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: G.goldLight, fontFamily: 'DM Mono, monospace', lineHeight: 1, marginBottom: 4 }}>
                {fmt(kpis.mrr || 0)}
              </div>
              <div style={{ fontSize: 10, color: G.muted, marginBottom: 14 }}>Ingresos recurrentes confirmados</div>
              <div style={{ display: 'flex', gap: 12, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Proyeccion anual</div>
                  <div style={{ fontSize: 14, color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(kpis.arr || 0)}</div>
                </div>
                <div style={{ width: 1, background: G.border }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Pagando ahora</div>
                  <div style={{ fontSize: 14, color: G.green, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{kpis.subscriptionsByStatus?.active || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* CARD 6 — Health / Retry (only if autoSubscription enabled) */}
          {kpis.autoSubscriptionEnabled && (
            <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.orange}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120, background: `radial-gradient(circle, ${G.orange}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, background: `${G.orange}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.orange, fontWeight: 700 }}>♥</div>
                <div>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Salud suscripciones</div>
                  <div style={{ fontSize: 11, color: G.dim }}>Pipeline activo</div>
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: G.orange, fontFamily: 'DM Mono, monospace', lineHeight: 1, marginBottom: 4 }}>
                {((subsByStatus.active + subsByStatus.trial) / Math.max(1, subsByStatus.total - subsByStatus.cancelled) * 100).toFixed(0)}%
              </div>
              <div style={{ fontSize: 10, color: G.muted, marginBottom: 14 }}>Vivas / no canceladas</div>
              <div style={{ display: 'flex', gap: 12, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>Churn</div>
                  <div style={{ fontSize: 14, color: G.red, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{subsByStatus.total > 0 ? ((subsByStatus.cancelled / subsByStatus.total) * 100).toFixed(1) : '0'}%</div>
                </div>
                <div style={{ width: 1, background: G.border }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: G.muted, marginBottom: 2 }}>En retry</div>
                  <div style={{ fontSize: 14, color: G.orange, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{subsByStatus.failed || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* CARD 7 — Trials próximos 5 días (FRESHOS solamente, no retries)
              Se actualiza solo conforme pasan los días: el bucketing usa
              today + offset en cada render, así que mañana CARD seguirá
              mostrando los siguientes 5 días sin tener que tocar nada.
              Layout: en desktop ocupa 2 columnas (caben los 5 días en
              fila); en móvil solo 1 columna y los días hacen wrap. */}
          {kpis.autoSubscriptionEnabled && kpis.upcomingTrials5d && (
            <div className="trials-upcoming-card" style={{ background: `linear-gradient(135deg, ${G.card} 0%, ${G.surface} 100%)`, border: `1px solid ${G.blue}30`, borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: 140, height: 140, background: `radial-gradient(circle, ${G.blue}15 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <div style={{ width: 32, height: 32, background: `${G.blue}20`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: G.blue, fontWeight: 700, flexShrink: 0 }}>📅</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Trials próximos 5 días</div>
                    <div style={{ fontSize: 11, color: G.dim }}>Solo trials frescos · sin retries</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: G.blue, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>
                    {kpis.upcomingTrials5d.totalUniqueCustomers || kpis.upcomingTrials5d.totalCount}
                  </div>
                  <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                    clientes ({kpis.upcomingTrials5d.totalCount} cobros)
                  </div>
                </div>
              </div>
              <div className="trials-day-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6, paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
                {kpis.upcomingTrials5d.days.map((d, i) => {
                  const labelMap = ['Hoy', 'Mañana', 'En 2d', 'En 3d', 'En 4d'];
                  const label = labelMap[i] || `+${i}d`;
                  const dayName = (() => {
                    try {
                      return new Date(d.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short' });
                    } catch { return ''; }
                  })();
                  // Top number = unique customers (the one that matters for
                  // forecasting). Bottom number = RC count (the one that
                  // matters for cron load). Operator confirmed not to show
                  // \$potencial because rebill rate swings day-to-day.
                  const customers = d.uniqueCustomers != null ? d.uniqueCustomers : d.count;
                  return (
                    <div key={d.date} style={{ textAlign: 'center', padding: '8px 2px', borderRadius: 8, background: i === 0 ? `${G.blue}15` : 'transparent', border: `1px solid ${i === 0 ? G.blue + '40' : G.border}`, minWidth: 0 }}>
                      <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                      <div style={{ fontSize: 9, color: G.dim, marginBottom: 2, whiteSpace: 'nowrap' }}>{dayName} {d.date.slice(8, 10)}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: customers > 0 ? G.white : G.dim, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }} title="Clientes únicos">
                        {customers}
                      </div>
                      <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap' }} title="Cobros totales (RCs)">
                        {d.count > 0 ? d.count + ' cobros' : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {!hasGateways && (
        <div className="connect-banner" style={{ marginTop: 16 }}>
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 17, fontWeight: 600, marginBottom: 5, color: G.goldLight }}>
              Conecta tus pasarelas para activar el sistema
            </div>
            <div style={{ fontSize: 12, color: G.muted }}>
              Ve a Pasarelas → Nueva pasarela → introduce tus credenciales de Stripe, Square o TailoredPayments
            </div>
          </div>
          <button className="btn btn-gold" style={{ whiteSpace: 'nowrap' }} onClick={() => onNavigate && onNavigate('gateways')}>Configurar ahora →</button>
        </div>
      )}
    </div>
  );
}

function Payments({ showToast }) {
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [cleanupLoading, setCleanupLoading] = useState(false);
  // ── Pagination (fix mobile crash 2026-05-13) ───────────────────────────
  // Rendering 11k+ payments at once produced ~70k DOM nodes which mobile
  // Safari kills via its per-tab memory limit (the tab "crashed and went
  // back to dashboard" reported by operator). Paginating keeps DOM size
  // bounded regardless of total payments count, so the page-size selector
  // below is deliberately capped at 100.
  const [pageSize, setPageSize] = useState(50);
  const PAYMENTS_PAGE_SIZE = pageSize;
  const [page, setPage] = useState(1);

  // ── Filtros y presentación extra ───────────────────────────────────────
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [ignoreNoEmail, setIgnoreNoEmail] = useState(false);
  const [range, setRange] = useState({ preset: 'all', from: '', to: '' });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [dense, setDense] = useState(false);
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [visibleCols, setVisibleCols] = useState(() => new Set(
    ['fecha', 'ref', 'tipo', 'fuente', 'cliente', 'tienda', 'gateway', 'metodo', 'monto', 'estado', 'respuesta', 'acciones']
  ));
  const show = (k) => visibleCols.has(k);
  const toggleCol = (k) => setVisibleCols(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const load = useCallback(() => {
    setLoading(true);
    paymentsApi.getAll()
      .then(d => { setPayments(d.payments || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever any filter changes so we don't end up on
  // an out-of-range page that renders blank.
  useEffect(() => { setPage(1); }, [filter, search, gatewayFilter, shopFilter, ignoreNoEmail, range, pageSize]);

  // Resuelve un preset a límites [desde, hasta] en la zona del panel. Se
  // calcula sobre el día local del tenant, no UTC, para que "Hoy" signifique
  // hoy para el operador y no un día desplazado.
  const rangeBounds = useMemo(() => {
    const { preset, from, to } = range;
    if (preset === 'all') return null;
    if (preset === 'custom') {
      if (!from && !to) return null;
      return { from: from ? from + 'T00:00:00' : '', to: to ? to + 'T23:59:59' : '' };
    }
    const dayKey = (d) => {
      try { return d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); } catch { return d.toISOString().slice(0, 10); }
    };
    const now = new Date();
    const today = dayKey(now);
    const shift = (n) => dayKey(new Date(now.getTime() - n * 86400000));
    const presets = {
      today:    { from: today,     to: today },
      yesterday:{ from: shift(1),  to: shift(1) },
      d7:       { from: shift(6),  to: today },
      d30:      { from: shift(29), to: today },
      d90:      { from: shift(89), to: today },
      d365:     { from: shift(364), to: today },
      month:    { from: today.slice(0, 8) + '01', to: today },
    };
    const r = presets[preset];
    return r ? { from: r.from + 'T00:00:00', to: r.to + 'T23:59:59' } : null;
  }, [range]);

  const emailOf = (p) => p.metadata?.customer?.email || p.metadata?.customerEmail || p.customerEmail || p.email || (String(p.customerId || '').includes('@') ? p.customerId : '');

  const filtered = payments.filter(p => {
    const ms = filter === 'ALL' || p.status === filter;
    const sq = search?.toLowerCase() || '';
    const mq = !search || p.customerId?.toLowerCase().includes(sq) || p.email?.toLowerCase().includes(sq) || p.id?.includes(search) || p.externalId?.includes(search) || p.metadata?.orderId?.includes(search) || p.metadata?.shopName?.toLowerCase().includes(sq);
    if (!ms || !mq) return false;
    if (gatewayFilter && !String(p.gateway || p.psp || '').toLowerCase().includes(gatewayFilter.toLowerCase())) return false;
    if (shopFilter && !String(p.metadata?.shopName || '').toLowerCase().includes(shopFilter.toLowerCase())) return false;
    if (ignoreNoEmail && !emailOf(p)) return false;
    if (rangeBounds) {
      const d = p.createdAt || '';
      if (rangeBounds.from && d < rangeBounds.from) return false;
      if (rangeBounds.to && d > rangeBounds.to) return false;
    }
    return true;
  }).sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const pick = (p) => {
      switch (sort.key) {
        case 'monto':   return p.amount || 0;
        case 'estado':  return p.status || '';
        case 'cliente': return emailOf(p) || '';
        case 'tienda':  return p.metadata?.shopName || '';
        case 'gateway': return p.gateway || '';
        case 'metodo':  return p.psp || '';
        default:        return p.createdAt || '';
      }
    };
    const va = pick(a), vb = pick(b);
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  // Page slicing — capped to total pages so URL hashes / stale state don't
  // produce empty pages.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAYMENTS_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = (safePage - 1) * PAYMENTS_PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAYMENTS_PAGE_SIZE, filtered.length);
  const pagedRows = filtered.slice(startIdx, endIdx);

  const succeeded = payments.filter(p => p.status === 'SUCCEEDED');
  const failed = payments.filter(p => p.status === 'FAILED');
  const pending = payments.filter(p => p.status === 'PENDING');
  const totalIncome = succeeded.reduce((s, p) => s + (p.amount || 0), 0);
  const totalRejected = failed.reduce((s, p) => s + (p.amount || 0), 0);
  const totalAbandoned = pending.reduce((s, p) => s + (p.amount || 0), 0);
  const nonPending = payments.filter(p => p.status !== 'PENDING');
  const successRate = nonPending.length ? ((succeeded.length / nonPending.length) * 100).toFixed(1) : '0';
  const subPayments = payments.filter(p => p.metadata?.source === 'recurring' || p.metadata?.source === 'charge-now');
  const subIncome = subPayments.filter(p => p.status === 'SUCCEEDED').reduce((s, p) => s + (p.amount || 0), 0);

  // Exporta respetando los filtros activos y las columnas visibles. `scope`
  // 'page' exporta lo que se ve; 'all' exporta todo lo filtrado.
  const exportRows = (fmtKind, scope) => {
    const src = scope === 'page' ? pagedRows : filtered;
    const defs = [
      ['fecha',     'Fecha',     p => p.createdAt || ''],
      ['ref',       'Ref',       p => p.externalId || p.metadata?.orderId || p.id || ''],
      ['tipo',      'Tipo',      p => p.status === 'PENDING' ? 'Abandonado' : p.metadata?.source === 'recurring' ? 'Suscripcion' : p.metadata?.source === 'charge-now' ? 'Cobro manual' : 'Pago'],
      ['fuente',    'Fuente',    p => p.metadata?.trafficSource || ((p.metadata?.fbc || p.metadata?.fbp) ? 'meta' : '')],
      ['cliente',   'Cliente',   p => emailOf(p)],
      ['tienda',    'Tienda',    p => p.metadata?.shopName || ''],
      ['gateway',   'Gateway',   p => p.gateway || ''],
      ['metodo',    'Metodo',    p => p.psp || ''],
      ['monto',     'Monto',     p => ((p.amount || 0) / 100).toFixed(2)],
      ['moneda',    'Moneda',    p => p.currency || ''],
      ['estado',    'Estado',    p => p.status || ''],
      ['respuesta', 'Respuesta', p => p.error || (p.status === 'SUCCEEDED' ? 'OK' : '')],
      ['direccion', 'Direccion', p => {
        const a = p.metadata?.customer?.address || p.metadata?.billingAddress || {};
        return [a.line1, a.city, a.postal_code || a.zip, a.country].filter(Boolean).join(', ');
      }],
    ];
    // 'gateway', 'moneda' y 'direccion' no son columnas de la tabla pero sí
    // datos que el operador quiere en el fichero, así que van siempre.
    const always = new Set(['gateway', 'moneda', 'direccion']);
    const cols = defs.filter(([k]) => always.has(k) || show(k));
    const header = cols.map(c => c[1]);
    const body = src.map(p => cols.map(c => c[2](p)));
    const stamp = new Date().toISOString().split('T')[0];
    const name = `pagos_${scope === 'page' ? 'pagina' : 'todos'}_${stamp}`;
    if (fmtKind === 'csv') {
      const csv = [header, ...body].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name + '.csv'; a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pagos');
      XLSX.writeFile(wb, name + '.xlsx');
    }
    showToast(`${src.length} pagos exportados`, 'success');
  };

  const RANGE_LABELS = {
    all: 'Todo el periodo', today: 'Hoy', yesterday: 'Ayer', d7: 'Ultimos 7 dias',
    d30: 'Ultimos 30 dias', d90: 'Ultimos 90 dias', d365: 'Ultimos 365 dias',
    month: 'Mes actual', custom: 'Personalizado',
  };

  const PAYMENT_COLS = [
    ['fecha', 'Fecha'], ['ref', 'Ref'], ['tipo', 'Tipo'], ['fuente', 'Fuente'],
    ['cliente', 'Cliente'], ['tienda', 'Tienda'], ['gateway', 'Gateway'], ['metodo', 'Metodo'],
    ['monto', 'Monto'], ['estado', 'Estado'], ['respuesta', 'Respuesta'],
    ['direccion', 'Direccion'], ['acciones', 'Acciones'],
  ];

  const kpiCard = (icon, label, value, sub, color) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: G.muted }}>{label}</span>
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{sub}</div>
    </div>
  );

  return (
    <div className="content">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {kpiCard(fmtSymbol(), 'Ingresos totales', fmt(totalIncome / 100), `${succeeded.length} pagos exitosos`, G.green)}
        {kpiCard('↗', 'Rechazados', fmt(totalRejected / 100), `${failed.length} pagos rechazados`, G.red)}
        {kpiCard('◎', 'Tasa de exito', `${successRate}%`, `${nonPending.length} transacciones procesadas`)}
        {kpiCard('🛒', 'Abandonados', fmt(totalAbandoned / 100), `${pending.length} carritos sin pagar`, G.orange)}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="search" style={{ flex: 1, minWidth: 250 }} placeholder="Buscar por pedido, email, nombre o ID transaccion..." value={search} onChange={e => setSearch(e.target.value)} />
        <button
          className="btn btn-sm"
          style={{ background: G.orange + '20', color: G.orange, border: `1px solid ${G.orange}40`, fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}
          disabled={cleanupLoading}
          onClick={async () => {
            setCleanupLoading(true);
            try {
              const preview = await cleanupApi.previewAbandoned(90);
              if (preview.olderThanDays === 0) {
                showToast('No hay carritos abandonados de mas de 90 dias');
                setCleanupLoading(false);
                return;
              }
              setConfirmModal({
                title: 'Limpiar carritos antiguos',
                message: `Se eliminaran ${preview.olderThanDays} carritos abandonados de mas de 90 dias.\nSe mantendran ${preview.wouldKeep} carritos recientes.\n\nEsto libera memoria del servidor y no afecta los pagos.`,
                onConfirm: async () => {
                  try {
                    const result = await cleanupApi.deleteAbandoned(90);
                    showToast(`${result.deleted} carritos eliminados. Quedan ${result.remaining}.`);
                    load();
                  } catch (e) {
                    showToast('Error al limpiar: ' + (e.response?.data?.error || e.message));
                  }
                  setConfirmModal(null);
                },
              });
            } catch (e) {
              showToast('Error: ' + (e.response?.data?.error || e.message));
            }
            setCleanupLoading(false);
          }}
        >
          {cleanupLoading ? '...' : 'Limpiar carritos +90 dias'}
        </button>
        <button
          className="btn btn-sm"
          style={{ background: `${G.green}15`, color: G.green, border: `1px solid ${G.green}40`, fontSize: 12, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}
          onClick={async () => {
            try {
              const preview = await cleanupApi.pendingConfirmations();
              if (preview.pendingTotal === 0) {
                showToast('Todos los emails de confirmacion ya se enviaron');
                return;
              }
              const sendCount = Math.min(preview.pendingTotal, 50);
              setConfirmModal({
                title: 'Reenviar confirmaciones de pedido',
                message: `Hay ${preview.pendingTotal} pedidos sin email de confirmacion:\n• ${preview.failed} fallaron por limite de envio\n• ${preview.neverAttempted} nunca se intentaron\n\nSe enviaran ${sendCount} ahora (~${Math.ceil(sendCount * 12 / 60)} min).\nEl resto puedes enviarlo despues.`,
                onConfirm: async () => {
                  try {
                    const result = await cleanupApi.resendConfirmations(sendCount);
                    showToast(result.message || `${result.queued} emails en cola`);
                  } catch (e) {
                    showToast('Error: ' + (e.response?.data?.error || e.message));
                  }
                  setConfirmModal(null);
                },
              });
            } catch (e) {
              showToast('Error: ' + (e.response?.data?.error || e.message));
            }
          }}
        >
          Reenviar confirmaciones pendientes
        </button>
        <div className="pill-group">
          {['ALL', 'SUCCEEDED', 'FAILED', 'PENDING', 'PROCESSING'].map(s => (
            <button key={s} className={`pill ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
              {s === 'ALL' ? 'Todos' : s === 'SUCCEEDED' ? 'Pagados' : s === 'FAILED' ? 'Fallidos' : s === 'PENDING' ? 'Abandonados' : 'Procesando'}
            </button>
          ))}
        </div>
      </div>

      {/* Segunda barra: rango de fechas, filtros finos y presentación. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setRangeOpen(o => !o)}>
            🗓 {RANGE_LABELS[range.preset]}
            {range.preset === 'custom' && (range.from || range.to) ? ` (${range.from || '…'} → ${range.to || '…'})` : ''}
          </button>
          {rangeOpen && (
            <div style={{ position: 'absolute', left: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 230, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
              {Object.entries(RANGE_LABELS).filter(([k]) => k !== 'custom').map(([k, label]) => (
                <button
                  key={k}
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4, background: range.preset === k ? G.goldDim : undefined }}
                  onClick={() => { setRange({ preset: k, from: '', to: '' }); setRangeOpen(false); }}
                >{label}</button>
              ))}
              <div style={{ borderTop: `1px solid ${G.border}`, marginTop: 8, paddingTop: 10 }}>
                <div style={{ fontSize: 10, color: G.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'DM Mono, monospace' }}>Personalizado</div>
                <input type="date" className="form-input" style={{ marginBottom: 6, fontSize: 12 }} value={range.from} onChange={e => setRange(r => ({ ...r, preset: 'custom', from: e.target.value }))} />
                <input type="date" className="form-input" style={{ marginBottom: 8, fontSize: 12 }} value={range.to} onChange={e => setRange(r => ({ ...r, preset: 'custom', to: e.target.value }))} />
                <button className="btn btn-gold btn-sm" style={{ width: '100%' }} onClick={() => setRangeOpen(false)}>Aplicar</button>
              </div>
            </div>
          )}
        </div>

        <input className="search" style={{ width: 160 }} placeholder="Gateway…" value={gatewayFilter} onChange={e => setGatewayFilter(e.target.value)} />
        <input className="search" style={{ width: 160 }} placeholder="Tienda…" value={shopFilter} onChange={e => setShopFilter(e.target.value)} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: G.dim, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={ignoreNoEmail} onChange={e => setIgnoreNoEmail(e.target.checked)} />
          Ignorar pagos sin email
        </label>

        {(gatewayFilter || shopFilter || ignoreNoEmail || range.preset !== 'all' || search) && (
          <button className="btn btn-ghost btn-sm" onClick={() => {
            setGatewayFilter(''); setShopFilter(''); setIgnoreNoEmail(false);
            setRange({ preset: 'all', from: '', to: '' }); setSearch('');
          }}>✕ Resetear filtros</button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {filtered.length.toLocaleString('es-ES')} de {payments.length.toLocaleString('es-ES')}
          </span>
          <div style={{ position: 'relative' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setColsOpen(o => !o)}>▤ Columnas</button>
            {colsOpen && (
              <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 180, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                {PAYMENT_COLS.map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px', fontSize: 12, color: G.white, cursor: 'pointer' }}>
                    <input type="checkbox" checked={show(k)} onChange={() => toggleCol(k)} />
                    {label}
                  </label>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(PAYMENT_COLS.map(c => c[0])))}>Todas</button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setColsOpen(false)}>Cerrar</button>
                </div>
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setDense(d => !d)}>{dense ? '↕ Normal' : '↕ Compacta'}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportRows('csv', 'page')} title="CSV de la página visible">↓ CSV pág.</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportRows('csv', 'all')} title="CSV de todo lo filtrado">↓ CSV todo</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportRows('xlsx', 'all')} title="Excel de todo lo filtrado">↓ Excel</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="empty"><div className="empty-icon">◌</div>Cargando transacciones...</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {PAYMENT_COLS.filter(([k]) => show(k)).map(([k, label]) => {
                    const sortable = ['fecha', 'cliente', 'tienda', 'gateway', 'metodo', 'monto', 'estado'].includes(k);
                    const sortKey = k === 'fecha' ? 'createdAt' : k;
                    return (
                      <th
                        key={k}
                        onClick={() => sortable && toggleSort(sortKey)}
                        style={{ cursor: sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', padding: dense ? '7px 12px' : undefined }}
                        title={sortable ? 'Ordenar' : undefined}
                      >
                        {label}
                        {sort.key === sortKey && <span style={{ color: G.gold, marginLeft: 5 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={PAYMENT_COLS.filter(([k]) => show(k)).length}><div className="empty"><div className="empty-icon">◎</div>Sin transacciones.</div></td></tr>
                )}
                {pagedRows.map(p => (
                  <tr key={p.id}>
                    {show('fecha') && <td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap', padding: dense ? '7px 12px' : undefined }}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '--'}
                      <div style={{ fontSize: 10, color: G.muted }}>{p.createdAt ? new Date(p.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: APP_TZ }) : ''}</div>
                    </td>}
                    {show('ref') && <td style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: G.dim, padding: dense ? '7px 12px' : undefined }}>{p.externalId ? p.externalId.substring(0, 12) : (p.metadata?.orderId || p.id)?.substring(0, 12)}</td>}
                    {show('tipo') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      <span className={`badge ${p.status === 'PENDING' ? 'badge-orange' : p.metadata?.source === 'recurring' || p.metadata?.source === 'charge-now' ? 'badge-blue' : 'badge-gold'}`} style={{ fontSize: 10 }}>
                        {p.status === 'PENDING' ? 'Abandonado' : p.metadata?.source === 'recurring' ? 'Suscripcion' : p.metadata?.source === 'charge-now' ? 'Cobro manual' : 'Pago'}
                      </span>
                    </td>}
                    {show('fuente') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      {(() => {
                        let src = (p.metadata?.trafficSource || '').toLowerCase();
                        // Fallback: infer Meta from fbc/fbp cookies stored in metadata (pre-feature payments)
                        if (!src && (p.metadata?.fbc || p.metadata?.fbp)) src = 'meta';
                        if (src === 'meta') return <span className="badge badge-blue" style={{ fontSize: 10 }}>📘 Meta</span>;
                        if (src === 'google') return <span className="badge badge-green" style={{ fontSize: 10 }}>🔵 Google</span>;
                        if (src === 'tiktok') return <span className="badge" style={{ fontSize: 10, background: '#333', color: '#fff' }}>🎵 TikTok</span>;
                        if (src) return <span className="badge" style={{ fontSize: 10 }}>{src}</span>;
                        if (p.metadata?.source === 'recurring' || p.metadata?.source === 'charge-now') return <span style={{ fontSize: 10, color: G.muted }}>—</span>;
                        return <span style={{ fontSize: 10, color: G.muted }}>Organico</span>;
                      })()}
                    </td>}
                    {show('cliente') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{emailOf(p) || p.customerId || '--'}</div>
                    </td>}
                    {show('tienda') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{p.metadata?.shopName || '--'}</div>
                    </td>}
                    {show('gateway') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      <div style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: G.dim, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.gateway || ''}>{p.gateway || '--'}</div>
                    </td>}
                    {show('metodo') && <td style={{ padding: dense ? '7px 12px' : undefined }}><span className="tag" style={{ fontSize: 10 }}>{p.psp || '--'}</span></td>}
                    {show('monto') && <td style={{ padding: dense ? '7px 12px' : undefined }}><span className="mono" style={{ fontWeight: 600, color: G.goldLight }}>{fmt((p.amount || 0) / 100)}</span></td>}
                    {show('estado') && <td style={{ padding: dense ? '7px 12px' : undefined }}><StatusBadge status={p.status} /></td>}
                    {show('respuesta') && <td style={{ fontSize: 11, padding: dense ? '7px 12px' : undefined }}>
                      {p.status === 'SUCCEEDED' && <span style={{ color: G.green }}>OK</span>}
                      {p.status === 'PENDING' && <span style={{ color: G.muted }}>—</span>}
                      {p.status === 'FAILED' && (() => {
                        const d = p.error || '';
                        // Short human-readable summary mapped from the raw decline text.
                        // Hover the row to see the full processor response in the tooltip.
                        let label = d.slice(0, 60);
                        if (/insufficient.?funds|\b51\b/i.test(d))     label = '💰 Saldo insuficiente';
                        else if (/do.?not.?honor|\b05\b/i.test(d))     label = '🚫 Do not honor';
                        else if (/suspected.?fraud|\b59\b/i.test(d))   label = '🛑 Sospecha de fraude';
                        else if (/expired|\b54\b/i.test(d))            label = '⏰ Tarjeta caducada';
                        else if (/lost.?card|\b41\b/i.test(d))         label = '🚷 Tarjeta perdida';
                        else if (/stolen.?card|\b43\b/i.test(d))       label = '🚷 Tarjeta robada';
                        else if (/closed.?account|\b46\b/i.test(d))    label = '❌ Cuenta cerrada';
                        else if (/restricted|\b62\b/i.test(d))         label = '🚷 Tarjeta restringida';
                        else if (/invalid.?card/i.test(d))             label = '🔢 Tarjeta inválida';
                        else if (/try.?again|\b91\b/i.test(d))         label = '🔁 Banco busy';
                        else if (/refer.?to.?issuer|contact.?card|\b02\b/i.test(d)) label = '📞 Llamar al banco';
                        else if (/\b65\b|activity.?limit/i.test(d))    label = '🚧 Límite diario';
                        else if (/general.?error|ERROR\s+\d{4}|\b06\b/i.test(d)) label = '⚠️ Error técnico';
                        else if (/AVS\s*(REJECT|MISMATCH)/i.test(d))   label = '📍 AVS mismatch';
                        // 2026-06-11 — Regex estricto: solo categorizar como
                        // CVV si el mensaje habla específicamente del security
                        // code (incorrect/invalid/failed/wrong cvv/cvc/cv2).
                        // Antes /CVV|cvc/i matcheaba CUALQUIER mensaje con cvc
                        // dentro, etiquetando declines reales como CVV falso.
                        else if (/(incorrect|invalid|wrong|fail|reject|bad|miss)[^a-z]*c(v[v|c2]|sc|security[- ]code)/i.test(d) || /security[- ]code\s+(incorrect|invalid|wrong|fail|reject|bad)/i.test(d) || /cvc[_-]?check[_-]?fail/i.test(d))
                          label = '🔢 CVV inválido';
                        return <span style={{ color: G.red }} title={d}>{label || '✗ Decline'}</span>;
                      })()}
                    </td>}
                    {show('direccion') && (() => {
                      const a = p.metadata?.customer?.address || p.metadata?.billingAddress || {};
                      const city = a.city || p.metadata?.billingCity || '';
                      const country = a.country || p.metadata?.billingCountry || '';
                      const full = [a.line1, city, a.postal_code || a.zip, country].filter(Boolean).join(', ');
                      return (
                        <td style={{ fontSize: 11, color: G.dim, maxWidth: 190, padding: dense ? '7px 12px' : undefined }} title={full}>
                          {city || country
                            ? <>
                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{city || '--'}</div>
                                {country && <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>{country}</div>}
                              </>
                            : <span style={{ color: G.muted }}>--</span>}
                        </td>
                      );
                    })()}
                    {show('acciones') && <td style={{ padding: dense ? '7px 12px' : undefined }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDetailModal(p)} title="Ver detalle" style={{ padding: '4px 8px' }}>👁</button>
                        {p.status === 'SUCCEEDED' && (
                          <button className="btn btn-danger btn-sm" onClick={() => {
                            setConfirmModal({
                              title: 'Confirmar Reembolso',
                              message: `¿Estás seguro de que quieres reembolsar este pago de ${fmt((p.amount || 0) / 100)}?\n\nCliente: ${p.customerId || p.email || 'Desconocido'}`,
                              onConfirm: () => {
                                setConfirmModal(null);
                                paymentsApi.refund(p.id).then(() => { showToast('Reembolso procesado OK', 'success'); load(); }).catch(e => showToast(e.message, 'error'));
                              }
                            });
                          }}>Reembolsar</button>
                        )}
                        {p.status === 'REFUNDED' && (() => {
                          const fromSync = !!p.metadata?.refundedFromBalanceSync;
                          const label = fromSync ? '↩ Revertir (sync auto)' : '↩ Revertir Refund';
                          const confirmTitle = fromSync ? 'Revertir reembolso automático (sync)' : 'Revertir Reembolso';
                          const confirmMsg = fromSync
                            ? `Este pago fue marcado como REFUNDED por el sync automático con TailoredPay.\n\nOpciones:\n\n1) SAFE (recomendado si no se hizo refund real en el portal): solo cambiar el status a SUCCEEDED, SIN re-cobrar al cliente.\n\n2) RE-COBRAR (solo si el refund era real y quieres volver a cobrarlo): se ejecuta un charge nuevo en TailoredPay.\n\n¿Cuál prefieres?\n\nRef: ${p.metadata?.portalRefundTxId || '?'} · ${fmt((p.amount || 0) / 100)} · ${p.customerId || 'sin email'}`
                            : `¿Revertir el reembolso y marcar como SUCCEEDED?\n\nPago: ${fmt((p.amount || 0) / 100)}`;
                          return (
                            <button className="btn btn-sm" style={{ background: fromSync ? G.gold : G.green, color: '#000', fontWeight: 600 }} title={fromSync ? 'Este refund lo hizo el sync automático — revisa antes de revertir' : 'Revertir el refund'} onClick={() => {
                              setConfirmModal({
                                title: confirmTitle,
                                message: confirmMsg,
                                confirmLabel: fromSync ? 'Safe revert (no re-cobrar)' : 'Revertir',
                                extraLabel: fromSync ? 'Re-cobrar (si era real)' : null,
                                onConfirm: () => {
                                  setConfirmModal(null);
                                  // If the refund came from the sync cron, default to safe-revert
                                  // (no re-charge). The "extra" button handles the re-charge path
                                  // if the operator really wants it.
                                  paymentsApi.undoRefund(p.id, { safeRevert: fromSync })
                                    .then(() => { showToast(fromSync ? 'Status restaurado (sin re-cobrar)' : 'Reembolso revertido — pago marcado como SUCCEEDED', 'success'); load(); })
                                    .catch(e => showToast(e.message, 'error'));
                                },
                                onExtra: fromSync ? () => {
                                  setConfirmModal(null);
                                  paymentsApi.undoRefund(p.id, { safeRevert: false })
                                    .then(() => { showToast('Re-cobrado OK — marcado como SUCCEEDED', 'success'); load(); })
                                    .catch(e => showToast(e.message, 'error'));
                                } : null,
                              });
                            }}>{label}</button>
                          );
                        })()}
                        {p.status === 'SUCCEEDED' && !p.dxmHidden && (
                          <button className="btn btn-ghost btn-sm" title="Ocultar de DianXiaoMi (no lo verá el chino)" style={{ padding: '4px 8px', borderColor: G.orange, color: G.orange }} onClick={() => {
                            setConfirmModal({
                              title: 'Cancelar pedido en DianXiaoMi',
                              message: `El chino dejará de ver este pedido (no se envía).\nNO reembolsa ni cancela la suscripción.\n\nCliente: ${p.customerId || p.email || 'Desconocido'}`,
                              onConfirm: () => {
                                setConfirmModal(null);
                                fetch(`${BACKEND}/api/payments/${p.id}/cancel-fulfillment`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                                  body: JSON.stringify({ reason: 'operator-cancelled' }),
                                }).then(r => r.json()).then(d => {
                                  if (d?.success) { showToast('Pedido oculto de DianXiaoMi', 'success'); load(); }
                                  else showToast(d?.error || 'Error', 'error');
                                }).catch(e => showToast(e.message, 'error'));
                              }
                            });
                          }}>🚫 No enviar</button>
                        )}
                        {p.dxmHidden && (
                          <button className="btn btn-ghost btn-sm" title="Volver a mostrar en DianXiaoMi" style={{ padding: '4px 8px', borderColor: G.green, color: G.green }} onClick={() => {
                            fetch(`${BACKEND}/api/payments/${p.id}/restore-fulfillment`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                            }).then(r => r.json()).then(d => {
                              if (d?.success) { showToast('Pedido restaurado en DianXiaoMi', 'success'); load(); }
                              else showToast(d?.error || 'Error', 'error');
                            }).catch(e => showToast(e.message, 'error'));
                          }}>↻ Restaurar</button>
                        )}
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ color: G.muted, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>
            Mostrando {filtered.length === 0 ? 0 : startIdx + 1}–{endIdx} de {filtered.length} ({payments.length} totales) · página {safePage} de {totalPages}
          </div>
          <select
            className="form-input"
            style={{ width: 'auto', padding: '4px 26px 4px 8px', fontSize: 11 }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            title="Filas por página"
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pág.</option>)}
          </select>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-sm"
              disabled={safePage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              style={{ padding: '6px 12px', fontSize: 12, opacity: safePage <= 1 ? 0.4 : 1, cursor: safePage <= 1 ? 'not-allowed' : 'pointer' }}
            >← Anterior</button>
            <span style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', minWidth: 60, textAlign: 'center' }}>
              {safePage} / {totalPages}
            </span>
            <button
              className="btn btn-sm"
              disabled={safePage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              style={{ padding: '6px 12px', fontSize: 12, opacity: safePage >= totalPages ? 0.4 : 1, cursor: safePage >= totalPages ? 'not-allowed' : 'pointer' }}
            >Siguiente →</button>
          </div>
        )}
      </div>

      {detailModal && (
        <Modal title={`Pago ${detailModal.externalId ? '#' + detailModal.externalId.substring(0, 12) : '#' + detailModal.id?.substring(0, 8)}`} onClose={() => setDetailModal(null)}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Cliente</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{detailModal.customerId || detailModal.metadata?.customer?.email || detailModal.email || '--'}</div>
                {detailModal.metadata?.customer?.name && <div style={{ fontSize: 12, color: G.dim, marginTop: 4 }}>{detailModal.metadata.customer.name}</div>}
                {detailModal.metadata?.customer?.phone && <div style={{ fontSize: 12, color: G.dim }}>{detailModal.metadata.customer.phone}</div>}
                {detailModal.metadata?.customer?.address?.line1 && (
                  <div style={{ fontSize: 12, color: G.dim, marginTop: 4, lineHeight: 1.4 }}>
                    {detailModal.metadata.customer.address.line1}
                    {detailModal.metadata.customer.address.line2 ? `, ${detailModal.metadata.customer.address.line2}` : ''}
                    <br/>
                    {[detailModal.metadata.customer.address.postal_code, detailModal.metadata.customer.address.city, detailModal.metadata.customer.address.country].filter(Boolean).join(' ')}
                  </div>
                )}
                {detailModal.metadata?.shopName && <div style={{ fontSize: 12, color: G.dim, marginTop: 4 }}>Tienda: <span style={{ color: G.goldLight }}>{detailModal.metadata.shopName}</span></div>}
              </div>
              <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Detalles del pago</div>
                <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                  <div><span style={{ color: G.muted }}>Monto:</span> <strong style={{ color: G.goldLight }}>{fmt((detailModal.amount || 0) / 100)}</strong></div>
                  <div><span style={{ color: G.muted }}>Estado:</span> <StatusBadge status={detailModal.status} /></div>
                  <div><span style={{ color: G.muted }}>Gateway:</span> {detailModal.gateway || '--'} ({detailModal.psp})</div>
                  <div><span style={{ color: G.muted }}>ID externo:</span> <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>{detailModal.externalId || '--'}</span></div>
                </div>
              </div>
            </div>
            {/* ── Producto comprado ──────────────────────────────
                Shows the actual product(s) purchased. Highest-priority info for
                merchants reviewing an order. Falls back gracefully when the
                payment doesn't carry item data (old payments, batch, recurring). */}
            {(() => {
              const items = Array.isArray(detailModal.metadata?.items) ? detailModal.metadata.items : [];
              const hasItems = items.length > 0;
              const orderId = detailModal.metadata?.orderId;
              const source = detailModal.metadata?.source || 'checkout';
              // No product info at all AND nothing useful to show → skip this block
              if (!hasItems && !orderId && !detailModal.description) return null;
              return (
                <div style={{ background: `linear-gradient(135deg, ${G.card} 0%, #1a1a1a 100%)`, borderRadius: 12, padding: 18, border: `1px solid ${G.gold}40`, boxShadow: `0 0 0 1px ${G.gold}15` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: G.goldLight, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                      {hasItems ? `Producto${items.length > 1 ? 's' : ''} comprado${items.length > 1 ? 's' : ''}` : 'Detalle del pedido'}
                    </div>
                    {hasItems && (
                      <div style={{ fontSize: 10, color: G.muted, background: G.bg, padding: '3px 8px', borderRadius: 12, border: `1px solid ${G.border}` }}>
                        {items.length} articulo{items.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  {hasItems ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {items.map((item, idx) => {
                        const title = item.title || item.product_title || item.name || 'Producto sin nombre';
                        const qty = item.quantity || 1;
                        const price = item.price != null ? (parseFloat(item.price) / 100) : null;
                        const lineTotal = price != null ? (price * qty) : null;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, background: G.bg, borderRadius: 8, border: `1px solid ${G.border}` }}>
                            {item.image ? (
                              <img src={item.image} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', border: `1px solid ${G.border}`, flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 52, height: 52, borderRadius: 8, background: G.card, border: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>📦</div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{title}</div>
                              {item.variant_title && <div style={{ fontSize: 11, color: G.dim }}>{item.variant_title}</div>}
                              {item.sku && <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>SKU: {item.sku}</div>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: G.goldLight }}>{lineTotal != null ? fmt(lineTotal) : '--'}</div>
                              <div style={{ fontSize: 10, color: G.muted }}>
                                {price != null ? `${fmt(price)} × ${qty}` : `× ${qty}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ padding: 12, background: G.bg, borderRadius: 8, border: `1px dashed ${G.border}` }}>
                      <div style={{ fontSize: 13, color: G.dim, marginBottom: 6 }}>
                        {source === 'recurring' || source === 'charge-now' || source === 'auto-subscription'
                          ? 'Cobro recurrente de suscripcion'
                          : source === 'batch'
                            ? 'Cobro en lote'
                            : 'Sin detalle de productos en los metadatos del pago.'}
                      </div>
                      {detailModal.description && <div style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{detailModal.description}</div>}
                      {orderId && <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>Pedido: {orderId}</div>}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Plain description (shown separately only if we have items so description doesn't duplicate) */}
            {detailModal.description && Array.isArray(detailModal.metadata?.items) && detailModal.metadata.items.length > 0 && (
              <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Descripcion</div>
                <div style={{ fontSize: 13 }}>{detailModal.description}</div>
              </div>
            )}
            {/* Tracking number section */}
            {detailModal.status === 'SUCCEEDED' && (
              <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Numero de seguimiento</div>
                {detailModal.trackingNumber ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 14, fontWeight: 600, color: G.green }}>
                      {detailModal.trackingNumber}
                    </span>
                    <span style={{ fontSize: 11, color: G.muted }}> enviado el {detailModal.trackingAddedAt ? new Date(detailModal.trackingAddedAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '--'}</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id="tracking-input"
                      type="text"
                      placeholder="Introduce numero de tracking..."
                      style={{ flex: 1, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={async () => {
                        const trackingNumber = document.getElementById('tracking-input')?.value?.trim();
                        if (!trackingNumber) return;
                        try {
                          const r = await fetch(`${BACKEND}/api/payments/${detailModal.id}/tracking`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                            body: JSON.stringify({ trackingNumber }),
                          });
                          if (r.ok) {
                            const updated = await r.json();
                            setDetailModal({ ...detailModal, trackingNumber: updated.trackingNumber, trackingAddedAt: updated.trackingAddedAt });
                            fetchData();
                          }
                        } catch {}
                      }}
                      style={{ padding: '8px 16px' }}
                    >Enviar</button>
                  </div>
                )}
              </div>
            )}
            {detailModal.error && (
              <div style={{ background: G.redDim, borderRadius: 10, padding: 16, border: `1px solid ${G.red}30` }}>
                <div style={{ fontSize: 11, color: G.red, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Error</div>
                <div style={{ fontSize: 13, color: G.red }}>{detailModal.error}</div>
              </div>
            )}
            <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
              Fecha: {detailModal.createdAt ? new Date(detailModal.createdAt).toLocaleString('es-ES', { timeZone: APP_TZ }) : '--'} | Intentos: {detailModal.attempts || 1} | Fuente: {detailModal.metadata?.source || 'checkout'}
            </div>
          </div>
          {/* ── Buscar datos en pasarela + Crear suscripción ── */}
          {(detailModal.status === 'SUCCEEDED' || detailModal.status === 'REFUNDED') && (
            <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.gold}40`, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: G.goldLight, textTransform: 'uppercase', fontWeight: 700 }}>Crear Suscripción</div>
                {detailModal.externalId && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={async () => {
                    try {
                      showToast('Consultando pasarela...', 'info');
                      const r = await fetch(`${BACKEND}/api/payments/${detailModal.id}/gateway-info`, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` } });
                      if (!r.ok) { const d = await r.json().catch(()=>({})); throw new Error(d.error || 'Error'); }
                      const info = await r.json();
                      const parts = [
                        info.firstName && `Nombre: ${info.firstName} ${info.lastName || ''}`,
                        info.email && `Email: ${info.email}`,
                        info.phone && `Tel: ${info.phone}`,
                        info.address1 && `Dir: ${info.address1}, ${info.city || ''} ${info.zip || ''} ${info.country || ''}`,
                        info.ccNumber && `Tarjeta: ${info.ccType || ''} ${info.ccNumber}`,
                        info.customerVaultId && `Vault ID: ${info.customerVaultId}`,
                      ].filter(Boolean);
                      if (parts.length) {
                        showToast(parts.join(' | '), 'success');
                        // Auto-fill the email input if found
                        if (info.email) {
                          const input = document.getElementById('sub-email-input');
                          if (input) { input.value = info.email; }
                        }
                      } else {
                        showToast('No se encontraron datos del cliente en la pasarela', 'warning');
                      }
                    } catch (e) { showToast(e.message, 'error'); }
                  }}>🔍 Buscar datos en pasarela</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="sub-email-input"
                  type="email"
                  placeholder="Email del cliente (pulsa 🔍 para buscarlo en la pasarela)"
                  defaultValue={detailModal.customerId || detailModal.metadata?.customer?.email || detailModal.email || ''}
                  style={{ flex: 1, background: '#1a1a1a', border: `1px solid ${G.gold}60`, borderRadius: 8, padding: '8px 12px', color: G.goldLight, fontSize: 13, outline: 'none' }}
                />
                <button className="btn btn-sm" style={{ background: G.gold, color: '#000', fontWeight: 600, whiteSpace: 'nowrap' }} onClick={() => {
                  const email = document.getElementById('sub-email-input')?.value?.trim();
                  if (!email || !email.includes('@')) { showToast('Introduce un email válido', 'error'); return; }
                  setConfirmModal({
                    title: 'Crear Suscripción',
                    message: `¿Crear suscripción recurrente para ${email}?\n\nSe guardará la tarjeta del pago original para cobros futuros.`,
                    onConfirm: () => {
                      setConfirmModal(null);
                      subscriptionsApi.fromPayment({ paymentId: detailModal.id, email }).then(() => {
                        showToast('Suscripción creada correctamente', 'success'); load();
                      }).catch(e => showToast(e.response?.data?.error || e.message, 'error'));
                    }
                  });
                }}>+ Crear Suscripción</button>
              </div>
              <div style={{ fontSize: 10, color: G.muted, marginTop: 6 }}>Se usará la tarjeta del pago original (vault en TailoredPay) para cobros recurrentes.</div>
            </div>
          )}
          <div className="modal-actions" style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setDetailModal(null)}>Cerrar</button>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <div className="modal-bg" onClick={() => setConfirmModal(null)} style={{ zIndex: 10000 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: G.goldLight }}>{confirmModal.title}</div>
            <div style={{ fontSize: 14, color: G.dim, whiteSpace: 'pre-line', marginBottom: 24, lineHeight: 1.6 }}>{confirmModal.message}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmModal(null)} style={{ minWidth: 100 }}>Cancelar</button>
              {confirmModal.onExtra && (
                <button className="btn btn-danger" onClick={confirmModal.onExtra} style={{ minWidth: 100 }}>
                  {confirmModal.extraLabel || 'Opción 2'}
                </button>
              )}
              <button className={confirmModal.onExtra ? 'btn btn-primary' : 'btn btn-danger'} onClick={confirmModal.onConfirm} style={{ minWidth: 100 }}>
                {confirmModal.confirmLabel || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Gateways({ showToast }) {
  const [gateways, setGateways] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', psp: 'STRIPE', trafficPct: 0, active: true });
  const [creds, setCreds] = useState({ secretKey: '', webhookSecret: '', locationId: '', appId: '', environment: 'production', merchantId: '', baseUrl: '' });
  const [limitsGw, setLimitsGw] = useState(null);
  const [limForm, setLimForm] = useState({ amount: '', orders: '' });
  useEffect(() => {
    gatewaysApi.getAll().then(setGateways).catch(() => setGateways([]));
  }, []);

  const pspFields = {
    STRIPE: [
      { key: 'secretKey', label: 'Secret Key', placeholder: 'sk_live_...' },
      { key: 'webhookSecret', label: 'Webhook Secret', placeholder: 'whsec_...' },
    ],
    SQUARE: [
  { key: 'secretKey', label: 'Access Token', placeholder: 'EAAAxxxxx...' },
  { key: 'appId', label: 'Application ID', placeholder: 'sq0idp-...' },
  { key: 'locationId', label: 'Location ID', placeholder: 'LXXXXXXXXX' },
  { key: 'environment', label: 'Entorno', placeholder: 'production' },
],
    TAILORED: [
      { key: 'securityKey', label: 'Security Key', placeholder: 'Ej: AEytXm2d...' },
      { key: 'tokenizationKey', label: 'Tokenization Key', placeholder: 'Ej: 2YKd55-Kv52g9-...' },
    ],
  };

  const getCredentials = () => {
    if (form.psp === 'STRIPE') return { secretKey: creds.secretKey, webhookSecret: creds.webhookSecret };
    if (form.psp === 'SQUARE') return { accessToken: creds.secretKey, appId: creds.appId, locationId: creds.locationId, environment: creds.environment };
    return { securityKey: creds.securityKey, tokenizationKey: creds.tokenizationKey };
  };

  const save = () => {
    gatewaysApi.create({ ...form, credentials: getCredentials() })
      .then(g => { setGateways(prev => [...prev, g]); setModal(false); showToast('Pasarela añadida OK', 'success'); })
      .catch(e => showToast(e.message, 'error'));
  };

  const toggle = (gw) => {
    gatewaysApi.update(gw.id, { active: !gw.active })
      .then(u => { setGateways(prev => prev.map(g => g.id === gw.id ? u : g)); showToast(`Pasarela ${u.active ? 'activada' : 'pausada'}`, 'success'); });
  };

  const remove = (id) => {
    if (!window.confirm('¿Eliminar esta pasarela?')) return;
    gatewaysApi.delete(id).then(() => { setGateways(prev => prev.filter(g => g.id !== id)); showToast('Eliminada', 'info'); });
  };

  const openLimits = (g) => {
    setLimForm({ amount: g.dailyLimitAmount ? String(g.dailyLimitAmount) : '', orders: g.dailyLimitOrders ? String(g.dailyLimitOrders) : '' });
    setLimitsGw(g);
  };
  const saveLimits = () => {
    const dailyLimitAmount = limForm.amount === '' ? 0 : Math.max(0, parseFloat(limForm.amount) || 0);
    const dailyLimitOrders = limForm.orders === '' ? 0 : Math.max(0, parseInt(limForm.orders, 10) || 0);
    gatewaysApi.update(limitsGw.id, { dailyLimitAmount, dailyLimitOrders })
      .then(u => { setGateways(prev => prev.map(g => g.id === limitsGw.id ? u : g)); setLimitsGw(null); showToast('Tope diario guardado', 'success'); })
      .catch(e => showToast(e.message, 'error'));
  };

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <button className="btn btn-gold" onClick={() => setModal(true)}>+ Nueva pasarela</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Nombre</th><th>PSP</th><th>Tráfico %</th><th>Estado</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {gateways.length === 0 && (
                <tr><td colSpan={5}>
                  <div className="empty">
                    <div className="empty-icon">⬡</div>
                    Sin pasarelas configuradas. Añade tu primera pasarela.
                  </div>
                </td></tr>
              )}
              {gateways.map(g => (
                <tr key={g.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {PSP_LOGOS[g.psp] ? <img src={PSP_LOGOS[g.psp]} alt={g.psp} style={{ width: 32, height: 22, objectFit: 'contain', background: '#fff', borderRadius: 5, padding: '2px 5px', border: `1px solid ${G.border}`, flexShrink: 0 }} /> : <div className="gateway-psp-icon">{g.psp?.[0] || '?'}</div>}
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${g.psp === 'STRIPE' ? 'badge-blue' : g.psp === 'SQUARE' ? 'badge-gold' : 'badge-green'}`}>
                      {g.psp}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div className="progress" style={{ width: 100 }}>
                        <div className="progress-fill" style={{ width: `${g.trafficPct || 0}%` }} />
                      </div>
                      <span className="mono" style={{ fontSize: 13, color: G.goldLight, minWidth: 36 }}>{g.trafficPct || 0}%</span>
                    </div>
                  </td>
                  <td><StatusBadge status={g.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggle(g)}>{g.active ? 'Pausar' : 'Activar'}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openLimits(g)} title="Tope diario del checkout">
                        Tope{(g.dailyLimitAmount || g.dailyLimitOrders) ? ' ✓' : ''}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => remove(g.id)}>Eliminar</button>
                    </div>
                    {(g.dailyLimitAmount || g.dailyLimitOrders) ? (
                      <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>
                        Tope/día: {g.dailyLimitAmount ? `${g.dailyLimitAmount}€` : '—'}{g.dailyLimitOrders ? ` · ${g.dailyLimitOrders} ped.` : ''}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {limitsGw && (
        <Modal title={`Tope diario — ${limitsGw.name}`} onClose={() => setLimitsGw(null)}>
          <p style={{ color: G.muted, fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
            Cuando esta pasarela llegue a su tope de <b>hoy</b>, deja de salir en el checkout y el tráfico rota a las demás. Se reinicia solo cada día. Vacío o 0 = sin tope. El importe se cuenta en € (todo convertido).
          </p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Límite de importe (€/día)</label>
              <input type="number" min={0} className="form-input" placeholder="ej: 500 — vacío = sin tope" value={limForm.amount} onChange={e => setLimForm(p => ({ ...p, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Límite de pedidos (nº/día)</label>
              <input type="number" min={0} className="form-input" placeholder="ej: 50 — vacío = sin tope" value={limForm.orders} onChange={e => setLimForm(p => ({ ...p, orders: e.target.value }))} />
            </div>
          </div>
          <button className="btn btn-gold" onClick={saveLimits}>Guardar tope</button>
        </Modal>
      )}

      {modal && (
        <Modal title="Nueva pasarela de pago" onClose={() => setModal(false)}>
          <div className="form-group">
            <label className="form-label">Nombre identificador</label>
            <input className="form-input" placeholder="ej: Stripe -- cuenta principal" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Proveedor (PSP)</label>
              <select className="form-input" value={form.psp} onChange={e => setForm(p => ({ ...p, psp: e.target.value }))}>
                <option value="STRIPE">Stripe</option>
                <option value="SQUARE">Square</option>
                <option value="TAILORED">TailoredPayments</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">% de tráfico</label>
              <input type="number" className="form-input" min={0} max={100} value={form.trafficPct} onChange={e => setForm(p => ({ ...p, trafficPct: +e.target.value }))} />
            </div>
          </div>
          {pspFields[form.psp]?.map(f => (
            <div key={f.key} className="form-group">
              <label className="form-label">{f.label}</label>
              <input
                type={f.key.includes('Key') || f.key.includes('Token') || f.key.includes('Secret') ? 'password' : 'text'}
                className="form-input" placeholder={f.placeholder}
                value={creds[f.key] || ''}
                onChange={e => setCreds(p => ({ ...p, [f.key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
            <button className="btn btn-gold" onClick={save}>Guardar pasarela</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Subscriptions({ showToast, user }) {
  const [subs, setSubs] = useState([]);
  const [byStatus, setByStatus] = useState({ trial: 0, active: 0, failed: 0, cancelled: 0, paused: 0, total: 0 });
  const [newToday, setNewToday] = useState(0);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('nextBillingDate');  // 'nextBillingDate' | 'firstAttemptDate'
  const [sortOrder, setSortOrder] = useState('asc');  // 'asc' | 'desc'
  const [overdueJob, setOverdueJob] = useState(null);  // { jobId, total, processed, success, failed, skipped, currentEmail, inProgress }
  const overduePollRef = useRef(null);
  // Batch A+B cobro masivo (2026-06-02 — evita errores de monto en charge-now manual)
  const [abBatchModal, setAbBatchModal] = useState(false);
  const [abBatchTargetStatus, setAbBatchTargetStatus] = useState('ACTIVE'); // 'ACTIVE' | 'TRIAL'
  const [abBatchPreview, setAbBatchPreview] = useState(null);  // { customersCount, withAB, aOnly, etaMinutes }
  const [abBatchJob, setAbBatchJob] = useState(null);  // { jobId, totalCustomers, processed, success, failed, totalRecoveredCents, status }
  const [abAmountA, setAbAmountA] = useState('89.97');
  const [abAmountB, setAbAmountB] = useState('90.87');
  const [abFallbackB, setAbFallbackB] = useState('49.99');
  const [abDelaySec, setAbDelaySec] = useState('25');
  const abBatchPollRef = useRef(null);
  const [chargeModal, setChargeModal] = useState(null);
  const [infoModal, setInfoModal] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeDesc, setChargeDesc] = useState('');
  const [charging, setCharging] = useState(false);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    subscriptionsApi.getAll()
      .then(d => {
        setSubs(d.subscriptions || []);
        setByStatus(d.byStatus || { trial: 0, active: 0, failed: 0, cancelled: 0, paused: 0, total: (d.subscriptions || []).length });
        setNewToday(d.newToday || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const chargeNow = async (opts = {}) => {
    if (!chargeAmount || !chargeModal) return;
    setCharging(true);
    try {
      const url = `${BACKEND}/api/subscriptions/${chargeModal.id}/charge-now${opts.force ? '?force=1' : ''}`;
      const r = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ amount: Math.round(parseFloat(chargeAmount) * 100), description: chargeDesc || undefined }),
      });
      const d = await r.json();
      // 409 = guard blocked (recent fail, hard decline, etc.). Offer force option.
      if (r.status === 409 && d.error && !opts.force) {
        const confirmed = window.confirm(
          d.error + '\n\n' +
          '⚠️ AVISO GRAVE: Si la tarjeta YA ha fallado recientemente, reintentar ahora puede hacer que:\n' +
          '• El banco del cliente marque la tarjeta como "SUSPECTED FRAUD"\n' +
          '• El cliente quede PERMANENTEMENTE bloqueado para cobros recurrentes\n' +
          '• Tu decline ratio suba → riesgo de "high-risk merchant"\n\n' +
          '¿Forzar el cobro de todas formas?'
        );
        setCharging(false);
        if (confirmed) {
          return chargeNow({ force: true });
        }
        return;
      }
      if (!r.ok) throw new Error(d.error || 'Error al cobrar');
      const msg = d.activated
        ? `Cobro de ${chargeAmount}${fmtSymbol()} OK — suscripcion activada + email de bienvenida enviado`
        : `Cobro de ${chargeAmount}${fmtSymbol()} realizado OK`;
      showToast(msg, 'success');
      setChargeModal(null); setChargeAmount(''); setChargeDesc(''); load();
    } catch (e) { showToast(e.message, 'error'); }
    setCharging(false);
  };

  const cancelSub = async (id) => {
    if (!window.confirm('¿Cancelar suscripcion? Esta accion detiene los cobros automaticos.')) return;
    try {
      const r = await fetch(`${BACKEND}/api/subscriptions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      showToast('Suscripcion cancelada', 'info');
      load();
    } catch (e) {
      showToast('Error al cancelar: ' + e.message, 'error');
    }
  };

  const loadInfo = async (sub) => {
    try {
      const r = await fetch(`${BACKEND}/api/subscriptions/${sub.id}/info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setCustomerInfo(d);
      setInfoModal(sub);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const filtered = subs.filter(s => {
    const ms = filter === 'ALL' || s.status === filter;
    const mq = !search || s.customerId?.toLowerCase().includes(search.toLowerCase());
    return ms && mq;
  }).slice().sort((a, b) => {
    // Sort by the active sort key ('nextBillingDate' or 'firstAttemptDate').
    // 'asc' = earliest first, 'desc' = latest first. Rows without a value
    // sink to the bottom in both orders.
    const pick = (s) => {
      const raw = sortKey === 'firstAttemptDate' ? s.firstAttemptDate : s.nextBillingDate;
      return raw ? new Date(raw).getTime() : null;
    };
    const av = pick(a);
    const bv = pick(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return sortOrder === 'asc' ? av - bv : bv - av;
  });

  // Click a header: if it's already the active sort key, flip asc/desc.
  // Otherwise, switch to that key with 'asc' default.
  const onSortClick = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };
  const sortArrow = (key) => sortKey === key ? (sortOrder === 'asc' ? '▲' : '▼') : '⇅';

  // Style helper for the top breakdown tiles
  const statCard = (label, value, color, dot, sub) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} />
        <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  // Status label in Spanish for the table badges
  const statusLabel = (s) => ({
    TRIAL: 'Trial',
    ACTIVE: 'Activo',
    PAUSED: 'Pausada',
    CANCELLED: 'Cancelada',
    FAILED: 'Fallida',
  })[s] || s || '--';

  const statusStyle = (s) => {
    if (s === 'TRIAL')     return { bg: G.blue + '20', color: G.blue, border: G.blue + '50' };
    if (s === 'ACTIVE')    return { bg: G.green + '20', color: G.green, border: G.green + '50' };
    if (s === 'FAILED')    return { bg: G.red + '20', color: G.red, border: G.red + '50' };
    if (s === 'CANCELLED') return { bg: G.muted + '20', color: G.muted, border: G.muted + '50' };
    if (s === 'PAUSED')    return { bg: G.goldLight + '20', color: G.goldLight, border: G.goldLight + '50' };
    return { bg: G.border, color: G.muted, border: G.border };
  };

  return (
    <div className="content">
      {/* Status breakdown — TRIAL / ACTIVO / FALLIDAS / PAUSADAS / CANCELADAS */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {statCard('Nuevas hoy', newToday, G.goldLight, G.goldLight, 'Altas del dia (España)')}
        {statCard('Trial', byStatus.trial, G.blue, G.blue, 'En periodo de prueba')}
        {statCard('Activo', byStatus.active, G.green, G.green, 'Cobrando recurrente')}
        {statCard('Fallidas', byStatus.failed, G.red, G.red, 'Retry agotado')}
        {statCard('Pausadas', byStatus.paused, G.goldLight, G.goldLight, 'Pausa manual')}
        {statCard('Canceladas', byStatus.cancelled, G.muted, G.muted, 'Dadas de baja')}
      </div>
      <div style={{ fontSize: 10, color: G.muted, marginBottom: 16, fontFamily: 'DM Mono, monospace' }}>
        Horario Madrid · Retry: 3 intentos inmediatos + cron +4h → +24h → +48h → +72h → +120h (6 total)
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* SS4 (2026-06-10) — Sync Stripe subs + Batch por fecha. Visibles
            para TODO tenant Gold (no solo admin), porque son operaciones
            normales del cliente con su propia cuenta Stripe. */}
        <button className="btn" style={{ fontWeight: 700, background: '#635BFF', color: '#fff', border: 'none' }}
          onClick={async (e) => {
            const btn = e.currentTarget; btn.disabled = true; const orig = btn.textContent; btn.textContent = 'Sincronizando…';
            try {
              const r = await fetch(`${BACKEND}/api/subscriptions/sync-stripe`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              });
              const d = await r.json();
              if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
              showToast(`✅ Sync OK — ${d.inserted} nuevas, ${d.updated} actualizadas, ${d.errors} errores (de ${d.fetched} subs en Stripe)`, 'success');
              load();
            } catch (err) { showToast('Error: ' + err.message, 'error'); }
            finally { btn.disabled = false; btn.textContent = orig; }
          }}>↻ Sincronizar Stripe</button>

        <button className="btn" style={{ fontWeight: 700, background: '#0ea5e9', color: '#fff', border: 'none' }}
          onClick={async () => {
            const today = new Date().toISOString().slice(0, 10);
            const date = window.prompt('Fecha de creación de las subs (YYYY-MM-DD).\nEjemplo: 2026-06-05 cobrará las subs Stripe nativas creadas ese día.\n\nFecha:', today);
            if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
              if (date) showToast('Formato fecha inválido (debe ser YYYY-MM-DD)', 'error');
              return;
            }
            // 2026-08-04 — elegir procesador (o todos). Cargamos las pasarelas
            // Stripe del tenant y dejamos elegir de cuál cobrar.
            let gatewayId = null, gwLabel = 'TODOS los procesadores';
            try {
              const gr = await fetch(`${BACKEND}/api/gateways`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              const raw = await gr.json();
              const stripeGws = (Array.isArray(raw) ? raw : (raw.gateways || [])).filter(g => g && String(g.psp).toUpperCase() === 'STRIPE');
              if (stripeGws.length > 1) {
                const menu = stripeGws.map((g, i) => `${i + 1}) ${g.name}`).join('\n');
                const pick = window.prompt(`¿De qué procesador cobrar las del ${date}?\n\n${menu}\n0) TODOS\n\nEscribe el número:`, '0');
                if (pick === null) return; // canceló
                const n = parseInt(pick, 10);
                if (n >= 1 && n <= stripeGws.length) { gatewayId = stripeGws[n - 1].id; gwLabel = stripeGws[n - 1].name; }
              }
            } catch (e) { /* si falla la carga, seguimos con TODOS */ }
            if (!window.confirm(`¿Lanzar batch de cobro AHORA?\n\nFecha: ${date}\nProcesador: ${gwLabel}\n\nEsto disparará Stripe.invoices.create + pay en cada sub.`)) return;
            fetch(`${BACKEND}/api/batch/charge-by-date`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ date, gatewayId }),
            })
              .then(r => r.json().then(d => ({ ok: r.ok, d })))
              .then(({ ok, d }) => {
                if (!ok) { showToast('Error: ' + (d.error || 'desconocido'), 'error'); return; }
                if (d.candidates === 0) { showToast(d.message || 'No se encontraron subs ese día/procesador', 'info'); return; }
                showToast(`✅ Batch ${date} (${gwLabel}) — ${d.ok} cobradas, ${d.failed} fallidas de ${d.candidates}`, d.ok > 0 ? 'success' : 'error');
                load();
              })
              .catch(err => showToast('Error: ' + err.message, 'error'));
          }}>📅 Cobrar batch por fecha</button>

        {/* 2026-06-07 — All subscription bulk-ops (retry-all, batch A+B,
            audit, force-import, smart cleanup, club-welcome reseed…) are
            SaaS-operator tools. Merchants never operate at this level.
            Wrapped in a single isAdmin fragment so the toolbar collapses
            for clients while super-admins keep the full operator panel. */}
        {isAdmin && <>
        {byStatus.failed > 0 && (
          <button id="btn-retry-all" className="btn btn-danger" style={{ fontWeight: 700 }} onClick={async (e) => {
            // Confirm with explanation about the smart-retry cooldown.
            // Operator's common mistake: clicking right after a failed cron cycle,
            // which means the card will get retried 4h later at the SAME issuer
            // state → guaranteed to fail + wastes attempts + raises decline ratio.
            // The 48h/60h cooldown exists to let Account Updater refresh tokens
            // and issuers cool down before retry.
            const confirmed = window.confirm(
              `Reintentar ${byStatus.failed} suscripciones fallidas.\n\n` +
              `⚠️ Las RCs en "cooldown smart-retry" (esperando ventana de 48-72h tras el último fallo) se SALTARÁN automáticamente para no quemar intentos.\n\n` +
              `Si quieres forzar TODAS (incluso las en cooldown) cancela y usa el botón con Shift.\n\n` +
              `¿Continuar?`
            );
            if (!confirmed) return;
            const force = e.shiftKey === true;
            const btn = e.currentTarget;
            btn.disabled = true;
            btn.textContent = force ? '⏳ Forzando...' : '⏳ Reintentando...';
            showToast(force ? `Forzando ${byStatus.failed}...` : `Reintentando (respetando cooldown)...`, 'info');
            try {
              const url = `${BACKEND}/api/subscriptions/retry-all-failed${force ? '?force=1' : ''}`;
              const r = await fetch(url, {
                method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              });
              const data = await r.json();
              if (r.status === 409) {
                showToast(data.error || 'Ya hay un batch en curso', 'error');
              } else if (data.succeeded > 0 || data.failed > 0) {
                const skippedMsg = data.skipped > 0 ? ` · ⏸ ${data.skipped} en cooldown` : '';
                showToast(`✅ ${data.succeeded} cobrados OK, ❌ ${data.failed} fallidos de ${data.total}${skippedMsg}`, data.succeeded > 0 ? 'success' : 'error');
              } else {
                showToast(data.message || 'Sin resultados', 'info');
              }
              load();
            } catch (e) { showToast('Error: ' + e.message, 'error'); }
            finally { btn.disabled = false; btn.textContent = `🔄 Reintentar fallidas (${byStatus.failed})`; }
          }}>🔄 Reintentar fallidas ({byStatus.failed})</button>
        )}
        <button className="btn btn-gold" style={{ fontWeight: 700 }} disabled={!!overdueJob?.inProgress} onClick={async () => {
          if (overdueJob?.inProgress) { showToast('Ya hay un batch en curso', 'info'); return; }
          if (!window.confirm('Procesa ahora TODAS las suscripciones atrasadas (TRIAL/ACTIVE/FAILED con retries) a 1 cobro por minuto.\n\nNo cierres el dashboard durante el proceso.\n\n¿Continuar?')) return;
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/process-overdue`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const data = await r.json();
            if (data.total === 0) { showToast(data.message || 'Sin atrasadas', 'info'); return; }
            if (data.error) { showToast('Error: ' + data.error, 'error'); return; }
            setOverdueJob({ ...data, processed: 0, success: 0, failed: 0, skipped: 0, currentEmail: '', inProgress: true });
            showToast(`🚀 Procesando ${data.total} atrasadas (~${data.etaMinutes} min)`, 'success');
            // Poll status every 15s
            if (overduePollRef.current) clearInterval(overduePollRef.current);
            overduePollRef.current = setInterval(async () => {
              try {
                const s = await fetch(`${BACKEND}/api/subscriptions/process-overdue/status/${data.jobId}`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                const st = await s.json();
                if (st.error) return;
                setOverdueJob(prev => ({ ...(prev || {}), ...st }));
                if (!st.inProgress) {
                  clearInterval(overduePollRef.current);
                  overduePollRef.current = null;
                  showToast(`✅ Batch completado: ${st.success} OK · ${st.failed} fallos · ${st.skipped} saltados`, 'success');
                  load();
                }
              } catch { /* keep polling */ }
            }, 15000);
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>⚡ Procesar atrasadas</button>
        <button className="btn" style={{ fontWeight: 700, background: '#0ea5e9', color: '#fff', border: 'none' }} disabled={abBatchJob?.status === 'running'} onClick={async () => {
          try {
            showToast('Calculando candidatos A+B ACTIVE...', 'info');
            const r = await fetch(`${BACKEND}/api/batch/preview-active-ab`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'ACTIVE' }),
            });
            const data = await r.json();
            if (!r.ok) { showToast(data.error || 'Error preview', 'error'); return; }
            setAbBatchTargetStatus('ACTIVE');
            setAbAmountA('89.97'); setAbAmountB('90.87');
            setAbBatchPreview(data);
            setAbBatchModal(true);
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>💳 Cobrar A+B Activos</button>
        <button className="btn" style={{ fontWeight: 700, background: '#8b5cf6', color: '#fff', border: 'none' }} disabled={abBatchJob?.status === 'running'} onClick={async () => {
          try {
            showToast('Calculando candidatos A+B TRIAL...', 'info');
            const r = await fetch(`${BACKEND}/api/batch/preview-active-ab`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'TRIAL' }),
            });
            const data = await r.json();
            if (!r.ok) { showToast(data.error || 'Error preview', 'error'); return; }
            setAbBatchTargetStatus('TRIAL');
            setAbAmountA('89.97'); setAbAmountB('90.87');
            setAbBatchPreview(data);
            setAbBatchModal(true);
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🎯 Cobrar A+B Trials</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} onClick={async () => {
          showToast('Reparando links de rebill...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/repair-rebill-links`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const data = await r.json();
            showToast(`🔧 ${data.fixed} links reparados de ${data.total}`, data.fixed > 0 ? 'success' : 'info');
            load();
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🔧 Reparar rebills</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12, borderColor: G.gold, color: G.gold }} onClick={async () => {
          // Vault backfill — preview first, then confirm to run.
          try {
            showToast('Calculando candidatos...', 'info');
            const preview = await subscriptionsApi.vaultBackfillPreview();
            const t = preview.totals || {};
            if (t.candidatesToBackfill === 0) {
              showToast(`Nada que migrar — ${t.alreadyVaulted} ya vaulted, ${t.noRawCardData} sin tarjeta cruda`, 'info');
              return;
            }
            const etaMin = Math.ceil(t.candidatesToBackfill * 0.6 / 60);
            const msg =
              `Voy a migrar ${t.candidatesToBackfill} clientes al Customer Vault de TailoredPay.\n\n` +
              `• Ya vaulted: ${t.alreadyVaulted}\n` +
              `• Candidatos: ${t.candidatesToBackfill}\n` +
              `• Sin SCF link: ${t.candidatesWithoutScfLink} (aún así se intenta)\n\n` +
              `Duración estimada: ${etaMin} min (cobro de $0 por cliente para verificar la tarjeta).\n` +
              `La tarjeta en crudo se mantiene como backup, NO se borra.\n\n` +
              `¿Continuar?`;
            if (!window.confirm(msg)) return;
            showToast(`🔐 Migrando ${t.candidatesToBackfill} clientes al vault... (puede tardar ${etaMin} min)`, 'info');
            const result = await subscriptionsApi.vaultBackfillRun();
            const r = result.result || {};
            showToast(`✅ ${r.success} migrados · ❌ ${r.errors} errores · intentados ${r.attempted}`, r.errors > 0 ? 'info' : 'success');
          } catch (e) {
            showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
          }
        }}>🔐 Migrar al Vault</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12, borderColor: G.goldLight, color: G.goldLight }} onClick={async () => {
          try {
            const audit = await subscriptionsApi.vaultBackfillAudit();
            // Dump to a readable alert so the user can copy/paste it here.
            const c = audit.counts || {};
            const a = audit.assessment || {};
            const cs = audit.cumulativeStats || {};
            const imp = audit.impossiblesBreakdown || {};
            const lines = [
              `📊 AUDITORÍA VAULT`,
              ``,
              `RCs activos: ${c.active}`,
              `· Vaulted (OK): ${c.vaulted} (${c.vaultedPct}%)`,
              `· Sin vault + con tarjeta (pendientes migrar): ${c.notVaultedWithCard}`,
              `· Sin vault + sin tarjeta (imposibles): ${c.notVaultedNoCard}`,
              ``,
              `SCF linked: ${c.scfLinked}/${c.vaulted}  (${a.scfCoveragePct}%)`,
              `Backup de tarjeta cruda: ${c.vaultedWithCardBackup} vaulted mantienen raw card`,
              ``,
              `─── Desglose "imposibles" (${imp.total || 0}) ───`,
              `· Aún cobrables via repeat-sale: ${imp.stillBillableViaRepeatSale || 0}`,
              `· Realmente muertos (sin ninguna vía): ${imp.trulyDead || 0}`,
              `· Por status: ACTIVE=${imp.byStatus?.ACTIVE || 0}, TRIAL=${imp.byStatus?.TRIAL || 0}`,
              `· Por source: ${Object.entries(imp.bySource || {}).map(([k, v]) => `${k}=${v}`).join(', ')}`,
              ``,
              `Acumulado: ${cs.totalSuccess} migrados, ${cs.totalErrors} errores`,
              `Migración completa: ${a.migrationComplete ? 'SÍ' : 'NO — quedan ' + c.notVaultedWithCard}`,
              `Salud general: ${audit.healthy ? '✅ OK' : '⚠️ revisar'}`,
              ``,
              `(JSON completo copiado al portapapeles)`,
            ];
            // Copy to clipboard + show alert
            const text = lines.join('\n') + '\n\n--- JSON completo ---\n' + JSON.stringify(audit, null, 2);
            if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(() => {});
            showToast('Auditoría copiada al portapapeles', 'success');
            window.alert(lines.join('\n'));
            console.log('[VaultAudit]', audit);
          } catch (e) {
            showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
          }
        }}>📊 Auditar Vault</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12, borderColor: G.blue, color: G.blue }} onClick={async () => {
          try {
            const days = parseInt(window.prompt('¿Cuántos días quieres revisar? (por defecto 7)', '7'), 10) || 7;
            const audit = await subscriptionsApi.upcomingChargesAudit(days);
            const b = audit.breakdown || {};
            const a = audit.assessment || {};
            const lines = [
              `📅 CARGOS PRÓXIMOS ${audit.windowDays} DÍAS`,
              ``,
              `Total: ${audit.totalCharges} cargos · $${audit.totalAmount}`,
              ``,
              `─── Desglose por vía de cobro ───`,
              `✅ Vault (MIT):      ${b.tier1Vault.count}  · $${b.tier1Vault.amount}`,
              `✓  Tarjeta cruda:    ${b.tier2RawCard.count}  · $${b.tier2RawCard.amount}`,
              `⚠  Repeat-sale:     ${b.tier3Fallback.count}  · $${b.tier3Fallback.amount}`,
              `    ├─ Wallets (fallarán): ${b.tier3Fallback.walletLikely}`,
              `    └─ No-wallet (OK):     ${b.tier3Fallback.nonWallet}`,
              `❌ Sin vía posible:  ${b.dead.count}  · $${b.dead.amount}`,
              ``,
              `Recuperable:  ${a.recoverableShare}%`,
              `Predicción aprobación:  ${audit.predictedSuccessPct}%`,
              `Estado: ${a.healthy ? '✅ Todos OK' : '⚠ Hay riesgos — mira el JSON'}`,
              ``,
              `(JSON copiado al portapapeles)`,
            ];
            const text = lines.join('\n') + '\n\n--- JSON completo ---\n' + JSON.stringify(audit, null, 2);
            if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(() => {});
            showToast('Auditoría de cobros copiada al portapapeles', 'success');
            window.alert(lines.join('\n'));
            console.log('[UpcomingChargesAudit]', audit);
          } catch (e) {
            showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
          }
        }}>📅 Auditar Próximos</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} onClick={async () => {
          if (!window.confirm('¿Enviar email de bienvenida al club a todos los activos que no lo tienen?')) return;
          showToast('Enrollando en el club...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/enroll-missing-club`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const data = await r.json();
            showToast(`📧 ${data.enrolled} enrollados de ${data.total}`, data.enrolled > 0 ? 'success' : 'info');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>📧 Enviar club a faltantes</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} onClick={async () => {
          showToast('Calculando clientes sin welcome...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/send-missing-club-welcomes?dryRun=true`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const preview = await r.json();
            if (preview.error) return showToast('Error: ' + preview.error, 'error');
            if (preview.missing === 0) return showToast(`✓ Todos los ${preview.totalChargedActive} cobrados activos ya recibieron el welcome`, 'success');
            const diag = preview.diagnostic || {};
            const diagMsg = `\n\n🔬 DIAGNÓSTICO:\nTotal emailLogs: ${diag.totalEmailLogs || 0}\nclubMembers: ${diag.clubMembers || 0}\ncombinedAlreadySent: ${diag.combinedAlreadySent || 0}\n\nTemplates en emailLogs (ordenado):\n${(diag.templatesSortedByCount || []).slice(0, 15).join('\n')}\n\nEmails faltan (10):\n${(diag.missingSample || []).join('\n')}`;
            if (!window.confirm(`Enviar club_welcome a ${preview.missing} clientes ACTIVOS?\n\nTotal activos: ${preview.totalActive}\nActivos cobrados: ${preview.totalChargedActive}\nYa recibieron: ${preview.alreadySent}\nFaltan: ${preview.missing}${diagMsg}\n\n¿Confirmar envío?`)) return;
            showToast(`📧 Enviando ${preview.missing} welcomes... no cierres el dashboard`, 'info');
            const r2 = await fetch(`${BACKEND}/api/subscriptions/send-missing-club-welcomes?dryRun=false`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const result = await r2.json();
            if (result.error) return showToast('Error: ' + result.error, 'error');
            showToast(`✅ Enviados: ${result.sent} · Fallados: ${result.failed}`, result.sent > 0 ? 'success' : 'error');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🎉 Enviar welcomes atrasados</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} title="Reenviar welcome a un email concreto (ignora idempotencia)" onClick={async () => {
          const email = window.prompt('Email del cliente al que reenviar el club_welcome:\n\n(se ignorará la idempotencia — se envía aunque ya tenga clubMember/emailLog)');
          if (!email || !/@/.test(email)) return;
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/send-missing-club-welcomes?dryRun=false&forceEmail=${encodeURIComponent(email.trim().toLowerCase())}`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const result = await r.json();
            if (result.error) return showToast('Error: ' + result.error, 'error');
            if (result.sent > 0) showToast(`✅ Enviado a ${email}`, 'success');
            else if (result.missing === 0) showToast(`✗ ${email} no es un cliente ACTIVE tuyo`, 'error');
            else showToast(`✗ No se pudo enviar: ${result.details?.[0]?.reason || 'razón desconocida'}`, 'error');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>📧 Reenviar a email...</button>
        <button className="btn btn-gold" style={{ fontWeight: 700, fontSize: 12 }} title="Arregla los datos históricos: inserta pagos FAILED que faltan, añade shopId a pagos existentes, y envía los club_welcome pendientes" onClick={async () => {
          showToast('Analizando datos a reparar...', 'info');
          try {
            // 1. Preview
            const r = await fetch(`${BACKEND}/api/subscriptions/repair-batch?dryRun=true`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const preview = await r.json();
            if (preview.error) return showToast('Error: ' + preview.error, 'error');
            const s1 = preview.step1_failedBackfill || {};
            const s2 = preview.step2_shopIdEnrich || {};
            const msg = `REPARACIÓN DE DATOS\n\n`
              + `1. Pagos FALLIDOS que faltan en historial:\n`
              + `   · Escaneados: ${s1.scanned || 0}\n`
              + `   · A insertar: ${s1.inserted || 0}\n`
              + `   · Ya existían: ${s1.skipped || 0}\n\n`
              + `2. Pagos EXITOSOS:\n`
              + `   · A enriquecer con shopId: ${s2.updated || 0}\n`
              + `   · A insertar (phantom — ACTIVE sin row): ${s2.phantomInserted || 0}\n`
              + `   · Ya existían: ${s2.phantomSkipped || 0}\n\n`
              + `3. Emails del club pendientes:\n`
              + `   · Se enviarán tras ejecutar (3s entre cada uno)\n\n`
              + `¿Ejecutar reparación?`;
            if (!window.confirm(msg)) return;
            showToast(`🔧 Reparando... no cierres el dashboard (puede tardar varios min si hay muchos emails)`, 'info');
            // 2. Execute
            const r2 = await fetch(`${BACKEND}/api/subscriptions/repair-batch?dryRun=false`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const result = await r2.json();
            if (result.error) return showToast('Error: ' + result.error, 'error');
            const e1 = result.step1_failedBackfill || {};
            const e2 = result.step2_shopIdEnrich || {};
            const e3 = result.step3_emailsSent || {};
            const done = `✅ Reparado:\n`
              + `· ${e1.inserted || 0} pagos FAILED insertados\n`
              + `· ${e2.updated || 0} pagos SUCCEEDED enriquecidos\n`
              + `· ${e2.phantomInserted || 0} pagos SUCCEEDED insertados (phantom)\n`
              + `· ${e3.sent || 0} emails enviados (${e3.failed || 0} fallaron)`;
            showToast(done, 'success');
            window.alert(done);
            // Refrescar la vista de subscripciones (función `load` local al componente)
            try { load(); } catch {}
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🔧 Reparar datos históricos</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} title="Analiza qué RCs tienen datos incompletos (IP, dirección, phone...) que causan 'do not honor' en el banco" onClick={async () => {
          showToast('Auditando billing...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/audit/billing`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const g = d.gapCounts || {};
            const rf = d.recentFailed || {};
            const recs = (d.recommendations || []).map(x => `  ⚠️ ${x}`).join('\n');
            const gapLines = Object.entries(g).filter(([, v]) => v > 0).map(([k, v]) => `  · ${k}: ${v}`).join('\n');
            const msg = `AUDITORÍA DE BILLING & DO-NOT-HONOR\n\n`
              + `📊 TOTALES:\n`
              + `  · RCs totales: ${d.totalRCs}\n`
              + `  · RCs cobrables (ACTIVE/TRIAL/FAILED-retry): ${d.chargeableRCs}\n`
              + `  · Con billing completo: ${d.completeBilling}\n`
              + `  · Con billing incompleto: ${d.incompleteBilling}\n\n`
              + `🔎 CAMPOS QUE FALTAN:\n${gapLines || '  (ninguno)'}\n\n`
              + `❌ DO NOT HONOR (últimos 30 fails):\n`
              + `  · Do not honor: ${rf.doNotHonorCount} / ${rf.total} (${rf.doNotHonorRate}%)\n\n`
              + (recs ? `💡 RECOMENDACIONES:\n${recs}\n\n` : '')
              + `Detalle en consola. 50 RCs con gaps listados ahí.`;
            console.log('[Billing Audit]', d);
            window.alert(msg);
            showToast(`Auditoría: ${d.incompleteBilling} RCs con billing incompleto`, d.incompleteBilling > 0 ? 'info' : 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🏦 Auditar billing</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} title="Estima cuántos cobros FAILED podrías recuperar si reintentas ahora" onClick={async () => {
          showToast('Analizando fallos...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/audit/retry-forecast`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const d = await r.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const s = d.summary || {};
            const b = d.buckets || {};
            const recs = (d.recommendations || []).map(x => `  💡 ${x}`).join('\n');
            const msg = `PRONÓSTICO DE RECUPERACIÓN\n\n`
              + `📊 SUMMARY:\n`
              + `  · Total FAILED: ${s.totalFailed}\n`
              + `  · Hard/exhausted (no recuperables): ${s.hardOrExhausted}\n`
              + `  · Reintentables: ${s.retryableNow}\n`
              + `  · ═════════════════\n`
              + `  · Recuperaciones esperadas: ~${s.predictedRecoveries} (${s.predictedRecoveryRate}%)\n`
              + `  · Revenue esperado: ~$${s.predictedRevenue}\n\n`
              + `🪣 BUCKETS:\n`
              + `  · HARD (${b.hard?.count || 0}): ${b.hard?.recoveryRate} → ~${b.hard?.predicted || 0}\n`
              + `  · EXHAUSTED (${b.exhausted?.count || 0}): ${b.exhausted?.recoveryRate} → ~${b.exhausted?.predicted || 0}\n`
              + `  · RECENT <24h (${b.recent?.count || 0}): ${b.recent?.recoveryRate} → ~${b.recent?.predicted || 0}\n`
              + `  · COOLED 1-7d (${b.cooled?.count || 0}): ${b.cooled?.recoveryRate} → ~${b.cooled?.predicted || 0} ← ventana óptima\n`
              + `  · STALE >7d (${b.stale?.count || 0}): ${b.stale?.recoveryRate} → ~${b.stale?.predicted || 0}\n`
              + `  · NO-IP legacy (${b.noIpLegacy?.count || 0}): uplift extra con el fix IP\n\n`
              + (recs ? `${recs}\n` : '');
            console.log('[Retry Forecast]', d);
            window.alert(msg);
            showToast(`Forecast: ~${s.predictedRecoveries} recuperaciones · ~$${s.predictedRevenue}`, 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🔮 Pronóstico recuperación</button>
        <button className="btn btn-gold" style={{ fontWeight: 700, fontSize: 12 }} title="Cancela automáticamente los HARD declines (fraud/pickup/closed) y reprograma los recuperables (insufficient funds → +5d, do-not-honor → +60h)" onClick={async () => {
          showToast('Analizando fallos...', 'info');
          try {
            // Preview
            const r = await fetch(`${BACKEND}/api/subscriptions/audit/classify-declines?dryRun=true`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const p = await r.json();
            if (p.error) return showToast('Error: ' + p.error, 'error');
            const s = p.summary || {};
            const rs = p.reschedule || {};
            const msg = `CLASIFICAR Y LIMPIAR FALLOS\n\n`
              + `📊 ANÁLISIS DE ${s.totalFailed} RCs EN FAILED:\n`
              + `  ❌ HARD → CANCELLED: ${s.hardToCancel}\n`
              + `     (pickup/fraud/closed/MAC:03 — cards imposibles)\n\n`
              + `  🔄 REPROGRAMAR:\n`
              + `  · Insufficient funds → ${rs.insufficientFunds}\n`
              + `    Cantidad: ${s.insufficientToReschedule}\n`
              + `  · Exceeds limit → ${rs.exceedsLimit}\n`
              + `    Cantidad: ${s.exceedsLimitToReschedule}\n`
              + `  · Do not honor → ${rs.doNotHonor}\n`
              + `    Cantidad: ${s.doNotHonorToReschedule}\n`
              + `  · Transient → ${rs.transient}\n`
              + `    Cantidad: ${s.transientToReschedule}\n`
              + `  · Other → +3d: ${s.otherToReschedule}\n\n`
              + `💰 Revenue recuperable esperado: ~$${p.predictedRecoverableRevenue}\n\n`
              + `¿Ejecutar (cancelar HARD + reprogramar soft)?`;
            if (!window.confirm(msg)) return;
            // Execute
            const r2 = await fetch(`${BACKEND}/api/subscriptions/audit/classify-declines?dryRun=false`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const d = await r2.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const a = d.applied || {};
            const done = `✅ Hecho:\n`
              + `· ${a.cancelled} HARD cancelados\n`
              + `· ${a.rescheduled} soft reprogramados\n`
              + `· ${a.skipped} sin cambios\n\n`
              + `Los soft van a cobrar automáticamente en sus nuevas fechas (via cron).`;
            window.alert(done);
            showToast(`Limpieza: ${a.cancelled} cancelados + ${a.rescheduled} reprogramados`, 'success');
            try { load(); } catch {}
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>🧹 Clasificar y limpiar fallos</button>
        <button className="btn btn-ghost" style={{ fontWeight: 600, fontSize: 12 }} title="Inserta en el historial de Pagos las filas FAILED que no existen pero sí se cobraron (legacy, charge-now viejos)" onClick={async () => {
          showToast('Calculando filas FAILED que faltan...', 'info');
          try {
            const r = await fetch(`${BACKEND}/api/subscriptions/audit/backfill-failed-payments?dryRun=true`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const p = await r.json();
            if (p.error) return showToast('Error: ' + p.error, 'error');
            if (p.toInsertCount === 0 && p.toUpdateCount === 0) {
              return showToast(`✓ Nada que hacer (${p.totalFailedRCs} RCs en FAILED, ${p.alreadyExistsCount} ya tienen fila correcta)`, 'success');
            }
            const samplesIns = (p.sampleToInsert || []).map(s => `  · ${s.email} $${(s.amount/100).toFixed(2)} ${(s.createdAt || '').slice(0,10)} — ${(s.error || '').slice(0, 60)}`).join('\n');
            const samplesUpd = (p.sampleToUpdate || []).map(s => `  · ${s.email} → ${(s.newError || '').slice(0, 70)}`).join('\n');
            const msg = `REPARAR FALLOS HISTÓRICOS\n\n`
              + `📊 RESUMEN:\n`
              + `  · RCs en FAILED: ${p.totalFailedRCs}\n`
              + `  · Ya tienen fila correcta: ${p.alreadyExistsCount}\n`
              + `  · A INSERTAR (sin fila): ${p.toInsertCount}\n`
              + `  · A ACTUALIZAR (placeholder "historical_no_detail" → motivo real): ${p.toUpdateCount}\n\n`
              + (samplesIns ? `📋 INSERTAR (10):\n${samplesIns}\n\n` : '')
              + (samplesUpd ? `🔁 ACTUALIZAR (10):\n${samplesUpd}\n\n` : '')
              + `¿Ejecutar?`;
            if (!window.confirm(msg)) return;
            const r2 = await fetch(`${BACKEND}/api/subscriptions/audit/backfill-failed-payments?dryRun=false`, {
              method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
            });
            const d = await r2.json();
            if (d.error) return showToast('Error: ' + d.error, 'error');
            const done = `✅ Hecho:\n`
              + `  · Insertadas: ${d.inserted}\n`
              + `  · Actualizadas (motivo real): ${d.updated}\n\n`
              + (d.insertErrors && d.insertErrors.length > 0 ? `⚠️ ${d.insertErrors.length} errores\n` : '')
              + `Ahora aparecerán correctamente en Pagos + Beneficios.`;
            window.alert(done);
            showToast(`✅ ${d.inserted} añadidas · ${d.updated} actualizadas`, 'success');
          } catch (e) { showToast('Error: ' + e.message, 'error'); }
        }}>📥 Importar fallos históricos</button>
        </>}
        <input className="search" style={{ flex: 1, minWidth: 250 }} placeholder="Buscar por nombre o email..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="pill-group">
          {[['ALL', 'Todos'], ['TRIAL', 'Trial'], ['ACTIVE', 'Activo'], ['FAILED', 'Fallidas'], ['PAUSED', 'Pausadas'], ['CANCELLED', 'Canceladas']].map(([k, v]) => (
            <button key={k} className={`pill ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{v}</button>
          ))}
        </div>
      </div>

      {/* Overdue batch progress card */}
      {overdueJob && (
        <div style={{ background: G.card, border: `2px solid ${G.gold}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{overdueJob.inProgress ? '⚡' : '✅'}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: G.white }}>
                  {overdueJob.inProgress ? 'Procesando atrasadas' : 'Batch completado'}
                </div>
                <div style={{ fontSize: 11, color: G.dim, marginTop: 2, fontFamily: 'DM Mono, monospace' }}>
                  {overdueJob.processed}/{overdueJob.total} · ✓{overdueJob.success} · ✗{overdueJob.failed} · ↷{overdueJob.skipped}
                  {overdueJob.inProgress && overdueJob.remaining > 0 && ` · ETA ~${overdueJob.remaining} min`}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {overdueJob.inProgress && (
                <button className="btn btn-danger btn-sm" onClick={async () => {
                  if (!window.confirm('¿Cancelar el batch? Las que ya se cobraron no se revierten.')) return;
                  try {
                    await fetch(`${BACKEND}/api/subscriptions/process-overdue/cancel/${overdueJob.jobId}`, {
                      method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    showToast('Cancelando batch...', 'info');
                  } catch { /* ignore */ }
                }}>Cancelar</button>
              )}
              {!overdueJob.inProgress && (
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  if (overduePollRef.current) clearInterval(overduePollRef.current);
                  setOverdueJob(null);
                }}>Cerrar</button>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ height: 6, background: G.bg, borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{
              height: '100%',
              width: `${overdueJob.total > 0 ? (overdueJob.processed / overdueJob.total * 100) : 0}%`,
              background: `linear-gradient(90deg, ${G.gold}, ${G.goldLight || G.gold})`,
              transition: 'width 0.6s ease',
            }} />
          </div>
          {overdueJob.currentEmail && overdueJob.inProgress && (
            <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace' }}>
              🔄 Cobrando ahora: <span style={{ color: G.white }}>{overdueJob.currentEmail}</span>
            </div>
          )}
        </div>
      )}

      <div className="card">
        {loading ? <div className="empty"><div className="empty-icon">◌</div>Cargando...</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Importe</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click para ordenar por 1er intento de pago"
                  onClick={() => onSortClick('firstAttemptDate')}
                >
                  1er Intento <span style={{ color: sortKey === 'firstAttemptDate' ? G.gold : G.dim, fontSize: 11 }}>{sortArrow('firstAttemptDate')}</span>
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click para ordenar por próximo cobro"
                  onClick={() => onSortClick('nextBillingDate')}
                >
                  Proximo Cobro <span style={{ color: sortKey === 'nextBillingDate' ? G.gold : G.dim, fontSize: 11 }}>{sortArrow('nextBillingDate')}</span>
                </th>
                <th>Fallos</th>
                <th>Acciones</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={8}><div className="empty"><div className="empty-icon">↺</div>Sin suscripciones.</div></td></tr>}
                {filtered.map((s, i) => {
                  const st = statusStyle(s.status);
                  return (
                    <tr key={s.id}>
                      <td style={{ fontSize: 12, color: G.muted, fontFamily: 'DM Mono, monospace' }}>#{subs.length - i}</td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{s.customerId || '--'}</div>
                      </td>
                      <td>
                        <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, borderRadius: 6, padding: '2px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {statusLabel(s.status)}
                        </span>
                        {s.status === 'FAILED' && s.lastDeclineDetail && (
                          <div
                            style={{ fontSize: 10, color: G.red, marginTop: 4, maxWidth: 260, lineHeight: 1.3, fontFamily: 'DM Mono, monospace', wordBreak: 'break-word' }}
                            title={s.lastDeclineDetail + (s.lastFailDate ? '\n\nÚltimo fallo: ' + new Date(s.lastFailDate).toLocaleString('es-ES', { timeZone: APP_TZ }) : '')}
                          >
                            {(() => {
                              const d = s.lastDeclineDetail || '';
                              // Short human-readable summary of the most common decline codes
                              if (/insufficient.?funds|\b51\b/i.test(d)) return '💰 Saldo insuficiente';
                              if (/do.?not.?honor|\b05\b/i.test(d))     return '🚫 Do not honor (banco)';
                              if (/suspected.?fraud|\b59\b/i.test(d))    return '🛑 Sospecha de fraude';
                              if (/expired/i.test(d) || /\b54\b/.test(d)) return '⏰ Tarjeta caducada';
                              if (/lost|\b41\b/i.test(d))                 return '🚷 Tarjeta perdida';
                              if (/stolen|\b43\b/i.test(d))               return '🚷 Tarjeta robada';
                              if (/closed.?account|\b46\b/i.test(d))      return '❌ Cuenta cerrada';
                              if (/restricted|\b62\b/i.test(d))           return '🚷 Tarjeta restringida';
                              // 2026-06-11 — Regex estricto para CVV (igual fix
                              // que línea 3185). El operator reportó decline
                              // de Apple Pay falsamente etiquetado como CVV.
                              if (/(incorrect|invalid|wrong|fail|reject|bad|miss)[^a-z]*c(v[v|c2]|sc|security[- ]code)/i.test(d) || /cvc[_-]?check[_-]?fail/i.test(d)) return '🔢 CVV inválido';
                              if (/invalid.?card/i.test(d))               return '🔢 Tarjeta inválida';
                              if (/try.?again|\b91\b/i.test(d))           return '🔁 Reintentar (banco busy)';
                              if (/refer.?to.?issuer|contact.?card|\b02\b/i.test(d)) return '📞 Llamar al banco';
                              if (/\b65\b|activity.?limit/i.test(d))      return '🚧 Límite diario';
                              if (/general.?error|ERROR\s+\d{4}|\b06\b/i.test(d)) return '⚠️ Error técnico';
                              return d.slice(0, 60) + (d.length > 60 ? '…' : '');
                            })()}
                            {s.hardDecline && <span style={{ marginLeft: 4, color: G.red, fontWeight: 700 }}>· HARD</span>}
                          </div>
                        )}
                      </td>
                      <td><span className="mono" style={{ fontWeight: 600, color: G.goldLight }}>{fmt((s.amount || 0) / 100)}/{s.interval === 'month' ? 'mes' : s.interval === 'year' ? 'ano' : 'sem'}</span></td>
                      <td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                        {s.firstAttemptDate ? (
                          <>
                            <div>{new Date(s.firstAttemptDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: APP_TZ })}</div>
                            <div style={{ fontSize: 10, color: G.dim, marginTop: 2 }}>
                              🕐 {new Date(s.firstAttemptDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: APP_TZ })} {tzMeta(APP_TZ).city}
                            </div>
                          </>
                        ) : '--'}
                      </td>
                      <td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                        {s.nextBillingDate ? (
                          <>
                            <div>{new Date(s.nextBillingDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: APP_TZ })}</div>
                            <div style={{ fontSize: 10, color: G.dim, marginTop: 2 }}>
                              🕐 {new Date(s.nextBillingDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: APP_TZ })} {tzMeta(APP_TZ).city}
                            </div>
                          </>
                        ) : '--'}
                      </td>
                      <td style={{ fontSize: 12, color: s.failCount ? G.red : G.muted, textAlign: 'center', fontFamily: 'DM Mono, monospace' }}>{s.failCount || 0}/6</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {s.status !== 'CANCELLED' && <button className="btn btn-gold btn-sm" onClick={() => { setChargeModal(s); setChargeAmount(((s.amount || 0) / 100).toFixed(2)); setChargeDesc(''); }}>Cobrar ahora</button>}
                          {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => loadInfo(s)}>Info</button>}
                          {(s.status === 'ACTIVE' || s.status === 'TRIAL') && <button className="btn btn-ghost btn-sm" onClick={() => subscriptionsApi.pause(s.id).then(() => { showToast('Pausada', 'info'); load(); })}>Pausar</button>}
                          {s.status === 'PAUSED' && <button className="btn btn-ghost btn-sm" onClick={() => subscriptionsApi.resume(s.id).then(() => { showToast('Reanudada', 'success'); load(); })}>Reanudar</button>}
                          {s.status !== 'CANCELLED' && <button className="btn btn-danger btn-sm" onClick={() => cancelSub(s.id)}>Cancelar</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {chargeModal && (
        <Modal title={`Cobrar a ${chargeModal.customerId}`} onClose={() => setChargeModal(null)}>
          <div className="form-group"><label className="form-label">Importe a cobrar ({fmtSymbol()})</label><input type="number" className="form-input" placeholder="49.99" value={chargeAmount} onChange={e => setChargeAmount(e.target.value)} autoFocus /></div>
          <div className="form-group"><label className="form-label">Motivo del cobro</label><input type="text" className="form-input" placeholder="Ej: Cobro mensual Club Premium" value={chargeDesc} onChange={e => setChargeDesc(e.target.value)} /></div>
          <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
            Se cobrara <strong>{chargeAmount || '0.00'}{fmtSymbol()}</strong> a <strong>{chargeModal.customerId}</strong> usando los datos de tarjeta guardados.
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setChargeModal(null)}>Cancelar</button>
            <button className="btn btn-gold" onClick={chargeNow} disabled={charging || !chargeAmount}>{charging ? 'Cobrando...' : `Cobrar ${chargeAmount || '0.00'}${fmtSymbol()}`}</button>
          </div>
        </Modal>
      )}

      {abBatchModal && (
        <Modal title={`${abBatchTargetStatus === 'TRIAL' ? '🎯' : '💳'} Cobrar batch A+B a ${abBatchTargetStatus === 'TRIAL' ? 'TRIALS' : 'Activos'}`} onClose={() => { if (abBatchJob?.status !== 'running') { setAbBatchModal(false); setAbBatchPreview(null); setAbBatchJob(null); } }}>
          {!abBatchJob && abBatchPreview && (
            <>
              <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                <strong>{abBatchPreview.customersCount} customers {abBatchTargetStatus}</strong> con vault disponible.<br />
                · {abBatchPreview.withAB} tienen A+B (2 RCs) — cascade aplicará<br />
                · {abBatchPreview.aOnly} sólo tienen A (1 RC) — sólo cobro A<br />
                ETA estimada: ~{abBatchPreview.etaMinutes} min
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Cantidad A ($USD)</label>
                  <input type="number" step="0.01" className="form-input" value={abAmountA} onChange={e => setAbAmountA(e.target.value)} placeholder="89.97" />
                </div>
                <div className="form-group">
                  <label className="form-label">Cantidad B ($USD)</label>
                  <input type="number" step="0.01" className="form-input" value={abAmountB} onChange={e => setAbAmountB(e.target.value)} placeholder="90.87" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fallback B ($USD)</label>
                  <input type="number" step="0.01" className="form-input" value={abFallbackB} onChange={e => setAbFallbackB(e.target.value)} placeholder="49.99" />
                </div>
                <div className="form-group">
                  <label className="form-label">Delay entre customers (s)</label>
                  <input type="number" className="form-input" value={abDelaySec} onChange={e => setAbDelaySec(e.target.value)} placeholder="25" min="5" max="120" />
                </div>
              </div>
              <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 16, fontSize: 12, color: '#92400e' }}>
                ⚠ Lógica cascade: A → si OK cobra B normal; si A fail → B con fallback; si A OK & B fail → retry B con fallback.<br />
                Saltarán automáticamente customers ya cobrados con éxito en últimas 2h.
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => { setAbBatchModal(false); setAbBatchPreview(null); }}>Cancelar</button>
                <button className="btn btn-gold" onClick={async () => {
                  const amountACents = Math.round(parseFloat(abAmountA) * 100);
                  const amountBCents = Math.round(parseFloat(abAmountB) * 100);
                  const fallbackBCents = Math.round(parseFloat(abFallbackB) * 100);
                  const delaySeconds = parseInt(abDelaySec, 10);
                  if (!amountACents || !amountBCents || !fallbackBCents || !delaySeconds) { showToast('Todos los campos son obligatorios', 'error'); return; }
                  if (!window.confirm(`Cobrar:\n· A = $${(amountACents/100).toFixed(2)}\n· B normal = $${(amountBCents/100).toFixed(2)}\n· B fallback = $${(fallbackBCents/100).toFixed(2)}\n· Delay = ${delaySeconds}s\n\nA ${abBatchPreview.customersCount} customers ${abBatchTargetStatus}\n\n¿Continuar?`)) return;
                  try {
                    const r = await fetch(`${BACKEND}/api/batch/charge-active-ab`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: abBatchTargetStatus, amountACents, amountBCents, fallbackBCents, delaySeconds }),
                    });
                    const data = await r.json();
                    if (!r.ok) { showToast(data.error || 'Error lanzando batch', 'error'); return; }
                    showToast(`🚀 Batch lanzado: ${data.totalCustomers} customers, ETA ~${data.etaMinutes} min`, 'success');
                    setAbBatchJob({ ...data, status: 'running', processed: 0, success: 0, failed: 0, skipped: 0, totalRecoveredCents: 0 });
                    // Polling cada 10s
                    if (abBatchPollRef.current) clearInterval(abBatchPollRef.current);
                    abBatchPollRef.current = setInterval(async () => {
                      try {
                        const s = await fetch(`${BACKEND}/api/batch/charge-active-ab-status/${data.jobId}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                        });
                        const st = await s.json();
                        if (st.error) return;
                        setAbBatchJob(st);
                        if (st.status === 'completed' || st.status === 'error') {
                          clearInterval(abBatchPollRef.current);
                          abBatchPollRef.current = null;
                          const msg = st.status === 'completed'
                            ? `✅ Batch DONE — ${st.success} OK · ${st.failed} fail · ${st.skipped} skip · $${(st.totalRecoveredCents/100).toFixed(2)} recovered`
                            : `❌ Batch ERROR — ${st.error}`;
                          showToast(msg, st.status === 'completed' ? 'success' : 'error');
                          load();
                        }
                      } catch { /* keep polling */ }
                    }, 10000);
                  } catch (e) { showToast('Error: ' + e.message, 'error'); }
                }}>🚀 Lanzar batch A+B</button>
              </div>
            </>
          )}
          {abBatchJob && (
            <div>
              <div style={{ background: abBatchJob.status === 'completed' ? '#d1fae5' : abBatchJob.status === 'error' ? '#fee2e2' : G.goldDim, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  {abBatchJob.status === 'running' ? '⏳ Procesando...' : abBatchJob.status === 'completed' ? '✅ Completado' : '❌ Error'}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  Progreso: <strong>{abBatchJob.processed} / {abBatchJob.totalCustomers}</strong> ({Math.round((abBatchJob.processed / abBatchJob.totalCustomers) * 100)}%)<br />
                  ✅ Success: <strong>{abBatchJob.success}</strong> &nbsp;·&nbsp; ❌ Fail: <strong>{abBatchJob.failed}</strong> &nbsp;·&nbsp; ⏸ Skip (2h guard): <strong>{abBatchJob.skipped || 0}</strong><br />
                  💰 Recovered: <strong>${(abBatchJob.totalRecoveredCents / 100).toFixed(2)}</strong>
                  {abBatchJob.error && <><br /><span style={{ color: '#b91c1c' }}>Error: {abBatchJob.error}</span></>}
                </div>
              </div>
              <div style={{ background: '#e5e7eb', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: abBatchJob.status === 'error' ? '#dc2626' : '#10b981', height: '100%', width: `${(abBatchJob.processed / abBatchJob.totalCustomers) * 100}%`, transition: 'width 0.3s' }}></div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" disabled={abBatchJob.status === 'running'} onClick={() => { setAbBatchModal(false); setAbBatchPreview(null); setAbBatchJob(null); }}>{abBatchJob.status === 'running' ? 'Procesando...' : 'Cerrar'}</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {infoModal && customerInfo && (
        <Modal title={`Info: ${infoModal.customerId}`} onClose={() => { setInfoModal(null); setCustomerInfo(null); }}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
              <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Datos del cliente</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><span style={{ color: G.muted }}>Nombre:</span> <strong>{customerInfo.name || '--'}</strong></div>
                <div><span style={{ color: G.muted }}>Email:</span> <strong>{customerInfo.email || '--'}</strong></div>
                <div><span style={{ color: G.muted }}>Telefono:</span> <strong>{customerInfo.phone || '--'}</strong></div>
                <div><span style={{ color: G.muted }}>Direccion:</span> <strong>{customerInfo.address?.line1 || '--'}, {customerInfo.address?.city || ''} {customerInfo.address?.postal_code || ''}</strong></div>
              </div>
            </div>
            <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
              <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Tarjeta guardada</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><span style={{ color: G.muted }}>Numero:</span> <strong style={{ fontFamily: 'DM Mono, monospace' }}>{customerInfo.cardLast4 ? '**** **** **** ' + customerInfo.cardLast4 : 'No disponible'}</strong></div>
                <div><span style={{ color: G.muted }}>{customerInfo.cardType ? 'Tipo:' : 'Caducidad:'}</span> <strong>{customerInfo.cardType || customerInfo.cardExpiry || '--'}</strong></div>
              </div>
            </div>
            <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
              <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Historial de cobros</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><span style={{ color: G.muted }}>Cobros:</span> <strong>{customerInfo.chargeCount || 0}</strong></div>
                <div><span style={{ color: G.muted }}>Ultimo:</span> <strong>{customerInfo.lastChargeDate ? new Date(customerInfo.lastChargeDate).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '--'}</strong></div>
                <div><span style={{ color: G.muted }}>Proximo:</span> <strong>{customerInfo.nextChargeDate ? new Date(customerInfo.nextChargeDate).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: APP_TZ }) + ' ' + tzMeta(APP_TZ).city : '--'}</strong></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13, marginTop: 8 }}>
                <div><span style={{ color: G.muted }}>Importe:</span> <strong style={{ color: G.goldLight }}>{fmt((customerInfo.amount || 0) / 100)}</strong></div>
                <div><span style={{ color: G.muted }}>Intervalo:</span> <strong>{customerInfo.interval === 'month' ? 'Mensual' : customerInfo.interval === 'year' ? 'Anual' : 'Semanal'}</strong></div>
                <div><span style={{ color: G.muted }}>Motivo:</span> <strong>{customerInfo.description || '--'}</strong></div>
              </div>
            </div>
            {customerInfo.payments?.length > 0 && (
              <div style={{ background: G.card, borderRadius: 10, padding: 16, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>Ultimos pagos</div>
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {customerInfo.payments.map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${G.border}`, fontSize: 12 }}>
                      <span>{new Date(p.date).toLocaleDateString('es-ES', { timeZone: APP_TZ })}</span>
                      <span><StatusBadge status={p.status} /></span>
                      <span style={{ fontWeight: 600, color: G.goldLight }}>{fmt((p.amount || 0) / 100)}</span>
                      <span style={{ color: G.muted }}>{p.description || '--'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="modal-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => { setInfoModal(null); setCustomerInfo(null); }}>Cerrar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SubscriptionStats({ showToast }) {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverBar, setHoverBar] = useState(null);
  const [declineData, setDeclineData] = useState(null);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [declineExpanded, setDeclineExpanded] = useState(null);

  useEffect(() => {
    Promise.all([
      subscriptionsApi.getStats().catch(() => null),
      subscriptionsApi.getAll().catch(() => ({ subscriptions: [] })),
      paymentsApi.getAll().catch(() => ({ payments: [] })),
      subscriptionsApi.declineAnalysis().catch(() => null),
    ])
      .then(([st, s, p, da]) => {
        setStats(st);
        setSubs(s.subscriptions || []);
        setPayments(p.payments || []);
        if (da) setDeclineData(da);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="content"><div className="empty"><div className="empty-icon">◌</div>Cargando estadisticas...</div></div>;

  // New York TZ helpers (client-side bar chart only — KPIs come from backend stats which are already Madrid-TZ)
  const madridDayStr = (isoOrDate) => {
    if (!isoOrDate) return '';
    const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('sv-SE', { timeZone: APP_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  };
  const madridDateLabel = (isoOrDate) => {
    const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('es-ES', { timeZone: APP_TZ, day: 'numeric', month: 'short' }).format(d);
  };

  const eur = (p) => p.amountInEUR != null ? parseFloat(p.amountInEUR) : (parseFloat(p.amount) || 0) / 100;

  // Pull KPIs from backend (source of truth) with local fallbacks
  const byStatus = stats?.byStatus || {
    trial: subs.filter(s => s.status === 'TRIAL').length,
    active: subs.filter(s => s.status === 'ACTIVE').length,
    failed: subs.filter(s => s.status === 'FAILED').length,
    cancelled: subs.filter(s => s.status === 'CANCELLED').length,
    paused: subs.filter(s => s.status === 'PAUSED').length,
  };
  const totalSubs = stats?.total ?? subs.length;
  const mrr = stats?.mrr ?? 0;
  const newToday = stats?.newToday ?? 0;
  const cancelledToday = stats?.cancelledToday ?? 0;
  const incomeToday = stats?.incomeToday ?? 0;
  const totalIncome = stats?.totalIncome ?? 0;
  const ltv = stats?.ltv ?? 0;
  const churnRate = stats?.churnRate ?? '0';
  const subSucceededCount = stats?.subscriptionPayments?.count ?? 0;

  // Local bar chart using Madrid-TZ day buckets
  const subPaymentsLocal = payments.filter(p =>
    p.metadata?.source === 'recurring' || p.metadata?.source === 'charge-now' || p.metadata?.source === 'auto-subscription'
  );
  const subSucceededLocal = subPaymentsLocal.filter(p => p.status === 'SUCCEEDED');
  const last30 = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const dayKey = madridDayStr(d);
    const dayIncome = subSucceededLocal
      .filter(p => madridDayStr(p.createdAt) === dayKey)
      .reduce((s, p) => s + eur(p), 0);
    last30.push({ date: d, income: dayIncome });
  }
  const maxIncome = Math.max(...last30.map(d => d.income), 1);

  const kpi = (label, value, sub, icon, color) => (
    <div style={{ background: G.card, border: `1px solid ${color ? color + '40' : G.border}`, borderRadius: 12, padding: '18px 20px', minWidth: 180, flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
        <span style={{ fontSize: 16, color: color || G.gold }}>{icon}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const statusTile = (label, count, color, icon, hint) => (
    <div style={{ background: G.card, border: `1px solid ${color}30`, borderRadius: 12, padding: '16px 18px', flex: 1, minWidth: 150 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
        <span style={{ fontSize: 14, color }}>{icon}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'DM Mono, monospace' }}>{count}</div>
      {hint && <div style={{ fontSize: 10, color: G.muted, marginTop: 2 }}>{hint}</div>}
    </div>
  );

  return (
    <div className="content">
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: G.white, marginBottom: 4 }}>Estadisticas de Suscripciones</div>
          <div style={{ fontSize: 12, color: G.muted }}>
            Horario {tzMeta(APP_TZ).city} ({APP_TZ}) - Corte diario a las 00:00 - Hoy: {stats?.today || new Date().toLocaleDateString('sv-SE', { timeZone: APP_TZ })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: G.muted, textAlign: 'right' }}>
          <div>Retry schedule: +24h - +48h - +72h</div>
          <div>3 intentos antes de marcar como FALLIDA</div>
        </div>
      </div>

      {/* Subscription status breakdown */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: G.muted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Estado del ciclo de suscripcion</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          {statusTile('Trial', byStatus.trial || 0, G.blue, '?', 'En periodo de prueba')}
          {statusTile('Activo', byStatus.active || 0, G.green, '+', 'Pagando')}
          {statusTile('Fallidas', byStatus.failed || 0, G.red, '!', '3/3 retries agotados')}
          {statusTile('Pausadas', byStatus.paused || 0, G.orange, '||', 'Cobro detenido')}
          {statusTile('Canceladas', byStatus.cancelled || 0, G.muted, 'X', `${cancelledToday} hoy`)}
        </div>
      </div>

      {/* Main KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        {kpi('MRR Recurrente', fmt(mrr), 'Ingresos mensuales recurrentes', '$', G.gold)}
        {kpi('Nuevas (Hoy)', newToday, `Altas hoy (${tzMeta(APP_TZ).city} TZ)`, '+', G.green)}
        {kpi('Ingresos (Hoy)', fmt(incomeToday), `${stats?.today || 'hoy'}`, '$', G.gold)}
        {kpi('Churn Rate', `${churnRate}%`, `${cancelledToday} canceladas hoy`, '-', G.red)}
      </div>

      {/* Secondary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        {kpi('Total Miembros', totalSubs, 'Todas las suscripciones', '#', null)}
        {kpi('Ingresos Totales', fmt(totalIncome), `${subSucceededCount} pagos recurrentes`, '$', null)}
        {kpi('LTV Promedio', fmt(ltv), 'Valor de vida por cliente', '>', null)}
        {kpi('Activas + Trial', (byStatus.active || 0) + (byStatus.trial || 0), 'Pipeline de ingresos', '=', G.green)}
      </div>

      {/* Revenue chart */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Ingresos por Suscripciones</span>
          <span style={{ fontSize: 11, color: G.muted }}>Ultimos 30 dias ({tzMeta(APP_TZ).city} TZ)</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 12, color: G.dim }}>
          <span>Total: <span style={{ color: G.gold, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(totalIncome)}</span></span>
          <span>Pagos: <span style={{ color: G.white, fontWeight: 600 }}>{subSucceededCount}</span></span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 180, position: 'relative' }} onMouseLeave={() => setHoverBar(null)}>
          {last30.map((d, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', cursor: 'pointer', position: 'relative' }}
              onMouseEnter={() => setHoverBar(i)} onClick={() => setHoverBar(hoverBar === i ? null : i)}>
              <div style={{ width: '100%', maxWidth: 16, background: hoverBar === i ? G.gold : (d.income > 0 ? `${G.gold}60` : G.border), borderRadius: 3, minHeight: 2, height: `${Math.max((d.income / maxIncome) * 100, 2)}%`, transition: 'all 0.2s' }} />
              {hoverBar === i && (
                <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 8, padding: '8px 12px', zIndex: 20, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,.5)', pointerEvents: 'none', minWidth: 120 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: G.white, marginBottom: 2, fontFamily: 'DM Mono, monospace' }}>{madridDateLabel(d.date)}</div>
                  <div style={{ fontSize: 13, color: G.gold, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(d.income)}</div>
                  <div style={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: 8, height: 8, background: G.surface, borderRight: `1px solid ${G.border}`, borderBottom: `1px solid ${G.border}` }} />
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: G.muted }}>
          <span>{madridDateLabel(last30[0]?.date)}</span>
          <span>{madridDateLabel(last30[14]?.date)}</span>
          <span>{madridDateLabel(last30[29]?.date)}</span>
        </div>
      </div>

      {/* ── Decline Analysis ──────────────────────────────────────────── */}
      {declineData && declineData.totalFailed > 0 && (() => {
        const cats = [
          { key: 'mac03_no_retry',       label: 'Banco rechaza permanente',   icon: '!!', color: G.red,    hint: 'MAC:03 — El banco dice que nunca vuelvas a intentarlo' },
          { key: 'r1_stop_recurring',    label: 'Cliente cancelo',            icon: 'X',  color: G.red,    hint: 'R1 — El cliente pidio parar cobros recurrentes' },
          { key: 'hard_decline',         label: 'Tarjeta invalida / robada',  icon: '!',  color: G.red,    hint: 'Tarjeta reportada como perdida, robada o cerrada' },
          { key: 'mac01_card_updated',   label: 'Tarjeta reemplazada',        icon: '*',  color: G.blue,   hint: 'MAC:01 — Account Updater obtendra la nueva tarjeta automaticamente' },
          { key: 'mac02_try_later',      label: 'Reintentar mas tarde',       icon: '>',  color: G.orange, hint: 'MAC:02 — El banco dice que intentes mas adelante' },
          { key: 'activity_limit',       label: 'Limite diario',             icon: '#',  color: G.orange, hint: 'Codigo 65 — Limite de actividad, se reintenta automaticamente' },
          { key: 'insufficient_funds',   label: 'Fondos insuficientes',       icon: '$',  color: G.orange, hint: 'NSF — Sin fondos, se reintenta automaticamente' },
          { key: 'soft_decline_retryable', label: 'Decline generico',         icon: '~',  color: G.gold,   hint: '"Do Not Honor" sin MAC — retryable, en cola de reintentos' },
          { key: 'avs_issue',            label: 'Problema de AVS',            icon: '?',  color: G.gold,   hint: 'Verificacion de direccion fallo' },
          { key: 'unknown',              label: 'Sin detalle',                icon: '?',  color: G.muted,  hint: 'No hay info del decline — revisar manualmente' },
        ];
        const lostPct = declineData.totalFailed > 0 ? ((declineData.totalLost / declineData.totalFailed) * 100).toFixed(0) : 0;
        const recPct = declineData.totalFailed > 0 ? ((declineData.totalRecoverable / declineData.totalFailed) * 100).toFixed(0) : 0;
        return (
          <div className="card" style={{ padding: 24, marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: G.white, marginBottom: 4 }}>Analisis de Declines</div>
                <div style={{ fontSize: 11, color: G.muted }}>Desglose de suscripciones fallidas por tipo de rechazo</div>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.red, fontFamily: 'DM Mono, monospace' }}>{declineData.totalLost}</div>
                  <div style={{ fontSize: 10, color: G.muted }}>Perdidas ({lostPct}%)</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>{declineData.totalRecoverable}</div>
                  <div style={{ fontSize: 10, color: G.muted }}>Recuperables ({recPct}%)</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>{declineData.totalFailed}</div>
                  <div style={{ fontSize: 10, color: G.muted }}>Total fallidas</div>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 20, background: G.border }}>
              {declineData.totalLost > 0 && <div style={{ width: `${lostPct}%`, background: G.red, transition: 'width 0.5s' }} />}
              {declineData.totalRecoverable > 0 && <div style={{ width: `${recPct}%`, background: G.green, transition: 'width 0.5s' }} />}
            </div>

            {/* Category breakdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cats.map(cat => {
                const count = declineData.summary?.[cat.key] || 0;
                if (count === 0) return null;
                const items = declineData.detail?.[cat.key] || [];
                const isExpanded = declineExpanded === cat.key;
                return (
                  <div key={cat.key}>
                    <div
                      onClick={() => setDeclineExpanded(isExpanded ? null : cat.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: G.surface, border: `1px solid ${cat.color}25`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                      <span style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: `${cat.color}15`, color: cat.color, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{cat.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: G.white }}>{cat.label}</div>
                        <div style={{ fontSize: 10, color: G.muted, marginTop: 1 }}>{cat.hint}</div>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: cat.color, fontFamily: 'DM Mono, monospace', minWidth: 30, textAlign: 'right' }}>{count}</div>
                      <span style={{ fontSize: 10, color: G.muted, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>V</span>
                    </div>
                    {isExpanded && items.length > 0 && (
                      <div style={{ margin: '4px 0 8px 38px', background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${G.border}` }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: G.muted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>Cliente</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.muted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>Monto</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.muted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>Intentos</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: G.muted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>Detalle</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.slice(0, 20).map((it, idx) => (
                              <tr key={idx} style={{ borderBottom: idx < items.length - 1 ? `1px solid ${G.border}` : 'none' }}>
                                <td style={{ padding: '6px 12px', color: G.white }}>{it.email || it.name || '-'}</td>
                                <td style={{ padding: '6px 12px', textAlign: 'right', color: G.gold, fontFamily: 'DM Mono, monospace' }}>${parseFloat(it.amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '6px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>{it.failCount || 0}</td>
                                <td style={{ padding: '6px 12px', color: G.dim, fontSize: 10, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.detail || '-'}</td>
                              </tr>
                            ))}
                            {items.length > 20 && (
                              <tr><td colSpan={4} style={{ padding: '6px 12px', color: G.muted, fontSize: 10, textAlign: 'center' }}>... y {items.length - 20} mas</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Analytics({ showToast }) {
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverBar, setHoverBar] = useState(null);

  useEffect(() => {
    Promise.all([paymentsApi.getAll(), subscriptionsApi.getAll()])
      .then(([p, s]) => { setPayments(p.payments || []); setSubs(s.subscriptions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="content"><div className="empty"><div className="empty-icon">◌</div>Cargando analiticas...</div></div>;

  const eur = (p) => p.amountInEUR != null ? p.amountInEUR : (p.amount || 0) / 100;
  const succeeded = payments.filter(p => p.status === 'SUCCEEDED');
  const totalIncome = succeeded.reduce((s, p) => s + eur(p), 0);
  const totalOrders = payments.length;
  const uniqueEmails = new Set(payments.map(p => p.customerId || p.email).filter(Boolean));
  const aov = succeeded.length > 0 ? totalIncome / succeeded.length : 0;

  const today = new Date().toDateString();
  const todayPayments = succeeded.filter(p => new Date(p.createdAt).toDateString() === today);
  const todayIncome = todayPayments.reduce((s, p) => s + eur(p), 0);

  // Income per day last 30 days
  const last30 = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const ds = d.toDateString();
    const dayIncome = succeeded.filter(p => new Date(p.createdAt).toDateString() === ds).reduce((s, p) => s + eur(p), 0);
    const dayOrders = payments.filter(p => new Date(p.createdAt).toDateString() === ds).length;
    last30.push({ date: d, income: dayIncome, orders: dayOrders });
  }
  const maxIncome = Math.max(...last30.map(d => d.income), 1);

  // Orders by status
  const statusCounts = { SUCCEEDED: succeeded.length, FAILED: payments.filter(p => p.status === 'FAILED').length, PROCESSING: payments.filter(p => p.status === 'PROCESSING' || p.status === 'CREATED').length };

  // Top gateways
  const gwCounts = {};
  payments.forEach(p => { const gw = p.gateway || 'Desconocido'; gwCounts[gw] = (gwCounts[gw] || 0) + 1; });

  const kpi = (label, value, sub, icon) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: G.muted }}>{label}</span>
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="content">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {kpi('Ingresos Totales', fmt(totalIncome), 'vs periodo anterior', fmtSymbol())}
        {kpi('Pedidos', totalOrders, 'vs periodo anterior', '🛒')}
        {kpi('Nuevos Clientes', uniqueEmails.size, '', '👥')}
        {kpi('AOV (Ticket Promedio)', fmt(aov), '', '↗')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Ingresos por Dia</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 12, color: G.dim }}>
            <span>Total: <span style={{ color: G.gold, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(totalIncome)}</span></span>
            <span>Hoy: <span style={{ color: G.white, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(todayIncome)}</span></span>
          </div>
          <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 160, position: 'relative' }} onMouseLeave={() => setHoverBar(null)}>
            {last30.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', cursor: 'pointer', position: 'relative' }}
                onMouseEnter={() => setHoverBar(i)} onClick={() => setHoverBar(hoverBar === i ? null : i)}>
                <div style={{ width: '100%', maxWidth: 14, background: hoverBar === i ? G.gold : (d.income > 0 ? `${G.gold}60` : G.border), borderRadius: 3, minHeight: 2, height: `${Math.max((d.income / maxIncome) * 100, 2)}%`, transition: 'all 0.2s' }} />
                {hoverBar === i && (
                  <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 8, padding: '8px 12px', zIndex: 20, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,.5)', pointerEvents: 'none', minWidth: 130 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: G.white, marginBottom: 2, fontFamily: 'DM Mono, monospace' }}>{d.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: APP_TZ })}</div>
                    <div style={{ fontSize: 13, color: G.gold, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{fmt(d.income)}</div>
                    <div style={{ fontSize: 11, color: G.muted }}>{d.orders} pedido{d.orders !== 1 ? 's' : ''}</div>
                    <div style={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: 8, height: 8, background: G.surface, borderRight: `1px solid ${G.border}`, borderBottom: `1px solid ${G.border}` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: G.muted }}>
            <span>{last30[0]?.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: APP_TZ })}</span>
            <span>{last30[29]?.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: APP_TZ })}</span>
          </div>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Estado de Pagos</div>
          {totalOrders === 0 ? <div style={{ color: G.muted, fontSize: 13 }}>Sin pedidos</div> : (
            <div style={{ display: 'grid', gap: 12 }}>
              {Object.entries(statusCounts).map(([status, count]) => (
                <div key={status}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: G.dim }}>{status === 'SUCCEEDED' ? 'Exitosos' : status === 'FAILED' ? 'Fallidos' : 'Procesando'}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                  <div style={{ height: 6, background: G.border, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(count / totalOrders) * 100}%`, background: status === 'SUCCEEDED' ? G.green : status === 'FAILED' ? G.red : G.gold, borderRadius: 3, transition: 'width 0.5s' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Pasarelas mas utilizadas</div>
        {Object.keys(gwCounts).length === 0 ? <div style={{ color: G.muted, fontSize: 13 }}>Sin datos de pasarelas</div> : (
          <div style={{ display: 'grid', gap: 10 }}>
            {Object.entries(gwCounts).sort((a, b) => b[1] - a[1]).map(([gw, count]) => (
              <div key={gw} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 500, minWidth: 140 }}>{gw}</span>
                <div style={{ flex: 1, height: 6, background: G.border, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(count / totalOrders) * 100}%`, background: G.gold, borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 12, color: G.muted, fontFamily: 'DM Mono, monospace', minWidth: 30, textAlign: 'right' }}>{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TopProducts — Ranking de productos más vendidos
//
// Tabla ordenada por cantidad vendida descendente.
// Filtros: rango de fechas, top-N (50 / 100 / 200), incluir o no SOLO succeded.
// Cards arriba: total productos únicos / total unidades / total revenue.
// ─────────────────────────────────────────────────────────────────────────────
function TopProducts({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(50);
  const [statusFilter, setStatusFilter] = useState('SUCCEEDED');

  const load = () => {
    setLoading(true);
    paymentsApi.getTopProducts({ from, to, limit, status: statusFilter })
      .then((res) => { setData(res); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        showToast && showToast('Error cargando productos: ' + (err.response?.data?.error || err.message), 'error');
      });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit, statusFilter]);

  const kpi = (label, value, sub) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="content">
      {/* Filtros */}
      <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Desde</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '6px 10px', borderRadius: 6, fontFamily: 'DM Mono, monospace', fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Hasta</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '6px 10px', borderRadius: 6, fontFamily: 'DM Mono, monospace', fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Top</div>
          <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '6px 10px', borderRadius: 6, fontSize: 13 }}>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
            <option value={200}>Top 200</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Pagos</div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '6px 10px', borderRadius: 6, fontSize: 13 }}>
            <option value="SUCCEEDED">Solo exitosos</option>
            <option value="ALL">Todos (incluye fallidos)</option>
          </select>
        </div>
        <button className="btn btn-gold btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Cargando...' : 'Aplicar filtros'}
        </button>
      </div>

      {loading && !data && (
        <div className="empty"><div className="empty-icon">◌</div>Cargando ranking de productos...</div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {kpi('Productos únicos', data.totalProducts, 'con al menos 1 venta')}
            {kpi('Unidades vendidas', data.totalQuantity.toLocaleString(), 'total en rango')}
            {kpi('Revenue total', fmt(data.totalRevenueEUR), 'agregado en rango')}
          </div>

          {/* Tabla */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${G.border}`, fontSize: 15, fontWeight: 600 }}>
              Ranking de productos más vendidos
              <span style={{ marginLeft: 10, fontSize: 11, color: G.muted, fontWeight: 400 }}>
                (mostrando top {data.products.length} de {data.totalProducts})
              </span>
            </div>
            {data.products.length === 0 ? (
              <div className="empty" style={{ padding: 40 }}>
                <div className="empty-icon">◇</div>
                Sin productos vendidos en este rango
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 60, textAlign: 'center' }}>#</th>
                      <th>Producto</th>
                      <th style={{ textAlign: 'left' }}>SKU</th>
                      <th style={{ textAlign: 'right' }}>Cantidad</th>
                      <th style={{ textAlign: 'right' }}>Pedidos</th>
                      <th style={{ textAlign: 'right' }}>Precio prom.</th>
                      <th style={{ textAlign: 'right' }}>Revenue total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.products.map((p) => (
                      <tr key={p.key}>
                        <td style={{ textAlign: 'center', fontFamily: 'DM Mono, monospace', color: p.rank <= 3 ? G.gold : G.dim, fontWeight: p.rank <= 3 ? 700 : 400 }}>
                          {p.rank}
                        </td>
                        <td style={{ fontWeight: 500, color: G.white }}>{p.title || '(sin nombre)'}</td>
                        <td style={{ color: G.muted, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{p.sku || '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.gold, fontWeight: 600 }}>
                          {p.quantitySold.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.dim }}>
                          {p.paymentsCount}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.dim }}>
                          {fmt(p.avgPriceEUR)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.white, fontWeight: 600 }}>
                          {fmt(p.revenueEUR)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tracking — admin UI for the Tracking auto-tracking module
//
// Sub-tabs: Dashboard | Envíos | Configuración
//   - Dashboard: KPI cards + recent activity
//   - Envíos:    filterable list + detail drawer with timeline
//   - Config:    daysToDeliver, brand, first-email delay, business hours
// ─────────────────────────────────────────────────────────────────────────
function Tracking({ showToast }) {
  const [tab, setTab] = useState('dashboard');
  return (
    <div className="content">
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: `1px solid ${G.border}`, paddingBottom: 0 }}>
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'list',      label: 'Envíos' },
          { id: 'settings',  label: 'Configuración' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '10px 18px', background: 'transparent', border: 'none',
              borderBottom: tab === t.id ? `2px solid ${G.gold}` : '2px solid transparent',
              color: tab === t.id ? G.white : G.muted,
              fontWeight: tab === t.id ? 700 : 500, cursor: 'pointer', fontSize: 14,
            }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'dashboard' && <TrackingDashboard showToast={showToast} />}
      {tab === 'list'      && <TrackingList showToast={showToast} />}
      {tab === 'settings'  && <TrackingSettings showToast={showToast} />}
    </div>
  );
}

function TrackingDashboard({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    trackingApi.dashboard()
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setLoading(false); showToast && showToast('Error: ' + (e.response?.data?.error || e.message), 'error'); });
  }, [showToast]);
  if (loading) return <div className="empty"><div className="empty-icon">◌</div>Cargando...</div>;
  if (!data) return <div className="empty">Sin datos</div>;
  const kpi = (label, value, color) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || G.white, fontFamily: 'DM Mono, monospace' }}>{value}</div>
    </div>
  );
  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {kpi('En tránsito', data.counts.inTransit, G.gold)}
        {kpi('Entregados hoy', data.counts.deliveredToday, G.green)}
        {kpi('Incidencias', data.counts.exceptions, G.red)}
        {kpi('Sin update >48h', data.counts.pendingAction)}
        {kpi('Total', data.counts.total, G.dim)}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${G.border}`, fontSize: 15, fontWeight: 600 }}>Actividad reciente</div>
        {(data.recent || []).length === 0 ? (
          <div className="empty" style={{ padding: 40 }}><div className="empty-icon">📦</div>Sin envíos todavía</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Código</th><th>Pedido</th><th>Cliente</th><th>Destino</th><th>Estado</th><th>Actualizado</th></tr></thead>
              <tbody>
                {data.recent.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: G.gold }}>{s.trackingCode}</td>
                    <td>{s.externalOrderName || '-'}</td>
                    <td style={{ color: G.dim }}>{s.customerName || '-'}</td>
                    <td style={{ color: G.dim, fontSize: 12 }}>{s.destinationCity}{s.destinationCountry ? ', ' + s.destinationCountry : ''}</td>
                    <td><span style={{ padding: '3px 10px', borderRadius: 999, background: G.surface, fontSize: 11, fontWeight: 600 }}>{s.currentStatus}</span></td>
                    <td style={{ color: G.muted, fontSize: 12 }}>{s.currentStatusAt ? new Date(s.currentStatusAt).toLocaleDateString('es-ES') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function TrackingList({ showToast }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [catalog, setCatalog] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    trackingApi.listShipments(params)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setLoading(false); showToast && showToast('Error: ' + (e.response?.data?.error || e.message), 'error'); });
  }, [q, statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { trackingApi.statusCatalog().then(r => setCatalog(r.catalog || {})); }, []);

  return (
    <>
      <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Búsqueda</div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Código, pedido, email..."
            style={{ width: '100%', background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6, fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>Estado</div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
            <option value="">Todos</option>
            {Object.entries(catalog).map(([code, info]) => (
              <option key={code} value={code}>{info?.label?.es || code}</option>
            ))}
          </select>
        </div>
        <button className="btn btn-gold btn-sm" onClick={load} disabled={loading}>{loading ? 'Cargando...' : 'Buscar'}</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${G.border}`, fontSize: 14, fontWeight: 600 }}>
          {data.total} envíos
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Código</th><th>Pedido</th><th>Cliente</th><th>Destino</th><th>Estado</th><th>Creado</th><th></th></tr></thead>
            <tbody>
              {(data.items || []).map(s => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(s.id)}>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: G.gold }}>{s.trackingCode}</td>
                  <td>{s.externalOrderName || '-'}</td>
                  <td style={{ color: G.dim, fontSize: 12 }}>{s.customer?.email}</td>
                  <td style={{ color: G.dim, fontSize: 12 }}>{s.destination?.city}{s.destination?.country ? ', ' + s.destination.country : ''}</td>
                  <td>
                    {(s.preorder && s.preorder.isPreorder && !s.preorder.releasedAt) ? (
                      <span title={s.preorder.releaseAt ? `Salida prevista: ${new Date(s.preorder.releaseAt).toLocaleDateString('es-ES')}` : 'Salida manual'} style={{ padding: '3px 10px', borderRadius: 999, background: '#312e81', color: '#c7d2fe', fontSize: 11, fontWeight: 700 }}>⏳ RESERVA{s.preorder.releaseAt ? ` · ${new Date(s.preorder.releaseAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}` : ''}</span>
                    ) : (
                      <span style={{ padding: '3px 10px', borderRadius: 999, background: G.surface, fontSize: 11, fontWeight: 600 }}>{s.currentStatus}</span>
                    )}
                  </td>
                  <td style={{ color: G.muted, fontSize: 12 }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('es-ES') : '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setSelected(s.id); }}>Ver</button>
                    {(s.preorder && s.preorder.isPreorder && !s.preorder.releasedAt) && (
                      <button className="btn btn-gold btn-sm" style={{ marginLeft: 6 }} onClick={(e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Liberar la reserva ${s.trackingCode} ahora? Se enviará el email de seguimiento y arrancará el recorrido.`)) return;
                        trackingApi.releaseShipment(s.id).then(() => { showToast('Reserva liberada: el seguimiento arranca ahora', 'success'); load(); }).catch(err => showToast(err.response?.data?.error || err.message, 'error'));
                      }}>Enviar ahora</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected && <ShipmentDetailModal shipmentId={selected} onClose={() => { setSelected(null); load(); }} showToast={showToast} catalog={catalog} />}
    </>
  );
}

function ShipmentDetailModal({ shipmentId, onClose, showToast, catalog }) {
  const [data, setData] = useState(null);
  const [addingEvent, setAddingEvent] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const reload = useCallback(() => {
    trackingApi.getShipment(shipmentId)
      .then(d => setData(d))
      .catch(e => showToast && showToast('Error: ' + (e.response?.data?.error || e.message), 'error'));
  }, [shipmentId, showToast]);
  useEffect(() => { reload(); }, [reload]);
  const submitEvent = async () => {
    if (!newStatus) return showToast && showToast('Selecciona un estado', 'error');
    try {
      await trackingApi.addEvent(shipmentId, { statusCode: newStatus, location: newLocation, notes: newNotes });
      setAddingEvent(false); setNewStatus(''); setNewLocation(''); setNewNotes('');
      reload();
      showToast && showToast('Evento añadido', 'success');
    } catch (e) { showToast && showToast('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };
  if (!data) return null;
  const { shipment, events } = data;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 14, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 18, fontWeight: 700, color: G.gold }}>{shipment.trackingCode}</div>
            <div style={{ color: G.muted, fontSize: 13, marginTop: 4 }}>{shipment.externalOrderName}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div><div style={{ fontSize: 11, color: G.muted }}>Cliente</div><div style={{ fontSize: 14 }}>{shipment.customer?.name}<br /><span style={{ color: G.dim, fontSize: 12 }}>{shipment.customer?.email}</span></div></div>
          <div><div style={{ fontSize: 11, color: G.muted }}>Destino</div><div style={{ fontSize: 14 }}>{shipment.destination?.city}, {shipment.destination?.country}</div></div>
          <div><div style={{ fontSize: 11, color: G.muted }}>Estado</div><div style={{ fontSize: 14, fontWeight: 600 }}>{shipment.currentStatus}{(shipment.preorder && shipment.preorder.isPreorder) && <div style={{ fontSize: 12, color: '#a5b4fc', fontWeight: 500, marginTop: 2 }}>⏳ Reserva · {shipment.preorder.releasedAt ? `liberada el ${new Date(shipment.preorder.releasedAt).toLocaleDateString('es-ES')}` : (shipment.preorder.releaseAt ? `salida prevista ${new Date(shipment.preorder.releaseAt).toLocaleDateString('es-ES')}` : 'salida manual')}</div>}</div></div>
          <div><div style={{ fontSize: 11, color: G.muted }}>Entrega estimada</div><div style={{ fontSize: 14 }}>{shipment.estimatedDelivery ? new Date(shipment.estimatedDelivery).toLocaleDateString('es-ES') : '-'}</div></div>
        </div>
        {!addingEvent ? (
          <button className="btn btn-gold btn-sm" onClick={() => setAddingEvent(true)}>+ Añadir evento</button>
        ) : (
          <div style={{ background: G.surface, padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ display: 'grid', gap: 10 }}>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={{ background: G.card, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6 }}>
                <option value="">Selecciona estado...</option>
                {Object.entries(catalog).map(([code, info]) => (<option key={code} value={code}>{info?.label?.es || code}</option>))}
              </select>
              <input value={newLocation} onChange={e => setNewLocation(e.target.value)} placeholder="Ubicación (opcional)" style={{ background: G.card, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6 }} />
              <input value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Notas internas (opcional)" style={{ background: G.card, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-gold btn-sm" onClick={submitEvent}>Guardar</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setAddingEvent(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>Timeline</div>
          <div style={{ position: 'relative', paddingLeft: 20 }}>
            <div style={{ position: 'absolute', left: 6, top: 6, bottom: 6, width: 2, background: G.border }} />
            {(events || []).map((e, i) => (
              <div key={e.id} style={{ position: 'relative', paddingBottom: 14 }}>
                <div style={{ position: 'absolute', left: -20, top: 4, width: 14, height: 14, borderRadius: '50%', background: i === 0 ? G.gold : G.muted, border: `2px solid ${G.card}` }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: G.white }}>{catalog[e.statusCode]?.label?.es || e.statusCode}</div>
                <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>{new Date(e.occurredAt).toLocaleString('es-ES')}{e.location ? ' · 📍 ' + e.location : ''}</div>
                {e.notes && <div style={{ fontSize: 12, color: G.dim, marginTop: 4, fontStyle: 'italic' }}>{e.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackingSettings({ showToast }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { trackingApi.getSettings().then(r => setCfg(r.settings)); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await trackingApi.saveSettings(cfg); setCfg(r.settings); showToast && showToast('Guardado', 'success'); }
    catch (e) { showToast && showToast('Error: ' + (e.response?.data?.error || e.message), 'error'); }
    setSaving(false);
  };
  if (!cfg) return <div className="empty">Cargando...</div>;
  const field = (label, key, type = 'number', desc) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: G.muted, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <input type={type} value={cfg[key] ?? ''} onChange={e => setCfg({ ...cfg, [key]: type === 'number' ? parseFloat(e.target.value) : e.target.value })}
        style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6, width: 200 }} />
      {desc && <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>{desc}</div>}
    </div>
  );
  return (
    <div className="card" style={{ padding: 24, maxWidth: 600 }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Configuración del tracking</div>
      {field('Días hasta la entrega', 'daysToDeliver', 'number', '3 a 30 días. Más días = más eventos intermedios.')}
      {field('Retraso del email del tracking (horas)', 'firstEmailDelayHours', 'number', 'Horas entre el pago y el primer email con el tracking. Default 12h.')}
      {field('Hora inicio (laboral cliente)', 'businessHourStart', 'number', 'Hora local del cliente desde la que se generan eventos. Default 9.')}
      {field('Hora fin (laboral cliente)', 'businessHourEnd', 'number', 'Hora local hasta la que se generan eventos. Default 18.')}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: G.white, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!cfg.sendUpdateEmails} onChange={e => setCfg({ ...cfg, sendUpdateEmails: e.target.checked })} />
          Enviar email en cada actualización del tracking
        </label>
      </div>
      {field('Marca (nombre)', 'brandName', 'text', 'Aparece en emails y página pública.')}
      {field('Dominio público', 'publicDomain', 'text', 'Sin https://. Ej: carryexpress365.com')}

      <div style={{ fontSize: 15, fontWeight: 700, margin: '24px 0 6px', paddingTop: 18, borderTop: `1px solid ${G.border}` }}>
        ✉️ Email del transportista
      </div>
      <div style={{ fontSize: 12, color: G.dim, marginBottom: 14 }}>
        Buzón desde el que salen los emails de seguimiento al cliente (el remitente es la marca del <strong>transportista</strong>, no la tienda — ej: "Expedira" &lt;Soporte@expediraexpress.com&gt;). Si lo dejas vacío se usa el remitente global de CarryExpress.
      </div>
      {field('SMTP host', 'smtpHost', 'text', 'Ej: smtp.ionos.es')}
      {field('SMTP puerto', 'smtpPort', 'number', '587 (STARTTLS) o 465 (SSL). Default 587.')}
      {field('SMTP usuario', 'smtpUser', 'text', 'Normalmente el email completo del buzón.')}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: G.muted, marginBottom: 4, fontWeight: 600 }}>SMTP contraseña</div>
        <input type="password" value={cfg.smtpPass ?? ''} placeholder={cfg.smtpPassSet ? '•••••••• (guardada — vacío = no cambiar)' : ''}
          onChange={e => setCfg({ ...cfg, smtpPass: e.target.value })}
          style={{ background: G.surface, border: `1px solid ${G.border}`, color: G.white, padding: '8px 12px', borderRadius: 6, width: 200 }} />
        <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Se guarda cifrada. Déjala vacía para mantener la actual.</div>
      </div>
      {field('Nombre del remitente', 'fromName', 'text', 'Ej: Expedira')}
      {field('From address', 'fromAddress', 'text', 'Ej: Soporte@expediraexpress.com (vacío = SMTP usuario)')}
      {field('Email de soporte (footer)', 'supportEmail', 'text', 'Aparece como contacto en el email. Ej: Soporte@expediraexpress.com')}
      <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
    </div>
  );
}

// ── Batch Payments · metadatos de estado (historial de lotes) ───────────────
// 2026-09-18. Declarados FUERA del componente (y antes de él, sin ventana TDZ)
// para no recrear los objetos en cada render. Las claves son EXACTAMENTE los
// estados que devuelve backend/src/routes/batch.js.
const BATCH_STATUS_META = {
  running:   ['badge-blue',   'En curso'],
  completed: ['badge-green',  'Completado'],
  stopped:   ['badge-orange', 'Detenido'],
  failed:    ['badge-red',    'Fallido'],
};
const BATCH_ROW_STATE_META = {
  pending:  ['badge-gray',   'Pendiente'],
  claimed:  ['badge-orange', 'En vuelo'],
  executed: ['badge-green',  'Cobrado'],
  failed:   ['badge-red',    'Fallido'],
  stopped:  ['badge-gray',   'Detenido'],
  expired:  ['badge-gray',   'Caducado'],
};
const BATCH_STATUS_FILTERS = [['all', 'Todos'], ['running', 'En curso'], ['completed', 'Completados'], ['stopped', 'Detenidos'], ['failed', 'Fallidos']];
const BATCH_PROVIDERS = [['ALL', 'Todos los proveedores'], ['STRIPE', 'Stripe'], ['SQUARE', 'Square'], ['TAILORED', 'TailoredPayments']];

function BatchStatusBadge({ status }) {
  const [cls, label] = BATCH_STATUS_META[status] || ['badge-gray', status || '--'];
  if (status === 'running') {
    return (
      <span className={`badge ${cls}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ display: 'inline-block', animation: 'spin 1.1s linear infinite', fontSize: 10, lineHeight: 1 }}>⟳</span>
        {label}
      </span>
    );
  }
  return <span className={`badge ${cls}`}>{label}</span>;
}

// Una fila atascada en `claimed` significa que el proceso murió entre el cobro
// y la respuesta del procesador: NO se sabe si el cliente fue cargado. El
// backend nunca la reintenta y aquí tampoco se ofrece reintentar.
function BatchRowStateBadge({ state, needsManualReview }) {
  if (needsManualReview) {
    return <span className="badge badge-red" title="El cobro se envió pero no se recibió respuesta. Revisa el procesador ANTES de volver a cobrar: reintentar podría duplicar el cargo.">⚠ Revisión manual</span>;
  }
  const [cls, label] = BATCH_ROW_STATE_META[state] || ['badge-gray', state || '--'];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function BatchPayment({ showToast }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ psp: '', gatewayId: '', dateFrom: '', dateTo: '', amount: '', currency: 'EUR', description: '', spreadMinutes: '' });
  const [customers, setCustomers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gateways, setGateways] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduled, setScheduled] = useState(null);

  const [batches, setBatches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [histLoading, setHistLoading] = useState(true);
  const [histError, setHistError] = useState(null);
  const [fStatus, setFStatus] = useState('all');
  const [fProvider, setFProvider] = useState('ALL');
  const [histPage, setHistPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    gatewaysApi.getAll().then(list => {
      const active = (Array.isArray(list) ? list : []).filter(g => g.active);
      setGateways(active);
      if (active.length && !form.gatewayId) {
        setForm(p => ({ ...p, gatewayId: active[0].id, psp: active[0].psp }));
      }
    }).catch(() => {});
  }, []);

  const pspGateways = gateways.filter(g => !form.psp || g.psp === form.psp);

  const fetchCustomers = () => {
    setLoading(true);
    batchApi.fetchCustomers({ psp: form.psp, gatewayId: form.gatewayId || undefined, dateFrom: form.dateFrom, dateTo: form.dateTo })
      .then(d => { setCustomers(d.customers || []); setStep(2); setLoading(false); })
      .catch(e => { showToast(e.response?.data?.error || e.message, 'error'); setLoading(false); });
  };

  const parseSpread = () => {
    const raw = String(form.spreadMinutes ?? '').trim();
    if (raw === '') return { ok: true, value: null };
    const n = parseInt(raw, 10);
    if (!Number.isInteger(n) || String(n) !== raw || n < 1 || n > 1440) return { ok: false, value: null };
    return { ok: true, value: n };
  };

  const charge = () => {
    const eligible = customers.filter(c => c.hasMethod);
    const sp = parseSpread();
    if (!sp.ok) { showToast('El reparto debe ser un número entero de minutos entre 1 y 1440 (24 h). Déjalo vacío para cobrar todo de golpe.', 'error'); return; }
    const selGw = gateways.find(g => g.id === form.gatewayId);
    const acctName = selGw ? `${selGw.psp} - ${selGw.name || selGw.id}` : '';
    const spreadTxt = sp.value ? `\nRepartido en ${sp.value} minutos.` : '';
    const acctTxt = acctName ? `\nCuenta: ${acctName}` : '';
    if (!window.confirm(`¿Cobrar ${fmtSymbol()}${form.amount} a ${eligible.length} clientes?${acctTxt}${spreadTxt}`)) return;
    setLoading(true);
    const payload = { customerIds: eligible.map(c => c.id), amount: Math.round((+form.amount || 0) * 100), currency: form.currency, description: form.description };
    if (form.gatewayId) payload.gatewayId = form.gatewayId;
    if (sp.value) payload.spreadMinutes = sp.value;
    if (form.dateFrom) payload.audienceFrom = form.dateFrom;
    if (form.dateTo) payload.audienceTo = form.dateTo;
    batchApi.charge(payload)
      .then(r => {
        if (r && r.spread) { setScheduled(r); setResults(null); }
        else { setResults(r); setScheduled(null); }
        setStep(3);
        setLoading(false);
        setHistPage(1);
        loadHistory(true);
      })
      .catch(e => { showToast(e.response?.data?.error || e.message, 'error'); setLoading(false); });
  };

  const runPreview = () => {
    setPreviewLoading(true);
    const body = { amount: Math.round((+form.amount || 0) * 100), currency: form.currency };
    if (form.dateFrom) body.dateFrom = form.dateFrom;
    if (form.dateTo) body.dateTo = form.dateTo;
    batchHistoryApi.preview(body)
      .then(d => { setPreview(d || null); setPreviewOpen(false); setPreviewLoading(false); })
      .catch(e => { setPreview(null); showToast('No se pudo previsualizar: ' + (e.response?.data?.error || e.message), 'error'); setPreviewLoading(false); });
  };

  const loadHistory = useCallback(async (silent = false) => {
    if (!silent) setHistLoading(true);
    try {
      const d = await batchHistoryApi.list({ status: fStatus, provider: fProvider, page: histPage, limit: 10 });
      setBatches(Array.isArray(d?.batches) ? d.batches : []);
      setSummary(d?.summary || null);
      setPagination(d?.pagination || null);
      setHistError(null);
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setBatches([]); setSummary(null); setPagination(null); setHistError(msg);
      if (!silent) showToast('No se pudo cargar el historial de lotes: ' + msg, 'error');
    } finally {
      if (!silent) setHistLoading(false);
    }
  }, [fStatus, fProvider, histPage, showToast]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadDetail = useCallback(async (id, silent = false) => {
    if (!id) return;
    if (!silent) setDetailLoading(true);
    try {
      const d = await batchHistoryApi.get(id);
      setDetail(d || null);
    } catch (e) {
      if (!silent) {
        showToast('No se pudo abrir el lote: ' + (e.response?.data?.error || e.message), 'error');
        setDetail(null); setDetailId(null);
      }
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, [showToast]);

  const openDetail = (id) => { setDetailId(id); setDetail(null); loadDetail(id); };
  const closeDetail = () => { setDetailId(null); setDetail(null); loadHistory(true); };

  const liveKey = detailId
    ? `d:${detailId}:${detail?.batch?.status || '?'}`
    : `l:${batches.some(b => b.status === 'running') ? '1' : '0'}`;
  useEffect(() => {
    const isLive = detailId ? liveKey.endsWith(':running') : liveKey === 'l:1';
    if (!isLive) return undefined;
    const t = setInterval(() => {
      if (detailId) loadDetail(detailId, true);
      else loadHistory(true);
    }, 7000);
    return () => clearInterval(t);
  }, [liveKey, detailId, loadDetail, loadHistory]);

  const stopBatch = (b) => {
    if (!b) return;
    const pend = Math.max(0, (b.progress?.total || 0) - (b.progress?.executed || 0));
    const msg = `¿Detener este lote?\n\nCuenta: ${b.gatewayAccount || '--'}\nProgreso: ${b.progress?.executed || 0} de ${b.progress?.total || 0} (${pend} pendientes)\n\nSe CANCELAN los cobros pendientes.\nLos cobros YA REALIZADOS no se revierten ni se reembolsan.`;
    if (!window.confirm(msg)) return;
    setStopping(true);
    batchHistoryApi.stop(b.id)
      .then(r => {
        if (r?.stopped) showToast(`Lote detenido — ${r.cancelledCharges || 0} cobros cancelados, ${r.inFlightCharges || 0} en vuelo.`, 'success');
        else showToast(r?.reason || 'El lote ya no estaba en curso', 'info');
        loadHistory(true);
        if (detailId === b.id) loadDetail(b.id, true);
      })
      .catch(e => showToast('No se pudo detener: ' + (e.response?.data?.error || e.message), 'error'))
      .finally(() => setStopping(false));
  };

  const money = (cents, cur) => (
    <>
      <span className="mono">{fmt((cents || 0) / 100)}</span>
      {cur && cur !== 'EUR' ? <span className="mono" style={{ color: G.muted, fontSize: 10, marginLeft: 4 }}>{cur}</span> : null}
    </>
  );

  const rangeTxt = (b) => {
    if (!b.audienceFrom && !b.audienceTo) return '--';
    return `${b.audienceFrom ? fmtDay(b.audienceFrom) : '…'} → ${b.audienceTo ? fmtDay(b.audienceTo) : '…'}`;
  };

  const humanDuration = (ms) => {
    if (!ms || ms < 0) return '--';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const batchDuration = (b) => {
    const start = b.startedAt ? new Date(b.startedAt).getTime() : 0;
    if (!start) return { total: 0, left: 0 };
    const end = b.endedAt ? new Date(b.endedAt).getTime() : (b.scheduledEndAt ? new Date(b.scheduledEndAt).getTime() : 0);
    const now = Date.now();
    const total = end ? end - start : 0;
    const elapsed = now - start;
    const left = b.status === 'running' && end ? Math.max(0, end - now) : 0;
    return { total, elapsed, left };
  };

  const perRebill = (b) => {
    const total = b.progress?.total || 0;
    const dur = batchDuration(b);
    if (!total || !dur.total) return { time: '--', rate: '--' };
    const perMs = dur.total / total;
    const perSec = Math.round(perMs / 1000);
    const m = Math.floor(perSec / 60);
    const s = perSec % 60;
    const timeTxt = m > 0 ? `${m}m ${s}s` : `${s}s`;
    const perMin = total > 0 && dur.total > 0 ? (total / (dur.total / 60000)).toFixed(2) : '0';
    return { time: timeTxt, rate: `${perMin} per minute` };
  };

  const acceptance = (b) => {
    const executed = b.progress?.executed || 0;
    if (!executed) return { pct: '--', detail: '0 / 0' };
    const succ = b.succeeded || 0;
    const pct = ((succ / executed) * 100).toFixed(1) + '%';
    return { pct, detail: `${succ} / ${executed}` };
  };

  const providerBadgeCls = (psp) => psp === 'STRIPE' ? 'badge-blue' : psp === 'SQUARE' ? 'badge-gold' : 'badge-green';

  const launchVisible = step === 1;

  return (
    <div className="content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: G.white }}>Batch payments {summary ? <span className="badge badge-gray" style={{ fontSize: 12, marginLeft: 8, verticalAlign: 'middle' }}>{summary.total}</span> : null}</h2>
          <div style={{ fontSize: 12, color: G.muted, marginTop: 2 }}>History and tracking of batch payments</div>
        </div>
        {!launchVisible && <button className="btn btn-gold" onClick={() => setStep(1)}>Launch batch payment</button>}
      </div>

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <div className="kpi-card"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${G.blue}22`, color: G.blue, fontSize: 15 }}>▤</span>
          <div><div className="kpi-value" style={{ fontSize: 28 }}>{summary ? summary.total : '--'}</div><div className="kpi-label">TOTAL</div></div>
        </div></div>
        <div className="kpi-card"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${G.blue}22`, color: G.blue, fontSize: 15 }}><span style={{ display: 'inline-block', animation: summary?.running ? 'spin 1.4s linear infinite' : 'none' }}>⟳</span></span>
          <div><div className="kpi-value" style={{ fontSize: 28, color: summary?.running ? G.blue : undefined }}>{summary ? summary.running : '--'}</div><div className="kpi-label">RUNNING</div></div>
        </div></div>
        <div className="kpi-card"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${G.green}22`, color: G.green, fontSize: 15 }}>✓</span>
          <div><div className="kpi-value" style={{ fontSize: 28, color: G.green }}>{summary ? summary.completed : '--'}</div><div className="kpi-label">COMPLETED</div></div>
        </div></div>
        <div className="kpi-card"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `${G.red}22`, color: G.red, fontSize: 13 }}>■</span>
          <div><div className="kpi-value" style={{ fontSize: 28, color: summary?.stoppedOrFailed ? G.red : undefined }}>{summary ? summary.stoppedOrFailed : '--'}</div><div className="kpi-label">STOPPED / FAILED</div></div>
        </div></div>
      </div>

      {launchVisible && (
        <div className="card" style={{ maxWidth: 580, marginBottom: 24 }}>
          <div className="card-head"><span className="card-title">Launch batch payment</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Account</label>
                <select className="form-input" value={form.gatewayId} onChange={e => {
                  const gw = gateways.find(g => g.id === e.target.value);
                  setForm(p => ({ ...p, gatewayId: e.target.value, psp: gw ? gw.psp : p.psp }));
                }}>
                  <option value="">-- Select an account --</option>
                  {gateways.map(g => <option key={g.id} value={g.id}>{g.psp} - {g.name || g.id}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Clientes desde</label><input type="date" className="form-input" value={form.dateFrom} onChange={e => setForm(p => ({ ...p, dateFrom: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Hasta</label><input type="date" className="form-input" value={form.dateTo} onChange={e => setForm(p => ({ ...p, dateTo: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Importe ({fmtSymbol()})</label><input type="number" className="form-input" placeholder="9.99" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Moneda</label>
                <select className="form-input" value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}><option>EUR</option><option>USD</option></select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Descripción</label><input className="form-input" placeholder="Cobro mensual plan Pro" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="form-group">
              <label className="form-label">Spread over (minutes)</label>
              <input type="number" min="1" max="1440" className="form-input" placeholder="ej: 530 (= ~9 horas)" value={form.spreadMinutes} onChange={e => setForm(p => ({ ...p, spreadMinutes: e.target.value }))} />
              <div style={{ fontSize: 11, color: G.dim, marginTop: 5, lineHeight: 1.5 }}>
                {form.spreadMinutes && parseInt(form.spreadMinutes) > 0
                  ? `≈ ${humanDuration(parseInt(form.spreadMinutes) * 60000)}. Los cobros se reparten equitativamente dentro de esta ventana.`
                  : 'Opcional. Déjalo vacío para cobrar todo de golpe.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-gold" onClick={fetchCustomers} disabled={loading || !form.gatewayId}>{loading ? 'Cargando clientes...' : 'Ver clientes →'}</button>
              <button className="btn btn-ghost" onClick={runPreview} disabled={previewLoading}>{previewLoading ? 'Calculando...' : 'Previsualizar (no cobra)'}</button>
            </div>

            {preview && (
              <div style={{ marginTop: 16, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Estimación — no se ha cobrado nada</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 11, color: G.dim, marginBottom: 2 }}>Clientes cobrables</div><div className="mono" style={{ fontSize: 20, color: G.goldLight, fontWeight: 600 }}>{preview.chargeableCustomers ?? 0}</div></div>
                  <div><div style={{ fontSize: 11, color: G.dim, marginBottom: 2 }}>Importe estimado</div><div className="mono" style={{ fontSize: 20, color: G.goldLight, fontWeight: 600 }}>{fmt((preview.estimatedTotal || 0) / 100)}</div></div>
                  {preview.undatedCustomers > 0 && <div><div style={{ fontSize: 11, color: G.dim, marginBottom: 2 }}>Sin fecha de alta</div><div className="mono" style={{ fontSize: 20, color: G.orange, fontWeight: 600 }}>{preview.undatedCustomers}</div></div>}
                </div>
                {(preview.chargeableCustomers || 0) === 0 && <div style={{ fontSize: 12, color: G.orange, marginTop: 10 }}>Ningún cliente con tarjeta guardada entra en ese periodo.</div>}
                {Array.isArray(preview.dailyBreakdown) && preview.dailyBreakdown.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPreviewOpen(o => !o)}>{previewOpen ? '▾' : '▸'} Desglose por día ({preview.dailyBreakdown.length})</button>
                    {previewOpen && (
                      <div className="table-wrap" style={{ marginTop: 10, maxHeight: 240, overflowY: 'auto' }}>
                        <table><thead><tr><th>Día</th><th style={{ textAlign: 'right' }}>Clientes</th><th style={{ textAlign: 'right' }}>Estimado</th></tr></thead>
                          <tbody>{preview.dailyBreakdown.map(d => <tr key={d.date}><td className="mono" style={{ fontSize: 11 }}>{fmtDay(d.date)}</td><td className="mono" style={{ fontSize: 11, textAlign: 'right' }}>{d.customers}</td><td className="mono" style={{ fontSize: 11, textAlign: 'right', color: G.goldLight }}>{fmt((d.estimatedTotal || 0) / 100)}</td></tr>)}</tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-head">
            <span className="card-title">Clientes encontrados: {customers.length}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: G.gold, fontFamily: 'DM Mono, monospace' }}>{customers.filter(c => c.hasMethod).length} con método de pago</span>
              {parseSpread().value ? <span className="badge badge-blue">Repartido en {humanDuration(parseSpread().value * 60000)}</span> : null}
              <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>← Atrás</button>
              <button className="btn btn-gold btn-sm" onClick={charge} disabled={loading || !customers.filter(c => c.hasMethod).length}>{loading ? 'Procesando...' : `Cobrar ${fmtSymbol()}${form.amount}`}</button>
            </div>
          </div>
          <div className="table-wrap">
            <table><thead><tr><th>#</th><th>Email</th><th>Nombre</th><th>Método de pago</th></tr></thead>
              <tbody>{customers.map((c, i) => <tr key={c.id}><td style={{ color: G.muted, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>{i + 1}</td><td style={{ fontSize: 12 }}>{c.email}</td><td style={{ fontSize: 12, color: G.dim }}>{c.name || '--'}</td><td><span className={`badge ${c.hasMethod ? 'badge-green' : 'badge-red'}`}>{c.hasMethod ? `OK ${c.methodCount} tarjeta(s)` : '✗ Sin método'}</span></td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}

      {step === 3 && results && (
        <div style={{ marginBottom: 24 }}>
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: 600 }}>
            <div className="kpi-card"><div className="kpi-label">Total procesados</div><div className="kpi-value">{results.total}</div></div>
            <div className="kpi-card"><div className="kpi-label">Exitosos</div><div className="kpi-value" style={{ color: G.green }}>{results.success}</div></div>
            <div className="kpi-card"><div className="kpi-label">Fallidos</div><div className="kpi-value" style={{ color: G.red }}>{results.failed}</div></div>
          </div>
          <div className="card">
            <div className="table-wrap">
              <table><thead><tr><th>Cliente ID</th><th>Estado</th><th>Detalle</th></tr></thead>
                <tbody>{results.results?.map((r, i) => <tr key={i}><td style={{ fontSize: 11, fontFamily: 'DM Mono, monospace' }}>{r.customerId}</td><td><StatusBadge status={r.status === 'success' ? 'SUCCEEDED' : 'FAILED'} /></td><td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>{r.paymentId || r.error || '--'}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => { setStep(1); setResults(null); setCustomers([]); }}>← Nuevo batch</button>
        </div>
      )}

      {step === 3 && scheduled && (
        <div style={{ marginBottom: 24 }}>
          <div className="card" style={{ maxWidth: 640 }}>
            <div className="card-head"><span className="card-title">Lote programado</span><BatchStatusBadge status={scheduled.status || 'running'} /></div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: G.dim, marginBottom: 14, lineHeight: 1.6 }}>
                {scheduled.total} cobros repartidos durante los próximos {humanDuration((scheduled.spreadMinutes || 0) * 60000)}.
              </div>
              <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 14 }}>
                <div className="kpi-card"><div className="kpi-label">Cobros programados</div><div className="kpi-value">{scheduled.total ?? '--'}</div></div>
                <div className="kpi-card"><div className="kpi-label">Importe por cliente</div><div className="kpi-value">{fmt((scheduled.amount || 0) / 100)}</div></div>
                <div className="kpi-card"><div className="kpi-label">Termina (aprox.)</div><div className="kpi-value" style={{ fontSize: 16 }}>{fmtDayTime(scheduled.scheduledEndAt).time || '--'}</div><div className="kpi-delta" style={{ color: G.muted }}>{fmtDayTime(scheduled.scheduledEndAt).day}</div></div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-gold btn-sm" onClick={() => openDetail(scheduled.batchId)}>Ver progreso del lote</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setStep(1); setScheduled(null); setCustomers([]); }}>← Nuevo batch</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── FILTERS (status pills + provider pills) ─── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</span>
          <div className="pill-group">
            {BATCH_STATUS_FILTERS.map(([v, label]) => (
              <button key={v} className={`pill ${fStatus === v ? 'active' : ''}`} onClick={() => { setFStatus(v); setHistPage(1); }}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Provider</span>
          <div className="pill-group">
            {BATCH_PROVIDERS.map(([v, label]) => (
              <button key={v} className={`pill ${fProvider === v ? 'active' : ''}`} onClick={() => { setFProvider(v); setHistPage(1); }}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => loadHistory()}>↻</button>
        </div>
      </div>

      {/* ─── BATCH TABLE (expandable rows like HP) ─── */}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Provider</th><th>Status</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Total Collected</th>
                <th>Range</th><th>Progress</th>
                <th>Ends</th><th>Account</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {histLoading && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: G.muted }}>Cargando historial…</td></tr>}
              {!histLoading && histError && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: G.orange }}>{histError}</td></tr>}
              {!histLoading && !histError && batches.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: G.muted }}>Todavía no hay lotes. Los cobros que lances aparecerán aquí.</td></tr>}
              {!histLoading && !histError && batches.map(b => {
                const pct = b.progress?.percent || 0;
                const dur = batchDuration(b);
                const isExpanded = expandedId === b.id;
                const acc = acceptance(b);
                const pr = perRebill(b);
                const succ = b.succeeded || 0;
                const fail = b.failed || 0;
                const collectedMoney = (b.totalCollected || 0) / 100;
                return (
                  <React.Fragment key={b.id}>
                    <tr style={{ cursor: 'pointer', background: isExpanded ? G.surface : undefined }} onClick={() => setExpandedId(isExpanded ? null : b.id)}>
                      <td style={{ fontSize: 11, color: G.muted, textAlign: 'center' }}>{isExpanded ? '▾' : '▸'}</td>
                      <td>
                        <span className={`badge ${providerBadgeCls(b.provider)}`} style={{ fontSize: 11 }}>{b.provider || '--'}</span>
                      </td>
                      <td><BatchStatusBadge status={b.status} /></td>
                      <td style={{ textAlign: 'right' }}>{money(b.amount, b.currency)}</td>
                      <td style={{ textAlign: 'right', color: G.goldLight, fontWeight: 600 }}>{money(b.totalCollected, b.currency)}</td>
                      <td className="mono" style={{ fontSize: 11, color: G.dim, whiteSpace: 'nowrap' }}>{rangeTxt(b)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="progress" style={{ width: 72 }}>
                            <div className="progress-fill" style={{
                              width: `${Math.min(100, pct)}%`,
                              background: b.status === 'running' ? G.blue
                                : b.status === 'completed' ? G.green
                                : b.status === 'failed' ? G.red
                                : b.status === 'stopped' ? G.orange : G.muted,
                              transition: 'width .5s ease',
                            }} />
                          </div>
                          <span className="mono" style={{ fontSize: 11, color: G.dim, whiteSpace: 'nowrap' }}>
                            {b.progress?.executed || 0}/{b.progress?.total || 0} · {pct}%
                            {succ > 0 && <span style={{ color: G.green, marginLeft: 4 }}>✓{succ}</span>}
                            {fail > 0 && <span style={{ color: G.red, marginLeft: 4 }}>✗{fail}</span>}
                          </span>
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {b.status === 'running' && b.scheduledEndAt ? (
                          <div style={{ color: G.orange }}>
                            <div>~{fmtDayTime(b.scheduledEndAt).time}</div>
                            {dur.left > 0 && <div style={{ fontSize: 10, color: G.dim, marginTop: 1 }}>{humanDuration(dur.left)}</div>}
                          </div>
                        ) : b.endedAt ? (
                          <div style={{ color: G.muted }}>
                            <div>{fmtDayTime(b.endedAt).day}</div>
                            <div style={{ fontSize: 10, color: G.dim, marginTop: 1 }}>{fmtDayTime(b.endedAt).time}</div>
                          </div>
                        ) : <span style={{ color: G.muted }}>--</span>}
                      </td>
                      <td style={{ fontSize: 12, color: G.dim, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.gatewayAccount || '--'}</td>
                      <td className="mono" style={{ fontSize: 11, color: G.muted, whiteSpace: 'nowrap' }}>{fmtDayTime(b.createdAt).day} {fmtDayTime(b.createdAt).time}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openDetail(b.id)}>Detail</button>
                          {b.status === 'running' && <button className="btn btn-danger btn-sm" disabled={stopping} onClick={() => stopBatch(b)}>Stop</button>}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={11} style={{ padding: '12px 16px', background: G.surface, borderTop: 'none' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px' }}>
                              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Duration</div>
                              <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: G.white }}>{humanDuration(dur.total)}</div>
                              {b.status === 'running' && dur.left > 0 && <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>Left ~{humanDuration(dur.left)}</div>}
                            </div>
                            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px' }}>
                              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Per Rebill</div>
                              <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: G.white }}>{pr.time}</div>
                              <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{pr.rate}</div>
                            </div>
                            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px' }}>
                              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Acceptance</div>
                              <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: G.white }}>{acc.pct}</div>
                              <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{acc.detail}</div>
                            </div>
                            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px' }}>
                              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Progress</div>
                              <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: G.white }}>{pct}%</div>
                              <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{b.progress?.executed || 0} / {b.progress?.total || 0}</div>
                            </div>
                            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px' }}>
                              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Settled</div>
                              <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: G.goldLight }}>{fmtSymbol()}{fmt(collectedMoney)}</div>
                            </div>
                          </div>
                          <div style={{ marginTop: 10, fontSize: 12, color: G.dim }}>
                            <span style={{ fontWeight: 600, color: G.white }}>Actual outcome of the charges</span>
                            <div style={{ marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                              {succ > 0 && <span><span style={{ color: G.green }}>●</span> SUCCEEDED: {succ} ({fmtSymbol()}{fmt(collectedMoney)})</span>}
                              {fail > 0 && <span><span style={{ color: G.red }}>●</span> FAILED: {fail}</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination && pagination.pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${G.border}`, flexWrap: 'wrap' }}>
            <div className="mono" style={{ fontSize: 11, color: G.muted }}>{pagination.matching} lotes</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" disabled={histPage <= 1} onClick={() => setHistPage(1)}>«</button>
              <button className="btn btn-ghost btn-sm" disabled={histPage <= 1} onClick={() => setHistPage(p => p - 1)}>‹</button>
              <span className="mono" style={{ fontSize: 11, color: G.dim, minWidth: 62, textAlign: 'center' }}>{histPage} / {pagination.pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={histPage >= pagination.pages} onClick={() => setHistPage(p => p + 1)}>›</button>
              <button className="btn btn-ghost btn-sm" disabled={histPage >= pagination.pages} onClick={() => setHistPage(pagination.pages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {detailId && (
        <Modal title="Detalle del lote" onClose={closeDetail}>
          {detailLoading && <div className="empty" style={{ padding: 30 }}>Cargando lote…</div>}
          {!detailLoading && !detail && <div className="empty" style={{ padding: 30 }}><div className="empty-icon">◻</div>No se pudo cargar el lote.</div>}
          {!detailLoading && detail && (() => {
            const b = detail.batch || {};
            const counts = detail.counts || {};
            const charges = Array.isArray(detail.charges) ? detail.charges : [];
            const review = charges.filter(c => c.needsManualReview);
            const errors = Array.isArray(detail.errors) ? detail.errors : [];
            return (
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                  <BatchStatusBadge status={b.status} />
                  <span className={`badge ${providerBadgeCls(b.provider)}`}>{b.provider || '--'}</span>
                  <span style={{ fontSize: 12, color: G.dim }}>{b.gatewayAccount || '--'}</span>
                  {b.spreadMinutes ? <span className="tag">spread {humanDuration(b.spreadMinutes * 60000)}</span> : <span className="tag">instant</span>}
                  <span className="mono" style={{ fontSize: 10, color: G.muted }}>{b.id}</span>
                </div>

                <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 14 }}>
                  <div className="kpi-card"><div className="kpi-label">Progreso</div><div className="kpi-value" style={{ fontSize: 18 }}>{b.progress?.executed || 0}/{b.progress?.total || 0}</div><div className="kpi-delta" style={{ color: G.muted }}>{b.progress?.percent || 0}%</div></div>
                  <div className="kpi-card"><div className="kpi-label">Cobrados</div><div className="kpi-value" style={{ fontSize: 18, color: G.green }}>{b.succeeded || 0}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Fallidos</div><div className="kpi-value" style={{ fontSize: 18, color: (b.failed || 0) > 0 ? G.red : undefined }}>{b.failed || 0}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Recaudado</div><div className="kpi-value" style={{ fontSize: 18, color: G.goldLight }}>{fmt((b.totalCollected || 0) / 100)}</div></div>
                </div>

                {review.length > 0 && (
                  <div style={{ background: G.redDim, border: `1px solid ${G.red}66`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: G.red, marginBottom: 5 }}>⚠ {review.length} cobro{review.length === 1 ? '' : 's'} requiere{review.length === 1 ? '' : 'n'} revisión manual</div>
                    <div style={{ fontSize: 11, color: G.dim, lineHeight: 1.6 }}>
                      Salieron al procesador pero no se recibió respuesta. NO se reintentan automáticamente. Comprueba cada uno en el panel del procesador antes de hacer nada.
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, fontSize: 11, color: G.dim }}>
                  {Object.entries(counts).map(([k, v]) => <span key={k} className="tag">{(BATCH_ROW_STATE_META[k] || [null, k])[1]}: {v}</span>)}
                </div>

                <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table>
                    <thead><tr><th>Cliente</th><th style={{ textAlign: 'right' }}>Importe</th><th>Programado</th><th>Estado</th><th>Referencia / error</th></tr></thead>
                    <tbody>
                      {charges.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: G.muted }}>Este lote no tiene cobros registrados.</td></tr>}
                      {charges.map(c => (
                        <tr key={c.id} style={c.needsManualReview ? { background: G.redDim } : undefined}>
                          <td style={{ fontSize: 12 }}>{c.email || c.customerId || '--'}</td>
                          <td style={{ textAlign: 'right' }}>{money(c.amount, c.currency)}</td>
                          <td className="mono" style={{ fontSize: 11, color: G.muted, whiteSpace: 'nowrap' }}>{fmtDayTime(c.scheduledAt).time || '--'}</td>
                          <td><BatchRowStateBadge state={c.state} needsManualReview={c.needsManualReview} /></td>
                          <td className="mono" style={{ fontSize: 11, color: c.error ? G.red : G.muted, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.error || c.externalId || ''}>{c.error || c.externalId || c.responseText || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detail.chargesTruncated && <div style={{ fontSize: 11, color: G.orange, marginTop: 8 }}>Mostrando los primeros {detail.chargesReturned} cobros del lote.</div>}

                {errors.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Errores ({errors.length})</div>
                    <div style={{ maxHeight: 130, overflowY: 'auto', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 10 }}>
                      {errors.map(er => <div key={er.id} className="mono" style={{ fontSize: 11, color: G.dim, marginBottom: 4 }}><span style={{ color: G.white }}>{er.email || er.customerId}</span> — <span style={{ color: G.red }}>{er.error}</span></div>)}
                    </div>
                  </div>
                )}

                <div className="modal-actions">
                  <button className="btn btn-ghost" onClick={closeDetail}>Cerrar</button>
                  {b.status === 'running' && <button className="btn btn-danger" disabled={stopping} onClick={() => stopBatch(b)}>{stopping ? 'Deteniendo...' : 'Detener lote'}</button>}
                </div>
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

function SimpleList({ apiObj, columns, createFields, title, showToast }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    apiObj.getAll().then(d => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([]));
  }, [apiObj]);
  useEffect(() => { load(); }, [load]);

  const save = () => {
    apiObj.create(form)
      .then(() => { showToast('Creado OK', 'success'); setModal(false); load(); })
      .catch(e => showToast(e.message, 'error'));
  };

  const del = (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    apiObj.delete(id).then(() => { showToast('Eliminado', 'info'); load(); }).catch(e => showToast(e.message, 'error'));
  };

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <button className="btn btn-gold" onClick={() => { setForm({}); setModal(true); }}>+ {title}</button>
      </div>
      <div className="card">
        {items.length === 0 ? <div className="empty"><div className="empty-icon">◻</div>Sin registros. Añade el primero.</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th>Acciones</th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    {columns.map(c => (
                      <td key={c.key}>{c.render ? c.render(item[c.key], item) : <span style={{ fontSize: 13 }}>{item[c.key] || '--'}</span>}</td>
                    ))}
                    <td><button className="btn btn-danger btn-sm" onClick={() => del(item.id)}>Eliminar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && (
        <Modal title={title} onClose={() => setModal(false)}>
          {createFields.map(f => (
            <div key={f.key} className="form-group">
              <label className="form-label">{f.label}</label>
              {f.type === 'select' ? (
                <select className="form-input" value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input className="form-input" type={f.type || 'text'} placeholder={f.placeholder} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
            <button className="btn btn-gold" onClick={save}>Guardar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Webhooks({ showToast }) {
  const [hooks, setHooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('endpoints');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ url: '', events: ['payment.succeeded'], active: true });

  const EVENTS = ['payment.succeeded', 'payment.failed', 'payment.created', 'subscription.created', 'subscription.cancelled', 'batch.completed', '*'];

  const load = useCallback(() => {
    webhooksApi.getAll().then(d => setHooks(Array.isArray(d) ? d : [])).catch(() => setHooks([]));
    webhooksApi.getLogs().then(d => setLogs(Array.isArray(d) ? d : [])).catch(() => setLogs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = () => {
    webhooksApi.create(form).then(() => { showToast('Webhook creado OK', 'success'); setModal(false); load(); }).catch(e => showToast(e.message, 'error'));
  };

  return (
    <div className="content">
      <div className="section-tabs">
        {['endpoints', 'logs'].map(t => <button key={t} className={`section-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t === 'endpoints' ? 'Endpoints' : 'Logs de entregas'}</button>)}
      </div>

      {tab === 'endpoints' && (
        <>
          <div style={{ marginBottom: 16 }}><button className="btn btn-gold" onClick={() => setModal(true)}>+ Nuevo webhook</button></div>
          <div className="card">
            {hooks.length === 0 ? <div className="empty"><div className="empty-icon">⊕</div>Sin webhooks configurados.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>URL destino</th><th>Eventos</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {hooks.map(h => (
                    <tr key={h.id}>
                      <td style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: G.dim }}>{h.url}</td>
                      <td>{(h.events || []).map(e => <span key={e} className="tag" style={{ marginRight: 4, marginBottom: 2 }}>{e}</span>)}</td>
                      <td><StatusBadge status={h.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => webhooksApi.update(h.id, { active: !h.active }).then(() => { showToast('Actualizado', 'info'); load(); })}>{h.active ? 'Pausar' : 'Activar'}</button>
                        <button className="btn btn-danger btn-sm" onClick={() => webhooksApi.delete(h.id).then(() => { showToast('Eliminado', 'info'); load(); })}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}

      {tab === 'logs' && (
        <div className="card">
          {logs.length === 0 ? <div className="empty"><div className="empty-icon">◎</div>Sin logs todavía.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Evento</th><th>Estado</th><th>Intento</th><th>Fecha</th></tr></thead>
              <tbody>
                {logs.slice(0, 50).map(l => (
                  <tr key={l.id}>
                    <td><span className="tag">{l.event}</span></td>
                    <td><StatusBadge status={l.status === 'delivered' ? 'SUCCEEDED' : 'FAILED'} /></td>
                    <td style={{ color: G.muted, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>#{l.attempt}</td>
                    <td style={{ color: G.muted, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>{l.createdAt ? new Date(l.createdAt).toLocaleString('es-ES', { timeZone: APP_TZ }) : '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {modal && (
        <Modal title="Nuevo webhook endpoint" onClose={() => setModal(false)}>
          <div className="form-group"><label className="form-label">URL destino</label><input className="form-input" placeholder="https://tu-app.com/webhooks" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} /></div>
          <div className="form-group">
            <label className="form-label">Eventos a escuchar</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {EVENTS.map(ev => (
                <button key={ev} type="button"
                  className={`pill ${form.events.includes(ev) ? 'active' : ''}`}
                  style={{ border: `1px solid ${G.border}`, borderRadius: 6 }}
                  onClick={() => setForm(p => ({ ...p, events: p.events.includes(ev) ? p.events.filter(e => e !== ev) : [...p.events, ev] }))}>
                  {ev}
                </button>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
            <button className="btn btn-gold" onClick={save}>Crear webhook</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// CONSTANTS & API
// -----------------------------------------------------------------------------

// 2026-06-05 — SaaS: read from REACT_APP_API_URL (injected by Cloudflare
// Pages at build time). Falls back to api.zeroriskpay.com when missing.
const BACKEND = (process.env.REACT_APP_API_URL || 'https://api.zeroriskpay.com').replace(/\/$/, '');

// PSP logo map for gateway tables (used outside GatewaySetup component)
const PSP_LOGOS = {
  STRIPE: '/logos/stripe.png', PAYPAL: '/logos/paypal.jpg', ADYEN: '/logos/adyen.png',
  CHECKOUTCOM: '/logos/checkoutcom.jpeg', MOLLIE: '/logos/mollie.png', AUTHORIZE: '/logos/authorize.png',
  BRAINTREE: '/logos/braintree.png', WORLDPAY: '/logos/worldpay.png', KLARNA: '/logos/klarna.png',
  SQUARE: '/logos/square.png', TAILORED: '/logos/tailored.png', COINGATE: '/logos/coingate.png',
};

// Auth-aware axios helper for setup endpoints
const setupAxios = () => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return { headers };
};
const setupApi = {
  validateStripe:   (k)    => axios.post(`${BACKEND}/api/setup/validate-stripe`, { secretKey: k }, setupAxios()).then(r => r.data),
  createWebhook:    (k)    => axios.post(`${BACKEND}/api/setup/create-stripe-webhook`, { secretKey: k, backendUrl: BACKEND }, setupAxios()).then(r => r.data),
  validateSquare:   (t, e) => axios.post(`${BACKEND}/api/setup/validate-square`, { accessToken: t, environment: e }, setupAxios()).then(r => r.data),
  getSettings:      ()     => axios.get(`${BACKEND}/api/setup/settings`, setupAxios()).then(r => r.data),
  saveSettings:     (d)    => axios.post(`${BACKEND}/api/setup/settings`, d, setupAxios()).then(r => r.data),
  runAiRouting:     ()     => axios.post(`${BACKEND}/api/setup/ai-routing`, {}, setupAxios()).then(r => r.data),
  getAiHistory:     ()     => axios.get(`${BACKEND}/api/setup/ai-history`, setupAxios()).then(r => r.data),
  getFraudStats:    ()     => axios.get(`${BACKEND}/api/setup/fraud-stats`, setupAxios()).then(r => r.data),
  getDailyReport:   ()     => axios.post(`${BACKEND}/api/setup/send-daily-report`, {}, setupAxios()).then(r => r.data),
};

// -----------------------------------------------------------------------------
// PLAN TIERS -- precios actualizados
// -----------------------------------------------------------------------------

// 2026-06-07 — Pricing model (final operator spec).
// Monthly EUR + variable transaction % + per-tracking USD fee.
// Features layered cumulatively (Plata inherits Bronce, Oro inherits Plata).
const PLAN_TIERS = {
  bronze: {
    name: 'Plan Bronce', emoji: '🥉', color: '#CD7F32', bg: '#CD7F3215', border: '#CD7F3240',
    price: 1000,         // monthly EUR
    currencySymbol: '€',
    trackingFee: 1.0,    // USD per tracking
    txFeePct: 7.5,
    billing: '1.000 €/mes + 7,5% por transacción + $1/tracking',
    features: [
      '4 pasarelas de pago',
      'Pixels ilimitados',
      'Email marketing',
      '2 plantillas de checkout',
      'Suscripciones y Payment Links',
      'Apple Pay & Google Pay',
      '$1 por tracking emitido',
      'Soporte por ticket',
    ],
  },
  silver: {
    name: 'Plan Plata', emoji: '🥈', color: '#A8A8A8', bg: '#A8A8A815', border: '#A8A8A840',
    price: 2000,
    currencySymbol: '€',
    trackingFee: 0.75,
    txFeePct: 5.0,
    billing: '2.000 €/mes + 5% por transacción + $0,75/tracking',
    features: [
      'Todo lo del plan Bronce, más:',
      '6 pasarelas de pago',
      'Rotador interno modo ghost (failover invisible)',
      'Retry Checkout',
      '4 plantillas de checkout',
      'Batch Payments',
      '$0,75 por tracking emitido',
      'Soporte prioritario por chat',
    ],
  },
  gold: {
    name: 'Plan Oro', emoji: '🥇', color: '#C9A84C', bg: '#C9A84C15', border: '#C9A84C40',
    price: 3000,
    currencySymbol: '€',
    trackingFee: 0.5,
    txFeePct: 3.5,
    billing: '3.000 €/mes + 3,5% por transacción + $0,50/tracking',
    features: [
      'Todo lo del plan Plata, más:',
      'Pasarelas ilimitadas',
      'Retry Checkout con cascade',
      'Cascade de precios A+B',
      'Vault propio',
      'Alertas vía Telegram',
      'Bot que responde a clientes automáticamente',
      'Email marketing personalizado',
      '8 plantillas de checkout',
      'Soporte 1:1 dedicado',
      '$0,50 por tracking emitido',
    ],
  },
};

// -----------------------------------------------------------------------------
// SECTION → MINIMUM PLAN MAP (matches landing page features)
// -----------------------------------------------------------------------------

// 2026-06-07 — Section/plan map aligned with operator-defined tiers.
// Bronze tenants get the orchestration core; Silver adds rotator/A-B
// routing + reconciliation; Gold unlocks the advanced & custom tools.
// 2026-06-10 — Mapping corregido. Antes había secciones del cliente
// marcadas 'admin' (balances/retry/sub-stats/batch) → un Oro veía el
// paywall. También bumps/analytics/templates eran exclusivos de Plata/Oro
// por error; son básicos para todos los clientes que pagan.
// Tiene que cuadrar con PLAN_FEATURES en App.js (sidebar) — si cambias
// uno, actualiza el otro.
const SECTION_PLAN_MAP = {
  // ── Bronze (core orchestration for every paying tenant) ──
  'dashboard':      'bronze',
  'payments':       'bronze',
  'payment-links':  'bronze',
  'subscriptions':  'bronze',
  'sub-stats':      'bronze',
  'tracking':       'bronze',
  'beneficios':     'bronze',
  'chargebacks':    'bronze',
  'analytics':      'bronze',
  'upgrade':        'bronze',
  'gateways':       'bronze',
  'checkouts':      'bronze',
  'coupons':        'bronze',
  'bumps':          'bronze',
  'templates':      'bronze',
  'settings':       'bronze',
  'shops':          'bronze',
  'products':       'bronze',
  'pixels':         'bronze',
  'emails':         'bronze',
  'customers':      'bronze',
  'live-funnel':    'bronze',
  'gateway-health': 'bronze',
  'sms-templates':  'bronze',
  'sms-campaigns':  'bronze',
  'sms-history':    'bronze',

  // ── Silver (routing intelligence + retry + batch + reconciliation) ──
  'rules':          'silver',
  'reconciliation': 'silver',
  'retry':          'silver',
  'batch':          'silver',

  // ── Gold (webhooks API + club + bot email) ──
  'webhooks':       'gold',
  'club':           'gold',
  'botEmail':       'gold',

  // ── Admin only (super-admin SaaS operator) ──
  'clients':        'admin',
  'leads':          'admin',
};

const PLAN_RANK = { bronze: 1, silver: 2, gold: 3, admin: 99 };

function canAccessSection(userPlan, userRole, sectionId) {
  if (userRole === 'admin') return true;
  const required = SECTION_PLAN_MAP[sectionId] || 'bronze';
  if (required === 'admin') return userRole === 'admin';
  const userRank = PLAN_RANK[userPlan] || 1;
  const requiredRank = PLAN_RANK[required] || 1;
  return userRank >= requiredRank;
}

function getRequiredPlan(sectionId) {
  return SECTION_PLAN_MAP[sectionId] || 'bronze';
}

// -----------------------------------------------------------------------------
// UPGRADE MODAL — shown when clicking locked feature
// -----------------------------------------------------------------------------

// 2026-06-11 — Copy persuasivo por sección + tier, "para tontos". Cuando el
// cliente toca un candado, ve los beneficios CONCRETOS de esa sección al
// hacer upgrade (no la lista genérica del plan). Si una sección no tiene
// copy aquí, el modal cae al fallback con PLAN_TIERS[tier].features.
const SECTION_UPGRADE_COPY = {
  retry: {
    icon: '↺',
    title: 'Retry Dashboard',
    purpose: 'Es el panel donde ves qué cobros han fallado y cuántos has conseguido recuperar gracias a los reintentos automáticos de ZRP.',
    silver: {
      hook: 'Recupera ~25% del dinero que crees perdido',
      bullets: [
        '+1.000-1.500 €/mes recuperados en un negocio típico de 100 cobros/día',
        'Ves cada euro recuperado por reintento — sabes lo que pagas, sabes lo que ganas',
        'Detectas qué tarjetas/PSPs fallan más y corriges la fuga',
      ],
      promise: 'Inversión recuperada el primer mes en negocios con >50 cobros/día.',
    },
    gold: {
      hook: 'Sube otro 5-10% sobre lo que recupera Plata',
      bullets: [
        '+300-500 €/mes extra sobre Plata gracias al cascade automático entre pasarelas',
        '+8.000 €/mes más con cascade de precios A+B (si €97 falla, intenta €47)',
        'Tú configuras horarios, intervalos y orden de PSPs — optimizado para TU negocio',
      ],
      promise: 'En negocios con ticket >€50, el upgrade a Oro suele costarse 5-10x en recuperación adicional.',
    },
  },
  rules: {
    icon: '🎛',
    title: 'Reglas de Routing',
    purpose: 'Es el panel donde decides en qué pasarela se cobra cada venta — manualmente, con A/B testing o dejando que la IA aprenda sola.',
    silver: {
      hook: 'Sube tu conversión 5-15% sin tocar copy ni precio',
      bullets: [
        'Mandas cada tarjeta al PSP que la cobra mejor — más ventas con el mismo tráfico',
        'AI Routing aprende solo qué pasarela convierte mejor por tipo de tarjeta',
        'A/B testing real: 50/50 entre dos PSPs, mides cuál te paga más',
      ],
      promise: 'En negocios de €30k/mes, +5% conversión = +1.500 €/mes con cero esfuerzo.',
    },
    gold: {
      hook: 'Failover invisible — ni una venta se pierde por un PSP caído',
      bullets: [
        'Ghost rotator: si Stripe rechaza, prueba Square sin que el cliente lo note',
        'El cliente ve 1 solo formulario y 1 solo intento. La rotación es silenciosa.',
        'Stripe cae 4h un sábado: tu negocio sigue cobrando, no pierdes ni un €',
      ],
      promise: 'Cobertura completa: lo que en Plata era "intento perdido", en Oro es "cobro rescatado".',
    },
  },
  reconciliation: {
    icon: '🔍',
    title: 'Reconciliación',
    purpose: 'Es el panel que compara automáticamente lo que dice cada pasarela (Stripe, Square, NMI…) con lo que tienes registrado en ZRP, detectando discrepancias en segundos.',
    silver: {
      hook: 'De 6 horas a 5 minutos cuadrando contabilidad',
      bullets: [
        'Tu contable se ahorra 5h/mes = 150-300 €/mes en su factura',
        'Detecta cobros perdidos, refunds duplicados, comisiones inesperadas',
        'Exporta CSV listo para subir directamente a Holded/Quipu/A3',
      ],
      promise: 'El propio ahorro en contabilidad ya paga 1/4 del upgrade a Plata.',
    },
  },
  batch: {
    icon: '⊞',
    title: 'Batch Payment',
    purpose: 'Es el panel para cobrar a muchos clientes de golpe filtrando por fecha o criterio. Útil para renovaciones manuales, recargos puntuales, o cobros masivos del día 1.',
    silver: {
      hook: 'Recobra a 200 clientes en 1 clic en vez de uno por uno',
      bullets: [
        'Ahorras 3-4 horas cada vez que toca cobrar manualmente',
        'Filtras por día, producto o plan y le das a "Cobrar todos"',
        'Resultado al instante: 187 OK, 13 fallidos — y el retry caza los fallidos',
      ],
      promise: 'Si haces 1 cobro masivo/mes, recuperas 36-48 horas al año.',
    },
  },
  webhooks: {
    icon: '⊕',
    title: 'Webhooks API',
    purpose: 'Es el panel donde configuras URLs para que ZRP avise a tu Shopify, CRM, ERP o sistema custom en tiempo real cada vez que pasa algo (cobro, refund, sub cancelada, etc.).',
    gold: {
      hook: 'Tu negocio reacciona solo — sin tocar el panel de ZRP',
      bullets: [
        'Cobro → Shopify da acceso al curso. Refund → cancela el acceso. Sin manual.',
        'Cada evento llega a tu sistema en <1 segundo, con firma HMAC para seguridad',
        'Hasta 3 reintentos si tu sistema está caído — cero eventos perdidos',
      ],
      promise: 'Cero clientes preguntando "¿por qué no tengo acceso?". Automatización total.',
    },
  },
  club: {
    icon: '👑',
    title: 'Club de Miembros',
    purpose: 'Es el panel que da a tus clientes con sub recurrente un área privada con login propio, donde acceden a contenido protegido, descargas y vídeos — gestionado por ZRP, sin Wordpress.',
    gold: {
      hook: 'Monta un negocio recurrente sin pagar Wordpress ni hosting',
      bullets: [
        '300 socios × €47/mes = 14.100 €/mes de ingresos recurrentes predecibles',
        'Cero coste de hosting (€99/mes ahorrados) y cero mantenimiento (5h/mes ahorradas)',
        'Si un socio deja de pagar, pierde acceso automáticamente — cero churn manual',
      ],
      promise: 'Ingresos recurrentes desde el día 1. Lo que con Wordpress te toma 2 semanas, aquí te toma 1 hora.',
    },
  },
  botEmail: {
    icon: '🤖',
    title: 'Bot Email IA',
    purpose: 'Es la sección donde conectas tu inbox de soporte (IMAP/SMTP). El bot lee TODOS los emails de tus clientes, los clasifica con IA y los contesta automáticamente usando plantillas personalizadas — 24 horas al día, sin tocar nada.',
    gold: {
      hook: '100% automatizado, soporte 24/7 sin contratar a nadie',
      bullets: [
        'Contesta el 100% de los emails de soporte — tracking, refunds, FAQs, todo',
        'Cero supervisión: el bot decide y responde solo con tus plantillas',
        'Ahorras 40-60h/mes de soporte = €800-1.800/mes (a €20/h)',
        'Plantillas personalizadas por motivo y por idioma (ES/EN)',
        'La IA (Claude) la paga ZeroRiskPay — coste cero para ti',
      ],
      promise: 'En negocios con 50+ emails/día, el bot ahorra más en sueldos que todo el plan Oro junto.',
    },
  },
  templates: {
    icon: '◻',
    title: 'Templates Custom',
    purpose: 'Es el panel donde personalizas el aspecto de tus emails automáticos y páginas de checkout con tu marca: colores, logo, copy, CSS propio.',
    gold: {
      hook: 'Convierte 10-20% más con un checkout que se ve TUYO',
      bullets: [
        '8 plantillas de checkout (vs 2 en Bronce, 4 en Plata) — más A/B, más conversión',
        'Tus emails automáticos llevan tu logo, tus colores, tu tono — no "powered by ZRP"',
        'Trust subido: el cliente cree que está pagándote a TI, no a un intermediario',
      ],
      promise: 'En €30k/mes, +10% conversión = +3.000 €/mes con un checkout más cuidado.',
    },
  },
};

function UpgradeModal({ section, requiredPlan, userPlan, onClose, onNavigate }) {
  // 2026-06-11 — Modal muestra beneficios CONCRETOS por sección, no la
  // lista genérica del plan. Lee de SECTION_UPGRADE_COPY. Tiers a mostrar:
  // todos los planes ESTRICTAMENTE por encima del actual del usuario.
  //   Bronce → tarjetas de Plata + Oro
  //   Plata  → tarjeta de Oro
  //   Oro    → defensive null (no debería disparar nunca)
  const currentRank = PLAN_RANK[userPlan] || 1;
  const upgradeTiers = ['silver', 'gold'].filter(p => PLAN_RANK[p] > currentRank);
  if (upgradeTiers.length === 0) return null;

  const minRequired = PLAN_TIERS[requiredPlan] || PLAN_TIERS.silver;
  const sectionCopy = SECTION_UPGRADE_COPY[section];
  const subject = `Upgrade%20al%20plan%20${encodeURIComponent(upgradeTiers.map(p => PLAN_TIERS[p].name).join(' o '))}`;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, padding:20, overflowY:'auto' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background:G.card, border:`1px solid ${minRequired.border}`, borderRadius:20, padding:'32px 28px 28px', maxWidth: upgradeTiers.length > 1 ? 760 : 480, width:'100%', position:'relative', maxHeight:'90vh', overflowY:'auto' }}>
        <button onClick={onClose} style={{ position:'absolute', top:14, right:16, background:'none', border:'none', color:G.muted, fontSize:22, cursor:'pointer', lineHeight:1, padding:4 }}>×</button>

        <div style={{ textAlign:'center', marginBottom:22 }}>
          <div style={{ fontSize:42, marginBottom:8 }}>🔒</div>
          <h2 style={{ color:G.white, fontSize:22, fontWeight:700, margin:'0 0 6px', fontFamily:'Cormorant Garamond, serif' }}>
            {sectionCopy ? `${sectionCopy.icon} ${sectionCopy.title}` : 'Funcionalidad Premium'}
          </h2>
          <p style={{ color:G.dim, fontSize:13, margin:'0 0 12px', lineHeight:1.5 }}>
            Necesitas <span style={{ color:minRequired.color, fontWeight:700 }}>{minRequired.emoji} {minRequired.name}</span> o superior para usar esta sección.
          </p>
          {sectionCopy?.purpose && (
            <p style={{ color:G.muted, fontSize:12.5, margin:'0 auto', maxWidth:520, lineHeight:1.6, fontStyle:'italic' }}>
              {sectionCopy.purpose}
            </p>
          )}
        </div>

        <div style={{ display:'grid', gridTemplateColumns: upgradeTiers.length > 1 ? '1fr 1fr' : '1fr', gap:14, marginBottom:22 }}>
          {upgradeTiers.map(planKey => {
            const tier = PLAN_TIERS[planKey];
            const isRequired = planKey === requiredPlan;
            // Si hay copy específico para la sección + este tier, lo usamos.
            // Fallback: lista genérica del plan (tier.features).
            const tierCopy = sectionCopy?.[planKey];

            return (
              <div key={planKey} style={{ background:tier.bg, border:`${isRequired ? 2 : 1}px solid ${tier.border}`, borderRadius:12, padding:'20px 18px 16px', textAlign:'left', position:'relative' }}>
                {isRequired && (
                  <div style={{ position:'absolute', top:-10, left:14, background:tier.color, color:'#000', fontSize:9, fontWeight:800, padding:'3px 8px', borderRadius:4, textTransform:'uppercase', letterSpacing:0.5 }}>
                    Plan mínimo
                  </div>
                )}

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
                  <div style={{ color:tier.color, fontWeight:700, fontSize:15 }}>{tier.emoji} {tier.name}</div>
                  <div style={{ color:tier.color, fontWeight:700, fontSize:13 }}>{fmt(tier.price, { decimals: 0 })}/mes</div>
                </div>
                <div style={{ color:G.muted, fontSize:10, marginBottom:14 }}>{tier.txFeePct}% por tx · ${tier.trackingFee}/tracking</div>

                {tierCopy ? (
                  <>
                    <div style={{ color:G.white, fontWeight:600, fontSize:13, marginBottom:10, lineHeight:1.4 }}>{tierCopy.hook}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:7, marginBottom:tierCopy.promise ? 12 : 0 }}>
                      {tierCopy.bullets.map((b, i) => (
                        <div key={i} style={{ color:G.dim, fontSize:12, display:'flex', alignItems:'flex-start', gap:8, lineHeight:1.5 }}>
                          <span style={{ color:tier.color, flexShrink:0, fontSize:11 }}>▸</span><span>{b}</span>
                        </div>
                      ))}
                    </div>
                    {tierCopy.promise && (
                      <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${tier.border}`, color:tier.color, fontSize:11.5, fontWeight:600, lineHeight:1.5 }}>
                        💡 {tierCopy.promise}
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback: lista genérica de features del plan
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    {tier.features.map((f, i) => (
                      <div key={i} style={{ color:G.dim, fontSize:11, display:'flex', alignItems:'flex-start', gap:6, lineHeight:1.5 }}>
                        <span style={{ color:tier.color, flexShrink:0 }}>✓</span><span>{f}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
          <button onClick={onClose} style={{ padding:'10px 22px', borderRadius:10, background:'transparent', border:`1px solid ${G.border}`, color:G.dim, fontSize:13, cursor:'pointer', fontWeight:600 }}>
            Cerrar
          </button>
          {onNavigate && (
            <button onClick={() => { onClose(); onNavigate('upgrade'); }} style={{ padding:'10px 22px', borderRadius:10, background:`linear-gradient(135deg, ${minRequired.color}, ${minRequired.color}dd)`, color:'#000', fontSize:13, cursor:'pointer', fontWeight:700 }}>
              📖 Más info — comparativa completa
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// UPGRADE PAGE — full comparison + benefit deep-dive, accessible from sidebar
// and from the UpgradeModal "Ver comparativa completa" button.
// 2026-06-11 — Pensado para vender el upgrade SIN que el operator tenga que
// explicarlo. Cada feature lleva: gancho, descripción ✕ para tontos, y un
// ejemplo numérico que aterriza el ROI.
// -----------------------------------------------------------------------------

const UPGRADE_FEATURES_SILVER = [
  {
    icon: '↺',
    title: 'Retry Dashboard',
    hook: 'Recupera ventas que ahora estás perdiendo cada día',
    desc: 'Cuando una tarjeta es declinada, ZRP la reintenta sola con el timing óptimo. El dashboard te muestra qué cobros fallaron, cuántos se han recuperado y cuánto dinero te ha traído. Sabes exactamente cuánto vale el sistema para ti.',
    example: '100 cobros al día. 15 fallan en el primer intento. Con retry recuperas ~4-5 al día = ~25% recuperado = €1.000-1.500/mes extra para un negocio típico.',
  },
  {
    icon: '🎛',
    title: 'Reglas de Routing (AI + A/B)',
    hook: 'Decide en qué pasarela se cobra cada tipo de venta',
    desc: 'Mandas las tarjetas grandes a Stripe (paga mejor en B2B), las pequeñas a Square (menor comisión), las europeas a NMI. O dejas que el AI Routing aprenda solo qué PSP convierte mejor para cada tipo de tarjeta. O activas A/B testing: 50% a Stripe, 50% a Square, mides cuál convierte más.',
    example: 'Activas AI Routing en cursos de €97 y €197. En 2 semanas el sistema descubre que Stripe convierte 12% más en los €197 pero 5% menos en los €97. Enruta solo. +€2.300/mes sin tocar nada.',
  },
  {
    icon: '🔍',
    title: 'Reconciliación Contable',
    hook: 'Sin Excels — compara Stripe/Square con ZRP automáticamente',
    desc: 'ZRP descarga lo que dice cada pasarela y lo cruza con tus pagos en ZRP. Detecta cobros que están en una pero no en la otra, refunds que no encajan, comisiones inesperadas. Exporta CSV listo para tu contable.',
    example: 'Tu contable suele tardar 6h/mes cuadrando Stripe con tu sistema. Con Reconciliación: 5 minutos. Le ahorras 5h/mes (= €150-300/mes en su factura).',
  },
  {
    icon: '⊞',
    title: 'Batch Payment',
    hook: 'Cobra a 500 clientes a la vez con un clic',
    desc: 'Eliges una fecha, ZRP cobra a todos los clientes que firmaron ese día (o filtras por producto, plan, etc.). Ideal para renovaciones manuales, segundo cargo masivo, recargos puntuales.',
    example: 'Día 30 del mes recobras a 200 clientes en 1 clic en vez de uno por uno. Ahorras 3-4h/mes. Ves resultado: 187 OK, 13 fallidos — el retry los atrapará después.',
  },
  {
    icon: '💳',
    title: '6 pasarelas (vs 4 en Bronce)',
    hook: 'Más PSPs = más opciones para optimizar conversión',
    desc: 'Bronce te limita a 4 procesadores. Plata te deja añadir 2 más. Útil para diversificar riesgo (si un PSP cae, los otros siguen) y para combinarlo con AI Routing.',
    example: 'Con 6 PSPs activos, el riesgo de "cero ventas durante 4h porque Stripe está caído" se reduce a casi cero.',
  },
  {
    icon: '🛡',
    title: 'Ghost Rotator (failover invisible)',
    hook: 'Si un PSP falla, prueba el siguiente sin que el cliente lo note',
    desc: 'Rotator interno modo ghost: si Stripe rechaza, intenta Square; si Square rechaza, intenta NMI. El cliente ve UN solo formulario y UN solo intento. La rotación es invisible.',
    example: 'Cliente paga €197. Stripe rechaza por fraud check. Square acepta. Cliente nunca vio el rechazo. Conversión +5-10% en cobros donde antes un solo "no" cerraba la venta.',
  },
  {
    icon: '📦',
    title: 'Tracking más barato ($0,75 vs $1)',
    hook: 'Ahorras 25% en cada envío que emites',
    desc: 'CarryExpress de ZRP cobra $1 por tracking en Bronce, $0,75 en Plata, $0,50 en Oro. Si emites 500 trackings/mes, son $125 menos al mes.',
    example: '500 envíos/mes → ahorras $125/mes = $1.500/año en trackings.',
  },
];

const UPGRADE_FEATURES_GOLD = [
  {
    icon: '🤖',
    title: 'Bot Email IA 24/7 (NUEVO)',
    hook: '100% automatizado — el bot contesta TODOS los emails sin tocar nada',
    desc: 'Conectas tu inbox (soporte@tu-dominio.com) vía IMAP/SMTP y el bot empieza a leer y contestar emails cada 5 min. Clasifica con IA, responde con tus plantillas FAQ por motivo e idioma, sin intervención humana. La IA (Claude) la paga ZeroRiskPay — sin coste extra. Logs y stats auditables.',
    example: 'Un negocio típico con 50 emails/día: el bot contesta los 50 sin que tú toques el teclado. Ahorro: 40-60h/mes = €800-1.800/mes en soporte. El plan Oro se paga solo el primer mes.',
  },
  {
    icon: '🔄',
    title: 'Cascade de PSPs (failover encadenado)',
    hook: 'Stripe → Square → NMI → … hasta que uno cobre',
    desc: 'No es "intenta una vez y se rinde". Es: intenta Stripe, si rechaza intenta Square, si rechaza intenta NMI. ZRP recorre tus pasarelas en orden hasta que una acepta. El cliente solo ve el resultado final.',
    example: '15 cobros fallaron tras retry en Plata. Con cascade de PSPs en Oro, recuperas ~3-5 adicionales al día. €300-500/mes extra.',
  },
  {
    icon: '🪜',
    title: 'Cascade de precios A+B',
    hook: 'Si €97 falla, prueba con €47 antes de rendirte',
    desc: 'Configuras un producto principal (A = €97) y uno alternativo (B = €47). Si el cobro de €97 falla incluso tras todos los retries, ZRP intenta el de €47. Recuperas ventas que con Plata estaban perdidas.',
    example: '100 cobros/día a €97. 15 fallan tras todo el retry/cascade de PSPs. Con cascade de precios, recuperas 6 a €47 = €282 más al día = €8.460/mes adicionales.',
  },
  {
    icon: '⊕',
    title: 'Webhooks API',
    hook: 'Conecta ZRP con tu Shopify, CRM, ERP o sistema custom',
    desc: 'Cada cobro, refund, sub creada o cancelada → tu sistema recibe un HTTP POST al instante con la info. Hasta 3 reintentos con firma HMAC. Automatizas facturación, entregas, accesos, emails — sin tocar el dashboard de ZRP.',
    example: 'Cliente paga un curso €197. ZRP webhook → tu Shopify → ya tiene acceso. ZRP webhook → Brevo → empieza onboarding. ZRP webhook → Notion → factura generada. 3 sistemas reaccionan en <1 segundo.',
  },
  {
    icon: '👑',
    title: 'Club de Miembros',
    hook: 'Área privada de socios sin Wordpress, sin plugins',
    desc: 'Cada cliente que paga una sub recurrente tiene su login propio. Accede a contenido protegido, descarga material, ve vídeos. Si deja de pagar, su acceso se cancela automáticamente. Todo gestionado por ZRP.',
    example: 'Membership de €47/mes con 300 socios = €14.100/mes. Sin Wordpress (€99/mes hosting + 5h/mes mantenimiento ahorrados). Sin churn manual: cancela el cobro = cancela el acceso, mismo segundo.',
  },
  {
    icon: '🤖',
    title: 'Bot que responde emails',
    hook: 'El bot contesta los emails de tus clientes — sin que tú toques nada',
    desc: 'En Bronce/Plata, ZRP solo ENVÍA emails (notificación de compra, tracking, etc.). En Oro hay un bot conectado al inbox del soporte que CONTESTA cuando el cliente responde — pregunta dónde está su pedido, pide un refund, quiere cambiar la tarjeta, etc. — usando los datos reales del cliente.',
    example: 'Cliente: "¿Dónde está mi pedido CE24-A8K3J?" Bot: "Tu paquete salió ayer de Madrid, llega mañana 12 oct. Tracking: https://zrp.com/track/CE24-A8K3J". Sin tú tocar nada. Ahorras 40-60 emails/día = ~1h/día = 20h/mes.',
  },
  {
    icon: '🔔',
    title: 'Alertas vía Telegram',
    hook: 'Te llega un ping en Telegram cuando algo importante pasa',
    desc: 'Cobros de >€500, sub cancelada, chargeback recibido, gateway caído, tasa de fallo >5%. Te llega un mensaje al instante en tu Telegram. No te enteras 6h después por el dashboard.',
    example: 'Stripe deja de procesar a las 14h. Te llega ping a las 14:01. Activas Square como principal. Pérdida: 1 minuto. Sin Telegram: te enteras a las 18h cuando revisas KPIs.',
  },
  {
    icon: '🔐',
    title: 'Vault propio',
    hook: 'Tus tarjetas en TU vault, no atadas a una pasarela',
    desc: 'En Bronce/Plata las tarjetas tokenizadas viven en Stripe (o el PSP que uses). Si te quieres cambiar mañana a Adyen, NO te puedes llevar las suscripciones recurrentes — son rehenes de Stripe. Con Vault propio, las tarjetas están en ZRP cifradas, y puedes mover subs entre PSPs sin perder ninguna.',
    example: 'Cambias de Stripe a Adyen porque Adyen te ofrece 0,5% menos de comisión. Con Vault Oro: migras las 200 subs activas en un script. Sin Vault: pierdes el ~30% (cancelan al pedirle re-tokenizar).',
  },
  {
    icon: '∞',
    title: 'Pasarelas ilimitadas',
    hook: 'Mete todas las que quieras (Stripe, Square, NMI, Adyen, Checkout.com…)',
    desc: 'Sin límite de procesadores. Combinas con cascade y AI Routing para sacar el máximo de cada uno.',
    example: 'Negocio que vende en US, Europa y LATAM con 8 PSPs distintos (uno especializado por región). Imposible en Bronce/Plata.',
  },
  {
    icon: '⚙',
    title: 'Retry y reglas configurables',
    hook: 'Tú decides cuántos reintentos, cuándo y con qué pasarela',
    desc: 'En Plata el retry está pre-configurado (3 intentos, intervalos fijos). En Oro tú defines: cuántos intentos, qué pasarela en cada intento, qué precio en cada intento, qué hora del día evitar, etc.',
    example: 'Aprendes que reintentar a las 9:00 lunes da mejor resultado que a las 22:00 viernes. Lo configuras. Tu retry rate sube otro 2-3%.',
  },
  {
    icon: '✉',
    title: 'Email marketing personalizado',
    hook: 'Cambias copy, colores, logos y CTAs en cada email automático',
    desc: 'En Bronce/Plata los emails automáticos llevan la plantilla por defecto de ZRP. En Oro editas cada uno: copy, colores, logo, links, CTAs. Cero pinta de "ZRP" para tu cliente final.',
    example: 'Tu cliente recibe el email de tracking y piensa que viene de tu marca, no de un proveedor genérico. Trust subido, marca reforzada.',
  },
  {
    icon: '◻',
    title: '8 plantillas de checkout (vs 2 Bronce, 4 Plata)',
    hook: 'Más opciones de diseño para probar cuál convierte',
    desc: 'Cada vertical convierte mejor con un diseño distinto. Con 8 plantillas pruebas más combinaciones y das con la que mejor convierte en TU nicho.',
    example: 'Pruebas 3 plantillas distintas en una campaña de tráfico frío. La 3a convierte 18% mejor. Eso son €1.800/mes extra por cada €10k de ad spend.',
  },
  {
    icon: '💎',
    title: 'Soporte 1:1 dedicado',
    hook: 'Tu persona de soporte, no un ticket en una cola',
    desc: 'Tienes un Telegram/Slack directo con tu account manager. Respuestas en minutos, no horas. Ayuda a configurar retry óptimo, elegir PSPs, debug de campañas.',
    example: 'Sábado a las 22h te falla una campaña Black Friday. En Bronce mandas un ticket — respuesta lunes. En Oro escribes en Telegram — respuesta en 15 min, salvas la campaña.',
  },
  {
    icon: '📦',
    title: 'Tracking más barato ($0,50 vs $1)',
    hook: 'Mitad de precio en cada envío vs Bronce',
    desc: '$0,50 por tracking en Oro vs $1 en Bronce. Si emites 1000 trackings/mes, ahorras $500/mes = $6.000/año.',
    example: '2000 envíos/mes: ahorras $1.000/mes vs Bronce. Solo eso ya paga 1/3 de la diferencia de plan.',
  },
];

const COMPARISON_ROWS = [
  { label: 'Dashboard, Pagos, Suscripciones, Tracking', bronze: '✓', silver: '✓', gold: '✓' },
  { label: 'Payment Links, Productos, Pixels, Tiendas', bronze: '✓', silver: '✓', gold: '✓' },
  { label: 'Beneficios, Analíticas, Chargebacks', bronze: '✓', silver: '✓', gold: '✓' },
  { label: 'Apple Pay y Google Pay', bronze: '✓', silver: '✓', gold: '✓' },
  { label: 'Pasarelas de pago', bronze: '4', silver: '6', gold: '∞' },
  { label: 'Comisión por transacción', bronze: '7,5 %', silver: '5 %', gold: '3,5 %' },
  { label: 'Coste por tracking emitido', bronze: '$1,00', silver: '$0,75', gold: '$0,50' },
  { label: 'Cuota mensual', bronze: '€1.000', silver: '€2.000', gold: '€3.000' },
  { label: 'Retry de pagos fallidos', bronze: '—', silver: '✓', gold: '✓ + cascade' },
  { label: 'Reglas de routing (AI + A/B)', bronze: '—', silver: '✓', gold: '✓ + ghost rotator' },
  { label: 'Reconciliación contable', bronze: '—', silver: '✓', gold: '✓' },
  { label: 'Batch Payment masivo', bronze: '—', silver: '✓', gold: '✓' },
  { label: 'Plantillas de checkout', bronze: '2', silver: '4', gold: '8' },
  { label: 'Cascade de precios A+B (€97 → €47)', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Webhooks API a tu sistema', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Club de miembros', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Bot Email IA (IMAP/SMTP propio + plantillas)', bronze: '—', silver: '—', gold: '✓ Activo' },
  { label: 'Alertas vía Telegram', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Vault propio de tarjetas', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Retry totalmente configurable', bronze: '—', silver: '—', gold: '✓' },
  { label: 'Email marketing personalizado', bronze: 'Básico', silver: 'Básico', gold: 'Custom' },
  { label: 'Soporte', bronze: 'Ticket', silver: 'Chat prioritario', gold: '1:1 dedicado' },
];

function FeatureBlock({ feat, tierColor }) {
  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 22, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ fontSize: 32, lineHeight: 1, flexShrink: 0, width: 44, height: 44, background: `${tierColor}15`, border: `1px solid ${tierColor}40`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {feat.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: G.white, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{feat.title}</div>
          <div style={{ color: tierColor, fontSize: 13, fontWeight: 600, marginBottom: 10, lineHeight: 1.5 }}>{feat.hook}</div>
          <div style={{ color: G.dim, fontSize: 13, lineHeight: 1.65, marginBottom: 12 }}>{feat.desc}</div>
          <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px', color: G.muted, fontSize: 12, lineHeight: 1.6 }}>
            <span style={{ color: tierColor, fontWeight: 700 }}>💡 Ejemplo real:</span> {feat.example}
          </div>
        </div>
      </div>
    </div>
  );
}

function UpgradePage({ userPlan }) {
  const plan = (userPlan || 'bronze').toLowerCase();
  const isGold = plan === 'gold';
  const isSilver = plan === 'silver';
  const isBronze = plan === 'bronze';

  const silverColor = PLAN_TIERS.silver.color;
  const goldColor = PLAN_TIERS.gold.color;

  return (
    <div className="content" style={{ paddingBottom: 60 }}>
      {/* HERO */}
      <div style={{ background: `linear-gradient(135deg, ${G.card}, ${G.black})`, border: `1px solid ${G.border}`, borderRadius: 16, padding: '32px 28px', marginBottom: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>{isGold ? '🥇' : '🚀'}</div>
        <h1 style={{ color: G.white, fontSize: 28, fontWeight: 700, margin: '0 0 10px', fontFamily: 'Cormorant Garamond, serif' }}>
          {isGold ? 'Estás en el Plan Oro' : 'Haz crecer tu negocio con ZeroRiskPay'}
        </h1>
        <p style={{ color: G.dim, fontSize: 14, margin: 0, lineHeight: 1.6, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
          {isGold
            ? 'Tienes todas las funciones desbloqueadas. Si necesitas algo más (custom integration, vault dedicado, soporte 24/7), habla con tu account manager.'
            : 'Cada upgrade desbloquea funciones que recuperan más ventas, te dan más control y te ahorran horas a la semana. Aquí ves exactamente qué obtienes.'}
        </p>
        <div style={{ marginTop: 16, display: 'inline-flex', gap: 8, alignItems: 'center', background: G.black, border: `1px solid ${G.border}`, borderRadius: 999, padding: '5px 14px', fontSize: 11, color: G.muted }}>
          Plan actual: <span style={{ color: PLAN_TIERS[plan]?.color || G.gold, fontWeight: 700 }}>{PLAN_TIERS[plan]?.emoji} {PLAN_TIERS[plan]?.name}</span>
        </div>
      </div>

      {/* TABLA COMPARATIVA */}
      <div className="card" style={{ marginBottom: 24, overflow: 'hidden' }}>
        <div className="card-head">
          <span className="card-title">Comparativa completa</span>
          <span style={{ color: G.muted, fontSize: 11 }}>{COMPARISON_ROWS.length} funciones</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: G.black, color: G.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', minWidth: 240 }}>Función</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', color: PLAN_TIERS.bronze.color, minWidth: 90 }}>{PLAN_TIERS.bronze.emoji} Bronce</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', color: PLAN_TIERS.silver.color, minWidth: 90 }}>{PLAN_TIERS.silver.emoji} Plata</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', color: PLAN_TIERS.gold.color, minWidth: 90 }}>{PLAN_TIERS.gold.emoji} Oro</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${G.border}40` }}>
                  <td style={{ padding: '9px 14px', color: G.white }}>{row.label}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'center', color: row.bronze === '—' ? G.muted : G.dim, fontWeight: 600 }}>{row.bronze}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'center', color: row.silver === '—' ? G.muted : silverColor, fontWeight: 600 }}>{row.silver}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'center', color: row.gold === '—' ? G.muted : goldColor, fontWeight: 600 }}>{row.gold}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SILVER FEATURES (visible to bronze, and as recap to silver) */}
      {!isGold && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${silverColor}40` }}>
            <div style={{ fontSize: 28 }}>{PLAN_TIERS.silver.emoji}</div>
            <div>
              <h2 style={{ color: silverColor, margin: 0, fontSize: 20, fontWeight: 700 }}>
                {isBronze ? `Si subes a ${PLAN_TIERS.silver.name} (${fmt(PLAN_TIERS.silver.price, { decimals: 0 })}/mes) desbloqueas:` : `Funciones de ${PLAN_TIERS.silver.name} (las tuyas):`}
              </h2>
              <p style={{ color: G.muted, fontSize: 12, margin: '4px 0 0' }}>{PLAN_TIERS.silver.billing}</p>
            </div>
          </div>
          {UPGRADE_FEATURES_SILVER.map((f, i) => (
            <FeatureBlock key={i} feat={f} tierColor={silverColor} />
          ))}
        </div>
      )}

      {/* GOLD FEATURES */}
      {!isGold && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${goldColor}40` }}>
            <div style={{ fontSize: 28 }}>{PLAN_TIERS.gold.emoji}</div>
            <div>
              <h2 style={{ color: goldColor, margin: 0, fontSize: 20, fontWeight: 700 }}>
                Si subes a {PLAN_TIERS.gold.name} ({fmt(PLAN_TIERS.gold.price, { decimals: 0 })}/mes) desbloqueas {isSilver ? 'además' : 'todo esto'}:
              </h2>
              <p style={{ color: G.muted, fontSize: 12, margin: '4px 0 0' }}>{PLAN_TIERS.gold.billing}</p>
            </div>
          </div>
          {UPGRADE_FEATURES_GOLD.map((f, i) => (
            <FeatureBlock key={i} feat={f} tierColor={goldColor} />
          ))}
        </div>
      )}

      {/* CTA */}
      {!isGold && (
        <div style={{ background: `linear-gradient(135deg, ${(isSilver ? goldColor : silverColor)}25, ${G.black})`, border: `1px solid ${isSilver ? goldColor : silverColor}60`, borderRadius: 16, padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{isSilver ? '🥇' : '🥈'}</div>
          <h2 style={{ color: G.white, fontSize: 22, margin: '0 0 8px', fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>
            ¿Listo para subir de plan?
          </h2>
          <p style={{ color: G.dim, fontSize: 13, margin: '0 0 18px', maxWidth: 540, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Escríbenos por email y te activamos el upgrade en el mismo día. Sin contratos, cambias de plan cuando quieras.
          </p>
          <a
            href={`mailto:saas@zeroriskpay.com?subject=${encodeURIComponent(`Upgrade desde ${PLAN_TIERS[plan]?.name || 'Bronce'}`)}&body=${encodeURIComponent('Hola,\n\nQuiero subir mi plan en ZeroRiskPay. Mi cuenta actual es:\n\n- Email: \n- Plan deseado: \n\nGracias.')}`}
            style={{ display: 'inline-block', padding: '12px 32px', borderRadius: 12, background: `linear-gradient(135deg, ${isSilver ? goldColor : silverColor}, ${(isSilver ? goldColor : silverColor)}dd)`, color: '#000', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
          >
            Solicitar upgrade ahora →
          </a>
        </div>
      )}

      {isGold && (
        <div style={{ background: `linear-gradient(135deg, ${goldColor}20, ${G.black})`, border: `1px solid ${goldColor}60`, borderRadius: 16, padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
          <h2 style={{ color: G.white, fontSize: 22, margin: '0 0 8px', fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>
            Todas las funciones desbloqueadas
          </h2>
          <p style={{ color: G.dim, fontSize: 13, margin: 0, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Tienes el plan máximo. Si necesitas algo a medida (vault dedicado, custom integration, account manager 24/7), habla con tu contacto.
          </p>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// BOT EMAIL PAGE (Oro) — config IMAP/SMTP + plantillas FAQ + logs + stats.
// Plan gated en el sidebar; backend rechaza con 403 si plan < Oro.
// -----------------------------------------------------------------------------

function BotEmailPage({ showToast }) {
  const [tab, setTab] = useState('config');
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/bot-email/configs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => {
        setShops(d.shops || []);
        setConfigs(d.configs || []);
      })
      .catch(() => {});
  }, []);

  const multiShop = shops.length >= 2;
  const tabs = [
    { id: 'config', label: '⚙ Configuración' },
    { id: 'faq', label: '📝 Plantillas FAQ' },
    { id: 'logs', label: '📋 Logs' },
    { id: 'stats', label: '📊 Stats' },
  ];
  return (
    <div className="content">
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ color: G.white, margin: '0 0 6px', fontFamily: 'Cormorant Garamond, serif' }}>🤖 Bot Email</h2>
        <p style={{ color: G.dim, fontSize: 13, margin: 0 }}>
          Tu inbox de soporte respondido por IA. Configura tus credenciales IMAP/SMTP y el bot empezará a procesar emails cada 5 minutos.
          {multiShop && ' Cada tienda puede tener su propio email de soporte.'}
        </p>
      </div>
      {multiShop && tab === 'config' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button
            className={`btn btn-sm ${selectedShopId === null ? 'btn-gold' : 'btn-ghost'}`}
            onClick={() => setSelectedShopId(null)}
            style={{ fontSize: 12 }}
          >General</button>
          {shops.map(s => {
            const hasCfg = configs.some(c => c.shopId === s.id);
            return (
              <button key={s.id}
                className={`btn btn-sm ${selectedShopId === s.id ? 'btn-gold' : 'btn-ghost'}`}
                onClick={() => setSelectedShopId(s.id)}
                style={{ fontSize: 12 }}
              >
                {s.name || s.domain || s.id.slice(0, 8)}
                {hasCfg && <span style={{ color: G.green, marginLeft: 5, fontSize: 10 }}>●</span>}
              </button>
            );
          })}
        </div>
      )}
      <div className="section-tabs" style={{ marginBottom: 16 }}>
        {tabs.map(t => (
          <button key={t.id} className={`section-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>
      {tab === 'config' && <BotEmailConfig key={selectedShopId || '_global'} showToast={showToast} shopId={selectedShopId} shopName={selectedShopId ? (shops.find(s => s.id === selectedShopId) || {}).name : null} />}
      {tab === 'faq' && <BotEmailFaq showToast={showToast} />}
      {tab === 'logs' && <BotEmailLogs />}
      {tab === 'stats' && <BotEmailStats />}
    </div>
  );
}

function BotEmailConfig({ showToast, shopId, shopName }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [imapPass, setImapPass] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [testResult, setTestResult] = useState({});
  const [testSendTo, setTestSendTo] = useState('');
  // 2026-10-14 — simulador de respuesta (no envía nada)
  const [sim, setSim] = useState({ customerEmail: '', subject: '', body: '' });
  const [simOut, setSimOut] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const runSim = async () => {
    setSimBusy(true); setSimOut(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/bot-email/test-reply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...sim, shopId: shopId || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setSimOut(d);
    } catch (e) { showToast(`Error: ${e.message}`, 'error'); }
    setSimBusy(false);
  };

  const qs = shopId ? `?shopId=${encodeURIComponent(shopId)}` : '';

  const load = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/bot-email/config${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setCfg(d.config); setForm(d.config || { imapPort: 993, smtpPort: 587, imapTls: true }); })
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const body = { ...form };
      if (shopId) body.shopId = shopId;
      if (imapPass) body.imapPass = imapPass;
      if (smtpPass) body.smtpPass = smtpPass;
      const res = await fetch(`${BACKEND}/api/bot-email/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      const d = await res.json();
      setCfg(d.config);
      setImapPass(''); setSmtpPass('');
      showToast('Configuración guardada', 'success');
    } catch (e) { showToast(`Error: ${e.message}`, 'error'); }
    setBusy(false);
  };

  const testImap = async () => {
    setBusy(true); setTestResult({});
    try {
      const token = localStorage.getItem('token');
      const body = { ...form };
      if (shopId) body.shopId = shopId;
      if (imapPass) body.imapPass = imapPass;
      const res = await fetch(`${BACKEND}/api/bot-email/test-imap`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      setTestResult({ imap: { ok: !!d.ok, ...d } });
    } catch (e) { setTestResult({ imap: { ok: false, error: e.message } }); }
    setBusy(false);
  };
  const testSmtp = async () => {
    setBusy(true); setTestResult(p => ({ ...p, smtp: null }));
    try {
      const token = localStorage.getItem('token');
      // F44: el destino del test ES OBLIGATORIO para que realmente envíe
      // un email (sin sendTo el backend solo verifica credenciales y no llega
      // nada al inbox → operator cree que no funciona).
      const dest = (testSendTo || form.smtpFromAddress || form.smtpUser || form.imapUser || '').trim();
      if (!dest) {
        setTestResult(p => ({ ...p, smtp: { ok: false, error: 'Pon un email destino en "Enviar prueba a"' } }));
        setBusy(false);
        return;
      }
      const body = { ...form };
      if (shopId) body.shopId = shopId;
      if (smtpPass) body.smtpPass = smtpPass;
      if (imapPass) body.imapPass = imapPass;
      const saveRes = await fetch(`${BACKEND}/api/bot-email/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (saveRes.ok) {
        const sd = await saveRes.json();
        setCfg(sd.config);
      }
      const body2 = { ...form, sendTo: dest };
      if (shopId) body2.shopId = shopId;
      if (smtpPass) body2.smtpPass = smtpPass;
      const res = await fetch(`${BACKEND}/api/bot-email/test-smtp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body2),
      });
      const d = await res.json();
      setTestResult(p => ({ ...p, smtp: { ok: !!d.ok, sentTo: dest, ...d } }));
      if (d.ok) {
        setSmtpPass(''); // la pass ya quedó guardada en el save anterior
      }
    } catch (e) { setTestResult(p => ({ ...p, smtp: { ok: false, error: e.message } })); }
    setBusy(false);
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const runBody = shopId ? JSON.stringify({ shopId }) : undefined;
      const runHeaders = { Authorization: `Bearer ${token}` };
      if (runBody) runHeaders['Content-Type'] = 'application/json';
      const res = await fetch(`${BACKEND}/api/bot-email/run-now`, { method: 'POST', headers: runHeaders, body: runBody });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      showToast(`Ejecutado: ${d.stats?.replied || 0} respondidos, ${d.stats?.escalated || 0} escalados`, 'success');
    } catch (e) { showToast(`Error: ${e.message}`, 'error'); }
    setBusy(false);
  };

  if (loading) return <div className="empty"><div className="empty-icon">◌</div>Cargando…</div>;

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const input = (label, k, type = 'text', extra = {}) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>{label}</label>
      <input type={type} value={form[k] || ''} onChange={e => update(k, type === 'number' ? Number(e.target.value) : e.target.value)} {...extra}
        style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <div>
      {shopName && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, background: G.gold + '12', border: `1px solid ${G.gold}44`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🏪</span>
          <span style={{ fontSize: 13, color: G.white, fontWeight: 600 }}>Configuración para: {shopName}</span>
        </div>
      )}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><span className="card-title">🏷 Marca</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {input('Nombre de tu marca', 'brandName')}
            {input('Dominio público (ej: tutienda.com)', 'brandPublicDomain')}
          </div>
        </div>
      </div>

      {/* 2026-10-14 — Contexto por tienda + reglas propias (pre-orders / reservas) */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><span className="card-title">🧠 Contexto y reglas de respuesta</span></div>
        <div className="card-body">
          {shopId ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.scopeToShop} onChange={e => update('scopeToShop', e.target.checked)} />
              <span style={{ fontSize: 13, color: G.white }}>Responder solo con los pedidos de <strong>esta tienda</strong> (artículos comprados, si es reserva o normal, estado de cada envío)</span>
            </label>
          ) : (
            <div style={{ fontSize: 12, color: G.dim, marginBottom: 12 }}>El contexto por tienda se activa en el bot de cada tienda (selector de arriba).</div>
          )}
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Reglas de esta tienda (plazos, reservas, devoluciones, tono). Sustituyen a las genéricas.</label>
            <textarea value={form.rulesText || ''} onChange={e => update('rulesText', e.target.value)} rows={7} maxLength={4000}
              placeholder={'Ejemplo (en inglés para tiendas UK):\n- Orders dispatch within 24-48h; UK delivery 3-5 working days after dispatch.\n- Pre-orders dispatch from 6 November 2026; no tracking until then.\n- Pre-orders can be cancelled before dispatch by replying to this email; the team confirms.'}
              style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          {input('Enlace de reclamaciones (se añade cuando el cliente se queja: no recibido, cobro doble, reembolso…)', 'claimsUrl', 'text', { placeholder: 'https://tutienda.com/pages/reclamaciones' })}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Idioma por defecto</label>
              <select value={form.defaultLanguage || ''} onChange={e => update('defaultLanguage', e.target.value)}
                style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13 }}>
                <option value="">Detectar por el email del cliente</option>
                <option value="en">Inglés</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><span className="card-title">🧪 Probar respuesta (no envía nada)</span></div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: G.dim, marginBottom: 10 }}>Guarda la configuración antes de probar. Escribe un email como lo haría un cliente y verás qué contestaría el bot y con qué datos.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Email del cliente (para buscar sus pedidos)</label>
              <input value={sim.customerEmail} onChange={e => setSim(p => ({ ...p, customerEmail: e.target.value }))} placeholder="cliente@gmail.com"
                style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Asunto</label>
              <input value={sim.subject} onChange={e => setSim(p => ({ ...p, subject: e.target.value }))} placeholder="Where is my order?"
                style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          <textarea value={sim.body} onChange={e => setSim(p => ({ ...p, body: e.target.value }))} rows={4} placeholder="Hi, I ordered the Astral Lamp two weeks ago and still have no tracking..."
            style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 10 }} />
          <button onClick={runSim} disabled={simBusy || !sim.body} className="btn btn-ghost">{simBusy ? 'Generando…' : '🧪 Ver respuesta'}</button>
          {simOut && (
            <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
              <div style={{ fontSize: 12, color: G.dim }}>Intención: <strong style={{ color: G.white }}>{simOut.intent}</strong> · Idioma: {simOut.language} · Fuente: {simOut.source}{simOut.scopeToShop ? ` · Pedidos en esta tienda: ${(simOut.orders || []).length}` : ''}</div>
              {simOut.facts && <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: G.dim, background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: 10, margin: 0, maxHeight: 220, overflow: 'auto' }}>{simOut.facts}</pre>}
              {simOut.reply ? (
                <div style={{ background: G.black, border: `1px solid ${G.gold}55`, borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: G.gold, fontWeight: 700, marginBottom: 6 }}>{simOut.reply.subject}</div>
                  <div style={{ fontSize: 13, color: G.white, whiteSpace: 'pre-wrap' }}>{simOut.reply.body}</div>
                </div>
              ) : <div style={{ fontSize: 12, color: '#f87171' }}>El bot no generaría respuesta (sin plantilla ni IA).</div>}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><span className="card-title">📥 IMAP (lectura del inbox)</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            {input('Host IMAP (ej: imap.ionos.es)', 'imapHost')}
            {input('Puerto', 'imapPort', 'number')}
          </div>
          {input('Usuario (email completo)', 'imapUser')}
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>
              Contraseña {cfg?.imapPassMasked && <span style={{ color: G.green, marginLeft: 6 }}>● guardada</span>}
            </label>
            <input type="password" value={imapPass} onChange={e => setImapPass(e.target.value)} placeholder={cfg?.imapPassMasked || 'Tu contraseña IMAP'}
              style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>
              Para Gmail/Outlook necesitas <strong>app password</strong>, no tu contraseña real.
            </div>
          </div>
          <button onClick={testImap} disabled={busy} className="btn btn-ghost btn-sm">🧪 Probar conexión IMAP</button>
          {testResult.imap && (
            <div style={{ marginTop: 10, padding: 10, borderRadius: 6, fontSize: 12, background: testResult.imap.ok ? G.green + '15' : G.red + '15', color: testResult.imap.ok ? G.green : G.red }}>
              {testResult.imap.ok ? `✓ Conectado. Inbox tiene ${testResult.imap.unseen} emails no leídos.` : `✕ ${testResult.imap.error}`}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><span className="card-title">📤 SMTP (envío de respuestas)</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            {input('Host SMTP (ej: smtp.ionos.es)', 'smtpHost')}
            {input('Puerto', 'smtpPort', 'number')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {input('Usuario SMTP (vacío = mismo que IMAP)', 'smtpUser')}
            {input('From address (vacío = mismo que IMAP)', 'smtpFromAddress')}
          </div>
          {input('Nombre del remitente (ej: Soporte TuMarca)', 'smtpFromName')}
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>
              Contraseña SMTP {cfg?.smtpPassMasked && <span style={{ color: G.green, marginLeft: 6 }}>● guardada</span>}
            </label>
            <input type="password" value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder={cfg?.smtpPassMasked || 'Vacío = misma que IMAP'}
              style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          {/* F44 (2026-06-11) — Input destino + botón combinado Guardar+Probar.
              Antes: el test solo verificaba credenciales sin enviar email →
              operator no veía nada y creía que SMTP estaba roto. */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>
              Enviar prueba a
            </label>
            <input type="email" value={testSendTo} onChange={e => setTestSendTo(e.target.value)}
              placeholder={form.smtpFromAddress || form.smtpUser || form.imapUser || 'tu-email@ejemplo.com'}
              style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
              Si lo dejas vacío usaremos {form.smtpFromAddress || form.smtpUser || form.imapUser || 'el email de envío'}.
            </div>
          </div>
          <button onClick={testSmtp} disabled={busy} className="btn btn-gold btn-sm">
            {busy ? 'Probando…' : '💾🧪 Guardar y enviar prueba'}
          </button>
          {testResult.smtp && (
            <div style={{ marginTop: 10, padding: 12, borderRadius: 6, fontSize: 12, background: testResult.smtp.ok ? G.green + '15' : G.red + '15', color: testResult.smtp.ok ? G.green : G.red, border: `1px solid ${testResult.smtp.ok ? G.green + '40' : G.red + '40'}` }}>
              {testResult.smtp.ok ? (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>✓ Configuración guardada y email de prueba enviado</div>
                  <div style={{ color: G.white, opacity: 0.85 }}>Destinatario: <code>{testResult.smtp.sentTo}</code></div>
                  <div style={{ color: G.muted, marginTop: 4, fontSize: 11 }}>
                    Si en 1-2 min no lo ves en la bandeja, revisa SPAM. {testResult.smtp.messageId && <>· ID: <code style={{ fontSize: 10 }}>{String(testResult.smtp.messageId).slice(0, 24)}…</code></>}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>✕ No se pudo enviar el email de prueba</div>
                  <div style={{ color: G.white, opacity: 0.85 }}>{testResult.smtp.error}</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16, background: form.enabled ? G.green + '10' : G.card, border: `1px solid ${form.enabled ? G.green + '60' : G.border}` }}>
        <div style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ color: G.white, fontWeight: 700, marginBottom: 4 }}>
              {form.enabled ? '🟢 Bot activado' : '⏸ Bot pausado'}
            </div>
            <div style={{ fontSize: 12, color: G.muted }}>
              {form.enabled ? 'Procesa emails cada 5 minutos.' : 'Activa el bot cuando hayas verificado las conexiones.'}
            </div>
          </div>
          <button onClick={async () => {
            // 2026-08-30 — el toggle DEBE persistir al instante. Antes solo cambiaba
            // el estado local (form.enabled) y había que darle a Guardar después;
            // si el operator salía de la pantalla sin guardar, al volver el bot
            // aparecía desactivado otra vez.
            const nextEnabled = !form.enabled;
            update('enabled', nextEnabled);
            setBusy(true);
            try {
              const token = localStorage.getItem('token');
              const body = { ...form, enabled: nextEnabled };
              if (shopId) body.shopId = shopId;
              if (imapPass) body.imapPass = imapPass;
              if (smtpPass) body.smtpPass = smtpPass;
              const res = await fetch(`${BACKEND}/api/bot-email/config`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
              if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
              const d = await res.json();
              setCfg(d.config);
              showToast(nextEnabled ? 'Bot activado' : 'Bot pausado', 'success');
            } catch (e) {
              update('enabled', !nextEnabled);
              showToast(`Error: ${e.message}`, 'error');
            }
            setBusy(false);
          }} className={`btn ${form.enabled ? 'btn-danger' : 'btn-gold'} btn-sm`}>
            {form.enabled ? 'Pausar bot' : 'Activar bot'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={busy} className="btn btn-gold">{busy ? 'Guardando…' : '💾 Guardar configuración'}</button>
        {cfg?.enabled && <button onClick={runNow} disabled={busy} className="btn btn-ghost">▶ Ejecutar ahora (test manual)</button>}
      </div>
    </div>
  );
}

function BotEmailFaq({ showToast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/bot-email/faq`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(() => setData({ templates: [], defaults: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const [preview, setPreview] = useState(null);

  const upsertTemplate = async (t, isNew) => {
    const subject = window.prompt(`Asunto para "${t.intent}" (${t.language}):`, t.subject || '');
    if (subject === null) return;
    const body = window.prompt(`Cuerpo del email (variables: {{firstName}}, {{trackingCode}}, {{trackingUrl}}, {{brandName}}, {{deliveryDate}}):`, t.bodyText || t.body || '');
    if (body === null) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const url = isNew ? `${BACKEND}/api/bot-email/faq` : `${BACKEND}/api/bot-email/faq/${t.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent: t.intent, language: t.language, subject, bodyText: body, enabled: true }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      showToast('Plantilla guardada', 'success');
      load();
    } catch (e) { showToast(`Error: ${e.message}`, 'error'); }
    setBusy(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('¿Eliminar plantilla? Volverá a usarse el default del bot.')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/bot-email/faq/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      load();
    } catch (e) { showToast(`Error: ${e.message}`, 'error'); }
    setBusy(false);
  };

  if (!data) return <div className="empty"><div className="empty-icon">◌</div>Cargando…</div>;
  const all = [...data.templates.map(t => ({ ...t, isDefault: false })), ...data.defaults];

  return (
    <div>
      <div style={{ marginBottom: 12, padding: 12, background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, fontSize: 12, color: G.dim }}>
        Cada plantilla cubre un <strong>intent</strong> (motivo del email) en un idioma. Si no la personalizas, el bot usa una respuesta por defecto.
        Variables disponibles: <code style={{ color: G.gold }}>{'{{firstName}}'}</code> <code style={{ color: G.gold }}>{'{{trackingCode}}'}</code> <code style={{ color: G.gold }}>{'{{trackingUrl}}'}</code> <code style={{ color: G.gold }}>{'{{brandName}}'}</code> <code style={{ color: G.gold }}>{'{{deliveryDate}}'}</code>
      </div>
      <div className="card">
        <div className="table-wrap"><table>
          <thead><tr><th>Intent</th><th>Idioma</th><th>Asunto</th><th>Origen</th><th></th></tr></thead>
          <tbody>
            {all.map((t, i) => (
              <tr key={t.id || `${t.intent}-${t.language}-${i}`}>
                <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: G.white }}>{t.intent}</td>
                <td>{t.language === 'es' ? '🇪🇸 ES' : '🇬🇧 EN'}</td>
                <td style={{ fontSize: 12, color: G.dim }}>{(t.subject || '').slice(0, 60)}</td>
                <td>{t.isDefault ? <span style={{ color: G.muted, fontSize: 10 }}>DEFAULT</span> : <span style={{ color: G.green, fontSize: 10 }}>● TUYA</span>}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" disabled={busy} title="Ver plantilla"
                    onClick={() => setPreview(t)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => upsertTemplate(t, t.isDefault)}>{t.isDefault ? 'Personalizar' : 'Editar'}</button>
                  {!t.isDefault && <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => deleteTemplate(t.id)}>✕</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
      {preview && (
        <div onClick={() => setPreview(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: G.card, border: `1px solid ${G.border}`, borderRadius: 10,
            maxWidth: 560, width: '100%', maxHeight: '80vh', overflowY: 'auto', padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: G.gold }}>{preview.intent}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: G.dim }}>{preview.language === 'es' ? '🇪🇸 ES' : '🇬🇧 EN'}</span>
                {preview.isDefault
                  ? <span style={{ marginLeft: 8, color: G.muted, fontSize: 10 }}>DEFAULT</span>
                  : <span style={{ marginLeft: 8, color: G.green, fontSize: 10 }}>● TUYA</span>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>ASUNTO</div>
            <div style={{ fontSize: 14, color: G.white, fontWeight: 700, marginBottom: 14 }}>{preview.subject || '—'}</div>
            <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>CUERPO</div>
            <pre style={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
              fontSize: 13, color: G.dim, background: G.bg, border: `1px solid ${G.border}`,
              borderRadius: 8, padding: 14, margin: 0, lineHeight: 1.6,
            }}>{preview.bodyText || preview.body || '—'}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function BotEmailLogs() {
  const [logs, setLogs] = useState(null);
  // 2026-09-03 — click en una fila 'replied' despliega la respuesta exacta que
  // el bot envió a ese cliente (repliedSubject + repliedBody, guardados por el
  // bot desde esta fecha; logs anteriores no tienen el cuerpo).
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/bot-email/logs?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setLogs(d.logs))
      .catch(() => setLogs([]));
  }, []);
  if (!logs) return <div className="empty"><div className="empty-icon">◌</div>Cargando…</div>;
  const color = { replied: G.green, escalated: G.gold, error: G.red, skipped_loop: G.muted, skipped_rate: G.orange, skipped_dedup: G.muted, skipped_no_customer: G.orange, skipped_spam: G.muted, no_template: G.orange };
  return (
    <div className="card">
      {logs.length === 0 ? <div className="empty"><div className="empty-icon">📋</div>Sin logs todavía. El bot necesita estar activado y procesar al menos un email.</div> : (
        <div className="table-wrap"><table>
          <thead><tr><th></th><th>Fecha</th><th>De</th><th>Asunto</th><th>Intent</th><th>Confianza</th><th>Resultado</th></tr></thead>
          <tbody>
            {logs.map(l => (
              <React.Fragment key={l.id}>
                <tr onClick={() => setOpenId(openId === l.id ? null : l.id)} style={{ cursor: 'pointer' }} title="Click para ver la respuesta enviada">
                  <td style={{ fontSize: 10, color: G.muted, width: 18 }}>{openId === l.id ? '▾' : '▸'}</td>
                  <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>{l.processedAt ? new Date(l.processedAt).toLocaleString('es-ES') : '—'}</td>
                  <td style={{ fontSize: 11 }}>{l.fromAddress || '—'}</td>
                  <td style={{ fontSize: 11, color: G.dim }}>{(l.subject || '').slice(0, 40)}</td>
                  <td style={{ fontSize: 11 }}>{l.intent || '—'}</td>
                  <td style={{ fontSize: 11 }}>{l.confidence != null ? `${(l.confidence * 100).toFixed(0)}%` : '—'}</td>
                  <td><span style={{ color: color[l.outcome] || G.muted, fontWeight: 700, fontSize: 11 }}>{l.outcome || '—'}</span></td>
                </tr>
                {openId === l.id && (
                  <tr>
                    <td colSpan={7} style={{ background: G.black, padding: '12px 16px' }}>
                      {l.outcome === 'replied' ? (
                        <div>
                          <div style={{ fontSize: 10, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Respuesta enviada a {l.fromAddress}</div>
                          <div style={{ fontSize: 12, color: G.gold, fontWeight: 700, marginBottom: 8 }}>Asunto: {l.repliedSubject || '—'}</div>
                          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12, lineHeight: 1.6, color: G.white, margin: 0, background: 'transparent' }}>
                            {l.repliedBody || 'El texto de esta respuesta no se guardó (log anterior al 03-09-2026). Las respuestas nuevas sí guardan el texto completo.'}
                          </pre>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: G.muted }}>
                          El bot no envió respuesta a este email (resultado: <span style={{ color: color[l.outcome] || G.muted, fontWeight: 700 }}>{l.outcome}</span>{l.error ? ` — ${l.error}` : ''}).
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function BotEmailStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/bot-email/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setStats)
      .catch(() => setStats({ total: 0, replied: 0, escalated: 0, errors: 0, byIntent: {} }));
  }, []);
  if (!stats) return <div className="empty"><div className="empty-icon">◌</div>Cargando…</div>;
  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {[
          { l: 'Procesados 30d', v: stats.total, c: G.white },
          { l: 'Auto-respondidos', v: stats.replied, c: G.green },
          { l: 'Spam descartados', v: stats.byOutcome?.skipped_spam || 0, c: G.muted },
          { l: 'Errores', v: stats.errors, c: G.red },
        ].map((k, i) => (
          <div key={i} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{ fontSize: 26, color: k.c }}>{k.v}</div></div>
        ))}
      </div>
      <div className="card">
        <div className="card-head"><span className="card-title">Por intent (30 días)</span></div>
        <div className="card-body">
          {Object.keys(stats.byIntent || {}).length === 0 ? <div style={{ color: G.muted }}>Sin datos todavía.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(stats.byIntent).sort(([, a], [, b]) => b - a).map(([intent, n]) => (
                <div key={intent} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ width: 180, color: G.dim, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{intent}</span>
                  <div style={{ flex: 1, background: G.black, borderRadius: 4, height: 14, overflow: 'hidden' }}>
                    <div style={{ width: `${(n / stats.total * 100).toFixed(0)}%`, height: '100%', background: G.gold }} />
                  </div>
                  <span style={{ color: G.white, fontWeight: 600, width: 40, textAlign: 'right' }}>{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PAUSED OVERLAY — mostrado cuando el tenant está suspendido (status=paused).
// Renderiza pantalla completa sin sidebar ni botones — solo el mensaje y
// un mailto a soporte. Cualquier API que el frontend intentara llamar
// devolvería 423 por el middleware auth() (belt + braces).
// -----------------------------------------------------------------------------

function PausedOverlay({ user, onLogout }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: G.black, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 100000 }}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 18, padding: '40px 32px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: `0 20px 60px ${G.black}` }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>⏸</div>
        <h1 style={{ color: G.white, fontSize: 26, margin: '0 0 10px', fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
          Cuenta suspendida
        </h1>
        <p style={{ color: G.dim, fontSize: 14, margin: '0 0 8px', lineHeight: 1.6 }}>
          {user?.name ? `Hola ${user.name},` : 'Hola,'} tu cuenta de ZeroRiskPay está temporalmente pausada.
        </p>
        <p style={{ color: G.muted, fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
          Durante el periodo de suspensión no puedes acceder al panel ni realizar cobros, pero todos tus datos siguen seguros y se reactivarán al regularizar la cuenta.
        </p>
        <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 20, fontSize: 12, color: G.dim, lineHeight: 1.6 }}>
          Para reactivar tu cuenta o resolver cualquier duda, escríbenos a<br />
          <a href={`mailto:saas@zeroriskpay.com?subject=${encodeURIComponent('Reactivar cuenta — ' + (user?.email || ''))}`} style={{ color: G.gold, fontWeight: 600, textDecoration: 'none' }}>
            saas@zeroriskpay.com
          </a>
        </div>
        <button onClick={onLogout} style={{ background: 'transparent', border: `1px solid ${G.border}`, color: G.muted, padding: '9px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EMAIL TEMPLATES
// -----------------------------------------------------------------------------

const TEMPLATE_TYPES = [
  { value: 'abandoned_cart',     label: '🛒 Abandoned Cart',           desc: 'Sent automatically 20 min after a customer abandons checkout.' },
  { value: 'order_confirmation', label: '✅ Order Confirmation',       desc: 'Sent to the customer after a successful payment.' },
  { value: 'preorder_confirmation', label: '⏳ Pre-order Confirmation', desc: 'Sent instead of the normal confirmation when the order contains pre-order items (shops with pre-orders enabled). Variables: {{preorderItemsHtml}} {{regularItemsHtml}} {{releaseDate}} {{deliveryEstimate}}.' },
  { value: 'tracking',           label: '📦 Tracking (Tracking)',  desc: 'Sent when a tracking number is added. Branded as Tracking.' },
  { value: 'club_welcome',       label: '🎉 Club Welcome / Receipt',  desc: 'Sent when the trial ends and subscription is charged. Shows pricing, access credentials, and how to cancel.' },
  { value: 'club_day1',          label: '💎 Club Day 1 — 20% Off',    desc: 'Sent +6h after purchase. Welcome to ZeroRiskPay Club + DISCOUNT20 code.' },
  { value: 'club_day2',          label: '💎 Club Day 2 — 2-for-1',    desc: 'Sent +26h after purchase. 2-for-1 on pieces under $20.' },
  { value: 'club_day3',          label: '💎 Club Day 3 — PDF Guide',  desc: 'Sent +42h after purchase. Luxury houses PDF guide download.' },
  { value: 'custom',             label: '✉ Custom',                    desc: 'Free template, not sent automatically.' },
];

function typeLabel(type) {
  const t = TEMPLATE_TYPES.find(x => x.value === type);
  return t ? t.label : (type || '—');
}

// ── Email Deliverability panel ─────────────────────────────────────────────
// Shows email delivery logs: sent / bounce / invalid. Lets the merchant
// diagnose why transactional emails are failing, retry sends, and run a
// deliverability test to their own address.
// 2026-06-07 — DianxiaomiERP component removed (legacy operator ERP).


function EmailDeliverability({ showToast, isAdmin }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, bounce: 0, invalid: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | sent | bounce | invalid
  const [search, setSearch] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [selected, setSelected] = useState(null);
  // Inline retry form state (only for bounce rows in the detail modal)
  const [retryEmail, setRetryEmail] = useState('');
  const [retrying, setRetrying] = useState(false);
  // Missing confirmations state
  const [missingConf, setMissingConf] = useState(null);
  const [loadingMissing, setLoadingMissing] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  // Email queue state
  const [showQueue, setShowQueue] = useState(false);
  const [emailQueue, setEmailQueue] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('status', filter);
    if (search) params.set('q', search);
    fetch(`${BACKEND}/api/email-logs?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { logs: [], stats: { total: 0, sent: 0, bounce: 0, invalid: 0 } })
      .then(d => {
        setLogs(d.logs || []);
        setStats(d.stats || { total: 0, sent: 0, bounce: 0, invalid: 0 });
      })
      .catch(() => { setLogs([]); })
      .finally(() => setLoading(false));
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  // ── Missing confirmations ──
  const loadMissing = useCallback(() => {
    const token = localStorage.getItem('token');
    setLoadingMissing(true);
    fetch(`${BACKEND}/api/payments/missing-confirmations`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { total: 0, orders: [] })
      .then(d => setMissingConf(d))
      .catch(() => setMissingConf(null))
      .finally(() => setLoadingMissing(false));
  }, []);

  useEffect(() => { loadMissing(); }, [loadMissing]);

  // ── Email queue ──
  const loadQueue = useCallback(() => {
    const token = localStorage.getItem('token');
    setLoadingQueue(true);
    fetch(`${BACKEND}/api/email-queue`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { total: 0, queue: [] })
      .then(d => setEmailQueue(d))
      .catch(() => setEmailQueue(null))
      .finally(() => setLoadingQueue(false));
  }, []);

  const resendAll = async (force = false) => {
    if (!window.confirm(force
      ? 'Reenviar confirmacion a TODOS los pedidos (incluyendo los ya enviados)?'
      : `Enviar confirmacion a ${missingConf?.withEmail || 0} pedidos pendientes?`
    )) return;
    setResending(true);
    setResendResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND}/api/payments/resend-confirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ force }),
      });
      const d = await r.json();
      setResendResult(d);
      if (d.ok) {
        let msg = `✉ ${d.sent} enviados`;
        if (d.failed > 0) msg += ` · ${d.failed} fallidos`;
        if (d.noEmail > 0) msg += ` · ${d.noEmail} sin email`;
        if (d.errors?.length > 0) msg += ` — ${d.errors[0].reason}`;
        showToast?.(msg, d.sent > 0 ? 'success' : (d.failed > 0 ? 'error' : 'warning'));
        loadMissing();
        load();
      } else {
        showToast?.(d.error || 'Error al reenviar', 'error');
      }
    } catch (err) {
      showToast?.(`Error: ${err.message}`, 'error');
    } finally {
      setResending(false);
    }
  };

  const resendOne = async (paymentId) => {
    setResending(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND}/api/payments/resend-confirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paymentIds: [paymentId] }),
      });
      const d = await r.json();
      if (d.ok && d.sent > 0) {
        showToast?.('Email de confirmacion reenviado', 'success');
        loadMissing();
        load();
      } else {
        showToast?.(d.errors?.[0]?.reason || 'No se pudo enviar', 'error');
      }
    } catch (err) {
      showToast?.(`Error: ${err.message}`, 'error');
    } finally {
      setResending(false);
    }
  };

  const runTest = async () => {
    if (!testEmail) { showToast?.('Introduce un email para el test', 'error'); return; }
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND}/api/email-logs/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: testEmail }),
      });
      const data = await r.json();
      if (r.ok) {
        showToast?.(`✓ Email de prueba enviado a ${testEmail}`, 'success');
        load();
      } else {
        showToast?.(`✗ ${data.error}${data.suggestion ? ` (sugerencia: ${data.suggestion})` : ''}`, 'error');
      }
    } catch (err) {
      showToast?.(`Error: ${err.message}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  const deliveryRate = stats.total > 0 ? ((stats.sent / stats.total) * 100).toFixed(1) : '0.0';
  const bounceRate = stats.total > 0 ? ((stats.bounce / stats.total) * 100).toFixed(1) : '0.0';

  const statusColor = (s) => {
    if (s === 'sent') return G.green;
    if (s === 'bounce' || s === 'bounce-limit-reached') return G.red;
    if (s === 'bounce-superseded') return G.muted;
    return G.orange || '#F59E0B';
  };
  const statusIcon  = (s) => {
    if (s === 'sent') return '✓';
    if (s === 'bounce' || s === 'bounce-limit-reached') return '✗';
    if (s === 'bounce-superseded') return '↻';
    return '⚠';
  };
  const statusLabel = (s) => {
    if (s === 'sent') return 'Enviado';
    if (s === 'bounce') return 'Rebotado';
    if (s === 'bounce-limit-reached') return 'Limite rebotes';
    if (s === 'bounce-superseded') return 'Corregido';
    if (s === 'invalid') return 'Invalido';
    return s || '—';
  };

  // Retry a bounced email, optionally with a corrected recipient address.
  const runRetry = async (log) => {
    const token = localStorage.getItem('token');
    setRetrying(true);
    try {
      const payload = {};
      if (retryEmail && retryEmail.trim() && retryEmail.trim() !== log.to) {
        payload.newEmail = retryEmail.trim();
      }
      const r = await fetch(`${BACKEND}/api/email-logs/${log.id}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok && data.success) {
        showToast?.(`✓ Email reenviado a ${data.to || log.to}`, 'success');
        setSelected(null);
        setRetryEmail('');
        load();
      } else {
        showToast?.(`✗ ${data.error || 'Error al reenviar'}${data.suggestion ? ` (sugerencia: ${data.suggestion})` : ''}`, 'error');
      }
    } catch (err) {
      showToast?.(`Error: ${err.message}`, 'error');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div>
      {/* KPI row */}
      <div className="grid-4" style={{ gap: 12, marginBottom: 16 }}>
        <div className="kpi-card" style={{ background: G.card, borderRadius: 12, padding: 16, border: `1px solid ${G.border}` }}>
          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Total emails</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', marginTop: 6 }}>{stats.total}</div>
        </div>
        <div className="kpi-card" style={{ background: G.card, borderRadius: 12, padding: 16, border: `1px solid ${G.green}30` }}>
          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Entregados</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: G.green, marginTop: 6 }}>{stats.sent}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>{deliveryRate}% delivery rate</div>
        </div>
        <div className="kpi-card" style={{ background: G.card, borderRadius: 12, padding: 16, border: `1px solid ${G.red}30` }}>
          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Rebotes</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: G.red, marginTop: 6 }}>{stats.bounce}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>{bounceRate}% bounce rate</div>
        </div>
        <div className="kpi-card" style={{ background: G.card, borderRadius: 12, padding: 16, border: `1px solid ${G.border}` }}>
          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Invalidos</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#F59E0B', marginTop: 6 }}>{stats.invalid}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>Formato o typo</div>
        </div>
      </div>

      {/* ── Email queue button + panel ── */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          className={`btn ${showQueue ? 'btn-gold' : 'btn-ghost'} btn-sm`}
          onClick={() => { const next = !showQueue; setShowQueue(next); if (next && !emailQueue) loadQueue(); }}
          style={{ padding: '8px 18px', fontSize: 12 }}
        >
          {loadingQueue ? '...' : showQueue ? '✕ Cerrar cola' : '📋 Ver cola de envio'}
          {emailQueue && emailQueue.total > 0 && !showQueue && (
            <span style={{ marginLeft: 8, background: `${G.gold}25`, color: G.goldLight, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{emailQueue.total}</span>
          )}
        </button>
        {showQueue && <button className="btn btn-ghost btn-sm" onClick={loadQueue} disabled={loadingQueue} style={{ padding: '6px 12px', fontSize: 11 }}>{loadingQueue ? '...' : '↻ Refrescar'}</button>}
      </div>

      {showQueue && (
        <div className="card" style={{ marginBottom: 16, border: `1px solid ${G.gold}30` }}>
          <div className="card-head" style={{ borderBottom: `1px solid ${G.border}` }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>📋</span>
              Cola de envio
              {emailQueue && (
                <span style={{ background: `${G.gold}20`, color: G.goldLight, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                  {emailQueue.total} en cola
                </span>
              )}
            </span>
            {emailQueue && emailQueue.total > 0 && (
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: G.muted }}>
                <span>Confirmaciones: <strong style={{ color: G.goldLight }}>{emailQueue.confirmations}</strong></span>
                <span>Tracking: <strong style={{ color: G.blue }}>{emailQueue.tracking}</strong></span>
              </div>
            )}
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loadingQueue ? (
              <div style={{ padding: 30, textAlign: 'center', color: G.muted }}>Cargando cola...</div>
            ) : !emailQueue || emailQueue.total === 0 ? (
              <div style={{ padding: 30, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <div style={{ color: G.green, fontSize: 13, fontWeight: 600 }}>No hay emails en cola</div>
                <div style={{ color: G.muted, fontSize: 11, marginTop: 4 }}>Todos los emails se han procesado correctamente.</div>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: `${G.gold}08`, borderBottom: `1px solid ${G.border}` }}>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Tipo</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Destinatario</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Cliente</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Tienda</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Importe</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>En cola desde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailQueue.queue.map((q, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${G.border}30` }}>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                            background: q.type === 'confirmation' ? `${G.goldDim}` : `${G.blueDim}`,
                            color: q.type === 'confirmation' ? G.goldLight : G.blue,
                            border: `1px solid ${q.type === 'confirmation' ? G.gold : G.blue}30`,
                          }}>
                            {q.type === 'confirmation' ? 'Confirmacion' : 'Tracking'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#fff', fontFamily: 'DM Mono, monospace' }}>{q.email}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: G.dim }}>{q.name}</td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: G.muted }}>{q.shop}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
                          {q.currency === 'EUR' ? '\u20ac' : q.currency === 'USD' ? '$' : q.currency}{q.amount}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                          {q.queuedAt ? new Date(q.queuedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: APP_TZ }) : '\u2014'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {emailQueue && emailQueue.total > 0 && (
              <div style={{ padding: '10px 16px', borderTop: `1px solid ${G.border}`, fontSize: 11, color: G.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: G.goldLight }}>⏳</span> Estos emails se estan enviando con intervalos de 12s por el limite de IONOS (5/min). Tiempo estimado: ~{Math.ceil((emailQueue.total * 12) / 60)} min
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Missing order confirmations card ── */}
      {missingConf && missingConf.total > 0 && (
        <div className="card" style={{ marginBottom: 16, border: `1px solid ${G.red}40` }}>
          <div className="card-head" style={{ borderBottom: `1px solid ${G.border}` }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              Confirmaciones de pedido pendientes
              <span style={{ background: `${G.red}20`, color: G.red, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                {missingConf.total}
              </span>
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={loadMissing} disabled={loadingMissing}
                style={{ padding: '5px 12px', fontSize: 11, background: G.bg, color: G.muted, border: `1px solid ${G.border}`, borderRadius: 6, cursor: 'pointer' }}>
                {loadingMissing ? '...' : '↻'}
              </button>
              <button className="btn btn-gold btn-sm" onClick={() => resendAll(false)} disabled={resending || missingConf.withEmail === 0}
                style={{ padding: '5px 14px', fontSize: 11 }}>
                {resending ? 'Enviando...' : `Enviar a ${missingConf.withEmail} con email`}
              </button>
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {/* Stats summary */}
            <div style={{ padding: '12px 16px', display: 'flex', gap: 16, flexWrap: 'wrap', borderBottom: `1px solid ${G.border}`, fontSize: 12 }}>
              <span style={{ color: G.muted }}>Total sin confirmar: <strong style={{ color: '#fff' }}>{missingConf.total}</strong></span>
              <span style={{ color: G.green }}>Con email: <strong>{missingConf.withEmail}</strong></span>
              <span style={{ color: G.red }}>Sin email: <strong>{missingConf.withoutEmail}</strong></span>
              {resendResult && (
                <span style={{ color: G.gold, fontWeight: 700 }}>
                  Ultimo envio: {resendResult.sent} enviados, {resendResult.failed} fallidos
                </span>
              )}
            </div>
            {/* Orders list */}
            {missingConf.orders.length > 0 && (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: G.bg, borderBottom: `1px solid ${G.border}` }}>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Pedido</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Email</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Importe</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Estado</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Fecha</th>
                      <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingConf.orders.slice(0, 50).map(o => (
                      <tr key={o.id} style={{ borderBottom: `1px solid ${G.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#fff', fontFamily: 'DM Mono, monospace' }}>#{o.shortId}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: o.hasEmail ? '#fff' : G.red, fontFamily: 'DM Mono, monospace' }}>
                          {o.hasEmail ? o.email : '— sin email —'}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
                          {/* this cell shows the order's ORIGINAL currency (not our display toggle) since abandoned orders keep their native currency */}
                          {o.currency === 'EUR' ? '€' : o.currency === 'USD' ? '$' : o.currency}{o.amount}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                            background: o.status === 'failed' ? `${G.red}20` : `${G.orange || '#F59E0B'}20`,
                            color: o.status === 'failed' ? G.red : (G.orange || '#F59E0B'),
                          }}>
                            {o.status === 'failed' ? 'Fallido' : o.status === 'pending' ? 'Pendiente' : 'No enviado'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                          {o.createdAt ? new Date(o.createdAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: APP_TZ }) : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          {o.hasEmail && (
                            <button onClick={() => resendOne(o.id)} disabled={resending}
                              style={{
                                padding: '4px 12px', fontSize: 10, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                                background: `${G.gold}15`, color: G.gold, border: `1px solid ${G.gold}40`, fontFamily: 'inherit',
                              }}>
                              Enviar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Resultado del ultimo envio */}
            {resendResult && (
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${G.border}`, background: resendResult.sent > 0 ? `${G.green}08` : `${G.red}08` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: resendResult.sent > 0 ? G.green : G.red, marginBottom: 6 }}>
                  Resultado: {resendResult.sent} enviados, {resendResult.failed} fallidos, {resendResult.noEmail} sin email
                </div>
                {resendResult.errors?.length > 0 && (
                  <div style={{ fontSize: 11, color: G.muted, lineHeight: 1.6 }}>
                    {resendResult.errors.map((e, i) => (
                      <div key={i} style={{ padding: '2px 0' }}>
                        <span style={{ color: G.red }}>✗</span> {e.email || e.orderId}: <code style={{ color: '#F59E0B', fontSize: 10, background: G.bg, padding: '1px 4px', borderRadius: 3 }}>{e.reason}</code>
                      </div>
                    ))}
                  </div>
                )}
                {resendResult.detail?.length > 0 && resendResult.errors?.length === 0 && (
                  <div style={{ fontSize: 11, color: G.muted, lineHeight: 1.6 }}>
                    {resendResult.detail.map((d, i) => (
                      <div key={i} style={{ padding: '2px 0' }}>
                        <span style={{ color: d.status === 'sent' ? G.green : d.status === 'no-email' ? '#F59E0B' : G.red }}>
                          {d.status === 'sent' ? '✓' : d.status === 'no-email' ? '—' : '✗'}
                        </span> {d.email || d.orderId}: {d.status === 'sent' ? 'Enviado' : d.status === 'no-email' ? 'Sin email' : d.reason || 'Error'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test email tool */}
      {isAdmin && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-head">
            <span className="card-title">✉ Test de entrega</span>
            <span style={{ fontSize: 10, color: G.muted }}>Envía un email a tu dirección para verificar SMTP</span>
          </div>
          <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="tu@email.com"
              style={{ flex: 1, minWidth: 220, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
            />
            <button
              className="btn btn-gold"
              onClick={runTest}
              disabled={testing}
              style={{ padding: '10px 20px' }}
            >{testing ? 'Enviando…' : 'Enviar test'}</button>
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: 3 }}>
          {[
            { key: 'all',     label: 'Todos'      },
            { key: 'sent',    label: 'Entregados' },
            { key: 'bounce',  label: 'Rebotes'    },
            { key: 'invalid', label: 'Invalidos'  },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 600,
                background: filter === f.key ? G.gold : 'transparent',
                color: filter === f.key ? '#000' : G.muted,
                border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >{f.label}</button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar email, asunto o error..."
          style={{ flex: 1, minWidth: 220, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '8px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refrescar</button>
      </div>

      {/* Logs table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: G.muted }}>Cargando…</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
              <div style={{ color: G.muted, fontSize: 13 }}>Sin registros de email todavía.</div>
              <div style={{ color: G.dim, fontSize: 11, marginTop: 4 }}>Los envíos transaccionales aparecerán aquí en tiempo real.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: G.bg, borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Estado</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Destinatario</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Asunto</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Tipo</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}>Fecha</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: `1px solid ${G.border}`, cursor: 'pointer' }} onClick={() => { setSelected(log); setRetryEmail(log.to || ''); }}>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', background: `${statusColor(log.status)}15`, border: `1px solid ${statusColor(log.status)}40`, borderRadius: 12, fontSize: 11, fontWeight: 700, color: statusColor(log.status) }}>
                        {statusIcon(log.status)} {statusLabel(log.status)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#fff', fontFamily: 'DM Mono, monospace' }}>{log.to}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: G.muted, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.subject || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: G.dim }}>{log.template || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                      {log.createdAt ? new Date(log.createdAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: APP_TZ }) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: 16, color: G.muted }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <Modal title={`Detalle — ${selected.to}`} onClose={() => { setSelected(null); setRetryEmail(''); }}>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ background: G.card, borderRadius: 10, padding: 14, border: `1px solid ${statusColor(selected.status)}40` }}>
              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Estado</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: statusColor(selected.status) }}>
                {statusIcon(selected.status)} {statusLabel(selected.status)}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
              <div><span style={{ color: G.muted }}>Destinatario:</span> <span style={{ color: '#fff', fontFamily: 'DM Mono, monospace' }}>{selected.to}</span></div>
              <div><span style={{ color: G.muted }}>Asunto:</span> <span style={{ color: '#fff' }}>{selected.subject || '—'}</span></div>
              <div><span style={{ color: G.muted }}>Template:</span> <span style={{ color: '#fff' }}>{selected.template || '—'}</span></div>
              <div><span style={{ color: G.muted }}>Fecha:</span> <span style={{ color: '#fff', fontFamily: 'DM Mono, monospace' }}>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('es-ES', { timeZone: APP_TZ }) : '—'}</span></div>
              {selected.bounceCode && (
                <div><span style={{ color: G.muted }}>Codigo SMTP:</span> <code style={{ color: G.red, background: G.bg, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{selected.bounceCode}</code></div>
              )}
              {selected.retriedAt && (
                <div><span style={{ color: G.muted }}>Reintentado:</span> <span style={{ color: G.green, fontFamily: 'DM Mono, monospace' }}>{new Date(selected.retriedAt).toLocaleString('es-ES', { timeZone: APP_TZ })} → {selected.retriedTo || '—'} ({selected.retryResult || '—'})</span></div>
              )}
            </div>
            {selected.error && (
              <div style={{ background: `${G.red}15`, borderRadius: 10, padding: 14, border: `1px solid ${G.red}40` }}>
                <div style={{ fontSize: 10, color: G.red, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Error</div>
                <div style={{ fontSize: 12, color: '#fff', fontFamily: 'DM Mono, monospace', wordBreak: 'break-word', lineHeight: 1.5 }}>{selected.error}</div>
              </div>
            )}
            {(selected.status === 'bounce' || selected.status === 'bounce-limit-reached') && (
              <div style={{ background: G.card, borderRadius: 10, padding: 14, border: `1px solid ${G.border}` }}>
                <div style={{ fontSize: 11, color: G.goldLight, fontWeight: 700, marginBottom: 6 }}>¿Qué significa este rebote?</div>
                <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.6 }}>
                  {selected.status === 'bounce-limit-reached' ? (
                    <>Se han bloqueado los envíos automáticos a esta dirección después de 3 rebotes consecutivos. Corrige la dirección abajo y reintenta manualmente.</>
                  ) : selected.bounceCode?.startsWith('5') ? (
                    <>Rebote permanente (hard bounce): la dirección no existe o el servidor la rechazó definitivamente. Recomendación: verificar que no sea un typo y corregir la dirección.</>
                  ) : selected.bounceCode?.startsWith('4') ? (
                    <>Rebote temporal (soft bounce): el servidor está ocupado, el buzón lleno, o hay un problema temporal. Puedes reintentar más tarde.</>
                  ) : (
                    <>Error de entrega genérico. Revisa los logs SMTP para más detalles.</>
                  )}
                </div>
              </div>
            )}
            {/* Retry form — only show if we have a retryable payload */}
            {(selected.status === 'bounce' || selected.status === 'bounce-limit-reached') && selected.retryPayload && (
              <div style={{ background: G.card, borderRadius: 10, padding: 14, border: `1px solid ${G.gold}40` }}>
                <div style={{ fontSize: 11, color: G.goldLight, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Reenviar con dirección corregida</div>
                <div style={{ fontSize: 12, color: G.muted, marginBottom: 10, lineHeight: 1.5 }}>
                  Corrige la dirección si detectas un error (ej. <code style={{ color: G.gold }}>gmial.com</code> → <code style={{ color: G.gold }}>gmail.com</code>) y pulsa Reenviar. El email original se mandará a la nueva dirección.
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    value={retryEmail}
                    onChange={e => setRetryEmail(e.target.value)}
                    placeholder={selected.to}
                    style={{ flex: 1, minWidth: 220, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'DM Mono, monospace' }}
                  />
                  <button
                    className="btn btn-gold"
                    onClick={() => runRetry(selected)}
                    disabled={retrying || !retryEmail.trim()}
                    style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}
                  >{retrying ? 'Reenviando…' : '↻ Reenviar'}</button>
                </div>
              </div>
            )}
            {(selected.status === 'bounce' || selected.status === 'bounce-limit-reached') && !selected.retryPayload && (
              <div style={{ background: `${G.muted}15`, borderRadius: 10, padding: 14, border: `1px solid ${G.border}`, fontSize: 12, color: G.muted, lineHeight: 1.5 }}>
                Este rebote no se puede reintentar porque se registró antes del despliegue del sistema de reintentos. Los futuros rebotes podrán reenviarse desde esta ventana.
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function EmailPreviewFrame({ html, subject }) {
  const iframeRef = useRef(null);
  // Sample data to replace template variables
  const sampleVars = {
    name: 'Maria Garcia', customerName: 'Maria Garcia', email: 'maria@ejemplo.com',
    amount: '$49.99', originalAmountStr: '$49.99', finalAmountStr: '$39.99', discountAmountStr: '$10.00',
    shop: 'ZeroRiskPay USA', shopName: 'ZeroRiskPay USA',
    orderId: 'NLP-20240415-A7K', checkoutUrl: '#', discountCode: '20DISCOUNT',
    discountPercent: '20%', trackingNumber: 'YT2409281734521', contactEmail: 'support@zeroriskpay.com',
    currency: 'USD', itemCount: '2',
    // Club-specific vars
    clubName: 'ZeroRiskPay USA CLUB', password: 'Xk9$mP2w', loginUrl: '#',
    logoHtml: '<img src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'40\'%3E%3Crect width=\'120\' height=\'40\' rx=\'8\' fill=\'%231a1a2e\'/%3E%3Ctext x=\'60\' y=\'26\' text-anchor=\'middle\' fill=\'%23C9A84C\' font-family=\'sans-serif\' font-size=\'14\' font-weight=\'700\'%3ESHOP LOGO%3C/text%3E%3C/svg%3E" alt="Shop Logo" style="max-height:40px"/>',
    itemsHtml: '<table width="100%" cellpadding="0" cellspacing="0">'
      + '<tr><td style="padding:10px 0;border-bottom:1px solid #eee;vertical-align:middle;width:60px"><div style="width:60px;height:60px;border-radius:8px;background:#f5f5f0;display:flex;align-items:center;justify-content:center;font-size:24px">👟</div></td>'
      + '<td style="padding:10px 12px;border-bottom:1px solid #eee"><div style="font-size:14px;font-weight:600">Premium Sneakers</div><div style="color:#888;font-size:12px;margin-top:2px">Size: 42 / Color: Black</div><div style="color:#888;font-size:12px">x1</div></td>'
      + '<td style="padding:10px 0;border-bottom:1px solid #eee;text-align:right;font-weight:700;font-size:14px">$34.99</td></tr>'
      + '<tr><td style="padding:10px 0;vertical-align:middle;width:60px"><div style="width:60px;height:60px;border-radius:8px;background:#f5f5f0;display:flex;align-items:center;justify-content:center;font-size:24px">🧢</div></td>'
      + '<td style="padding:10px 12px"><div style="font-size:14px;font-weight:600">Snapback Cap</div><div style="color:#888;font-size:12px;margin-top:2px">One Size</div><div style="color:#888;font-size:12px">x1</div></td>'
      + '<td style="padding:10px 0;text-align:right;font-weight:700;font-size:14px">$15.00</td></tr>'
      + '</table>',
  };

  const renderHtml = (raw) => {
    let rendered = raw || '';
    Object.keys(sampleVars).forEach(k => {
      rendered = rendered.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), sampleVars[k]);
    });
    // Wrap in a beautiful email shell like real email clients show
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body{margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}
      .email-shell{max-width:600px;margin:20px auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08)}
      .email-header{background:#fafafa;padding:12px 20px;border-bottom:1px solid #eee;font-size:13px;color:#666}
      .email-subject{font-weight:700;color:#333;font-size:15px;margin-bottom:2px}
      .email-meta{font-size:11px;color:#999}
      .email-body{padding:0}
    </style></head><body>
      <div class="email-shell">
        <div class="email-header">
          <div class="email-subject">${(subject || 'Sin asunto').replace(/\{\{(\w+)\}\}/g, (m, k) => sampleVars[k] || m)}</div>
          <div class="email-meta">De: Mi Tienda Online &lt;soporte@mitienda.com&gt; &nbsp;|&nbsp; Para: maria@ejemplo.com</div>
        </div>
        <div class="email-body">${rendered}</div>
      </div>
    </body></html>`;
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(renderHtml(html));
    doc.close();
  });

  return <iframe ref={iframeRef} title="Email Preview" style={{ width: '100%', height: 500, border: 'none', borderRadius: 8, background: '#f4f4f5' }} />;
}

function EmailTemplates({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'abandoned_cart', subject: '', html: '', active: true, shopId: '' });
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editTab, setEditTab] = useState('code'); // 'code' | 'preview'
  // 2026-10-14 — plantillas por tienda (selector "Tienda" en el editor)
  const [tplShops, setTplShops] = useState([]);
  useEffect(() => { shopsApi.getAll().then(d => setTplShops(Array.isArray(d) ? d : [])).catch(() => setTplShops([])); }, []);

  const load = useCallback(() => {
    templatesApi.getAll().then(d => setTemplates(Array.isArray(d) ? d : [])).catch(() => setTemplates([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ name: '', type: 'abandoned_cart', subject: '', html: '', active: true, shopId: '' }); setEditTab('code'); setModal(true); };
  const openEdit = (t) => { setEditItem(t); setForm({ shopId: t.shopId || '', name: t.name || '', type: t.type || 'custom', subject: t.subject || '', html: t.html || '', active: t.active !== false }); setEditTab('code'); setModal(true); };
  const save = async () => {
    try {
      if (editItem) {
        const updated = await templatesApi.update(editItem.id, form);
        setTemplates(p => p.map(t => t.id === editItem.id ? updated : t));
        showToast('Plantilla actualizada', 'success');
      } else {
        const created = await templatesApi.create(form);
        setTemplates(p => [...p, created]);
        showToast('Plantilla creada', 'success');
      }
      setModal(false);
    } catch (e) { showToast('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };
  const remove = (id) => { if (!window.confirm('Eliminar esta plantilla?')) return; templatesApi.delete(id).then(() => { setTemplates(p => p.filter(t => t.id !== id)); showToast('Eliminada', 'info'); }); };

  const inputStyle = { width: '100%', padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13, outline: 'none' };
  const selectedTypeInfo = TEMPLATE_TYPES.find(x => x.value === form.type);
  const tabBtn = (tab, label) => ({
    padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', borderRadius: '8px 8px 0 0',
    background: editTab === tab ? G.gold : 'transparent', color: editTab === tab ? '#000' : G.dim,
    transition: 'all .2s',
  });

  return (
    <div className="content">
      <div style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: 13, color: G.dim, lineHeight: 1.6 }}>
        Las plantillas con tipo <strong style={{ color: G.white }}>Carrito Abandonado</strong>, <strong style={{ color: G.white }}>Confirmación de Pedido</strong> o <strong style={{ color: G.white }}>Número de Seguimiento</strong> reemplazan automáticamente el email correspondiente cuando están activas. Usa variables <code style={{ color: '#D4A853' }}>{'{{orderId}}'}</code>, <code style={{ color: '#D4A853' }}>{'{{amount}}'}</code>, <code style={{ color: '#D4A853' }}>{'{{shop}}'}</code>, <code style={{ color: '#D4A853' }}>{'{{checkoutUrl}}'}</code>, <code style={{ color: '#D4A853' }}>{'{{discountCode}}'}</code>, <code style={{ color: '#D4A853' }}>{'{{trackingNumber}}'}</code>.
      </div>

      {/* 2026-06-07 — i18n notice for the auto-language pipeline. */}
      <div style={{ background: 'linear-gradient(135deg, rgba(75,159,232,0.08), rgba(164,92,255,0.08))', border: '1px solid rgba(75,159,232,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: 13, color: G.dim, lineHeight: 1.6 }}>
        <strong style={{ color: G.goldLight, fontSize: 14 }}>🌍 Multi-idioma automático</strong>
        <div style={{ marginTop: 6 }}>
          Las plantillas se envían automáticamente en el idioma del cliente cuando se puede detectar
          (por el header <code style={{ color: '#D4A853' }}>Accept-Language</code> del checkout o por
          el país de la dirección de envío). Si quieres una versión específica para un idioma, crea una
          plantilla del mismo tipo con el sufijo <code style={{ color: '#D4A853' }}>_es</code>,
          {' '}<code style={{ color: '#D4A853' }}>_en</code>, <code style={{ color: '#D4A853' }}>_fr</code>,
          {' '}<code style={{ color: '#D4A853' }}>_de</code>, <code style={{ color: '#D4A853' }}>_it</code>,
          {' '}<code style={{ color: '#D4A853' }}>_pt</code> en el nombre interno
          (ej. <em>welcome_fr</em>). Si no hay plantilla para el idioma detectado, el sistema cae a la
          plantilla por defecto (español o inglés según corresponda).
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => {
          if (!window.confirm('Esto eliminara las plantillas actuales y creara las 4 plantillas por defecto nuevas. Continuar?')) return;
          templatesApi.reseed().then(d => { setTemplates(Array.isArray(d) ? d : []); showToast('Plantillas restauradas', 'success'); }).catch(() => showToast('Error restaurando', 'error'));
        }} style={{ fontSize: 12 }}>Restaurar plantillas por defecto</button>
        <button className="btn btn-gold" onClick={openCreate}>+ Nueva plantilla</button>
      </div>
      {templates.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✉</div>
          <div style={{ color: G.muted, fontSize: 13 }}>No hay plantillas. Cargando…</div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Nombre</th><th>Tipo</th><th>Asunto</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</td>
                    <td style={{ fontSize: 12, color: G.dim }}>{typeLabel(t.type)}</td>
                    <td style={{ fontSize: 12, color: G.dim, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                    <td><StatusBadge status={t.active !== false ? 'ACTIVE' : 'INACTIVE'} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setPreviewTemplate(t)} title="Vista previa" style={{ fontSize: 14, padding: '4px 10px' }}>👁</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => remove(t.id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Preview modal (read-only, visual) ── */}
      {previewTemplate && (
        <div className="modal-bg" onClick={() => setPreviewTemplate(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, width: '90%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ color: G.white, fontWeight: 700, fontSize: 16 }}>{previewTemplate.name}</div>
                <div style={{ color: G.dim, fontSize: 12, marginTop: 2 }}>{typeLabel(previewTemplate.type)} — Vista previa con datos de ejemplo</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setPreviewTemplate(null)} style={{ fontSize: 18 }}>✕</button>
            </div>
            <EmailPreviewFrame html={previewTemplate.html} subject={previewTemplate.subject} />
          </div>
        </div>
      )}

      {/* ── Edit/Create modal with tabs ── */}
      {modal && (
        <div className="modal-bg" onClick={() => setModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, width: '95%', maxWidth: 900, maxHeight: '92vh', overflow: 'auto', padding: 0 }}>
            <div style={{ padding: '18px 24px 0', borderBottom: `1px solid ${G.border}` }}>
              <div style={{ color: G.white, fontWeight: 700, fontSize: 16, marginBottom: 12 }}>{editItem ? `Editar: ${editItem.name}` : 'Nueva plantilla'}</div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={tabBtn('code', 'Codigo')} onClick={() => setEditTab('code')}>Codigo HTML</button>
                <button style={tabBtn('preview', 'Preview')} onClick={() => setEditTab('preview')}>Vista Previa</button>
              </div>
            </div>
            <div style={{ padding: 24 }}>
              {editTab === 'code' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Nombre</label>
                      <input style={inputStyle} placeholder="Cart Recovery" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Tipo</label>
                      <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                        {TEMPLATE_TYPES.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Tienda</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.shopId || ''} onChange={e => setForm({ ...form, shopId: e.target.value })}>
                      <option value="">Todas las tiendas (plantilla general)</option>
                      {tplShops.map(sh => <option key={sh.id} value={sh.id}>{sh.name || sh.url}</option>)}
                    </select>
                    <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Si eliges una tienda, esta plantilla solo se usa para sus pedidos y tiene prioridad sobre la general.</div>
                  </div>
                  {selectedTypeInfo && <div style={{ fontSize: 11, color: G.dim, marginBottom: 12 }}>{selectedTypeInfo.desc}</div>}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Asunto</label>
                    <input style={inputStyle} placeholder="Tu carrito te espera! - {{shop}}" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>HTML del email</label>
                    <textarea style={{ ...inputStyle, minHeight: 260, fontFamily: 'DM Mono, monospace', fontSize: 12, lineHeight: 1.5 }} placeholder="<h1>Hola {{name}}</h1>" value={form.html} onChange={e => setForm({ ...form, html: e.target.value })} />
                    <div style={{ fontSize: 11, color: G.dim, marginTop: 6 }}>Variables: <code style={{ color: G.gold }}>{'{{name}}'}</code> <code style={{ color: G.gold }}>{'{{email}}'}</code> <code style={{ color: G.gold }}>{'{{amount}}'}</code> <code style={{ color: G.gold }}>{'{{shop}}'}</code> <code style={{ color: G.gold }}>{'{{orderId}}'}</code> <code style={{ color: G.gold }}>{'{{checkoutUrl}}'}</code> <code style={{ color: G.gold }}>{'{{discountCode}}'}</code> <code style={{ color: G.gold }}>{'{{trackingNumber}}'}</code> <code style={{ color: G.gold }}>{'{{contactEmail}}'}</code></div>
                  </div>
                  <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
                    <span style={{ color: G.white, fontSize: 13 }}>Activa</span>
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ marginBottom: 12, padding: '10px 14px', background: G.black, borderRadius: 8, border: `1px solid ${G.border}` }}>
                    <div style={{ fontSize: 11, color: G.dim, marginBottom: 4 }}>Asunto</div>
                    <div style={{ color: G.white, fontSize: 14, fontWeight: 600 }}>{form.subject || 'Sin asunto'}</div>
                  </div>
                  <EmailPreviewFrame html={form.html} subject={form.subject} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-gold" style={{ flex: 1 }} onClick={save}>{editItem ? 'Guardar cambios' : 'Crear plantilla'}</button>
                <button className="btn btn-ghost" style={{ flex: 0 }} onClick={() => setModal(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PIXEL SETTINGS (Facebook / Google Ads / TikTok)
// -----------------------------------------------------------------------------

function PixelSettings({ showToast }) {
  const [config, setConfig] = useState({
    facebook: { pixelId: '', pixelIds: [], pixelEntries: [], capiToken: '', enabled: false },
    google: { tagId: '', purchaseLabel: '', checkoutLabel: '', enabled: false },
    tiktok: { pixelId: '', pixelIds: [], pixelEntries: [], accessToken: '', enabled: false },
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [newFbPixel, setNewFbPixel] = useState('');
  const [newFbToken, setNewFbToken] = useState('');
  const [newTtPixel, setNewTtPixel] = useState('');
  const [newTtToken, setNewTtToken] = useState('');

  // Helper: get the effective pixelEntries list, deriving from legacy fields
  // (pixelIds + capiToken) when backend hasn't sent the new shape yet.
  const fbEntries = (() => {
    const fb = config.facebook || {};
    if (Array.isArray(fb.pixelEntries) && fb.pixelEntries.length) return fb.pixelEntries;
    const ids = (fb.pixelIds && fb.pixelIds.length) ? fb.pixelIds : (fb.pixelId ? [fb.pixelId] : []);
    return ids.map(id => ({ id: String(id || ''), capiToken: '' }));
  })();

  // Mutator: update facebook.pixelEntries + derived pixelIds/pixelId in one go.
  const setFbEntries = (next) => {
    const cleaned = next
      .map(e => ({ id: String(e?.id || '').trim(), capiToken: String(e?.capiToken || '').trim() }))
      .filter(e => e.id);
    setConfig(prev => ({
      ...prev,
      facebook: {
        ...prev.facebook,
        pixelEntries: cleaned,
        pixelIds: cleaned.map(e => e.id),
        pixelId: cleaned[0]?.id || '',
      },
    }));
  };

  // TikTok (2026-11-04): varios pixels por tienda, mismo esquema que Facebook
  // (pixelEntries [{id, accessToken}] + token global de respaldo).
  const ttEntries = (() => {
    const tt = config.tiktok || {};
    if (Array.isArray(tt.pixelEntries) && tt.pixelEntries.length) return tt.pixelEntries;
    const ids = (tt.pixelIds && tt.pixelIds.length) ? tt.pixelIds : (tt.pixelId ? [tt.pixelId] : []);
    return ids.map(id => ({ id: String(id || ''), accessToken: '' }));
  })();
  const setTtEntries = (next) => {
    const cleaned = next
      .map(e => ({ id: String(e?.id || '').trim(), accessToken: String(e?.accessToken || '').trim() }))
      .filter(e => e.id);
    setConfig(prev => ({
      ...prev,
      tiktok: { ...prev.tiktok, pixelEntries: cleaned, pixelIds: cleaned.map(e => e.id), pixelId: cleaned[0]?.id || '' },
    }));
  };
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [shopsStatus, setShopsStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [ttTesting, setTtTesting] = useState(''); // id del pixel de TikTok en prueba

  // Prueba UN pixel: su token propio o, si está vacío, el global de la tienda.
  const testTikTok = async (entry) => {
    const pixelId = String(entry?.id || '').trim();
    const accessToken = String(entry?.accessToken || config.tiktok?.accessToken || '').trim();
    if (!pixelId || !accessToken) { showToast('Ese pixel no tiene token (ni propio ni global)', 'error'); return; }
    setTtTesting(pixelId);
    try {
      const r = await pixelsApi.testTikTokLive({ shopId: selectedShopId || undefined, pixelId, accessToken });
      if (r.ok) showToast(`TikTok OK — el pixel ${pixelId} aceptó el evento de prueba`, 'success');
      else showToast('TikTok rechazó el evento: ' + (r.error || r.response || `code ${r.code}`), 'error');
    } catch (e) {
      showToast('Error probando TikTok: ' + (e.response?.data?.error || e.message), 'error');
    }
    setTtTesting('');
  };

  // Load shops on mount
  useEffect(() => {
    shopsApi.getAll().then(s => {
      setShops(s || []);
      // Auto-select first shop if available
      if (s && s.length > 0) {
        setSelectedShopId(s[0].id);
      }
    }).catch(() => {});
    pixelsApi.getShopsStatus().then(setShopsStatus).catch(() => {});
  }, []);

  // Load pixel config when selectedShopId changes
  useEffect(() => {
    if (!selectedShopId && shops.length === 0) {
      // No shops — load user-level config
      pixelsApi.getConfig().then(c => { setConfig(c); setLoaded(true); }).catch(() => setLoaded(true));
    } else if (selectedShopId) {
      setLoaded(false);
      pixelsApi.getConfig(selectedShopId).then(c => { setConfig(c); setLoaded(true); }).catch(() => setLoaded(true));
    }
  }, [selectedShopId, shops.length]);

  const upd = (platform, field, val) => {
    setConfig(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: val } }));
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
  };

  const migrateToShop = async (shopId) => {
    setMigrating(true);
    try {
      const result = await pixelsApi.migrateToShop(shopId);
      if (result.migrated) {
        showToast(`Pixel migrado a ${result.shopName}`, 'success');
        pixelsApi.getShopsStatus().then(setShopsStatus).catch(() => {});
        // Reload config for the shop
        const c = await pixelsApi.getConfig(shopId);
        setConfig(c);
      } else {
        showToast(result.reason || 'No hay config para migrar', 'error');
      }
    } catch (e) {
      showToast('Error migrando: ' + (e.response?.data?.error || e.message), 'error');
    }
    setMigrating(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Clean up empty entries before sending. The backend prefers the new
      // pixelEntries shape; we still send pixelIds for any legacy reader.
      const payload = { ...config };
      if (payload.facebook) {
        const entries = (payload.facebook.pixelEntries || [])
          .map(e => ({ id: String(e?.id || '').trim(), capiToken: String(e?.capiToken || '').trim() }))
          .filter(e => e.id);
        payload.facebook = {
          ...payload.facebook,
          pixelEntries: entries,
          pixelIds: entries.map(e => e.id),
          pixelId: entries[0]?.id || '',
        };
      }
      if (payload.tiktok) {
        const entries = (payload.tiktok.pixelEntries || ttEntries || [])
          .map(e => ({ id: String(e?.id || '').trim(), accessToken: String(e?.accessToken || '').trim() }))
          .filter(e => e.id);
        payload.tiktok = {
          ...payload.tiktok,
          pixelEntries: entries,
          pixelIds: entries.map(e => e.id),
          pixelId: entries[0]?.id || '',
        };
      }
      // Include shopId so backend saves as per-shop config
      if (selectedShopId) payload.shopId = selectedShopId;
      const res = await pixelsApi.saveConfig(payload);
      setConfig(res);
      pixelsApi.getShopsStatus().then(setShopsStatus).catch(() => {});
      showToast('Configuracion de pixeles guardada', 'success');
    } catch (e) {
      showToast('Error al guardar: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSaving(false);
  };

  if (!loaded) return <div style={{ textAlign: 'center', padding: 60, color: G.dim }}>Cargando...</div>;

  const inputStyle = { width: '100%', padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13, outline: 'none' };
  const labelStyle = { display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 };
  const helpStyle = { fontSize: 11, color: G.dim, marginTop: 4 };
  const sectionCard = { background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, marginBottom: 20 };
  const platformHeader = (icon, name, color) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 16, color }}>{name}</span>
    </div>
  );

  const fbEnabled = !!((config.facebook?.pixelIds || []).filter(Boolean).length || config.facebook?.pixelId);
  const gEnabled = !!(config.google?.tagId);
  const ttEnabled = ttEntries.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ color: G.white, margin: 0, fontSize: 20 }}>Pixeles / Tracking</h2>
          <p style={{ color: G.dim, margin: '4px 0 0', fontSize: 13 }}>Configura los pixeles de Facebook, Google Ads y TikTok para rastrear conversiones.</p>
        </div>
        <button className="btn btn-gold" onClick={save} disabled={saving} style={{ minWidth: 140 }}>
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {/* Shop selector */}
      {shops.length > 0 && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ color: G.white, fontWeight: 600, fontSize: 13 }}>Tienda:</label>
            <select
              value={selectedShopId}
              onChange={e => handleShopChange(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13, outline: 'none', cursor: 'pointer' }}
            >
              {shops.map(s => {
                const status = shopsStatus?.shops?.find(ss => ss.shopId === s.id);
                const hasCfg = status?.hasPixelConfig;
                return (
                  <option key={s.id} value={s.id}>
                    {s.name || s.url || s.id} {hasCfg ? '\u2713' : ''}
                  </option>
                );
              })}
            </select>
            <div style={{ fontSize: 11, color: G.muted }}>
              Cada tienda puede tener sus propios pixeles. Selecciona la tienda que quieres configurar.
            </div>
          </div>

          {/* Migration banner — show if there's a legacy config not yet assigned to a shop */}
          {shopsStatus?.hasLegacyConfig && (() => {
            const currentShopHasConfig = shopsStatus?.shops?.find(s => s.shopId === selectedShopId)?.hasPixelConfig;
            if (currentShopHasConfig) return null;
            return (
              <div style={{ marginTop: 12, padding: 12, background: G.surface, borderRadius: 10, border: `1px solid ${G.borderGold}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14 }}>{'\u26A0\uFE0F'}</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ color: G.gold, fontSize: 12, fontWeight: 600 }}>Pixel sin asignar a tienda</div>
                  <div style={{ color: G.dim, fontSize: 11, marginTop: 2 }}>Tienes una configuracion de pixel global. Puedes migrarla a esta tienda.</div>
                </div>
                <button className="btn btn-gold" style={{ fontSize: 11, padding: '6px 14px' }} disabled={migrating}
                  onClick={() => migrateToShop(selectedShopId)}>
                  {migrating ? 'Migrando...' : 'Migrar a esta tienda'}
                </button>
              </div>
            );
          })()}

          {/* Shop pixel status grid */}
          {shopsStatus?.shops?.length > 1 && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {shopsStatus.shops.map(s => (
                <div key={s.shopId} onClick={() => handleShopChange(s.shopId)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 11, cursor: 'pointer',
                    background: s.shopId === selectedShopId ? G.goldDim : G.surface,
                    border: `1px solid ${s.shopId === selectedShopId ? G.gold + '60' : G.border}`,
                    color: s.shopId === selectedShopId ? G.gold : G.dim,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.hasPixelConfig ? G.green : G.red }} />
                  {s.shopName}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Facebook / Meta — Multiple Pixels (plug & play) */}
      <div style={sectionCard}>
        {platformHeader('🔵', 'Facebook / Meta', '#4B9FE8')}

        {/* --- Pixels table: ID + token per row --- */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Pixels de Facebook (ID + token CAPI por pixel)</label>

          {fbEntries.length === 0 ? (
            <div style={{ background: G.black, border: `1px dashed ${G.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', marginBottom: 12 }}>
              <span style={{ color: G.dim, fontSize: 13 }}>No hay pixels configurados. Añade uno abajo.</span>
            </div>
          ) : (
            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1.4fr 32px',
                gap: 8, padding: '8px 12px', background: G.surface,
                fontSize: 10, color: G.muted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                borderBottom: `1px solid ${G.border}`,
              }}>
                <span>Pixel ID</span>
                <span>CAPI Token</span>
                <span></span>
              </div>
              {fbEntries.map((entry, idx) => (
                <div key={idx} style={{
                  display: 'grid', gridTemplateColumns: '1fr 1.4fr 32px',
                  gap: 8, padding: '8px 12px',
                  borderBottom: idx < fbEntries.length - 1 ? `1px solid ${G.border}33` : 'none',
                  alignItems: 'center',
                }}>
                  <input
                    style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13, padding: '8px 10px' }}
                    value={entry.id}
                    placeholder="1234567890123456"
                    onChange={e => {
                      const next = [...fbEntries];
                      next[idx] = { ...next[idx], id: e.target.value.replace(/\s/g, '') };
                      setFbEntries(next);
                    }}
                  />
                  <input
                    style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13, padding: '8px 10px' }}
                    type="text"
                    value={entry.capiToken}
                    placeholder="EAA... (opcional, usa el global si vacío)"
                    onChange={e => {
                      const next = [...fbEntries];
                      next[idx] = { ...next[idx], capiToken: e.target.value.trim() };
                      setFbEntries(next);
                    }}
                  />
                  <button onClick={() => setFbEntries(fbEntries.filter((_, i) => i !== idx))}
                    style={{
                      background: 'transparent', border: 'none', color: G.dim, cursor: 'pointer', fontSize: 16,
                      padding: '4px 6px', borderRadius: 6, lineHeight: 1,
                    }}
                    title="Eliminar pixel"
                    onMouseEnter={e => { e.target.style.color = '#ef4444'; e.target.style.background = '#ef444415'; }}
                    onMouseLeave={e => { e.target.style.color = G.dim; e.target.style.background = 'transparent'; }}
                  >✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Add new row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 8 }}>
            <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13 }}
              placeholder="Pixel ID"
              value={newFbPixel}
              onChange={e => setNewFbPixel(e.target.value.replace(/\s/g, ''))}
            />
            <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13 }}
              type="text"
              placeholder="CAPI Token (opcional)"
              value={newFbToken}
              onChange={e => setNewFbToken(e.target.value.trim())}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('fb-add-pixel-btn')?.click();
                }
              }}
            />
            <button id="fb-add-pixel-btn" onClick={() => {
              const id = newFbPixel.trim();
              if (!id) return;
              if (fbEntries.find(e => e.id === id)) { showToast('Este Pixel ID ya esta añadido', 'error'); return; }
              setFbEntries([...fbEntries, { id, capiToken: newFbToken.trim() }]);
              setNewFbPixel('');
              setNewFbToken('');
            }} style={{
              background: '#4B9FE8', color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              opacity: newFbPixel.trim() ? 1 : 0.4,
            }}>Añadir</button>
          </div>
          <div style={helpStyle}>Cada pixel puede usar su propio token CAPI. Si dejas un token vacío, ese pixel usa el token global de abajo. Facebook Events Manager &rarr; Configuracion &rarr; API de Conversiones &rarr; Generar token de acceso.</div>
        </div>

        {/* Global CAPI Token (fallback) */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Token CAPI global (fallback)</label>
          <input style={inputStyle} type="text" placeholder="EAAxxxxxxx... (opcional)" value={config.facebook?.capiToken || ''}
            onChange={e => upd('facebook', 'capiToken', e.target.value)} />
          <div style={helpStyle}>Se usa para cualquier pixel de la tabla que tenga el campo "CAPI Token" vacío. Si todos tus pixels tienen su propio token, este campo puede quedar vacío.</div>
        </div>
        {fbEnabled && (
          <div style={{ background: G.greenDim, border: `1px solid ${G.green}40`, borderRadius: 8, padding: '12px 16px', color: G.green, fontSize: 13 }}>
            <strong>{fbEntries.length} pixel(es) activo(s)</strong> · {fbEntries.filter(e => e.capiToken).length} con token propio · {fbEntries.filter(e => !e.capiToken).length} usando fallback global. Eventos browser (Pixel JS) + servidor (CAPI) con deduplicacion automatica.
          </div>
        )}
      </div>

      {/* Google Ads */}
      <div style={sectionCard}>
        {platformHeader('🔴', 'Google Ads', '#E84B4B')}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Pixel Principal (Google Ads Tag)</label>
          <input style={inputStyle} placeholder="AW-XXXXXXXXX" value={config.google?.tagId || ''}
            onChange={e => upd('google', 'tagId', e.target.value)} />
          <div style={helpStyle}>ID de Google Ads. Se carga en todas las paginas de la tienda. Formato: AW-XXXXXXXXX</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Conversion Thank You (Compra)</label>
          <input style={inputStyle} placeholder="AW-XXXXXXXXX/XXXXXXXXXXX" value={config.google?.purchaseLabel || ''}
            onChange={e => upd('google', 'purchaseLabel', e.target.value)} />
          <div style={helpStyle}>send_to completo para el evento de compra. Se dispara en la pagina de confirmacion. Formato: AW-XXX/LABEL</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Conversion Checkout (Inicio de compra)</label>
          <input style={inputStyle} placeholder="AW-XXXXXXXXX/XXXXXXXXXXX" value={config.google?.checkoutLabel || ''}
            onChange={e => upd('google', 'checkoutLabel', e.target.value)} />
          <div style={helpStyle}>send_to completo para el evento de inicio de compra. Se dispara al entrar en checkout. Formato: AW-XXX/LABEL</div>
        </div>
      </div>

      {/* TikTok — varios pixels por tienda (2026-11-04), mismo esquema que Facebook */}
      <div style={sectionCard}>
        {platformHeader('🎵', 'TikTok', '#FF004F')}

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Pixels de TikTok (ID + token Events API por pixel)</label>

          {ttEntries.length === 0 ? (
            <div style={{ background: G.black, border: `1px dashed ${G.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', marginBottom: 12 }}>
              <span style={{ color: G.dim, fontSize: 13 }}>No hay pixels de TikTok. Añade uno abajo.</span>
            </div>
          ) : (
            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1.4fr auto 32px',
                gap: 8, padding: '8px 12px', background: G.surface,
                fontSize: 10, color: G.muted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                borderBottom: `1px solid ${G.border}`,
              }}>
                <span>Pixel ID</span>
                <span>Access Token (Events API)</span>
                <span></span>
                <span></span>
              </div>
              {ttEntries.map((entry, idx) => {
                const canTest = !!(entry.accessToken || config.tiktok?.accessToken);
                return (
                  <div key={idx} style={{
                    display: 'grid', gridTemplateColumns: '1fr 1.4fr auto 32px',
                    gap: 8, padding: '8px 12px',
                    borderBottom: idx < ttEntries.length - 1 ? `1px solid ${G.border}33` : 'none',
                    alignItems: 'center',
                  }}>
                    <input
                      style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13, padding: '8px 10px' }}
                      value={entry.id}
                      placeholder="CXXXXXXXXXXXXXXXXX"
                      onChange={e => {
                        const next = [...ttEntries];
                        next[idx] = { ...next[idx], id: e.target.value.replace(/\s/g, '') };
                        setTtEntries(next);
                      }}
                    />
                    <input
                      style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13, padding: '8px 10px' }}
                      type="text"
                      value={entry.accessToken}
                      placeholder="Token (opcional, usa el global si vacío)"
                      onChange={e => {
                        const next = [...ttEntries];
                        next[idx] = { ...next[idx], accessToken: e.target.value.trim() };
                        setTtEntries(next);
                      }}
                    />
                    <button onClick={() => testTikTok(entry)} disabled={!!ttTesting || !canTest} title="Envía un evento de prueba (TEST_ZRP) a este pixel"
                      style={{
                        background: '#FF004F', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px',
                        fontWeight: 700, fontSize: 12, cursor: ttTesting ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                        opacity: (ttTesting || !canTest) ? 0.4 : 1,
                      }}>{ttTesting === entry.id ? 'Probando…' : 'Probar'}</button>
                    <button onClick={() => setTtEntries(ttEntries.filter((_, i) => i !== idx))}
                      style={{
                        background: 'transparent', border: 'none', color: G.dim, cursor: 'pointer', fontSize: 16,
                        padding: '4px 6px', borderRadius: 6, lineHeight: 1,
                      }}
                      title="Eliminar pixel"
                      onMouseEnter={e => { e.target.style.color = '#ef4444'; e.target.style.background = '#ef444415'; }}
                      onMouseLeave={e => { e.target.style.color = G.dim; e.target.style.background = 'transparent'; }}
                    >✕</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add new row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 8 }}>
            <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13 }}
              placeholder="Pixel ID (CXXXXXXXXXXXXXXXXX)"
              value={newTtPixel}
              onChange={e => setNewTtPixel(e.target.value.replace(/\s/g, ''))}
            />
            <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: 13 }}
              type="text"
              placeholder="Access Token (opcional)"
              value={newTtToken}
              onChange={e => setNewTtToken(e.target.value.trim())}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('tt-add-pixel-btn')?.click();
                }
              }}
            />
            <button id="tt-add-pixel-btn" onClick={() => {
              const id = newTtPixel.trim();
              if (!id) return;
              if (ttEntries.find(e => e.id === id)) { showToast('Este Pixel ID de TikTok ya esta añadido', 'error'); return; }
              setTtEntries([...ttEntries, { id, accessToken: newTtToken.trim() }]);
              setNewTtPixel('');
              setNewTtToken('');
            }} style={{
              background: '#FF004F', color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              opacity: newTtPixel.trim() ? 1 : 0.4,
            }}>Añadir</button>
          </div>
          <div style={helpStyle}>Puedes poner un pixel por cuenta publicitaria: todos reciben los mismos eventos (PageView, ViewContent, AddToCart, InitiateCheckout y CompletePayment). Pixel ID: TikTok Ads Manager &rarr; Assets &rarr; Events &rarr; Web Events. El token es opcional y por pixel; si lo dejas vacío usa el global de abajo.</div>
        </div>

        {/* Global Events API token (fallback) */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Access Token global (Events API — fallback)</label>
          <input style={{ ...inputStyle, fontFamily: 'DM Mono, monospace' }} type="text" placeholder="Token largo generado en Events Manager (opcional)"
            value={config.tiktok?.accessToken || ''}
            onChange={e => upd('tiktok', 'accessToken', e.target.value.trim())} />
          <div style={helpStyle}>TikTok Events Manager &rarr; tu pixel &rarr; Settings &rarr; Events API &rarr; Generate Access Token. Se usa para los pixels de la tabla sin token propio. Con token, InitiateCheckout / AddPaymentInfo / CompletePayment / Subscribe se envian tambien desde el servidor (deduplicados con el navegador por event_id). "Probar" envia un evento de prueba (test_event_code TEST_ZRP) que no cuenta como trafico real.</div>
        </div>
        {ttEnabled && (
          <div style={{ background: G.blueDim, border: `1px solid ${G.blue}40`, borderRadius: 8, padding: '12px 16px', color: G.blue, fontSize: 13 }}>
            <strong>{ttEntries.length} pixel(es) de TikTok activo(s)</strong> · {ttEntries.filter(e => e.accessToken || config.tiktok?.accessToken).length} con Events API (server-side) · {ttEntries.filter(e => !(e.accessToken || config.tiktok?.accessToken)).length} solo navegador.
            {' '}Eventos del checkout: PageView, InitiateCheckout y CompletePayment (con valor y moneda) en todos los pixels.
          </div>
        )}
      </div>

      {/* Backfill CAPI */}
      {fbEnabled && config.facebook?.capiToken && (
        <div style={sectionCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>🔄</span>
            <div>
              <div style={{ fontWeight: 700, color: G.white, fontSize: 14 }}>Recuperar conversiones perdidas</div>
              <div style={{ fontSize: 11, color: G.muted }}>Envia a Meta CAPI las compras de los ultimos 7 dias que no se trackearon por el pixel del navegador. Solo compras de checkout (no rebills).</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-sm"
              disabled={backfilling}
              style={{ background: '#4B9FE8', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: backfilling ? 'wait' : 'pointer', opacity: backfilling ? 0.6 : 1 }}
              onClick={async () => {
                setBackfilling(true);
                setBackfillResult(null);
                try {
                  const res = await pixelsApi.backfill({ daysBack: 7, shopId: selectedShopId });
                  setBackfillResult(res);
                  showToast(res.sent > 0 ? `${res.sent} eventos Purchase enviados a Meta` : 'No hay compras pendientes de enviar', res.sent > 0 ? 'success' : 'info');
                } catch (err) {
                  showToast('Error: ' + (err.response?.data?.error || err.message), 'error');
                }
                setBackfilling(false);
              }}
            >
              {backfilling ? 'Enviando...' : 'Enviar ultimos 7 dias'}
            </button>
            {backfillResult && (
              <span style={{ fontSize: 12, color: backfillResult.sent > 0 ? G.green : G.muted }}>
                {backfillResult.sent}/{backfillResult.total} enviados
              </span>
            )}
          </div>
          {backfillResult && backfillResult.results && backfillResult.results.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 200, overflow: 'auto', background: G.surface, borderRadius: 8, border: `1px solid ${G.border}`, padding: 8 }}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: G.muted, borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Monto</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Fecha</th>
                  <th style={{ padding: '4px 8px', textAlign: 'center' }}>Estado</th>
                </tr></thead>
                <tbody>{backfillResult.results.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${G.border}` }}>
                    <td style={{ padding: '4px 8px', color: G.white }}>{r.email || '-'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: G.gold, fontFamily: 'DM Mono, monospace' }}>${(r.amount / 100).toFixed(2)}</td>
                    <td style={{ padding: '4px 8px', color: G.muted }}>{new Date(r.date).toLocaleDateString()}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', color: r.status === 'sent' ? G.green : G.red, fontSize: 10 }}>{r.status === 'sent' ? 'OK' : (r.error || r.status)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Resumen */}
      <div style={{ ...sectionCard, background: G.goldDim, border: `1px solid ${G.borderGold}` }}>
        <div style={{ fontWeight: 700, color: G.gold, marginBottom: 12, fontSize: 14 }}>Resumen de Integraciones</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {[
            { name: 'Facebook Pixel', active: fbEnabled, color: '#4B9FE8' },
            { name: 'Google Ads', active: gEnabled, color: '#E84B4B' },
            { name: 'TikTok Pixel', active: ttEnabled, color: '#FF004F' },
          ].map(p => (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: G.card, borderRadius: 8, border: `1px solid ${G.border}` }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.active ? G.green : G.red }}></span>
              <span style={{ color: G.white, fontSize: 13, fontWeight: 500 }}>{p.name}</span>
              <span style={{ color: p.active ? G.green : G.dim, fontSize: 11, fontWeight: 600 }}>{p.active ? 'Activo' : 'No configurado'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// COPY BUTTON
// -----------------------------------------------------------------------------

// ──────────────────────────────────────────────────────────────────────────
// ClubProductsForm — the 2 products (A $89.97 / B $90.87) of this shop's
// subscription club. Each product has SKU (for DianXiaoMi), name, image URL
// and description. Shown in Shop settings above the contact-email block.
// The stored structure on `shop.clubProducts` is:
//   { productA: { sku, name, price, imageUrl, description },
//     productB: { sku, name, price, imageUrl, description } }
// ──────────────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
// AutoSubscriptionConfig — unified club & recurring billing settings
// ══════════════════════════════════════════════════════════════════════════
// Replaces the old simple "1 price" auto-sub settings and the separate
// per-shop "club products" section. Everything lives here now:
//   • Toggle auto-subscription on/off globally
//   • Billing cadence (days to first rebill, subsequent interval)
//   • Per-shop configuration:
//       - 2 products (A and B), each with image upload, SKU, name,
//         description, price, and charge reason (shown on card statement)
//       - Both products are billed separately at trial end
//       - Charge reasons can be distinct per product for better matching
// ══════════════════════════════════════════════════════════════════════════
function AutoSubscriptionConfig({ settings, save, shops, gateways, fmtSymbol, showToast }) {
  const [expandedShop, setExpandedShop] = useState(null);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [defaultShopId, setDefaultShopId] = useState('');

  const runMigration = async (dryRun) => {
    setMigrating(true);
    try {
      const token = localStorage.getItem('token') || '';
      const params = new URLSearchParams({ dryRun: dryRun ? 'true' : 'false' });
      if (defaultShopId) params.set('defaultShopId', defaultShopId);
      const { data } = await axios.post(
        `${BACKEND}/api/subscriptions/migrate-trials-to-new-model?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (dryRun) {
        setMigrationPreview(data);
        setMigrationResult(null);
      } else {
        setMigrationResult(data);
        setMigrationPreview(null);
        showToast && showToast(`✓ Migrados ${data.migrated} clientes`, 'success');
      }
    } catch (err) {
      showToast && showToast(`✗ ${err?.response?.data?.error || err.message}`, 'error');
    }
    setMigrating(false);
  };
  const asub = settings.autoSubscription || { enabled: false };

  const updateASub = (patch) => save({ ...settings, autoSubscription: { ...asub, ...patch } });

  const updateShopProduct = (shopId, slot, patch) => {
    const allProducts = asub.shopProducts || {};
    const shopProducts = allProducts[shopId] || {
      productA: { name: '', sku: '', price: 89.97, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
      productB: { name: '', sku: '', price: 90.87, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
    };
    updateASub({
      shopProducts: {
        ...allProducts,
        [shopId]: {
          ...shopProducts,
          [slot]: { ...shopProducts[slot], ...patch },
        },
      },
    });
  };

  const uploadImage = (shopId, slot, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Sube una imagen (jpg/png/webp)', 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showToast('Imagen demasiado grande (máx 2MB)', 'error'); return; }
    const reader = new FileReader();
    reader.onload = () => updateShopProduct(shopId, slot, { imageUrl: reader.result });
    reader.onerror = () => showToast('Error al leer la imagen', 'error');
    reader.readAsDataURL(file);
  };

  const getShopProduct = (shopId, slot) => {
    const defaults = {
      productA: { name: '', sku: '', price: 89.97, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
      productB: { name: '', sku: '', price: 90.87, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
    };
    return { ...defaults[slot], ...((asub.shopProducts?.[shopId] || {})[slot] || {}) };
  };

  const ProductEditor = ({ shopId, slot, defaultPrice, labelColor }) => {
    const p = getShopProduct(shopId, slot);
    const inputId = `sub-img-${shopId}-${slot}`;
    return (
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: labelColor, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
            {slot === 'productA' ? 'Producto A' : 'Producto B'}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>
            ${parseFloat(p.price || defaultPrice).toFixed(2)}
          </div>
        </div>

        {/* Image */}
        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Imagen del producto</label>
          <div style={{ position: 'relative', width: '100%', height: 150, background: G.black, borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${G.border}` }}>
            {p.imageUrl ? (
              <>
                <img src={p.imageUrl} alt={p.name || slot} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                <button type="button" onClick={() => updateShopProduct(shopId, slot, { imageUrl: '' })} style={{ position: 'absolute', top: 6, right: 6, background: '#000000cc', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>✕ Quitar</button>
              </>
            ) : (
              <div style={{ color: G.dim, fontSize: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📷</div>
                Sin imagen
              </div>
            )}
          </div>
          <input id={inputId} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => uploadImage(shopId, slot, e.target.files?.[0])} />
          <label htmlFor={inputId} style={{ display: 'block', marginTop: 6, padding: '7px 10px', background: G.gold, color: G.black, borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, textAlign: 'center' }}>
            📤 {p.imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Nombre</label>
            <input type="text" placeholder="Bikini Leopardo"
              value={p.name}
              onChange={e => updateShopProduct(shopId, slot, { name: e.target.value })}
              style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, marginTop: 3 }} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>SKU</label>
            <input type="text" placeholder="BKL-001"
              value={p.sku}
              onChange={e => updateShopProduct(shopId, slot, { sku: e.target.value })}
              style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, fontFamily: 'DM Mono, monospace', marginTop: 3 }} />
          </div>
        </div>

        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Descripción (para email de bienvenida)</label>
          <textarea placeholder="Ej: Serum antiedad con vitamina C pura al 20%"
            value={p.description} rows={2}
            onChange={e => updateShopProduct(shopId, slot, { description: e.target.value })}
            style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 12, marginTop: 3, resize: 'vertical' }} />
        </div>

        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>💳 Motivo del cobro <span style={{ color: G.dim, textTransform: 'none' }}>(lo que ve el cliente en su extracto)</span></label>
          <input type="text" placeholder="Ej: MARTA SERUM VITAMINA C"
            value={p.chargeReason}
            onChange={e => updateShopProduct(shopId, slot, { chargeReason: e.target.value })}
            style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, marginTop: 3 }} />
        </div>

        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Precio mensual ($)</label>
          <input type="number" step="0.01"
            value={p.price || defaultPrice}
            onChange={e => updateShopProduct(shopId, slot, { price: parseFloat(e.target.value) || 0 })}
            style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, fontFamily: 'DM Mono, monospace', marginTop: 3 }} />
        </div>

        {/* Stripe Price ID — F43 (2026-06-11): un priceId por cada gateway Stripe activo.
            Permite conectar 2+ cuentas Stripe distintas, cada una con su propio priceId
            para el mismo producto. El orchestrator usa stripePriceIds[gateway.id] || stripePriceId
            cuando enrola, así que el campo legacy stripePriceId sigue funcionando como fallback. */}
        {(() => {
          const stripeGateways = (gateways || []).filter(g => g && g.active && String(g.psp).toUpperCase() === 'STRIPE');
          const priceIdsMap = p.stripePriceIds || {};
          // Sin ningún gateway Stripe configurado → mostrar input legacy (compat)
          if (stripeGateways.length === 0) {
            return (
              <div>
                <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Stripe Price ID <span style={{ color: G.dim, textTransform: 'none' }}>(añade un gateway Stripe primero para activar este campo)</span>
                </label>
                <input type="text" placeholder="price_1AbCdEf..."
                  value={p.stripePriceId || ''}
                  onChange={e => updateShopProduct(shopId, slot, { stripePriceId: e.target.value.trim() })}
                  style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.dim, fontSize: 12, fontFamily: 'DM Mono, monospace', marginTop: 3 }} />
              </div>
            );
          }
          // 1+ gateways Stripe → un input por cada gateway
          const updatePriceIdForGw = (gwId, value) => {
            const newMap = { ...(p.stripePriceIds || {}) };
            const trimmed = (value || '').trim();
            if (trimmed) newMap[gwId] = trimmed;
            else delete newMap[gwId];
            updateShopProduct(shopId, slot, { stripePriceIds: newMap });
          };
          return (
            <div>
              <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
                Stripe Price ID por cuenta <span style={{ color: G.dim, textTransform: 'none' }}>(opcional · habilita Stripe Subscription nativa)</span>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                {stripeGateways.map(gw => {
                  const val = priceIdsMap[gw.id] || (priceIdsMap[gw.id] === undefined && p.stripePriceId && stripeGateways.length === 1 ? p.stripePriceId : '');
                  const filled = !!val;
                  return (
                    <div key={gw.id} style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontSize: 11, color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={gw.name}>
                        <span style={{ color: filled ? G.green : G.dim, marginRight: 4 }}>{filled ? '●' : '○'}</span>
                        {gw.name || 'Stripe'}
                      </div>
                      <input type="text" placeholder="price_1AbCdEf..."
                        value={val}
                        onChange={e => updatePriceIdForGw(gw.id, e.target.value)}
                        style={{ width: '100%', background: G.bg, border: `1px solid ${filled ? G.green : G.border}`, borderRadius: 6, padding: '7px 10px', color: G.white, fontSize: 12, fontFamily: 'DM Mono, monospace' }} />
                    </div>
                  );
                })}
              </div>
              {Object.keys(priceIdsMap).length > 0 && (
                <div style={{ fontSize: 10, color: G.green, marginTop: 6 }}>
                  ✓ {Object.keys(priceIdsMap).length} cuenta(s) configurada(s). El cobro se enrola en la cuenta Stripe que reciba el primer pago del cliente.
                </div>
              )}
              {p.stripePriceId && !Object.keys(priceIdsMap).length && (
                <div style={{ fontSize: 10, color: G.muted, marginTop: 6 }}>
                  Tienes el legacy <code style={{ background: G.bg, padding: '1px 4px', borderRadius: 3 }}>{p.stripePriceId}</code> guardado. Rellena el campo de la cuenta correcta arriba para migrar.
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  // 2026-06-07 — Per-shop product-count selector.
  // Until today the UI forced every shop to configure BOTH product A and
  // product B even if the merchant only wanted to charge one. The backend
  // (subscriptions.js line 741 and club-welcome-trigger.js line 96) already
  // tolerates A-only or B-only configurations — only the form was rigid.
  // We now read/write asub.shopProducts[shopId].mode ∈ {'single','dual'}.
  // Default = 'dual' for backwards compatibility with operators who already
  // configured both products.
  const getShopMode = (shopId) => {
    const stored = (asub.shopProducts?.[shopId] || {}).mode;
    return stored === 'single' ? 'single' : 'dual';
  };

  const setShopMode = (shopId, mode) => {
    const allProducts = asub.shopProducts || {};
    const shopProducts = allProducts[shopId] || {
      productA: { name: '', sku: '', price: 89.97, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
      productB: { name: '', sku: '', price: 90.87, imageUrl: '', description: '', chargeReason: '', stripePriceId: '', stripePriceIds: {} },
    };
    updateASub({
      shopProducts: {
        ...allProducts,
        [shopId]: { ...shopProducts, mode },
      },
    });
  };

  const ShopCard = ({ shop }) => {
    const isOpen = expandedShop === shop.id;
    const pA = getShopProduct(shop.id, 'productA');
    const pB = getShopProduct(shop.id, 'productB');
    const mode = getShopMode(shop.id);
    const isDual = mode === 'dual';
    const isConfigured = isDual
      ? ((pA.name && pA.price) || (pB.name && pB.price))
      : (pA.name && pA.price);
    const totalConfigured = isDual
      ? (parseFloat(pA.price || 0) + parseFloat(pB.price || 0))
      : parseFloat(pA.price || 0);
    return (
      <div style={{ background: G.card, border: `1px solid ${isConfigured ? G.green + '40' : G.border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
        <div
          onClick={() => setExpandedShop(isOpen ? null : shop.id)}
          style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 18 }}>{isOpen ? '▾' : '▸'}</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.white }}>{shop.name || shop.url || shop.id}</div>
              <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{shop.url || ''}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: G.muted, background: G.bg, padding: '3px 8px', borderRadius: 4, fontWeight: 700, letterSpacing: 0.4 }}>
              {isDual ? '2 PRODUCTOS' : '1 PRODUCTO'}
            </span>
            {isConfigured ? (
              <span style={{ fontSize: 10, color: G.green, background: `${G.green}15`, padding: '3px 8px', borderRadius: 4, fontWeight: 700 }}>
                ✓ Configurado · ${totalConfigured.toFixed(2)}
              </span>
            ) : (
              <span style={{ fontSize: 10, color: G.orange, background: `${G.orange}15`, padding: '3px 8px', borderRadius: 4, fontWeight: 700 }}>
                Sin configurar
              </span>
            )}
          </div>
        </div>
        {isOpen && (
          <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${G.border}` }}>
            {/* Mode picker — 1 vs 2 products. Changing this re-renders the
                editor list below; partial productB data stays in storage so
                switching back to 2-product mode doesn't wipe it. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0 4px' }}>
              <span style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>¿Cuántos productos cobrar?</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShopMode(shop.id, 'single'); }}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                  background: !isDual ? G.gold : G.bg,
                  color: !isDual ? G.black : G.muted,
                  border: `1px solid ${!isDual ? G.gold : G.border}`,
                }}>
                1 producto
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShopMode(shop.id, 'dual'); }}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                  background: isDual ? G.gold : G.bg,
                  color: isDual ? G.black : G.muted,
                  border: `1px solid ${isDual ? G.gold : G.border}`,
                }}>
                2 productos
              </button>
            </div>
            <div style={{ fontSize: 11, color: G.dim, margin: '10px 0 14px', lineHeight: 1.5 }}>
              {isDual
                ? <>Al terminar el trial se ejecutarán <strong>2 cobros separados</strong> por cliente: uno por Producto A y otro por Producto B. Pon un motivo de cobro distinto en cada uno para que el cliente lo identifique claramente en su extracto bancario.</>
                : <>Al terminar el trial se ejecutará <strong>1 solo cobro</strong> por cliente, correspondiente al Producto A. Útil cuando vendes un solo plan o suscripción mensual sin upsell.</>
              }
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isDual ? 'repeat(auto-fit, minmax(300px, 1fr))' : 'minmax(300px, 520px)', gap: 12 }}>
              {ProductEditor({ shopId: shop.id, slot: 'productA', defaultPrice: 89.97, labelColor: G.gold })}
              {isDual && ProductEditor({ shopId: shop.id, slot: 'productB', defaultPrice: 90.87, labelColor: G.gold })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">↺ Suscripción automática · Club del producto</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: asub.enabled ? G.green : G.muted }}>
            {asub.enabled ? '● Activa' : '○ Inactiva'}
          </span>
          <button
            className={`btn ${asub.enabled ? 'btn-danger' : 'btn-gold'} btn-sm`}
            onClick={() => updateASub({ enabled: !asub.enabled })}
          >
            {asub.enabled ? 'Desactivar' : 'Activar'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {/* Intro: how it works */}
        <div style={{ background: `${G.gold}10`, border: `1px solid ${G.gold}30`, borderRadius: 10, padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: G.gold, marginBottom: 6 }}>¿Cómo funciona?</div>
          <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6 }}>
            Cuando un cliente hace checkout, entra automáticamente en un <strong>free trial</strong>. Pasados los días configurados, se ejecuta el cobro recurrente y el cliente recibe el email de bienvenida al Club.
            <br /><br />
            En cada tienda eliges si quieres cobrar <strong>1 solo producto</strong> (un único cargo mensual) o <strong>2 productos separados</strong> (dos cargos distintos con motivos diferentes en el extracto). Puedes cambiar el modo en cualquier momento — los clientes que ya estaban suscritos siguen con su modelo original.
          </div>
        </div>

        {asub.enabled ? (
          <>
            {/* Global cadence settings */}
            <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                Cadencia de cobros (global)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Días de free trial</label>
                  <input type="number" className="form-input"
                    value={asub.delayDays || 5}
                    onChange={e => updateASub({ delayDays: +e.target.value || 5 })} />
                  <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>Días desde checkout hasta primer cobro</div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Intervalo recurrente</label>
                  <select className="form-input"
                    value={asub.interval || 'month'}
                    onChange={e => updateASub({ interval: e.target.value })}>
                    <option value="month">Mensual</option>
                    <option value="year">Anual</option>
                    <option value="week">Semanal</option>
                  </select>
                  <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>Frecuencia de rebills posteriores</div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">🚚 Precio envío ($)</label>
                  <input type="number" step="0.01" className="form-input"
                    value={asub.shippingPrice != null ? asub.shippingPrice : 4.99}
                    onChange={e => updateASub({ shippingPrice: parseFloat(e.target.value) || 0 })} />
                  <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>Se cobra cuando el producto es gratis ($0)</div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Email de bienvenida</label>
                  <div style={{
                    padding: '8px 12px', background: G.bg, border: `1px solid ${G.green}40`,
                    borderRadius: 6, color: G.green, fontSize: 12, fontWeight: 600,
                  }}>
                    ✓ Auto tras primer cobro
                  </div>
                  <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>Edítalo en Plantillas → Club Welcome</div>
                </div>
              </div>

              {/* DXM shipping toggle — controls whether sub rebills become
                  fulfillment orders downstream. Default = true (ship).
                  When off, sub charges are pre-stamped dxmHidden=true so
                  the chino never sees them. Fresh checkouts are unaffected. */}
              <div style={{
                marginTop: 16, padding: 14,
                background: (asub.shipSubsToDxm !== false) ? `${G.green}08` : `${G.red}08`,
                border: `1px solid ${(asub.shipSubsToDxm !== false) ? G.green : G.red}40`,
                borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ color: G.white, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                    📦 Enviar productos de suscripciones a DianXiaoMi
                  </div>
                  <div style={{ color: G.dim, fontSize: 11, lineHeight: 1.5 }}>
                    {(asub.shipSubsToDxm !== false)
                      ? 'ACTIVADO — cada rebill exitoso genera un pedido en DianXiaoMi (el chino lo envía como cualquier compra).'
                      : 'DESACTIVADO — los rebills se cobran pero NO aparecen en DianXiaoMi (el chino no los envía). Los checkouts nuevos siguen entrando normal.'}
                  </div>
                </div>
                <button
                  onClick={() => updateASub({ shipSubsToDxm: !(asub.shipSubsToDxm !== false) })}
                  style={{
                    padding: '8px 16px', minWidth: 120,
                    background: (asub.shipSubsToDxm !== false) ? G.green : G.red,
                    color: '#000', fontWeight: 700, fontSize: 12,
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {(asub.shipSubsToDxm !== false) ? '● Activado' : '○ Desactivado'}
                </button>
              </div>
            </div>

            {/* Per-shop products */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                🛍️ Productos del club por tienda ({shops.length})
              </div>
              {shops.length === 0 ? (
                <div className="empty" style={{ paddingTop: 8 }}>
                  <div className="empty-icon" style={{ fontSize: 24 }}>🏪</div>
                  <div style={{ fontSize: 13, color: G.muted, textAlign: 'center' }}>
                    Crea una tienda primero para configurar sus productos del club.
                  </div>
                </div>
              ) : (
                shops.map(s => <React.Fragment key={s.id}>{ShopCard({ shop: s })}</React.Fragment>)
              )}
            </div>

            {/* ── MIGRATION TOOL ── */}
            <div style={{ background: `${G.gold}08`, border: `1px dashed ${G.gold}60`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                🔄 Migrar clientes TRIAL existentes
              </div>
              <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6, marginBottom: 14 }}>
                Los clientes que entraron en TRIAL <strong>antes</strong> de configurar los productos A+B actuales siguen con su suscripción antigua (1 solo cobro). Esto los migra al modelo nuevo: reetiqueta su suscripción como <strong>Producto A</strong> y crea un gemelo de <strong>Producto B</strong>, ambos con el mismo vault y fecha de cobro. Cuando termine su trial se cobrarán los 2 productos automáticamente.
              </div>

              {/* Fallback shop picker — assigns this shop to every RC we can't auto-resolve */}
              <div style={{ marginBottom: 12, padding: 10, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8 }}>
                <label style={{ display: 'block', fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                  Tienda por defecto (para los "sin tienda")
                </label>
                <select
                  value={defaultShopId}
                  onChange={e => { setDefaultShopId(e.target.value); setMigrationPreview(null); setMigrationResult(null); }}
                  className="form-input"
                  style={{ maxWidth: 400 }}
                >
                  <option value="">— Auto-detectar (recomendado) —</option>
                  {shops.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.url || s.id} {settings?.autoSubscription?.shopProducts?.[s.id]?.productA?.price && settings?.autoSubscription?.shopProducts?.[s.id]?.productB?.price ? '✓' : '⚠ sin A+B'}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 6, lineHeight: 1.5 }}>
                  Si auto-detección falla (cliente antiguo sin shopId en metadata, sin pendingOrder, etc.), usará esta tienda. Solo válidas las tiendas con A+B configurados.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={migrating}
                  onClick={() => runMigration(true)}
                  style={{ borderColor: G.gold, color: G.gold }}
                >
                  {migrating ? '⏳ Calculando...' : '👁️ Ver preview (no modifica nada)'}
                </button>
                {migrationPreview && migrationPreview.migrated > 0 && (
                  <button
                    className="btn btn-gold btn-sm"
                    disabled={migrating}
                    onClick={() => {
                      if (window.confirm(`Vas a migrar ${migrationPreview.migrated} clientes. Esto reetiqueta sus suscripciones como Producto A y crea un Producto B gemelo para cada uno. ¿Continuar?`)) {
                        runMigration(false);
                      }
                    }}
                  >
                    ✅ Migrar {migrationPreview.migrated} clientes de verdad
                  </button>
                )}
              </div>

              {/* Preview result */}
              {migrationPreview && (
                <div style={{ marginTop: 14, padding: 12, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                    Preview (dry-run) — no se ha modificado nada
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, fontSize: 12 }}>
                    <div><div style={{ color: G.muted, fontSize: 10 }}>Total en TRIAL</div><div style={{ color: G.white, fontWeight: 700, fontSize: 18 }}>{migrationPreview.totalTrials}</div></div>
                    <div><div style={{ color: G.green, fontSize: 10 }}>✓ Ya migrados</div><div style={{ color: G.green, fontWeight: 700, fontSize: 18 }}>{migrationPreview.alreadyMigrated}</div></div>
                    <div><div style={{ color: G.gold, fontSize: 10 }}>🔄 Se migrarán</div><div style={{ color: G.gold, fontWeight: 700, fontSize: 18 }}>{migrationPreview.migrated}</div></div>
                    <div><div style={{ color: G.muted, fontSize: 10 }}>⚠ Sin tienda</div><div style={{ color: G.orange, fontWeight: 700, fontSize: 18 }}>{migrationPreview.skippedNoShop}</div></div>
                    <div><div style={{ color: G.muted, fontSize: 10 }}>⚠ Sin productos config</div><div style={{ color: G.orange, fontWeight: 700, fontSize: 18 }}>{migrationPreview.skippedShopNotConfigured}</div></div>
                  </div>
                  {/* Resolution breakdown — shows how each RC's shop was identified */}
                  {migrationPreview.resolvedVia && (
                    <div style={{ marginTop: 10, padding: 10, background: `${G.gold}08`, borderRadius: 6, fontSize: 11, color: G.dim }}>
                      <div style={{ fontWeight: 700, color: G.gold, marginBottom: 4 }}>¿Cómo se identificó la tienda de cada cliente?</div>
                      {migrationPreview.resolvedVia.metadata > 0 && <div>• metadata.shopId (modernos): <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.metadata}</strong></div>}
                      {migrationPreview.resolvedVia.originalPayment > 0 && <div>• Pago original: <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.originalPayment}</strong></div>}
                      {migrationPreview.resolvedVia.pendingOrderByEmail > 0 && <div>• Cart abandono por email: <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.pendingOrderByEmail}</strong></div>}
                      {migrationPreview.resolvedVia.paymentByEmail > 0 && <div>• Pago por email: <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.paymentByEmail}</strong></div>}
                      {migrationPreview.resolvedVia.singleConfiguredShop > 0 && <div>• Única tienda configurada del user: <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.singleConfiguredShop}</strong></div>}
                      {migrationPreview.resolvedVia.defaultShopIdQuery > 0 && <div>• Tienda por defecto (tu elección): <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.defaultShopIdQuery}</strong></div>}
                      {migrationPreview.resolvedVia.productsFromFallbackShop > 0 && (
                        <div style={{ color: G.gold }}>• Tienda identificada pero sin A+B → <strong>usando productos de la tienda por defecto:</strong> <strong style={{ color: G.white }}>{migrationPreview.resolvedVia.productsFromFallbackShop}</strong></div>
                      )}
                      {migrationPreview.singleConfiguredShop && (
                        <div style={{ marginTop: 4, color: G.green }}>✓ Tienes UNA sola tienda con A+B configurados: {migrationPreview.singleConfiguredShop.name} — se aplica a los que no se resuelven de otra forma.</div>
                      )}
                    </div>
                  )}

                  {/* Unconfigured shops warning */}
                  {migrationPreview.unconfiguredShops && Object.keys(migrationPreview.unconfiguredShops).length > 0 && (
                    <div style={{ marginTop: 10, padding: 10, background: `${G.orange}12`, border: `1px solid ${G.orange}40`, borderRadius: 6, fontSize: 11 }}>
                      <div style={{ fontWeight: 700, color: G.orange, marginBottom: 4 }}>
                        ⚠ Tiendas sin Producto A + B configurados:
                      </div>
                      {Object.values(migrationPreview.unconfiguredShops).map(us => (
                        <div key={us.id} style={{ color: G.dim, marginLeft: 8 }}>
                          • <strong style={{ color: G.white }}>{us.name}</strong> — {us.count} clientes afectados
                        </div>
                      ))}
                      <div style={{ marginTop: 6, color: G.dim, lineHeight: 1.5 }}>
                        Opciones: (1) configura A+B para estas tiendas arriba ↑, o (2) elige una "Tienda por defecto" para que use sus productos en vez, o (3) déjalos sin migrar si son de otro negocio.
                      </div>
                    </div>
                  )}
                  {migrationPreview.details && migrationPreview.details.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 11, color: G.muted }}>Ver detalle ({migrationPreview.details.length} registros)</summary>
                      <div style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto', fontSize: 11, fontFamily: 'DM Mono, monospace', color: G.dim }}>
                        {migrationPreview.details.map((d, i) => (
                          <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${G.border}` }}>
                            <div style={{ color: G.white }}>{d.email || d.rcId}</div>
                            {d.reason && <div style={{ color: G.orange }}>↳ {d.reason}</div>}
                            {d.after && <div style={{ color: G.green }}>↳ A: ${(d.after.productA.amount / 100).toFixed(2)} · B: ${(d.after.productB.amount / 100).toFixed(2)}</div>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Real migration result */}
              {migrationResult && (
                <div style={{ marginTop: 14, padding: 12, background: `${G.green}10`, border: `1px solid ${G.green}40`, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: G.green, marginBottom: 6 }}>
                    ✅ Migración completada
                  </div>
                  <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.7 }}>
                    {migrationResult.migrated} clientes migrados · {migrationResult.createdCompanionRCs} suscripciones Producto B creadas · {migrationResult.alreadyMigrated} ya estaban migrados · {migrationResult.skippedNoShop + migrationResult.skippedShopNotConfigured} omitidos (sin tienda o sin productos configurados).
                  </div>
                </div>
              )}
            </div>

            {/* PSP badges */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Pasarelas compatibles
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {[
                  { psp: 'Stripe', logo: '/logos/stripe.png', c: '#635BFF', s: '100% automático' },
                  { psp: 'Square', logo: '/logos/square.png', c: '#00B86B', s: 'Card Vault' },
                  { psp: 'TailoredPay', logo: '/logos/tailored.png', c: '#FF6B35', s: 'Vault NMI' },
                  { psp: 'PayPal', logo: '/logos/paypal.jpg', c: '#003087', s: 'Billing Agreement' },
                  { psp: 'Braintree', logo: '/logos/braintree.png', c: '#4B45B2', s: 'Vault + Sub' },
                ].map((g, i) => (
                  <div key={i} style={{ background: `${g.c}10`, border: `1px solid ${g.c}30`, borderRadius: 8, padding: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <img src={g.logo} alt={g.psp} style={{ width: 22, height: 14, objectFit: 'contain', background: '#fff', borderRadius: 3, padding: '1px 3px' }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: g.c }}>{g.psp}</span>
                    </div>
                    <div style={{ fontSize: 10, color: G.dim }}>{g.s}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="empty" style={{ paddingTop: 8 }}>
            <div className="empty-icon" style={{ fontSize: 24 }}>↺</div>
            <div style={{ fontSize: 13, color: G.muted, textAlign: 'center' }}>
              Activa para cobrar automáticamente tras cada trial.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClubProductsForm({ shop, setSel, setShops, showToast }) {
  const defaults = {
    productA: { sku: '', name: '', price: 89.97, imageUrl: '', description: '' },
    productB: { sku: '', name: '', price: 90.87, imageUrl: '', description: '' },
  };
  const [cp, setCp] = useState({
    productA: { ...defaults.productA, ...(shop.clubProducts?.productA || {}) },
    productB: { ...defaults.productB, ...(shop.clubProducts?.productB || {}) },
  });
  const [saving, setSaving] = useState(false);

  // Re-sync when shop selection changes — use a ref to read defaults so
  // we don't need them as a dependency (they're local constants each render).
  const shopIdRef = useRef(shop.id);
  useEffect(() => {
    if (shopIdRef.current === shop.id) return;
    shopIdRef.current = shop.id;
    setCp({
      productA: { sku: '', name: '', price: 89.97, imageUrl: '', description: '', ...(shop.clubProducts?.productA || {}) },
      productB: { sku: '', name: '', price: 90.87, imageUrl: '', description: '', ...(shop.clubProducts?.productB || {}) },
    });
  }, [shop.id, shop.clubProducts]);

  const updateField = (slot, field, value) => {
    setCp(prev => ({ ...prev, [slot]: { ...prev[slot], [field]: value } }));
  };

  const save = () => {
    setSaving(true);
    const payload = {
      clubProducts: {
        productA: { ...cp.productA, price: parseFloat(cp.productA.price) || 89.97 },
        productB: { ...cp.productB, price: parseFloat(cp.productB.price) || 90.87 },
      },
    };
    shopsApi.update(shop.id, payload).then(() => {
      setSel(p => ({ ...p, clubProducts: payload.clubProducts }));
      setShops(p => p.map(x => x.id === shop.id ? { ...x, clubProducts: payload.clubProducts } : x));
      showToast('Productos del club guardados', 'success');
    }).catch(e => showToast('Error: ' + e.message, 'error')).finally(() => setSaving(false));
  };

  const handleFileUpload = (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Por favor sube una imagen (jpg, png, webp)', 'error');
      return;
    }
    // Max 2MB (base64-encoded images get embedded in email)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Imagen demasiado grande (máx 2MB)', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateField(slot, 'imageUrl', reader.result);
    reader.onerror = () => showToast('Error al leer la imagen', 'error');
    reader.readAsDataURL(file);
  };

  const ProductCard = ({ slot, label }) => {
    const p = cp[slot];
    const fileInputId = `club-img-${slot}`;
    return (
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: G.gold, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>{label}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>${p.price.toFixed(2)}</div>
        </div>

        {/* Image preview + upload */}
        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Imagen del producto</label>
          <div style={{ position: 'relative', width: '100%', height: 160, background: G.black, borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${G.border}` }}>
            {p.imageUrl ? (
              <>
                <img src={p.imageUrl} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                <button
                  type="button"
                  onClick={() => updateField(slot, 'imageUrl', '')}
                  style={{ position: 'absolute', top: 6, right: 6, background: '#000000cc', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
                >✕ Quitar</button>
              </>
            ) : (
              <div style={{ color: G.dim, fontSize: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📷</div>
                Sin imagen — haz click abajo para subirla
              </div>
            )}
          </div>
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => handleFileUpload(slot, e.target.files?.[0])}
          />
          <label
            htmlFor={fileInputId}
            style={{
              display: 'block', marginTop: 6, padding: '8px 10px',
              background: G.gold, color: G.black, borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, textAlign: 'center', letterSpacing: 0.5,
            }}
          >📤 {p.imageUrl ? 'Cambiar imagen' : 'Subir imagen'}</label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Nombre</label>
            <input
              type="text" placeholder="ej: Bikini Leopardo"
              value={p.name}
              onChange={e => updateField(slot, 'name', e.target.value)}
              style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, marginTop: 3 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>SKU</label>
            <input
              type="text" placeholder="ej: BKL-001"
              value={p.sku}
              onChange={e => updateField(slot, 'sku', e.target.value)}
              style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, fontFamily: 'DM Mono, monospace', marginTop: 3 }}
            />
          </div>
        </div>

        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Descripción</label>
          <textarea
            placeholder="Breve descripción que aparecerá en el email de bienvenida"
            value={p.description}
            onChange={e => updateField(slot, 'description', e.target.value)}
            rows={2}
            style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 12, marginTop: 3, resize: 'vertical' }}
          />
        </div>

        <div>
          <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Precio mensual ($)</label>
          <input
            type="number" step="0.01"
            value={p.price}
            onChange={e => updateField(slot, 'price', e.target.value)}
            style={{ width: '100%', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 10px', color: G.white, fontSize: 13, fontFamily: 'DM Mono, monospace', marginTop: 3 }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            🛍️ Productos del club (subscripciones día 5)
          </div>
          <div style={{ fontSize: 11, color: G.dim, marginTop: 3 }}>
            Después del trial de 5 días se crean 2 subscripciones separadas. Estos productos se muestran al chino (DianXiaoMi) y al cliente (email de bienvenida).
          </div>
        </div>
        <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} style={{ padding: '9px 18px' }}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        <ProductCard slot="productA" label="Producto A · $89.97/mes" />
        <ProductCard slot="productB" label="Producto B · $90.87/mes" />
      </div>
    </div>
  );
}

function CopyButton({ text, label = 'Copiar' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, minWidth: 80 }}
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
      {copied ? 'OK Copiado' : label}
    </button>
  );
}

// -----------------------------------------------------------------------------
// GET STARTED WIZARD
// -----------------------------------------------------------------------------

function GetStarted({ onComplete, onClose }) {
  const [step, setStep] = useState(0);
  const steps = [
    { icon: '⬡', title: 'Conecta tu pasarela', section: 'gateways',
      desc: 'Ve a Pasarelas -> + Añadir -> introduce tu Secret Key de Stripe o Square. El sistema la valida y crea el webhook automáticamente.',
      tips: ['Stripe: dashboard.stripe.com -> Developers -> API Keys -> Secret key (sk_live_...)', 'Square: developer.squareup.com -> My Apps -> Credentials -> Production Access Token'] },
    { icon: '👥', title: 'Crea un cliente', section: 'clients',
      desc: 'Ve a Clientes -> + Nuevo cliente -> introduce sus datos y asígnale un plan según su facturación mensual.',
      tips: ['🥉 Bronce: hasta €20.000/mes -- €499/mes', '🥈 Plata: hasta €100.000/mes -- €1.499/mes', '🥇 Oro: más de €100.000/mes -- €2.499/mes'] },
    { icon: '◫', title: 'Conecta Shopify', section: 'shops',
      desc: 'Ve a Tiendas -> + Nueva tienda -> añade la URL. El sistema genera un script de 1 línea para instalar en 2 minutos.',
      tips: ['Shopify -> Online Store -> Themes -> Edit code -> theme.liquid', 'Pega el script antes de </body> -> Guardar -- listo OK'] },
    { icon: '⚙', title: 'Configura opciones', section: 'settings',
      desc: 'Activa la IA routing, el detector de fraude, Payment Links y la reconciliación automática.',
      tips: ['🤖 IA Routing: aprende y optimiza qué pasarela convierte mejor', '🛡 Fraude: bloquea IPs y emails sospechosos automáticamente'] },
  ];
  const s = steps[step];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#00000095', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(8px)', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.borderGold}`, borderRadius: 20, padding: '36px 40px', width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 32px 100px #00000090', position: 'relative' }}>
        {/* X close button - always visible, especially on mobile */}
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', color: G.muted, fontSize: 22, cursor: 'pointer', zIndex: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.color = G.white} onMouseOut={e => e.currentTarget.style.color = G.muted}>✕</button>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 42, marginBottom: 10 }}>👑</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, color: G.goldLight, marginBottom: 6 }}>Bienvenido a ZeroRiskPay</div>
          <div style={{ fontSize: 13, color: G.muted }}>4 pasos para dejarlo todo funcionando</div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          {steps.map((_, i) => <div key={i} onClick={() => setStep(i)} style={{ flex: 1, height: 4, borderRadius: 2, background: i < step ? G.green : i === step ? G.gold : G.border, cursor: 'pointer', transition: 'all 0.3s' }} />)}
        </div>
        <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ fontSize: 24, marginBottom: 10 }}>{s.icon}</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: G.goldLight, marginBottom: 10 }}>Paso {step + 1}: {s.title}</div>
          <div style={{ fontSize: 13, color: G.white, lineHeight: 1.7, marginBottom: 14 }}>{s.desc}</div>
          {s.tips.map((t, i) => <div key={i} style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', lineHeight: 1.8 }}>{t}</div>)}
          <button className="btn btn-gold" style={{ marginTop: 16, width: '100%' }} onClick={() => onComplete(s.section)}>Ir a {s.title} -></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {steps.map((st, i) => (
            <div key={i} onClick={() => setStep(i)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: i === step ? G.goldDim : i < step ? '#16a34a10' : G.black, border: `1px solid ${i === step ? G.borderGold : i < step ? '#16a34a40' : G.border}`, borderRadius: 10, cursor: 'pointer' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: i < step ? '#16a34a20' : G.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: i < step ? '#16a34a' : i === step ? G.goldLight : G.muted, flex: '0 0 26px' }}>{i < step ? 'OK' : i + 1}</div>
              <span style={{ fontSize: 13, fontWeight: 600, color: i === step ? G.goldLight : i < step ? '#16a34a' : G.muted }}>{st.title}</span>
              {i === step && <span style={{ marginLeft: 'auto', fontSize: 10, color: G.gold }}>{'<'}- Actual</span>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onClose}>Cerrar y configurar después</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setStep(s => s - 1)}>{'<'}- Anterior</button>}
            {step < steps.length - 1 && <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setStep(s => s + 1)}>Siguiente -></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// GATEWAY WIZARD (Plug & Play)
// -----------------------------------------------------------------------------

function GatewayWizard({ onSave, onCancel, showToast }) {
  const [psp, setPsp] = useState('STRIPE');
  const [step, setStep] = useState(1);
  const [fields, setFields] = useState({});
  const [valResult, setValResult] = useState(null);
  const [whResult, setWhResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trafficPct, setTrafficPct] = useState(100);
  const [gwName, setGwName] = useState('');

  const PSP_CFG = {
    STRIPE: {
      color: '#635BFF', bg: '#635BFF15', border: '#635BFF40', logo: '/logos/stripe.png', title: 'Stripe',
      desc: 'La pasarela mas popular. Apple Pay, Google Pay y tarjetas.',
      fields: [
        { key: 'secretKey', label: 'Secret Key', placeholder: 'sk_live_51Hx...', secret: true, hint: '1. Entra en dashboard.stripe.com\n2. Ve a Developers -> API Keys\n3. Copia la Secret Key (empieza por sk_live_ o sk_test_)\n4. La key de test empieza por sk_test_, la de produccion por sk_live_', link: 'https://dashboard.stripe.com/apikeys' },
        { key: 'publishableKey', label: 'Publishable Key', placeholder: 'pk_live_51Hx...', hint: 'En la misma pagina de API Keys, copia la Publishable Key (pk_live_ o pk_test_)' },
        { key: 'webhookSecret', label: 'Webhook Signing Secret', placeholder: 'whsec_...', secret: true, optional: true, hint: '1. En Stripe: Developers -> Webhooks -> + Add endpoint\n2. URL del endpoint: https://api.zeroriskpay.com/api/webhooks/stripe/<TU_USER_ID>\n   (te lo mostramos al guardar esta pasarela)\n3. Eventos: customer.subscription.* + invoice.paid + invoice.payment_failed + charge.refunded\n4. Stripe te muestra el "Signing secret" (empieza por whsec_) — pegalo aqui\n5. Si dejas vacio este campo, ZRP gestiona los rebills internamente (no aparecen como Subscription nativa en tu Stripe).', link: 'https://dashboard.stripe.com/webhooks' },
      ],
    },
    PAYPAL: {
      color: '#003087', bg: '#00308715', border: '#00308740', logo: '/logos/paypal.jpg', title: 'PayPal',
      desc: 'Pagos con PayPal, tarjetas y Venmo. La mas usada del mundo.',
      fields: [
        { key: 'clientId', label: 'Client ID', placeholder: 'AW3d...', secret: false, hint: '1. Ve a developer.paypal.com/dashboard\n2. Haz clic en "Apps & Credentials"\n3. Selecciona tu app (o crea una nueva)\n4. Copia el Client ID que aparece debajo del nombre', link: 'https://developer.paypal.com/dashboard/applications' },
        { key: 'clientSecret', label: 'Client Secret', placeholder: 'EHx8...', secret: true, hint: 'En la misma pagina de tu app, haz clic en "Show" debajo de Secret para revelarlo y copialo' },
        { key: 'sandbox', label: 'Modo Sandbox (test)', placeholder: 'false', hint: 'Pon "true" para pruebas con dinero ficticio, "false" para cobros reales en produccion. En el dashboard de PayPal, asegurate de estar en la pestana correcta (Sandbox/Live)' },
      ],
    },
    ADYEN: {
      color: '#0ABF53', bg: '#0ABF5315', border: '#0ABF5340', logo: '/logos/adyen.png', title: 'Adyen',
      desc: 'Plataforma global de pagos. Tarjetas, wallets, metodos locales.',
      fields: [
        { key: 'apiKey', label: 'API Key', placeholder: 'AQEjhmfuXNWTx2...', secret: true, hint: '1. Entra en ca-test.adyen.com (test) o ca-live.adyen.com (produccion)\n2. Ve a Developers -> API credentials\n3. Selecciona tu credencial ws@Company o crea una nueva\n4. En "Authentication" genera una nueva API Key\n5. Copia la key completa (empieza por AQE...)', link: 'https://ca-test.adyen.com' },
        { key: 'merchantAccount', label: 'Merchant Account', placeholder: 'MiEmpresaECOM', hint: 'En el menu de Adyen: Account -> Merchant accounts. Copia exactamente el nombre (ej: MiEmpresaECOM). Es case-sensitive.' },
        { key: 'environment', label: 'Entorno', placeholder: 'test o live', hint: '"test" para pruebas (ca-test.adyen.com), "live" para produccion real (ca-live.adyen.com)' },
        { key: 'livePrefix', label: 'Live Prefix (solo produccion)', placeholder: 'abc123...', hint: 'Solo necesario en produccion. En Developers -> API URLs, copia el prefijo unico de tu cuenta (ej: "1c339a5cd1e08")', optional: true },
      ],
    },
    CHECKOUTCOM: {
      color: '#FF6B00', bg: '#FF6B0015', border: '#FF6B0040', logo: '/logos/checkoutcom.jpeg', title: 'Checkout.com',
      desc: 'Pagos online de alto rendimiento. Tarjetas y wallets digitales.',
      fields: [
        { key: 'secretKey', label: 'Secret Key', placeholder: 'sk_sbox_xxxx...', secret: true, hint: '1. Entra en hub.checkout.com (The Hub)\n2. Ve a Settings -> Channels\n3. Selecciona tu canal y ve a "API Keys"\n4. Copia la Secret Key (empieza por sk_sbox_ en sandbox o sk_ en live)', link: 'https://hub.checkout.com' },
        { key: 'publicKey', label: 'Public Key', placeholder: 'pk_sbox_xxxx...', hint: 'En la misma pagina de API Keys, copia la Public Key (pk_sbox_ o pk_)' },
        { key: 'environment', label: 'Entorno', placeholder: 'sandbox o live', hint: '"sandbox" para pruebas, "live" para produccion. Las keys de sandbox empiezan por sk_sbox_ / pk_sbox_' },
      ],
    },
    MOLLIE: {
      color: '#FF5722', bg: '#FF572215', border: '#FF572240', logo: '/logos/mollie.png', title: 'Mollie',
      desc: 'Popular en Europa. iDEAL, Bancontact, SEPA, tarjetas y mas.',
      fields: [
        { key: 'apiKey', label: 'API Key', placeholder: 'live_xxxx... o test_xxxx...', secret: true, hint: '1. Entra en my.mollie.com/dashboard\n2. Ve a Developers -> API keys\n3. Copia la Live API Key (live_xxxx) para produccion\n4. O la Test API Key (test_xxxx) para pruebas\n5. Las keys de test solo funcionan en modo sandbox', link: 'https://my.mollie.com/dashboard/developers/api-keys' },
      ],
    },
    AUTHORIZE: {
      color: '#1A1F71', bg: '#1A1F7115', border: '#1A1F7140', logo: '/logos/authorize.png', title: 'Authorize.net',
      desc: 'Procesador de Visa/CyberSource. Popular en EEUU y Canada.',
      fields: [
        { key: 'loginId', label: 'API Login ID', placeholder: '5KP3u95bQp...', secret: false, hint: '1. Entra en account.authorize.net (o sandbox.authorize.net para test)\n2. Ve a Account -> Settings -> Security Settings -> API Credentials & Keys\n3. Copia el "API Login ID" (cadena alfanumerica corta)', link: 'https://account.authorize.net' },
        { key: 'transactionKey', label: 'Transaction Key', placeholder: '4Ktq966gC55tJ7X...', secret: true, hint: 'En la misma pagina de API Credentials, haz clic en "New Transaction Key". IMPORTANTE: Apuntala porque solo se muestra una vez.' },
        { key: 'sandbox', label: 'Modo Sandbox', placeholder: 'false', hint: '"true" para usar sandbox.authorize.net (test), "false" para api.authorize.net (produccion real)' },
      ],
    },
    BRAINTREE: {
      color: '#4B45B2', bg: '#4B45B215', border: '#4B45B240', logo: '/logos/braintree.png', title: 'Braintree',
      desc: 'Propiedad de PayPal. Tarjetas, PayPal, Venmo, Apple Pay.',
      fields: [
        { key: 'merchantId', label: 'Merchant ID', placeholder: 'abc123def...', hint: '1. Entra en braintreegateway.com (o sandbox.braintreegateway.com para test)\n2. Ve a Settings (engranaje arriba-derecha) -> Business\n3. Copia el "Merchant ID" que aparece arriba', link: 'https://www.braintreegateway.com/login' },
        { key: 'publicKey', label: 'Public Key', placeholder: 'abc123...', hint: 'En Settings -> API -> API Keys, Tokenization Keys, Encryption Keys. Busca tu API Key activa y copia la "Public Key"' },
        { key: 'privateKey', label: 'Private Key', placeholder: 'abc123def456...', secret: true, hint: 'En la misma fila de la API Key, haz clic en "View" para ver la Private Key. Copiala completa.' },
        { key: 'sandbox', label: 'Modo Sandbox', placeholder: 'false', hint: '"true" para sandbox.braintreegateway.com (test), "false" para produccion' },
      ],
    },
    WORLDPAY: {
      color: '#EB001B', bg: '#EB001B15', border: '#EB001B40', logo: '/logos/worldpay.png', title: 'Worldpay',
      desc: 'Procesador global de FIS. Tarjetas internacionales.',
      fields: [
        { key: 'serviceKey', label: 'Service Key', placeholder: 'T_S_xxxx...', secret: true, hint: '1. Entra en online.worldpay.com\n2. Ve a Settings -> API Keys\n3. Copia la Service Key (empieza por T_S_ en test o S_ en live)\n4. Esta key se usa para procesar pagos desde el servidor', link: 'https://online.worldpay.com' },
        { key: 'clientKey', label: 'Client Key', placeholder: 'T_C_xxxx...', hint: 'En la misma pagina, copia la Client Key (T_C_ en test o C_ en live). Se usa para tokenizar tarjetas en el navegador.' },
        { key: 'sandbox', label: 'Modo Sandbox', placeholder: 'false', hint: '"true" para test (api.worldpay.com/v1 con keys T_S_/T_C_), "false" para produccion' },
      ],
    },
    KLARNA: {
      color: '#FFB3C7', bg: '#FFB3C715', border: '#FFB3C740', logo: '/logos/klarna.png', title: 'Klarna',
      desc: 'Compra ahora, paga despues. Financiacion y pago a plazos.',
      fields: [
        { key: 'username', label: 'API Username (UID)', placeholder: 'K123456_abc...', secret: false, hint: '1. Entra en portal.klarna.com\n2. Ve a Settings -> Klarna API credentials\n3. Genera un nuevo par de credenciales\n4. Copia el Username (formato: K12345_abcdef)', link: 'https://portal.klarna.com' },
        { key: 'password', label: 'API Password', placeholder: 'xxxx...', secret: true, hint: 'Al generar las credenciales, se muestra el Password una sola vez. Si lo perdiste, genera un nuevo par.' },
        { key: 'region', label: 'Region', placeholder: 'eu, us u oc', hint: '"eu" = Europa (api.klarna.com)\n"us" = Estados Unidos (api-na.klarna.com)\n"oc" = Oceania (api-oc.klarna.com)\nSelecciona segun donde estan tus clientes' },
        { key: 'sandbox', label: 'Modo Sandbox', placeholder: 'false', hint: '"true" para Playground (pruebas sin dinero real), "false" para produccion' },
      ],
    },
    SQUARE: {
      color: '#3E82F1', bg: '#3E82F115', border: '#3E82F140', logo: '/logos/square.png', title: 'Square',
      desc: 'Pagos presenciales y online. Ideal para retail.',
      fields: [
        { key: 'accessToken', label: 'Access Token', placeholder: 'EAAAxxxxx...', secret: true, hint: '1. Entra en developer.squareup.com/apps\n2. Selecciona tu aplicacion\n3. En la pestana "Credentials", copia el Production Access Token\n4. El token empieza por EAAA... (produccion) o EAAAEz... (sandbox)', link: 'https://developer.squareup.com/apps' },
        { key: 'appId', label: 'Application ID', placeholder: 'sq0idp-...', hint: 'En la misma pagina de Credentials, copia el Application ID (empieza por sq0idp-)' },
        { key: 'locationId', label: 'Location ID', placeholder: 'LXXXXXXXXX', hint: 'Ve a squareup.com/dashboard -> Account & Settings -> Locations. Copia el ID de la ubicacion donde quieres recibir pagos (empieza por L)' },
      ],
    },
    TAILORED: {
      color: '#C9A84C', bg: '#C9A84C15', border: '#C9A84C40', logo: '/logos/tailored.png', title: 'TailoredPay',
      desc: 'Pasarela directa NMI con tokenizacion segura.',
      fields: [
        { key: 'securityKey', label: 'Security Key', placeholder: 'Ej: AEytXm2d...', secret: true, hint: '1. Entra en tu panel de TailoredPayments (secure.nmi.com)\n2. Ve a Settings -> Security Keys\n3. En "API Security Keys" haz clic en "Add"\n4. Selecciona permisos y copia la Security Key generada' },
        { key: 'tokenizationKey', label: 'Tokenization Key (Collect.js)', placeholder: 'Ej: 2YKd55-Kv52g9-...', hint: 'En Settings -> Security Keys -> "Tokenization Keys"\nEsta key se usa para Collect.js (formulario seguro en el navegador)' },
      ],
    },
    COINGATE: {
      color: '#00C853', bg: '#00C85315', border: '#00C85340', logo: '/logos/coingate.png', title: 'CoinGate (Crypto)',
      desc: 'Pagos con Bitcoin, Ethereum, USDT y +50 criptomonedas.',
      fields: [
        { key: 'apiKey', label: 'API Auth Token', placeholder: 'xxxx...', secret: true, hint: '1. Entra en coingate.com y logueate\n2. Ve a API -> Apps (o business.coingate.com/api)\n3. Crea una nueva App\n4. Copia el "Auth Token" generado\n5. Este token se usa para crear ordenes de pago crypto', link: 'https://coingate.com' },
        { key: 'sandbox', label: 'Modo Sandbox', placeholder: 'false', hint: '"true" para sandbox (sandbox.coingate.com - test sin crypto real), "false" para produccion (coingate.com)' },
      ],
    },
  };
  const cfg = PSP_CFG[psp];

  const validate = async () => {
    setLoading(true); setStep(3);
    try {
      const c = PSP_CFG[psp];
      // Validate all required fields are filled
      const requiredFields = c.fields.filter(f => !f.optional);
      for (const f of requiredFields) {
        if (!fields[f.key] && f.key !== 'sandbox') throw new Error(`Introduce ${f.label}`);
      }

      if (psp === 'STRIPE') {
        if (!fields.secretKey?.startsWith('sk_')) throw new Error('La Secret Key debe empezar por sk_live_ o sk_test_');
        const val = await setupApi.validateStripe(fields.secretKey);
        if (!val.valid) throw new Error(val.error);
        setValResult(val);
        const wh = await setupApi.createWebhook(fields.secretKey);
        setWhResult(wh);
        await gatewaysApi.create({ name: gwName || `Stripe -- ${val.account?.name || val.account?.email}`, psp: 'STRIPE', trafficPct, active: true, credentials: { secretKey: fields.secretKey, publishableKey: fields.publishableKey || '', webhookSecret: wh.webhookSecret || '' } });
      } else if (psp === 'SQUARE') {
        const val = await setupApi.validateSquare(fields.accessToken, 'production');
        if (!val.valid) throw new Error(val.error);
        setValResult(val);
        await gatewaysApi.create({ name: gwName || `Square -- ${val.merchant?.name}`, psp: 'SQUARE', trafficPct, active: true, credentials: { accessToken: fields.accessToken, appId: fields.appId || '', locationId: fields.locationId || '', environment: 'production' } });
      } else if (psp === 'TAILORED') {
        await gatewaysApi.create({ name: gwName || 'TailoredPay', psp: 'TAILORED', trafficPct, active: true, credentials: { securityKey: fields.securityKey, tokenizationKey: fields.tokenizationKey } });
        setValResult({ valid: true, account: { name: 'TailoredPay' } });
      } else if (psp === 'PAYPAL') {
        await gatewaysApi.create({ name: gwName || 'PayPal', psp: 'PAYPAL', trafficPct, active: true, credentials: { clientId: fields.clientId, clientSecret: fields.clientSecret, sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'PayPal' } });
      } else if (psp === 'ADYEN') {
        await gatewaysApi.create({ name: gwName || 'Adyen', psp: 'ADYEN', trafficPct, active: true, credentials: { apiKey: fields.apiKey, merchantAccount: fields.merchantAccount, environment: fields.environment || 'test', livePrefix: fields.livePrefix || '' } });
        setValResult({ valid: true, account: { name: fields.merchantAccount || 'Adyen' } });
      } else if (psp === 'CHECKOUTCOM') {
        await gatewaysApi.create({ name: gwName || 'Checkout.com', psp: 'CHECKOUTCOM', trafficPct, active: true, credentials: { secretKey: fields.secretKey, publicKey: fields.publicKey || '', environment: fields.environment || 'sandbox' } });
        setValResult({ valid: true, account: { name: 'Checkout.com' } });
      } else if (psp === 'MOLLIE') {
        await gatewaysApi.create({ name: gwName || 'Mollie', psp: 'MOLLIE', trafficPct, active: true, credentials: { apiKey: fields.apiKey } });
        setValResult({ valid: true, account: { name: 'Mollie' } });
      } else if (psp === 'AUTHORIZE') {
        await gatewaysApi.create({ name: gwName || 'Authorize.net', psp: 'AUTHORIZE', trafficPct, active: true, credentials: { loginId: fields.loginId, transactionKey: fields.transactionKey, sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'Authorize.net' } });
      } else if (psp === 'BRAINTREE') {
        await gatewaysApi.create({ name: gwName || 'Braintree', psp: 'BRAINTREE', trafficPct, active: true, credentials: { merchantId: fields.merchantId, publicKey: fields.publicKey, privateKey: fields.privateKey, sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'Braintree' } });
      } else if (psp === 'WORLDPAY') {
        await gatewaysApi.create({ name: gwName || 'Worldpay', psp: 'WORLDPAY', trafficPct, active: true, credentials: { serviceKey: fields.serviceKey, clientKey: fields.clientKey || '', sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'Worldpay' } });
      } else if (psp === 'KLARNA') {
        await gatewaysApi.create({ name: gwName || 'Klarna', psp: 'KLARNA', trafficPct, active: true, credentials: { username: fields.username, password: fields.password, region: fields.region || 'eu', sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'Klarna' } });
      } else if (psp === 'COINGATE') {
        await gatewaysApi.create({ name: gwName || 'CoinGate (Crypto)', psp: 'COINGATE', trafficPct, active: true, credentials: { apiKey: fields.apiKey, sandbox: fields.sandbox === 'true' } });
        setValResult({ valid: true, account: { name: 'CoinGate' } });
      }
      setStep(4);
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); setStep(2); }
    setLoading(false);
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {step === 1 && (
        <div style={{ padding: 28 }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: G.white, marginBottom: 6 }}>Conectar pasarela de pago</div>
          <div style={{ fontSize: 12, color: G.muted, marginBottom: 20 }}>Selecciona la pasarela y el sistema te guiará.</div>
          <div className="psp-grid">
            {Object.entries(PSP_CFG).map(([k, p]) => (
              <button key={k} onClick={() => setPsp(k)} style={{ background: psp === k ? p.bg : G.black, border: `2px solid ${psp === k ? p.border : G.border}`, borderRadius: 12, padding: '14px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', transition: 'all 0.2s' }}>
                <img src={p.logo} alt={p.title} className="psp-logo-img" style={{ borderRadius: 6, background: '#fff', padding: '3px 6px' }} />
                <div style={{ fontSize: 11, fontWeight: 700, color: psp === k ? p.color : G.white, textAlign: 'center' }}>{p.title}</div>
              </button>
            ))}
          </div>
          <div className="form-group">
            <label className="form-label">Nombre identificador (opcional)</label>
            <input className="form-input" placeholder={`ej: ${cfg.title} -- principal`} value={gwName} onChange={e => setGwName(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">% de tráfico para esta pasarela</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="range" min={0} max={100} value={trafficPct} onChange={e => setTrafficPct(+e.target.value)} style={{ flex: 1 }} />
              <span className="mono" style={{ color: G.goldLight, fontWeight: 700, minWidth: 44 }}>{trafficPct}%</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button className="btn btn-ghost" onClick={onCancel}>Cancelar</button>
            <button className="btn btn-gold" onClick={() => setStep(2)}>Siguiente -></button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div style={{ padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>{'<'}- Atrás</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {cfg.logo && <img src={cfg.logo} alt={cfg.title} style={{ width: 32, height: 22, objectFit: 'contain', borderRadius: 4, background: '#fff', padding: '2px 4px' }} />}
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: cfg.color }}>{cfg.title}</span>
            </div>
          </div>
          {cfg.fields.map(f => (
            <div key={f.key} style={{ marginBottom: 18 }}>
              <label className="form-label">{f.label}</label>
              {f.hint && (
                <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', lineHeight: 1.7, whiteSpace: 'pre-line' }}>📍 {f.hint}</div>
                  {f.link && <a href={f.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: cfg.color, fontWeight: 600 }}>Abrir -></a>}
                </div>
              )}
              <input type={f.secret ? 'password' : 'text'} className="form-input" placeholder={f.placeholder}
                value={fields[f.key] || ''} onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost" onClick={onCancel}>Cancelar</button>
            <button className="btn btn-gold" onClick={validate} disabled={loading}>{loading ? '⏳ Validando...' : `OK Validar y conectar`}</button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>◌</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: G.goldLight }}>Conectando {cfg.title}...</div>
          <div style={{ fontSize: 13, color: G.muted, marginTop: 8 }}>Validando credenciales y creando webhook...</div>
        </div>
      )}
      {step === 4 && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: G.goldLight, marginBottom: 20 }}>{cfg.title} conectado</div>
          {valResult?.account && (
            <div style={{ background: G.black, border: `1px solid ${cfg.border}`, borderRadius: 12, padding: 16, marginBottom: 16, textAlign: 'left' }}>
              {[['Nombre', valResult.account.name], ['Email', valResult.account.email], ['País', valResult.account.country], ['Cobros activos', valResult.account.chargesEnabled ? 'OK Sí' : '⚠ Activa tu cuenta en Stripe']].filter(([, v]) => v).map(([k, v], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, borderBottom: `1px solid ${G.border}20` }}>
                  <span style={{ color: G.muted }}>{k}</span><span style={{ color: G.white, fontWeight: 600 }}>{String(v)}</span>
                </div>
              ))}
            </div>
          )}
          {whResult?.success && (
            <div style={{ background: '#16a34a10', border: '1px solid #16a34a40', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: '#16a34a', textAlign: 'left' }}>
              OK Webhook creado automáticamente -- {whResult.alreadyExisted ? 'ya existía' : `ID: ${whResult.webhookId}`}
            </div>
          )}
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={onSave}>Ir al dashboard -></button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// GATEWAYS NEW
// -----------------------------------------------------------------------------

// ── Pasarelas · métricas operativas (aprobación / chargebacks / salud) ──────
// 2026-09-18. Los nombres de campo son EXACTAMENTE los que devuelve
// backend/src/routes/gateways.js → buildGatewayMetrics(): approvalRate,
// attempts30d, chargebackRate, chargebackCount, health, lowVolume,
// insufficientData, metricsWindowDays, chargebacksUnattributed30d.
//
// Si el cálculo de métricas falla, el backend devuelve la pasarela SIN ninguno
// de esos campos (la lista nunca se cae por una columna nueva) → aquí eso se
// pinta como "--", nunca como un 0%.
const GW_HEALTH_META = {
  healthy:  ['badge-green',  'Correcta'],
  warning:  ['badge-orange', 'Atención'],
  critical: ['badge-red',    'Crítica'],
};
// Umbrales de color del % de aprobación. Son el espejo exacto de
// DECLINE_RATE_WARNING_PCT (12) y DECLINE_RATE_CRITICAL_PCT (18) del backend:
// 100 − 12 = 88 y 100 − 18 = 82. Si allí cambian, cambian aquí.
const GW_APPROVAL_WARNING_PCT = 88;
const GW_APPROVAL_CRITICAL_PCT = 82;
// El backend marca lowVolume por debajo de 30 intentos e insufficientData sin
// ningún intento. En ambos casos el porcentaje es ruido estadístico: un 100%
// sobre 3 intentos NO puede pintarse igual que un 95% sobre 500.
const gwHasMetrics = (g) => !!g && g.approvalRate !== undefined;
const gwSmallSample = (g) => !!g && (g.insufficientData === true || g.lowVolume === true);
const gwSampleTitle = (g) => {
  if (!gwHasMetrics(g)) return 'Métricas no disponibles en esta carga.';
  const win = g.metricsWindowDays || 30;
  if (g.insufficientData) return `Sin intentos de cobro en los últimos ${win} días.`;
  if (g.lowVolume) return `Muestra pequeña: ${g.attempts30d || 0} intentos en ${win} días. Hacen falta al menos 30 para que el porcentaje signifique algo.`;
  return `${g.attempts30d || 0} intentos en los últimos ${win} días.`;
};

function GatewaysNew({ showToast, onNavigate }) {
  const [gateways, setGateways] = useState([]);
  const [shops, setShops] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editGw, setEditGw] = useState(null);
  const [editPct, setEditPct] = useState('');
  const [editName, setEditName] = useState('');
  const [editAmt, setEditAmt] = useState('');
  const [editOrd, setEditOrd] = useState('');
  // 2026-11-01 — "Tiendas donde cobra": 'all' = todas (por defecto) / 'some' = solo editShops
  const [editScope, setEditScope] = useState('all');
  const [editShops, setEditShops] = useState([]);
  const load = useCallback(() => {
    gatewaysApi.getAll().then(setGateways).catch(() => setGateways([]));
    shopsApi.getAll().then(d => setShops(Array.isArray(d) ? d : [])).catch(() => setShops([]));
  }, []);
  const shopLabel = (s) => (s && (s.name || String(s.url || '').replace(/^https?:\/\//, '') || s.id)) || '?';
  const gwShopNames = (g) => (Array.isArray(g.shopIds) ? g.shopIds : []).map(id => { const s = shops.find(x => x.id === id); return s ? shopLabel(s) : '(tienda borrada)'; });
  const gwAllowsShop = (g, s) => !Array.isArray(g.shopIds) || !g.shopIds.length || g.shopIds.includes(s.id);
  // Tiendas que se han quedado sin NINGUNA pasarela activa que las admita → su checkout fallará.
  const shopsWithoutGateway = (gateways.length && shops.length) ? shops.filter(s => !gateways.some(g => g.active && gwAllowsShop(g, s))) : [];
  const toggleEditShop = (id) => setEditShops(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  useEffect(() => { load(); }, [load]);
  const toggle = gw => gatewaysApi.update(gw.id, { active: !gw.active }).then(u => { setGateways(p => p.map(g => g.id === gw.id ? u : g)); showToast(`${u.active ? 'Activada' : 'Pausada'}`, 'success'); }).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error'));
  const remove = id => { if (!window.confirm('Eliminar esta pasarela?')) return; gatewaysApi.delete(id).then(() => { setGateways(p => p.filter(g => g.id !== id)); showToast('Eliminada', 'info'); }).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error')); };
  const totalPct = gateways.filter(g => g.active).reduce((s, g) => s + (g.trafficPct || 0), 0);
  // Es un dato del TENANT (no por pasarela): chargebacks abiertos de la ventana
  // que no se han podido enlazar con ningún pago. Viene repetido en cada fila.
  const unattributedCbs = gateways.find(g => g.chargebacksUnattributed30d)?.chargebacksUnattributed30d || 0;

  const openEdit = (gw) => {
    setEditGw(gw); setEditPct(String(gw.trafficPct || 0)); setEditName(gw.name || ''); setEditAmt(gw.dailyLimitAmount ? String(gw.dailyLimitAmount) : ''); setEditOrd(gw.dailyLimitOrders ? String(gw.dailyLimitOrders) : '');
    const ids = Array.isArray(gw.shopIds) ? gw.shopIds : [];
    setEditShops(ids); setEditScope(ids.length ? 'some' : 'all');
  };
  const saveEdit = () => {
    const pct = parseInt(editPct) || 0;
    const dailyLimitAmount = editAmt === '' ? 0 : Math.max(0, parseFloat(editAmt) || 0);
    const dailyLimitOrders = editOrd === '' ? 0 : Math.max(0, parseInt(editOrd, 10) || 0);
    if (editScope === 'some' && !editShops.length) { showToast('Elige al menos una tienda, o marca "Todas las tiendas"', 'error'); return; }
    const shopIds = editScope === 'some' ? editShops : [];
    gatewaysApi.update(editGw.id, { trafficPct: pct, name: editName, dailyLimitAmount, dailyLimitOrders, shopIds }).then(u => {
      setGateways(p => p.map(g => g.id === editGw.id ? u : g));
      setEditGw(null);
      showToast('Pasarela actualizada', 'success');
    }).catch(e => showToast('Error: ' + e.message, 'error'));
  };

  if (showWizard) return (
    <div className="content">
      <GatewayWizard showToast={showToast} onSave={() => { setShowWizard(false); load(); showToast('Pasarela conectada OK', 'success'); }} onCancel={() => setShowWizard(false)} />
    </div>
  );

  return (
    <div className="content">
      {gateways.length > 0 && totalPct !== 100 && (
        <div style={{ background: '#ea580c15', border: '1px solid #ea580c40', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#ea580c' }}>
          El trafico activo suma {totalPct}% -- debe ser 100%. Ajusta los porcentajes.
        </div>
      )}
      {shopsWithoutGateway.length > 0 && (
        <div style={{ background: '#dc262615', border: '1px solid #dc262640', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#f87171' }}>
          ⚠ {shopsWithoutGateway.length === 1 ? 'Esta tienda no tiene' : 'Estas tiendas no tienen'} ninguna pasarela activa que la{shopsWithoutGateway.length === 1 ? '' : 's'} admita: <b>{shopsWithoutGateway.map(shopLabel).join(', ')}</b>. Sus checkouts fallarán hasta que abras alguna pasarela a {shopsWithoutGateway.length === 1 ? 'ella' : 'ellas'} (Editar → Tiendas donde cobra).
        </div>
      )}
      {gateways.length === 0 && (
        <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 14, padding: 40, textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⬡</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: G.goldLight, marginBottom: 10 }}>Sin pasarelas configuradas</div>
          <div style={{ fontSize: 13, color: G.muted, marginBottom: 20 }}>Conecta tu primera pasarela para empezar a recibir pagos.</div>
          <button className="btn btn-gold" onClick={() => setShowWizard(true)}>+ Conectar primera pasarela</button>
        </div>
      )}
      {gateways.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16 }}>
            {onNavigate
              ? <button className="btn btn-ghost" onClick={() => onNavigate('gateway-health')} title="Aprobación, chargebacks y capacidad recomendada por cuenta">◉ Salud de pasarelas</button>
              : <span style={{ fontSize: 12, color: G.muted, alignSelf: 'center' }}>Salud de pasarelas: sección “Salud de pasarelas” del menú</span>}
            <button className="btn btn-gold" onClick={() => setShowWizard(true)}>+ Añadir pasarela</button>
          </div>
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Pasarela</th><th>PSP</th><th>Trafico</th><th style={{ textAlign: 'right' }}>Aprobación</th><th style={{ textAlign: 'right' }}>Chargebacks</th><th>Salud</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {gateways.map(g => (
                    <React.Fragment key={g.id}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {PSP_LOGOS[g.psp] ? <img src={PSP_LOGOS[g.psp]} alt={g.psp} style={{ width: 32, height: 22, objectFit: 'contain', background: '#fff', borderRadius: 5, padding: '2px 5px', border: `1px solid ${G.border}`, flexShrink: 0 }} /> : <div className="gateway-psp-icon">{g.psp?.[0] || '?'}</div>}
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</span>
                        </div>
                      </td>
                      <td><span className={`badge ${g.psp === 'STRIPE' ? 'badge-blue' : g.psp === 'SQUARE' ? 'badge-gold' : 'badge-green'}`}>{g.psp}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="progress" style={{ width: 70 }}><div className="progress-fill" style={{ width: `${g.trafficPct || 0}%` }} /></div>
                          <span className="mono" style={{ fontSize: 12, color: G.goldLight }}>{g.trafficPct || 0}%</span>
                        </div>
                        {(g.dailyLimitAmount || g.dailyLimitOrders) ? (
                          <div style={{ fontSize: 10, color: G.dim, marginTop: 3 }}>
                            Tope/dia: {g.dailyLimitAmount ? `${g.dailyLimitAmount}€` : '—'}{g.dailyLimitOrders ? ` · ${g.dailyLimitOrders} ped.` : ''}
                          </div>
                        ) : null}
                        {Array.isArray(g.shopIds) && g.shopIds.length ? (
                          <div style={{ fontSize: 10, color: '#7dd3fc', marginTop: 3 }} title={`Solo cobra pedidos de: ${gwShopNames(g).join(', ')}`}>
                            Solo: {gwShopNames(g).join(', ')}
                          </div>
                        ) : null}
                      </td>
                      {/* Aprobación — "--" atenuado cuando la muestra es pequeña o no hay datos */}
                      <td style={{ textAlign: 'right' }} title={gwSampleTitle(g)}>
                        {!gwHasMetrics(g) ? (
                          <span className="mono" style={{ fontSize: 12, color: G.muted }}>--</span>
                        ) : (
                          <div style={{ opacity: gwSmallSample(g) ? 0.5 : 1 }}>
                            <div className="mono" style={{
                              fontSize: 13, fontWeight: 600,
                              color: gwSmallSample(g) || g.approvalRate == null ? G.muted
                                : g.approvalRate < GW_APPROVAL_CRITICAL_PCT ? G.red
                                : g.approvalRate < GW_APPROVAL_WARNING_PCT ? G.orange
                                : G.green,
                            }}>
                              {gwSmallSample(g) || g.approvalRate == null ? '--' : `${g.approvalRate}%`}
                            </div>
                            <div className="mono" style={{ fontSize: 10, color: G.muted, marginTop: 2 }}>{g.attempts30d || 0} / {g.metricsWindowDays || 30}d</div>
                          </div>
                        )}
                      </td>
                      {/* Chargebacks — mismo criterio de honestidad estadística */}
                      <td style={{ textAlign: 'right' }} title={gwSampleTitle(g)}>
                        {!gwHasMetrics(g) ? (
                          <span className="mono" style={{ fontSize: 12, color: G.muted }}>--</span>
                        ) : (
                          <div style={{ opacity: gwSmallSample(g) ? 0.5 : 1 }}>
                            <div className="mono" style={{
                              fontSize: 13, fontWeight: 600,
                              color: gwSmallSample(g) || g.chargebackRate == null ? G.muted
                                : g.chargebackRate >= 1 ? G.red
                                : g.chargebackRate >= 0.65 ? G.orange
                                : G.white,
                            }}>
                              {gwSmallSample(g) || g.chargebackRate == null ? '--' : `${g.chargebackRate}%`}
                            </div>
                            <div className="mono" style={{ fontSize: 10, color: G.muted, marginTop: 2 }}>{g.chargebackCount || 0} caso{(g.chargebackCount || 0) === 1 ? '' : 's'}</div>
                          </div>
                        )}
                      </td>
                      {/* Salud — veredicto del backend; en gris si la muestra no da para fiarse */}
                      <td title={gwSampleTitle(g)}>
                        {!gwHasMetrics(g) || g.insufficientData || !g.health ? (
                          <span className="badge badge-gray">Sin datos</span>
                        ) : (
                          <>
                            <span className={`badge ${g.lowVolume ? 'badge-gray' : (GW_HEALTH_META[g.health] || ['badge-gray'])[0]}`}>
                              {(GW_HEALTH_META[g.health] || [null, g.health])[1]}
                            </span>
                            {g.lowVolume && <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>muestra pequeña</div>}
                          </>
                        )}
                      </td>
                      <td><StatusBadge status={g.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(g)}>Editar</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => toggle(g)}>{g.active ? 'Pausar' : 'Activar'}</button>
                          <button className="btn btn-danger btn-sm" onClick={() => remove(g.id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                    {g.psp === 'STRIPE' && g.userId && (
                      <tr>
                        <td colSpan={8} style={{ background: '#0d1a2a', padding: '10px 14px', borderTop: 'none' }}>
                          <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>📡 URL del webhook (pegar en Stripe Dashboard → Developers → Webhooks)</div>
                          <code style={{ display: 'block', fontFamily: 'DM Mono, monospace', fontSize: 11, color: '#7dd3fc', background: '#000', padding: '8px 10px', borderRadius: 4, wordBreak: 'break-all' }}>
                            {`https://api.zeroriskpay.com/api/webhooks/stripe/${g.userId}`}
                          </code>
                          <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>
                            {g.credentials?.webhookSecret
                              ? <span style={{ color: G.green }}>✓ Webhook signing secret guardado — los eventos de esta cuenta Stripe se procesarán correctamente.</span>
                              : <span style={{ color: G.orange }}>⚠ Falta el "Webhook signing secret" (whsec_…) en las credenciales. Sin esto, los webhooks entrantes serán rechazados. Edita la pasarela para añadirlo.</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pie de tabla: qué significan las columnas nuevas y qué NO cubren */}
            <div style={{ padding: '12px 22px', borderTop: `1px solid ${G.border}`, fontSize: 11, color: G.muted, lineHeight: 1.7 }}>
              Aprobación y chargebacks se calculan sobre los cobros reales de los últimos {gateways.find(gwHasMetrics)?.metricsWindowDays || 30} días.
              Con menos de 30 intentos se muestra “--”: el porcentaje no significaría nada.
              {unattributedCbs > 0 && (
                <div style={{ color: G.orange, marginTop: 4 }}>
                  ⚠ {unattributedCbs} chargeback{unattributedCbs === 1 ? '' : 's'} de la ventana no se ha{unattributedCbs === 1 ? '' : 'n'} podido enlazar con ningún pago, así que no suma{unattributedCbs === 1 ? '' : 'n'} en ninguna fila: los porcentajes de arriba se quedan cortos.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {editGw && (
        <Modal title={`Editar: ${editGw.name}`} onClose={() => setEditGw(null)}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Nombre</label>
            <input style={{ width: '100%', padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13 }}
              value={editName} onChange={e => setEditName(e.target.value)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>% de trafico</label>
            <input type="number" min="0" max="100" style={{ width: '100%', padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13 }}
              value={editPct} onChange={e => setEditPct(e.target.value)} />
            <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Porcentaje de trafico que recibira esta pasarela (0-100)</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Tope diario del checkout</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input type="number" min="0" placeholder="€/dia (vacio = sin tope)" style={{ flex: 1, padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13 }}
                value={editAmt} onChange={e => setEditAmt(e.target.value)} />
              <input type="number" min="0" placeholder="pedidos/dia" style={{ flex: 1, padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13 }}
                value={editOrd} onChange={e => setEditOrd(e.target.value)} />
            </div>
            <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Al llegar al tope de hoy, esta pasarela deja de salir en el checkout y el trafico rota a las demas. Se reinicia solo cada dia. Importe en € (todo convertido). Vacio o 0 = sin tope.</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, color: G.white, fontWeight: 600, fontSize: 13 }}>Tiendas donde cobra</label>
            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: G.white, marginBottom: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="gw-shop-scope" checked={editScope === 'all'} onChange={() => setEditScope('all')} /> Todas las tiendas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="gw-shop-scope" checked={editScope === 'some'} onChange={() => setEditScope('some')} /> Solo estas tiendas
              </label>
            </div>
            {editScope === 'some' && (
              shops.length === 0
                ? <div style={{ fontSize: 12, color: G.orange }}>No tienes tiendas creadas todavía.</div>
                : (
                  <div style={{ display: 'grid', gap: 6, maxHeight: 180, overflowY: 'auto', padding: '10px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8 }}>
                    {shops.map(s => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: G.white, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editShops.includes(s.id)} onChange={() => toggleEditShop(s.id)} />
                        <span>{shopLabel(s)}</span>
                        {s.name && s.url ? <span style={{ fontSize: 11, color: G.dim }}>{String(s.url).replace(/^https?:\/\//, '')}</span> : null}
                      </label>
                    ))}
                  </div>
                )
            )}
            <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Por defecto una pasarela cobra en todas tus tiendas. Si eliges tiendas, solo recibirá pedidos de esas; el resto de tiendas reparte entre las demás pasarelas.</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: G.dim }}>PSP: </span><span className={`badge ${editGw.psp === 'STRIPE' ? 'badge-blue' : editGw.psp === 'SQUARE' ? 'badge-gold' : 'badge-green'}`}>{editGw.psp}</span>
          </div>
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={saveEdit}>Guardar cambios</button>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SHOPS WITH SCRIPT
// -----------------------------------------------------------------------------

// 2026-10-14 — Reservas (pre-order) por tienda. Hecho para Celestial Guild (UK):
// detecta artículos de reserva por tag de Shopify, envía la confirmación de
// reserva, no arranca el tracking hasta la fecha de salida y usa el buzón del
// bot de la tienda como remitente. Solo afecta a la tienda donde se activa.
function PreorderSettingsCard({ shop, showToast, onSaved }) {
  const p = (shop && shop.preorder) || {};
  const [enabled, setEnabled] = useState(p.enabled === true);
  const [tags, setTags] = useState(Array.isArray(p.tags) && p.tags.length ? p.tags.join(', ') : 'preorder, pre-order');
  const [productIds, setProductIds] = useState(Array.isArray(p.productIds) ? p.productIds.join(', ') : '');
  const [titleKeywords, setTitleKeywords] = useState(Array.isArray(p.titleKeywords) && p.titleKeywords.length ? p.titleKeywords.join(', ') : 'pre-order, preorder');
  const [releaseDate, setReleaseDate] = useState(p.releaseDate || '');
  const [releaseMode, setReleaseMode] = useState(p.releaseMode === 'manual' ? 'manual' : 'date');
  const [deliveryEstimate, setDeliveryEstimate] = useState(p.deliveryEstimate || '');
  const [transitDays, setTransitDays] = useState(p.transitDays || '');
  const [shopBotSender, setShopBotSender] = useState(!!(shop && shop.orderEmails && shop.orderEmails.sender === 'shop_bot'));
  const [backupEmail, setBackupEmail] = useState(!(shop && shop.tenantBackupEmail === false));
  const [emailLanguage, setEmailLanguage] = useState((shop && shop.emailLanguage) || '');
  const to = (shop && shop.trackingOrigin) || {};
  const [originCC, setOriginCC] = useState(to.countryCode || '');
  const [originCity, setOriginCity] = useState(to.city || '');
  const [originLabel, setOriginLabel] = useState(to.label || '');
  const [requirePhone, setRequirePhone] = useState(!!(shop && shop.requirePhone));
  const [saving, setSaving] = useState(false);
  const inp = { width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 10, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 };
  const list = (v) => String(v || '').split(/[,\n;]/).map(x => x.trim()).filter(Boolean);
  const save = async () => {
    if (enabled && releaseMode === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(releaseDate)) { showToast('Pon la fecha de salida (AAAA-MM-DD) o elige salida manual', 'error'); return; }
    setSaving(true);
    try {
      const patch = {
        preorder: { enabled, tags: list(tags), productIds: list(productIds), titleKeywords: list(titleKeywords), releaseDate: releaseDate || '', releaseMode, deliveryEstimate: deliveryEstimate.trim(), transitDays: parseInt(transitDays, 10) || 0 },
        orderEmails: { sender: shopBotSender ? 'shop_bot' : 'tenant' },
        tenantBackupEmail: backupEmail,
        emailLanguage,
        requirePhone,
        trackingOrigin: originCC.trim() ? { countryCode: originCC.trim().toUpperCase().slice(0, 2), city: originCity.trim(), label: originLabel.trim() } : null,
      };
      const updated = await shopsApi.update(shop.id, patch);
      showToast('Reservas guardadas', 'success');
      if (onSaved) onSaved({ ...shop, ...patch, ...(updated && updated.id ? { updatedAt: updated.updatedAt } : {}) });
    } catch (err) { showToast(err.response?.data?.error || err.message, 'error'); }
    setSaving(false);
  };
  return (
    <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Reservas (pre-order)</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span style={{ fontSize: 13, color: G.white }}>Esta tienda vende artículos en reserva (se entregan más tarde)</span>
      </label>
      {enabled && (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 12, color: G.dim }}>Un pedido es reserva si algún artículo lleva uno de estos tags en Shopify, está en la lista de productos o su título contiene una de las palabras. En pedidos mixtos, los artículos normales salen ya y los de reserva esperan.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Tags de Shopify (separados por coma)</label><input style={inp} value={tags} onChange={e => setTags(e.target.value)} placeholder="preorder, pre-order" /></div>
            <div><label style={lbl}>Palabras en el título</label><input style={inp} value={titleKeywords} onChange={e => setTitleKeywords(e.target.value)} placeholder="pre-order" /></div>
          </div>
          <div><label style={lbl}>IDs o handles de producto (opcional, separados por coma)</label><input style={inp} value={productIds} onChange={e => setProductIds(e.target.value)} placeholder="8123456789, astral-lamp" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Salida de las reservas</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={releaseMode} onChange={e => setReleaseMode(e.target.value)}>
                <option value="date">En una fecha</option>
                <option value="manual">Manual (botón Enviar ahora)</option>
              </select>
            </div>
            <div><label style={lbl}>Fecha de salida</label><input type="date" style={inp} value={releaseDate} onChange={e => setReleaseDate(e.target.value)} disabled={releaseMode !== 'date'} /></div>
            <div><label style={lbl}>Días de tránsito tras la salida</label><input type="number" min="0" style={inp} value={transitDays} onChange={e => setTransitDays(e.target.value)} placeholder="por defecto del tracking" /></div>
          </div>
          <div><label style={lbl}>Texto de entrega para el email (en el idioma de la tienda)</label><input style={inp} value={deliveryEstimate} onChange={e => setDeliveryEstimate(e.target.value)} placeholder="Delivery within 3-5 working days after dispatch" /></div>
          <div style={{ fontSize: 11, color: G.dim }}>Tip: un tag <code>ships-2026-11-20</code> en un producto fija su propia fecha de salida.</div>
        </div>
      )}
      <div style={{ borderTop: `1px solid ${G.border}`, marginTop: 14, paddingTop: 12, display: 'grid', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={shopBotSender} onChange={e => setShopBotSender(e.target.checked)} />
          <span style={{ fontSize: 13, color: G.white }}>Enviar los emails de pedido desde el buzón del bot de esta tienda (en vez del email del comercio)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={requirePhone} onChange={e => setRequirePhone(e.target.checked)} />
          <span style={{ fontSize: 13, color: G.white }}>Teléfono obligatorio en el checkout (no se puede pagar sin un móvil válido; mejora el SMS de carrito abandonado)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={backupEmail} onChange={e => setBackupEmail(e.target.checked)} />
          <span style={{ fontSize: 13, color: G.white }}>Enviar también el email de refuerzo del tenant tras el tracking</span>
        </label>
        <div>
          <label style={lbl}>Origen del envío para el seguimiento (solo esta tienda; vacío = el del transitario)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 10 }}>
            <input style={inp} value={originCC} onChange={e => setOriginCC(e.target.value)} placeholder="NO" maxLength={2} title="País ISO-2, ej. NO" />
            <input style={inp} value={originCity} onChange={e => setOriginCity(e.target.value)} placeholder="Oslo" />
            <input style={inp} value={originLabel} onChange={e => setOriginLabel(e.target.value)} placeholder="Oslo — Norway (texto en emails y página)" />
          </div>
          <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Los días de tránsito siguen siendo los configurados en Tracking del transitario.</div>
        </div>
        <div style={{ maxWidth: 360 }}>
          <label style={lbl}>Idioma de los emails de pedido cuando no se deduce del país</label>
          <select style={{ ...inp, cursor: 'pointer' }} value={emailLanguage} onChange={e => setEmailLanguage(e.target.value)}>
            <option value="">Automático (país del cliente, si no español)</option>
            <option value="en">Inglés</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 12 }}><button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar reservas'}</button></div>
    </div>
  );
}

// ── Chequeador de webs (2026-10-21) ─────────────────────────────────────────
// Estado de la web de cada tienda (backend site-monitor: cada 5 min; caída =
// 3 comprobaciones seguidas fallidas). Pastilla en Tiendas + banner rojo arriba.
const SITE_STATUS = {
  up:       { label: 'Online',          color: G.green,  hint: 'La web y el checkout responden' },
  checking: { label: 'Comprobando…',    color: G.orange, hint: 'Ha fallado alguna comprobación; se confirma en unos minutos' },
  down:     { label: 'Caída',           color: G.red,    hint: 'No responde: los clientes no pueden comprar' },
  password: { label: 'Con contraseña',  color: G.red,    hint: 'La tienda está cerrada con contraseña en la plataforma' },
  blocked:  { label: 'Sin verificar',   color: G.dim,    hint: 'La web bloquea las comprobaciones automáticas (anti-bots)' },
  unknown:  { label: 'Sin comprobar',   color: G.dim,    hint: 'Aún no se ha comprobado' },
};
const fmtSiteTime = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: APP_TZ }) : '—');

function SiteStatusPill({ st }) {
  const cfg = SITE_STATUS[(st && st.status) || 'unknown'] || SITE_STATUS.unknown;
  const title = [cfg.hint, st && st.lastError ? `Último error: ${st.lastError}` : null, st && st.lastCheckAt ? `Comprobado: ${fmtSiteTime(st.lastCheckAt)}` : null].filter(Boolean).join('\n');
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: cfg.color, border: `1px solid ${cfg.color}55`, background: `${cfg.color}14`, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, boxShadow: st && (st.status === 'down' || st.status === 'password') ? `0 0 6px ${cfg.color}` : 'none' }} />
      {cfg.label}
    </span>
  );
}

function SiteDownBanner({ onNavigate }) {
  const [down, setDown] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => shopsApi.siteStatus()
      .then(d => { if (alive) setDown((d && d.shops ? d.shops : []).filter(s => s.status === 'down' || s.status === 'password')); })
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!down.length) return null;
  return (
    <div className="site-down-banner" role="alert" style={{ padding: '12px 16px', borderRadius: 10, border: `1px solid ${G.red}66`, background: `${G.red}18`, color: G.white, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <div style={{ flex: 1, minWidth: 220, fontSize: 13, lineHeight: 1.5 }}>
        {down.map(s => (
          <div key={s.shopId}>
            <b>{s.shopName}</b>{' '}
            {s.status === 'password' ? 'está cerrada con contraseña' : 'no funciona'}
            {s.downSince ? ` desde el ${fmtSiteTime(s.downSince)}` : ''}
            {s.lastError ? <span style={{ color: G.muted }}> · {s.lastError}</span> : null}
          </div>
        ))}
      </div>
      <button className="btn btn-ghost btn-sm" onClick={() => onNavigate && onNavigate('shops')}>Ver tiendas</button>
    </div>
  );
}

function ShopsWithScript({ showToast }) {
  const [shops, setShops] = useState([]);
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', successUrl: '', contactEmail: '', platform: 'shopify' });
  const [uploading, setUploading] = useState(false);
  // La lista de tiendas no trae logoData (PERF): el logo existente se muestra
  // desde GET /api/shops/:id/logo; logoVersion fuerza recarga tras subir/quitar.
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const selShopId = sel ? sel.id : null;
  useEffect(() => { setLogoLoaded(false); }, [selShopId]);

  const PLATFORMS = {
    shopify: { name: 'Shopify', logo: '/logos/shopify.png', color: '#96bf48', urlHint: 'mitienda.myshopify.com', steps: ['Entra en tu Shopify Admin (tu-tienda.myshopify.com/admin)', 'Ve a Canal de ventas -> Tienda online -> Temas', 'Haz clic en los tres puntos (...) junto a tu tema activo -> "Editar codigo"', 'En la carpeta Layout, abre el archivo theme.liquid', 'Pulsa Ctrl+F y busca: </head>', 'Pega el script de ZeroRiskPay justo ANTES de </head>', 'Haz clic en "Save" arriba a la derecha -- listo OK'] },
    woocommerce: { name: 'WooCommerce', logo: '/logos/woocommerce.png', color: '#9B5C8F', urlHint: 'mitienda.com', steps: ['Entra en tu Panel WordPress (tu-tienda.com/wp-admin)', 'Ve a Plugins -> Anadir nuevo -> busca "Insert Headers and Footers" (de WPBeginner) e instalalo', 'Ve a Settings -> Insert Headers and Footers', 'Pega el script de ZeroRiskPay en el campo "Scripts in Header"', 'Haz clic en "Save" -- listo OK', 'Alternativa sin plugin: Apariencia -> Editor de temas -> header.php -> pega antes de </head>'] },
    magento: { name: 'Magento', logo: '/logos/magento.png', color: '#F46F25', urlHint: 'mitienda.com', steps: ['Entra en tu Panel de Magento Admin', 'Ve a Content -> Design -> Configuration', 'Selecciona tu Store View y haz clic en "Edit"', 'Despliega la seccion "HTML Head"', 'En el campo "Scripts and Style Sheets" pega el script de ZeroRiskPay', 'Haz clic en "Save Configuration"', 'Ve a System -> Cache Management -> Flush Magento Cache'] },
    prestashop: { name: 'PrestaShop', logo: '/logos/prestashop.webp', color: '#DF0067', urlHint: 'mitienda.com', steps: ['Entra en tu Backoffice de PrestaShop', 'Ve a Modulos -> Module Manager -> busca "Custom code" o instala el modulo gratis', 'Alternativa: Ve a Diseno -> Posiciones', 'Busca el hook "displayHeader"', 'Haz clic en "Enlazar un modulo" y selecciona Custom Code', 'Pega el script de ZeroRiskPay y guarda', 'Limpia la cache en Parametros avanzados -> Rendimiento'] },
    bigcommerce: { name: 'BigCommerce', logo: '/logos/bigcommerce.png', color: '#34313F', urlHint: 'mitienda.mybigcommerce.com', steps: ['Entra en tu Panel de BigCommerce', 'Ve a Storefront -> Script Manager', 'Haz clic en "Create a Script"', 'Nombre: ZeroRiskPay | Ubicacion: Header | Paginas: All Pages', 'Tipo: Script | Pega el codigo completo del script', 'Haz clic en "Save" -- listo OK'] },
    wix: { name: 'Wix', logo: '/logos/wix.png', color: '#FAAD4D', urlHint: 'mitienda.com', steps: ['Entra en tu Panel de Wix', 'Ve a Configuracion del sitio -> Codigo personalizado (o Settings -> Custom Code)', 'Haz clic en "+ Anadir codigo personalizado"', 'Ponle nombre: ZeroRiskPay', 'Pega el script completo', 'Selecciona "Head" como ubicacion y "Todas las paginas"', 'Haz clic en "Aplicar" -- listo OK'] },
    custom: { name: 'API / Custom', logo: '/logos/custom.webp', color: '#C9A84C', urlHint: 'mitienda.com', steps: ['Abre el archivo HTML principal de tu sitio (index.html, header.php, layout, etc.)', 'Pega el script de ZeroRiskPay justo antes de la etiqueta </head>', 'Para integracion por API: usa POST /api/checkout/session para crear sesiones de pago', 'Envia amount (centimos), currency, y customerId en el body JSON', 'Documentacion completa en docs.zeroriskpay.com'] },
  };
  const load = useCallback(() => { shopsApi.getAll().then(setShops).catch(() => setShops([])); }, []);
  useEffect(() => { load(); }, [load]);

  // Estado de las webs (chequeador) + a quién se avisa
  const [siteStatus, setSiteStatus] = useState({});
  const [alertEmails, setAlertEmails] = useState('');
  const [alertCustom, setAlertCustom] = useState(false);
  const [alertEditing, setAlertEditing] = useState(false);
  const alertEditingRef = useRef(false);
  useEffect(() => { alertEditingRef.current = alertEditing; }, [alertEditing]);
  const [checking, setChecking] = useState(() => new Set());
  const loadSite = useCallback(() => {
    shopsApi.siteStatus().then(d => {
      setSiteStatus(Object.fromEntries((d && d.shops ? d.shops : []).map(x => [x.shopId, x])));
      setAlertCustom(!!(d && d.customAlertEmails));
      if (!alertEditingRef.current) setAlertEmails(((d && d.alertEmails) || []).join(', '));
    }).catch(() => {});
  }, []);
  useEffect(() => { loadSite(); const t = setInterval(loadSite, 60000); return () => clearInterval(t); }, [loadSite]);
  const checkNow = async (shopId) => {
    setChecking(p => new Set(p).add(shopId));
    try {
      const st = await shopsApi.siteCheck(shopId);
      if (st) setSiteStatus(p => ({ ...p, [shopId]: st }));
      const k = st && st.lastKind;
      if (k === 'up') showToast('La web responde correctamente', 'success');
      else if (k === 'blocked') showToast('La web bloquea las comprobaciones automáticas; ábrela tú para confirmarlo', 'info');
      else showToast(`Fallo: ${(st && st.lastError) || 'no responde'}`, 'error');
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
    finally { setChecking(p => { const n = new Set(p); n.delete(shopId); return n; }); }
  };
  const saveAlertEmails = async () => {
    try {
      const r = await shopsApi.setSiteAlerts(alertEmails.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean));
      setAlertEmails((r.alertEmails || []).join(', ')); setAlertCustom(!!r.customAlertEmails); setAlertEditing(false);
      showToast('Emails de aviso guardados', 'success');
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const create = async () => {
    if (!form.name || !form.url) { showToast('Nombre y URL obligatorios', 'error'); return; }
    try {
      const s = await shopsApi.create({ name: form.name, url: form.url, successUrl: form.successUrl || `https://${form.url}/pages/gracias`, contactEmail: form.contactEmail || '', platform: form.platform || 'shopify', active: true });
      setShops(p => [...p, s]); setSel(s); setModal('script');
      setForm({ name: '', url: '', successUrl: '', contactEmail: '', platform: 'shopify' }); showToast('Tienda creada OK', 'success');
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const handleLogoUpload = (shop) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/svg+xml';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { showToast(`Logo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 2 MB.`, 'error'); return; }
      setUploading(true);
      const reader = new FileReader();
      // El error del PUT ocurría dentro de onload (async) y el try/catch de
      // fuera no lo veía: sin toast y el botón se quedaba en "subiendo".
      reader.onload = async (ev) => {
        const logoData = ev.target.result;
        try {
          await shopsApi.update(shop.id, { logoData });
          setShops(p => p.map(s => s.id === shop.id ? { ...s, logoData, hasLogo: true } : s));
          setSel(p => (p && p.id === shop.id ? { ...p, logoData, hasLogo: true } : p));
          setLogoLoaded(true);
          setLogoVersion(Date.now());
          showToast('Logo actualizado', 'success');
        } catch (err) {
          const msg = err.response?.data?.error || err.message || 'Error subiendo el logo';
          showToast(`No se pudo guardar el logo: ${msg}`, 'error');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => { showToast('No se pudo leer el archivo', 'error'); setUploading(false); };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const removeLogo = async (shop) => {
    try {
      await shopsApi.update(shop.id, { logoData: '' });
      setShops(p => p.map(s => s.id === shop.id ? { ...s, logoData: '', hasLogo: false } : s));
      setSel(p => (p && p.id === shop.id ? { ...p, logoData: '', hasLogo: false } : p));
      setLogoLoaded(false);
      setLogoVersion(Date.now());
      showToast('Logo eliminado', 'info');
    } catch (err) { showToast(err.response?.data?.error || err.message, 'error'); }
  };

  const getScript = shop => {
    const rawUrl = (shop.url || '').replace(/^(https?:\/\/)?(www\.)?/, '');
    // Si la tienda no tiene dominio propio (URL .myshopify.com), NO podemos
    // generar un checkout.<url>/nlp.js — checkout.xxx.myshopify.com no es
    // ningún dominio nuestro. Devolvemos un snippet con placeholder claro
    // para que el operator sepa que hace falta configurar dominio propio.
    if (/\.myshopify\.com$/i.test(rawUrl)) {
      return `<!-- ${shop.name} -->\n<!-- FALTA: configura primero un dominio propio de checkout (ej. checkout.tutienda.com) en el panel del cliente. -->\n<script defer src="https://checkout.TU-DOMINIO-PROPIO.com/nlp.js?shop=${shop.url || shop.id}"></script>`;
    }
    return `<!-- ${shop.name} -->\n<script defer src="https://checkout.${rawUrl}/nlp.js?shop=${shop.url || shop.id}"></script>`;
  };

  return (
    <div className="content">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-gold" onClick={() => setModal('create')}>+ Nueva tienda</button>
      </div>
      {shops.length > 0 && (
        <div className="card" style={{ marginBottom: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Avisos de web caída</div>
            <div style={{ fontSize: 12, color: G.dim, marginTop: 2 }}>Comprobamos tus webs y checkouts cada 5 minutos. Si una cae, te avisamos por email{alertCustom ? '' : ' (ahora al email de tu cuenta)'}.</div>
          </div>
          {alertEditing ? (
            <>
              <input className="form-input" style={{ minWidth: 260, width: "auto" }} value={alertEmails} onChange={e => setAlertEmails(e.target.value)} placeholder="tu@email.com, otro@email.com (máx. 3)" />
              <button className="btn btn-gold btn-sm" onClick={saveAlertEmails}>Guardar</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { alertEditingRef.current = false; setAlertEditing(false); loadSite(); }}>Cancelar</button>
            </>
          ) : (
            <>
              <span className="mono" style={{ fontSize: 12 }}>{alertEmails || '—'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setAlertEditing(true)}>Cambiar</button>
            </>
          )}
        </div>
      )}
      {shops.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">◫</div>Sin tiendas. Añade tu primera tienda o e-commerce.</div></div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Logo</th><th>Tienda</th><th>Plataforma</th><th>URL</th><th>Web</th><th>Express Checkout</th><th>Estado</th><th>Configuracion</th><th></th></tr></thead>
              <tbody>
                {shops.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {s.logoData ? (
                          <img src={s.logoData} alt="" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6, border: `1px solid ${G.border}`, background: G.surface, cursor: 'pointer' }} onClick={() => handleLogoUpload(s)} title="Click para cambiar logo" />
                        ) : (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => handleLogoUpload(s)} disabled={uploading}>
                            {uploading ? '...' : 'Subir'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>
                      {(() => { const pl = PLATFORMS[s.platform] || PLATFORMS.shopify; return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <img src={pl.logo} alt={pl.name} style={{ width: 28, height: 20, objectFit: 'contain', background: '#fff', borderRadius: 4, padding: '2px 4px', border: `1px solid ${G.border}` }} />
                          <span style={{ fontSize: 11, color: pl.color, fontWeight: 700 }}>{pl.name}</span>
                        </div>
                      ); })()}
                    </td>
                    <td><span className="mono" style={{ fontSize: 11, color: G.dim }}>{s.url}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                        {s.active === false ? <span style={{ fontSize: 11, color: G.dim }}>— (tienda inactiva)</span> : <SiteStatusPill st={siteStatus[s.id]} />}
                        {siteStatus[s.id] && siteStatus[s.id].lastError && siteStatus[s.id].status !== 'up' && (
                          <span style={{ fontSize: 10, color: G.dim, maxWidth: 220, lineHeight: 1.3 }}>{siteStatus[s.id].lastError}</span>
                        )}
                        {s.active !== false && (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} disabled={checking.has(s.id)} onClick={() => checkNow(s.id)}>
                            {checking.has(s.id) ? 'Comprobando…' : 'Comprobar ahora'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div onClick={() => { const v = s.applePay !== false; shopsApi.update(s.id, { applePay: !v }).then(() => setShops(p => p.map(x => x.id === s.id ? { ...x, applePay: !v } : x))).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error')); }} style={{ width: 36, height: 20, borderRadius: 10, background: s.applePay !== false ? G.green : G.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                            <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: s.applePay !== false ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                          </div>
                          <span style={{ fontSize: 11, color: G.dim }}> Apple Pay</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div onClick={() => { const v = s.googlePay !== false; shopsApi.update(s.id, { googlePay: !v }).then(() => setShops(p => p.map(x => x.id === s.id ? { ...x, googlePay: !v } : x))).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error')); }} style={{ width: 36, height: 20, borderRadius: 10, background: s.googlePay !== false ? G.green : G.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                            <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: s.googlePay !== false ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                          </div>
                          <span style={{ fontSize: 11, color: G.dim }}> Google Pay</span>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge status={s.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => { setSel(s); setModal('script'); }}>Editar</button></td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => { if (!window.confirm('¿Eliminar?')) return; shopsApi.delete(s.id).then(() => { setShops(p => p.filter(x => x.id !== s.id)); showToast('Eliminada', 'info'); }); }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {modal === 'create' && (
        <Modal title="Nueva tienda" onClose={() => setModal(null)}>
          <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Plataforma e-commerce *</label>
          <div className="platform-grid">
            {Object.entries(PLATFORMS).map(([k, p]) => (
              <button key={k} type="button" onClick={() => setForm(f => ({ ...f, platform: k }))} style={{ background: form.platform === k ? `${p.color}18` : G.black, border: `2px solid ${form.platform === k ? p.color : G.border}`, borderRadius: 10, padding: '10px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'all 0.2s' }}>
                <img src={p.logo} alt={p.name} className="platform-logo-img" style={{ borderRadius: 4, background: '#fff', padding: '2px 4px' }} />
                <div style={{ fontSize: 10, fontWeight: 700, color: form.platform === k ? p.color : G.muted }}>{p.name}</div>
              </button>
            ))}
          </div>
          <div className="form-group"><label className="form-label">Nombre *</label><input className="form-input" placeholder="Mi Tienda Online" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">URL *</label><input className="form-input" placeholder={PLATFORMS[form.platform]?.urlHint || 'mitienda.com'} value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} /><div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Sin https://</div></div>
          <div className="form-group"><label className="form-label">URL de exito (opcional)</label><input className="form-input" placeholder="https://mitienda.com/pages/gracias" value={form.successUrl} onChange={e => setForm(p => ({ ...p, successUrl: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Email de contacto (opcional)</label><input className="form-input" type="email" placeholder="info@mitienda.com" value={form.contactEmail} onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))} /></div>
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-gold" onClick={create}>Crear y configurar</button></div>
        </Modal>
      )}
      {modal === 'script' && sel && (
        <Modal title={`Configuracion -- ${sel.name}`} onClose={() => setModal(null)}>
          {/* Logo section */}
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Logo del checkout</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {sel.hasLogo !== false && (
                <img key={`${sel.id}-${logoVersion}`} src={sel.logoData || shopsApi.logoUrl(sel.id, logoVersion || sel.updatedAt || '')} alt="Logo"
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => setLogoLoaded(false)}
                  style={{ width: 60, height: 40, objectFit: 'contain', borderRadius: 6, border: `1px solid ${G.border}`, background: '#fff', padding: 4, display: (sel.logoData || logoLoaded) ? 'block' : 'none' }} />
              )}
              {!(sel.logoData || logoLoaded) && (
                <div style={{ width: 60, height: 40, borderRadius: 6, border: `1.5px dashed ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: G.muted }}>Sin logo</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: G.dim, marginBottom: 6 }}>Se mostrara en la cabecera del checkout. Max 2MB, formato PNG/JPG/SVG.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleLogoUpload(sel)} disabled={uploading}>{uploading ? 'Subiendo…' : ((sel.logoData || logoLoaded) ? 'Cambiar logo' : 'Subir logo')}</button>
                  {(sel.logoData || logoLoaded) && <button className="btn btn-danger btn-sm" onClick={() => removeLogo(sel)}>Quitar</button>}
                </div>
              </div>
            </div>
          </div>
          <PreorderSettingsCard key={sel.id} shop={sel} showToast={showToast} onSaved={(u) => { setSel(u); setShops(p => p.map(x => x.id === u.id ? { ...x, ...u } : x)); }} />
          {/* Apple Pay / Google Pay config */}
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Apple Pay / Google Pay</div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div onClick={() => { const v = sel.applePay !== false; shopsApi.update(sel.id, { applePay: !v }).then(() => { setSel(p => ({ ...p, applePay: !v })); setShops(p => p.map(x => x.id === sel.id ? { ...x, applePay: !v } : x)); }).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error')); }} style={{ width: 40, height: 22, borderRadius: 11, background: sel.applePay !== false ? G.green : G.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: sel.applePay !== false ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}> Apple Pay</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div onClick={() => { const v = sel.googlePay !== false; shopsApi.update(sel.id, { googlePay: !v }).then(() => { setSel(p => ({ ...p, googlePay: !v })); setShops(p => p.map(x => x.id === sel.id ? { ...x, googlePay: !v } : x)); }).catch(e => showToast('Error: ' + (e.response?.data?.error || e.message), 'error')); }} style={{ width: 40, height: 22, borderRadius: 11, background: sel.googlePay !== false ? G.green : G.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: sel.googlePay !== false ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}> Google Pay</span>
              </div>
            </div>
            <div style={{ background: `${G.goldDim}`, border: `1px solid ${G.borderGold}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G.goldLight, marginBottom: 6 }}>Configuracion necesaria:</div>
              <div style={{ fontSize: 11, color: G.dim, lineHeight: 1.7 }}>
                1. Entra en el panel de administracion de tu pasarela de pago<br/>
                2. Busca la seccion de <strong style={{ color: G.white }}>Apple Pay</strong>, <strong style={{ color: G.white }}>Digital Wallets</strong> o <strong style={{ color: G.white }}>Domain Verification</strong><br/>
                3. Registra el dominio <strong style={{ color: G.goldLight }}>app.zeroriskpay.com</strong> como dominio verificado para Apple Pay<br/>
                4. Sube el archivo de verificacion de Apple o usa la verificacion automatica que ofrezca tu pasarela<br/>
                5. Google Pay se activa automaticamente en la mayoria de pasarelas -- no requiere configuracion adicional
              </div>
            </div>
          </div>
          {/* Contact email config */}
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Email de contacto</div>
            <div style={{ fontSize: 12, color: G.dim, marginBottom: 10 }}>Este email aparecera en los emails de confirmacion, tracking y carrito abandonado para que el cliente final pueda contactar con la tienda.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                placeholder="info@mitienda.com"
                defaultValue={sel.contactEmail || ''}
                id="shop-contact-email"
                style={{ flex: 1, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
              />
              <button
                className="btn btn-gold btn-sm"
                onClick={() => {
                  const val = document.getElementById('shop-contact-email')?.value?.trim() || '';
                  shopsApi.update(sel.id, { contactEmail: val }).then(() => {
                    setSel(p => ({ ...p, contactEmail: val }));
                    setShops(p => p.map(x => x.id === sel.id ? { ...x, contactEmail: val } : x));
                    showToast(val ? 'Email de contacto guardado' : 'Email de contacto eliminado', 'success');
                  });
                }}
                style={{ padding: '9px 18px' }}
              >Guardar</button>
            </div>
          </div>
          <div style={{ background: '#16a34a10', border: '1px solid #16a34a40', borderRadius: 10, padding: 14, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>OK Una sola linea en Shopify -- instalacion en 2 minutos</div>
            <div style={{ fontSize: 12, color: G.muted }}>El script intercepta el boton de checkout y redirige a tu checkout de ZeroRiskPay automaticamente.</div>
          </div>
          <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: '14px 16px', fontFamily: 'DM Mono, monospace', fontSize: 12, color: G.green, lineHeight: 1.8, marginBottom: 8 }}>
            <div style={{ color: G.muted }}>{'<!-- ' + sel.name + ' -->'}</div>
            {/\.myshopify\.com$/i.test((sel.url || '').replace(/^(https?:\/\/)?(www\.)?/, '')) && (
              <div style={{ color: '#f59e0b' }}>{'<!-- FALTA: dominio propio de checkout — configura uno en Dominios personalizados antes de instalar. -->'}</div>
            )}
            <div>{`<script defer src="https://checkout.${/\.myshopify\.com$/i.test((sel.url || '').replace(/^(https?:\/\/)?(www\.)?/, '')) ? 'TU-DOMINIO-PROPIO.com' : (sel.url || '').replace(/^(https?:\/\/)?(www\.)?/, '')}/nlp.js?shop=${sel.url || sel.id}"></script>`}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}><CopyButton text={getScript(sel)} label=" Copiar script" /></div>
          <div style={{ fontWeight: 700, fontSize: 12, color: G.muted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Instrucciones para {PLATFORMS[sel.platform || 'shopify']?.name || 'tu plataforma'}:</div>
          {(PLATFORMS[sel.platform || 'shopify']?.steps || PLATFORMS.shopify.steps).map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.borderGold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: G.goldLight, flex: '0 0 22px' }}>{i + 1}</div>
              <div style={{ fontSize: 13, color: G.white, lineHeight: 1.5 }}>{s}</div>
            </div>
          ))}
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(null)}>Cerrar</button><CopyButton text={getScript(sel)} label=" Copiar script" /></div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// LEADS MANAGEMENT (admin only)
// -----------------------------------------------------------------------------

function Leads({ showToast }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, newThisWeek: 0, converted: 0, conversionRate: '0' });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [detail, setDetail] = useState(null);
  const token = localStorage.getItem('token');
  const hdr = { Authorization: `Bearer ${token}` };

  const planLabels = { bronce: 'Bronce', plata: 'Plata', oro: 'Oro', contacto: 'A medida', personalizado: 'Personalizado' };
  const volumeLabels = { '<10k': '< 10k €', '10k-50k': '10k–50k €', '50k-200k': '50k–200k €', '200k-1m': '200k–1M €', '>1m': '> 1M €', '>200k': '> 200k €' };
  const timelineLabels = { ya: 'Urgente', '1-2sem': '1-2 semanas', mes: 'Este mes', '3meses': '3 meses', explorando: 'Explorando' };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${BACKEND}/api/leads`, { headers: hdr })
      .then(r => { setLeads(r.data.leads || []); setStats(r.data.stats || {}); setLoading(false); })
      .catch(() => { setLeads([]); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND}/api/leads/${id}/status`, { status }, { headers: hdr });
      showToast(`Estado actualizado: ${status}`, 'success');
      load();
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const sendSequence = async (leadId) => {
    try {
      const r = await axios.post(`${BACKEND}/api/leads/send-sequence`, { leadId }, { headers: hdr });
      showToast(r.data.message || 'Email enviado', 'success');
      load();
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const exportCSV = () => {
    const rows = [['Email', 'Nombre', 'Teléfono', 'Empresa', 'Plan', 'Volumen', 'Cuándo', 'Comentarios', 'Fuente', 'Estado', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'Fecha']];
    filtered.forEach(l => rows.push([l.email, l.name || '', l.phone || '', l.company || '', l.plan || '', l.volume || '', l.timeline || '', l.message || '', l.source || '', l.status || '', l.utmSource || '', l.utmMedium || '', l.utmCampaign || '', l.createdAt || '']));
    const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast('CSV exportado', 'success');
  };

  const filtered = leads.filter(l => {
    const ms = statusFilter === 'ALL' || l.status === statusFilter;
    const sq = search?.toLowerCase() || '';
    const mq = !search || l.email?.toLowerCase().includes(sq) || l.name?.toLowerCase().includes(sq) || l.company?.toLowerCase().includes(sq);
    return ms && mq;
  });

  const statusColors = { NEW: { bg: G.blueDim, color: G.blue, border: G.blue + '40' }, CONTACTED: { bg: G.orangeDim, color: G.orange, border: G.orange + '40' }, CONVERTED: { bg: G.greenDim, color: G.green, border: G.green + '40' }, UNSUBSCRIBED: { bg: G.redDim, color: G.red, border: G.red + '40' } };
  const statusBadge = (status) => {
    const c = statusColors[status] || statusColors.NEW;
    return <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{status}</span>;
  };

  return (
    <div className="content">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: G.muted }}>Total leads</span>
            <span style={{ fontSize: 16 }}>@</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>{stats.total}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Todos los leads capturados</div>
        </div>
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: G.muted }}>Nuevos esta semana</span>
            <span style={{ fontSize: 16 }}>+</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: G.blue, fontFamily: 'DM Mono, monospace' }}>{stats.newThisWeek}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Ultimos 7 dias</div>
        </div>
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: G.muted }}>Tasa de conversion</span>
            <span style={{ fontSize: 16 }}>%</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>{stats.conversionRate}%</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{stats.converted} convertidos de {stats.total}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="search" style={{ flex: 1, minWidth: 250 }} placeholder="Buscar por email, nombre o empresa..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="pill-group">
          {['ALL', 'NEW', 'CONTACTED', 'CONVERTED', 'UNSUBSCRIBED'].map(s => (
            <button key={s} className={`pill ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s === 'ALL' ? 'Todos' : s === 'NEW' ? 'Nuevos' : s === 'CONTACTED' ? 'Contactados' : s === 'CONVERTED' ? 'Convertidos' : 'Baja'}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={exportCSV} style={{ fontSize: 11 }}>Exportar CSV</button>
      </div>

      <div className="card">
        {loading ? <div className="empty"><div className="empty-icon">@</div>Cargando leads...</div> :
          filtered.length === 0 ? <div className="empty"><div className="empty-icon">@</div>Sin leads todavia.</div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Email</th><th>Nombre</th><th>Teléfono</th><th>Plan</th><th>Empresa</th><th>Fuente</th><th>Estado</th><th>Fecha</th><th>Acciones</th></tr></thead>
                <tbody>
                  {filtered.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', color: G.goldLight }}>{l.email}</td>
                      <td style={{ fontSize: 12, fontWeight: 500 }}>{l.name || '--'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'DM Mono, monospace' }}>
                        {l.phone ? <a href={`tel:${l.phone}`} style={{ color: G.goldLight, textDecoration: 'none' }}>{l.phone}</a> : <span style={{ color: G.dim }}>--</span>}
                      </td>
                      <td>{l.plan ? <span className="tag" style={{ fontSize: 10, background: G.goldDim, color: G.goldLight, border: `1px solid ${G.gold}40` }}>{planLabels[l.plan] || l.plan}</span> : <span style={{ color: G.dim, fontSize: 11 }}>--</span>}</td>
                      <td style={{ fontSize: 11, color: G.dim, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.company}>{l.company || '--'}</td>
                      <td><span className="tag" style={{ fontSize: 10 }}>{l.source || 'landing'}</span></td>
                      <td>{statusBadge(l.status || 'NEW')}</td>
                      <td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap' }}>
                        {l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '--'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setDetail(l)} title="Ver datos completos">Ver</button>
                          <select
                            value={l.status || 'NEW'}
                            onChange={e => updateStatus(l.id, e.target.value)}
                            style={{ background: G.black, color: G.dim, border: `1px solid ${G.border}`, borderRadius: 6, padding: '4px 6px', fontSize: 10, cursor: 'pointer' }}
                          >
                            <option value="NEW">NEW</option>
                            <option value="CONTACTED">CONTACTED</option>
                            <option value="CONVERTED">CONVERTED</option>
                            <option value="UNSUBSCRIBED">UNSUBSCRIBED</option>
                          </select>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => sendSequence(l.id)} title="Enviar siguiente email de secuencia">Seq</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 14, padding: 28, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 11, color: G.muted, marginBottom: 4 }}>SOLICITUD #{detail.id}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.white }}>{detail.name || 'Sin nombre'}</div>
                <div style={{ fontSize: 12, color: G.goldLight, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>{detail.email}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'transparent', border: 'none', color: G.dim, fontSize: 22, cursor: 'pointer', padding: '0 6px' }}>×</button>
            </div>

            <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
              {detail.phone && <DetailRow k="Teléfono"><a href={`tel:${detail.phone}`} style={{ color: G.goldLight, textDecoration: 'none' }}>{detail.phone}</a></DetailRow>}
              {detail.company && <DetailRow k="Empresa / web">{detail.company}</DetailRow>}
              {detail.plan && <DetailRow k="Plan elegido"><span style={{ color: G.goldLight, fontWeight: 600 }}>{planLabels[detail.plan] || detail.plan}</span></DetailRow>}
              {detail.volume && <DetailRow k="Volumen">{volumeLabels[detail.volume] || detail.volume}</DetailRow>}
              {detail.timeline && <DetailRow k="Cuándo empieza">{timelineLabels[detail.timeline] || detail.timeline}</DetailRow>}
              <DetailRow k="Estado">{statusBadge(detail.status || 'NEW')}</DetailRow>
              <DetailRow k="Fuente">{detail.source || 'landing'}</DetailRow>
              {(detail.utmSource || detail.utmMedium || detail.utmCampaign) && (
                <DetailRow k="UTM">
                  <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace' }}>
                    {detail.utmSource ? `src=${detail.utmSource}` : null}
                    {detail.utmMedium ? ` · med=${detail.utmMedium}` : null}
                    {detail.utmCampaign ? ` · cmp=${detail.utmCampaign}` : null}
                  </div>
                </DetailRow>
              )}
              <DetailRow k="Recibido">{detail.createdAt ? new Date(detail.createdAt).toLocaleString('es-ES') : '--'}</DetailRow>
              {detail.message && (
                <div style={{ marginTop: 6, padding: '12px 14px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Comentarios</div>
                  <div style={{ fontSize: 13, color: G.white, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{detail.message}</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 22, justifyContent: 'flex-end' }}>
              {detail.phone && <a href={`tel:${detail.phone}`} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>Llamar</a>}
              <a href={`mailto:${detail.email}`} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>Email</a>
              <button className="btn btn-sm" style={{ fontSize: 12 }} onClick={() => setDetail(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: `1px solid ${G.border}` }}>
      <div style={{ flex: '0 0 130px', fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
      <div style={{ flex: 1, color: G.white }}>{children}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CLIENTS WITH PLANS (precios €499/€1499/€2499)
// -----------------------------------------------------------------------------

function Clients({ showToast }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [selTier, setSelTier] = useState('bronze');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [planForm, setPlanForm] = useState({ name: 'Plan Bronce', price: 499, delayDays: 30, interval: 'month', tier: 'bronze' });
  const token = localStorage.getItem('token');
  const hdr = { Authorization: `Bearer ${token}` };

  // Normalize plan: could be string "bronze", object {tier:"bronze",...}, or null
  const getPlan = (p) => {
    if (!p) return 'bronze';
    if (typeof p === 'string') return PLAN_TIERS[p] ? p : 'bronze';
    if (typeof p === 'object' && p.tier) return p.tier;
    return 'bronze';
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${BACKEND}/api/auth/users`, { headers: hdr })
      .then(r => { setClients(r.data || []); setLoading(false); })
      .catch(() => { setClients([]); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickTier = key => { setSelTier(key); setPlanForm(p => ({ ...p, name: PLAN_TIERS[key].name, price: PLAN_TIERS[key].price, tier: key })); };

  const createClient = async () => {
    if (!form.name || !form.email || !form.password) { showToast('Completa todos los campos', 'error'); return; }
    if (form.password.length < 8) { showToast('Contraseña mínimo 8 caracteres', 'error'); return; }
    try {
      await axios.post(`${BACKEND}/api/auth/register`, { ...form, plan: planForm.tier || 'bronze' }, { headers: hdr });
      showToast(`Cliente creado OK -- puede iniciar sesión con ${form.email}`, 'success');
      setModal(null); setForm({ name: '', email: '', password: '' }); setSelTier('bronze');
      setPlanForm({ name: 'Plan Bronce', price: 499, delayDays: 30, interval: 'month', tier: 'bronze' }); load();
    } catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const savePlan = async () => {
    try { await axios.put(`${BACKEND}/api/auth/users/${sel.id}/plan`, { plan: planForm.tier || 'bronze' }, { headers: hdr }); showToast('Plan actualizado OK', 'success'); setModal(null); load(); }
    catch (e) { showToast(e.response?.data?.error || e.message, 'error'); }
  };

  const tierBadge = tier => {
    const t = PLAN_TIERS[tier || 'bronze'];
    return <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{t.emoji} {t.name}</span>;
  };

  const TierSelector = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 10 }}>
        {Object.entries(PLAN_TIERS).map(([k, t]) => (
          <button key={k} type="button" onClick={() => pickTier(k)} style={{ background: selTier === k ? t.bg : G.black, border: `2px solid ${selTier === k ? t.border : G.border}`, borderRadius: 10, padding: '14px 10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, transition: 'all 0.2s' }}>
            <div style={{ fontSize: 22 }}>{t.emoji}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: selTier === k ? t.color : G.muted }}>{t.name}</div>
            <div style={{ fontSize: 10, color: G.muted, textAlign: 'center' }}>{t.billing}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: selTier === k ? t.color : G.dim }}>{fmt(t.price, { decimals: 0 })}/mes</div>
          </button>
        ))}
      </div>
      {selTier && (
        <div style={{ background: PLAN_TIERS[selTier].bg, border: `1px solid ${PLAN_TIERS[selTier].border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          {PLAN_TIERS[selTier].features.map((f, i) => <div key={i} style={{ fontSize: 11, color: PLAN_TIERS[selTier].color, lineHeight: 1.8 }}>OK {f}</div>)}
        </div>
      )}
    </>
  );

  return (
    <div className="content">
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        {[{ l: 'Total clientes', v: clients.length }, { l: '🥉 Bronce', v: clients.filter(c => getPlan(c.plan) === 'bronze').length }, { l: '🥈 Plata', v: clients.filter(c => getPlan(c.plan) === 'silver').length }, { l: '🥇 Oro', v: clients.filter(c => getPlan(c.plan) === 'gold').length }].map((k, i) => (
          <div key={i} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{ fontSize: 26 }}>{k.v}</div></div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-gold" onClick={() => setModal('create')}>+ Nuevo cliente</button>
      </div>
      <div className="card">
        {loading ? <div className="empty"><div className="empty-icon">◌</div>Cargando...</div> :
          clients.length === 0 ? <div className="empty"><div className="empty-icon">👥</div>Sin clientes todavía.</div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Cliente</th><th>Email</th><th>Plan</th><th>{fmtSymbol()}/mes</th><th>Alta</th><th>Acciones</th></tr></thead>
                <tbody>
                  {clients.map(c => (
                    <tr key={c.id}>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.borderGold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: G.goldLight }}>{c.name?.[0]?.toUpperCase()}</div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</span>
                      </div></td>
                      <td style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace' }}>{c.email}</td>
                      <td>{tierBadge(getPlan(c.plan))}</td>
                      <td style={{ fontWeight: 700, color: G.goldLight }}>{fmt(PLAN_TIERS[getPlan(c.plan)].price, { decimals: 0 })}</td>
                      <td style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '--'}</td>
                      <td><div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setSel(c); const t = getPlan(c.plan); setSelTier(t); setPlanForm({ name: PLAN_TIERS[t]?.name || 'Plan Bronce', price: PLAN_TIERS[t]?.price || 499, delayDays: 30, interval: 'month', tier: t }); setModal('plan'); }}>✏ Plan</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { if (!window.confirm('¿Eliminar cliente?')) return; axios.delete(`${BACKEND}/api/auth/users/${c.id}`, { headers: hdr }).then(() => { showToast('Eliminado', 'info'); load(); }).catch(e => showToast(e.message, 'error')); }}>✕</button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal === 'create' && (
        <Modal title="Nuevo cliente" onClose={() => setModal(null)}>
          <div className="form-group"><label className="form-label">Nombre *</label><input className="form-input" placeholder="Juan García" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Email *</label><input type="email" className="form-input" placeholder="juan@empresa.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Contraseña * (mín. 8 caracteres)</label><input type="password" className="form-input" placeholder="--------" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} /></div>
          <label className="form-label" style={{ display: 'block', marginBottom: 10 }}>Plan según facturación *</label>
          <TierSelector />
          <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.goldLight, marginBottom: 10, textTransform: 'uppercase' }}>↺ Suscripción automática ZeroRiskPay</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{fmtSymbol()}/mes</label><input type="number" className="form-input" value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: +e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Días delay</label><input type="number" className="form-input" value={planForm.delayDays} onChange={e => setPlanForm(p => ({ ...p, delayDays: +e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Cada</label>
                <select className="form-input" value={planForm.interval} onChange={e => setPlanForm(p => ({ ...p, interval: e.target.value }))}>
                  <option value="month">Mes</option><option value="year">Año</option><option value="week">Semana</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-gold" onClick={createClient}>Crear cliente</button></div>
        </Modal>
      )}
      {modal === 'plan' && sel && (
        <Modal title={`Plan -- ${sel.name}`} onClose={() => setModal(null)}>
          <TierSelector />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">€/mes</label><input type="number" className="form-input" value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: +e.target.value }))} /></div>
            <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Días delay</label><input type="number" className="form-input" value={planForm.delayDays} onChange={e => setPlanForm(p => ({ ...p, delayDays: +e.target.value }))} /></div>
            <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Intervalo</label>
              <select className="form-input" value={planForm.interval} onChange={e => setPlanForm(p => ({ ...p, interval: e.target.value }))}>
                <option value="month">Mensual</option><option value="year">Anual</option><option value="week">Semanal</option>
              </select>
            </div>
          </div>
          <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: G.goldLight }}>
            {sel.name} -> paga producto -> a los {planForm.delayDays}d se le cobra {fmt(Number(planForm.price), { decimals: 0 })} -> cada {planForm.interval === 'month' ? 'mes' : planForm.interval === 'year' ? 'año' : 'semana'}
          </div>
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-gold" onClick={savePlan}>Guardar plan</button></div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PAYMENT LINKS -- nueva feature
// -----------------------------------------------------------------------------

function PaymentLinks({ showToast }) {
  const [links, setLinks] = useState(() => { try { return JSON.parse(localStorage.getItem('nlp_paylinks') || '[]'); } catch { return []; } });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', currency: 'EUR', expiresIn: '24' });
  const [copied, setCopied] = useState(null);

  const save = updated => { setLinks(updated); localStorage.setItem('nlp_paylinks', JSON.stringify(updated)); };

  const create = () => {
    if (!form.description || !form.amount) { showToast('Descripción e importe obligatorios', 'error'); return; }
    const id = Math.random().toString(36).substring(2, 10).toUpperCase();
    const link = {
      id, description: form.description, amount: parseFloat(form.amount), currency: form.currency,
      // 2026-06-07 — Build the public checkout URL by swapping the
      // `app.` subdomain for `checkout.`. Works on zeroriskpay.com
      // (app.zeroriskpay.com → checkout.zeroriskpay.com) and on any
      // tenant vanity subdomain that follows the same convention.
      url: `${window.location.origin.replace('://app.', '://checkout.')}/checkout.html?linkId=${id}&amount=${Math.round(parseFloat(form.amount) * 100)}&desc=${encodeURIComponent(form.description)}`,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + parseInt(form.expiresIn) * 3600000).toISOString(),
      status: 'ACTIVE', views: 0, paid: false,
    };
    save([link, ...links]);
    setModal(false); setForm({ description: '', amount: '', currency: 'EUR', expiresIn: '24' });
    navigator.clipboard.writeText(link.url);
    showToast('Link creado y copiado al portapapeles OK', 'success');
  };

  const copyLink = (url, id) => { navigator.clipboard.writeText(url); setCopied(id); setTimeout(() => setCopied(null), 2000); };
  const shareWhatsApp = url => window.open(`https://wa.me/?text=${encodeURIComponent('Paga aquí de forma segura: ' + url)}`, '_blank');
  const shareEmail = (url, desc) => window.open(`mailto:?subject=${encodeURIComponent('Pago: ' + desc)}&body=${encodeURIComponent('Haz clic aquí para pagar de forma segura:\n\n' + url)}`, '_blank');
  const deleteLink = id => { if (!window.confirm('¿Eliminar este link?')) return; save(links.filter(l => l.id !== id)); showToast('Eliminado', 'info'); };

  return (
    <div className="content">
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        {[{ l: 'Links activos', v: links.filter(l => l.status === 'ACTIVE').length }, { l: 'Cobrados', v: links.filter(l => l.paid).length }, { l: 'Total generado', v: fmt(links.filter(l => l.paid).reduce((s, l) => s + l.amount, 0)) }].map((k, i) => (
          <div key={i} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{ fontSize: 26 }}>{k.v}</div></div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-gold" onClick={() => setModal(true)}>+ Crear Payment Link</button>
      </div>

      {links.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">🔗</div>
            <div style={{ fontSize: 14, color: G.white, marginBottom: 6 }}>Sin payment links todavía</div>
            <div style={{ fontSize: 13, color: G.muted }}>Crea un link en 1 clic y compártelo por WhatsApp, email o SMS</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {links.map(link => {
            const expired = new Date(link.expiresAt) < new Date();
            return (
              <div key={link.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: link.paid ? '#16a34a20' : expired ? G.border : G.goldDim, border: `1px solid ${link.paid ? '#16a34a40' : expired ? G.border : G.borderGold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flex: '0 0 44px' }}>
                    {link.paid ? '✅' : expired ? '⏰' : '🔗'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{link.description}</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: G.goldLight }}>{fmt(link.amount)}</span>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: link.paid ? '#16a34a20' : expired ? G.border : G.goldDim, color: link.paid ? '#16a34a' : expired ? G.muted : G.goldLight, fontWeight: 700 }}>
                        {link.paid ? 'COBRADO' : expired ? 'EXPIRADO' : 'ACTIVO'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                      ID: {link.id} · Creado {new Date(link.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ })} · Expira {new Date(link.expiresAt).toLocaleString('es-ES', { timeZone: APP_TZ })}
                    </div>
                    <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.url}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => copyLink(link.url, link.id)} style={{ color: copied === link.id ? G.green : G.dim }}>
                      {copied === link.id ? 'OK' : ''}
                    </button>
                    <button className="btn btn-ghost btn-sm" title="WhatsApp" onClick={() => shareWhatsApp(link.url)}>💬</button>
                    <button className="btn btn-ghost btn-sm" title="Email" onClick={() => shareEmail(link.url, link.description)}>✉️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteLink(link.id)}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <Modal title="Crear Payment Link" onClose={() => setModal(false)}>
          <div className="form-group"><label className="form-label">Descripción *</label><input className="form-input" placeholder="ej: Factura #123 -- Diseño web" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Importe *</label><input type="number" step="0.01" className="form-input" placeholder="99.99" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Moneda</label>
              <select className="form-input" value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
                <option value="EUR">EUR €</option><option value="USD">USD $</option><option value="GBP">GBP £</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Expira en</label>
            <select className="form-input" value={form.expiresIn} onChange={e => setForm(p => ({ ...p, expiresIn: e.target.value }))}>
              <option value="1">1 hora</option><option value="24">24 horas</option><option value="72">3 días</option><option value="168">7 días</option><option value="720">30 días</option>
            </select>
          </div>
          {form.amount && <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 10, padding: 14, fontSize: 12, color: G.goldLight }}>
            El link generado permite cobrar <strong>{fmt(form.amount || 0)}</strong> por <strong>{form.description || '...'}</strong>. Se copiará automáticamente al crearlo.
          </div>}
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button><button className="btn btn-gold" onClick={create}>Crear y copiar link</button></div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RETRY DASHBOARD -- nueva feature
// -----------------------------------------------------------------------------

function RetryDashboard({ showToast }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Recovery rows from /api/beneficios/rebill-rate — enriched with
  // originalPurchaseAt, recoveredAt, attemptsToday, priorAttempts.
  // Refreshes whenever the operator changes the range pill.
  const [recoveryRows, setRecoveryRows] = useState([]);

  useEffect(() => {
    setLoading(true);
    paymentsApi.getAll()
      .then(r => { setPayments((r.payments || r || [])); setLoading(false); })
      .catch(() => { setPayments([]); setLoading(false); });
  }, []);

  useEffect(() => {
    // Map the range pill to windowDays for the beneficios endpoint.
    const days = range === 'today' ? 1
      : range === 'yesterday' ? 2
      : range === '7d' ? 7
      : range === '30d' ? 30
      : range === '90d' ? 90
      : 1;
    beneficiosApi.rebillRate({ windowDays: days })
      .then(r => setRecoveryRows((r && r.recovery && Array.isArray(r.recovery.rows)) ? r.recovery.rows : []))
      .catch(() => setRecoveryRows([]));
  }, [range, customFrom, customTo]);

  // Normalise a payment amount to EUR units (not cents).
  // amountInEUR is already in EUR units, amount is in cents.
  const amountEUR = (p) => {
    if (p.amountInEUR != null && p.amountInEUR !== '') return parseFloat(p.amountInEUR) || 0;
    return (parseFloat(p.amount) || 0) / 100;
  };

  // Date filter — Madrid timezone (Europe/Madrid), 00:00 cutoff
  const madridToday = () => new Date().toLocaleDateString('sv-SE', { timeZone: APP_TZ });
  const madridDay = (iso) => { try { return new Date(iso).toLocaleDateString('sv-SE', { timeZone: APP_TZ }); } catch { return ''; } };

  const getFiltered = () => {
    const todayStr = madridToday();
    let from, to;
    if (range === 'today') { from = todayStr; to = todayStr; }
    else if (range === 'yesterday') {
      const y = new Date(); y.setDate(y.getDate() - 1);
      from = y.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); to = from;
    }
    else if (range === '7d') { const d = new Date(); d.setDate(d.getDate() - 7); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); to = todayStr; }
    else if (range === '30d') { const d = new Date(); d.setDate(d.getDate() - 30); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); to = todayStr; }
    else if (range === '90d') { const d = new Date(); d.setDate(d.getDate() - 90); from = d.toLocaleDateString('sv-SE', { timeZone: APP_TZ }); to = todayStr; }
    else if (range === 'custom') { from = customFrom; to = customTo || customFrom || todayStr; }
    else return payments;
    return payments.filter(p => {
      if (!p.createdAt) return false;
      const d = madridDay(p.createdAt);
      return (!from || d >= from) && (!to || d <= to);
    });
  };

  const all = getFiltered();
  const failed = all.filter(p => p.status === 'FAILED');
  const recovered = all.filter(p => p.status === 'SUCCEEDED' && (p.attempts || 1) > 1);
  const recoveryRate = failed.length + recovered.length > 0 ? ((recovered.length / (failed.length + recovered.length)) * 100).toFixed(1) : '0';
  const revenueRecovered = recovered.reduce((s, p) => s + amountEUR(p), 0);
  const revenueFailed = failed.reduce((s, p) => s + amountEUR(p), 0);

  const byGateway = {};
  all.forEach(p => {
    const gw = p.gateway || p.psp || 'Unknown';
    if (!byGateway[gw]) byGateway[gw] = { total: 0, succeeded: 0, failed: 0, recovered: 0 };
    byGateway[gw].total++;
    if (p.status === 'SUCCEEDED') { byGateway[gw].succeeded++; if ((p.attempts || 1) > 1) byGateway[gw].recovered++; }
    if (p.status === 'FAILED') byGateway[gw].failed++;
  });

  return (
    <div className="content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[['today','Hoy'],['yesterday','Ayer'],['7d','7 dias'],['30d','30 dias'],['90d','90 dias'],['custom','Personalizado']].map(([k, v]) => (
          <button key={k} className={`pill ${range === k ? 'active' : ''}`} onClick={() => setRange(k)}>{v}</button>
        ))}
      </div>
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 8, padding: '6px 10px', color: G.white, fontSize: 12 }} />
          <span style={{ color: G.dim, fontSize: 12 }}>hasta</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 8, padding: '6px 10px', color: G.white, fontSize: 12 }} />
        </div>
      )}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 24 }}>
        {[
          { l: 'Pagos fallidos', v: failed.length, sub: `${fmt(revenueFailed)} en riesgo`, color: G.red, icon: '✕' },
          { l: 'Recuperados', v: recovered.length, sub: `${recovered.length} pagos`, color: G.green, icon: '↺' },
          { l: 'Tasa recuperación', v: `${recoveryRate}%`, sub: `${failed.length + recovered.length} intentos`, color: G.goldLight, icon: '📈' },
          { l: 'Revenue recuperado', v: fmt(revenueRecovered), sub: 'rescatado', color: G.green, icon: '💰' },
        ].map((k, i) => (
          <div key={i} className="kpi-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="kpi-label">{k.l}</div>
              <span style={{ fontSize: 18 }}>{k.icon}</span>
            </div>
            <div className="kpi-value" style={{ fontSize: 28, color: k.color }}>{k.v}</div>
            {k.sub && <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {recovered.length > 0 && (
        <div style={{ background: '#16a34a10', border: '1px solid #16a34a40', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 36 }}>💚</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>El sistema recuperó {recovered.length} pago{recovered.length > 1 ? 's' : ''} que habrían fallado</div>
            <div style={{ fontSize: 13, color: G.dim }}>Cuando una pasarela falla, el orquestador reintenta automáticamente con otra. <strong style={{ color: G.white }}>{fmt(revenueRecovered)}</strong> en revenue que no se perdió.</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* By gateway */}
        <div className="card">
          <div className="card-head"><span className="card-title">Rendimiento por pasarela</span></div>
          <div className="card-body">
            {Object.keys(byGateway).length === 0 ? (
              <div className="empty"><div className="empty-icon" style={{ fontSize: 24 }}>⬡</div>Sin datos aún</div>
            ) : Object.entries(byGateway).map(([gw, stats], i) => {
              const rate = stats.total > 0 ? (stats.succeeded / stats.total * 100).toFixed(0) : 0;
              return (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{gw}</span>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 11, color: G.green }}>OK {stats.succeeded}</span>
                      <span style={{ fontSize: 11, color: G.red }}>✕ {stats.failed}</span>
                      {stats.recovered > 0 && <span style={{ fontSize: 11, color: G.goldLight }}>↺ {stats.recovered}</span>}
                      <span style={{ fontSize: 11, fontWeight: 700, color: parseInt(rate) >= 80 ? G.green : parseInt(rate) >= 50 ? G.goldLight : G.red }}>{rate}%</span>
                    </div>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: `${rate}%`, background: parseInt(rate) >= 80 ? G.green : parseInt(rate) >= 50 ? G.gold : G.red }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent failures */}
        <div className="card">
          <div className="card-head"><span className="card-title">Últimos fallos y recuperaciones</span></div>
          <div className="card-body">
            {loading ? <div className="empty"><div className="empty-icon">◌</div>Cargando...</div> :
              all.filter(p => p.status === 'FAILED' || (p.attempts || 1) > 1).slice(0, 8).length === 0 ? (
                <div className="empty"><div className="empty-icon" style={{ fontSize: 24 }}>✅</div>Sin fallos recientes</div>
              ) : all.filter(p => p.status === 'FAILED' || (p.attempts || 1) > 1).slice(0, 8).map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${G.border}20` }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>{p.status === 'SUCCEEDED' ? '↺' : '✕'}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: p.status === 'SUCCEEDED' ? G.green : G.red }}>{p.status === 'SUCCEEDED' ? 'Recuperado' : 'Fallido'}</span>
                      <span style={{ fontSize: 11, color: G.muted }}>via {p.gateway || 'N/A'}</span>
                    </div>
                    {(p.attempts || 1) > 1 && <div style={{ fontSize: 10, color: G.goldLight, fontFamily: 'DM Mono, monospace' }}>Intento {p.attempts}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: G.white }}>{fmt(amountEUR(p))}</div>
                    <div style={{ fontSize: 10, color: G.muted }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : ''}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* === RECOVERIES TABLE (per-RC retry detail) === */}
      {recoveryRows.length > 0 && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 20, marginTop: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: G.white, marginBottom: 4 }}>
            📋 Retries del periodo ({recoveryRows.filter(r => r.status === 'SUCCEEDED').length} recuperados / {recoveryRows.length} totales)
          </div>
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 14 }}>
            Cada fila: 1 RC con intentos previos al día. Recuperados arriba.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Estado</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Cliente</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>$</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Compró</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Cobro hoy</th>
                  <th style={{ textAlign: 'center', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Intentos<br/>(prev/hoy/tot)</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', color: G.muted, fontWeight: 600 }}>Razón fail</th>
                </tr>
              </thead>
              <tbody>
                {recoveryRows.map((r, i) => {
                  const isOk = r.status === 'SUCCEEDED';
                  const fmtTs = (iso) => {
                    if (!iso) return '—';
                    try {
                      const d = new Date(iso);
                      return d.toLocaleString('es-ES', { timeZone: APP_TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                    } catch { return '—'; }
                  };
                  return (
                    <tr key={r.rcId || i} style={{ borderBottom: `1px solid ${G.border}` }}>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          background: isOk ? (G.green + '22') : (G.red + '22'),
                          color: isOk ? G.green : G.red,
                          padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                        }}>
                          {isOk ? '✓ OK' : '✗ FAIL'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', color: G.white, fontSize: 11 }}>{r.email}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.white, fontFamily: 'DM Mono, monospace' }}>
                        ${((r.amount || 0) / 100).toFixed(2)}
                      </td>
                      <td style={{ padding: '8px 10px', color: G.muted, fontSize: 11 }}>{fmtTs(r.originalPurchaseAt)}</td>
                      <td style={{ padding: '8px 10px', color: isOk ? G.green : G.muted, fontSize: 11 }}>
                        {fmtTs(r.recoveredAt || r.firstAttemptTodayAt)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                        {r.priorAttempts}/{r.attemptsToday}/<span style={{ color: G.white }}>{r.totalAttempts}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: G.muted, fontSize: 10, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {!isOk ? (r.lastDeclineDetail || '—').slice(0, 80) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RULE BUILDER -- nueva feature (Constructor visual de reglas)
// -----------------------------------------------------------------------------

function RuleBuilder({ showToast }) {
  const [rules, setRules] = useState(() => { try { return JSON.parse(localStorage.getItem('nlp_rules') || '[]'); } catch { return []; } });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', conditionField: 'amount', conditionOp: 'gt', conditionValue: '', action: 'STRIPE', priority: 1 });
  const [gateways, setGateways] = useState([]);

  useEffect(() => { gatewaysApi.getAll().then(setGateways).catch(() => setGateways([])); }, []);

  const save = updated => { setRules(updated); localStorage.setItem('nlp_rules', JSON.stringify(updated)); };
  const create = () => {
    if (!form.name || !form.conditionValue) { showToast('Nombre y valor de condición obligatorios', 'error'); return; }
    const rule = { id: Date.now().toString(), ...form, active: true, createdAt: new Date().toISOString() };
    save([...rules, rule].sort((a, b) => a.priority - b.priority));
    setModal(false); setForm({ name: '', conditionField: 'amount', conditionOp: 'gt', conditionValue: '', action: 'STRIPE', priority: 1 });
    showToast('Regla creada OK', 'success');
  };
  const toggleRule = id => save(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));
  const deleteRule = id => { if (!window.confirm('¿Eliminar regla?')) return; save(rules.filter(r => r.id !== id)); };

  const FIELDS = { amount: `Importe (${fmtSymbol()})`, currency: 'Moneda', country: 'País', email: 'Email cliente' };
  const OPS = { gt: 'mayor que', lt: 'menor que', eq: 'igual a', contains: 'contiene' };
  const ACTIONS = { ...Object.fromEntries(gateways.map(g => [g.psp || g.name, g.name])), FAILOVER: 'Failover automático', BLOCK: 'Bloquear pago' };

  const describeRule = r => `SI ${FIELDS[r.conditionField] || r.conditionField} ${OPS[r.conditionOp] || r.conditionOp} "${r.conditionValue}" -> USAR ${ACTIONS[r.action] || r.action}`;

  return (
    <div className="content">
      <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 32 }}>🎛</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: G.goldLight, marginBottom: 4 }}>Constructor visual de reglas de routing</div>
          <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6 }}>Crea reglas sin código para decidir qué pasarela usa cada pago según el importe, país, email o moneda. Como BridgerPay Router.</div>
        </div>
        <button className="btn btn-gold" style={{ flex: '0 0 auto' }} onClick={() => setModal(true)}>+ Nueva regla</button>
      </div>

      {rules.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">🎛</div>
            <div style={{ fontSize: 14, color: G.white, marginBottom: 6 }}>Sin reglas configuradas</div>
            <div style={{ fontSize: 13, color: G.muted, textAlign: 'center', lineHeight: 1.7 }}>
              Ejemplo: "Si importe {'>'} {fmt(500, { decimals: 0 })} -> usar Stripe"<br />
              "Si país = UK -> usar Square"
            </div>
            <button className="btn btn-gold" style={{ marginTop: 16 }} onClick={() => setModal(true)}>Crear primera regla</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rules.map((rule, i) => (
            <div key={rule.id} style={{ background: G.card, border: `1px solid ${rule.active ? G.borderGold : G.border}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, opacity: rule.active ? 1 : 0.5 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: rule.active ? G.goldDim : G.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: rule.active ? G.goldLight : G.muted, flex: '0 0 32px' }}>#{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: G.white, marginBottom: 3 }}>{rule.name}</div>
                <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace' }}>
                  <span style={{ color: G.muted }}>SI</span> <span style={{ color: G.goldLight }}>{FIELDS[rule.conditionField] || rule.conditionField}</span>
                  <span style={{ color: G.muted }}> {OPS[rule.conditionOp] || rule.conditionOp} </span>
                  <span style={{ color: G.white, fontWeight: 700 }}>"{rule.conditionValue}"</span>
                  <span style={{ color: G.muted }}> -> USAR </span>
                  <span style={{ color: G.green, fontWeight: 700 }}>{ACTIONS[rule.action] || rule.action}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                <button className={`btn btn-sm ${rule.active ? 'btn-ghost' : 'btn-gold'}`} onClick={() => toggleRule(rule.id)}>{rule.active ? 'Pausar' : 'Activar'}</button>
                <button className="btn btn-danger btn-sm" onClick={() => deleteRule(rule.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title="Nueva regla de routing" onClose={() => setModal(false)}>
          <div className="form-group"><label className="form-label">Nombre de la regla *</label><input className="form-input" placeholder="ej: Pagos grandes a Stripe" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: 'uppercase', marginBottom: 12 }}>Condición (SI...)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Campo</label>
                <select className="form-input" value={form.conditionField} onChange={e => setForm(p => ({ ...p, conditionField: e.target.value }))}>
                  {Object.entries(FIELDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Operación</label>
                <select className="form-input" value={form.conditionOp} onChange={e => setForm(p => ({ ...p, conditionOp: e.target.value }))}>
                  {Object.entries(OPS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Valor</label>
                <input className="form-input" placeholder={form.conditionField === 'amount' ? '500' : form.conditionField === 'country' ? 'ES' : 'valor'} value={form.conditionValue} onChange={e => setForm(p => ({ ...p, conditionValue: e.target.value }))} />
              </div>
            </div>
          </div>
          <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: 'uppercase', marginBottom: 12 }}>Acción (ENTONCES usar...)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Pasarela / Acción</label>
                <select className="form-input" value={form.action} onChange={e => setForm(p => ({ ...p, action: e.target.value }))}>
                  {gateways.map(g => <option key={g.id} value={g.psp || g.name}>{g.name}</option>)}
                  <option value="FAILOVER">Failover automático</option>
                  <option value="BLOCK">Bloquear pago</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Prioridad (1 = primero)</label>
                <input type="number" className="form-input" min={1} max={100} value={form.priority} onChange={e => setForm(p => ({ ...p, priority: +e.target.value }))} />
              </div>
            </div>
          </div>
          {form.conditionValue && (
            <div style={{ background: G.goldDim, border: `1px solid ${G.borderGold}`, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: G.goldLight, fontFamily: 'DM Mono, monospace' }}>
              <strong>Vista previa:</strong> SI {FIELDS[form.conditionField]} {OPS[form.conditionOp]} "{form.conditionValue}" -> {form.action}
            </div>
          )}
          <div className="modal-actions"><button className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button><button className="btn btn-gold" onClick={create}>Crear regla</button></div>
        </Modal>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RECONCILIATION -- nueva feature
// -----------------------------------------------------------------------------

function Reconciliation({ showToast }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [dateRange, setDateRange] = useState('today');

  const generate = async () => {
    setLoading(true);
    try {
      const data = await paymentsApi.getAll();
      const all = data.payments || data || [];
      setPayments(all);

      const now = new Date();
      let startDate;
      if (dateRange === 'today') startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      else if (dateRange === 'week') startDate = new Date(now.getTime() - 7 * 86400000);
      else startDate = new Date(now.getFullYear(), now.getMonth(), 1);

      const filtered = all.filter(p => new Date(p.createdAt) >= startDate);
      const succeeded = filtered.filter(p => p.status === 'SUCCEEDED');
      const failed = filtered.filter(p => p.status === 'FAILED');
      const refunded = filtered.filter(p => p.status === 'REFUNDED');
      const recovered = succeeded.filter(p => (p.attempts || 1) > 1);

      const byGateway = {};
      filtered.forEach(p => {
        const gw = p.gateway || p.psp || 'Sin pasarela';
        if (!byGateway[gw]) byGateway[gw] = { total: 0, succeeded: 0, failed: 0, refunded: 0, revenue: 0, recovered: 0 };
        byGateway[gw].total++;
        if (p.status === 'SUCCEEDED') { byGateway[gw].succeeded++; byGateway[gw].revenue += p.amountInEUR || p.amount || 0; if ((p.attempts || 1) > 1) byGateway[gw].recovered++; }
        if (p.status === 'FAILED') byGateway[gw].failed++;
        if (p.status === 'REFUNDED') byGateway[gw].refunded++;
      });

      const totalRevenue = succeeded.reduce((s, p) => s + (p.amountInEUR || p.amount || 0), 0);
      const refundedAmount = refunded.reduce((s, p) => s + (p.amountInEUR || p.amount || 0), 0);
      const discrepancies = [];

      Object.entries(byGateway).forEach(([gw, stats]) => {
        const rate = stats.total > 0 ? (stats.succeeded / stats.total * 100) : 0;
        if (rate < 70 && stats.total >= 5) discrepancies.push({ type: 'warning', msg: `${gw} tiene una tasa de éxito baja: ${rate.toFixed(1)}% (${stats.succeeded}/${stats.total} pagos)` });
        if (stats.refunded > stats.succeeded * 0.1) discrepancies.push({ type: 'error', msg: `${gw} tiene alto ratio de reembolsos: ${stats.refunded} de ${stats.succeeded} pagos` });
      });

      setReport({ date: now.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: APP_TZ }), dateRange, total: filtered.length, succeeded: succeeded.length, failed: failed.length, refunded: refunded.length, recovered: recovered.length, totalRevenue: totalRevenue.toFixed(2), refundedAmount: refundedAmount.toFixed(2), netRevenue: (totalRevenue - refundedAmount).toFixed(2), conversionRate: filtered.length > 0 ? (succeeded.length / filtered.length * 100).toFixed(1) : '0', byGateway, discrepancies });
      showToast('Reconciliación generada OK', 'success');
    } catch (e) { showToast(e.message, 'error'); }
    setLoading(false);
  };

  const exportCSV = () => {
    if (!report) return;
    const rows = [['Pasarela', 'Total', 'Exitosos', 'Fallidos', 'Reembolsados', 'Recuperados', `Revenue ${fmtSymbol()}`, 'Tasa %'],
      ...Object.entries(report.byGateway).map(([gw, s]) => [gw, s.total, s.succeeded, s.failed, s.refunded, s.recovered, s.revenue.toFixed(2), (s.total > 0 ? (s.succeeded / s.total * 100).toFixed(1) : 0)])];
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = `reconciliacion_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  return (
    <div className="content">
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head"><span className="card-title">🔍 Reconciliación automática</span></div>
        <div className="card-body">
          <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.7, marginBottom: 16 }}>
            Compara automáticamente todos los pagos por pasarela, detecta discrepancias y genera un informe completo listo para contabilidad.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 150 }}>
              <label className="form-label">Periodo</label>
              <select className="form-input" value={dateRange} onChange={e => setDateRange(e.target.value)}>
                <option value="today">Hoy</option>
                <option value="week">Últimos 7 días</option>
                <option value="month">Este mes</option>
              </select>
            </div>
            <button className="btn btn-gold" style={{ marginTop: 16 }} disabled={loading} onClick={generate}>
              {loading ? '⏳ Generando...' : '🔍 Generar reconciliación'}
            </button>
            {report && <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={exportCSV}>↓ Exportar CSV</button>}
          </div>
        </div>
      </div>

      {report && (
        <>
          {report.discrepancies.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: G.red, marginBottom: 10 }}>⚠️ Discrepancias detectadas ({report.discrepancies.length})</div>
              {report.discrepancies.map((d, i) => (
                <div key={i} style={{ background: d.type === 'error' ? '#ef444415' : '#ea580c15', border: `1px solid ${d.type === 'error' ? '#ef444440' : '#ea580c40'}`, borderRadius: 8, padding: 12, marginBottom: 8, fontSize: 12, color: d.type === 'error' ? '#ef4444' : '#ea580c' }}>
                  {d.type === 'error' ? '🔴' : '🟡'} {d.msg}
                </div>
              ))}
            </div>
          )}
          {report.discrepancies.length === 0 && (
            <div style={{ background: '#16a34a10', border: '1px solid #16a34a40', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: '#16a34a' }}>
              ✅ Sin discrepancias detectadas -- todos los datos cuadran correctamente
            </div>
          )}

          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
            {[{ l: 'Revenue bruto', v: fmt(report.totalRevenue), c: G.green }, { l: 'Reembolsado', v: fmt(report.refundedAmount), c: G.red }, { l: 'Revenue neto', v: fmt(report.netRevenue), c: G.goldLight }, { l: 'Conversión', v: `${report.conversionRate}%`, c: G.white }].map((k, i) => (
              <div key={i} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{ fontSize: 22, color: k.c }}>{k.v}</div></div>
            ))}
          </div>

          <div className="card">
            <div className="card-head">
              <span className="card-title">Desglose por pasarela -- {report.date}</span>
              <span style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>{report.dateRange === 'today' ? 'Hoy' : report.dateRange === 'week' ? 'Últimos 7 días' : 'Este mes'}</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Pasarela</th><th>Total</th><th>OK Exitosos</th><th>✕ Fallidos</th><th>↩ Reembols.</th><th>↺ Recup.</th><th>Revenue</th><th>Tasa</th></tr></thead>
                <tbody>
                  {Object.entries(report.byGateway).map(([gw, s], i) => {
                    const rate = s.total > 0 ? (s.succeeded / s.total * 100).toFixed(1) : 0;
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{gw}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace' }}>{s.total}</td>
                        <td style={{ color: G.green, fontFamily: 'DM Mono, monospace' }}>{s.succeeded}</td>
                        <td style={{ color: G.red, fontFamily: 'DM Mono, monospace' }}>{s.failed}</td>
                        <td style={{ color: G.muted, fontFamily: 'DM Mono, monospace' }}>{s.refunded}</td>
                        <td style={{ color: G.goldLight, fontFamily: 'DM Mono, monospace' }}>{s.recovered}</td>
                        <td style={{ fontWeight: 700, color: G.goldLight }}>{fmt(s.revenue)}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="progress" style={{ width: 50 }}><div className="progress-fill" style={{ width: `${rate}%`, background: parseFloat(rate) >= 80 ? G.green : parseFloat(rate) >= 50 ? G.gold : G.red }} /></div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: parseFloat(rate) >= 80 ? G.green : parseFloat(rate) >= 50 ? G.goldLight : G.red }}>{rate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SETTINGS
// -----------------------------------------------------------------------------

// 2026-06-07 — Deploy E: per-tenant SMTP for storefront mails.
// The form lets a merchant connect their own SMTP (Ionos / Gmail app
// password / Sendgrid / AWS SES — anything nodemailer can drive). The
// platform still owns tracking emails; this only re-routes payment
// confirmations, club mailings, abandoned-cart recovery and support
// bot replies so they look like they're coming from the merchant's
// own address.
function EmailProviderConfig({ showToast, user }) {
  const blank = { host: '', port: 587, secure: false, user: '', pass: '', fromEmail: '', fromName: '', replyTo: '' };
  const [cfg, setCfg] = useState(blank);
  const [loaded, setLoaded] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);   // already configured?
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [savingFlag, setSavingFlag] = useState(false);
  const [testingFlag, setTestingFlag] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState(false);
  const [testTo, setTestTo] = useState(user?.email || '');

  useEffect(() => {
    emailProviderApi.get()
      .then(d => {
        if (d && d.smtp) {
          setCfg({
            host:      d.smtp.host || '',
            port:      d.smtp.port || 587,
            secure:    !!d.smtp.secure,
            user:      d.smtp.user || '',
            pass:      '',
            fromEmail: d.smtp.fromEmail || '',
            fromName:  d.smtp.fromName || '',
            replyTo:   d.smtp.replyTo || '',
          });
          setHasSaved(true);
          setVerifiedAt(d.smtp.verifiedAt || null);
          setUpdatedAt(d.smtp.updatedAt || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (!cfg.host || !cfg.user) {
      showToast('Host y usuario son obligatorios', 'error');
      return;
    }
    if (!hasSaved && !cfg.pass) {
      showToast('Contraseña obligatoria la primera vez', 'error');
      return;
    }
    setSavingFlag(true);
    try {
      const r = await emailProviderApi.save(cfg);
      setCfg(c => ({ ...c, pass: '' }));
      setHasSaved(true);
      setVerifiedAt(r.smtp?.verifiedAt || null);
      setUpdatedAt(r.smtp?.updatedAt || null);
      showToast('SMTP guardado', 'success');
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error');
    }
    setSavingFlag(false);
  };

  const test = async () => {
    if (!testTo || !testTo.includes('@')) {
      showToast('Indica un email destino para la prueba', 'error');
      return;
    }
    // F45b (2026-06-11) — Lógica simplificada y robusta:
    //   - Si pass tiene valor → guardar (sea primera vez o cambio)
    //   - Si pass vacía Y hasSaved → solo test (la config ya está en BD)
    //   - Si pass vacía Y !hasSaved → exigir pass
    //
    // Antes (F45): siempre intentaba guardar incluso con pass vacía si
    // tenía host+user. Eso provocaba que cuando hasSaved=true y el
    // operator tocaba Test sin reescribir pass, el backend recibía
    // pass='' y devolvía 'no previous password on file' si por algún
    // motivo el record de DB se había perdido.
    setTestingFlag(true);
    try {
      if (cfg.pass) {
        // Validar mínimos antes de intentar guardar
        if (!cfg.host || !cfg.user) {
          showToast('Host y usuario son obligatorios', 'error');
          setTestingFlag(false);
          return;
        }
        try {
          const r = await emailProviderApi.save(cfg);
          // F45b: NO limpiar cfg.pass aquí — si el test falla y el
          // operator quiere reintentar, debe mantenerse el valor para
          // que el siguiente intento tampoco mande pass vacía.
          setHasSaved(true);
          setUpdatedAt(r.smtp?.updatedAt || new Date().toISOString());
        } catch (saveErr) {
          const err = saveErr.response?.data?.error || saveErr.message;
          showToast('Fallo al guardar: ' + err, 'error');
          setTestingFlag(false);
          return;
        }
      } else if (!hasSaved) {
        showToast('Rellena la contraseña SMTP. Si ya la guardaste, recarga la página.', 'error');
        setTestingFlag(false);
        return;
      }
      // Test (config ya está guardada en BD por save anterior o reciente)
      await emailProviderApi.test(testTo);
      showToast(`Email de prueba enviado a ${testTo}. Si no lo ves en 1-2 min, revisa SPAM.`, 'success');
      setVerifiedAt(new Date().toISOString());
      // Limpiar pass SOLO tras éxito completo (save + test)
      setCfg(c => ({ ...c, pass: '' }));
    } catch (e) {
      // F46c (2026-06-11) — Sin reescribir mensaje. El backend trae info
      // diagnóstica entre [brackets] cuando readTenantConfig falla.
      const err = e.response?.data?.error || e.message;
      showToast('Fallo SMTP: ' + err, 'error');
    }
    setTestingFlag(false);
  };

  const remove = async () => {
    if (!window.confirm('¿Olvidar configuración SMTP? Los emails volverán a salir desde la plataforma.')) return;
    setDeletingFlag(true);
    try {
      await emailProviderApi.remove();
      setCfg(blank);
      setHasSaved(false);
      setVerifiedAt(null);
      setUpdatedAt(null);
      showToast('Configuración SMTP eliminada', 'success');
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error');
    }
    setDeletingFlag(false);
  };

  if (!loaded) return <div className="empty"><div className="empty-icon">✉</div>Cargando…</div>;

  const status = hasSaved
    ? (verifiedAt ? { dot: G.green,  text: 'Verificado'  } : { dot: G.gold,  text: 'Sin verificar — usa el botón "Enviar prueba"' })
    : { dot: G.muted, text: 'Sin configurar — los emails salen desde ZeroRiskPay' };

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">✉ Email de la tienda (SMTP)</span>
        <span style={{ fontSize: 12, color: status.dot }}>● {status.text}</span>
      </div>
      <div className="card-body">
        <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.7, marginBottom: 18 }}>
          Conecta el servidor SMTP de tu tienda para que las confirmaciones de pago,
          el club, los emails de carrito abandonado y las respuestas del bot de soporte
          salgan desde <strong style={{ color: G.goldLight }}>tu propio email</strong>.
          <br />
          Los emails de <strong>tracking de envíos</strong> siempre salen desde ZeroRiskPay y no requieren configuración.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Servidor SMTP</label>
            <input className="form-input" placeholder="smtp.ionos.es" value={cfg.host} onChange={e => setCfg({ ...cfg, host: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Puerto</label>
            <input className="form-input" type="number" placeholder="587" value={cfg.port} onChange={e => setCfg({ ...cfg, port: parseInt(e.target.value, 10) || 587 })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: G.dim }}>
              <input type="checkbox" checked={cfg.secure} onChange={e => setCfg({ ...cfg, secure: e.target.checked })} />
              SSL/TLS (puerto 465)
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Usuario (email)</label>
            <input className="form-input" placeholder="support@tutienda.com" value={cfg.user} onChange={e => setCfg({ ...cfg, user: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Contraseña {hasSaved && <span style={{ fontSize: 10, color: G.muted }}>(deja vacío para conservar la actual)</span>}</label>
            <input className="form-input" type="password" placeholder={hasSaved ? '••••••••' : 'contraseña SMTP'} value={cfg.pass} onChange={e => setCfg({ ...cfg, pass: e.target.value })} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From email</label>
            <input className="form-input" placeholder="support@tutienda.com" value={cfg.fromEmail} onChange={e => setCfg({ ...cfg, fromEmail: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From nombre</label>
            <input className="form-input" placeholder="Mi Tienda" value={cfg.fromName} onChange={e => setCfg({ ...cfg, fromName: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Reply-To <span style={{ fontSize: 10, color: G.muted }}>(opcional)</span></label>
            <input className="form-input" placeholder="support@tutienda.com" value={cfg.replyTo} onChange={e => setCfg({ ...cfg, replyTo: e.target.value })} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
          <button className="btn btn-gold" disabled={savingFlag} onClick={save}>{savingFlag ? 'Guardando…' : '💾 Guardar'}</button>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
            <input className="form-input" style={{ width: 220 }} placeholder="enviar prueba a…" value={testTo} onChange={e => setTestTo(e.target.value)} />
            <button className="btn btn-ghost" disabled={testingFlag || !hasSaved} onClick={test}>{testingFlag ? '⏳' : '✉ Enviar prueba'}</button>
          </div>

          {hasSaved && (
            <button className="btn btn-danger btn-sm" disabled={deletingFlag} onClick={remove}>
              {deletingFlag ? '…' : '🗑 Eliminar'}
            </button>
          )}
        </div>

        {(updatedAt || verifiedAt) && (
          <div style={{ marginTop: 14, fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {updatedAt   && <>Última actualización: {new Date(updatedAt).toLocaleString('es-ES')}<br /></>}
            {verifiedAt  && <>Verificado: {new Date(verifiedAt).toLocaleString('es-ES')}</>}
          </div>
        )}
      </div>
    </div>
  );
}

function Settings({ showToast, user }) {
  const [settings, setSettings] = useState({ aiRouting: { enabled: false }, fraudSettings: { enabled: true, maxAttemptsPerIp: 3, blockDisposableEmails: true }, dailyReport: { enabled: false, hour: 23 }, autoSubscription: { enabled: false, price: '', delayDays: 30, interval: 'month' }, routing: 'percentage', retryAttempts: 3 });
  const [tab, setTab] = useState('subs');
  const [aiHistory, setAiHistory] = useState([]);
  const [fraudStats, setFraudStats] = useState(null);
  const [runningAi, setRunningAi] = useState(false);
  const [shops, setShops] = useState([]);
  // F43 (2026-06-11) — gateways necesario para AutoSubscriptionConfig
  // renderice un input de stripePriceId por cada cuenta Stripe activa.
  const [gateways, setGateways] = useState([]);

  useEffect(() => {
    setupApi.getSettings().then(setSettings).catch(() => {});
    setupApi.getAiHistory().then(setAiHistory).catch(() => {});
    setupApi.getFraudStats().then(setFraudStats).catch(() => {});
    shopsApi.getAll().then(s => setShops(s || [])).catch(() => {});
    gatewaysApi.getAll().then(g => setGateways(g || [])).catch(() => setGateways([]));
  }, []);

  const save = async ns => { setSettings(ns); try { await setupApi.saveSettings(ns); showToast('Guardado OK', 'success'); } catch { showToast('Error guardando', 'error'); } };

  const TABS = [
    { id: 'subs', label: '↺ Suscripción auto' },
    { id: 'ai', label: '🤖 IA Routing' },
    { id: 'fraud', label: '🛡 Fraude' },
    { id: 'report', label: '📊 Informe diario' },
    { id: 'email', label: '✉ Email de la tienda' },
    { id: 'general', label: '⚙ General' },
  ];

  return (
    <div className="content">
      <div className="section-tabs" style={{ marginBottom: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map(t => <button key={t.id} className={`section-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      {tab === 'subs' && (
        <AutoSubscriptionConfig
          settings={settings}
          save={save}
          shops={shops}
          gateways={gateways}
          fmtSymbol={fmtSymbol}
          showToast={showToast}
        />
      )}

      {tab === 'ai' && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">🤖 IA routing inteligente</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: settings.aiRouting?.enabled ? G.green : G.muted }}>{settings.aiRouting?.enabled ? '● Activa' : '○ Inactiva'}</span>
              <button className={`btn ${settings.aiRouting?.enabled ? 'btn-danger' : 'btn-gold'} btn-sm`} onClick={() => save({ ...settings, aiRouting: { ...settings.aiRouting, enabled: !settings.aiRouting?.enabled } })}>{settings.aiRouting?.enabled ? 'Desactivar' : 'Activar'}</button>
              {settings.aiRouting?.enabled && <button className="btn btn-ghost btn-sm" disabled={runningAi} onClick={async () => { setRunningAi(true); try { const r = await setupApi.runAiRouting(); showToast(r.message || 'IA ejecutada', 'success'); setupApi.getAiHistory().then(setAiHistory); } catch (e) { showToast(e.message, 'error'); } setRunningAi(false); }}>{runningAi ? '⏳' : '▶ Ejecutar ahora'}</button>}
            </div>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.8, marginBottom: 16 }}>Analiza la tasa de éxito de cada pasarela en los últimos 7 días y ajusta automáticamente los % de tráfico para maximizar conversiones.</div>
            {aiHistory.length === 0 ? <div className="empty"><div className="empty-icon" style={{ fontSize: 24 }}>🤖</div>Necesita al menos 10 transacciones para aprender.</div> :
              aiHistory.slice(0, 5).map((h, i) => (
                <div key={i} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', marginBottom: 4 }}>{new Date(h.timestamp).toLocaleString('es-ES', { timeZone: APP_TZ })}</div>
                  {(h.decisions || []).map((d, j) => <div key={j} style={{ fontSize: 12 }}><span style={{ color: G.goldLight, fontWeight: 600 }}>{d.gateway}</span>: {d.oldPct}% -> <span style={{ color: G.green }}>{d.newPct}%</span> <span style={{ color: G.muted }}>({d.reason})</span></div>)}
                  {(!h.decisions || h.decisions.length === 0) && <div style={{ fontSize: 12, color: G.muted }}>Sin cambios -- ya están optimizados</div>}
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === 'fraud' && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">🛡 Detector de fraude</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: settings.fraudSettings?.enabled ? G.green : G.muted }}>{settings.fraudSettings?.enabled ? '● Activo' : '○ Inactivo'}</span>
              <button className={`btn ${settings.fraudSettings?.enabled ? 'btn-danger' : 'btn-gold'} btn-sm`} onClick={() => save({ ...settings, fraudSettings: { ...settings.fraudSettings, enabled: !settings.fraudSettings?.enabled } })}>{settings.fraudSettings?.enabled ? 'Desactivar' : 'Activar'}</button>
            </div>
          </div>
          <div className="card-body">
            {fraudStats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
                {[{ l: 'Intentos hoy', v: fraudStats.totalToday || 0, c: G.white }, { l: 'Bloqueados', v: fraudStats.blockedToday || 0, c: G.red }, { l: 'Permitidos', v: fraudStats.allowedToday || 0, c: G.green }].map((s, i) => (
                  <div key={i} className="kpi-card" style={{ padding: 14 }}><div className="kpi-label">{s.l}</div><div className="kpi-value" style={{ fontSize: 22, color: s.c }}>{s.v}</div></div>
                ))}
              </div>
            )}
            {[{ k: 'maxAttemptsPerIp', l: 'Máx. intentos por IP/hora', type: 'number', d: 'Bloquea IPs con más de X intentos' }, { k: 'blockDisposableEmails', l: 'Bloquear emails desechables', type: 'toggle', d: 'Bloquea mailinator, tempmail, yopmail...' }].map(rule => (
              <div key={rule.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${G.border}20` }}>
                <div><div style={{ fontSize: 13, fontWeight: 600 }}>{rule.l}</div><div style={{ fontSize: 11, color: G.muted }}>{rule.d}</div></div>
                {rule.type === 'toggle' ? <button className={`btn ${settings.fraudSettings?.[rule.k] ? 'btn-gold' : 'btn-ghost'} btn-sm`} onClick={() => save({ ...settings, fraudSettings: { ...settings.fraudSettings, [rule.k]: !settings.fraudSettings?.[rule.k] } })}>{settings.fraudSettings?.[rule.k] ? 'OK ON' : 'OFF'}</button>
                  : <input type="number" className="form-input" style={{ width: 80 }} value={settings.fraudSettings?.[rule.k] || ''} onChange={e => save({ ...settings, fraudSettings: { ...settings.fraudSettings, [rule.k]: +e.target.value } })} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'report' && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">📊 Informe diario por email</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: settings.dailyReport?.enabled ? G.green : G.muted }}>{settings.dailyReport?.enabled ? '● Activo' : '○ Inactivo'}</span>
              <button className={`btn ${settings.dailyReport?.enabled ? 'btn-danger' : 'btn-gold'} btn-sm`} onClick={() => save({ ...settings, dailyReport: { ...settings.dailyReport, enabled: !settings.dailyReport?.enabled } })}>{settings.dailyReport?.enabled ? 'Desactivar' : 'Activar'}</button>
            </div>
          </div>
          <div className="card-body">
            <div style={{ background: '#ea580c15', border: '1px solid #ea580c40', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ea580c', marginBottom: 6 }}>⚠️ Requiere SMTP en Render -> Environment:</div>
              <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', lineHeight: 1.8 }}>SMTP_HOST=smtp.gmail.com · SMTP_PORT=587 · SMTP_USER=tu@gmail.com · SMTP_PASS=app_password</div>
            </div>
            <div className="form-group">
              <label className="form-label">Hora de envío</label>
              <select className="form-input" value={settings.dailyReport?.hour || 23} onChange={e => save({ ...settings, dailyReport: { ...settings.dailyReport, hour: +e.target.value } })}>
                {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>)}
              </select>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={async () => { try { await setupApi.getDailyReport(); showToast('Informe generado OK', 'success'); } catch (e) { showToast(e.message, 'error'); } }}>📊 Generar prueba ahora</button>
          </div>
        </div>
      )}

      {tab === 'email' && <EmailProviderConfig showToast={showToast} user={user} />}

      {tab === 'general' && (
        <>
          <div className="card">
            <div className="card-head"><span className="card-title">⚙ Configuración general</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Estrategia de routing</label>
                <select className="form-input" value={settings.routing || 'percentage'} onChange={e => save({ ...settings, routing: e.target.value })}>
                  <option value="percentage">Por porcentaje -- distribuye el tráfico según los % configurados</option>
                  <option value="priority">Por prioridad -- usa la primera pasarela, las demás como failover</option>
                  <option value="round_robin">Round robin -- alterna entre pasarelas rotativamente</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Máximo reintentos por pago fallido</label>
                <input type="number" className="form-input" min={1} max={5} value={settings.retryAttempts || 3} onChange={e => save({ ...settings, retryAttempts: +e.target.value })} />
                <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Si un pago falla, el orquestador lo reintenta con otra pasarela automáticamente.</div>
              </div>

              {/* 2026-06-11 — Profit goal widget. Por defecto el banner
                  muestra "Camino al Millón" (1.000.000 €). Si el merchant
                  define una meta concreta aquí, el banner usa ese valor
                  con título auto-generado ("Camino a 100K", "Camino a 5M"…)
                  o el customGoalTitle de abajo si lo rellena. */}
              <div className="form-group">
                <label className="form-label">Meta de beneficio (banner del dashboard)</label>
                <input
                  type="number" className="form-input" min={0} step={1000}
                  placeholder="Por defecto 1.000.000 — pon un número para personalizar"
                  value={settings.profitGoal || ''}
                  onChange={e => save({ ...settings, profitGoal: parseFloat(e.target.value) || 0 })}
                />
                <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>
                  Define el objetivo de beneficio que quieres conseguir.
                  Si lo dejas vacío o a 0, el banner usa <strong>1.000.000 €</strong> por
                  defecto ("Camino al Millón"). La moneda se toma de tu cuenta
                  (Settings → Visualización de moneda).
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 10 }}>
                <label className="form-label">Título personalizado del banner (opcional)</label>
                <input
                  type="text" className="form-input" maxLength={80}
                  placeholder="Si vacío → se genera auto: 'Camino a los 100K', 'Camino al Millón', etc."
                  value={settings.customGoalTitle || ''}
                  onChange={e => save({ ...settings, customGoalTitle: e.target.value })}
                />
                <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>
                  Texto a mostrar como cabecera del banner. Si lo dejas vacío, el sistema lo
                  genera automáticamente según el goal ("Camino a los 500K", "Camino al Millón",
                  etc.). Personaliza con cosas como "Mi meta anual 2026" o "Objetivo Q4".
                </div>
              </div>
            </div>
          </div>

          {/* Display currency toggle — moved here from the topbar so the topbar
              stays clean on mobile. Purely cosmetic: flips how amounts are
              rendered. It does NOT affect charge amounts. */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head"><span className="card-title">💱 Visualización de moneda</span></div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
                Decide cómo se muestran los importes en el dashboard. Es un cambio
                <strong style={{ color: G.gold }}> puramente cosmético</strong> — los cobros reales siguen pasando por
                la pasarela en la moneda nativa (USD en NMI/TailoredPay). Cambia entre
                símbolo € y $ sin afectar a ningún importe.
              </div>
              <DisplayCurrencyToggle user={user} showToast={showToast} />
            </div>
          </div>

          {/* 2026-06-07 — Timezone selector (#96).
              Affects all "Hoy/Ayer/30d/Mes pasado" date math in the
              dashboard + the cutoff for the daily report cron. */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head"><span className="card-title">🕐 Zona horaria</span></div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
                Define tu zona horaria local. Los KPIs del dashboard (Hoy, Ayer, últimos 30d…)
                y el corte diario del informe de email respetan esta zona.
              </div>
              <select className="form-input" value={settings.timezone || 'America/New_York'}
                onChange={async e => { const tz = e.target.value; setAppTz(tz); await save({ ...settings, timezone: tz }); setTimeout(() => window.location.reload(), 400); }}>
                <option value="Europe/Madrid">🇪🇸 Madrid · CET / CEST (UTC+1/+2)</option>
                <option value="Europe/London">🇬🇧 Londres · GMT / BST (UTC+0/+1)</option>
                <option value="Europe/Paris">🇫🇷 París · CET / CEST</option>
                <option value="Europe/Berlin">🇩🇪 Berlín · CET / CEST</option>
                <option value="Europe/Rome">🇮🇹 Roma · CET / CEST</option>
                <option value="Europe/Lisbon">🇵🇹 Lisboa · WET / WEST (UTC+0/+1)</option>
                <option value="America/New_York">🇺🇸 Nueva York · EST / EDT (UTC-5/-4)</option>
                <option value="America/Chicago">🇺🇸 Chicago · CST / CDT</option>
                <option value="America/Denver">🇺🇸 Denver · MST / MDT</option>
                <option value="America/Los_Angeles">🇺🇸 Los Ángeles · PST / PDT</option>
                <option value="America/Mexico_City">🇲🇽 CDMX · CST</option>
                <option value="America/Sao_Paulo">🇧🇷 São Paulo · BRT (UTC-3)</option>
                <option value="America/Argentina/Buenos_Aires">🇦🇷 Buenos Aires · ART (UTC-3)</option>
                <option value="Asia/Tokyo">🇯🇵 Tokio · JST (UTC+9)</option>
                <option value="Asia/Shanghai">🇨🇳 Shanghai · CST (UTC+8)</option>
                <option value="Australia/Sydney">🇦🇺 Sídney · AEST / AEDT</option>
                <option value="UTC">UTC</option>
              </select>
              <div style={{ fontSize: 11, color: G.dim, marginTop: 6 }}>
                Actualmente: <strong style={{ color: G.gold }}>{settings.timezone || 'America/New_York'}</strong>
                {' · '}Hora local: <strong>{new Date().toLocaleString('es-ES', { timeZone: settings.timezone || 'America/New_York', hour: '2-digit', minute: '2-digit' })}</strong>
              </div>
            </div>
          </div>

          {/* 2026-06-07 — Per-gateway fee override (#94).
              Lets the merchant declare the % + fixed fee each processor
              charges them, so the P&L (Beneficios) shows accurate net
              revenue. Saved at settings.gatewayFees[gatewayId]. */}
          <GatewayFeesEditor settings={settings} save={save} showToast={showToast} />

          {/* 2026-06-07 — Carrier-by-country.
              Default labels live in /themes/_carrier-by-country.js
              (USPS for US, SEUR for ES, Chronopost for FR…). The merchant
              overrides them here when they want a specific brand. */}
          <CarriersByCountryEditor settings={settings} save={save} />

          {/* 2026-06-07 — Cart abandonment recovery (#5 on the operator's roadmap).
              When ON, the cron emails customers who created a checkout
              session but never paid, after delayMinutes. */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head"><span className="card-title">🛒 Recuperación de carrito abandonado</span></div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
                Envía un email automático a los clientes que crearon un checkout pero no terminaron de pagar.
                El email usa la plantilla <strong style={{ color: G.gold }}>cart_abandonment</strong> (créala en Templates con un link de checkout para que vuelvan en 1-click).
                Respeta el idioma del cliente.
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={!!settings.cartAbandonment?.enabled} onChange={e => save({ ...settings, cartAbandonment: { ...(settings.cartAbandonment || {}), enabled: e.target.checked } })} />
                <span style={{ color: G.white, fontWeight: 600 }}>Activar recuperación</span>
              </label>
              {settings.cartAbandonment?.enabled && (
                <>
                  {/* F48 (2026-06-11) — aviso obligatorio para que el cupón funcione end-to-end */}
                  <div style={{
                    background: `${G.gold}15`,
                    border: `1px solid ${G.gold}40`,
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 14,
                    fontSize: 13,
                    color: G.white,
                    lineHeight: 1.55,
                  }}>
                    <div style={{ fontWeight: 700, color: G.gold, marginBottom: 4 }}>⚠ IMPORTANTE — crea el cupón en Shopify</div>
                    El email de recuperación lleva un descuento del <strong>20%</strong> con el código <code style={{ background: G.bg, padding: '2px 6px', borderRadius: 4, color: G.gold, fontFamily: 'DM Mono, monospace' }}>DISCOUNT20</code>. Para que el cliente lo pueda aplicar en tu tienda Shopify, créalo allí también:
                    <div style={{ fontSize: 12, color: G.muted, marginTop: 8, lineHeight: 1.7 }}>
                      Shopify Admin → <strong>Descuentos</strong> → <strong>Crear descuento</strong> → Código <code style={{ background: G.bg, padding: '1px 5px', borderRadius: 3, color: G.gold }}>DISCOUNT20</code> · Tipo: porcentaje · Valor: 20% · Aplicar a: todos los productos.
                    </div>
                    <div style={{ fontSize: 11, color: G.muted, marginTop: 6 }}>
                      Sin este cupón en Shopify, el cliente verá el descuento en el checkout de ZeroRiskPay, pero NO al volver a tu tienda Shopify directamente.
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Minutos hasta enviar el email</label>
                    <input type="number" className="form-input" min={5} max={1440}
                      value={settings.cartAbandonment?.delayMinutes || 30}
                      onChange={e => save({ ...settings, cartAbandonment: { ...(settings.cartAbandonment || {}), delayMinutes: +e.target.value || 30 } })} />
                    <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Tiempo después del checkout sin pagar antes del primer recordatorio. Recomendado: 30-60 min.</div>
                  </div>
                </>
              )}

              {/* SMS de carrito abandonado — canal independiente del email */}
              {(() => {
                const smsCfg = settings.cartAbandonment?.sms || {};
                const saveSms = (patch) => save({ ...settings, cartAbandonment: { ...(settings.cartAbandonment || {}), sms: { ...smsCfg, ...patch } } });
                const bodyText = smsCfg.body || '{{shop}}: tu pedido de {{amount}} sigue guardado. Termina la compra con un 20% de descuento aquí: {{link}}';
                return (
                  <div style={{ borderTop: `1px solid ${G.border}`, marginTop: 18, paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, color: G.white, marginBottom: 6 }}>📱 SMS de carrito abandonado</div>
                    <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 12 }}>
                      Envía un SMS con el enlace para retomar el pedido (con el mismo cupón). Necesita el proveedor configurado en el módulo <strong style={{ color: G.gold }}>SMS</strong> y que el checkout capture el teléfono (plantilla Cecotec).
                      Solo se envía a números con prefijo internacional, 1 vez por teléfono cada 30 días, dentro del horario permitido, y nunca a quien ya ha comprado.
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                      <input type="checkbox" checked={!!smsCfg.enabled} onChange={e => saveSms({ enabled: e.target.checked })} />
                      <span style={{ color: G.white, fontWeight: 600 }}>Activar SMS</span>
                    </label>
                    {smsCfg.enabled && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div className="form-group">
                            <label className="form-label">Consentimiento en el checkout</label>
                            <select className="form-input" value={smsCfg.consentMode || 'checkbox'} onChange={e => saveSms({ consentMode: e.target.value })}>
                              <option value="checkbox">Casilla sin marcar (recomendado en la UE)</option>
                              <option value="notice">Solo aviso bajo el teléfono</option>
                              <option value="none">Nada (bajo tu responsabilidad)</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Horario permitido (hora local del cliente)</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input type="number" className="form-input" min={0} max={23} value={smsCfg.quietHoursStart ?? 9} onChange={e => saveSms({ quietHoursStart: +e.target.value })} />
                              <span style={{ color: G.muted }}>a</span>
                              <input type="number" className="form-input" min={1} max={24} value={smsCfg.quietHoursEnd ?? 21} onChange={e => saveSms({ quietHoursEnd: +e.target.value })} />
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Código de descuento del enlace</label>
                            <input type="text" className="form-input" defaultValue={smsCfg.discountCode || 'DISCOUNT20'} onBlur={e => saveSms({ discountCode: e.target.value.trim() })} />
                          </div>
                        </div>
                        <CartSmsSenderByShop value={smsCfg.senderByShop || {}} onChange={(map) => saveSms({ senderByShop: map })} />
                        <CartSmsSteps
                          steps={(smsCfg.steps && smsCfg.steps.length) ? smsCfg.steps : [
                            { enabled: true, delayMinutes: smsCfg.delayMinutes || 30, body: bodyText, bodyEn: smsCfg.bodyEn || '{{shop}}: your order of {{amount}} is still saved. Complete your purchase with 20% off here: {{link}}' },
                            { enabled: false, delayMinutes: 1440, body: '{{shop}}: último aviso, tu pedido de {{amount}} sigue guardado con el 20% de descuento. Termínalo aquí: {{link}}', bodyEn: '{{shop}}: last reminder — your order of {{amount}} is still saved with 20% off. Finish it here: {{link}}' },
                          ]}
                          onChange={(steps) => saveSms({ steps })}
                          title="Secuencia de SMS (general para todas las tiendas)"
                        />
                        <CartSmsByShop value={smsCfg.byShop || {}} tenantSteps={smsCfg.steps || []} onChange={(byShop) => saveSms({ byShop })} />
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CART SMS — remitente alfanumérico por tienda (Twilio: 3-11 letras/números).
// Vacío = se usa el remitente configurado en el módulo SMS.
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// CART SMS — secuencia de pasos (p.ej. 30 min + 24 h) y textos por tienda.
// Cada paso se mide desde que el cliente abandona el carrito. Se guarda al
// salir de cada campo (misma mecánica que el resto de Configuración).
// -----------------------------------------------------------------------------
function CartSmsSteps({ steps, onChange, title }) {
  const list = Array.isArray(steps) ? steps.slice(0, 3) : [];
  const setStep = (i, patch) => { const next = list.map((st, j) => (j === i ? { ...st, ...patch } : st)); onChange(next); };
  const addStep = () => { if (list.length >= 3) return; onChange([...list, { enabled: true, delayMinutes: list.length === 1 ? 1440 : 4320, body: '', bodyEn: '' }]); };
  const removeStep = (i) => { if (i === 0) return; onChange(list.filter((_, j) => j !== i)); };
  const human = (m) => { const n = +m || 0; if (n >= 1440 && n % 1440 === 0) return `= ${n / 1440} día${n / 1440 > 1 ? 's' : ''}`; if (n >= 60 && n % 60 === 0) return `= ${n / 60} h`; return ''; };
  const vars = <span>variables: <code>{'{{firstName}}'}</code> <code>{'{{shop}}'}</code> <code>{'{{amount}}'}</code> <code>{'{{link}}'}</code> <code>{'{{code}}'}</code></span>;
  return (
    <div style={{ marginTop: 6 }}>
      {title && <div style={{ fontWeight: 700, color: G.white, marginBottom: 6, fontSize: 13 }}>{title}</div>}
      {list.map((st, i) => (
        <div key={`${i}-${list.length}`} style={{ border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, marginBottom: 10, background: G.surface, opacity: st.enabled === false && i > 0 ? 0.6 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: G.gold, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>SMS {i + 1}</span>
            {i > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: G.white }}>
                <input type="checkbox" checked={st.enabled !== false} onChange={e => setStep(i, { enabled: e.target.checked })} /> Activo
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: G.muted }}>
              Enviar a los
              <input type="number" className="form-input" min={5} max={2820} style={{ width: 90 }} value={st.delayMinutes || (i === 0 ? 30 : 1440)}
                onChange={e => setStep(i, { delayMinutes: +e.target.value || (i === 0 ? 30 : 1440) })} />
              minutos {human(st.delayMinutes) && <span style={{ color: G.dim }}>{human(st.delayMinutes)}</span>}
            </label>
            {i > 0 && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => removeStep(i)}>Quitar</button>}
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <label className="form-label">Texto en español (se guarda al salir del campo)</label>
            <textarea className="form-input" rows={2} maxLength={320} defaultValue={st.body || ''} onBlur={e => { if (e.target.value !== (st.body || '')) setStep(i, { body: e.target.value }); }} />
          </div>
          <div className="form-group" style={{ marginBottom: 4 }}>
            <label className="form-label">Texto en inglés (clientes UK / US / IE, por país de la dirección)</label>
            <textarea className="form-input" rows={2} maxLength={320} defaultValue={st.bodyEn || ''} onBlur={e => { if (e.target.value !== (st.bodyEn || '')) setStep(i, { bodyEn: e.target.value }); }} />
          </div>
          <div style={{ fontSize: 11, color: G.muted }}>{vars}. Vacío = texto por defecto. El enlace abre el checkout con el cupón aplicado.</div>
        </div>
      ))}
      {list.length < 3 && <button className="btn btn-ghost btn-sm" onClick={addStep}>+ Añadir otro SMS</button>}
      <div style={{ fontSize: 11, color: G.muted, marginTop: 6 }}>Un flujo por carrito y uno por teléfono cada 30 días; nunca si ya ha pagado; solo dentro del horario permitido. Máximo 2.820 minutos (47 h): los carritos de más de 48 h ya no se tocan.</div>
    </div>
  );
}

function CartSmsByShop({ value, tenantSteps, onChange }) {
  const [shops, setShops] = useState([]);
  useEffect(() => { shopsApi.getAll().then(s => setShops(Array.isArray(s) ? s : [])).catch(() => {}); }, []);
  if (!shops.length) return null;
  const map = value && typeof value === 'object' ? value : {};
  const seed = () => (Array.isArray(tenantSteps) && tenantSteps.length ? tenantSteps : [{ enabled: true, delayMinutes: 30, body: '', bodyEn: '' }]).map(st => ({ ...st }));
  return (
    <div style={{ borderTop: `1px dashed ${G.border}`, marginTop: 14, paddingTop: 12 }}>
      <div style={{ fontWeight: 700, color: G.white, marginBottom: 4, fontSize: 13 }}>Textos propios por tienda (opcional)</div>
      <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>Si una tienda tiene su propia secuencia, se usa en vez de la general solo para sus carritos.</div>
      {shops.map(sh => {
        const own = map[sh.id] && Array.isArray(map[sh.id].steps) && map[sh.id].steps.length > 0;
        return (
          <div key={sh.id} style={{ marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: G.white }}>
              <input type="checkbox" checked={!!own} onChange={e => {
                const next = { ...map };
                if (e.target.checked) next[sh.id] = { steps: seed() }; else delete next[sh.id];
                onChange(next);
              }} />
              <span>Secuencia propia para <strong>{sh.name || sh.url}</strong></span>
            </label>
            {own && (
              <div style={{ marginLeft: 22, marginTop: 6 }}>
                <CartSmsSteps steps={map[sh.id].steps} onChange={(steps) => onChange({ ...map, [sh.id]: { steps } })} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CartSmsSenderByShop({ value, onChange }) {
  const [shops, setShops] = useState([]);
  useEffect(() => { shopsApi.getAll().then(s => setShops(Array.isArray(s) ? s : [])).catch(() => {}); }, []);
  if (!shops.length) return null;
  const valid = (v) => !v || (/^[A-Za-z0-9]{3,11}$/.test(v) && /[A-Za-z]/.test(v));
  return (
    <div className="form-group">
      <label className="form-label">Remitente del SMS por tienda (opcional)</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {shops.map(s => {
          const v = value[s.id] || '';
          return (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: G.muted, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name || s.url || s.id}</span>
              <input type="text" className="form-input" maxLength={11} placeholder="p.ej. TecOutlet" defaultValue={v}
                style={{ borderColor: valid(v) ? undefined : '#ef4444' }}
                onBlur={e => { const nv = e.target.value.trim(); if (nv && !valid(nv)) return; onChange({ ...value, [s.id]: nv }); }} />
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>3-11 caracteres, solo letras y números (sin espacios). Válido en España y Reino Unido sin registro; no sirve para números de EE. UU.</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CARRIERS BY COUNTRY — country-aware shipping method label
// -----------------------------------------------------------------------------
// MVP shipped "🇺🇸 Insured USPS Tracked Shipping" in every locale, which
// reads as obviously wrong for non-US shoppers. The /themes/_carrier-by-country.js
// runtime applies sensible defaults (DHL ES, Chronopost FR, Royal Mail GB...),
// but each merchant can override here.
function CarriersByCountryEditor({ settings, save }) {
  const defaults = useMemo(() => ([
    { code: 'US', flag: '🇺🇸', name: 'Estados Unidos', defTitle: 'Insured USPS Tracked Shipping', defSub: 'Orders ship within 1-2 business days' },
    { code: 'ES', flag: '🇪🇸', name: 'España',         defTitle: 'SEUR / DHL Express España',     defSub: 'Pedidos enviados en 1-2 días hábiles' },
    { code: 'FR', flag: '🇫🇷', name: 'Francia',        defTitle: 'Chronopost / DHL Express France', defSub: 'Expédié sous 1-2 jours ouvrés' },
    { code: 'DE', flag: '🇩🇪', name: 'Alemania',       defTitle: 'DHL Tracked Shipping',          defSub: 'Versand in 1-2 Werktagen' },
    { code: 'IT', flag: '🇮🇹', name: 'Italia',         defTitle: 'Poste Italiane / DHL Express',  defSub: 'Ordini spediti in 1-2 giorni lavorativi' },
    { code: 'PT', flag: '🇵🇹', name: 'Portugal',       defTitle: 'CTT Expresso',                  defSub: 'Pedidos enviados em 1-2 dias úteis' },
    { code: 'GB', flag: '🇬🇧', name: 'Reino Unido',    defTitle: 'Royal Mail Tracked',            defSub: 'Orders ship within 1-2 business days' },
    { code: 'NL', flag: '🇳🇱', name: 'Países Bajos',   defTitle: 'PostNL / DHL Express',          defSub: 'Verzending binnen 1-2 werkdagen' },
    { code: 'BE', flag: '🇧🇪', name: 'Bélgica',        defTitle: 'bpost / DHL Express',           defSub: 'Expédié sous 1-2 jours ouvrés' },
    { code: 'IE', flag: '🇮🇪', name: 'Irlanda',        defTitle: 'An Post / DHL Express',         defSub: 'Orders ship within 1-2 business days' },
    { code: 'MX', flag: '🇲🇽', name: 'México',         defTitle: 'DHL Express México',            defSub: 'Pedidos enviados en 1-2 días hábiles' },
    { code: 'BR', flag: '🇧🇷', name: 'Brasil',         defTitle: 'Correios Sedex',                defSub: 'Pedidos enviados em 1-2 dias úteis' },
    { code: 'CA', flag: '🇨🇦', name: 'Canadá',         defTitle: 'Canada Post Tracked',           defSub: 'Orders ship within 1-2 business days' },
    { code: 'AU', flag: '🇦🇺', name: 'Australia',      defTitle: 'Australia Post Tracked',        defSub: 'Orders ship within 1-2 business days' },
  ]), []);
  const map = settings.carriersByCountry || {};
  const updateCountry = (code, patch) => {
    const next = { ...map };
    next[code] = { ...(next[code] || {}), ...patch };
    if (!next[code].title && !next[code].sub) delete next[code];
    save({ ...settings, carriersByCountry: next });
  };
  const resetCountry = (code) => {
    const next = { ...map };
    delete next[code];
    save({ ...settings, carriersByCountry: next });
  };
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><span className="card-title">🚚 Transitario por país</span></div>
      <div className="card-body">
        <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Por defecto el checkout muestra el transitario lógico según el país de envío del cliente
          (USPS si compra en USA, SEUR/DHL si en España, Chronopost si en Francia…).
          Aquí puedes <strong style={{ color: G.gold }}>sobrescribir cada país</strong> con el nombre
          y descripción que prefieras (p.ej. "DHL Express Spain", "Correos Express", etc.).
          Si dejas vacío, se usa el default.
        </div>
        <div style={{ maxHeight: 380, overflowY: 'auto', border: `1px solid ${G.border}`, borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', background: G.bg, position: 'sticky', top: 0 }}>
                <th style={{ padding: 8 }}>País</th>
                <th>Nombre del transitario</th>
                <th>Subtítulo (ETA)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {defaults.map(c => {
                const ov = map[c.code] || {};
                const isOverridden = !!(ov.title || ov.sub);
                return (
                  <tr key={c.code} style={{ borderTop: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 16, marginRight: 6 }}>{c.flag}</span>{c.name}
                      <div style={{ fontSize: 9, color: G.dim, fontFamily: 'DM Mono, monospace' }}>{c.code}</div>
                    </td>
                    <td>
                      <input type="text"
                        value={ov.title || ''}
                        placeholder={c.defTitle}
                        onChange={e => updateCountry(c.code, { title: e.target.value })}
                        style={{ width: '100%', padding: '5px 8px', background: G.bg, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }} />
                    </td>
                    <td>
                      <input type="text"
                        value={ov.sub || ''}
                        placeholder={c.defSub}
                        onChange={e => updateCountry(c.code, { sub: e.target.value })}
                        style={{ width: '100%', padding: '5px 8px', background: G.bg, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }} />
                    </td>
                    <td style={{ padding: 8 }}>
                      {isOverridden ? (
                        <button onClick={() => resetCountry(c.code)} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>↺ Default</button>
                      ) : (
                        <span style={{ fontSize: 10, color: G.dim }}>Default</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// GATEWAY FEES EDITOR — per-processor % + fixed fee for accurate P&L (#94)
// -----------------------------------------------------------------------------
// The merchant declares what each gateway charges them; Beneficios / P&L
// uses these to compute net revenue. Defaults sane for the common case
// (Stripe 1.5% + €0.25 EU cards, NMI/Tailored ~3% + $0.25, etc.) but the
// operator overrides per-gateway here.
function GatewayFeesEditor({ settings, save, showToast }) {
  const [gateways, setGateways] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/gateways`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setGateways)
      .catch(() => setGateways([]));
  }, []);
  const fees = settings.gatewayFees || {};
  const updateFee = (gwId, patch) => {
    const next = { ...fees, [gwId]: { ...(fees[gwId] || {}), ...patch } };
    save({ ...settings, gatewayFees: next });
  };
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><span className="card-title">💸 Fees por pasarela</span></div>
      <div className="card-body">
        <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Declara el % + fee fijo que te cobra cada procesador. Beneficios y la sección P&L lo restan del revenue bruto para mostrar tu profit real.
          Si no lo configuras, se usan los defaults estándar del sector.
        </div>
        {gateways.length === 0 && (
          <div style={{ color: G.dim, fontSize: 13, fontStyle: 'italic' }}>Configura pasarelas primero para editar sus fees.</div>
        )}
        {gateways.map(gw => {
          const f = fees[gw.id] || {};
          return (
            <div key={gw.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, alignItems: 'end', marginBottom: 10, padding: 10, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: G.white }}>{gw.name}</div>
                <div style={{ fontSize: 11, color: G.muted }}>{gw.processor || gw.psp || '—'}</div>
              </div>
              <div>
                <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>% sobre venta</label>
                <input type="number" step="0.01" className="form-input" placeholder="2.9"
                  value={f.percent ?? ''} onChange={e => updateFee(gw.id, { percent: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Fee fijo (€/pago)</label>
                <input type="number" step="0.01" className="form-input" placeholder="0.30"
                  value={f.fixed ?? ''} onChange={e => updateFee(gw.id, { fixed: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CLUB MEMBERSHIP — config, rewards, members
// -----------------------------------------------------------------------------

function ClubMembership({ showToast }) {
  const [tab, setTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editReward, setEditReward] = useState(null);
  const [rewardForm, setRewardForm] = useState({ day: 1, type: 'ebook', title_es: '', title_en: '', desc_es: '', desc_en: '', content: '', icon: '' });
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [shopsStatus, setShopsStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [blog, setBlog] = useState({ articles: [], generatedAt: null, model: null });
  const [blogArticleCount, setBlogArticleCount] = useState(4);

  // Load shops on mount
  useEffect(() => {
    shopsApi.getAll().then(s => {
      setShops(s || []);
      if (s && s.length > 0) setSelectedShopId(s[0].id);
    }).catch(() => {});
    clubApi.getShopsStatus().then(setShopsStatus).catch(() => {});
  }, []);

  const loadConfig = useCallback((shopId) => {
    const sid = shopId || selectedShopId;
    clubApi.getConfig(sid).then(d => {
      if (d.configured === false) setConfig(null);
      else setConfig(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedShopId]);

  const loadMembers = useCallback((shopId) => {
    const sid = shopId || selectedShopId;
    clubApi.getMembers(sid).then(d => setMembers(d || [])).catch(() => {});
  }, [selectedShopId]);

  const loadStats = useCallback((shopId) => {
    const sid = shopId || selectedShopId;
    clubApi.getStats(sid).then(d => setStats(d)).catch(() => {});
  }, [selectedShopId]);

  const loadBlog = useCallback((shopId) => {
    const sid = shopId || selectedShopId;
    clubApi.getBlog(sid).then(d => setBlog(d || { articles: [] })).catch(() => setBlog({ articles: [] }));
  }, [selectedShopId]);

  // Reload everything when selectedShopId changes
  useEffect(() => {
    if (selectedShopId || shops.length === 0) {
      setLoading(true);
      loadConfig(selectedShopId);
      loadMembers(selectedShopId);
      loadStats(selectedShopId);
      loadBlog(selectedShopId);
    }
  }, [selectedShopId]); // eslint-disable-line

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    setEditReward(null);
    setRewardForm({ day: 1, type: 'ebook', title_es: '', title_en: '', desc_es: '', desc_en: '', content: '', icon: '' });
  };

  const migrateToShop = async (shopId) => {
    setMigrating(true);
    try {
      const result = await clubApi.migrateToShop(shopId);
      if (result.migrated) {
        showToast(`Club migrado a ${result.shopName}`, 'success');
        clubApi.getShopsStatus().then(setShopsStatus).catch(() => {});
        loadConfig(shopId);
        loadMembers(shopId);
        loadStats(shopId);
      } else {
        showToast(result.reason || 'No hay config para migrar', 'error');
      }
    } catch (e) {
      showToast('Error migrando: ' + (e.response?.data?.error || e.message), 'error');
    }
    setMigrating(false);
  };

  // ── Config handlers ──
  const toggleEnabled = (val) => {
    setSaving(true);
    clubApi.toggleConfig({ enabled: val, shopId: selectedShopId })
      .then(d => { setConfig(d); setSaving(false); showToast(val ? 'Club activado' : 'Club desactivado', 'success'); clubApi.getShopsStatus().then(setShopsStatus).catch(() => {}); })
      .catch(err => {
        setSaving(false);
        const msg = err.response?.data?.error || 'Error actualizando config';
        showToast(msg, 'error');
      });
  };

  const deleteClub = () => {
    if (!window.confirm('Estas seguro de que quieres BORRAR este club? Se cancelaran todos los miembros activos. Esta accion no se puede deshacer.')) return;
    setSaving(true);
    clubApi.deleteConfig(selectedShopId)
      .then(() => {
        setConfig(null);
        setMembers([]);
        setSaving(false);
        showToast('Club eliminado correctamente', 'info');
        clubApi.getShopsStatus().then(setShopsStatus).catch(() => {});
      })
      .catch(err => {
        setSaving(false);
        const msg = err.response?.data?.error || err.message || 'Error eliminando club';
        showToast(msg, 'error');
      });
  };

  const saveConfig = (updates) => {
    setSaving(true);
    clubApi.updateConfig({ ...updates, shopId: selectedShopId })
      .then(d => { setConfig(d); setSaving(false); showToast('Configuracion guardada', 'success'); })
      .catch(() => { setSaving(false); showToast('Error guardando', 'error'); });
  };

  const initClub = () => {
    setSaving(true);
    clubApi.createConfig({ shopId: selectedShopId })
      .then(d => { setConfig(d); setSaving(false); loadStats(); showToast('Club creado con recompensas por defecto', 'success'); clubApi.getShopsStatus().then(setShopsStatus).catch(() => {}); })
      .catch(() => { setSaving(false); showToast('Error creando club', 'error'); });
  };

  // ── Reward handlers ──
  const saveReward = () => {
    const reward = {
      day: parseInt(rewardForm.day) || 1,
      type: rewardForm.type,
      title: { es: rewardForm.title_es, en: rewardForm.title_en },
      description: { es: rewardForm.desc_es, en: rewardForm.desc_en },
      content: rewardForm.content || '',
      icon: rewardForm.icon || (rewardForm.type === 'ebook' ? '\u{1F4D8}' : rewardForm.type === 'discount' ? '\u{1F3AB}' : rewardForm.type === 'video' ? '\u{1F3AC}' : '\u{1F381}'),
      shopId: selectedShopId,
    };
    setSaving(true);
    const promise = editReward !== null
      ? clubApi.updateReward(editReward, reward)
      : clubApi.addReward(reward);
    promise.then(() => {
        setEditReward(null);
        setRewardForm({ day: 1, type: 'ebook', title_es: '', title_en: '', desc_es: '', desc_en: '', content: '', icon: '' });
        setSaving(false);
        loadConfig();
        showToast(editReward !== null ? 'Recompensa actualizada' : 'Recompensa creada', 'success');
      }).catch(() => { setSaving(false); showToast('Error guardando recompensa', 'error'); });
  };

  const deleteReward = (idx) => {
    if (!window.confirm('Eliminar esta recompensa?')) return;
    clubApi.deleteReward(idx, selectedShopId)
      .then(() => { loadConfig(); showToast('Recompensa eliminada', 'success'); })
      .catch(() => showToast('Error eliminando', 'error'));
  };

  const startEditReward = (reward, idx) => {
    setEditReward(idx);
    setRewardForm({
      day: reward.day,
      type: reward.type,
      title_es: reward.title?.es || '',
      title_en: reward.title?.en || '',
      desc_es: reward.description?.es || '',
      desc_en: reward.description?.en || '',
      content: reward.content || '',
      icon: reward.icon || '',
    });
    setTab('rewards');
  };

  // ── Member handlers ──
  const deleteMember = (id) => {
    if (!window.confirm('Eliminar este miembro del club? No podra acceder mas.')) return;
    clubApi.deleteMember(id)
      .then(() => { loadMembers(); loadStats(); showToast('Miembro eliminado', 'success'); })
      .catch(() => showToast('Error eliminando miembro', 'error'));
  };
  const [resendingEmail, setResendingEmail] = useState(null);
  const resendEmail = async (memberId, email) => {
    if (!window.confirm(`Reenviar email de bienvenida a ${email}? Se generara una contrasena nueva y la anterior dejara de funcionar.`)) return;
    setResendingEmail(memberId);
    try {
      await clubApi.resendMemberEmail(memberId);
      showToast(`Email reenviado a ${email} con nueva contrasena`, 'success');
    } catch (e) {
      showToast('Error reenviando email: ' + (e.response?.data?.error || e.message), 'error');
    }
    setResendingEmail(null);
  };

  const regenerateRewards = async () => {
    if (!window.confirm('Regenerar todas las recompensas? Las recompensas actuales seran reemplazadas por nuevas personalizadas para esta tienda.')) return;
    setSaving(true);
    try {
      const result = await clubApi.generateRewards(selectedShopId);
      showToast(`Recompensas regeneradas (${result.rewards?.length || 0}) — categoria: ${result.category}`, 'success');
      loadConfig();
    } catch (e) {
      showToast('Error regenerando: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSaving(false);
  };

  const regenerateRewardsAi = async (bypassCache = false) => {
    const msg = bypassCache
      ? '¿Generar nuevas recompensas personalizadas para tu club? Reemplazará las actuales. Tarda entre 15 y 25 segundos.'
      : '¿Generar recompensas personalizadas para tu club? Tarda entre 15 y 25 segundos.';
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const result = await clubApi.generateRewardsAi(selectedShopId, null, bypassCache);
      if (result.warning === 'AI_DISABLED') {
        showToast('Servicio no disponible en este momento — se usó la plantilla.', 'warning');
      } else if (result.warning === 'AI_FAILED') {
        showToast('No se pudieron generar las recompensas — se usó la plantilla.' + (result.resetInHours ? ' Reintenta en ' + result.resetInHours + 'h.' : ''), 'warning');
      } else {
        showToast('Recompensas generadas (' + (result.rewards?.length || 0) + ')', 'success');
      }
      loadConfig();
    } catch (e) {
      showToast('No se pudieron generar las recompensas. Inténtalo de nuevo.', 'error');
    }
    setSaving(false);
  };

  const generateBlogAi = async (bypassCache = false) => {
    const msg = bypassCache
      ? `¿Generar ${blogArticleCount} artículos nuevos para el blog? Reemplaza los artículos actuales. Tarda entre 45 y 90 segundos.`
      : `¿Generar ${blogArticleCount} artículos de blog personalizados para tu club? Tarda entre 45 y 90 segundos.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const result = await clubApi.generateBlogAi(selectedShopId, null, blogArticleCount, bypassCache);
      if (result.error === 'AI_DISABLED' || result.error === 'missing-key') {
        showToast('⚠ ANTHROPIC_API_KEY no está configurada en Render. Ve a Render → Environment → añade la key de Anthropic, y redeploya. Coste: ~$0.10-0.30 por generación de blog.', 'error');
      } else if (result.error === 'rate-limited') {
        showToast(`Límite diario alcanzado para este shop. Reintenta en ${result.resetInHours || 24}h.`, 'error');
      } else if (result.error === 'invalid-schema') {
        showToast('La IA respondió con formato inválido. Reintenta (a veces ocurre con respuestas cortadas).', 'error');
      } else if (result.error === 'timeout') {
        showToast('La generación tardó más de 3 min y se canceló. Prueba con menos artículos (p.ej. 2-3) para acortar el tiempo.', 'error');
      } else if (result.error) {
        showToast(`No se pudo generar el blog: ${result.error}${result.resetInHours ? ' — Reintenta en ' + result.resetInHours + 'h.' : ''}`, 'error');
      } else {
        showToast('Blog generado (' + (result.articles?.length || 0) + ' artículos)', 'success');
        loadBlog();
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message;
      showToast(`No se pudo generar el blog: ${msg || 'error desconocido'}`, 'error');
    }
    setSaving(false);
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Eliminar este articulo? No se puede deshacer.')) return;
    try {
      await clubApi.deleteArticle(articleId, selectedShopId);
      showToast('Articulo eliminado', 'success');
      loadBlog();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const deleteAllBlog = async () => {
    if (!window.confirm('Eliminar TODOS los articulos del blog? No se puede deshacer.')) return;
    try {
      await clubApi.deleteAllBlog(selectedShopId);
      showToast('Blog eliminado', 'success');
      loadBlog();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: G.dim }}>Cargando...</div>;

  // ── Shop selector bar (always visible) ──
  const shopSelector = shops.length > 0 ? (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ color: G.white, fontWeight: 600, fontSize: 13 }}>Tienda:</label>
        <select
          value={selectedShopId}
          onChange={e => handleShopChange(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, color: G.white, fontSize: 13, outline: 'none', cursor: 'pointer' }}
        >
          {shops.map(s => {
            const status = shopsStatus?.shops?.find(ss => ss.shopId === s.id);
            const hasCfg = status?.hasClubConfig;
            return (
              <option key={s.id} value={s.id}>
                {s.name || s.url || s.id} {hasCfg ? '\u2713' : ''}
              </option>
            );
          })}
        </select>
        <div style={{ fontSize: 11, color: G.muted }}>
          Cada tienda puede tener su propio club. Selecciona la tienda que quieres configurar.
        </div>
      </div>

      {/* Migration banner */}
      {shopsStatus?.hasLegacyConfig && (() => {
        const currentShopHasConfig = shopsStatus?.shops?.find(s => s.shopId === selectedShopId)?.hasClubConfig;
        if (currentShopHasConfig) return null;
        return (
          <div style={{ marginTop: 12, padding: 12, background: G.surface, borderRadius: 10, border: `1px solid ${G.borderGold}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14 }}>{'\u26A0\uFE0F'}</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: G.gold, fontSize: 12, fontWeight: 600 }}>Club sin asignar a tienda</div>
              <div style={{ color: G.dim, fontSize: 11, marginTop: 2 }}>Tienes una configuracion de club global. Puedes migrarla a esta tienda.</div>
            </div>
            <button className="btn btn-gold" style={{ fontSize: 11, padding: '6px 14px' }} disabled={migrating}
              onClick={() => migrateToShop(selectedShopId)}>
              {migrating ? 'Migrando...' : 'Migrar a esta tienda'}
            </button>
          </div>
        );
      })()}

      {/* Shop status chips */}
      {shopsStatus?.shops?.length > 1 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {shopsStatus.shops.map(s => (
            <div key={s.shopId} onClick={() => handleShopChange(s.shopId)}
              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 11, cursor: 'pointer', border: `1px solid ${s.shopId === selectedShopId ? G.gold + '60' : G.border}`, background: s.shopId === selectedShopId ? G.goldDim : G.surface, color: s.shopId === selectedShopId ? G.gold : G.dim, transition: 'all .2s', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{s.shopName}</span>
              {s.hasClubConfig && <span style={{ color: s.enabled ? G.green : G.orange, fontSize: 10 }}>{s.enabled ? '\u2713 Activo' : '\u25CB Pausado'}</span>}
              {!s.hasClubConfig && <span style={{ color: G.muted, fontSize: 10 }}>Sin config</span>}
              {s.memberCount > 0 && <span style={{ fontSize: 10, color: G.blue }}>{s.memberCount} miembros</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  // ── Not configured — setup screen ──
  if (!config || (!config.enabled && !config.rewards)) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {shopSelector}
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\u{1F451}'}</div>
          <h2 style={{ color: G.white, marginBottom: 8, fontFamily: 'Cormorant Garamond, serif' }}>Club de Miembros</h2>
          <p style={{ color: G.dim, marginBottom: 24, lineHeight: 1.6, maxWidth: 520, margin: '0 auto 24px' }}>
            {selectedShopId && shops.find(s => s.id === selectedShopId)
              ? `Crea un club exclusivo para "${shops.find(s => s.id === selectedShopId).name || shops.find(s => s.id === selectedShopId).url}". `
              : 'Crea un club exclusivo para tus clientes recurrentes. '}
            Cuando un cliente es rebilleado por primera vez,
            recibe credenciales de acceso y entra en un portal con recompensas que se desbloquean dia a dia.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
            {[
              { icon: '\u{1F4D8}', t: 'eBooks exclusivos' },
              { icon: '\u{1F3AB}', t: 'Codigos de descuento' },
              { icon: '\u{1F3AC}', t: 'Videos premium' },
              { icon: '\u{1F381}', t: 'Sorpresas VIP' },
            ].map((f, i) => (
              <div key={i} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <span style={{ color: G.white, fontSize: 13 }}>{f.t}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-gold" onClick={initClub} disabled={saving} style={{ fontSize: 15, padding: '14px 32px' }}>
            {saving ? 'Creando...' : 'Activar Club de Miembros'}
          </button>
        </div>
      </div>
    );
  }

  // ── Configured — main dashboard ──
  const rewards = config.rewards || [];
  const totalMembers = stats?.total || members.length;
  const activeMembers = stats?.active || members.filter(m => m.status === 'ACTIVE').length;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Shop selector */}
      {shopSelector}

      {/* KPI bar */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Miembros', value: totalMembers, color: G.gold },
          { label: 'Activos', value: activeMembers, color: G.green },
          { label: 'Recompensas', value: rewards.length, color: G.blue },
          { label: 'Estado', value: config.enabled ? 'ACTIVO' : 'PAUSADO', color: config.enabled ? G.green : G.orange },
        ].map((k, i) => (
          <div key={i} className="kpi-card">
            <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontFamily: 'DM Mono, monospace' }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color, fontFamily: 'Cormorant Garamond, serif' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="section-tabs" style={{ marginBottom: 24 }}>
        {['config', 'rewards', 'blog', 'members'].map(t => (
          <button key={t} className={`section-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'config' ? 'Configuracion' : t === 'rewards' ? 'Recompensas' : t === 'blog' ? 'Blog' : 'Miembros'}
          </button>
        ))}
      </div>

      {/* ── CONFIG TAB ── */}
      {tab === 'config' && (
        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ color: G.white, fontSize: 18, fontFamily: 'Cormorant Garamond, serif', margin: 0 }}>Configuracion del Club</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className={config.enabled ? 'btn btn-danger' : 'btn btn-gold'} onClick={() => toggleEnabled(!config.enabled)} disabled={saving} style={{ fontSize: 12 }}>
                {config.enabled ? 'Desactivar' : 'Activar'}
              </button>
              <button className="btn btn-sm" onClick={deleteClub} disabled={saving} style={{ fontSize: 11, background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
                Borrar Club
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, marginBottom: 16 }}>
            <div className="form-group">
              <label style={{ color: G.dim, fontSize: 12, display: 'block', marginBottom: 4 }}>Nombre del club</label>
              <input className="form-input" value={config.name || ''} onChange={e => setConfig({ ...config, name: e.target.value })} placeholder="Mi Tienda CLUB" />
              <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>Se auto-genera como "{'{nombre tienda}'} CLUB" al crear el club</div>
            </div>
            {config.category && (
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 12, display: 'block', marginBottom: 4 }}>Categoria</label>
                <div style={{ padding: '8px 14px', background: G.goldDim, border: `1px solid ${G.gold}40`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                  {config.category}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="form-group">
              <label style={{ color: G.dim, fontSize: 12, display: 'block', marginBottom: 4 }}>Mensaje bienvenida (ES)</label>
              <textarea className="form-input" rows={3} value={config.welcomeMessage?.es || ''} onChange={e => setConfig({ ...config, welcomeMessage: { ...config.welcomeMessage, es: e.target.value } })} placeholder="Bienvenido al Club! Se genera automaticamente..." />
            </div>
            <div className="form-group">
              <label style={{ color: G.dim, fontSize: 12, display: 'block', marginBottom: 4 }}>Mensaje bienvenida (EN)</label>
              <textarea className="form-input" rows={3} value={config.welcomeMessage?.en || ''} onChange={e => setConfig({ ...config, welcomeMessage: { ...config.welcomeMessage, en: e.target.value } })} placeholder="Welcome to the Club! Auto-generated..." />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label style={{ color: G.dim, fontSize: 12, display: 'block', marginBottom: 4 }}>Dominio personalizado (opcional)</label>
            <input className="form-input" value={config.customDomain || ''} onChange={e => setConfig({ ...config, customDomain: e.target.value })} placeholder="club.tutienda.com" />
            <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>Solo necesitas crear un CNAME en tu DNS apuntando a <span style={{ color: G.gold, fontFamily: 'DM Mono, monospace' }}>api.zeroriskpay.com</span>. Al guardar, el dominio se registra automaticamente en el servidor.</div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-gold" onClick={() => saveConfig(config)} disabled={saving} style={{ fontSize: 13 }}>
              {saving ? 'Guardando...' : 'Guardar Configuracion'}
            </button>
          </div>

          {/* Club URL info */}
          <div style={{ marginTop: 24, padding: 16, background: G.surface, borderRadius: 12, border: `1px solid ${G.border}` }}>
            <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>URL del portal de miembros</div>
            <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: G.gold, wordBreak: 'break-all' }}>
              {config.customDomain ? `https://${config.customDomain}` : `https://api.zeroriskpay.com/api/club/portal/?shop=${config.shopId || ''}`}
            </div>
            <div style={{ fontSize: 11, color: G.muted, marginTop: 6 }}>Los miembros acceden aqui con su email y contrasena.</div>
          </div>

          {/* DNS Plug & Play instructions */}
          <div style={{ marginTop: 20, padding: 24, background: G.card, borderRadius: 14, border: `1px solid ${G.borderGold}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 22 }}>{'\u{1F310}'}</span>
              <span style={{ fontWeight: 700, fontSize: 16, color: G.gold, fontFamily: 'Cormorant Garamond, serif' }}>DNS Plug & Play — Dominio Personalizado</span>
            </div>
            <p style={{ color: G.dim, fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
              Para que tus clientes accedan al club desde tu propio dominio (ej. <span style={{ color: G.gold, fontFamily: 'DM Mono, monospace' }}>club.tutienda.com</span>), sigue estos pasos:
            </p>

            {/* Step 1 */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: G.gold, fontWeight: 700, flexShrink: 0 }}>1</div>
              <div>
                <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Entra en tu proveedor de DNS</div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.6 }}>
                  Accede al panel de gestion de tu dominio. Los proveedores mas comunes son:
                  <span style={{ color: G.white }}> Cloudflare, GoDaddy, Namecheap, IONOS, Google Domains</span>.
                  Busca la seccion <span style={{ color: G.gold }}>"DNS"</span> o <span style={{ color: G.gold }}>"Registros DNS"</span>.
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: G.gold, fontWeight: 700, flexShrink: 0 }}>2</div>
              <div>
                <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Crea un registro CNAME</div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>Añade un nuevo registro DNS con estos valores exactos:</div>
                <div style={{ background: G.black, borderRadius: 10, padding: 14, border: `1px solid ${G.border}`, fontFamily: 'DM Mono, monospace', fontSize: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' }}>
                  <span style={{ color: G.muted }}>Tipo:</span><span style={{ color: G.white }}>CNAME</span>
                  <span style={{ color: G.muted }}>Nombre:</span><span style={{ color: G.gold }}>club</span>
                  <span style={{ color: G.muted }}>Valor:</span><span style={{ color: G.green }}>api.zeroriskpay.com</span>
                  <span style={{ color: G.muted }}>TTL:</span><span style={{ color: G.white }}>Auto / 3600</span>
                </div>
                <div style={{ color: G.dim, fontSize: 11, marginTop: 6 }}>
                  {'\u{2139}\u{FE0F}'} El campo "Nombre" es el subdominio. Si pones <span style={{ color: G.gold }}>club</span> y tu dominio es <span style={{ color: G.white }}>tutienda.com</span>, el resultado sera <span style={{ color: G.gold }}>club.tutienda.com</span>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: G.gold, fontWeight: 700, flexShrink: 0 }}>3</div>
              <div>
                <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Escribe el dominio aqui arriba</div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.6 }}>
                  En el campo <span style={{ color: G.gold }}>"Dominio personalizado"</span> de esta pagina, escribe exactamente el dominio que acabas de configurar.
                  Ejemplo: <span style={{ color: G.gold, fontFamily: 'DM Mono, monospace' }}>club.tutienda.com</span>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: G.gold, fontWeight: 700, flexShrink: 0 }}>4</div>
              <div>
                <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Espera la propagacion (5-30 min)</div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.6 }}>
                  Los cambios de DNS pueden tardar entre 5 y 30 minutos en propagarse. En Cloudflare suele ser instantaneo.
                  Si usas Cloudflare, asegurate de que el icono de la nube este en <span style={{ color: G.orange }}>gris (DNS only)</span>, no naranja.
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: G.goldDim, border: `1px solid ${G.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: G.gold, fontWeight: 700, flexShrink: 0 }}>5</div>
              <div>
                <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Comprueba que funciona</div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.6 }}>
                  Abre tu navegador y ve a <span style={{ color: G.gold, fontFamily: 'DM Mono, monospace' }}>{config.customDomain ? `https://${config.customDomain}` : 'https://club.tutienda.com'}</span>.
                  Si ves la pagina de login del club, todo esta correcto.
                </div>
              </div>
            </div>

            {/* Example box for common providers */}
            <div style={{ marginTop: 20, padding: 14, background: G.surface, borderRadius: 10, border: `1px solid ${G.border}` }}>
              <div style={{ fontSize: 11, color: G.muted, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Ejemplo segun proveedor</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { name: 'Cloudflare', path: 'DNS > Add Record > Type: CNAME' },
                  { name: 'GoDaddy', path: 'DNS Management > Add > CNAME' },
                  { name: 'Namecheap', path: 'Advanced DNS > Add Record > CNAME' },
                  { name: 'IONOS', path: 'Dominios > DNS > Añadir CNAME' },
                ].map(p => (
                  <div key={p.name} style={{ fontSize: 11, color: G.dim, lineHeight: 1.5 }}>
                    <span style={{ color: G.white, fontWeight: 600 }}>{p.name}:</span> {p.path}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REWARDS TAB ── */}
      {tab === 'rewards' && (
        <div>
          {/* Generate Rewards button + category info */}
          <div className="card" style={{ padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: G.white, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Generador inteligente de recompensas</div>
              <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.5 }}>
                Usa <strong style={{ color: G.gold }}>Generar con IA</strong> para recompensas 100% personalizadas para tu nicho.
                La opción <strong>plantilla</strong> usa recompensas genéricas por categoría.
                {config.category && <span style={{ color: G.gold, display: 'block', marginTop: 4 }}>Categoría detectada: <strong>{config.category}</strong></span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn"
                onClick={regenerateRewards}
                disabled={saving}
                style={{ fontSize: 12, padding: '10px 16px', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.05)', color: G.white, border: `1px solid ${G.border}` }}
                title="Usa plantillas genéricas por categoría"
              >
                {saving ? '...' : '\u2728 Plantilla'}
              </button>
              <button
                className="btn btn-gold"
                onClick={() => regenerateRewardsAi(false)}
                disabled={saving}
                style={{ fontSize: 12, padding: '10px 16px', whiteSpace: 'nowrap' }}
                title="Genera recompensas personalizadas (15-25s)"
              >
                {saving ? 'Generando...' : '\uD83E\uDD16 Generar con IA'}
              </button>
            </div>
          </div>

          {/* Merchant warnings for discount/surprise rewards */}
          {rewards.some(r => r._merchantWarning) && (
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rewards.filter(r => r._merchantWarning).map((r, i) => (
                <div key={i} style={{ padding: 14, background: 'rgba(232,75,75,0.06)', border: '1px solid rgba(232,75,75,0.2)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{'\u26A0\uFE0F'}</span>
                    <div>
                      <div style={{ color: '#E84B4B', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                        {r.icon} {r.title?.es || r.title || 'Recompensa'}
                      </div>
                      <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.5 }}>
                        {r._merchantWarning?.es || r._merchantWarning}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Merchant notes for ebook/video rewards */}
          {rewards.some(r => r._merchantNote) && (
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rewards.filter(r => r._merchantNote).map((r, i) => (
                <div key={i} style={{ padding: 14, background: 'rgba(75,159,232,0.06)', border: '1px solid rgba(75,159,232,0.2)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{'\u{2139}\u{FE0F}'}</span>
                    <div>
                      <div style={{ color: G.blue, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                        {r.icon} {r.title?.es || r.title || 'Recompensa'}
                      </div>
                      <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.5 }}>
                        {r._merchantNote?.es || r._merchantNote}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reward form */}
          <div className="card" style={{ padding: 24, marginBottom: 20 }}>
            <h3 style={{ color: G.white, fontSize: 16, fontFamily: 'Cormorant Garamond, serif', marginBottom: 16, margin: '0 0 16px' }}>
              {editReward !== null ? 'Editar Recompensa' : 'Nueva Recompensa'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 50px', gap: 12, marginBottom: 12 }}>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Dia</label>
                <input className="form-input" type="number" min="1" value={rewardForm.day} onChange={e => setRewardForm({ ...rewardForm, day: e.target.value })} />
              </div>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Tipo</label>
                <select className="form-input" value={rewardForm.type} onChange={e => setRewardForm({ ...rewardForm, type: e.target.value })}>
                  <option value="ebook">eBook / PDF</option>
                  <option value="discount">Codigo Descuento</option>
                  <option value="video">Video</option>
                  <option value="surprise">Sorpresa</option>
                  <option value="link">Link externo</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Icono</label>
                <input className="form-input" value={rewardForm.icon} onChange={e => setRewardForm({ ...rewardForm, icon: e.target.value })} placeholder={'\u{1F381}'} style={{ textAlign: 'center', fontSize: 18 }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Titulo (ES)</label>
                <input className="form-input" value={rewardForm.title_es} onChange={e => setRewardForm({ ...rewardForm, title_es: e.target.value })} placeholder="Guia exclusiva de bienvenida" />
              </div>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Title (EN)</label>
                <input className="form-input" value={rewardForm.title_en} onChange={e => setRewardForm({ ...rewardForm, title_en: e.target.value })} placeholder="Exclusive Welcome Guide" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Descripcion (ES)</label>
                <textarea className="form-input" rows={2} value={rewardForm.desc_es} onChange={e => setRewardForm({ ...rewardForm, desc_es: e.target.value })} placeholder="Descarga tu eBook exclusivo..." />
              </div>
              <div className="form-group">
                <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>Description (EN)</label>
                <textarea className="form-input" rows={2} value={rewardForm.desc_en} onChange={e => setRewardForm({ ...rewardForm, desc_en: e.target.value })} placeholder="Download your exclusive eBook..." />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ color: G.dim, fontSize: 11, display: 'block', marginBottom: 4 }}>
                Contenido / URL {rewardForm.type === 'discount' ? '(codigo se auto-genera si vacio)' : '(link al recurso)'}
              </label>
              <input className="form-input" value={rewardForm.content} onChange={e => setRewardForm({ ...rewardForm, content: e.target.value })} placeholder={rewardForm.type === 'discount' ? 'MITIENDA-50OFF (o dejar vacio)' : 'https://...'} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" onClick={saveReward} disabled={saving} style={{ fontSize: 12 }}>
                {saving ? 'Guardando...' : editReward !== null ? 'Actualizar' : 'Crear Recompensa'}
              </button>
              {editReward !== null && (
                <button className="btn btn-ghost" onClick={() => { setEditReward(null); setRewardForm({ day: 1, type: 'ebook', title_es: '', title_en: '', desc_es: '', desc_en: '', content: '', icon: '' }); }} style={{ fontSize: 12 }}>
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Rewards list */}
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ color: G.white, fontSize: 16, fontFamily: 'Cormorant Garamond, serif', margin: '0 0 16px' }}>
              Recompensas ({rewards.length})
            </h3>
            {rewards.length === 0 ? (
              <div className="empty">No hay recompensas configuradas</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rewards.sort((a, b) => a.day - b.day).map((r, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: G.surface, borderRadius: 10, border: `1px solid ${G.border}` }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: G.goldDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                      {r.icon || '\u{1F381}'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="badge" style={{ background: G.goldDim, color: G.gold, fontSize: 10 }}>Dia {r.day}</span>
                        <span className="badge" style={{ background: G.blueDim, color: G.blue, fontSize: 10 }}>{r.type}</span>
                      </div>
                      <div style={{ color: G.white, fontSize: 13, fontWeight: 600, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title?.es || r.title || '(sin titulo)'}
                      </div>
                      <div style={{ color: G.muted, fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.description?.es || r.description || ''}
                      </div>
                      {r.contentType === 'html' && <span style={{ fontSize: 9, color: G.green, background: 'rgba(75,232,160,0.08)', padding: '1px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block' }}>Contenido HTML auto-generado</span>}
                      {r.contentType === 'youtube' && <span style={{ fontSize: 9, color: '#E84B4B', background: 'rgba(232,75,75,0.08)', padding: '1px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block' }}>YouTube embed: {r.content}</span>}
                      {(r.productName?.es || r.productName || r.productImg) && <span style={{ fontSize: 9, color: G.gold, background: G.goldDim, padding: '1px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block' }}>Producto: {r.productName?.es || r.productName || ''} {r.productPrice ? '(' + r.productPrice + ')' : ''}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => startEditReward(r, idx)}>Editar</button>
                      <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', color: G.red }} onClick={() => deleteReward(idx)}>Eliminar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BLOG TAB ── */}
      {tab === 'blog' && (
        <div>
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ color: G.white, fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                  {'\uD83D\uDCDD'} Blog del Club
                </div>
                <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.5 }}>
                  Genera artículos exclusivos con IA que tus miembros VIP podrán leer en el portal del club.
                  Contenido 100% personalizado según el nicho de tu tienda, en español e inglés.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ color: G.dim, fontSize: 12 }}>N.º de articulos:</label>
              <select
                value={blogArticleCount}
                onChange={e => setBlogArticleCount(parseInt(e.target.value, 10))}
                style={{ background: G.surface || '#16132A', color: G.white, border: `1px solid ${G.border}`, borderRadius: 8, padding: '6px 10px', fontSize: 13 }}
              >
                <option value={2}>2 articulos</option>
                <option value={3}>3 articulos</option>
                <option value={4}>4 articulos</option>
                <option value={5}>5 articulos</option>
                <option value={6}>6 articulos</option>
                <option value={8}>8 articulos</option>
              </select>
              <button
                className="btn btn-gold"
                onClick={() => generateBlogAi(false)}
                disabled={saving}
                style={{ fontSize: 12, padding: '10px 16px', whiteSpace: 'nowrap' }}
                title="Genera artículos personalizados (45-90s)"
              >
                {saving ? 'Generando...' : '\uD83E\uDD16 Generar Blog con IA'}
              </button>
              {blog.articles?.length > 0 && (
                <>
                  <button
                    className="btn"
                    onClick={() => generateBlogAi(true)}
                    disabled={saving}
                    style={{ fontSize: 11, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', color: G.dim, border: `1px solid ${G.border}` }}
                    title="Forzar nueva generacion (ignora cache)"
                  >
                    Regenerar
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={deleteAllBlog}
                    disabled={saving}
                    style={{ fontSize: 11, padding: '8px 12px', background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 8, cursor: 'pointer' }}
                  >
                    Borrar todo
                  </button>
                </>
              )}
            </div>
            {blog.generatedAt && (
              <div style={{ marginTop: 12, fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                Ultima generacion: {new Date(blog.generatedAt).toLocaleString()} {blog.model ? '• ' + blog.model : ''}
              </div>
            )}
          </div>

          {(!blog.articles || blog.articles.length === 0) ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: G.dim }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{'\uD83D\uDCDA'}</div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>Aun no hay articulos en el blog</div>
              <div style={{ fontSize: 12, color: G.muted }}>Haz clic en "Generar Blog con IA" para crear articulos personalizados para tu tienda.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {blog.articles.map((a, i) => (
                <div key={a.id || i} className="card" style={{ padding: 20, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14, background: 'rgba(201,168,76,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0,
                  }}>{a.coverEmoji || '\uD83D\uDCD6'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: G.white, fontWeight: 600, fontSize: 15, marginBottom: 4, fontFamily: 'Cormorant Garamond, serif' }}>
                      {a.title?.es || a.title?.en || 'Articulo'}
                    </div>
                    <div style={{ color: G.dim, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
                      {a.excerpt?.es || a.excerpt?.en || ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: G.muted, flexWrap: 'wrap' }}>
                      {Array.isArray(a.tags) && a.tags.map((tag, ti) => (
                        <span key={ti} style={{ background: 'rgba(201,168,76,0.08)', color: G.gold, padding: '2px 7px', borderRadius: 5, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{tag}</span>
                      ))}
                      <span style={{ marginLeft: 'auto' }}>{'\u{1F552}'} {a.readTime || 4} min</span>
                      <span>• EN: {a.title?.en ? '\u2713' : '\u2717'}</span>
                      <span>• ES: {a.title?.es ? '\u2713' : '\u2717'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteArticle(a.id)}
                    style={{ background: 'transparent', border: `1px solid ${G.red}40`, color: G.red, borderRadius: 8, padding: '6px 10px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}
                    title="Eliminar articulo"
                  >
                    {'\u2715'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MEMBERS TAB ── */}
      {tab === 'members' && (
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ color: G.white, fontSize: 16, fontFamily: 'Cormorant Garamond, serif', margin: '0 0 16px' }}>
            Miembros del Club ({members.length})
          </h3>
          {members.length === 0 ? (
            <div className="empty">
              <div style={{ fontSize: 36, marginBottom: 12 }}>{'\u{1F451}'}</div>
              <div>Aun no hay miembros</div>
              <div style={{ fontSize: 12, color: G.muted, marginTop: 6 }}>
                Los clientes se inscriben automaticamente cuando se les cobra la primera suscripcion recurrente.
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Miembro desde</th>
                    <th>Dias</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => {
                    const daysSince = Math.floor((Date.now() - new Date(m.enrolledAt || m.createdAt).getTime()) / 86400000);
                    return (
                      <tr key={m.id}>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{m.email}</td>
                        <td>{m.name || m.customerName || '-'}</td>
                        <td>
                          <span className="badge" style={{ background: m.status === 'ACTIVE' ? G.greenDim : G.redDim, color: m.status === 'ACTIVE' ? G.green : G.red }}>
                            {m.status || 'ACTIVE'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: G.dim }}>{m.enrolledAt ? new Date(m.enrolledAt).toLocaleDateString() : '-'}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', color: G.gold }}>{daysSince}</td>
                        <td style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: 11, padding: '3px 8px', color: G.gold }}
                            onClick={() => resendEmail(m.id, m.email)}
                            disabled={resendingEmail === m.id || m.status !== 'ACTIVE'}
                            title="Reenviar email de bienvenida con nueva contrasena"
                          >
                            {resendingEmail === m.id ? '\u23f3' : '\u2709\ufe0f Reenviar email'}
                          </button>
                          <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: G.red }} onClick={() => deleteMember(m.id)}>
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// BENEFICIOS (P&L) — revenue / refunds / fees / COGS / ad-spend / profit
// -----------------------------------------------------------------------------
//
// The Beneficios module is the operator's financial cockpit. It joins five
// data sources to produce a single Profit number:
//
//   Profit = Revenue − Refunds − Fees − COGS − AdSpend
//
// • Revenue / Refunds     come from the already-existing payments collection.
// • Fees                  come from the `feeStructure` saved per gateway
//                         (seeded with a PSP-specific default on creation).
// • COGS                  come from the product-cost catalog edited here
//                         (CRUD + auto-import from pendingOrders).
// • AdSpend               come from Meta Ads (System User token) and Google
//                         Ads (OAuth refresh token) synced every 3h by the
//                         backend cron, plus optional CSV / manual entries.
//
// The UI is organised into 4 inner tabs (kept inside the page so the left
// sidebar stays clean):
//   1. Resumen   — date-range picker + KPI cards + per-shop P&L table + SVG
//                  daily chart.
//   2. Costes    — gateway fee editor (percent / flat / chargeback) and
//                  COGS catalog editor with auto-import from Shopify orders.
//   3. Anuncios  — Meta / Google connect + campaign↔shop mapping + manual
//                  + CSV import fallbacks.
//   4. Forecast  — next-month projection based on rebill rate × avg ticket ×
//                  avg daily signups from the last N days.
//   5. Config    — timezone (default Europe/Madrid), rebill-rate override.
//
// The module is 100% user-scoped on the backend; we only render what the
// current JWT is allowed to see.

// fmtEUR stays in Beneficios for historical reasons — but delegates to the
// global fmt() so the EUR/USD toggle also flips P&L numbers. The "es-ES"
// thousands separator we used before is lost (fmt() uses toFixed) in
// exchange for toggle support; the operator values that more.
function fmtEUR(n, decimals = 2) {
  return fmt(n, { decimals });
}
// Normalize a URL-ish string so "HTTPS://Store.myshopify.com/" matches
// "store.myshopify.com" in the shops list. Used by resolveShopName below.
function normShopUrl(u) {
  if (!u) return '';
  return String(u).trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
}
// Resolve a friendly shop name from the shops array. Falls back to the
// subdomain of a ".myshopify.com" URL so the dashboard never shows the raw
// noisy URL. `shops` = array of {id, url, name}. `shopId` = id or URL.
function resolveShopName(shops, shopId, fallbackName) {
  if (!shops) shops = [];
  const idN = normShopUrl(shopId);
  if (idN) {
    const found = shops.find(s => s.id === shopId || normShopUrl(s.url) === idN || normShopUrl(s.name) === idN);
    if (found && found.name) return found.name;
  }
  const fbN = normShopUrl(fallbackName);
  if (fbN) {
    const found = shops.find(s => normShopUrl(s.url) === fbN || s.id === fallbackName);
    if (found && found.name) return found.name;
  }
  const candidate = fallbackName || shopId || '';
  const cN = normShopUrl(candidate);
  if (cN.endsWith('.myshopify.com')) return cN.slice(0, -'.myshopify.com'.length);
  return candidate || '—';
}
function fmtPct(n, decimals = 2) {
  const num = parseFloat(n) || 0;
  return `${num.toFixed(decimals)}%`;
}
function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: APP_TZ }); } catch { return String(d); }
}
// 2026-09-02 — los pills Hoy/Ayer de Beneficios usan la tz del tenant
// (APP_TZ, sincronizada desde settings.timezone al montar), igual que
// bucketiza ahora el backend (parseRange resuelve del mismo setting).
// Antes NY fijo: entre las 00:00 y las 06:00 de Madrid el pill "Hoy"
// pedía el día anterior. sv-SE da YYYY-MM-DD en la tz objetivo.
function todayIso() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: APP_TZ });
}
function daysAgoIso(n) {
  const d = new Date(Date.now() - n * 86400000);
  return d.toLocaleDateString('sv-SE', { timeZone: APP_TZ });
}
function yesterdayIso() { return daysAgoIso(1); }
function dayBeforeYesterdayIso() { return daysAgoIso(2); }

// ═══════════════════════════════════════════════════════════════════════
//   BALANCES — TailoredPay accounting cockpit
// ═══════════════════════════════════════════════════════════════════════
// Groups NMI transactions by settlement_batch_id (= one bank deposit).
// Shows REAL gross, refunds, net-before-fees — no estimated fees that
// could confuse bank reconciliation. Drill-down per batch available.
function Balances({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Daily settlement data pasted from Payarc/TailoredPay portal
  const [dailyData, setDailyData] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const loadDaily = useCallback(() => {
    fetch(`${BACKEND}/api/balances/daily-settlement`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(d => { if (!d.error) setDailyData(d); })
      .catch(() => {});
  }, []);

  const parseImportText = useCallback(async (text) => {
    if (!text.trim()) { setImportPreview(null); return; }
    try {
      const r = await fetch(`${BACKEND}/api/balances/daily-settlement/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ text }),
      });
      const d = await r.json();
      setImportPreview(d);
    } catch (e) { setImportPreview({ error: e.message }); }
  }, []);

  const saveImport = useCallback(async () => {
    if (!importPreview?.days?.length) return;
    setImporting(true);
    try {
      const r = await fetch(`${BACKEND}/api/balances/daily-settlement/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ days: importPreview.days }),
      });
      const d = await r.json();
      if (d.error) { showToast('Error: ' + d.error, 'error'); }
      else {
        showToast(`Guardados ${d.saved} nuevos · ${d.updated} actualizados`, 'success');
        setShowImportModal(false);
        setImportText('');
        setImportPreview(null);
        loadDaily();
      }
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
    finally { setImporting(false); }
  }, [importPreview, loadDaily, showToast]);

  const deleteDay = useCallback(async (date) => {
    if (!window.confirm(`Borrar el día ${date}?`)) return;
    try {
      const r = await fetch(`${BACKEND}/api/balances/daily-settlement/${date}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await r.json();
      if (d.error) showToast('Error: ' + d.error, 'error');
      else { showToast('Día borrado', 'success'); loadDaily(); }
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  }, [loadDaily, showToast]);

  useEffect(() => { loadDaily(); }, [loadDaily]);

  // `silent` = true → don't show the full-screen spinner (used by auto-refresh
  // and sync-now). Leaves the prior UI up so the page doesn't flash blank.
  // `force` = true → bypass server cache, re-compute live from NMI.
  // `period` = preset filter (today / yesterday / antier / 7d / 30d / 90d / all)
  const [period, setPeriod] = useState('all');
  const load = useCallback(({ silent = false, force = false, periodOverride } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    // Map period preset → from/to query string. "All" = no filter.
    // Date math uses the browser's local TZ which is fine for daily windows.
    const ymd = (d) => d.toISOString().slice(0, 10);
    const todayStr = ymd(new Date());
    const ago = (n) => ymd(new Date(Date.now() - n * 86400000));
    const p = periodOverride || period;
    let qs = '';
    switch (p) {
      case 'today':     qs = `?from=${todayStr}&to=${todayStr}`; break;
      case 'yesterday': qs = `?from=${ago(1)}&to=${ago(1)}`; break;
      case 'antier':    qs = `?from=${ago(2)}&to=${ago(2)}`; break;
      case '7d':        qs = `?from=${ago(6)}&to=${todayStr}`; break;
      case '30d':       qs = `?from=${ago(29)}&to=${todayStr}&days=30`; break;
      case '90d':       qs = `?from=${ago(89)}&to=${todayStr}&days=90`; break;
      default:          qs = ''; // all
    }
    if (force) qs += (qs ? '&' : '?') + 'force=1';
    const url = `${BACKEND}/api/balances/overview${qs}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); if (!silent) setLoading(false); return; }
        setData(d);
        if (!silent) setLoading(false);
      })
      .catch(e => { setError(e.message); if (!silent) setLoading(false); });
  }, [period]);

  // Trigger an immediate backend refund-sync + overview recompute. The
  // operator uses this after issuing a refund from the TailoredPay portal
  // and wants the change reflected without waiting for the 10-min cron.
  // Payarc API sync — pulls settlements + risk holds + reserved + fees +
  // chargebacks straight from Payarc, no XLS upload required. Default
  // window 14 days (covers reconciliation drift). Operator can change
  // via the input. Token must be set in Render env (PAYARC_API_TOKEN).
  const doSyncPayarc = useCallback(async (days = 14) => {
    setSyncing(true);
    try {
      const r = await fetch(`${BACKEND}/api/balances/payarc/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ days }),
      });
      const j = await r.json();
      if (j.error) { showToast('Error: ' + j.error, 'error'); return; }
      if (!j.ok) { showToast(`Sync no ejecutado: ${j.reason || 'razón desconocida'}`, 'error'); return; }
      showToast(`Payarc sync OK — ${j.fetched} días, ${j.inserted} nuevos, ${j.updated} actualizados`, 'success');
      load({ silent: true, force: true });
    } catch (e) {
      showToast('Error Payarc sync: ' + e.message, 'error');
    } finally { setSyncing(false); }
  }, [load, showToast]);

  const doSyncNow = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${BACKEND}/api/balances/sync-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const j = await r.json();
      if (j.error) { showToast('Error: ' + j.error, 'error'); return; }
      const s = j.refundSync || {};
      const msg = `Sync OK — ${s.matched || 0} refund(s) actualizados, ${s.orphanInserted || 0} nuevos insertados`;
      showToast(msg, 'success');
      load({ silent: true });
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    } finally {
      setSyncing(false);
    }
  }, [load, showToast]);

  // Silent auto-refresh every 60s. The server-side cron refreshes the cache
  // every 10 min, so worst case the UI lags ~10 min behind NMI — acceptable.
  // Polling the endpoint every 60s means the user always sees the cache
  // immediately (<100ms response) without burning NMI API calls.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        load({ silent: true });
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const saveReserveConfig = useCallback(async (percent, holdDays) => {
    try {
      const body = {};
      if (percent !== undefined) body.percent = percent;
      if (holdDays !== undefined) body.holdDays = holdDays;
      const r = await fetch(`${BACKEND}/api/balances/reserve-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) return showToast('Error: ' + d.error, 'error');
      showToast('Reserve config guardada · recargando...', 'info');
      load();
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  }, [load, showToast]);

  // Auto-calibrar — calcula el % real desde dailySettlements (Payarc XLS)
  // y guarda automáticamente. El operator no tiene que sacar la calculadora.
  const autoCalibrateReserve = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/balances/reserve-config/auto-calibrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await r.json();
      if (d.error) return showToast('Error: ' + d.error, 'error');
      const cal = d.calibration || {};
      showToast(`Reserve auto-calibrado a ${cal.percent}% (${cal.source})`, 'info');
      load();
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  }, [load, showToast]);

  const saveActualDeposit = useCallback(async (batchId, amount) => {
    try {
      const r = await fetch(`${BACKEND}/api/balances/actual-deposit/${encodeURIComponent(batchId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ amount: amount === '' ? null : parseFloat(amount) }),
      });
      const d = await r.json();
      if (d.error) return showToast('Error: ' + d.error, 'error');
      showToast('Deposit real guardado · recargando...', 'info');
      load();
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  }, [load, showToast]);

  const loadBatch = useCallback((batchId) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      setBatchDetail(null);
      return;
    }
    setExpandedBatch(batchId);
    setBatchLoading(true);
    setBatchDetail(null);
    fetch(`${BACKEND}/api/balances/batch/${encodeURIComponent(batchId)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) { showToast('Error: ' + d.error, 'error'); setBatchLoading(false); return; }
        setBatchDetail(d);
        setBatchLoading(false);
      })
      .catch(e => { showToast('Error: ' + e.message, 'error'); setBatchLoading(false); });
  }, [expandedBatch, showToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center', color: G.muted }}>
          <div style={{ fontSize: 24, marginBottom: 8, animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>◌</div>
          <div style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', letterSpacing: '0.1em' }}>CONSULTANDO NMI API...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <div style={{ background: G.card, border: `1px solid ${G.red}`, borderRadius: 12, padding: 24 }}>
          <div style={{ color: G.red, fontWeight: 700, fontSize: 14, marginBottom: 8 }}>❌ Error consultando TailoredPay</div>
          <div style={{ color: G.muted, fontSize: 13, marginBottom: 12 }}>{error}</div>
          <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6 }}>
            Posibles causas:
            <ul style={{ margin: '6px 0 0 18px' }}>
              <li>No tienes gateways TailoredPay configuradas</li>
              <li>Security key incorrecta</li>
              <li>Tu cuenta TailoredPay no tiene permiso de Query API (pedir a soporte que active "query.php access")</li>
            </ul>
          </div>
          <button className="btn btn-gold btn-sm" style={{ marginTop: 14 }} onClick={load}>↻ Reintentar</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary || {};
  const batches = data.batches || [];
  const refunds = data.refundTimeline || [];
  const fsInfo = data.feeStructureInfo || {};
  const gws = data.gatewaysQueried || [];
  const pending = data.pendingNoBatch || {};
  const today = new Date().toISOString().slice(0, 10);

  const kpiCard = (label, value, sub, color) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || G.white, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: G.dim, marginTop: 6 }}>{sub}</div>
    </div>
  );

  return (
    <div className="content">
      {/* Header info — cache status + sync now button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
          Fuente: NMI query.php · Rango: {data.queryRange?.from} → {data.queryRange?.to} ({data.queryRange?.days}d) · Txs: {data.totalTxsReturned}
          {gws.length > 0 && ' · Gateways: ' + gws.map(g => g.error ? `${g.gateway} ❌` : `${g.gateway}`).join(', ')}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Cache freshness chip */}
          {data._cache && (
            <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', padding: '4px 8px', border: `1px solid ${G.border}`, borderRadius: 6 }}>
              {data._cache.hit ? '📦 caché' : '⚡ live'} ·{' '}
              {data._cache.ageSeconds < 60
                ? `${data._cache.ageSeconds}s`
                : data._cache.ageSeconds < 3600
                  ? `${Math.round(data._cache.ageSeconds / 60)} min`
                  : `${Math.round(data._cache.ageSeconds / 3600)} h`}
              {' atrás'}
              {' · auto-sync cada 10 min'}
            </div>
          )}
          {/* Refund sync status chip — shows when the cron last reconciled portal refunds */}
          {data._refundSync?.lastRunAt && (
            <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', padding: '4px 8px', border: `1px solid ${G.border}`, borderRadius: 6 }}>
              🔄 refunds sincronizados:{' '}
              {data._refundSync.lastResult
                ? `${data._refundSync.lastResult.matched || 0}↻ / ${data._refundSync.lastResult.orphanInserted || 0}+`
                : 'pending'}
            </div>
          )}
          <button className="btn btn-ghost btn-sm" onClick={doSyncNow} disabled={syncing} title="Traer refunds nuevos de TailoredPay + recalcular ahora">
            {syncing ? '↻ Sincronizando...' : '🔄 Sync refunds'}
          </button>
          {data?.payarcApiStatus?.enabled ? (
            <button className="btn btn-ghost btn-sm" onClick={() => doSyncPayarc(14)} disabled={syncing} title="Pull settlements + risk holds + reserved + fees + chargebacks de la API de Payarc (últimos 14 días)">
              {syncing ? '↻ ...' : '⚡ Sync Payarc API'}
            </button>
          ) : (
            <span style={{ fontSize: 10, color: G.dim, fontStyle: 'italic' }} title="Añade PAYARC_API_TOKEN en Render env vars para activar auto-sync">
              ⚠️ PAYARC_API_TOKEN no configurado
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => load({ force: true })}>↻ Recargar</button>
        </div>
      </div>

      {/* Period filter — Hoy / Ayer / Anteayer / 7d / 30d / 90d / Todo */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: G.muted, marginRight: 8 }}>Periodo:</span>
        {[
          ['today', 'Hoy'],
          ['yesterday', 'Ayer'],
          ['antier', 'Anteayer'],
          ['7d', 'Últimos 7d'],
          ['30d', 'Últimos 30d'],
          ['90d', 'Últimos 90d'],
          ['all', 'Todo'],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`btn btn-sm ${period === key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setPeriod(key); load({ periodOverride: key }); }}
            style={{
              padding: '4px 10px', fontSize: 12,
              background: period === key ? G.gold : G.card,
              color: period === key ? G.black : G.muted,
              border: `1px solid ${period === key ? G.gold : G.border}`,
              borderRadius: 6, cursor: 'pointer', fontWeight: period === key ? 700 : 500,
            }}
          >
            {label}
          </button>
        ))}
        {period !== 'all' && (
          <span style={{ fontSize: 10, color: G.dim, marginLeft: 'auto' }}>
            Snapshots (Reserve, Siguiente pago) ignoran el filtro
          </span>
        )}
      </div>

      {/* KPI cards — con rolling reserve aplicado + Payarc summary cuando disponible */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Ya depositado: si tenemos Payarc summary, usar realToBank (released - riskHold);
            si no, fallback al cálculo NMI side. */}
        {(() => {
          const ps = data.payarcSummary;
          const realDeposited = ps != null
            ? ps.realToBankTotal
            : (s.alreadyDepositedNetAfterReserve || 0);
          const sub = ps != null
            ? `${ps.daysImported} días Payarc · Risk Hold $${ps.riskHoldTotal.toFixed(2)}${ps.riskHoldTotal > 0 ? ' ⚠️ retenido' : ''}`
            : `${s.batchesDeposited || 0} batches · ${data.rollingReserve?.percent || 0}% reserve retenido ($${(s.alreadyDepositedReserveHeld || 0).toFixed(2)})`;
          return kpiCard(
            'Ya depositado (real al banco)',
            `$${realDeposited.toFixed(2)}`,
            sub,
            ps?.riskHoldTotal > 0 ? G.red : G.green,
          );
        })()}
        {/* Por llegar: si Payarc tiene un Pending Settlement reciente, mostrarlo como
            "Siguiente pago" con la fecha. */}
        {(() => {
          const ps = data.payarcSummary;
          const hasPending = ps && ps.pendingSettlementLatest > 0;
          const value = hasPending
            ? `$${ps.pendingSettlementLatest.toFixed(2)}`
            : `$${(s.totalExpectedAfterReserve || 0).toFixed(2)}`;
          const sub = hasPending
            ? `Siguiente pago${ps.pendingSettlementLatestDate ? ' · ' + ps.pendingSettlementLatestDate : ''}`
            : `${s.batchesUpcoming || 0} batches + ${s.pendingTxsCount || 0} pending · reserve adicional $${((s.upcomingReserveHeld || 0) + (s.pendingReserveHeld || 0)).toFixed(2)}`;
          return kpiCard(
            hasPending ? 'Siguiente pago (Payarc)' : 'Por llegar (real al banco)',
            value,
            sub,
            G.gold,
          );
        })()}
        {/* Risk Hold — sólo si Payarc tiene 100% Risk Hold marcado.
            Es el dinero que Payarc bloqueó y NO va a llegar al banco aún. */}
        {data.payarcSummary && data.payarcSummary.riskHoldTotal > 0 && kpiCard(
          '🚫 Risk Hold (bloqueado)',
          `$${data.payarcSummary.riskHoldTotal.toFixed(2)}`,
          `Payarc retiene 100% · pendiente de liberación`,
          G.red,
        )}
        {kpiCard(
          'Reserve retenido (total)',
          `$${(s.reserveStillHeld || 0).toFixed(2)}`,
          `${data.rollingReserve?.percent || 0}% × ${data.rollingReserve?.holdDays || 0} días · ya liberado $${(s.reserveAlreadyReleased || 0).toFixed(2)}`,
          '#8A7BD8',
        )}
        {kpiCard(
          'Refunds',
          `$${(s.todayRefundAmount || 0).toFixed(2)}`,
          `${s.todayRefundCount || 0} hoy · ${s.last7dRefundCount || 0} en 7d ($${(s.last7dRefundAmount || 0).toFixed(2)})`,
          G.red,
        )}
      </div>

      {/* Calibration banner — shows average deduction % from actual deposits */}
      {data.calibration && data.calibration.calibratedCount > 0 && (
        <div style={{
          background: 'rgba(138, 123, 216, 0.1)',
          border: `1px solid #8A7BD880`,
          borderRadius: 10, padding: 14, marginBottom: 14, fontSize: 13, color: G.muted, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, color: '#8A7BD8', marginBottom: 6 }}>
            🎯 Calibrado con {data.calibration.calibratedCount} batch{data.calibration.calibratedCount > 1 ? 'es' : ''} real{data.calibration.calibratedCount > 1 ? 'es' : ''}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, marginBottom: 6 }}>
            <div>Deducción total promedio: <strong style={{ color: G.white }}>{data.calibration.avgDeductionPct}%</strong></div>
            <div>Reserve: <strong style={{ color: G.white }}>{data.calibration.avgReservePct}%</strong></div>
            <div>Fees efectivos (resto): <strong style={{ color: G.white }}>{data.calibration.avgFeesPct}%</strong></div>
          </div>
          <div style={{ fontSize: 11, color: G.dim }}>{data.calibration.note}</div>
        </div>
      )}

      {/* Diagnostic banner — explicit about why deposits might not show in bank */}
      {data.diagnostic && (
        <div style={{
          background: data.diagnostic.level === 'error' ? 'rgba(220, 53, 69, 0.1)' : data.diagnostic.level === 'warning' ? 'rgba(201, 168, 76, 0.1)' : 'rgba(40, 167, 69, 0.08)',
          border: `1px solid ${data.diagnostic.level === 'error' ? G.red : data.diagnostic.level === 'warning' ? G.gold : G.green}60`,
          borderRadius: 10, padding: 14, marginBottom: 14, fontSize: 13, color: G.muted, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, color: data.diagnostic.level === 'error' ? G.red : data.diagnostic.level === 'warning' ? G.gold : G.green, marginBottom: 6 }}>
            {data.diagnostic.level === 'error' ? '❌' : data.diagnostic.level === 'warning' ? '⚠️' : 'ℹ️'} {data.diagnostic.likelyCause}
          </div>
          <div style={{ marginBottom: 8 }}>{data.diagnostic.explanation}</div>
          <div style={{ fontSize: 12, color: G.white, fontWeight: 600 }}>→ {data.diagnostic.action}</div>
        </div>
      )}

      {/* Warning sobre fees */}
      <div style={{ background: 'rgba(201, 168, 76, 0.08)', border: `1px solid ${G.gold}40`, borderRadius: 10, padding: 14, marginBottom: 18, fontSize: 12, color: G.muted, lineHeight: 1.55 }}>
        <strong style={{ color: G.gold }}>⚠️ Importante:</strong> Los montos que ves son <strong>NET ANTES DE FEES</strong> — exactamente lo que TailoredPay RECIBE del acquirer. TailoredPay luego aplica interchange + assessment + monthly fees ({fsInfo.percent}%+${fsInfo.flat} por tx + otros) que <strong>NMI query.php no expone</strong>. Para el depósito EXACTO en tu banco, compara por <code style={{ background: G.card, padding: '1px 5px', borderRadius: 3 }}>batchId</code> en el portal de TailoredPay → Reports → Batch Settlement.
      </div>

      {/* Batches timeline — scrollable so all historical deposits fit */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: G.gold }}>
            🏦 Batches de settlement (1 batch = 1 depósito bancario)
          </div>
          {batches.length > 0 && (
            <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
              <strong style={{ color: G.white }}>{batches.length}</strong> batches ·
              últimos <strong style={{ color: G.white }}>{data.queryRange?.days || 730}d</strong>
            </div>
          )}
        </div>
        {batches.length === 0 && pending.salesCount === 0 ? (
          <div style={{ color: G.muted, fontSize: 12, padding: 12 }}>Sin batches en el periodo consultado.</div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 720, overflowY: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Estado</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fecha settlement</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Batch ID</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Gross</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Refunds</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Net NMI</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: G.green }}>Real al banco</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#8A7BD8' }}>% deducido</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sales</th>
                  <th style={{ padding: '8px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <React.Fragment key={b.batchId}>
                    <tr style={{ borderTop: `1px solid ${G.border}` }}>
                      <td style={{ padding: '8px 12px', cursor: 'pointer' }} onClick={() => loadBatch(b.batchId)}>
                        {b.status === 'depositado' && <span style={{ color: G.green, fontWeight: 700, fontSize: 11 }}>✓ depositado</span>}
                        {b.status === 'hoy' && <span style={{ color: G.gold, fontWeight: 700, fontSize: 11 }}>◉ hoy</span>}
                        {b.status === 'futuro' && <span style={{ color: G.gold, fontWeight: 700, fontSize: 11 }}>⏳ futuro</span>}
                      </td>
                      <td style={{ padding: '8px 12px', color: G.white, fontWeight: 600 }}>
                        {b.settlementDate || '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: G.dim, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>{b.batchId}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>${b.gross.toFixed(2)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: b.refunds > 0 ? G.red : G.muted, fontFamily: 'DM Mono, monospace' }}>-${b.refunds.toFixed(2)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>${b.netBeforeFees.toFixed(2)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={b.actualDeposit != null ? b.actualDeposit : ''}
                          placeholder={b.netAfterReserve.toFixed(2)}
                          onBlur={(e) => {
                            const newVal = e.target.value;
                            const oldVal = b.actualDeposit != null ? String(b.actualDeposit) : '';
                            if (newVal !== oldVal) saveActualDeposit(b.batchId, newVal);
                          }}
                          style={{
                            width: 90, textAlign: 'right', padding: '4px 6px',
                            background: b.actualDeposit != null ? 'rgba(40, 167, 69, 0.1)' : G.black,
                            color: b.actualDeposit != null ? G.green : G.muted,
                            border: `1px solid ${G.border}`, borderRadius: 4,
                            fontFamily: 'DM Mono, monospace', fontSize: 12,
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {b.actualDeductionPct != null ? (
                          <span style={{ color: '#8A7BD8', fontWeight: 700 }}>
                            {b.actualDeductionPct}%
                            <div style={{ fontSize: 10, color: G.dim }}>
                              res {data.rollingReserve?.percent}% + fees ~{b.implicitFeesPct}%
                            </div>
                          </span>
                        ) : (
                          <span style={{ color: G.dim, fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', cursor: 'pointer' }} onClick={() => loadBatch(b.batchId)}>
                        {b.salesCount}{b.refundCount > 0 && <span style={{ color: G.red }}> ({b.refundCount}R)</span>}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: G.gold, fontSize: 10, cursor: 'pointer' }} onClick={() => loadBatch(b.batchId)}>{expandedBatch === b.batchId ? '▼' : '▶'}</td>
                    </tr>
                    {expandedBatch === b.batchId && (
                      <tr style={{ background: G.black }}>
                        <td colSpan={10} style={{ padding: 16 }}>
                          {batchLoading ? (
                            <div style={{ color: G.muted, fontSize: 12, textAlign: 'center', padding: 20 }}>Cargando transacciones del batch...</div>
                          ) : !batchDetail ? (
                            <div style={{ color: G.muted, fontSize: 12 }}>Click en el batch para cargar.</div>
                          ) : (
                            <div>
                              <div style={{ fontSize: 11, color: G.muted, marginBottom: 10, fontFamily: 'DM Mono, monospace' }}>
                                {batchDetail.totalTxs} transacciones · {batchDetail.sales} sales · {batchDetail.refunds} refunds · {batchDetail.voids} voids
                              </div>
                              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                <table className="table" style={{ width: '100%', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ color: G.muted, textTransform: 'uppercase', fontSize: 9, letterSpacing: 1 }}>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Tipo</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Fecha</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Customer</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Card</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'right' }}>Amount</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Tx ID</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Auth</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {batchDetail.transactions.map(tx => (
                                      <tr key={tx.transactionId} style={{ borderTop: `1px solid ${G.border}` }}>
                                        <td style={{ padding: '6px 10px' }}>
                                          {tx.type === 'sale' && <span style={{ color: G.green, fontWeight: 600 }}>sale</span>}
                                          {tx.type === 'refund' && <span style={{ color: G.red, fontWeight: 600 }}>refund</span>}
                                          {tx.type === 'void' && <span style={{ color: G.muted, fontWeight: 600 }}>void</span>}
                                        </td>
                                        <td style={{ padding: '6px 10px', color: G.dim, fontFamily: 'DM Mono, monospace', fontSize: 10 }}>{(tx.firstSeenAt || '').slice(0, 16)}</td>
                                        <td style={{ padding: '6px 10px', color: G.white }}>{tx.customerName || tx.customerEmail || '—'}<div style={{ fontSize: 10, color: G.dim }}>{tx.customerEmail}</div></td>
                                        <td style={{ padding: '6px 10px', color: G.muted, fontSize: 11 }}>{tx.ccType} ••{tx.ccLast4}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: tx.type === 'refund' ? G.red : tx.type === 'sale' ? G.green : G.muted }}>
                                          {tx.type === 'refund' ? '-' : ''}${tx.amount.toFixed(2)}
                                        </td>
                                        <td style={{ padding: '6px 10px', color: G.dim, fontSize: 10, fontFamily: 'DM Mono, monospace' }}>{tx.transactionId}</td>
                                        <td style={{ padding: '6px 10px', color: G.dim, fontSize: 10 }}>{tx.authCode || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {/* Pending sin batch */}
                {pending.salesCount > 0 && (
                  <tr style={{ borderTop: `1px solid ${G.border}`, background: 'rgba(201, 168, 76, 0.05)' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ color: G.gold, fontWeight: 700, fontSize: 11 }}>⏳ pending</span>
                    </td>
                    <td style={{ padding: '8px 12px', color: G.gold, fontWeight: 600 }}>Sin asignar<div style={{ fontSize: 10, color: G.dim }}>2-3 días hábiles</div></td>
                    <td style={{ padding: '8px 12px', color: G.dim, fontSize: 11 }}>—</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>${pending.gross.toFixed(2)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: pending.refunds > 0 ? G.red : G.muted, fontFamily: 'DM Mono, monospace' }}>-${pending.refunds.toFixed(2)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>${pending.net.toFixed(2)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: G.dim, fontSize: 11 }}>(pendiente)</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: G.dim, fontSize: 11 }}>—</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{pending.salesCount}{pending.refundCount > 0 && <span style={{ color: G.red }}> ({pending.refundCount}R)</span>}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rolling reserve schedule */}
      {data.rollingReserve && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#8A7BD8' }}>
              🔒 Rolling Reserve — {data.rollingReserve.percent}% retenido por {data.rollingReserve.holdDays} días
              <span style={{ fontSize: 10, color: G.dim, marginLeft: 8 }}>({data.rollingReserve.source})</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: G.muted }}>
              <span>Ajustar:</span>
              <input
                type="number" step="0.1" min="0" max="50"
                defaultValue={data.rollingReserve.percent}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value);
                  if (isFinite(v) && v !== data.rollingReserve.percent) saveReserveConfig(v, undefined);
                }}
                style={{ width: 60, padding: '4px 6px', background: G.black, color: G.white, border: `1px solid ${G.border}`, borderRadius: 4, fontFamily: 'DM Mono, monospace', fontSize: 12, textAlign: 'right' }}
              />
              <span>%</span>
              <span style={{ marginLeft: 8 }}>×</span>
              <input
                type="number" step="1" min="0" max="365"
                defaultValue={data.rollingReserve.holdDays}
                onBlur={(e) => {
                  const v = parseInt(e.target.value);
                  if (isFinite(v) && v !== data.rollingReserve.holdDays) saveReserveConfig(undefined, v);
                }}
                style={{ width: 60, padding: '4px 6px', background: G.black, color: G.white, border: `1px solid ${G.border}`, borderRadius: 4, fontFamily: 'DM Mono, monospace', fontSize: 12, textAlign: 'right' }}
              />
              <span>días</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={autoCalibrateReserve}
                title="Calcula el % real desde tus dailySettlements (Payarc XLS) y lo guarda. Si nunca subiste un XLS usa 5.5% por defecto."
                style={{ marginLeft: 8 }}
              >
                ⚡ Auto-calibrar
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Actualmente retenido</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#8A7BD8', fontFamily: 'DM Mono, monospace' }}>${(data.rollingReserve.stillHeld || 0).toFixed(2)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Ya liberado</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>${(data.rollingReserve.alreadyReleased || 0).toFixed(2)}</div>
            </div>
          </div>
          {(data.rollingReserve.upcomingReleases || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, color: G.muted, marginBottom: 8 }}>Próximas liberaciones (primeras 10):</div>
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: G.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Fecha release</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right' }}>Monto</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Batch origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rollingReserve.upcomingReleases.slice(0, 10).map((r, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${G.border}` }}>
                        <td style={{ padding: '6px 10px', color: G.white }}>{r.date}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: G.green, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>+${r.amount.toFixed(2)}</td>
                        <td style={{ padding: '6px 10px', color: G.dim, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>{r.batchId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div style={{ fontSize: 11, color: G.dim, marginTop: 12, lineHeight: 1.5 }}>
            {data.rollingReserve.note}
          </div>
        </div>
      )}

      {/* Refunds per day — from NMI (all-time) + local DB fallback */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: G.red }}>
            ↩ Refunds por día ({data.queryRange?.days || 730}d)
          </div>
          {refunds.length > 0 && (
            <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
              Total: <strong style={{ color: G.red }}>-${refunds.reduce((s, r) => s + r.amount, 0).toFixed(2)}</strong>
              {' · '}
              <strong style={{ color: G.white }}>{refunds.reduce((s, r) => s + r.count, 0)}</strong> eventos
            </div>
          )}
        </div>
        {refunds.length === 0 ? (
          <div style={{ color: G.muted, fontSize: 12, padding: 12 }}>Sin refunds en el periodo consultado. 🎉</div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fecha</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Count</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Monto total</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map(r => (
                  <tr key={r.date} style={{ borderTop: `1px solid ${G.border}` }}>
                    <td style={{ padding: '8px 12px', color: G.white }}>{r.date}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{r.count}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: G.red, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>-${r.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ────────────────────────────────────────────────────────────────
          Datos reales del portal del procesador — pegados por el
          operator para tener números exactos (Released Funds, Pending
          Settlement, Reserved Funds, Fees, Chargebacks por día). Esto
          NO viene de NMI query.php — son los números que aparecen en el
          settlement summary del portal y los únicos exactos para el
          rolling reserve total y los fees reales.
      ──────────────────────────────────────────────────────────────── */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: G.gold }}>📊 Datos reales del portal Payarc</div>
            <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>Released Funds · Pending Settlement · Rolling Reserve · Fees · Chargebacks por día</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                showToast('Sincronizando con Payarc API…', 'info');
                try {
                  const r = await fetch(`${BACKEND}/api/balances/payarc/sync`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ days: 90 }),
                  });
                  const d = await r.json();
                  if (d.error) showToast('Error: ' + d.error, 'error');
                  else if (d.ok === false) showToast('Sync skip: ' + (d.reason || 'razón desconocida'), 'info');
                  else { showToast(`Sync OK · ${d.inserted} nuevos · ${d.updated} actualizados`, 'success'); loadDaily(); }
                } catch (e) { showToast('Error: ' + e.message, 'error'); }
              }}
              style={{ background: G.green, color: '#000', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              title="Pull desde Payarc API (últimos 90 días)"
            >
              ⚡ Sync Payarc API
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('Pull histórico de Payarc (365 días)? Tarda 1-2 min.')) return;
                showToast('Backfill 365 días en progreso…', 'info');
                try {
                  const r = await fetch(`${BACKEND}/api/balances/payarc/backfill`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ days: 365 }),
                  });
                  const d = await r.json();
                  if (d.error) showToast('Error: ' + d.error, 'error');
                  else { showToast(`Backfill OK · ${d.inserted} nuevos · ${d.updated} actualizados`, 'success'); loadDaily(); }
                } catch (e) { showToast('Error: ' + e.message, 'error'); }
              }}
              style={{ background: G.card, color: G.gold, border: `1px solid ${G.gold}`, padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              title="Pull histórico completo de Payarc (365 días)"
            >
              🔄 Backfill 365d
            </button>
            <button
              onClick={() => { setShowImportModal(true); setImportText(''); setImportPreview(null); }}
              style={{ background: G.surface, color: G.dim, border: `1px solid ${G.border}`, padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              title="Pega manual desde el portal (fallback si no funciona la API)"
            >
              📋 Importar manual
            </button>
          </div>
        </div>

        {dailyData && dailyData.rows && dailyData.rows.length > 0 ? (
          <>
            {/* Totales agregados de la ventana visible */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Rolling Reserve actual</div>
                <div style={{ fontSize: 20, color: G.goldLight, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  ${(dailyData.totals?.currentReservedFunds || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>retenido por TailoredPay</div>
              </div>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Pending Settlement</div>
                <div style={{ fontSize: 20, color: G.gold, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  ${(dailyData.totals?.currentPendingSettlement || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>próximo deposito al banco</div>
              </div>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Released (suma)</div>
                <div style={{ fontSize: 20, color: G.green, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  ${(dailyData.totals?.releasedFunds || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>{dailyData.totals?.txCount || 0} tx · liberado total</div>
              </div>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Fees totales</div>
                <div style={{ fontSize: 20, color: G.red, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  -${Math.abs(dailyData.totals?.fees || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>comisiones acumuladas</div>
              </div>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Chargebacks evitados</div>
                <div style={{ fontSize: 20, color: G.orange, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  -${(dailyData.totals?.chargebacksAmount || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>{dailyData.totals?.chargebacksCount || 0} disputas (RDR refunds)</div>
              </div>
              <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Volume total</div>
                <div style={{ fontSize: 20, color: G.white, fontWeight: 700, fontFamily: 'DM Mono, monospace', marginTop: 4 }}>
                  ${(dailyData.totals?.transactionVolume || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>transaccion volume</div>
              </div>
            </div>

            {/* Tabla por día */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: G.surface }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>FECHA</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>RELEASED</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>PENDING</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>RESERVE</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>FEES</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>VOLUME</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>CB</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.rows.map(r => (
                    <tr key={r.date} style={{ borderTop: `1px solid ${G.border}` }}>
                      <td style={{ padding: '8px 10px', color: G.white, fontWeight: 600 }}>{r.date}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.green, fontFamily: 'DM Mono, monospace' }}>
                        {r.releasedFunds != null ? `$${r.releasedFunds.toFixed(2)}` : '—'}
                        {r.releasedFundsCount && <div style={{ fontSize: 10, color: G.dim }}>{r.releasedFundsCount} tx</div>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.gold, fontFamily: 'DM Mono, monospace' }}>
                        {r.pendingSettlement != null ? `$${r.pendingSettlement.toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.goldLight, fontFamily: 'DM Mono, monospace' }}>
                        {r.reservedFunds != null ? `$${r.reservedFunds.toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.red, fontFamily: 'DM Mono, monospace' }}>
                        {r.fees != null ? `-$${Math.abs(r.fees).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.white, fontFamily: 'DM Mono, monospace' }}>
                        {r.transactionVolume != null ? `$${r.transactionVolume.toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: G.orange, fontFamily: 'DM Mono, monospace' }}>
                        {r.chargebacks?.length > 0
                          ? `${r.chargebacks.length} · -$${r.chargebacks.reduce((s, c) => s + (c.amount || 0), 0).toFixed(2)}`
                          : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <button
                          onClick={() => deleteDay(r.date)}
                          style={{ background: 'transparent', border: 'none', color: G.muted, cursor: 'pointer', fontSize: 14 }}
                          title="Borrar este día"
                        >×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div style={{ padding: 30, textAlign: 'center', color: G.muted, fontSize: 12 }}>
            Aún no hay datos importados. Pulsa <strong style={{ color: G.gold }}>Importar datos del portal</strong> y pega el resumen del settlement (cualquier rango de días).
          </div>
        )}
      </div>

      {/* Modal de importación */}
      {showImportModal && (
        <div
          onClick={() => setShowImportModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(6,5,10,0.85)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: G.card, border: `1px solid ${G.borderGold}`, borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ padding: 20, borderBottom: `1px solid ${G.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: G.gold }}>Importar datos del portal Payarc</div>
                <div style={{ fontSize: 12, color: G.muted, marginTop: 4 }}>
                  Pega el resumen del settlement (varios días al mismo tiempo). Detecto automáticamente Released, Pending, Reserve, Fees, Volume y Chargebacks.
                </div>
              </div>
              <button onClick={() => setShowImportModal(false)} style={{ background: 'transparent', border: 'none', color: G.muted, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <textarea
                value={importText}
                onChange={e => { setImportText(e.target.value); parseImportText(e.target.value); }}
                placeholder={`Ejemplo:\n04/30/2026 -$29.00 USD 1 Chargebacks 2026118006273\n04/30/2026 $4,726.17 USD Transaction Volume\n04/30/2026 -$315.68 USD Fees\n04/30/2026 $3,341.12 USD 236 Released Funds\n04/30/2026 $4,143.13 USD Pending Settlement\n04/30/2026 $267.36 USD Reserved Funds`}
                style={{ width: '100%', minHeight: 200, padding: 12, background: G.black, border: `1px solid ${G.border}`, color: G.white, borderRadius: 8, fontFamily: 'DM Mono, monospace', fontSize: 12, resize: 'vertical' }}
              />

              {/* Preview */}
              {importPreview && (
                <div style={{ marginTop: 16, padding: 14, background: G.surface, borderRadius: 8, border: `1px solid ${G.border}` }}>
                  <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                    Vista previa: {importPreview.days?.length || 0} día{importPreview.days?.length === 1 ? '' : 's'} detectado{importPreview.days?.length === 1 ? '' : 's'}
                  </div>
                  {importPreview.errors?.length > 0 && importPreview.errors.map((e, i) => (
                    <div key={i} style={{ color: G.red, fontSize: 11, marginBottom: 6 }}>⚠ {e}</div>
                  ))}
                  {importPreview.days && importPreview.days.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 11, fontFamily: 'DM Mono, monospace' }}>
                      {importPreview.days.map(d => (
                        <div key={d.date} style={{ padding: 8, background: G.black, borderRadius: 6, border: `1px solid ${G.border}` }}>
                          <div style={{ color: G.gold, fontWeight: 700, marginBottom: 4 }}>{d.date}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 4, color: G.dim }}>
                            {d.releasedFunds != null && <div>Released: <span style={{ color: G.green }}>${d.releasedFunds.toFixed(2)}</span>{d.releasedFundsCount && <span style={{ color: G.muted }}> ({d.releasedFundsCount})</span>}</div>}
                            {d.pendingSettlement != null && <div>Pending: <span style={{ color: G.gold }}>${d.pendingSettlement.toFixed(2)}</span></div>}
                            {d.reservedFunds != null && <div>Reserve: <span style={{ color: G.goldLight }}>${d.reservedFunds.toFixed(2)}</span></div>}
                            {d.fees != null && <div>Fees: <span style={{ color: G.red }}>${d.fees.toFixed(2)}</span></div>}
                            {d.transactionVolume != null && <div>Volume: <span style={{ color: G.white }}>${d.transactionVolume.toFixed(2)}</span></div>}
                            {d.chargebacks?.length > 0 && <div>CBs: <span style={{ color: G.orange }}>{d.chargebacks.length} · -${d.chargebacks.reduce((s, c) => s + (c.amount || 0), 0).toFixed(2)}</span></div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: 16, borderTop: `1px solid ${G.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowImportModal(false)} style={{ padding: '8px 14px', background: G.black, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
              <button
                onClick={saveImport}
                disabled={!importPreview?.days?.length || importing}
                style={{ padding: '8px 14px', background: importPreview?.days?.length ? G.gold : G.muted, color: '#000', border: 'none', borderRadius: 6, cursor: importPreview?.days?.length ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, opacity: importing ? 0.6 : 1 }}
              >
                {importing ? 'Guardando…' : `Guardar ${importPreview?.days?.length || 0} día${importPreview?.days?.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fee structure info + notes */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: G.gold, marginBottom: 14 }}>
          ⚙️ Info de fee structure (informativa — NO aplicada)
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: G.muted, marginBottom: 14 }}>
          <div>Percent: <strong style={{ color: G.white }}>{fsInfo.percent}%</strong></div>
          <div>Flat: <strong style={{ color: G.white }}>${fsInfo.flat}</strong></div>
          <div>Chargeback fee: <strong style={{ color: G.white }}>${fsInfo.chargebackFee}</strong></div>
          <div>Refund mantiene fee: <strong style={{ color: G.white }}>{fsInfo.refundKeepsFee ? 'sí' : 'no'}</strong></div>
        </div>
        <div style={{ fontSize: 11, color: G.dim, lineHeight: 1.6, marginBottom: 10 }}>
          <strong style={{ color: G.red }}>{fsInfo.warning}</strong>
        </div>
        <div style={{ fontSize: 11, color: G.dim, lineHeight: 1.6 }}>
          {(data.notes || []).map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Chargebacks page — full dispute ledger for the operator.
// Reads from /api/dashboard/chargebacks-list (returns enriched CBs +
// breakdowns). Lives in the "Finanzas" sidebar group right after Beneficios.
//
// What it shows:
//   1. KPI strip — total CBs, open, RDR, won, lost, $ disputed, $ open
//   2. Visa-zone status (normal / warning / VDMP / VFMP) computed from the
//      30d window cb-rate already returned by /chargebacks-summary.
//   3. Breakdown cards — by status, by reason, by processor
//   4. Pending response queue — sorted by daysLeft asc (most urgent first)
//   5. Full ledger table — filterable by status/processor/search
//   6. Detail modal — click a row to see the full record + linked payment
function Chargebacks({ showToast }) {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterProcessor, setFilterProcessor] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [emailHint, setEmailHint] = useState('');

  // Reset evidence state when changing the selected chargeback
  useEffect(() => { setEvidence(null); setEmailHint(''); }, [selected?.caseNumber]);

  const fetchEvidence = useCallback(async () => {
    if (!selected?.caseNumber) return;
    setEvidenceLoading(true);
    try {
      const data = await beneficiosApi.chargebackEvidence(selected.caseNumber, emailHint || undefined);
      setEvidence(data);
    } catch (e) {
      showToast('Error generando evidencia: ' + (e.response?.data?.error || e.message), 'error');
    }
    setEvidenceLoading(false);
  }, [selected?.caseNumber, emailHint, showToast]);

  const copyEvidenceText = useCallback(() => {
    if (!evidence?.prebuiltText) return;
    navigator.clipboard.writeText(evidence.prebuiltText).then(
      () => showToast('Evidencia copiada al portapapeles', 'success'),
      () => showToast('No se pudo copiar', 'error')
    );
  }, [evidence, showToast]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      beneficiosApi.chargebacksList().catch(e => ({ __err: e.message || 'fetch failed' })),
      beneficiosApi.chargebacksSummary().catch(() => null),
    ]).then(([list, sum]) => {
      if (list && list.__err) { setError(list.__err); setLoading(false); return; }
      setData(list);
      setSummary(sum);
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: G.muted }}>
        <div style={{ fontSize: 13, letterSpacing: 1 }}>Cargando chargebacks…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 30, background: G.redDim, border: '1px solid ' + G.red + '40', borderRadius: 10, color: G.red, fontSize: 13 }}>
        Error: {error}
      </div>
    );
  }
  if (!data) return null;

  const cbs = data.chargebacks || [];
  const sum = data.summary || {};
  const breakdown = data.breakdown || {};

  // Visa-rate zone (from summary endpoint, 30d window)
  const w30 = summary?.windows?.['30d'] || {};
  const cbRate30 = w30.cbRate || 0;
  const zone = w30.zone || 'normal';
  const zoneColor = zone === 'vfmp' ? G.red : zone === 'vdmp' ? G.orange : zone === 'warning' ? G.goldLight : G.green;
  const zoneLabel = zone === 'vfmp' ? 'VFMP (>1.5%) — Riesgo critico' :
                    zone === 'vdmp' ? 'VDMP (0.9–1.5%) — Programa monitoreo' :
                    zone === 'warning' ? 'Warning (0.65–0.9%) — Vigilancia' :
                    'Normal (<0.65%)';

  // Apply filters
  const filtered = cbs.filter(cb => {
    if (filterStatus !== 'ALL' && cb.status !== filterStatus) return false;
    if (filterProcessor !== 'ALL' && cb.processor !== filterProcessor) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(
        (cb.caseNumber || '').toLowerCase().includes(q) ||
        (cb.transactionId || '').toLowerCase().includes(q) ||
        (cb.email || '').toLowerCase().includes(q) ||
        (cb.customerName || '').toLowerCase().includes(q) ||
        (cb.cardLast4 || '').includes(q) ||
        (cb.reason || '').toLowerCase().includes(q)
      )) return false;
    }
    return true;
  });

  const fmtMoney = (n) => '$' + (Number(n) || 0).toFixed(2);
  const statusColor = (s) => {
    if (s === 'WON') return G.green;
    if (s === 'LOST') return G.red;
    if (s === 'RDR') return G.goldLight;
    if (s === 'CHARGEBACK') return G.orange;
    return G.muted;
  };
  const statusBg = (s) => {
    if (s === 'WON') return G.greenDim;
    if (s === 'LOST') return G.redDim;
    if (s === 'RDR') return G.goldDim;
    if (s === 'CHARGEBACK') return G.orangeDim;
    return G.border;
  };

  const kpis = [
    { label: 'Chargebacks total', value: sum.total || 0, sub: 'todos los tiempos', color: G.gold },
    { label: 'Abiertos', value: sum.open || 0, sub: 'no resueltos aun', color: G.orange },
    { label: 'RDR (recuperables)', value: sum.rdr || 0, sub: 'Visa Rapid Dispute', color: G.goldLight },
    { label: 'Ganados', value: sum.won || 0, sub: 'representation OK', color: G.green },
    { label: 'Perdidos', value: sum.lost || 0, sub: 'no recuperables', color: G.red },
    { label: 'Total disputado', value: fmtMoney(sum.totalAmount), sub: 'monto bruto', color: G.white },
    { label: 'En riesgo', value: fmtMoney(sum.openAmount), sub: 'monto abierto', color: G.orange },
    { label: 'CB Rate (30d)', value: cbRate30 + '%', sub: zoneLabel, color: zoneColor },
  ];

  const uniqueProcessors = Object.keys(breakdown.byProcessor || {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: G.muted, letterSpacing: 1, textTransform: 'uppercase' }}>Disputas y Chargebacks</div>
          <div style={{ fontSize: 12, color: G.dim, marginTop: 4 }}>
            Webhook automático de tu procesador de pagos. {data.lastSyncedAt ? `Último evento: ${new Date(data.lastSyncedAt).toLocaleString('es-ES')}` : 'Esperando primer evento.'}
          </div>
        </div>
        <button
          onClick={load}
          style={{ padding: '8px 14px', background: G.card, border: '1px solid ' + G.border, color: G.gold, borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
        >
          ↻ Refrescar
        </button>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ background: G.card, border: '1px solid ' + G.border, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: G.muted, letterSpacing: 1, textTransform: 'uppercase' }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 6 }}>{k.value}</div>
            <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Pending response banner */}
      {data.pendingResponse && data.pendingResponse.length > 0 && (
        <div style={{ background: G.redDim, border: '1px solid ' + G.red + '60', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, color: G.red, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>⚠ Pendiente de responder</div>
          <div style={{ fontSize: 12, color: G.white, marginTop: 6 }}>
            {data.pendingResponse.length} disputa{data.pendingResponse.length === 1 ? '' : 's'} con plazo activo. La mas urgente vence en {data.pendingResponse[0].daysLeft} dia{data.pendingResponse[0].daysLeft === 1 ? '' : 's'}.
          </div>
        </div>
      )}

      {/* Breakdown cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <div style={{ background: G.card, border: '1px solid ' + G.border, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Por status</div>
          {Object.entries(breakdown.byStatus || {}).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
            <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed ' + G.border }}>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: statusBg(s), color: statusColor(s), fontWeight: 600 }}>{s}</span>
              <span style={{ color: G.white, fontWeight: 700, fontSize: 14 }}>{n}</span>
            </div>
          ))}
        </div>
        <div style={{ background: G.card, border: '1px solid ' + G.border, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Por motivo</div>
          {Object.entries(breakdown.byReason || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([r, n]) => (
            <div key={r} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed ' + G.border }}>
              <span style={{ fontSize: 12, color: G.dim }}>{r}</span>
              <span style={{ color: G.white, fontWeight: 700, fontSize: 14 }}>{n}</span>
            </div>
          ))}
        </div>
        <div style={{ background: G.card, border: '1px solid ' + G.border, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Por procesador</div>
          {Object.entries(breakdown.byProcessor || {}).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
            <div key={p} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed ' + G.border }}>
              <span style={{ fontSize: 12, color: G.dim }}>{p}</span>
              <span style={{ color: G.white, fontWeight: 700, fontSize: 14 }}>{n}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters + table */}
      <div style={{ background: G.card, border: '1px solid ' + G.border, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', borderBottom: '1px solid ' + G.border }}>
          <input
            placeholder="Buscar por case#, email, last4, reason…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 220px', minWidth: 200, padding: '8px 12px', background: G.black, border: '1px solid ' + G.border, color: G.white, borderRadius: 6, fontSize: 12 }}
          />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '8px 10px', background: G.black, border: '1px solid ' + G.border, color: G.white, borderRadius: 6, fontSize: 12 }}
          >
            <option value="ALL">Todos los status</option>
            {Object.keys(breakdown.byStatus || {}).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filterProcessor}
            onChange={e => setFilterProcessor(e.target.value)}
            style={{ padding: '8px 10px', background: G.black, border: '1px solid ' + G.border, color: G.white, borderRadius: 6, fontSize: 12 }}
          >
            <option value="ALL">Todos los procesadores</option>
            {uniqueProcessors.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span style={{ fontSize: 11, color: G.muted }}>{filtered.length} resultado{filtered.length === 1 ? '' : 's'}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: G.surface }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>FECHA</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>CASE #</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>STATUS</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>MONTO</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>MOTIVO</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>CARD</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>CLIENTE</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: G.muted, fontWeight: 600, fontSize: 10, letterSpacing: 1 }}>PLAZO</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 30, textAlign: 'center', color: G.muted, fontSize: 12 }}>
                    {cbs.length === 0 ? 'No hay chargebacks aun. Cuando llegue el primero via webhook aparecera aqui.' : 'Ningun resultado con esos filtros.'}
                  </td>
                </tr>
              )}
              {filtered.map(cb => (
                <tr
                  key={cb.id}
                  onClick={() => setSelected(cb)}
                  style={{ borderTop: '1px solid ' + G.border, cursor: 'pointer', transition: 'background 120ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = G.cardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '10px 12px', color: G.dim }}>
                    {cb.chargebackDate ? new Date(cb.chargebackDate).toLocaleDateString('es-ES') : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: G.white, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>
                    {cb.caseNumber || '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: statusBg(cb.status), color: statusColor(cb.status), fontWeight: 600 }}>{cb.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', color: G.white, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                    {fmtMoney(cb.amount)}
                  </td>
                  <td style={{ padding: '10px 12px', color: G.dim, fontSize: 11 }}>{cb.reason || '—'}</td>
                  <td style={{ padding: '10px 12px', color: G.dim, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>
                    {cb.cardBrand && <span style={{ color: G.gold, marginRight: 4 }}>{cb.cardBrand}</span>}
                    {cb.cardLast4 ? '•••• ' + cb.cardLast4 : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: G.dim, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cb.customerName || cb.email || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {cb.daysLeft !== null && cb.isOpen ? (
                      <span style={{ fontSize: 11, color: cb.daysLeft <= 3 ? G.red : cb.daysLeft <= 7 ? G.orange : G.dim, fontWeight: 600 }}>
                        {cb.daysLeft >= 0 ? cb.daysLeft + 'd' : 'vencido'}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: G.muted }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(6,5,10,0.85)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: G.card, border: '1px solid ' + G.borderGold, borderRadius: 12, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ padding: 20, borderBottom: '1px solid ' + G.border, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase' }}>Chargeback</div>
                <div style={{ fontSize: 18, color: G.white, fontWeight: 700, marginTop: 4, fontFamily: 'DM Mono, monospace' }}>{selected.caseNumber || '—'}</div>
                <div style={{ marginTop: 8 }}>
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: statusBg(selected.status), color: statusColor(selected.status), fontWeight: 600 }}>{selected.status}</span>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', color: G.muted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 4 }}
              >×</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <DetailField label="Fecha disputa" value={selected.chargebackDate ? new Date(selected.chargebackDate).toLocaleString('es-ES') : '—'} />
              <DetailField label="Monto" value={fmtMoney(selected.amount)} valueColor={G.gold} />
              <DetailField label="Motivo" value={selected.reason || '—'} />
              <DetailField label="Reason code" value={selected.reasonCode || '—'} mono />
              <DetailField label="Procesador" value={selected.processor || '—'} />
              <DetailField label="Transaction ID" value={selected.transactionId || '—'} mono />
              <DetailField label="Tarjeta" value={selected.cardBrand + ' •••• ' + (selected.cardLast4 || '????')} mono />
              <DetailField label="Cliente" value={selected.customerName || '—'} />
              <DetailField label="Email" value={selected.email || '—'} />
              <DetailField label="Plazo respuesta" value={selected.respondBy ? new Date(selected.respondBy).toLocaleDateString('es-ES') + ' (' + (selected.daysLeft >= 0 ? selected.daysLeft + ' dias' : 'vencido') + ')' : '—'} valueColor={selected.daysLeft !== null && selected.daysLeft <= 3 && selected.isOpen ? G.red : G.white} />
              <DetailField label="Respondido" value={selected.responded ? 'Si' : 'No'} valueColor={selected.responded ? G.green : G.orange} />
              <DetailField label="Ultimo evento" value={selected.lastEventType || '—'} mono />
              <DetailField label="Sincronizado" value={selected.lastSyncedAt ? new Date(selected.lastSyncedAt).toLocaleString('es-ES') : '—'} />
            </div>
            {selected.originalPayment && (
              <div style={{ padding: 20, borderTop: '1px solid ' + G.border, background: G.surface }}>
                <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Pago original</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                  <DetailField label="Pago ID" value={selected.originalPayment.id} mono />
                  <DetailField label="Monto pagado" value={fmtMoney(selected.originalPayment.amount)} />
                  <DetailField label="Fecha" value={selected.originalPayment.createdAt ? new Date(selected.originalPayment.createdAt).toLocaleDateString('es-ES') : '—'} />
                  <DetailField label="Tienda" value={selected.originalPayment.shopName || selected.originalPayment.shopId || '—'} />
                  <DetailField label="Pasarela" value={selected.originalPayment.gatewayName || '—'} />
                </div>
              </div>
            )}
            {/* Evidence package — only useful for OPEN disputes that need representment */}
            {selected.isOpen && (
              <div style={{ padding: 20, borderTop: '1px solid ' + G.border }}>
                <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Paquete de evidencia (representment)</div>
                {!evidence ? (
                  <>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 10, lineHeight: 1.5 }}>
                      Genera el paquete con datos del pago original, historial del cliente, comunicaciones y tracking. Si conoces el email del cliente (visible en el portal de Payarc), pégalo abajo para narrow del match.
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <input
                        type="email"
                        placeholder="email del cardholder (opcional, mejora el match)"
                        value={emailHint}
                        onChange={e => setEmailHint(e.target.value.trim())}
                        style={{
                          flex: 1, background: G.black, border: '1px solid ' + G.border,
                          borderRadius: 6, padding: '8px 12px', color: G.white, fontSize: 13,
                          fontFamily: 'DM Mono, monospace',
                        }}
                      />
                      <button
                        onClick={fetchEvidence}
                        disabled={evidenceLoading}
                        style={{
                          background: G.gold, color: G.black, border: 'none', borderRadius: 6,
                          padding: '8px 16px', fontWeight: 700, fontSize: 13,
                          cursor: evidenceLoading ? 'wait' : 'pointer', opacity: evidenceLoading ? 0.6 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >{evidenceLoading ? 'Generando...' : '🛡 Generar evidencia'}</button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Recommendation banner */}
                    <div style={{
                      background: evidence.case?.isAutoResolvedByType ? G.greenDim : '#9c601020',
                      border: '1px solid ' + (evidence.case?.isAutoResolvedByType ? G.green : G.gold) + '60',
                      borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: G.white, lineHeight: 1.5,
                    }}>
                      <strong>{evidence.case?.recommendation || 'Sin recomendación'}</strong>
                      <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>match: {evidence.matchMethod || '?'}</div>
                    </div>

                    {/* Candidates list (when no auto-match) */}
                    {evidence.candidates?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: G.muted, marginBottom: 6 }}>Candidatos por monto+fecha (operador debe identificar el correcto):</div>
                        <div style={{ maxHeight: 160, overflowY: 'auto', background: G.black, border: '1px solid ' + G.border, borderRadius: 6 }}>
                          {evidence.candidates.map((c, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 70px 1fr 80px', gap: 8, padding: '6px 10px', fontSize: 11, fontFamily: 'DM Mono, monospace', color: G.dim, borderBottom: i < evidence.candidates.length - 1 ? '1px solid ' + G.border + '40' : 'none' }}>
                              <span>{(c.createdAt || '').slice(0, 10)}</span>
                              <span style={{ color: G.gold }}>${(c.amount || 0).toFixed(2)}</span>
                              <span style={{ color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>
                              <span style={{ textAlign: 'right' }}>{c.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prebuilt text — copy-paste to Payarc */}
                    <textarea
                      readOnly
                      value={evidence.prebuiltText || ''}
                      style={{
                        width: '100%', minHeight: 280, background: G.black, color: G.white,
                        border: '1px solid ' + G.border, borderRadius: 6, padding: 12,
                        fontSize: 11, fontFamily: 'DM Mono, monospace', lineHeight: 1.5,
                        boxSizing: 'border-box', resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        onClick={copyEvidenceText}
                        style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                      >📋 Copiar al portapapeles</button>
                      <button
                        onClick={() => setEvidence(null)}
                        style={{ background: 'transparent', color: G.muted, border: '1px solid ' + G.border, borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                      >Regenerar</button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={{ padding: 16, borderTop: '1px solid ' + G.border, background: G.black + '60', fontSize: 11, color: G.muted }}>
              {selected.status === 'RDR' ? (
                <>💡 RDR (Rapid Dispute Resolution): Visa permite resolver con un refund antes de que se convierta en chargeback duro. Reembolsa al cliente para evitar el cargo en el ratio.</>
              ) : selected.status === 'WON' ? (
                <>✓ Disputa ganada via representment. El cargo se mantuvo a tu favor.</>
              ) : selected.status === 'LOST' ? (
                <>✗ Disputa perdida. El monto fue debitado y cuenta en el ratio de Visa.</>
              ) : selected.isOpen ? (
                <>⏱ Disputa abierta. Sube evidencia (factura, prueba de entrega, política de refund firmada) en el portal de Payarc antes del plazo.</>
              ) : (
                <>Estado: {selected.status}</>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, mono, valueColor }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: G.muted, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, color: valueColor || G.white, marginTop: 4, fontFamily: mono ? 'DM Mono, monospace' : 'inherit', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Beneficios({ showToast }) {
  // ── Root state ────────────────────────────────────────────────────────
  // Al volver de conectar TikTok se abre directamente "Anuncios".
  const [tab, setTab] = useState(() => { try { return sessionStorage.getItem('zrp_open_tiktok') ? 'ads' : 'summary'; } catch { return 'summary'; } });
  const [from, setFrom] = useState(() => daysAgoIso(29));
  const [to, setTo] = useState(() => todayIso());
  const [shopFilter, setShopFilter] = useState('');

  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [rebill, setRebill] = useState(null);
  const [forecast, setForecast] = useState(null);

  const [config, setConfig] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [adSpend, setAdSpend] = useState([]);

  const [loading, setLoading] = useState(true);

  // ── Load everything on mount + on range/shop change ──────────────────
  // Accepts optional overrides so a caller that JUST changed the range can
  // pass the new values directly and avoid the setState → render → useEffect
  // race (React batches setState, so reading `from`/`to` from closure right
  // after setFrom/setTo still gives you the old values).
  const reloadAll = useCallback((overrides = {}) => {
    const fromArg = overrides.from ?? from;
    const toArg = overrides.to ?? to;
    const shopArg = overrides.shopId !== undefined ? overrides.shopId : shopFilter;
    setLoading(true);
    const params = { from: fromArg, to: toArg, _t: Date.now() };  // _t cache-buster
    if (shopArg) params.shopId = shopArg;
    Promise.all([
      beneficiosApi.summary(params).catch(e => ({ __err: e })),
      beneficiosApi.daily(params).catch(e => ({ __err: e })),
      beneficiosApi.rebillRate(params).catch(e => ({ __err: e })),
      beneficiosApi.forecast({ lookbackDays: 60, horizonDays: 30 }).catch(e => ({ __err: e })),
      beneficiosApi.getConfig().catch(() => null),
      gatewaysApi.getAll().catch(() => []),
      shopsApi.getAll().catch(() => []),
      beneficiosApi.listProducts().catch(() => []),
      beneficiosApi.listAdSpend({ from: fromArg, to: toArg }).catch(() => []),
      beneficiosApi.chargebacksSummary().catch(() => null),
    ]).then(([sum, dly, rb, fc, cfg, gws, shps, prds, spend, cbs]) => {
      setSummary(sum?.__err ? null : sum);
      setDaily(dly?.__err ? null : dly);
      setRebill(rb?.__err ? null : rb);
      setForecast(fc?.__err ? null : fc);
      setConfig(cfg);
      setGateways(gws || []);
      setShops(shps || []);
      setProducts(prds || []);
      setAdSpend(spend || []);
      // Chargebacks summary is exposed on window.__cbSummary so the KPI
      // card factory in BeneficiosSummary can read it without threading
      // through 12 layers of React props.
      if (cbs) window.__cbSummary = cbs;
      setLoading(false);
    });
  }, [from, to, shopFilter]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // Auto-refresh every 30s so rebill counters (scheduled / pending / success)
  // stay live while the operator is watching — useful during a
  // '⚡ Procesar atrasadas' batch or while the hourly cron runs.
  // Only polls when the tab is visible, and stops immediately if the
  // document visibility is hidden.
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      reloadAll();
      setLastRefreshedAt(Date.now());
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [reloadAll]);

  const tabs = [
    { id: 'summary',      label: 'Resumen',        icon: '\u{1F4CA}' },
    { id: 'gateway',      label: 'Profit Gateway', icon: '\u{1F4B0}' },
    { id: 'rentability',  label: 'Rentabilidad',   icon: '\u{1F3AF}' },
    { id: 'unit',         label: 'Unit Economics', icon: '\u{1F9EE}' },
    { id: 'costs',        label: 'Costes',         icon: '\u{1F4B3}' },
    { id: 'ads',          label: 'Anuncios',       icon: '\u{1F4E3}' },
    { id: 'forecast',     label: 'Forecast',       icon: '\u{1F52E}' },
    { id: 'config',       label: 'Config',         icon: '\u2699' },
  ];

  return (
    <div className="page">
      {/* Header: filters + tabs */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Desde</label>
          <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '6px 10px', fontSize: 13 }} />
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Hasta</label>
          <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '6px 10px', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tienda</label>
          <select className="input" value={shopFilter} onChange={e => setShopFilter(e.target.value)} style={{ padding: '6px 10px', fontSize: 13, minWidth: 180 }}>
            <option value="">Todas las tiendas</option>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          title="Refrescar ahora"
          style={{ fontSize: 12, padding: '6px 12px' }}
          onClick={() => { reloadAll(); setLastRefreshedAt(Date.now()); }}
        >
          🔄 Refrescar
        </button>
        <div style={{ fontSize: 10, color: G.dim, fontFamily: 'DM Mono, monospace' }}>
          Auto 30s · últ. {Math.max(0, Math.floor((Date.now() - lastRefreshedAt) / 1000))}s
        </div>
        <div style={{ flex: 1 }} />
        {[
          // Hoy / Ayer / Anteayer are single-day ranges (from === to) so the
          // `n` value is only used for `from` — `to` is derived per-case below.
          { label: 'Hoy', kind: 'today' },
          { label: 'Ayer', kind: 'yesterday' },
          { label: 'Anteayer', kind: 'dayBeforeYesterday' },
          { label: '7d', kind: 'range', n: 6 },
          { label: '30d', kind: 'range', n: 29 },
          { label: '90d', kind: 'range', n: 89 },
        ].map(({ label, kind, n }) => {
          let rangeFrom, rangeTo;
          if (kind === 'today') { rangeFrom = todayIso(); rangeTo = rangeFrom; }
          else if (kind === 'yesterday') { rangeFrom = yesterdayIso(); rangeTo = rangeFrom; }
          else if (kind === 'dayBeforeYesterday') { rangeFrom = dayBeforeYesterdayIso(); rangeTo = rangeFrom; }
          else { rangeFrom = daysAgoIso(n); rangeTo = todayIso(); }
          const active = from === rangeFrom && to === rangeTo;
          return (
            <button
              key={label}
              className="btn btn-ghost"
              disabled={loading}
              onClick={() => {
                // Update the inputs AND reload with the explicit range.
                // Passing overrides avoids the setState → render race and
                // guarantees the fetch uses the new range even when the
                // user double-clicks the already-active button.
                setFrom(rangeFrom);
                setTo(rangeTo);
                reloadAll({ from: rangeFrom, to: rangeTo });
              }}
              style={{
                background: active ? G.goldDim : 'transparent',
                color: active ? G.gold : undefined,
                borderColor: active ? G.gold : undefined,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {label}
            </button>
          );
        })}
        <button
          className="btn btn-primary"
          onClick={reloadAll}
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1, minWidth: 130 }}
        >
          {loading ? '⟳ Cargando\u2026' : '\u21BB Recargar'}
        </button>
      </div>

      {/* Tabs */}
      <div className="beneficios-tabs" style={{ gap: 4, borderBottom: `1px solid ${G.border}`, marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            style={{ padding: '10px 18px', background: tab === t.id ? G.goldDim : 'transparent', color: tab === t.id ? G.gold : G.muted, border: 'none', borderBottom: tab === t.id ? `2px solid ${G.gold}` : '2px solid transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, letterSpacing: 0.3, marginBottom: -1 }}>
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {loading && !summary && (
        <div style={{ textAlign: 'center', padding: 60, color: G.muted }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⏳</div>
          Cargando datos de P&L…
        </div>
      )}

      {/* Content wrapper — dimmed + pointer-events disabled while refetching so
         the user gets immediate visual feedback on every reload click. */}
      <div style={{
        opacity: loading && summary ? 0.45 : 1,
        transition: 'opacity 0.15s ease',
        pointerEvents: loading && summary ? 'none' : 'auto',
      }}>
        {tab === 'summary'     && summary && <BeneficiosSummary summary={summary} daily={daily} rebill={rebill} shops={shops} />}
        {tab === 'gateway'     && <BeneficiosGateway showToast={showToast} />}
        {tab === 'rentability' && <RentabilityCard />}
        {tab === 'unit'        && <BeneficiosUnitEconomics summary={summary} rebill={rebill} products={products} gateways={gateways} />}
        {tab === 'costs'    && <BeneficiosCosts gateways={gateways} setGateways={setGateways} products={products} setProducts={setProducts} shops={shops} showToast={showToast} onReload={reloadAll} />}
        {tab === 'ads'      && <BeneficiosAds config={config} setConfig={setConfig} adSpend={adSpend} setAdSpend={setAdSpend} shops={shops} from={from} to={to} showToast={showToast} onReload={reloadAll} />}
        {tab === 'forecast' && <BeneficiosForecast forecast={forecast} summary={summary} rebill={rebill} />}
        {tab === 'config'   && <BeneficiosConfig config={config} setConfig={setConfig} showToast={showToast} />}
      </div>
    </div>
  );
}

// ── Summary tab: KPIs + per-shop P&L table + daily SVG chart ───────────
// Backend (pnl.service.computePnL) returns the `total` object with:
//   grossRevenue, netRevenue, refunds, fees, feesReturned, netFees, cogs,
//   adSpend, profit, orders, refundCount, margin (pct), contributionMargin,
//   contributionMarginPct, poas, mer, aov, refundRate
// We map here rather than in the backend so the API stays raw and precise.
// 2026-06-01 — BeneficiosGateway: tabla de PROFIT REAL desde el gateway
// TailoredPay (skip MongoDB que está desincronizado). Muestra día a día:
// Sales / Refunds / AdSpend / NET PROFIT en NY timezone. AdSpend viene de
// adSpendRecords sincronizado por el cron Meta/Google cada 3h.
function BeneficiosGateway({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [error, setError] = useState(null);

  const load = async (n) => {
    setLoading(true);
    setError(null);
    try {
      const d = await beneficiosApi.gatewayRevenueByDay(n || days);
      setData(d);
    } catch (e) {
      setError(e.message || 'fetch failed');
      if (showToast) showToast('Error al consultar gateway: ' + (e.message || 'timeout'), 'error');
    }
    setLoading(false);
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const t = data?.totals || {};
  const days_ = data?.days || [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Rango</label>
        <select className="input" value={days} onChange={e => { const n = parseInt(e.target.value, 10); setDays(n); load(n); }} style={{ padding: '6px 10px', fontSize: 13 }}>
          <option value={3}>Últimos 3 días</option>
          <option value={7}>Últimos 7 días</option>
          <option value={14}>Últimos 14 días</option>
          <option value={30}>Últimos 30 días (lento, ~60s)</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={() => load(days)} disabled={loading} style={{ fontSize: 12, padding: '6px 12px' }}>
          {loading ? '⏳ Cargando...' : '🔄 Refrescar'}
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
          {data ? `${data.parsedTransactions || 0} transacciones · ${data.timezone || '?'}` : ''}
        </div>
      </div>

      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, fontWeight: 600 }}>📊 Totales del rango</div>
        <div className="beneficios-summary-grid">
          <KpiCard label="SALES" value={fmt$(t.salesRevenueUsd)} sub={`${t.sales || 0} cobros`} color={G.green} />
          <KpiCard label="REFUNDS" value={`-${fmt$(t.refundsAmountUsd)}`} sub={`${t.refunds || 0} reembolsos`} color={G.red} />
          <KpiCard label="ADSPEND" value={`-${fmt$(t.adSpendUsd)}`} sub="Meta + Google" color={G.muted} />
          <KpiCard label="NET PROFIT" value={fmt$(t.netProfitUsd)} sub={`Margen ${t.marginPct || 0}%`} color={G.gold} />
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: G.dim }}>
          Fórmula: <span style={{ fontFamily: 'DM Mono, monospace', color: G.text }}>netProfit = (sales - refunds) - adSpend</span>
          {' · '}Approval rate: <strong style={{ color: G.text }}>{t.approvalRate || 0}%</strong>
        </div>
      </div>

      {error && (
        <div style={{ background: G.card, border: `1px solid ${G.red}`, borderRadius: 10, padding: 16, marginBottom: 20, color: G.red }}>
          Error: {error}
        </div>
      )}

      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14, fontWeight: 600 }}>📅 Día a día (NY tz)</div>
        <div style={{ overflowX: 'auto' }}>
          <table className="beneficios-pl-table">
            <thead>
              <tr style={{ borderBottom: `2px solid ${G.border}`, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
                <th style={{ textAlign: 'left', padding: '10px 8px' }}>Fecha NY</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Sales</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Sales $</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Refunds</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Refund $</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>AdSpend</th>
                <th style={{ textAlign: 'right', padding: '10px 8px', color: G.gold }}>NET PROFIT</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Margen</th>
              </tr>
            </thead>
            <tbody>
              {days_.length === 0 && !loading && (
                <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: G.muted }}>No hay datos. Haz click en Refrescar.</td></tr>
              )}
              {days_.map(d => {
                const margin = d.salesRevenueUsd > 0 ? (d.netProfitUsd / d.salesRevenueUsd * 100) : 0;
                const profitColor = d.netProfitUsd > 0 ? G.green : d.netProfitUsd < 0 ? G.red : G.muted;
                return (
                  <tr key={d.date} style={{ borderBottom: `1px solid ${G.border}` }}>
                    <td style={{ padding: '10px 8px', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{d.date}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>{d.sales}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: G.green }}>{fmt$(d.salesRevenueUsd)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>{d.refunds}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: G.red }}>-{fmt$(d.refundsAmountUsd)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>{d.adSpendUsd ? `-${fmt$(d.adSpendUsd)}` : '—'}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: profitColor, fontWeight: 700 }}>{fmt$(d.netProfitUsd)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: G.muted, fontSize: 12 }}>{margin.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 16, padding: 12, background: G.cardSoft || G.card, borderRadius: 8, fontSize: 11, color: G.muted, lineHeight: 1.6 }}>
        💡 <strong>Datos:</strong> Sales y Refunds vienen directos del gateway TailoredPay (la verdad, no del MongoDB local que tiene 117 cobros desincronizados). AdSpend de adSpendRecords (Meta + Google sync cada 3h). Margen = NET PROFIT / Sales $. Todo en horario NY.
      </div>
    </div>
  );
}

// Reusable KPI card for the gateway dashboard
function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'transparent', border: `1px solid ${G.border}`, borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || G.text, fontFamily: 'DM Mono, monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function RebillDebugPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [from] = useState(() => new Date().toLocaleDateString('sv-SE', { timeZone: APP_TZ }));
  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/beneficios/rebill-rate/debug?from=${from}&to=${from}&_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const d = await r.json();
      setData(d);
    } catch (e) { setData({ error: e.message }); }
    setLoading(false);
  };
  if (!open) {
    return (
      <div style={{ marginTop: 14, padding: 12, background: G.card, border: `1px dashed ${G.border}`, borderRadius: 10 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(true); load(); }}>
          🔍 Ver debug del Rebill rate (hoy)
        </button>
      </div>
    );
  }
  if (loading) return <div style={{ marginTop: 14, padding: 12, color: G.muted }}>Cargando debug...</div>;
  if (!data) return null;
  if (data.error) return <div style={{ marginTop: 14, padding: 12, color: G.red }}>Error: {data.error}</div>;

  const t = data.totals || {};
  const items = data.breakdown || [];
  const byCategory = {
    success: items.filter(i => i.succeededInWindow),
    declined: items.filter(i => !i.succeededInWindow && i.failedInWindow),
    pending: items.filter(i => i.countedAsPending),
    excluded: items.filter(i => !i.countedAsScheduled),
  };
  const rawPayments = data.rawPaymentsToday || [];
  const section = (label, color, arr) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
        {label} — {arr.length}
      </div>
      <div style={{ maxHeight: 180, overflowY: 'auto', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: 8 }}>
        {arr.length === 0 ? <div style={{ fontSize: 11, color: G.dim }}>(vacío)</div> : arr.slice(0, 40).map((it, i) => (
          <div key={i} style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', padding: '3px 0', borderBottom: `1px solid ${G.border}`, color: G.white }}>
            <span style={{ color: G.gold }}>{it.email || it.rcId?.slice(0, 8)}</span>
            {it.productKey && it.productKey !== '(legacy)' && <span style={{ color: G.muted, marginLeft: 6 }}>[{it.productKey}]</span>}
            <span style={{ color: G.muted, marginLeft: 6 }}>status={it.status}</span>
            {it.nextChargeDay && <span style={{ color: G.muted, marginLeft: 6 }}>next={it.nextChargeDay}</span>}
            {it.firstAttemptDay && <span style={{ color: G.muted, marginLeft: 6 }}>1st={it.firstAttemptDay}</span>}
            <span style={{ color: G.dim, marginLeft: 6 }}>
              pays={it.paymentsInWindow} succ={it.succeededInWindow ? '✓' : '·'} fail={it.failedInWindow ? '✓' : '·'}
            </span>
            {it.excludedFromCount && <span style={{ color: G.red, marginLeft: 6 }}>✗ {it.excludedFromCount}</span>}
          </div>
        ))}
        {arr.length > 40 && <div style={{ fontSize: 10, color: G.dim, marginTop: 4 }}>+ {arr.length - 40} más</div>}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 14, padding: 14, background: G.card, border: `1px solid ${G.gold}`, borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: G.gold }}>🔍 Debug Rebill rate — {data.from}</div>
          <div style={{ fontSize: 10, color: G.dim, marginTop: 2, fontFamily: 'DM Mono, monospace' }}>
            tz: <strong style={{ color: G.white }}>{data.tz || '?'}</strong> · hoy según backend: <strong style={{ color: G.white }}>{data.todayInTz || '?'}</strong> · UTC ahora: <strong style={{ color: G.white }}>{data.nowUtc?.slice(0, 19).replace('T', ' ')}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>🔄 Recargar</button>
          {data.tz && data.tz !== 'America/New_York' && (
            <button className="btn btn-gold btn-sm" title="Cambiar tz a New York — alineado con Meta/Google ads + US customers" onClick={async () => {
              if (!window.confirm(`Cambiar tu zona horaria a America/New_York? (actual: ${data.tz})\n\nEsto alinea tu P&L con los día-buckets de Meta/Google (reportan en NY) y con la cohorte de clientes US.`)) return;
              try {
                const r = await fetch(`${BACKEND}/api/beneficios/config`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                  body: JSON.stringify({ timezone: 'America/New_York' }),
                });
                if (r.ok) { alert('✓ Cambiado a America/New_York. Recargando debug...'); load(); }
                else alert('Error: ' + r.status);
              } catch (e) { alert('Error: ' + e.message); }
            }}>⏰ Cambiar tz a New York</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>✕ Cerrar</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 14 }}>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: G.white }}>{t.scheduled || 0}</div><div style={{ fontSize: 10, color: G.muted }}>Programados</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: G.gold }}>{t.attempted || 0}</div><div style={{ fontSize: 10, color: G.muted }}>Intentados</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: G.green }}>{t.succeededInWindow || 0}</div><div style={{ fontSize: 10, color: G.muted }}>Cobrados</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: G.red }}>{t.failedInWindow || 0}</div><div style={{ fontSize: 10, color: G.muted }}>Fallados</div></div>
        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: G.white }}>{t.pending || 0}</div><div style={{ fontSize: 10, color: G.muted }}>Pendientes</div></div>
      </div>
      {section('COBRADOS', G.green, byCategory.success)}
      {section('FALLADOS', G.red, byCategory.declined)}
      {section('PENDIENTES', G.gold, byCategory.pending)}
      {byCategory.excluded.length > 0 && section('EXCLUIDOS (no cuentan)', G.muted, byCategory.excluded)}
      {/* Raw payments today — shows orphan or unusual-source payments */}
      <div style={{ marginTop: 12, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
          PAGOS ÚLTIMOS 3 DÍAS — {rawPayments.length}  (✓ = cuenta para hoy · NY day)
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', background: G.bg, border: `1px solid ${G.border}`, borderRadius: 6, padding: 8 }}>
          {rawPayments.length === 0 ? <div style={{ fontSize: 11, color: G.dim }}>(vacío)</div> : rawPayments.map((p, i) => (
            <div key={i} style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', padding: '3px 0', borderBottom: `1px solid ${G.border}`, color: G.white }}>
              <span style={{ color: p.inTodayWindow ? G.green : G.red, fontWeight: 700 }}>{p.inTodayWindow ? '✓' : '✗'}</span>
              <span style={{ color: G.muted, marginLeft: 4 }}>{p.dayKeyTz}</span>
              <span style={{ color: p.status === 'SUCCEEDED' ? G.green : p.status === 'FAILED' ? G.red : G.muted, marginLeft: 6 }}>{p.status}</span>
              <span style={{ color: G.gold, marginLeft: 6 }}>{p.customerId || p.email || '—'}</span>
              <span style={{ color: G.muted, marginLeft: 6 }}>${((p.amount || 0) / 100).toFixed(2)}</span>
              <span style={{ color: G.muted, marginLeft: 6 }}>src={p.source || '∅'}</span>
              <span style={{ color: G.muted, marginLeft: 6 }}>rid={p.recurringId ? p.recurringId.slice(0, 8) : '∅'}</span>
              <span style={{ color: G.dim, marginLeft: 6 }}>UTC {p.createdAt?.slice(0, 10)} {p.createdAt?.slice(11, 19)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BeneficiosSummary({ summary, daily, rebill, shops }) {
  const t = summary?.total || {};
  const perShop = summary?.perShop || [];
  const feePct = t.netRevenue > 0 ? (t.fees / t.netRevenue * 100) : 0;

  const kpiCards = [
    { label: 'Revenue bruto', value: fmtEUR(t.grossRevenue), sub: `${t.orders || 0} pagos`, color: G.gold },
    // ── REVENUE SPLIT (added 2026-05-21) ──────────────────────────────
    // Operator request: see how much volume comes from new purchases vs
    // recurring rebills. Backend (beneficios.js /summary) splits payments
    // by metadata.source — recurring/charge-now go to rebillRevenue, the
    // rest (checkout, free-plus-shipping, …) goes to salesRevenue.
    { label: 'Revenue ventas',
      value: fmtEUR(t.salesRevenue || 0),
      sub: `${t.salesOrders || 0} pagos nuevos${t.grossRevenue > 0 ? ` · ${(((t.salesRevenue || 0) / t.grossRevenue) * 100).toFixed(0)}%` : ''}`,
      color: G.green },
    { label: 'Revenue rebills',
      value: fmtEUR(t.rebillRevenue || 0),
      sub: `${t.rebillOrders || 0} cobros recurrentes${t.grossRevenue > 0 ? ` · ${(((t.rebillRevenue || 0) / t.grossRevenue) * 100).toFixed(0)}%` : ''}`,
      color: '#8A7BD8' },
    { label: 'Reembolsos',    value: fmtEUR(t.refunds), sub: `${t.refundCount || 0} órdenes`, color: G.red },
    // ── DISPUTES (added 2026-05-03) ──────────────────────────────────────
    // Both RDR (Visa Rapid Dispute Resolution) and real chargebacks debit
    // the merchant — even though only real chargebacks count toward Visa's
    // CB ratio. RDRs are the lion's share of the loss in many accounts and
    // were invisible in Beneficios until now.
    { label: 'Lost to Disputes',
      value: fmtEUR(t.disputeAmount || 0),
      sub: (() => {
        const dc = t.disputeCount || 0;
        const rd = t.rdrCount || 0;
        const cb = t.chargebackCount || 0;
        if (dc === 0) return 'Sin disputas';
        return `${dc} total · ${rd} RDR · ${cb} CB reales`;
      })(),
      color: (t.disputeAmount || 0) > 0 ? G.red : G.muted },
    { label: 'Revenue neto',  value: fmtEUR(t.netRevenue), sub: `Ticket ${fmtEUR(t.aov || 0)}`, color: G.white },
    // Fees pasarela — sub-label muestra fórmula real (no % opaco). Click
    // abre desglose detallado: % aplicado × volumen, flat × tx, fees
    // devueltas en refunds, fee neto final.
    { label: 'Fees pasarela',
      value: fmtEUR(t.fees),
      sub: '6.6% × volume + $0.43/tx · click para detalle',
      color: G.muted,
      onClick: () => {
        const gross = t.grossRevenue || 0;
        const orders = t.orders || 0;
        const fees = t.fees || 0;
        const feesReturned = t.feesReturned || 0;
        const netFees = t.netFees || (fees - feesReturned);
        const pctPart = gross * 0.066;
        const flatPart = orders * 0.43;
        const msg = 'DESGLOSE FEES PASARELA\n\n'
          + '── COMPONENTES ──\n'
          + '  ' + (0.066 * 100).toFixed(2) + '% × $' + gross.toFixed(2) + ' (volume) = $' + pctPart.toFixed(2) + '\n'
          + '  $0.43 × ' + orders + ' transacciones        = $' + flatPart.toFixed(2) + '\n'
          + '  ───────────────────────────\n'
          + '  Total fees brutas:                 $' + fees.toFixed(2) + '\n\n'
          + '── REFUNDS ──\n'
          + '  Fees devueltas en refunds:         $' + feesReturned.toFixed(2) + '\n'
          + '  (procesador devuelve % en cada refund)\n\n'
          + '── FEE NETO REAL ──\n'
          + '  Total neto:                        $' + netFees.toFixed(2) + '\n'
          + '  Sobre revenue bruto:               ' + (fees / Math.max(gross, 1) * 100).toFixed(2) + '%\n\n'
          + 'Tarifa contractual estimada (6.6% + $0.43/tx) derivada\n'
          + 'de regresión sobre 22 días de tu XLS Payarc.\n'
          + 'Cuando tengas el contrato exacto, se actualiza.';
        window.alert(msg);
      },
    },
    { label: 'Chargeback fees',
      value: fmtEUR(t.chargebackFees || 0),
      sub: (t.chargebackCount || 0) > 0
           ? `$25 × ${t.chargebackCount} CBs reales`
           : 'Sin chargebacks reales',
      color: (t.chargebackFees || 0) > 0 ? G.red : G.muted },
    // RDR / anti-chargeback service fee — Verifi/Payarc cobran ~$5
    // por cada RDR resuelto aunque no sea un chargeback formal.
    { label: 'RDR fees',
      value: fmtEUR(t.rdrFees || 0),
      sub: (t.rdrCount || 0) > 0
           ? `Anti-CB service · ${t.rdrCount} RDRs`
           : 'Sin RDR fees',
      color: (t.rdrFees || 0) > 0 ? G.red : G.muted },
    // Alert fees — Equifax (Verifi CDRN, Ethoca, RDR alerts).
    // $24/alert MC+Visa, $17/alert RDR Tier 1.
    // Each preventive refund (no matching CB) = 1 alert.
    { label: 'Alert fees',
      value: fmtEUR(t.alertFees || 0),
      sub: (t.alertCount || 0) > 0
           ? `Equifax · ${t.alertCount} alerts (~$24 c/u)`
           : 'Sin alertas',
      color: (t.alertFees || 0) > 0 ? G.red : G.muted },
    { label: 'COGS',          value: fmtEUR(t.cogs), sub: t.cogs > 0 ? 'Costes de producto' : 'Sin configurar', color: G.muted },
    { label: 'Ad Spend',      value: fmtEUR(t.adSpend), sub: t.adSpend > 0 ? `POAS ${(t.poas || 0).toFixed(2)}` : '—', color: '#8A7BD8' },
    { label: 'Profit neto',   value: fmtEUR(t.profit), sub: `Margen ${fmtPct(t.margin)}`, color: t.profit >= 0 ? G.green : G.red, big: true },
    { label: 'POAS',          value: (t.poas || 0).toFixed(2), sub: 'Profit / Ad Spend', color: (t.poas || 0) >= 1 ? G.green : G.red },
    { label: 'MER',           value: (t.mer || 0).toFixed(2), sub: 'Revenue / Ad Spend', color: G.gold },
    { label: 'Rebill rate',
      // FIX v2 (29 Apr 2026): the rate uses success/attempted (the rate of
      // actually-processed rebills). This is the most honest mid-day number:
      // it's the rate confirmed by data we have. It does NOT pretend to know
      // what the still-pending RCs will do.
      //
      // Earlier attempt to use success/scheduled (7/130 = 5.4%) was wrong —
      // that formula treats pending as if they were fails, which they're not.
      //
      // Sub-label is the part the operator actually needs to read:
      //   "X de Y cobrados (rate% real)"
      //   "Z pendientes" — clearly indicates partial day if any
      //   "(N programados)" — total scope so the math is verifiable
      value: fmtPct(rebill?.attempted > 0 ? (rebill.success / rebill.attempted * 100) : 0),
      sub: (() => {
        const success = rebill?.success || 0;
        const attempted = rebill?.attempted || 0;
        const pending = rebill?.pending || 0;
        const scheduled = rebill?.scheduled || 0;
        if (pending > 0) {
          // Mid-day: signal the rate is partial and how many are left.
          return `${success}/${attempted} procesados · ${pending} pendientes (${scheduled} total)`;
        }
        // End-of-period: pending=0, the rate is final.
        return `${success}/${attempted} cobrados · día cerrado`;
      })(),
      color: G.gold },
    // Refund rate (30d) — reads from /chargebacks-summary so it uses the
    // SAME denominator as Chargeback rate below (succeeded payments in
    // the last 30 days). Operator confirmed 2026-05-04 they want both
    // rates apples-to-apples; previously refund rate was filter-dependent
    // (changes with Hoy/Ayer/7d/30d) which caused a denominator mismatch
    // (986 vs 1633) when comparing the two cards side by side.
    { label: 'Refund rate (30d)',
      value: (() => {
        const cb = window.__cbSummary?.windows?.['30d'];
        return cb ? fmtPct(cb.refundRate) : fmtPct(t.refundRate);
      })(),
      sub: (() => {
        const cb = window.__cbSummary?.windows?.['30d'];
        if (cb) return `${cb.refunds}/${cb.succeeded} · últimos 30d`;
        return `${t.refundCount || 0}/${t.orders || 0}`;
      })(),
      color: G.muted,
    },
    { label: 'Chargeback rate (30d)',
      value: (() => {
        const cb = window.__cbSummary?.windows?.['30d'];
        return cb ? fmtPct(cb.cbRate) : '—';
      })(),
      sub: (() => {
        const cb = window.__cbSummary?.windows?.['30d'];
        if (!cb) return 'Sin sync — sube .xls';
        const zone = cb.zone === 'normal' ? '✓ OK' :
                     cb.zone === 'warning' ? '🟡 Warning' :
                     cb.zone === 'vdmp' ? '🟠 VDMP' :
                     '🔴 VFMP';
        return `${cb.cbsOpen}/${cb.succeeded} · ${zone}`;
      })(),
      color: (() => {
        const cb = window.__cbSummary?.windows?.['30d'];
        if (!cb) return G.muted;
        if (cb.zone === 'normal') return G.green;
        if (cb.zone === 'warning') return G.gold;
        return G.red;
      })() },
    { label: 'Contrib. margin', value: fmtEUR(t.contributionMargin), sub: fmtPct(t.contributionMarginPct), color: G.gold },
  ];

  return (
    <>
      {/* KPI grid */}
      <div className="beneficios-summary-grid">
        {kpiCards.map(k => (
          <div
            key={k.label}
            onClick={k.onClick}
            style={{
              background: G.card,
              border: `1px solid ${G.border}`,
              borderRadius: 10,
              padding: 14,
              position: 'relative',
              overflow: 'hidden',
              cursor: k.onClick ? 'pointer' : 'default',
              transition: k.onClick ? 'border-color 0.15s, transform 0.15s' : undefined,
              ...(k.big ? { borderColor: k.color, boxShadow: `0 0 20px ${k.color}18` } : {}),
            }}
            onMouseEnter={k.onClick ? (e) => { e.currentTarget.style.borderColor = k.color || G.gold; } : undefined}
            onMouseLeave={k.onClick ? (e) => { e.currentTarget.style.borderColor = G.border; } : undefined}
          >
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              {k.label}{k.onClick ? <span style={{ float: 'right', color: G.dim, fontSize: 9 }}>👁 click</span> : null}
            </div>
            <div style={{ fontSize: k.big ? 26 : 20, fontWeight: 700, color: k.color, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{k.value}</div>
            <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Rebill debug panel */}
      <RebillDebugPanel />

      {/* Daily SVG chart */}
      {daily?.dailyRows?.length > 0 && <BeneficiosDailyChart rows={daily.dailyRows} />}

      {/* Per-day metrics table — shows clientes nuevos + CPA per day */}
      {daily?.dailyRows?.length > 0 && <BeneficiosDailyMetricsTable rows={daily.dailyRows} />}

      {/* Per-shop P&L table */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, marginTop: 24, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, fontWeight: 700, color: G.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{'\u{1F3EA}'}</span> P&L por tienda
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tienda</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Revenue</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Refunds</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Net</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Fees</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>COGS</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ads</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Profit</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Margen</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>POAS</th>
              </tr>
            </thead>
            <tbody>
              {perShop.map(s => {
                const shopName = resolveShopName(shops, s.shopId, s.shopName);
                return (
                  <tr key={s.shopId || 'unknown'} style={{ borderTop: `1px solid ${G.border}` }}>
                    <td style={{ padding: '8px 12px', color: G.white, fontWeight: 600 }}>{shopName}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmtEUR(s.grossRevenue)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.red }}>{fmtEUR(s.refunds)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmtEUR(s.netRevenue)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.muted }}>{fmtEUR(s.fees)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.muted }}>{fmtEUR(s.cogs)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#8A7BD8' }}>{fmtEUR(s.adSpend)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: s.profit >= 0 ? G.green : G.red, fontWeight: 700 }}>{fmtEUR(s.profit)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: (s.margin || 0) >= 0 ? G.green : G.red }}>{fmtPct(s.margin)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: (s.poas || 0) >= 1 ? G.green : G.red }}>{s.adSpend > 0 ? (s.poas || 0).toFixed(2) : '—'}</td>
                  </tr>
                );
              })}
              {perShop.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: G.muted }}>Sin datos en este rango.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Daily chart: profit + revenue line series as SVG
// ── Per-day metrics table ─────────────────────────────────────────────
// Shows every day in the window with: clientes nuevos, CPA, checkouts
// totales, revenue, profit. Helps the operator spot anomalies per-day
// (e.g. "why is my CPA so high on Wednesday?") without having to slice
// the data manually.
function BeneficiosDailyMetricsTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  // Show most recent day first
  const sorted = [...rows].sort((a, z) => z.date.localeCompare(a.date));
  const sum = sorted.reduce((acc, r) => {
    acc.newCustomers += r.newCustomers || 0;
    acc.orders += r.orders || 0;
    acc.adSpend += r.adSpend || 0;
    acc.revenue += r.revenue || 0;
    acc.profit += r.profit || 0;
    acc.refunds += r.refunds || 0;
    acc.fees += r.fees || 0;
    acc.cogs += r.cogs || 0;
    // disputes column = amount perdido + TODOS los fees (CB + RDR)
    acc.disputes += (r.disputeAmount || 0) + (r.disputeFees || r.chargebackFees || 0);
    acc.alertFees += r.alertFees || 0;
    acc.alertCount += r.alertFeesCount || 0;
    acc.rebillsAttempted += r.rebillsAttempted || 0;
    acc.rebillsSucceeded += r.rebillsSucceeded || 0;
    return acc;
  }, { newCustomers: 0, orders: 0, adSpend: 0, revenue: 0, profit: 0, refunds: 0, fees: 0, cogs: 0, disputes: 0, alertFees: 0, alertCount: 0, rebillsAttempted: 0, rebillsSucceeded: 0 });
  const avgCpa = sum.newCustomers > 0 ? (sum.adSpend / sum.newCustomers) : 0;

  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, marginTop: 18, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, fontWeight: 700, color: G.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📅</span> Métricas por día
        <span style={{ fontSize: 11, color: G.muted, fontWeight: 400, marginLeft: 'auto' }}>
          {sum.newCustomers} clientes nuevos · CPA medio ${avgCpa.toFixed(2)}
        </span>
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
        <table className="table" style={{ width: '100%', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fecha</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Clientes nuevos</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Pedidos totales</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.gold }} title="Rebills cuyo PRIMER intento fue ese día. Los retries que aprueban días después suman a su cohorte original, no al día del retry.">Rebills</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ad spend</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.blue }}>CPA</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Revenue</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.muted }} title="Reembolsos del día">Refunds</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.muted }} title="Fees pasarela neto (post fees devueltas en refund)">Fees</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.muted }} title="COGS (productos físicos)">COGS</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.red }} title="Disputas REALES (no RDR/auto-resueltas)">Disputas</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', color: G.red }} title="Equifax alert fees ($24/alert MC+Visa, $17/RDR) - daily count">Alert fees</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Profit</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const dispDay = (r.disputeAmount || 0) + (r.disputeFees || r.chargebackFees || 0);
              return (
              <tr key={r.date} style={{ borderTop: `1px solid ${G.border}` }}>
                <td style={{ padding: '8px 12px', color: G.white, fontFamily: 'DM Mono, monospace' }}>{r.date}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.gold, fontWeight: 600 }}>{r.newCustomers || 0}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.dim }}>{r.orders || 0}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: (r.rebillsAttempted || 0) === 0 ? G.dim : ((r.rebillsRate || 0) >= 50 ? G.green : (r.rebillsRate || 0) >= 25 ? G.gold : G.red) }} title={(r.rebillsPending || 0) > 0 ? `${r.rebillsPending} aún pendientes (retries en curso)` : ''}>
                  {(r.rebillsAttempted || 0) === 0 ? '—' : (
                    <>
                      <span>{r.rebillsSucceeded || 0}/{r.rebillsAttempted || 0}</span>
                      <div style={{ fontSize: 10, color: G.dim, fontWeight: 400 }}>{(r.rebillsRate || 0).toFixed(0)}%{(r.rebillsPending || 0) > 0 ? ` · +${r.rebillsPending}p` : ''}</div>
                    </>
                  )}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: '#8A7BD8', fontFamily: 'DM Mono, monospace' }}>${(r.adSpend || 0).toFixed(2)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.blue, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>${(r.cpa || 0).toFixed(2)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.white, fontFamily: 'DM Mono, monospace' }}>${(r.revenue || 0).toFixed(2)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }} title={r.refundCount ? `${r.refundCount} refunds` : ''}>
                  {(r.refunds || 0) > 0 ? `-$${r.refunds.toFixed(2)}` : '$0.00'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                  {(r.fees || 0) > 0 ? `-$${r.fees.toFixed(2)}` : '$0.00'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                  {(r.cogs || 0) > 0 ? `-$${r.cogs.toFixed(2)}` : '$0.00'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: dispDay > 0 ? G.red : G.dim, fontFamily: 'DM Mono, monospace' }}>
                  {dispDay > 0 ? `-$${dispDay.toFixed(2)}` : '$0.00'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: (r.alertFees || 0) > 0 ? G.red : G.dim, fontFamily: 'DM Mono, monospace' }} title={r.alertFeesCount ? `${r.alertFeesCount} alertas` : ''}>
                  {(r.alertFees || 0) > 0 ? `-$${r.alertFees.toFixed(2)}` : '$0.00'}
                  {r.alertFeesCount > 0 && <div style={{ fontSize: 10, color: G.dim }}>{r.alertFeesCount} alerts</div>}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: (r.profit || 0) >= 0 ? G.green : G.red, fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>
                  {(r.profit || 0) >= 0 ? '+' : ''}${(r.profit || 0).toFixed(2)}
                </td>
              </tr>
            );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${G.border}`, background: G.surface, fontWeight: 700 }}>
              <td style={{ padding: '10px 12px', color: G.goldLight, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>Total</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.gold }}>{sum.newCustomers}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.dim }}>{sum.orders}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: sum.rebillsAttempted === 0 ? G.dim : G.gold }}>
                {sum.rebillsAttempted === 0 ? '—' : (
                  <>
                    <span>{sum.rebillsSucceeded}/{sum.rebillsAttempted}</span>
                    <div style={{ fontSize: 10, color: G.dim, fontWeight: 400 }}>{sum.rebillsAttempted > 0 ? `${(100 * sum.rebillsSucceeded / sum.rebillsAttempted).toFixed(0)}%` : ''}</div>
                  </>
                )}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: '#8A7BD8', fontFamily: 'DM Mono, monospace' }}>${sum.adSpend.toFixed(2)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.blue, fontFamily: 'DM Mono, monospace' }}>${avgCpa.toFixed(2)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.white, fontFamily: 'DM Mono, monospace' }}>${sum.revenue.toFixed(2)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                {sum.refunds > 0 ? `-$${sum.refunds.toFixed(2)}` : '$0.00'}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                {sum.fees > 0 ? `-$${sum.fees.toFixed(2)}` : '$0.00'}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                {sum.cogs > 0 ? `-$${sum.cogs.toFixed(2)}` : '$0.00'}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: sum.disputes > 0 ? G.red : G.dim, fontFamily: 'DM Mono, monospace' }}>
                {sum.disputes > 0 ? `-$${sum.disputes.toFixed(2)}` : '$0.00'}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: sum.alertFees > 0 ? G.red : G.dim, fontFamily: 'DM Mono, monospace' }}>
                {sum.alertFees > 0 ? `-$${sum.alertFees.toFixed(2)}` : '$0.00'}
                {sum.alertCount > 0 && <div style={{ fontSize: 10, color: G.dim, fontWeight: 400 }}>{sum.alertCount} alerts</div>}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: sum.profit >= 0 ? G.green : G.red, fontFamily: 'DM Mono, monospace' }}>
                {sum.profit >= 0 ? '+' : ''}${sum.profit.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function BeneficiosDailyChart({ rows }) {
  const W = 960, H = 220, P = 32;
  const revs = rows.map(r => r.revenue || 0);
  const profs = rows.map(r => r.profit || 0);
  const maxY = Math.max(...revs, 1);
  const minY = Math.min(...profs, 0);
  const range = maxY - minY || 1;
  const x = i => P + i * ((W - P * 2) / Math.max(1, rows.length - 1));
  const y = v => H - P - ((v - minY) / range) * (H - P * 2);
  const pathRev = rows.map((r, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(r.revenue || 0).toFixed(1)}`).join(' ');
  const pathProf = rows.map((r, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(r.profit || 0).toFixed(1)}`).join(' ');
  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
      <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, display: 'flex', gap: 16 }}>
        <span style={{ color: G.gold }}>— Revenue</span>
        <span style={{ color: G.green }}>— Profit</span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <line x1={P} y1={y(0)} x2={W - P} y2={y(0)} stroke={G.border} strokeWidth="1" strokeDasharray="2 2" />
        <path d={pathRev}  fill="none" stroke={G.gold} strokeWidth="2" />
        <path d={pathProf} fill="none" stroke={G.green} strokeWidth="2" />
        {rows.map((r, i) => (i % Math.max(1, Math.ceil(rows.length / 10)) === 0) && (
          <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill={G.muted}>{fmtDate(r.date)}</text>
        ))}
      </svg>
    </div>
  );
}

// ── Costs tab: gateway fee editor + COGS ──────────────────────────────
function BeneficiosCosts({ gateways, setGateways, products, setProducts, shops, showToast, onReload }) {
  const [editingGw, setEditingGw] = useState(null);
  const [editingProd, setEditingProd] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEditing, setBulkEditing] = useState(false);
  const [filter, setFilter] = useState('');
  const [matchStatus, setMatchStatus] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);

  const loadMatchStatus = async () => {
    setLoadingMatch(true);
    try {
      const r = await beneficiosApi.productsMatchStatus(30);
      setMatchStatus(r);
    } catch (e) {
      showToast('Error cargando diagnóstico: ' + (e.response?.data?.error || e.message), 'error');
    }
    setLoadingMatch(false);
  };

  // Quick-add an unmatched product straight from the diagnostic panel.
  const addFromUnmatched = async (item) => {
    try {
      const saved = await beneficiosApi.saveProduct({
        title: item.title,
        sku: item.sku,
        productId: item.productId,
        cost: 0,
        currency: 'EUR',
      });
      const existing = products.find(p => p.id === saved.id);
      setProducts(existing ? products.map(p => p.id === saved.id ? saved : p) : [...products, saved]);
      showToast(`"${item.title}" añadido al catálogo — edita su coste`, 'success');
      loadMatchStatus();
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const filteredProducts = products.filter(p => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (p.title || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.productId || '').toLowerCase().includes(q);
  });

  const allFilteredSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));
  const someFilteredSelected = filteredProducts.some(p => selectedIds.has(p.id));

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredProducts.forEach(p => next.delete(p.id));
      } else {
        filteredProducts.forEach(p => next.add(p.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const applyBulkUpdate = async (changes) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const r = await beneficiosApi.bulkUpdateProducts(ids, changes);
      // Refresh list from server to get consistent state
      const list = await beneficiosApi.listProducts();
      setProducts(list || []);
      setBulkEditing(false);
      clearSelection();
      showToast(`${r.updatedCount} costes actualizados${r.skippedCount ? ` (${r.skippedCount} omitidos)` : ''}`, 'success');
      onReload();
    } catch (e) {
      showToast('Error en edición en lote: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Eliminar ${ids.length} coste${ids.length === 1 ? '' : 's'} de producto? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await beneficiosApi.bulkDeleteProducts(ids);
      setProducts(products.filter(p => !selectedIds.has(p.id)));
      clearSelection();
      showToast(`${r.deleted} costes eliminados`, 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  // ── Quick-add inline row ────────────────────────────────────────────────
  // Operator typed just a title + cost and presses Enter: saves immediately
  // without opening the full modal. Ideal for batch data-entry.
  const [quick, setQuick] = useState({ title: '', cost: '' });
  const submitQuick = async () => {
    const title = (quick.title || '').trim();
    const costRaw = String(quick.cost ?? '').replace(',', '.').trim();
    const cost = parseFloat(costRaw);
    if (!title && !isFinite(cost)) return;
    try {
      const saved = await beneficiosApi.saveProduct({
        title,
        cost: isFinite(cost) ? cost : 0,
        currency: 'EUR',
      });
      const existing = products.find(p => p.id === saved.id);
      setProducts(existing ? products.map(p => p.id === saved.id ? saved : p) : [...products, saved]);
      setQuick({ title: '', cost: '' });
      showToast(title ? `"${title}" guardado` : 'Coste guardado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const saveFees = async (gw, fs) => {
    try {
      const updated = await beneficiosApi.updateFees(gw.id, fs);
      setGateways(gateways.map(g => g.id === gw.id ? updated : g));
      setEditingGw(null);
      showToast('Fees guardadas', 'success');
      onReload();
    } catch (e) {
      showToast('Error guardando fees: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const saveProduct = async (prod) => {
    try {
      const saved = await beneficiosApi.saveProduct(prod);
      const existing = products.find(p => p.id === saved.id);
      setProducts(existing ? products.map(p => p.id === saved.id ? saved : p) : [...products, saved]);
      setEditingProd(null);
      showToast('Producto guardado', 'success');
      onReload();
    } catch (e) {
      showToast('Error guardando producto: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('¿Eliminar este coste de producto?')) return;
    try {
      await beneficiosApi.deleteProduct(id);
      setProducts(products.filter(p => p.id !== id));
      showToast('Producto eliminado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const importFromOrders = async () => {
    setImporting(true);
    try {
      const r = await beneficiosApi.importProducts();
      showToast(`Importados ${r.imported || 0} productos nuevos (${r.skipped || 0} ya existían)`, 'success');
      const list = await beneficiosApi.listProducts();
      setProducts(list || []);
      onReload();
    } catch (e) {
      showToast('Error importando: ' + (e.response?.data?.error || e.message), 'error');
    }
    setImporting(false);
  };

  return (
    <>
      {/* Gateways + fee structure */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, marginBottom: 24, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, fontWeight: 700, color: G.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{'\u2692'}</span> Fees por pasarela
          <span style={{ fontSize: 11, color: G.muted, fontWeight: 400, marginLeft: 8 }}>
            Cada pasarela cobra un % + fijo. Edita aquí para que el cálculo de beneficios sea exacto.
          </span>
        </div>
        <table className="table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Pasarela</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>PSP</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Percent</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Flat</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Chargeback</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Currency</th>
              <th style={{ padding: '8px 12px', textAlign: 'center' }}>Fee on refund</th>
              <th style={{ padding: '8px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {gateways.map(g => {
              const fs = g.feeStructure || {};
              return (
                <tr key={g.id} style={{ borderTop: `1px solid ${G.border}` }}>
                  <td style={{ padding: '8px 12px', color: G.white, fontWeight: 600 }}>{g.name || g.id}</td>
                  <td style={{ padding: '8px 12px', color: G.gold }}>{g.psp}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{(fs.percent || 0).toFixed(2)}%</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{(fs.flat || 0).toFixed(2)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{(fs.chargebackFee || 0).toFixed(2)}</td>
                  <td style={{ padding: '8px 12px' }}>{fs.currency || 'EUR'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>{fs.refundKeepsFee !== false ? '\u2713' : '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setEditingGw(g)}>Editar</button>
                  </td>
                </tr>
              );
            })}
            {gateways.length === 0 && <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: G.muted }}>No hay pasarelas configuradas aún.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* COGS catalog */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: G.gold, fontWeight: 700 }}>{'\u{1F4E6}'} Catálogo de costes (COGS)</span>
          <span style={{ fontSize: 11, color: G.muted, marginLeft: 8 }}>
            Coste de cada producto. Selecciona varios para editar en lote.
          </span>
          <div style={{ flex: 1 }} />
          <input
            type="search"
            placeholder="Buscar…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ background: G.black, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, padding: '6px 10px', fontSize: 12, width: 160 }}
          />
          <button className="btn btn-ghost" onClick={importFromOrders} disabled={importing} style={{ fontSize: 12 }}>
            {importing ? '⏳ Importando…' : '↻ Importar de pedidos'}
          </button>
          <button className="btn btn-primary" onClick={() => setEditingProd({ title: '', sku: '', cost: 0, currency: 'EUR', shopId: '' })} style={{ fontSize: 12 }}>+ Nuevo</button>
        </div>

        {/* Bulk action bar — only visible with selection */}
        {selectedIds.size > 0 && (
          <div style={{ padding: '10px 16px', background: 'rgba(201,166,107,0.08)', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: G.gold, fontWeight: 600, fontSize: 13 }}>
              {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}
            </span>
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setBulkEditing(true)}>
              {'\u270F\uFE0F'} Editar costes en lote
            </button>
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 12px', color: G.red, borderColor: G.red }} onClick={bulkDelete}>
              {'\u{1F5D1}'} Eliminar seleccionados
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={clearSelection}>
              Deseleccionar
            </button>
          </div>
        )}

        <table className="table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
              <th style={{ padding: '8px 12px', textAlign: 'center', width: 36 }}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={el => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Producto</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>SKU / ID</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tienda</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Coste</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Envío</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Moneda</th>
              <th style={{ padding: '8px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(p => {
              const shopName = resolveShopName(shops, p.shopId);
              const isSel = selectedIds.has(p.id);
              return (
                <tr key={p.id} style={{ borderTop: `1px solid ${G.border}`, background: isSel ? 'rgba(201,166,107,0.05)' : 'transparent' }}>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleOne(p.id)} style={{ cursor: 'pointer' }} />
                  </td>
                  <td style={{ padding: '8px 12px', color: G.white }}>{p.title || '—'}</td>
                  <td style={{ padding: '8px 12px', color: G.muted, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{p.sku || p.productId || '—'}</td>
                  <td style={{ padding: '8px 12px', color: G.muted }}>{p.shopId ? shopName : 'Todas'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.gold }}>{parseFloat(p.cost || 0).toFixed(2)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.muted }}>{parseFloat(p.shippingCost || 0).toFixed(2)}</td>
                  <td style={{ padding: '8px 12px' }}>{p.currency || 'EUR'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditingProd(p)}>Editar</button>
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: G.red, marginLeft: 4 }} onClick={() => deleteProduct(p.id)}>Eliminar</button>
                  </td>
                </tr>
              );
            })}
            {filteredProducts.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 30, textAlign: 'center', color: G.muted }}>
                  {filter ? `Ningún producto coincide con "${filter}".` : 'Sin costes definidos. Usa la fila de abajo, "Importar de pedidos" o "+ Nuevo".'}
                </td>
              </tr>
            )}
            {/* Quick-add inline row */}
            <tr style={{ borderTop: `2px solid ${G.border}`, background: G.black }}>
              <td style={{ padding: '8px 12px', textAlign: 'center', color: G.gold, fontSize: 16 }}>+</td>
              <td colSpan={2} style={{ padding: '6px 12px' }}>
                <input
                  type="text"
                  placeholder="Título o SKU (ej. matricula)"
                  value={quick.title}
                  onChange={e => setQuick({ ...quick, title: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && submitQuick()}
                  style={{ width: '100%', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, padding: '6px 10px', fontSize: 12 }}
                />
              </td>
              <td style={{ padding: '6px 12px' }}>
                <span style={{ fontSize: 11, color: G.muted }}>— Todas —</span>
              </td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={quick.cost}
                  onChange={e => setQuick({ ...quick, cost: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && submitQuick()}
                  style={{ width: 90, background: G.card, border: `1px solid ${G.border}`, color: G.gold, borderRadius: 6, padding: '6px 10px', fontSize: 12, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}
                />
              </td>
              <td colSpan={2} style={{ padding: '6px 12px', fontSize: 11, color: G.muted }}>EUR</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding: '5px 12px' }}
                  onClick={submitQuick}
                  disabled={!((quick.title || '').trim() || parseFloat(String(quick.cost).replace(',', '.')))}
                >
                  Guardar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ padding: '10px 16px', fontSize: 11, color: G.muted, borderTop: `1px solid ${G.border}` }}>
          {'\u{1F4A1}'} Tip: escribe en la fila verde de arriba y pulsa <b style={{ color: G.gold }}>Enter</b> o <b style={{ color: G.gold }}>Guardar</b> — no necesitas abrir modales.
          Para que el coste se sume en el P&L, el título debe coincidir (case-insensitive) con el producto que aparece en los pedidos.
        </div>
      </div>

      {/* Match-status diagnostic: shows which products from recent payments
          do / do not match the catalogue. Helps explain why COGS aren't
          summing in the P&L. */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, marginTop: 24, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: G.gold, fontWeight: 700 }}>{'\u{1F50D}'} Diagnóstico de matching (últimos 30 días)</span>
          <span style={{ fontSize: 11, color: G.muted, marginLeft: 8 }}>
            Comprueba qué productos de tus pedidos se están vinculando al catálogo.
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={loadMatchStatus} disabled={loadingMatch} style={{ fontSize: 12 }}>
            {loadingMatch ? '⏳ Analizando…' : matchStatus ? '↻ Refrescar' : '▶ Analizar'}
          </button>
        </div>
        {matchStatus && (
          <div style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 700, marginBottom: 8 }}>
                  {'\u2713'} Con match ({matchStatus.matched.length})
                </div>
                {matchStatus.matched.length === 0 ? (
                  <div style={{ fontSize: 12, color: G.muted, fontStyle: 'italic' }}>Ningún producto de tus pedidos coincide con el catálogo.</div>
                ) : (
                  <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {matchStatus.matched.map((m, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: `1px solid ${G.border}` }}>
                        <span style={{ color: G.white }}>{m.title}</span>
                        <span style={{ color: G.muted, fontFamily: 'DM Mono, monospace' }}>×{m.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#f87171', fontWeight: 700, marginBottom: 8 }}>
                  {'\u26A0'} Sin match ({matchStatus.unmatched.length})
                </div>
                {matchStatus.unmatched.length === 0 ? (
                  <div style={{ fontSize: 12, color: G.muted, fontStyle: 'italic' }}>✨ ¡Todos los productos de tus pedidos tienen match!</div>
                ) : (
                  <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {matchStatus.unmatched.map((m, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12, borderBottom: `1px solid ${G.border}`, gap: 8 }}>
                        <span style={{ color: G.white, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</span>
                        <span style={{ color: G.muted, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>×{m.count}</span>
                        <button className="btn btn-ghost" style={{ fontSize: 10, padding: '2px 8px', color: G.gold, borderColor: G.gold }} onClick={() => addFromUnmatched(m)}>
                          + Añadir
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {matchStatus.unmatched.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: G.muted }}>
                    Estos productos <b>NO suman en COGS</b>. Click "+ Añadir" y edita el coste, o añádelos en el catálogo con el mismo título/SKU.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {!matchStatus && !loadingMatch && (
          <div style={{ padding: 24, fontSize: 12, color: G.muted, textAlign: 'center' }}>
            Pulsa <b style={{ color: G.gold }}>Analizar</b> para ver qué productos de tus pedidos se están sumando al COGS y cuáles no.
          </div>
        )}
      </div>

      {editingGw && <FeeEditorModal gw={editingGw} onSave={saveFees} onClose={() => setEditingGw(null)} />}
      {editingProd && <ProductCostModal prod={editingProd} shops={shops} onSave={saveProduct} onClose={() => setEditingProd(null)} />}
      {bulkEditing && (
        <BulkProductEditModal
          count={selectedIds.size}
          selectedProducts={products.filter(p => selectedIds.has(p.id))}
          shops={shops}
          onSave={applyBulkUpdate}
          onClose={() => setBulkEditing(false)}
        />
      )}
    </>
  );
}

function BulkProductEditModal({ count, selectedProducts, shops, onSave, onClose }) {
  // Each field has an "apply" toggle — only applied fields end up in the payload.
  const [applyCost, setApplyCost] = useState(false);
  const [costMode, setCostMode] = useState('absolute'); // 'absolute' | 'multiplier'
  const [costValue, setCostValue] = useState(0);
  const [multiplierPct, setMultiplierPct] = useState(0); // e.g. 10 = +10%

  const [applyShipping, setApplyShipping] = useState(false);
  const [shippingValue, setShippingValue] = useState(0);

  const [applyCurrency, setApplyCurrency] = useState(false);
  const [currencyValue, setCurrencyValue] = useState('EUR');

  const [applyShop, setApplyShop] = useState(false);
  const [shopValue, setShopValue] = useState('');

  // Preview current cost range of selection
  const costs = selectedProducts.map(p => parseFloat(p.cost || 0));
  const minCost = costs.length ? Math.min(...costs) : 0;
  const maxCost = costs.length ? Math.max(...costs) : 0;
  const avgCost = costs.length ? costs.reduce((a, b) => a + b, 0) / costs.length : 0;

  const anyApplied = applyCost || applyShipping || applyCurrency || applyShop;

  const handleSave = () => {
    const changes = {};
    if (applyCost) {
      if (costMode === 'absolute') {
        changes.cost = parseFloat(costValue) || 0;
      } else {
        // multiplier: e.g. 10% → 1.10, -5% → 0.95
        const pct = parseFloat(multiplierPct) || 0;
        changes.costMultiplier = 1 + (pct / 100);
      }
    }
    if (applyShipping) changes.shippingCost = parseFloat(shippingValue) || 0;
    if (applyCurrency) changes.currency = (currencyValue || 'EUR').toUpperCase();
    if (applyShop) changes.shopId = shopValue || null;
    onSave(changes);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 560, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 6 }}>
          Editar {count} coste{count === 1 ? '' : 's'} en lote
        </div>
        <div style={{ fontSize: 12, color: G.muted, marginBottom: 16 }}>
          Actual: min {minCost.toFixed(2)} · max {maxCost.toFixed(2)} · media {avgCost.toFixed(2)}.
          Marca las casillas de los campos que quieras aplicar. Los que dejes desmarcados no cambian.
        </div>

        {/* Cost — absolute or multiplier */}
        <div style={{ border: `1px solid ${applyCost ? G.gold : G.border}`, borderRadius: 8, padding: 12, marginBottom: 10, background: applyCost ? 'rgba(201,166,107,0.05)' : 'transparent' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: applyCost ? 10 : 0 }}>
            <input type="checkbox" checked={applyCost} onChange={e => setApplyCost(e.target.checked)} />
            <span style={{ color: G.white, fontWeight: 600 }}>Cambiar coste</span>
          </label>
          {applyCost && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button type="button" className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', background: costMode === 'absolute' ? G.gold : 'transparent', color: costMode === 'absolute' ? G.black : G.white, borderColor: costMode === 'absolute' ? G.gold : G.border }} onClick={() => setCostMode('absolute')}>
                  Valor fijo
                </button>
                <button type="button" className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', background: costMode === 'multiplier' ? G.gold : 'transparent', color: costMode === 'multiplier' ? G.black : G.white, borderColor: costMode === 'multiplier' ? G.gold : G.border }} onClick={() => setCostMode('multiplier')}>
                  Ajuste en %
                </button>
              </div>
              {costMode === 'absolute' ? (
                <Field label="Nuevo coste (cada producto seleccionado tendrá este valor)" type="number" step="0.01" value={costValue} onChange={setCostValue} />
              ) : (
                <>
                  <Field label="Porcentaje (+/-)" type="number" step="0.01" value={multiplierPct} onChange={setMultiplierPct} />
                  <div style={{ fontSize: 11, color: G.muted, marginTop: 4 }}>
                    Ej. +10 → incrementa cada coste un 10%. -5 → rebaja un 5%. 0 → sin cambio.
                    {multiplierPct !== 0 && avgCost > 0 && (
                      <> Coste medio pasaría de {avgCost.toFixed(2)} a <b style={{ color: G.gold }}>{(avgCost * (1 + parseFloat(multiplierPct) / 100)).toFixed(2)}</b>.</>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Shipping cost */}
        <div style={{ border: `1px solid ${applyShipping ? G.gold : G.border}`, borderRadius: 8, padding: 12, marginBottom: 10, background: applyShipping ? 'rgba(201,166,107,0.05)' : 'transparent' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: applyShipping ? 10 : 0 }}>
            <input type="checkbox" checked={applyShipping} onChange={e => setApplyShipping(e.target.checked)} />
            <span style={{ color: G.white, fontWeight: 600 }}>Cambiar coste de envío</span>
          </label>
          {applyShipping && (
            <Field label="Nuevo coste de envío (por unidad)" type="number" step="0.01" value={shippingValue} onChange={setShippingValue} />
          )}
        </div>

        {/* Currency */}
        <div style={{ border: `1px solid ${applyCurrency ? G.gold : G.border}`, borderRadius: 8, padding: 12, marginBottom: 10, background: applyCurrency ? 'rgba(201,166,107,0.05)' : 'transparent' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: applyCurrency ? 10 : 0 }}>
            <input type="checkbox" checked={applyCurrency} onChange={e => setApplyCurrency(e.target.checked)} />
            <span style={{ color: G.white, fontWeight: 600 }}>Cambiar moneda</span>
          </label>
          {applyCurrency && (
            <Field label="Moneda" value={currencyValue} onChange={v => setCurrencyValue(v.toUpperCase())} />
          )}
        </div>

        {/* Shop assignment */}
        <div style={{ border: `1px solid ${applyShop ? G.gold : G.border}`, borderRadius: 8, padding: 12, marginBottom: 16, background: applyShop ? 'rgba(201,166,107,0.05)' : 'transparent' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: applyShop ? 10 : 0 }}>
            <input type="checkbox" checked={applyShop} onChange={e => setApplyShop(e.target.checked)} />
            <span style={{ color: G.white, fontWeight: 600 }}>Asignar a tienda</span>
          </label>
          {applyShop && (
            <div>
              <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tienda</label>
              <select
                value={shopValue}
                onChange={e => setShopValue(e.target.value)}
                style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, padding: '8px 10px', marginTop: 4, fontSize: 13 }}
              >
                <option value="">— Todas las tiendas (sin asignar) —</option>
                {shops.map(s => <option key={s.id} value={s.id}>{s.name || s.url}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!anyApplied}>
            Aplicar a {count} producto{count === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeeEditorModal({ gw, onSave, onClose }) {
  const fs = gw.feeStructure || {};
  const [form, setForm] = useState({
    percent: fs.percent || 0,
    flat: fs.flat || 0,
    currency: fs.currency || 'EUR',
    chargebackFee: fs.chargebackFee || 0,
    refundKeepsFee: fs.refundKeepsFee !== false,
    note: fs.note || '',
  });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 480, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 16 }}>Editar fees — {gw.name} ({gw.psp})</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Percent (%)" type="number" step="0.01" value={form.percent} onChange={v => setForm({ ...form, percent: v })} />
          <Field label="Flat" type="number" step="0.01" value={form.flat} onChange={v => setForm({ ...form, flat: v })} />
          <Field label="Chargeback Fee" type="number" step="0.01" value={form.chargebackFee} onChange={v => setForm({ ...form, chargebackFee: v })} />
          <Field label="Currency" value={form.currency} onChange={v => setForm({ ...form, currency: v.toUpperCase() })} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: G.white, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.refundKeepsFee} onChange={e => setForm({ ...form, refundKeepsFee: e.target.checked })} />
          <span>La pasarela NO devuelve el flat al reembolsar (se pierde)</span>
        </label>
        <Field label="Nota" value={form.note} onChange={v => setForm({ ...form, note: v })} textarea />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => onSave(gw, form)}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function ProductCostModal({ prod, shops, onSave, onClose }) {
  const [form, setForm] = useState(prod);
  const hasIdentifier = !!((form.title || '').trim() || (form.sku || '').trim() || (form.productId || '').trim());
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 520, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 16 }}>{prod.id ? 'Editar coste' : 'Nuevo coste de producto'}</div>
        <Field label="Título" value={form.title || ''} onChange={v => setForm({ ...form, title: v })} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="SKU" value={form.sku || ''} onChange={v => setForm({ ...form, sku: v })} />
          <Field label="Product ID" value={form.productId || ''} onChange={v => setForm({ ...form, productId: v })} />
          <Field label="Coste" type="number" step="0.01" value={form.cost ?? 0} onChange={v => setForm({ ...form, cost: v })} />
          <Field label="Coste envío (opcional)" type="number" step="0.01" value={form.shippingCost ?? 0} onChange={v => setForm({ ...form, shippingCost: v })} />
          <Field label="Moneda" value={form.currency || 'EUR'} onChange={v => setForm({ ...form, currency: v.toUpperCase() })} />
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tienda (opcional)</label>
          <select className="input" value={form.shopId || ''} onChange={e => setForm({ ...form, shopId: e.target.value })} style={{ width: '100%', padding: '8px 10px', marginTop: 4 }}>
            <option value="">Todas las tiendas (global)</option>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {!hasIdentifier && (
          <div style={{ marginTop: 14, padding: 10, background: 'rgba(230,150,50,0.12)', border: '1px solid rgba(230,150,50,0.4)', borderRadius: 6, fontSize: 12, color: '#f0b060' }}>
            {'\u26A0\uFE0F'} Sin título/SKU/Product ID: el coste se guardará pero <b>no se aplicará automáticamente a los pagos</b>. Añade al menos un título (ej. "matricula") que coincida con el producto de tus pedidos para que el P&L lo sume.
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// Reusable small input. Mirrors the existing `.input` CSS class used across
// the app so spacing/borders match the rest of the design system.
function Field({ label, value, onChange, type = 'text', step, textarea, placeholder }) {
  const commonStyle = { width: '100%', padding: '8px 10px', marginTop: 4, fontSize: 13 };
  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</label>
      {textarea ? (
        <textarea
          className="input"
          value={value ?? ''}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          style={{ ...commonStyle, minHeight: 60, resize: 'vertical', fontFamily: 'DM Mono, monospace' }}
        />
      ) : (
        <input
          type={type}
          step={step}
          className="input"
          value={value ?? ''}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          style={commonStyle}
        />
      )}
    </div>
  );
}

// ── Ads tab: Meta + Google + TikTok + manual + CSV ────────────────────
const TIKTOK_COLOR = '#25C2D3';

function BeneficiosAds({ config, setConfig, adSpend, setAdSpend, shops, from, to, showToast, onReload }) {
  const [metaOpen, setMetaOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  // Al volver de conectar TikTok el modal se abre solo; la marca se borra al
  // cerrarlo (no al abrirlo: Beneficios puede montarse dos veces al cargar).
  const [tiktokOpen, setTiktokOpen] = useState(() => { try { return !!sessionStorage.getItem('zrp_open_tiktok'); } catch { return false; } });
  const closeTiktok = () => { try { sessionStorage.removeItem('zrp_open_tiktok'); } catch {} setTiktokOpen(false); };
  const [manualOpen, setManualOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [syncingTiktok, setSyncingTiktok] = useState(false);

  const metaConnected = !!config?.meta?.connected;
  const googleConnected = !!config?.google?.connected;
  const tiktokConnected = !!config?.tiktok?.connected;

  const syncMeta = async () => {
    setSyncingMeta(true);
    try {
      const r = await beneficiosApi.metaSync({ from, to });
      showToast(`Meta: ${r.rows || 0} registros sincronizados`, 'success');
      const spend = await beneficiosApi.listAdSpend({ from, to });
      setAdSpend(spend || []);
      onReload();
    } catch (e) {
      showToast('Meta sync falló: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSyncingMeta(false);
  };

  // "Limpiar + resincronizar": wipes existing Meta rows in [from, to] for
  // this user, then re-pulls fresh from the API. Use when the amount looks
  // wrong (stale FX conversion, duplicates from mapping changes, etc.).
  const cleanResyncMeta = async () => {
    if (!window.confirm(`Esto BORRARÁ todos los registros de Meta entre ${from} y ${to} y los volverá a sincronizar desde cero. ¿Continuar?`)) return;
    setSyncingMeta(true);
    try {
      const r = await beneficiosApi.metaResyncClean({ from, to });
      showToast(`Meta: borrados ${r.deleted || 0}, reinsertados ${r.inserted || 0}`, 'success');
      const spend = await beneficiosApi.listAdSpend({ from, to });
      setAdSpend(spend || []);
      onReload();
    } catch (e) {
      showToast('Meta resync falló: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSyncingMeta(false);
  };

  const syncGoogle = async () => {
    setSyncingGoogle(true);
    try {
      const r = await beneficiosApi.googleSync({ from, to });
      showToast(`Google: ${r.rows || 0} registros sincronizados`, 'success');
      const spend = await beneficiosApi.listAdSpend({ from, to });
      setAdSpend(spend || []);
      onReload();
    } catch (e) {
      showToast('Google sync falló: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSyncingGoogle(false);
  };

  const cleanResyncGoogle = async () => {
    if (!window.confirm(`Esto BORRARÁ todos los registros de Google entre ${from} y ${to} y los volverá a sincronizar desde cero. ¿Continuar?`)) return;
    setSyncingGoogle(true);
    try {
      const r = await beneficiosApi.googleResyncClean({ from, to });
      showToast(`Google: borrados ${r.deleted || 0}, reinsertados ${r.inserted || 0}`, 'success');
      const spend = await beneficiosApi.listAdSpend({ from, to });
      setAdSpend(spend || []);
      onReload();
    } catch (e) {
      showToast('Google resync falló: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSyncingGoogle(false);
  };

  const syncTiktok = async () => {
    setSyncingTiktok(true);
    try {
      const r = await beneficiosApi.tiktokSync({ from, to });
      showToast(r.rows ? `TikTok: ${r.rows} registros sincronizados` : (r.note || 'TikTok: sin gasto en el rango'), r.rows ? 'success' : 'info');
      const spend = await beneficiosApi.listAdSpend({ from, to });
      setAdSpend(spend || []);
      onReload();
    } catch (e) {
      showToast('TikTok sync falló: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSyncingTiktok(false);
  };

  const deleteSpend = async (id) => {
    if (!window.confirm('¿Eliminar este registro de gasto?')) return;
    try {
      await beneficiosApi.deleteAdSpend(id);
      setAdSpend(adSpend.filter(s => s.id !== id));
      showToast('Eliminado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  // Aggregate by source
  const totalsBySource = adSpend.reduce((acc, r) => { acc[r.source] = (acc[r.source] || 0) + (r.amount || 0); return acc; }, {});

  return (
    <>
      {/* Connector cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <AdsConnectorCard
          title="Meta Ads"
          subtitle="Facebook / Instagram Ads"
          icon={'\u{1F4D8}'}
          color="#1877F2"
          connected={metaConnected}
          spent={totalsBySource.meta || 0}
          lastSync={config?.lastSync?.meta}
          onManage={() => setMetaOpen(true)}
          onSync={syncMeta}
          onCleanResync={cleanResyncMeta}
          syncing={syncingMeta}
        />
        <AdsConnectorCard
          title="Google Ads"
          subtitle="Search / YouTube / Display"
          icon={'\u{1F535}'}
          color="#4285F4"
          connected={googleConnected}
          spent={totalsBySource.google || 0}
          lastSync={config?.lastSync?.google}
          onManage={() => setGoogleOpen(true)}
          onSync={syncGoogle}
          onCleanResync={cleanResyncGoogle}
          syncing={syncingGoogle}
        />
        <AdsConnectorCard
          title="TikTok Ads"
          subtitle="Cada cuenta publicitaria → su tienda"
          icon={'\u{1F3B5}'}
          color={TIKTOK_COLOR}
          connected={tiktokConnected}
          spent={totalsBySource.tiktok || 0}
          lastSync={config?.lastSync?.tiktok}
          onManage={() => setTiktokOpen(true)}
          onSync={syncTiktok}
          syncing={syncingTiktok}
        />
        <div style={{ background: G.card, border: `1px dashed ${G.border}`, borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 22 }}>{'\u{1F4DD}'}</div>
          <div style={{ fontWeight: 700, color: G.white }}>Manual + CSV</div>
          <div style={{ fontSize: 12, color: G.muted, flex: 1 }}>
            Añade gasto manualmente o importa un CSV de tu plataforma preferida (Snapchat, Pinterest…).
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setManualOpen(true)} style={{ fontSize: 12 }}>+ Manual</button>
            <button className="btn btn-ghost" onClick={() => setCsvOpen(true)} style={{ fontSize: 12 }}>{'\u{1F4C4}'} CSV</button>
          </div>
        </div>
      </div>

      {/* Ad spend table */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.border}`, fontWeight: 700, color: G.gold, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Registros de gasto publicitario ({from} → {to})</span>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 11, padding: '4px 10px' }}
            onClick={async () => {
              try {
                const d = await beneficiosApi.adSpendDiag({ from, to });
                const lines = [];
                lines.push(`Rango: ${d.range.from} → ${d.range.to}`);
                lines.push(`Filas: ${d.totalRows}  |  Σ amount: ${d.totalAmount}  |  Σ rawAmount: ${d.totalRawAmount}`);
                lines.push(`\nPor fuente:`);
                for (const [k, v] of Object.entries(d.bySource || {})) lines.push(`  ${k}: ${v.count} filas, amount=${v.sumAmount.toFixed(2)}, raw=${v.sumRaw.toFixed(2)}`);
                lines.push(`\nPor moneda:`);
                for (const [k, v] of Object.entries(d.byCurrency || {})) lines.push(`  ${k}: ${v.count} filas, amount=${v.sumAmount.toFixed(2)}, raw=${v.sumRaw.toFixed(2)}, fxRates=[${(v.fxRates||[]).join(', ')}]`);
                lines.push(`\nDesfases (amount ≠ rawAmount): ${d.mismatchCount}`);
                if (d.mismatches && d.mismatches.length > 0) {
                  d.mismatches.slice(0, 10).forEach(m => lines.push(`  ${m.date}  ${m.source}  ${m.rawCurrency}  raw=${m.rawAmount}  amount=${m.amount}  fx=${m.fxRate}  (${m.diffPct}%)`));
                  if (d.mismatches.length > 10) lines.push(`  ... y ${d.mismatches.length - 10} más`);
                }
                // eslint-disable-next-line no-console
                console.log('[AdSpend Diag]', d);
                window.alert(lines.join('\n'));
              } catch (e) {
                showToast('Diag falló: ' + (e.response?.data?.error || e.message), 'error');
              }
            }}
            title="Muestra un desglose de los gastos por fuente y moneda para diagnosticar diferencias con Ads Manager."
          >
            🔍 Diagnóstico
          </button>
        </div>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: G.black, color: G.muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1, position: 'sticky', top: 0 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fecha</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fuente</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tienda</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Campaña</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Importe</th>
                <th style={{ padding: '8px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {adSpend.slice().sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(r => {
                const shopName = resolveShopName(shops, r.shopId);
                return (
                  <tr key={r.id} style={{ borderTop: `1px solid ${G.border}` }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'DM Mono, monospace' }}>{r.date}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: r.source === 'meta' ? '#1877F220' : r.source === 'google' ? '#4285F420' : r.source === 'tiktok' ? TIKTOK_COLOR + '22' : G.goldDim, color: r.source === 'meta' ? '#4D8FE9' : r.source === 'google' ? '#6FA7F7' : r.source === 'tiktok' ? TIKTOK_COLOR : G.gold }}>
                        {r.source?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: G.muted }}>{shopName}</td>
                    <td style={{ padding: '8px 12px', color: G.muted, fontSize: 11 }}>{r.campaignName || r.campaignId || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: G.gold }}>{fmtEUR(r.amount)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {r.source === 'manual' && <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px', color: G.red }} onClick={() => deleteSpend(r.id)}>X</button>}
                    </td>
                  </tr>
                );
              })}
              {adSpend.length === 0 && <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: G.muted }}>Sin registros. Conecta Meta, Google, TikTok o añade manualmente.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {metaOpen && <MetaAdsModal config={config} setConfig={setConfig} shops={shops} showToast={showToast} onClose={() => setMetaOpen(false)} onReload={onReload} />}
      {googleOpen && <GoogleAdsModal config={config} setConfig={setConfig} shops={shops} showToast={showToast} onClose={() => setGoogleOpen(false)} onReload={onReload} />}
      {tiktokOpen && <TikTokAdsModal config={config} setConfig={setConfig} shops={shops} from={from} to={to} showToast={showToast} onClose={closeTiktok} onReload={onReload} />}
      {manualOpen && <ManualAdSpendModal shops={shops} showToast={showToast} onClose={() => setManualOpen(false)} onSaved={(r) => { setAdSpend([...adSpend, r]); onReload(); }} />}
      {csvOpen && <CsvImportModal showToast={showToast} onClose={() => setCsvOpen(false)} onDone={() => { beneficiosApi.listAdSpend({ from, to }).then(s => setAdSpend(s || [])); onReload(); }} />}
    </>
  );
}

function AdsConnectorCard({ title, subtitle, icon, color, connected, spent, lastSync, onManage, onSync, onCleanResync, syncing }) {
  return (
    <div style={{ background: G.card, border: `1px solid ${connected ? color + '60' : G.border}`, borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 24 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: G.white }}>{title}</div>
          <div style={{ fontSize: 11, color: G.muted }}>{subtitle}</div>
        </div>
        {connected && <div style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: G.green + '25', color: G.green, fontWeight: 700 }}>CONECTADO</div>}
      </div>
      <div style={{ fontSize: 11, color: G.muted }}>
        Gasto en rango: <span style={{ color: G.gold, fontFamily: 'DM Mono, monospace' }}>{fmtEUR(spent)}</span>
      </div>
      {lastSync && <div style={{ fontSize: 11, color: G.dim }}>Últ. sync: {new Date(lastSync).toLocaleString('es-ES', { timeZone: APP_TZ })}</div>}
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onManage}>
          {connected ? 'Configurar' : 'Conectar'}
        </button>
        {connected && <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={onSync} disabled={syncing}>{syncing ? '⏳' : '↻ Sincronizar'}</button>}
        {connected && onCleanResync && (
          <button
            className="btn btn-ghost"
            style={{ fontSize: 11, color: G.red, borderColor: G.red + '60' }}
            onClick={onCleanResync}
            disabled={syncing}
            title="Borra los registros del rango actual y los vuelve a sincronizar desde cero. Úsalo si ves importes incorrectos (FX obsoleto, duplicados, etc.)."
          >
            {syncing ? '⏳' : '🧹 Limpiar + resync'}
          </button>
        )}
      </div>
    </div>
  );
}

function MetaAdsModal({ config, setConfig, shops, showToast, onClose, onReload }) {
  const [token, setToken] = useState('');
  const [accounts, setAccounts] = useState([]);
  // Track WHY accounts is empty: null = loading, '' = loaded OK, string = error msg.
  // Shows a prominent banner so "Guardar mapeo no va" becomes "tu token no puede
  // listar ad accounts — el MultiSelect no tiene opciones, por eso no deja guardar".
  const [accountsError, setAccountsError] = useState(null);
  const [mapping, setMapping] = useState(config?.meta?.mapping || []);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Verbose diagnostic from backend when token validation fails:
  //   { error, hint, stage, metaCode, metaSubcode, metaType }
  const [connectError, setConnectError] = useState(null);
  const metaConnected = !!config?.meta?.connected;

  useEffect(() => {
    if (metaConnected) {
      setAccountsError(null);
      beneficiosApi.metaAccounts()
        .then(list => { setAccounts(list || []); setAccountsError(''); })
        .catch(e => {
          const msg = e.response?.data?.error || e.message || 'error desconocido';
          console.error('[meta/accounts] failed', e);
          setAccounts([]);
          setAccountsError(msg);
        });
    }
  }, [metaConnected]);

  const doConnect = async () => {
    if (!token.trim()) { showToast('Pega el System User token', 'error'); return; }
    setLoading(true);
    setConnectError(null);
    try {
      const r = await beneficiosApi.metaConnect(token.trim());
      const accountCount = Array.isArray(r.accounts) ? r.accounts.length : 0;
      setConfig({ ...config, meta: { ...(config?.meta || {}), connected: true, tokenMask: r.tokenMask } });
      showToast(`Meta conectado — ${accountCount} ad accounts`, 'success');
      setAccounts(r.accounts || []);
      onReload();
    } catch (e) {
      const body = e.response?.data || {};
      // Keep the structured error so we can render a helpful diagnostic block
      // below the token field (stage + Meta code + hint).
      setConnectError({
        error: body.error || e.message,
        hint: body.hint,
        stage: body.stage,
        metaCode: body.metaCode,
        metaSubcode: body.metaSubcode,
        metaType: body.metaType,
      });
      showToast('Error: ' + (body.error || e.message), 'error');
    }
    setLoading(false);
  };

  const doDisconnect = async () => {
    if (!window.confirm('¿Desconectar Meta? Se borrará el token (los registros ya sincronizados se mantienen).')) return;
    try {
      await beneficiosApi.metaDisconnect();
      setConfig({ ...config, meta: { connected: false } });
      setAccounts([]);
      setMapping([]);
      showToast('Meta desconectado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const [savingMapping, setSavingMapping] = useState(false);
  const saveMapping = async () => {
    setSaveError('');
    // Validate before hitting the backend — surface problems both as a
    // toast AND inline below the button so the operator can't miss them.
    if (!Array.isArray(mapping) || mapping.length === 0) {
      const msg = 'Añade al menos un mapeo (tienda + ad account).';
      setSaveError(msg); showToast(msg, 'error'); return;
    }
    const incompleteIdx = mapping.findIndex(m => !m.shopId || !Array.isArray(m.adAccountIds) || m.adAccountIds.length === 0);
    if (incompleteIdx >= 0) {
      const row = mapping[incompleteIdx];
      const reason = !row.shopId ? 'falta la tienda' : 'no has seleccionado ninguna ad account';
      const msg = `Fila ${incompleteIdx + 1}: ${reason}.`;
      setSaveError(msg); showToast(msg, 'error'); return;
    }
    setSavingMapping(true);
    try {
      const r = await beneficiosApi.metaMapping(mapping);
      setConfig({ ...config, meta: { ...config?.meta, mapping: r?.mapping || mapping } });
      showToast(`Mapeo guardado (${mapping.length} ${mapping.length === 1 ? 'fila' : 'filas'})`, 'success');
      onReload();
    } catch (e) {
      // Surface the HTTP status + raw server error so "no me deja guardar"
      // becomes actionable (e.g. 401 = token expired, 502 = backend down).
      const status = e.response?.status;
      const body = e.response?.data?.error || e.response?.data?.message || e.message || 'desconocido';
      const msg = `Error al guardar (${status || 'red'}): ${body}`;
      setSaveError(msg); showToast(msg, 'error');
      // Leave the modal open and the form intact so the user can retry.
      console.error('[meta/mapping] save failed', e);
    } finally {
      setSavingMapping(false);
    }
  };

  const addRow = () => setMapping([...mapping, { shopId: shops[0]?.id || '', adAccountIds: [], campaignIds: [], excludeCampaignIds: [] }]);
  const updateRow = (i, patch) => setMapping(mapping.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const deleteRow = (i) => setMapping(mapping.filter((_, idx) => idx !== i));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 720, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 700, color: G.gold, marginBottom: 16 }}>{'\u{1F4D8}'} Meta Ads</div>

        {!metaConnected && (
          <>
            <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 16 }}>
              <strong style={{ color: G.white }}>Paso 1.</strong> Entra en{' '}
              <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" style={{ color: G.gold }}>Business Manager → Users → System Users</a>.<br/>
              <strong style={{ color: G.white }}>Paso 2.</strong> Crea un System User, asigna tus Ad Accounts con permiso "Manage campaigns" + "View performance".<br/>
              <strong style={{ color: G.white }}>Paso 3.</strong> Genera un token con scopes: <code style={{ color: G.gold }}>ads_read</code>, <code style={{ color: G.gold }}>ads_management</code>, <code style={{ color: G.gold }}>business_management</code>.<br/>
              <strong style={{ color: G.white }}>Paso 4.</strong> Pégalo abajo. El token nunca se expone en la UI — queda cifrado en BD.
            </div>
            <Field label="System User Token" value={token} onChange={setToken} textarea />
            {connectError && (
              <div style={{ marginTop: 12, padding: 14, background: G.red + '15', borderRadius: 8, border: `1px solid ${G.red}40`, fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ color: G.red, fontWeight: 700, marginBottom: 6 }}>
                  ✗ {connectError.error || 'No se pudo validar el token'}
                </div>
                {connectError.hint && (
                  <div style={{ color: G.white, marginBottom: 8 }}>
                    <strong style={{ color: G.gold }}>Qué hacer:</strong> {connectError.hint}
                  </div>
                )}
                {(connectError.metaCode != null || connectError.stage) && (
                  <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                    Diagnóstico:
                    {connectError.stage && ` stage=${connectError.stage}`}
                    {connectError.metaCode != null && ` · code=${connectError.metaCode}`}
                    {connectError.metaSubcode != null && ` · subcode=${connectError.metaSubcode}`}
                    {connectError.metaType && ` · type=${connectError.metaType}`}
                  </div>
                )}
                {(String(connectError.error || '').toLowerCase().includes('access blocked') ||
                  String(connectError.hint || '').toLowerCase().includes('access blocked') ||
                  connectError.metaCode === 10) && (
                  <div style={{ fontSize: 12, color: G.dim, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${G.border}` }}>
                    <strong style={{ color: G.white }}>Checklist "API access blocked":</strong>
                    <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
                      <li>Abre la app en{' '}
                        <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" style={{ color: G.gold }}>developers.facebook.com/apps</a>
                        {' '}— si la app estuvo 90+ días sin uso Meta la marca inactiva; entrar y hacer una petición la reactiva.
                      </li>
                      <li>En{' '}
                        <a href="https://business.facebook.com/settings/requests" target="_blank" rel="noreferrer" style={{ color: G.gold }}>Business Settings → Requests</a>
                        {' '}revisa si hay requisitos de cumplimiento pendientes (Data Use Checkup, etc.).
                      </li>
                      <li>Verifica que la app esté en <strong>Modo Live</strong> (no Development). En Development solo los admins/devs pueden usar el token.</li>
                      <li>Regenera el token en{' '}
                        <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" style={{ color: G.gold }}>System Users</a>
                        {' '}con scopes <code style={{ color: G.gold }}>ads_read</code> + <code style={{ color: G.gold }}>business_management</code>.
                      </li>
                    </ol>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={doConnect} disabled={loading}>{loading ? 'Conectando…' : 'Conectar Meta'}</button>
            </div>
          </>
        )}

        {metaConnected && (
          <>
            <div style={{ fontSize: 13, color: accounts.length > 0 ? G.green : G.red, marginBottom: 16 }}>
              {accounts.length > 0
                ? `✓ Conectado (${accounts.length} ad accounts disponibles)`
                : accountsError === null
                  ? '⏳ Cargando ad accounts…'
                  : accountsError
                    ? `⚠ Token conectado pero NO hay ad accounts. El token no tiene permiso sobre ninguna cuenta. Causa: ${accountsError}`
                    : '⚠ Token conectado pero Meta devuelve 0 ad accounts. Revisa en Business Manager → System Users que la cuenta tenga ad accounts asignadas con permiso "View performance".'}
            </div>
            {accounts.length === 0 && accountsError !== null && (
              <div style={{ fontSize: 12, color: G.muted, marginBottom: 16, padding: 10, background: G.red + '15', borderRadius: 6, border: `1px solid ${G.red}40`, lineHeight: 1.6 }}>
                <strong style={{ color: G.red }}>Esto es por lo que "Guardar mapeo" no hace nada:</strong> sin ad accounts disponibles no se puede completar ninguna fila → la validación bloquea el guardado.
                <br /><br />
                <strong style={{ color: G.white }}>Solución:</strong> desconecta Meta abajo y vuelve a conectar con un token nuevo que tenga los scopes <code style={{ color: G.gold }}>ads_read</code> + <code style={{ color: G.gold }}>business_management</code> y ad accounts asignadas al System User.
              </div>
            )}
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 12 }}>
              Mapea cada tienda con una o más ad accounts (y opcionalmente filtra por campañas).
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mapping.map((m, i) => (
                <div key={i} style={{ border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="input" value={m.shopId} onChange={e => updateRow(i, { shopId: e.target.value })} style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}>
                      {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button className="btn btn-ghost" style={{ fontSize: 11, color: G.red }} onClick={() => deleteRow(i)}>X</button>
                  </div>
                  <MultiSelect
                    label="Ad accounts"
                    options={accounts.map(a => ({ value: a.id, label: `${a.name} — ${a.id}` }))}
                    values={m.adAccountIds || []}
                    onChange={v => updateRow(i, { adAccountIds: v })}
                  />
                  <Field label="Campaign IDs (opcional, separados por coma)"
                    value={(m.campaignIds || []).join(',')}
                    onChange={v => updateRow(i, { campaignIds: v.split(',').map(x => x.trim()).filter(Boolean) })} />
                  <Field label="Excluir campañas del gasto (IDs, separados por coma)"
                    value={(m.excludeCampaignIds || []).join(',')}
                    onChange={v => updateRow(i, { excludeCampaignIds: v.split(',').map(x => x.trim()).filter(Boolean) })} />
                </div>
              ))}
              <button className="btn btn-ghost" onClick={addRow} style={{ fontSize: 12 }}>+ Añadir mapeo</button>
            </div>

            {saveError && (
              <div style={{ marginTop: 14, padding: 10, background: G.red + '18', borderRadius: 6, border: `1px solid ${G.red}50`, color: G.red, fontSize: 12, fontWeight: 600 }}>
                ⚠ {saveError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 20 }}>
              <button className="btn btn-ghost" style={{ color: G.red }} onClick={doDisconnect}>Desconectar Meta</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
                <button
                  className="btn btn-primary"
                  onClick={saveMapping}
                  disabled={savingMapping}
                  style={{ minWidth: 150 }}
                >
                  {savingMapping ? '\u23F3 Guardando\u2026' : 'Guardar mapeo'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── TikTok Ads (2026-10-31) ───────────────────────────────────────────
// Conexión por OAuth de TikTok for Business; luego una tienda por cuenta
// publicitaria. Las activas salen primero; las limitadas/cerradas, plegadas
// (pueden tener gasto antiguo que asignar).

function tiktokStatusChip(status) {
  const s = String(status || '');
  if (s === 'STATUS_ENABLE') return { label: 'Activa', color: G.green };
  if (s === 'STATUS_LIMIT') return { label: 'Limitada', color: G.orange || G.gold };
  if (/PENDING|CONFIRM/.test(s)) return { label: 'En revisión', color: G.gold };
  if (/DISABLE|DELETE|CLOSE|FAIL/.test(s)) return { label: 'Cerrada', color: G.red };
  return { label: s ? s.replace('STATUS_', '').toLowerCase() : '—', color: G.muted };
}

function TikTokAdsModal({ config, setConfig, shops, from, to, showToast, onClose, onReload }) {
  // 2026-11-04 — varias conexiones (una por login de TikTok for Business), cada
  // una con sus cuentas publicitarias; el mapeo cuenta→tienda es común.
  const connections = config?.tiktok?.connections || [];
  const connected = !!config?.tiktok?.connected || connections.length > 0;
  const [groups, setGroups] = useState(null);          // null = cargando; [{ id, label, auth, accounts, error }]
  const [accountsError, setAccountsError] = useState('');
  const savedMapping = config?.tiktok?.mapping;
  const [assign, setAssign] = useState(() => Object.fromEntries((savedMapping || []).map(m => [m.advertiserId, m.shopId])));
  const [touched, setTouched] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // La config puede llegar después de abrir el modal (vuelta del OAuth) y se
  // recarga cada 30 s: se copia el mapeo guardado mientras no se haya tocado nada.
  useEffect(() => {
    if (!touched) setAssign(Object.fromEntries((savedMapping || []).map(m => [m.advertiserId, m.shopId])));
  }, [savedMapping, touched]);
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState('');

  const loadAccounts = () => {
    setGroups(null); setAccountsError('');
    return beneficiosApi.tiktokAccounts()
      .then(r => setGroups(Array.isArray(r)
        ? [{ id: 'single', label: '', auth: config?.tiktok?.auth || {}, accounts: r, error: null }] // backend antiguo
        : (r?.connections || [])))
      .catch(e => { setGroups([]); setAccountsError(e.response?.data?.error || e.message); });
  };
  useEffect(() => { if (connected) loadAccounts(); }, [connected]);

  // Sin connectionId = conectar otra cuenta (otro login); con él = reautorizar esa.
  const startConnect = async (connectionId) => {
    setConnectError(''); setBusy(true);
    try {
      const { url, state } = await beneficiosApi.tiktokAuthorizeUrl(window.location.origin, connectionId || undefined);
      sessionStorage.setItem('zrp_tiktok_oauth_state', state);
      window.location.href = url;
    } catch (e) {
      setConnectError(e.response?.data?.error || e.message);
      setBusy(false);
    }
  };

  const disconnect = async (conn) => {
    const label = conn?.label || 'esta conexión';
    const msg = conn && connections.length > 1
      ? `¿Desconectar ${label}? Se borra su acceso y el mapeo de sus cuentas (el gasto ya guardado se mantiene).`
      : '¿Desconectar TikTok? Se borra el acceso (el gasto ya guardado se mantiene).';
    if (!window.confirm(msg)) return;
    try {
      const r = await beneficiosApi.tiktokDisconnect(conn ? conn.id : undefined);
      const rest = conn ? connections.filter(c => c.id !== conn.id) : [];
      setConfig({ ...config, tiktok: { ...config?.tiktok, connected: rest.length > 0, connections: rest, auth: r?.auth, mapping: r?.mapping || (rest.length ? config?.tiktok?.mapping : []) } });
      setTouched(false);
      showToast(conn && rest.length ? `${label} desconectada` : 'TikTok desconectado', 'success');
      onReload();
      if (!rest.length) onClose(); else loadAccounts();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const saveAndSync = async () => {
    const mapping = Object.entries(assign).filter(([, shopId]) => shopId).map(([advertiserId, shopId]) => ({ advertiserId, shopId }));
    setBusy(true);
    try {
      const r = await beneficiosApi.tiktokMapping(mapping);
      setConfig({ ...config, tiktok: { ...config?.tiktok, mapping: r?.mapping || mapping } });
      if (mapping.length) {
        const s = await beneficiosApi.tiktokSync({ from, to });
        if (s.note && !s.rows) showToast(s.note, 'error');
        else showToast(`Guardado · ${s.rows || 0} días de gasto de TikTok (${from} → ${to})`, 'success');
      } else {
        showToast('Guardado · ninguna cuenta asignada', 'success');
      }
      onReload();
      onClose();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
    setBusy(false);
  };

  const assignedCount = Object.values(assign).filter(Boolean).length;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : null);
  const dangerBox = { marginBottom: 10, padding: 10, background: G.red + '15', borderRadius: 8, border: `1px solid ${G.red}40`, fontSize: 13, color: G.red, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' };
  const warnBox = { ...dangerBox, background: G.gold + '15', border: `1px solid ${G.gold}40`, color: G.gold };

  // Estado de la autorización de UNA conexión (30 días): aviso antes de caducar
  // y "Reautorizar" (mismo flujo que Conectar; el mapeo se conserva).
  const renderAuth = (a, connectionId) => {
    if (!a) return null;
    const until = fmtDate(a.refreshExpiresAt);
    if (a.needsReauth) {
      return (
        <div style={dangerBox}>
          <span style={{ flex: 1 }}>✗ La autorización ha caducado o se ha revocado: el gasto de estas cuentas no se actualiza hasta que vuelvas a autorizar.</span>
          <button className="btn btn-primary" onClick={() => startConnect(connectionId)} disabled={busy}>{busy ? 'Abriendo TikTok…' : 'Reautorizar'}</button>
        </div>
      );
    }
    if (a.warn) {
      return (
        <div style={warnBox}>
          <span style={{ flex: 1 }}>⚠ La autorización caduca {until ? `el ${until}` : 'pronto'} ({a.daysLeft} {a.daysLeft === 1 ? 'día' : 'días'}). Reautoriza para que el gasto siga entrando.</span>
          <button className="btn btn-primary" onClick={() => startConnect(connectionId)} disabled={busy}>{busy ? 'Abriendo TikTok…' : 'Reautorizar'}</button>
        </div>
      );
    }
    return until ? <div style={{ fontSize: 11, color: G.dim, marginBottom: 8 }}>Autorización válida hasta el {until} · se renueva al reautorizar</div> : null;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 760, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 700, color: G.gold, marginBottom: 16 }}>{'\u{1F3B5}'} TikTok Ads</div>

        {!connected && (
          <>
            <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 16 }}>
              Pulsa <strong style={{ color: G.white }}>Conectar con TikTok</strong>, entra con la cuenta de TikTok for Business que gestiona tus anuncios y autoriza las cuentas publicitarias.
              Al volver eliges a qué tienda va el gasto de cada una. Si tus cuentas están en varios logins de TikTok, luego puedes <strong style={{ color: G.white }}>conectar otra cuenta</strong>. Solo se lee el gasto: ZRP no crea ni toca anuncios.
            </div>
            {connectError && (
              <div style={{ marginBottom: 12, padding: 12, background: G.red + '15', borderRadius: 8, border: `1px solid ${G.red}40`, fontSize: 13, color: G.red }}>✗ {connectError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={() => startConnect()} disabled={busy}>{busy ? 'Abriendo TikTok…' : 'Conectar con TikTok'}</button>
            </div>
          </>
        )}

        {connected && (
          <>
            {connectError && (
              <div style={{ marginBottom: 12, padding: 12, background: G.red + '15', borderRadius: 8, border: `1px solid ${G.red}40`, fontSize: 13, color: G.red }}>✗ {connectError}</div>
            )}
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 14, lineHeight: 1.5 }}>
              Elige la tienda de cada cuenta publicitaria. Varias cuentas pueden ir a la misma tienda; una cuenta sin tienda no se cuenta en el gasto.
              Cada login de TikTok conectado aparece con sus cuentas.
            </div>
            {groups === null && <div style={{ color: G.muted, fontSize: 13, padding: '12px 0' }}>⏳ Cargando cuentas de TikTok…</div>}
            {accountsError && (
              <div style={{ marginBottom: 12, padding: 12, background: G.red + '15', borderRadius: 8, border: `1px solid ${G.red}40`, fontSize: 13, color: G.red }}>
                ✗ No se pudieron leer las cuentas: {accountsError}. Si el acceso caducó o se revocó, reautoriza la conexión.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(groups || []).map((g, gi) => {
                const list = g.accounts || [];
                const visible = list.filter(a => a.active || assign[a.id]);
                const hidden = list.filter(a => !a.active && !assign[a.id]);
                const rows = showInactive ? [...visible, ...hidden] : visible;
                const conn = connections.find(c => c.id === g.id) || {};
                const label = g.label || conn.label || `Conexión ${gi + 1}`;
                const connectedAt = fmtDate(g.connectedAt || conn.connectedAt);
                return (
                  <div key={g.id} style={{ border: `1px solid ${G.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                        <div style={{ fontSize: 11, color: G.dim }}>
                          {list.length} {list.length === 1 ? 'cuenta publicitaria' : 'cuentas publicitarias'}{connectedAt ? ` · conectada el ${connectedAt}` : ''}
                        </div>
                      </div>
                      {!g.auth?.needsReauth && !g.auth?.warn && (
                        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => startConnect(g.id)} disabled={busy} title="Renueva la autorización de 30 días sin perder el mapeo">Reautorizar</button>
                      )}
                      <button className="btn btn-ghost" style={{ fontSize: 12, color: G.red }} onClick={() => disconnect({ id: g.id, label, advertiserIds: conn.advertiserIds || list.map(a => a.id) })}>Desconectar</button>
                    </div>
                    {renderAuth(g.auth, g.id)}
                    {g.error && g.error !== 'reauth' && g.error !== 'no-token' && (
                      <div style={{ marginBottom: 8, fontSize: 12, color: G.red }}>✗ No se pudieron leer las cuentas de esta conexión: {g.error}</div>
                    )}
                    {list.length === 0 && !g.error && (
                      <div style={{ color: G.muted, fontSize: 13, padding: '6px 0' }}>Esta conexión no tiene cuentas publicitarias autorizadas.</div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rows.map(a => {
                        const chip = tiktokStatusChip(a.status);
                        return (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', border: `1px solid ${assign[a.id] ? TIKTOK_COLOR + '60' : G.border}`, borderRadius: 8, padding: '10px 12px', opacity: a.active ? 1 : 0.75 }}>
                            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 700, color: G.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name || a.id}</span>
                                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: chip.color + '22', color: chip.color, fontWeight: 700, whiteSpace: 'nowrap' }}>{chip.label}</span>
                              </div>
                              <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>
                                {a.id}{a.currency ? ` · ${a.currency}` : ''}{a.timezone ? ` · ${a.timezone}` : ''}
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: G.muted, textAlign: 'right', minWidth: 110 }}>
                              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Gasto 7 días</div>
                              <div style={{ fontFamily: 'DM Mono, monospace', color: a.spend7d > 0 ? G.gold : G.dim, fontVariantNumeric: 'tabular-nums' }}>
                                {a.spend7d == null ? '—' : `${a.spend7d.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${a.currency || ''}`}
                              </div>
                            </div>
                            <select className="input" value={assign[a.id] || ''} onChange={e => { setTouched(true); setAssign({ ...assign, [a.id]: e.target.value }); }} style={{ flex: '1 1 200px', padding: '6px 10px', fontSize: 12 }} aria-label={`Tienda para ${a.name || a.id}`}>
                              <option value="">— Sin tienda (no cuenta) —</option>
                              {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                        );
                      })}
                      {hidden.length > 0 && (
                        <button className="btn btn-ghost" style={{ fontSize: 12, alignSelf: 'flex-start' }} onClick={() => setShowInactive(!showInactive)}>
                          {showInactive ? 'Ocultar cuentas no activas' : `Mostrar cuentas no activas (${hidden.length})`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => startConnect()} disabled={busy} title="Añade otro login de TikTok for Business con sus cuentas">＋ Conectar otra cuenta de TikTok</button>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: G.muted }}>{assignedCount} {assignedCount === 1 ? 'cuenta asignada' : 'cuentas asignadas'}</span>
                <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
                <button className="btn btn-primary" onClick={saveAndSync} disabled={busy || groups === null} style={{ minWidth: 170 }}>{busy ? '⏳ Guardando…' : 'Guardar y sincronizar'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GoogleAdsModal({ config, setConfig, shops, showToast, onClose, onReload }) {
  const [customers, setCustomers] = useState([]);
  const [mapping, setMapping] = useState(config?.google?.mapping || []);
  const googleConnected = !!config?.google?.connected;

  useEffect(() => {
    if (googleConnected) {
      beneficiosApi.googleCustomers().then(setCustomers).catch(() => setCustomers([]));
    }
    // Handle OAuth callback code returned to this page. Google redirects the
    // whole browser tab back to `redirectUri` with `?code=...&state=...`.
    // The state is a backend-generated base64 blob — we don't validate it
    // client-side (CSRF is covered by JWT on the callback), we just submit
    // the code so the backend can exchange it for a refresh_token.
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const stashedRedirect = sessionStorage.getItem('beneficios_google_redirect');
    if (code && stashedRedirect) {
      beneficiosApi.googleCallback({ code, redirectUri: stashedRedirect }).then(r => {
        showToast('Google Ads conectado', 'success');
        setConfig({ ...config, google: { connected: true } });
        sessionStorage.removeItem('beneficios_google_redirect');
        // Clean URL to avoid re-posting on refresh
        window.history.replaceState({}, document.title, window.location.pathname);
        onReload();
      }).catch(e => showToast('Error OAuth: ' + (e.response?.data?.error || e.message), 'error'));
    }
  }, [googleConnected]);

  const startOAuth = async () => {
    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    try {
      const { url } = await beneficiosApi.googleAuthorizeUrl(redirectUri);
      // Persist the redirectUri so after Google bounces us back we can call
      // the exchange with EXACTLY the same string (Google compares it byte-
      // for-byte — any trailing slash or query string difference breaks it).
      sessionStorage.setItem('beneficios_google_redirect', redirectUri);
      window.location.href = url;
    } catch (e) {
      showToast('Error iniciando OAuth: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const doDisconnect = async () => {
    if (!window.confirm('¿Desconectar Google Ads?')) return;
    try {
      await beneficiosApi.googleDisconnect();
      setConfig({ ...config, google: { connected: false } });
      setCustomers([]);
      setMapping([]);
      showToast('Google desconectado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const saveMapping = async () => {
    try {
      await beneficiosApi.googleMapping(mapping);
      setConfig({ ...config, google: { ...config?.google, mapping } });
      showToast('Mapeo guardado', 'success');
      onReload();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const addRow = () => setMapping([...mapping, { shopId: shops[0]?.id || '', customerId: '', loginCustomerId: '', campaignIds: [] }]);
  const updateRow = (i, patch) => setMapping(mapping.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const deleteRow = (i) => setMapping(mapping.filter((_, idx) => idx !== i));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 720, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 700, color: G.gold, marginBottom: 16 }}>{'\u{1F535}'} Google Ads</div>

        {!googleConnected && (
          <>
            <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, marginBottom: 16 }}>
              Conecta tu cuenta Google Ads con OAuth2. Necesitamos permisos <code style={{ color: G.gold }}>adwords</code> y un{' '}
              <a href="https://developers.google.com/google-ads/api/docs/first-call/dev-token" target="_blank" rel="noreferrer" style={{ color: G.gold }}>Developer Token</a> en el backend.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={startOAuth}>{'\u{1F513}'} Conectar con Google</button>
            </div>
          </>
        )}

        {googleConnected && (
          <>
            <div style={{ fontSize: 13, color: G.green, marginBottom: 16 }}>{'\u2713'} Conectado ({customers.length} customers disponibles)</div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 12 }}>
              Mapea cada tienda con un Customer ID (si usas MCC, indica también el Login Customer ID).
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mapping.map((m, i) => (
                <div key={i} style={{ border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="input" value={m.shopId} onChange={e => updateRow(i, { shopId: e.target.value })} style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}>
                      {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button className="btn btn-ghost" style={{ fontSize: 11, color: G.red }} onClick={() => deleteRow(i)}>X</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: G.muted }}>Customer ID</label>
                      <select className="input" value={m.customerId || ''} onChange={e => updateRow(i, { customerId: e.target.value })} style={{ width: '100%', padding: '6px 10px', fontSize: 12 }}>
                        <option value="">— Seleccionar —</option>
                        {customers.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <Field label="Login Customer ID (MCC)" value={m.loginCustomerId || ''} onChange={v => updateRow(i, { loginCustomerId: v })} />
                  </div>
                  <Field label="Campaign IDs (opcional, separados por coma)"
                    value={(m.campaignIds || []).join(',')}
                    onChange={v => updateRow(i, { campaignIds: v.split(',').map(x => x.trim()).filter(Boolean) })} />
                </div>
              ))}
              <button className="btn btn-ghost" onClick={addRow} style={{ fontSize: 12 }}>+ Añadir mapeo</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 20 }}>
              <button className="btn btn-ghost" style={{ color: G.red }} onClick={doDisconnect}>Desconectar Google</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
                <button className="btn btn-primary" onClick={saveMapping}>Guardar mapeo</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MultiSelect({ label, options, values, onChange }) {
  const toggle = v => {
    onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);
  };
  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</label>
      <div style={{ marginTop: 4, maxHeight: 120, overflowY: 'auto', border: `1px solid ${G.border}`, borderRadius: 6, padding: 8 }}>
        {options.length === 0 && <div style={{ fontSize: 12, color: G.muted }}>Sin opciones</div>}
        {options.map(o => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={values.includes(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ManualAdSpendModal({ shops, showToast, onClose, onSaved }) {
  const [form, setForm] = useState({ date: todayIso(), shopId: shops[0]?.id || '', source: 'manual', amount: 0, campaignName: '' });
  const save = async () => {
    try {
      const r = await beneficiosApi.saveAdSpend(form);
      showToast('Registrado', 'success');
      onSaved(r);
      onClose();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 480, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 16 }}>+ Registro manual de gasto</div>
        <Field label="Fecha" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tienda</label>
          <select className="input" value={form.shopId} onChange={e => setForm({ ...form, shopId: e.target.value })} style={{ width: '100%', padding: '8px 10px', marginTop: 4 }}>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Fuente</label>
          <select className="input" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} style={{ width: '100%', padding: '8px 10px', marginTop: 4 }}>
            <option value="manual">Manual</option>
            <option value="tiktok">TikTok Ads</option>
            <option value="snapchat">Snapchat</option>
            <option value="twitter">X / Twitter</option>
            <option value="linkedin">LinkedIn</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <Field label="Importe (EUR)" type="number" step="0.01" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
        <Field label="Campaña (opcional)" value={form.campaignName} onChange={v => setForm({ ...form, campaignName: v })} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function CsvImportModal({ showToast, onClose, onDone }) {
  const [csv, setCsv] = useState('date,shopId,source,amount,campaignName\n');
  const [source, setSource] = useState('tiktok');
  const [loading, setLoading] = useState(false);

  const doImport = async () => {
    setLoading(true);
    try {
      const r = await beneficiosApi.importCsv(csv, source);
      showToast(`Importados ${r.imported || 0} registros`, 'success');
      onDone();
      onClose();
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: 720, maxWidth: '94vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 16 }}>{'\u{1F4C4}'} Importar CSV</div>
        <div style={{ fontSize: 12, color: G.muted, marginBottom: 12 }}>
          Columnas: <code style={{ color: G.gold }}>date,shopId,source,amount,campaignId?,campaignName?</code><br/>
          El shopId debe existir en tus tiendas. date en formato YYYY-MM-DD.
        </div>
        <Field label="Fuente por defecto" value={source} onChange={setSource} />
        <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginTop: 10 }}>CSV</label>
        <textarea className="input" value={csv} onChange={e => setCsv(e.target.value)} style={{ width: '100%', minHeight: 200, fontFamily: 'DM Mono, monospace', fontSize: 12, padding: 10, marginTop: 4 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={doImport} disabled={loading}>{loading ? 'Importando…' : 'Importar'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Forecast tab ──────────────────────────────────────────────────────
// Backend returns numeric percents (e.g. rebillRate=85.50 for 85.5%) and
// ── Unit Economics tab: LTV (2 months), CAC, CPA máximo ──────────────────
// Conservative calculator using only 2 months of LTV so projections don't
// lie. Defaults are loaded from the LAST N days (default 3) so they reflect
// the current scaling phase, not the full period.
function BeneficiosUnitEconomics({ summary, rebill, products, gateways }) {
  // Average COGS from catalogue (if any)
  const avgCogs = (products && products.length > 0)
    ? products.reduce((s, p) => s + (parseFloat(p.cost) || 0) + (parseFloat(p.shippingCost) || 0), 0) / products.length
    : 12;

  // Fee structure from first gateway (or default 2.9% + $0.30)
  const fs = gateways?.[0]?.feeStructure || { percent: 2.9, flat: 0.30 };
  const feePct = parseFloat(fs.percent) || 2.9;
  const feeFlat = parseFloat(fs.flat) || 0.30;
  const feeOf = (amount) => amount > 0 ? (amount * feePct / 100 + feeFlat) : 0;

  // ── Window selector for defaults (default = last 3 days) ──────────────
  const [windowDays, setWindowDays] = useState(3);
  const [liveDefaults, setLiveDefaults] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(true);

  // ── Operator-editable inputs — start with fallbacks, overwritten when defaults load
  const [aov, setAov] = useState(35);
  const [rebillPrice, setRebillPrice] = useState(180.84); // 2 subs: $89.97 + $90.87
  const [rebillRate1, setRebillRate1] = useState(60);
  const [month2Retention, setMonth2Retention] = useState(50);
  const [cogs, setCogs] = useState(+avgCogs.toFixed(2));
  const [cogsOnRebill, setCogsOnRebill] = useState(false);
  const [cpa, setCpa] = useState(20);
  const [userEdited, setUserEdited] = useState(false);

  // Fetch rolling-window defaults when windowDays changes. We only want to
  // re-fetch when the window changes (not when every little input changes),
  // so use a ref to read latest userEdited without listing it as a dep.
  const userEditedRef = useRef(userEdited);
  userEditedRef.current = userEdited;
  useEffect(() => {
    setLoadingDefaults(true);
    beneficiosApi.unitEconomicsDefaults(windowDays)
      .then(d => {
        setLiveDefaults(d);
        if (!userEditedRef.current) {
          if (d.aov > 0) setAov(d.aov);
          if (d.rebillPrice > 0) setRebillPrice(d.rebillPrice);
          if (d.rebillRate > 0) setRebillRate1(d.rebillRate);
          if (d.cpa > 0) setCpa(d.cpa);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingDefaults(false));
  }, [windowDays]);

  const wrapSetter = (setter) => (v) => { setUserEdited(true); setter(v); };
  const resetToDefaults = () => {
    if (!liveDefaults) return;
    if (liveDefaults.aov > 0) setAov(liveDefaults.aov);
    if (liveDefaults.rebillPrice > 0) setRebillPrice(liveDefaults.rebillPrice);
    if (liveDefaults.rebillRate > 0) setRebillRate1(liveDefaults.rebillRate);
    if (liveDefaults.cpa > 0) setCpa(liveDefaults.cpa);
    setUserEdited(false);
  };

  // ── LTV calculation (2 months max — conservative) ─────────────────────
  const rebillRevM1 = rebillPrice * (rebillRate1 / 100);
  const rebillRevM2 = rebillPrice * (rebillRate1 / 100) * (month2Retention / 100);
  const ltv = aov + rebillRevM1 + rebillRevM2;

  // ── Costs per customer ─────────────────────────────────────────────────
  const cogsInitial = cogs;
  const cogsM1 = cogsOnRebill ? cogs * (rebillRate1 / 100) : 0;
  const cogsM2 = cogsOnRebill ? cogs * (rebillRate1 / 100) * (month2Retention / 100) : 0;
  const totalCogs = cogsInitial + cogsM1 + cogsM2;

  const feesInitial = feeOf(aov);
  const feesM1 = feeOf(rebillPrice) * (rebillRate1 / 100);
  const feesM2 = feeOf(rebillPrice) * (rebillRate1 / 100) * (month2Retention / 100);
  const totalFees = feesInitial + feesM1 + feesM2;

  // ── Profit calc ────────────────────────────────────────────────────────
  const grossMargin = ltv - totalCogs - totalFees;
  const netProfitPerCustomer = grossMargin - cpa;
  const cpaMaxBreakEven = grossMargin;
  const cpaFor20Margin = grossMargin * 0.8;
  const cpaFor30Margin = grossMargin * 0.7;

  const ltvCacRatio = cpa > 0 ? ltv / cpa : 0;

  const Input = ({ label, value, onChange, suffix, step = 0.01 }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type="number" step={step} value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          style={{
            width: '100%', padding: '10px 12px', background: G.card, border: `1px solid ${G.border}`,
            borderRadius: 8, color: G.white, fontFamily: 'DM Mono, monospace', fontSize: 14,
          }}
        />
        {suffix && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: G.muted, fontSize: 12, fontFamily: 'DM Mono, monospace' }}>{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div>
      {/* Intro */}
      <div style={{ background: `${G.gold}10`, border: `1px solid ${G.gold}30`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: G.gold, fontWeight: 700, marginBottom: 6 }}>¿Es tu negocio rentable?</div>
        <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.5 }}>
          Calculadora conservadora de 2 meses. Los defaults se calculan sobre una ventana móvil — por defecto <strong>últimos 3 días</strong> — para reflejar tu fase de escalado actual, no datos antiguos. Puedes cambiar la ventana abajo.
        </div>
      </div>

      {/* Window selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Ventana de datos:</div>
        {[1, 3, 7, 14, 30].map(d => (
          <button
            key={d}
            onClick={() => { setUserEdited(false); setWindowDays(d); }}
            style={{
              padding: '6px 14px',
              background: windowDays === d ? G.gold : G.card,
              color: windowDays === d ? G.black : G.white,
              border: `1px solid ${windowDays === d ? G.gold : G.border}`,
              borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              fontFamily: 'DM Mono, monospace',
            }}
          >
            {d === 1 ? 'Hoy' : `${d}d`}
          </button>
        ))}
        {liveDefaults && !loadingDefaults && (
          <div style={{ fontSize: 11, color: G.dim, marginLeft: 8 }}>
            {liveDefaults.orders} pedidos · {liveDefaults.rebillSuccess}/{liveDefaults.rebillAttempts} rebills · ${liveDefaults.adSpend.toFixed(0)} ads
          </div>
        )}
        {userEdited && (
          <button
            onClick={resetToDefaults}
            style={{
              marginLeft: 'auto', padding: '6px 12px', background: 'transparent',
              color: G.gold, border: `1px solid ${G.gold}40`, borderRadius: 6,
              cursor: 'pointer', fontSize: 11,
            }}
          >
            ↻ Resetear a datos reales
          </button>
        )}
      </div>

      {/* Inputs grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Input label="AOV inicial" value={aov} onChange={wrapSetter(setAov)} suffix="$" />
        <Input label="Precio rebill" value={rebillPrice} onChange={wrapSetter(setRebillPrice)} suffix="$" />
        <Input label="% rebill mes 1" value={rebillRate1} onChange={wrapSetter(setRebillRate1)} suffix="%" />
        <Input label="% retención mes 2" value={month2Retention} onChange={wrapSetter(setMonth2Retention)} suffix="%" />
        <Input label="COGS producto" value={cogs} onChange={wrapSetter(setCogs)} suffix="$" />
        <Input label="CPA (lo que pagas)" value={cpa} onChange={wrapSetter(setCpa)} suffix="$" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, fontSize: 13, color: G.muted }}>
        <input type="checkbox" checked={cogsOnRebill} onChange={(e) => setCogsOnRebill(e.target.checked)} id="cor" />
        <label htmlFor="cor">Cobrar COGS también en rebills (si envías producto físico otra vez)</label>
      </div>

      {/* LTV breakdown */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>LTV — Lo que ganas por cliente en 2 meses</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Mono, monospace' }}>
          <tbody>
            <tr style={{ borderBottom: `1px solid ${G.border}22` }}>
              <td style={{ padding: '10px 0', color: G.dim, fontSize: 12 }}>Mes 0 (checkout inicial)</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.white, fontSize: 13 }}>${aov.toFixed(2)} × 100%</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.gold, fontSize: 14, fontWeight: 700, width: 120 }}>${aov.toFixed(2)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${G.border}22` }}>
              <td style={{ padding: '10px 0', color: G.dim, fontSize: 12 }}>Mes 1 (rebill)</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.white, fontSize: 13 }}>${rebillPrice.toFixed(2)} × {rebillRate1}%</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.gold, fontSize: 14, fontWeight: 700 }}>${rebillRevM1.toFixed(2)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${G.border}22` }}>
              <td style={{ padding: '10px 0', color: G.dim, fontSize: 12 }}>Mes 2 (rebill)</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.white, fontSize: 13 }}>${rebillPrice.toFixed(2)} × {rebillRate1}% × {month2Retention}%</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.gold, fontSize: 14, fontWeight: 700 }}>${rebillRevM2.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ padding: '14px 0 4px', color: G.white, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>LTV total (2 meses)</td>
              <td style={{ padding: '14px 0 4px', textAlign: 'right', color: G.green, fontSize: 20, fontWeight: 700 }}>${ltv.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Costs breakdown */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Costes por cliente (sin contar CPA)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Mono, monospace' }}>
          <tbody>
            <tr style={{ borderBottom: `1px solid ${G.border}22` }}>
              <td style={{ padding: '10px 0', color: G.dim, fontSize: 12 }}>COGS + envío</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.red, fontSize: 14, fontWeight: 700, width: 120 }}>-${totalCogs.toFixed(2)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${G.border}22` }}>
              <td style={{ padding: '10px 0', color: G.dim, fontSize: 12 }}>Fees pasarela ({feePct}% + ${feeFlat})</td>
              <td style={{ padding: '10px 0', textAlign: 'right', color: G.red, fontSize: 14, fontWeight: 700 }}>-${totalFees.toFixed(2)}</td>
            </tr>
            <tr>
              <td style={{ padding: '14px 0 4px', color: G.white, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Margen bruto (LTV − costes)</td>
              <td style={{ padding: '14px 0 4px', textAlign: 'right', color: G.green, fontSize: 20, fontWeight: 700 }}>${grossMargin.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Final verdict */}
      <div style={{
        background: netProfitPerCustomer >= 0 ? `${G.green}12` : `${G.red}12`,
        border: `2px solid ${netProfitPerCustomer >= 0 ? G.green : G.red}`,
        borderRadius: 12, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 12, color: netProfitPerCustomer >= 0 ? G.green : G.red, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>
          Veredicto con CPA de ${cpa.toFixed(2)}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontFamily: 'DM Mono, monospace' }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: netProfitPerCustomer >= 0 ? G.green : G.red }}>
            {netProfitPerCustomer >= 0 ? '+' : ''}${netProfitPerCustomer.toFixed(2)}
          </div>
          <div style={{ fontSize: 13, color: G.dim }}>profit por cliente</div>
        </div>
        <div style={{ fontSize: 13, color: G.white, marginTop: 10 }}>
          Ratio LTV / CAC: <span style={{ fontWeight: 700, color: ltvCacRatio >= 3 ? G.green : ltvCacRatio >= 1 ? G.gold : G.red }}>{ltvCacRatio.toFixed(2)}x</span>
          <span style={{ color: G.dim, fontSize: 12, marginLeft: 6 }}>
            {ltvCacRatio >= 3 ? '(excelente)' : ltvCacRatio >= 1 ? '(justo — puedes mejorar)' : '(mal — pierdes dinero)'}
          </span>
        </div>
      </div>

      {/* CPA recommendations */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 20 }}>
        <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>CPA máximo recomendado</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div style={{ padding: 14, background: G.surface, borderRadius: 8, border: `1px solid ${G.border}` }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Break-even</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: G.white, fontFamily: 'DM Mono, monospace' }}>${cpaMaxBreakEven.toFixed(2)}</div>
            <div style={{ fontSize: 11, color: G.dim }}>Ni ganas ni pierdes</div>
          </div>
          <div style={{ padding: 14, background: G.surface, borderRadius: 8, border: `1px solid ${G.gold}40` }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>20% margen</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: G.gold, fontFamily: 'DM Mono, monospace' }}>${cpaFor20Margin.toFixed(2)}</div>
            <div style={{ fontSize: 11, color: G.dim }}>Profit saludable</div>
          </div>
          <div style={{ padding: 14, background: G.surface, borderRadius: 8, border: `1px solid ${G.green}40` }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>30% margen</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: G.green, fontFamily: 'DM Mono, monospace' }}>${cpaFor30Margin.toFixed(2)}</div>
            <div style={{ fontSize: 11, color: G.dim }}>Objetivo óptimo</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Forecast tab ──────────────────────────────────────────────────────
// Backend returns numeric percents (e.g. rebillRate=85.50 for 85.5%) and
// deterministic projected-* values. We derive margin/POAS client-side
// because the backend intentionally leaves rich ratios to the consumer.
function BeneficiosForecast({ forecast, summary, rebill }) {
  if (!forecast) return <div style={{ color: G.muted, padding: 40, textAlign: 'center' }}>Sin datos de forecast.</div>;
  const f = forecast;
  const hz = f.horizonDays || 30;
  const projectedNewCustomers = (f.avgDailySignups || 0) * hz;
  const projectedMarginPct = f.projectedRevenue > 0 ? (f.projectedProfit / f.projectedRevenue * 100) : 0;
  // Trend arrows: compare 3d avg vs 30d avg
  const trend = (v3d, v30d) => {
    if (!v30d || !v3d) return '';
    const pct = ((v3d - v30d) / v30d * 100);
    if (Math.abs(pct) < 3) return ' →';
    return pct > 0 ? ` ↑${Math.round(pct)}%` : ` ↓${Math.abs(Math.round(pct))}%`;
  };
  const nc = f.projectedNewCustomers || Math.round(projectedNewCustomers);
  const subsPerCust = f.subsPerCustomer || 1;
  const newSubs = f.projectedNewSubs || (nc * subsPerCust);
  // Rebill operates in SUB units. In A+B every customer has 2 subs, so
  // 620 clientes → 1240 subs, and the rebill formula shows "1240 subs × rate%"
  // with per-sub amount as the unit. The amount is either the operator's
  // Config override OR the derived avgPricePerSub.
  const pricePerSub = f.avgPricePerSub != null
    ? f.avgPricePerSub
    : (f.avgSubAmount || 0) / (subsPerCust || 1);
  const firstRebillAmt = f.firstRebillAmount != null ? f.firstRebillAmount : pricePerSub;
  const secondRebillAmt = f.secondRebillAmount != null ? f.secondRebillAmount : firstRebillAmt;
  const combinedRebillTicket = f.avgSubAmount || (pricePerSub * subsPerCust);
  const firstRebillSubs = f.projectedFirstRebillSubs != null
    ? f.projectedFirstRebillSubs
    : Math.round(newSubs * (f.rebillRate || 0) / 100);
  const secondRebillSubs = f.projectedSecondRebillSubs != null
    ? f.projectedSecondRebillSubs
    : Math.round(firstRebillSubs * (f.secondRebillRate || 0) / 100);
  const firstRebillRev = f.projectedFirstRebillRevenue || 0;
  const secondRebillRev = f.projectedSecondRebillRevenue || 0;
  // Diagnostic chip: explain where the customer count came from so the
  // operator can spot data-layer problems (missing customerId on subs, etc.)
  const dbg = f._debug || {};
  const sourceLabel = {
    'checkout-emails': 'contados por email de checkout',
    'checkout-count': 'contados por nº de checkouts',
    'subs/2 (A+B fallback)': 'fallback: subs ÷ 2 (modelo A+B)',
    'subs (legacy)': 'fallback: subs (sin datos de checkout)',
  }[dbg.signupSource] || dbg.signupSource || '';
  return (<>
    <div style={{ fontSize: 12, color: G.muted, marginBottom: 12, padding: '8px 12px', background: G.card, borderRadius: 8, border: `1px solid ${G.border}` }}>
      📊 Proyección <b>próximos {hz} días</b> basada en ritmo de últimos 3 días.
      {sourceLabel && (
        <span style={{ marginLeft: 10, color: G.dim, fontSize: 11 }}>
          · clientes <b style={{ color: G.gold }}>{sourceLabel}</b>
          {dbg.uniqueCheckoutEmails30d !== undefined && dbg.subs30dCount !== undefined && (
            <span> ({dbg.uniqueCheckoutEmails30d} checkouts únicos en 30d vs {dbg.subs30dCount} subs → <b style={{ color: G.gold }}>{dbg.subsPerCustomer || 1} sub{(dbg.subsPerCustomer || 1) > 1 ? 's' : ''} por cliente</b>)</span>
          )}
        </span>
      )}
    </div>
    {/* ── Formulas visibles ── */}
    <div style={{ fontSize: 11, color: G.dim, marginBottom: 4, fontFamily: 'DM Mono, monospace' }}>
      NUEVAS VENTAS: {nc} clientes × {fmtEUR(f.checkoutTicket || 0)} = {fmtEUR(f.projectedNewSales || 0)}
    </div>
    <div style={{ fontSize: 11, color: G.dim, marginBottom: 4, fontFamily: 'DM Mono, monospace' }}>
      REBILL 1: {newSubs} subs × {fmtPct(f.rebillRate || 0)} = {firstRebillSubs} cobros × {fmtEUR(firstRebillAmt)} = {fmtEUR(firstRebillRev)}
    </div>
    <div style={{ fontSize: 11, color: G.dim, marginBottom: 14, fontFamily: 'DM Mono, monospace' }}>
      REBILL 2: {firstRebillSubs} subs × {fmtPct(f.secondRebillRate || 0)} = {secondRebillSubs} cobros × {fmtEUR(secondRebillAmt)} = {fmtEUR(secondRebillRev)}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
      <ForecastCard label={`Nuevos clientes (${hz}d)`}
        value={String(nc)}
        sub={`${(f.avgDailySignups || 0).toFixed(1)}/día × ${hz}d${trend(f.avgDailySignups, f.avgDailySignups30d)}`}
        color={G.gold} />
      <ForecastCard label={`Nuevas subs (${hz}d)`}
        value={String(newSubs)}
        sub={subsPerCust > 1 ? `${nc} clientes × ${subsPerCust} subs (A+B)` : `${nc} clientes × 1 sub`}
        color={G.gold} />
      <ForecastCard label="Ticket checkout"
        value={fmtEUR(f.checkoutTicket || 0)}
        sub={`30d: ${fmtEUR(f.checkoutTicket30d || 0)}${trend(f.checkoutTicket, f.checkoutTicket30d)}`}
        color={G.gold} />
      <ForecastCard label="Importe Rebill 1"
        value={fmtEUR(firstRebillAmt)}
        sub={f.firstRebillAmountSource === 'manual'
          ? '(manual, desde Config)'
          : (subsPerCust > 1 ? `auto · A+B por cliente: ${fmtEUR(combinedRebillTicket)}` : 'auto · por sub')}
        color={G.gold} />
      <ForecastCard label="Importe Rebill 2"
        value={fmtEUR(secondRebillAmt)}
        sub={f.secondRebillAmountSource === 'manual' ? '(manual, desde Config)' : 'auto · hereda del Rebill 1'}
        color={G.gold} />
      <ForecastCard label="Rebill 1 cobros"
        value={`${firstRebillSubs} de ${newSubs}`}
        sub={`${fmtPct(f.rebillRate || 0)} ${f.rebillRateSource === 'manual' ? '(manual)' : '(auto 3d)'}`}
        color={G.white} />
      <ForecastCard label="Rebill 2 cobros"
        value={`${secondRebillSubs} de ${firstRebillSubs}`}
        sub={`${fmtPct(f.secondRebillRate || 0)} ${f.secondRebillRateSource === 'manual' ? '(manual)' : '(auto)'}`}
        color={G.white} />
      <ForecastCard label="Revenue nuevas ventas"
        value={fmtEUR(f.projectedNewSales || 0)}
        sub={`${nc} clientes × ${fmtEUR(f.checkoutTicket || 0)}`}
        color={G.white} />
      <ForecastCard label="Revenue rebill 1"
        value={fmtEUR(firstRebillRev)}
        sub={`${firstRebillSubs} subs × ${fmtEUR(firstRebillAmt)}`}
        color={G.white} />
      <ForecastCard label="Revenue rebill 2"
        value={fmtEUR(secondRebillRev)}
        sub={`${secondRebillSubs} subs × ${fmtEUR(secondRebillAmt)}`}
        color={G.white} />
      <ForecastCard label="Revenue total"
        value={fmtEUR(f.projectedRevenue)}
        sub={`Ventas + Rebill 1 + Rebill 2`}
        color={G.white} />
      <ForecastCard label="Ad spend proyectado"
        value={fmtEUR(f.projectedAdSpend)}
        sub={`${fmtEUR(f.avgDailyAdSpend || 0)}/día × ${hz}d${trend(f.avgDailyAdSpend, f.avgDailyAdSpend30d)}`}
        color="#8A7BD8" />
      <ForecastCard label="Fees + COGS"
        value={fmtEUR((f.projectedFees || 0) + (f.projectedCogs || 0))}
        sub={`Fees ${fmtPct(f.realFeeRate || 0)} + COGS ${fmtPct(f.realCogsRate || 0)}`}
        color={G.muted} />
      <ForecastCard label="Profit proyectado"
        value={fmtEUR(f.projectedProfit)}
        sub={`Margen ${fmtPct(projectedMarginPct)} · ±${f.confidenceInterval || 10}%`}
        color={f.projectedProfit >= 0 ? G.green : G.red} big />
    </div>
  </>);
}

function ForecastCard({ label, value, sub, color, big }) {
  return (
    <div style={{ background: G.card, border: `1px solid ${big ? color : G.border}`, borderRadius: 10, padding: 18, boxShadow: big ? `0 0 20px ${color}20` : 'none' }}>
      <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: big ? 32 : 24, fontWeight: 700, color, fontFamily: 'DM Mono, monospace', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: G.dim, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

// ── Config tab ────────────────────────────────────────────────────────
function BeneficiosConfig({ config, setConfig, showToast }) {
  // null/undefined → auto (use 3d computed average)
  // number          → manual (that exact rate as a percent, e.g. 60 = 60%)
  const cfgRebill = config?.rebillRateOverride;
  const cfgSecond = config?.secondRebillRateOverride;
  const cfgRebillAmt = config?.rebillAmountOverride;
  const cfgSecondAmt = config?.secondRebillAmountOverride;
  const [form, setForm] = useState({
    timezone: config?.timezone || 'America/New_York',
    rebill1Auto: cfgRebill === null || cfgRebill === undefined,
    rebill1Manual: typeof cfgRebill === 'number' ? String(cfgRebill) : '',
    rebill2Auto: cfgSecond === null || cfgSecond === undefined,
    rebill2Manual: typeof cfgSecond === 'number' ? String(cfgSecond) : '',
    // Amount overrides (USD per sub on rebill day). null/undef = auto (from Settings/data)
    rebill1AmtAuto: cfgRebillAmt === null || cfgRebillAmt === undefined,
    rebill1AmtManual: typeof cfgRebillAmt === 'number' ? String(cfgRebillAmt) : '',
    rebill2AmtAuto: cfgSecondAmt === null || cfgSecondAmt === undefined,
    rebill2AmtManual: typeof cfgSecondAmt === 'number' ? String(cfgSecondAmt) : '',
    shopDefaults: config?.shopDefaults || {},
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await beneficiosApi.updateConfig({
        timezone: form.timezone,
        // Auto toggle → send null (backend will compute from last 3 days).
        // Manual → send the number the operator typed.
        rebillRateOverride: form.rebill1Auto
          ? null
          : (form.rebill1Manual === '' ? null : parseFloat(form.rebill1Manual)),
        secondRebillRateOverride: form.rebill2Auto
          ? null
          : (form.rebill2Manual === '' ? null : parseFloat(form.rebill2Manual)),
        rebillAmountOverride: form.rebill1AmtAuto
          ? null
          : (form.rebill1AmtManual === '' ? null : parseFloat(form.rebill1AmtManual)),
        secondRebillAmountOverride: form.rebill2AmtAuto
          ? null
          : (form.rebill2AmtManual === '' ? null : parseFloat(form.rebill2AmtManual)),
        shopDefaults: form.shopDefaults,
      });
      setConfig(saved);
      showToast('Configuración guardada', 'success');
    } catch (e) {
      showToast('Error: ' + (e.response?.data?.error || e.message), 'error');
    }
    setSaving(false);
  };

  const toggleBoxStyle = (checked) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    border: `1px solid ${checked ? G.gold : G.border}`,
    borderRadius: 6,
    background: checked ? G.gold + '12' : 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    color: checked ? G.gold : G.muted,
    marginBottom: 6,
    userSelect: 'none',
  });

  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, maxWidth: 640 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: G.gold, marginBottom: 16 }}>Configuración Beneficios</div>
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Zona horaria</label>
        <select className="input" value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} style={{ width: '100%', padding: '8px 10px', marginTop: 4 }}>
          {/* NY first — default for ad-account aligned bookkeeping */}
          <option value="America/New_York">America/New_York (ET) — recomendado</option>
          <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
          <option value="Europe/Madrid">Europe/Madrid (CET/CEST)</option>
          <option value="Europe/London">Europe/London (GMT/BST)</option>
          <option value="Europe/Berlin">Europe/Berlin</option>
          <option value="UTC">UTC</option>
        </select>
        <div style={{ fontSize: 11, color: G.dim, marginTop: 4 }}>
          Solo afecta al módulo Beneficios. Los cálculos bucketan fechas según esta zona.
        </div>
      </div>

      {/* ── Rebill Mes 1 ── */}
      <div style={{ marginTop: 20, padding: 14, border: `1px solid ${G.border}`, borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: G.white, marginBottom: 8 }}>
          Rebill Mes 1 (primera renovación)
        </div>
        {/* Rate */}
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tasa de éxito (%)</div>
        <label style={toggleBoxStyle(form.rebill1Auto)}>
          <input
            type="checkbox"
            checked={form.rebill1Auto}
            onChange={e => setForm({ ...form, rebill1Auto: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span>Usar media automática de los últimos 3 días</span>
        </label>
        <Field
          label="O establecer % manualmente (0-100)"
          type="number" step="0.1"
          value={form.rebill1Manual}
          onChange={v => setForm({ ...form, rebill1Manual: v, rebill1Auto: false })}
          placeholder="Ej. 60"
        />
        <div style={{ fontSize: 11, color: G.dim, marginTop: -6 }}>
          {form.rebill1Auto
            ? '✓ Se usa la media de los últimos 3 días (cálculo automático).'
            : (form.rebill1Manual ? `Se usará ${form.rebill1Manual}% fijo.` : 'Introduce un número o activa la casilla.')}
        </div>
        {/* Amount */}
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16, marginBottom: 4 }}>Importe por cobro ($ por sub)</div>
        <label style={toggleBoxStyle(form.rebill1AmtAuto)}>
          <input
            type="checkbox"
            checked={form.rebill1AmtAuto}
            onChange={e => setForm({ ...form, rebill1AmtAuto: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span>Usar importe automático (precio por sub de tus datos)</span>
        </label>
        <Field
          label="O establecer importe fijo en USD ($)"
          type="number" step="0.01"
          value={form.rebill1AmtManual}
          onChange={v => setForm({ ...form, rebill1AmtManual: v, rebill1AmtAuto: false })}
          placeholder="Ej. 90"
        />
        <div style={{ fontSize: 11, color: G.dim, marginTop: -6 }}>
          {form.rebill1AmtAuto
            ? '✓ Se usa el precio calculado automáticamente (avg por sub en A+B).'
            : (form.rebill1AmtManual ? `Se cobrarán $${form.rebill1AmtManual} por cada cobro (sub).` : 'Introduce un importe o activa la casilla.')}
        </div>
      </div>

      {/* ── Rebill Mes 2 ── */}
      <div style={{ marginTop: 14, padding: 14, border: `1px solid ${G.border}`, borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: G.white, marginBottom: 8 }}>
          Rebill Mes 2 (segunda renovación, ~35 días después de la 1ª)
        </div>
        {/* Rate */}
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tasa de éxito (%)</div>
        <label style={toggleBoxStyle(form.rebill2Auto)}>
          <input
            type="checkbox"
            checked={form.rebill2Auto}
            onChange={e => setForm({ ...form, rebill2Auto: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span>Usar media automática de los últimos 3 días</span>
        </label>
        <Field
          label="O establecer % manualmente (0-100)"
          type="number" step="0.1"
          value={form.rebill2Manual}
          onChange={v => setForm({ ...form, rebill2Manual: v, rebill2Auto: false })}
          placeholder="Ej. 40"
        />
        <div style={{ fontSize: 11, color: G.dim, marginTop: -6 }}>
          {form.rebill2Auto
            ? '✓ Se usa la media de los últimos 3 días (el 2º rebill hereda la tasa del 1º).'
            : (form.rebill2Manual ? `Se usará ${form.rebill2Manual}% fijo.` : 'Introduce un número o activa la casilla.')}
        </div>
        {/* Amount */}
        <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16, marginBottom: 4 }}>Importe por cobro ($ por sub)</div>
        <label style={toggleBoxStyle(form.rebill2AmtAuto)}>
          <input
            type="checkbox"
            checked={form.rebill2AmtAuto}
            onChange={e => setForm({ ...form, rebill2AmtAuto: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span>Usar el mismo importe que el Rebill Mes 1</span>
        </label>
        <Field
          label="O establecer importe fijo distinto en USD ($)"
          type="number" step="0.01"
          value={form.rebill2AmtManual}
          onChange={v => setForm({ ...form, rebill2AmtManual: v, rebill2AmtAuto: false })}
          placeholder="Ej. 90"
        />
        <div style={{ fontSize: 11, color: G.dim, marginTop: -6 }}>
          {form.rebill2AmtAuto
            ? '✓ El 2º rebill hereda el importe del 1º.'
            : (form.rebill2AmtManual ? `Se cobrarán $${form.rebill2AmtManual} por cada cobro (sub) del 2º rebill.` : 'Introduce un importe o activa la casilla.')}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar configuración'}</button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DISPLAY CURRENCY TOGGLE — compact EUR/USD switch in the topbar.
// Persists the preference server-side via PUT /api/auth/me/display-currency,
// updates localStorage, then reloads so every component re-renders with the
// new store value (no need for a Context listener on every money component).
// -----------------------------------------------------------------------------

// NYClock — compact, mobile-friendly live clock in America/New_York.
//
// Kept deliberately minimal (icon + HH:mm, NY label hidden on small screens)
// so it takes almost no topbar real estate on phones. The tooltip still
// carries the full context (date + timezone reason) for anyone who hovers.
function NYClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Align ticks to the next wall-clock minute so the display stays accurate
    // without wasting updates mid-second. Then continue at 60s intervals.
    let iv;
    const msToNextMinute = 60000 - (Date.now() % 60000);
    const t0 = setTimeout(() => {
      setNow(new Date());
      iv = setInterval(() => setNow(new Date()), 60000);
    }, msToNextMinute);
    return () => {
      clearTimeout(t0);
      if (iv) clearInterval(iv);
    };
  }, []);

  const timeStr = now.toLocaleTimeString('en-US', {
    timeZone: APP_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const dateStr = now.toLocaleDateString('en-US', {
    timeZone: APP_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  const meta = tzMeta(APP_TZ);
  return (
    <div
      className="ny-clock"
      title={`Hora en ${meta.city} (${dateStr}) — tu zona horaria del panel`}
    >
      <span className="ny-clock-icon">{meta.flag}</span>
      <span className="ny-clock-time">{timeStr}</span>
      <span className="ny-clock-label">{meta.city}</span>
    </div>
  );
}

function DisplayCurrencyToggle({ user, showToast }) {
  const [saving, setSaving] = useState(false);
  const current = user?.displayCurrency || 'EUR';

  const toggle = async () => {
    if (saving) return;
    const next = current === 'EUR' ? 'USD' : 'EUR';
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/auth/me/display-currency`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayCurrency: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Update localStorage user so after reload the new preference is live
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        u.displayCurrency = next;
        localStorage.setItem('user', JSON.stringify(u));
      } catch { /* noop */ }
      showToast && showToast(`Visualización cambiada a ${next}. Recargando…`, 'success');
      // Hard reload so every money string flips at once
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      setSaving(false);
      showToast && showToast(`No se pudo cambiar la moneda (${e.message})`, 'error');
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={saving}
      title="Cambiar visualización EUR / USD (no afecta a los cobros)"
      style={{
        fontSize: 11,
        fontFamily: 'DM Mono, monospace',
        padding: '6px 12px',
        background: G.card,
        color: G.gold,
        borderRadius: 8,
        border: `1px solid ${G.borderGold}`,
        cursor: saving ? 'wait' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        opacity: saving ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1 }}>{current === 'USD' ? '$' : '€'}</span>
      <span style={{ fontWeight: 600 }}>{current}</span>
      <span style={{ color: G.muted, fontSize: 10 }}>⇄</span>
    </button>
  );
}

// =============================================================================
// BUMPS & UPSELLS PANEL (merchant dashboard → Configuración → Bumps & Upsells)
// =============================================================================
// Editor for two distinct offer types:
//   • orderBumps         — checkbox addons shown IN the checkout, before pay.
//                          Added to the same charge as the original order
//                          (no vault reuse needed). MVP / themes render
//                          them via window.__zrpCheckoutConfig.orderBumps.
//   • postPurchaseUpsells — one-click offers shown on the success page
//                          AFTER a successful payment. Reuses the vault
//                          from the original payment so the customer
//                          doesn't re-enter card data.
function BumpsAndUpsellsPanel({ showToast }) {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('bumps');
  const [editing, setEditing] = useState(null);
  const reload = () => {
    const token = localStorage.getItem('token');
    return fetch(`${BACKEND}/api/setup/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setSettings);
  };
  useEffect(() => { reload(); }, []);
  const persist = async (patch) => {
    const token = localStorage.getItem('token');
    const next = { ...settings, ...patch };
    const r = await fetch(`${BACKEND}/api/setup/settings`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(next),
    });
    if (r.ok) { setSettings(next); return true; }
    showToast && showToast('Error guardando', 'error');
    return false;
  };
  if (!settings) return <div style={{ padding: 24, color: G.muted }}>Cargando…</div>;
  const key = tab === 'bumps' ? 'orderBumps' : 'postPurchaseUpsells';
  const list = settings[key] || [];
  const save = async (data) => {
    let next;
    if (data.id && list.some(x => x.id === data.id)) {
      next = list.map(x => x.id === data.id ? data : x);
    } else {
      const id = data.id || ('bump_' + Math.random().toString(36).slice(2, 10));
      next = [...list, { ...data, id }];
    }
    const ok = await persist({ [key]: next });
    if (ok) { setEditing(null); showToast && showToast('✓ Guardado', 'success'); }
  };
  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este item?')) return;
    const ok = await persist({ [key]: list.filter(x => x.id !== id) });
    if (ok) showToast && showToast('Eliminado', 'success');
  };
  const toggle = async (id, on) => {
    const ok = await persist({ [key]: list.map(x => x.id === id ? { ...x, active: on } : x) });
    if (!ok) return;
  };
  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: G.white }}>Bumps & Upsells</h2>
        <p style={{ color: G.muted, fontSize: 13, marginTop: 4, maxWidth: 760 }}>
          Sube el ticket medio. Los <strong>Order bumps</strong> aparecen en el checkout como checkbox opcional — se cobran junto al pedido.
          Los <strong>Post-purchase upsells</strong> son ofertas one-click que aparecen tras el pago exitoso usando el vault de la tarjeta — el cliente no vuelve a introducir datos.
        </p>
      </div>
      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          { id: 'bumps', label: '🛒 Order bumps (in-checkout)' },
          { id: 'upsells', label: '🎁 Post-purchase upsells (one-click)' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${tab === t.id ? G.gold : G.border}`,
            background: tab === t.id ? G.gold : 'transparent',
            color: tab === t.id ? G.black : G.white,
            fontWeight: 700, fontSize: 12,
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => setEditing({})} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 800, cursor: 'pointer', fontSize: 13 }}>
          + Nuevo {tab === 'bumps' ? 'bump' : 'upsell'}
        </button>
      </div>

      {list.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: G.card, border: `1px dashed ${G.border}`, borderRadius: 12, color: G.muted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{tab === 'bumps' ? '🛒' : '🎁'}</div>
          <div style={{ fontSize: 14, marginBottom: 8, color: G.white }}>
            {tab === 'bumps' ? 'Sin order bumps configurados' : 'Sin upsells post-compra configurados'}
          </div>
          <div style={{ fontSize: 12 }}>
            {tab === 'bumps'
              ? 'Crea uno para que aparezca como checkbox en el checkout y suba el ticket medio.'
              : 'Crea uno para mostrarlo en la página de éxito y subir el AOV con una compra adicional.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
          {list.map(b => (
            <div key={b.id} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 64, height: 64, borderRadius: 8, background: G.bg, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {b.imageUrl ? <img src={b.imageUrl} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ fontSize: 22, color: G.muted }}>📦</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: G.white, marginBottom: 2 }}>{b.title}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: G.green }}>{(b.currency || 'EUR') === 'EUR' ? '€' : '$'}{parseFloat(b.price || 0).toFixed(2)}</div>
                  {b.sku && <div style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>{b.sku}</div>}
                </div>
              </div>
              {b.description && <div style={{ fontSize: 12, color: G.muted, marginTop: 10, lineHeight: 1.5 }}>{b.description}</div>}
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: G.white, cursor: 'pointer' }}>
                  <input type="checkbox" checked={b.active !== false} onChange={e => toggle(b.id, e.target.checked)} />
                  Activo
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditing({ ...b })} style={{ background: 'transparent', color: G.white, border: `1px solid ${G.border}`, borderRadius: 5, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Editar</button>
                  <button onClick={() => remove(b.id)} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <BumpEditor item={editing} kind={tab} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function BumpEditor({ item, kind, onSave, onClose }) {
  const [form, setForm] = useState({
    title: item.title || '',
    description: item.description || '',
    imageUrl: item.imageUrl || '',
    price: item.price ?? '',
    currency: item.currency || 'EUR',
    sku: item.sku || '',
    active: item.active !== false,
    id: item.id,
  });
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const uploadImg = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { return; }
    const reader = new FileReader();
    reader.onload = () => set({ imageUrl: reader.result });
    reader.readAsDataURL(file);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.price) return;
    onSave({ ...form, price: parseFloat(form.price) });
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, color: G.white }}>{form.id ? `Editar ${kind === 'bumps' ? 'bump' : 'upsell'}` : `Nuevo ${kind === 'bumps' ? 'order bump' : 'upsell post-compra'}`}</h3>
        <div>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Imagen</label>
          <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ width: 80, height: 80, borderRadius: 8, background: G.card, border: `1px dashed ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {form.imageUrl ? <img src={form.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 22, color: G.muted }}>📦</span>}
            </div>
            <input type="file" accept="image/*" onChange={e => uploadImg(e.target.files?.[0])} style={{ color: G.white, fontSize: 12 }} />
            {form.imageUrl && <button type="button" onClick={() => set({ imageUrl: '' })} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>Quitar</button>}
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Título</label>
          <input required value={form.title} onChange={e => set({ title: e.target.value })} placeholder="Garantía extendida 1 año" style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Descripción</label>
          <textarea value={form.description} onChange={e => set({ description: e.target.value })} rows={3} placeholder="Cobertura ante cualquier fallo, devolución de dinero garantizada." style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Precio</label>
            <input required type="number" step="0.01" min="0.01" value={form.price} onChange={e => set({ price: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Moneda</label>
            <select value={form.currency} onChange={e => set({ currency: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }}>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>SKU (opcional)</label>
            <input value={form.sku} onChange={e => set({ sku: e.target.value })} placeholder="EXT-WARRANTY-12M" style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4, fontFamily: 'DM Mono, monospace' }} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, color: G.white, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} />
          Activo (visible en el checkout)
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" onClick={onClose} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>Cancelar</button>
          <button type="submit" style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, cursor: 'pointer' }}>{form.id ? 'Guardar' : 'Crear'}</button>
        </div>
      </form>
    </div>
  );
}

// =============================================================================
// COUPONS PANEL (merchant dashboard → Configuración → Cupones)
// =============================================================================
// CRUD over /api/coupons. The checkout reads valid coupons via the public
// POST /api/coupons/validate route (passes userId + amount → discount).
function CouponsPanel({ showToast }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = none, {} = new, {id...} = edit
  const reload = () => {
    const token = localStorage.getItem('token');
    return fetch(`${BACKEND}/api/coupons`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setCoupons)
      .catch(() => setCoupons([]));
  };
  useEffect(() => { reload().finally(() => setLoading(false)); }, []);
  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${BACKEND}/api/coupons/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { showToast && showToast('Cupón eliminado', 'success'); reload(); }
    else showToast && showToast('Error al eliminar', 'error');
  };
  const save = async (data) => {
    const token = localStorage.getItem('token');
    const isNew = !data.id;
    const url = `${BACKEND}/api/coupons${isNew ? '' : '/' + data.id}`;
    const r = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    const body = await r.json();
    if (!r.ok) { showToast && showToast(body.error || 'Error', 'error'); return; }
    showToast && showToast(isNew ? '✓ Cupón creado' : '✓ Cupón actualizado', 'success');
    setEditing(null);
    reload();
  };
  if (loading) return <div style={{ padding: 24, color: G.muted }}>Cargando…</div>;
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: G.white }}>Cupones de descuento</h2>
          <p style={{ color: G.muted, fontSize: 13, marginTop: 4, maxWidth: 660 }}>
            Códigos que tus clientes introducen en el checkout para obtener descuento. Funciona con todos los checkouts (MVP + 7 themes), todos los procesadores y todos los idiomas.
          </p>
        </div>
        <button onClick={() => setEditing({})} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 800, cursor: 'pointer', fontSize: 13 }}>+ Crear cupón</button>
      </div>
      {coupons.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: G.card, border: `1px dashed ${G.border}`, borderRadius: 12, color: G.muted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🎟</div>
          <div style={{ fontSize: 14, marginBottom: 8, color: G.white }}>Aún no tienes cupones</div>
          <div style={{ fontSize: 12 }}>Crea uno para regalar % de descuento, € fijos, o ofertas con fecha de caducidad.</div>
        </div>
      ) : (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: G.muted, fontSize: 11, textAlign: 'left', borderBottom: `1px solid ${G.border}` }}>
                <th style={{ padding: 10 }}>Código</th>
                <th>Descuento</th>
                <th>Usos</th>
                <th>Min. compra</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => {
                const isExpired = c.validUntil && new Date(c.validUntil).getTime() < Date.now();
                const isExhausted = c.maxUses > 0 && c.usedCount >= c.maxUses;
                return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 10, fontFamily: 'DM Mono, monospace', fontWeight: 700, color: G.gold }}>{c.code}</td>
                    <td>{c.kind === 'percent' ? `${c.value}%` : `${c.value}€`}</td>
                    <td>{c.usedCount}{c.maxUses > 0 ? ` / ${c.maxUses}` : ' / ∞'}</td>
                    <td>{c.minAmount > 0 ? `${c.minAmount}€` : '—'}</td>
                    <td style={{ fontSize: 11 }}>
                      {c.validFrom ? `desde ${new Date(c.validFrom).toLocaleDateString()}` : '—'}
                      {c.validUntil ? ` hasta ${new Date(c.validUntil).toLocaleDateString()}` : ''}
                    </td>
                    <td>
                      {!c.active && <span style={{ color: G.muted, fontSize: 11 }}>○ Desactivado</span>}
                      {c.active && isExpired && <span style={{ color: G.red, fontSize: 11 }}>● Caducado</span>}
                      {c.active && !isExpired && isExhausted && <span style={{ color: G.orange, fontSize: 11 }}>● Agotado</span>}
                      {c.active && !isExpired && !isExhausted && <span style={{ color: G.green, fontSize: 11 }}>● Activo</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button onClick={() => setEditing({ ...c })} style={{ background: 'transparent', color: G.white, border: `1px solid ${G.border}`, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginRight: 4 }}>Editar</button>
                      <button onClick={() => remove(c.id)} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && <CouponEditor coupon={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CouponEditor({ coupon, onSave, onClose }) {
  const [form, setForm] = useState({
    code: coupon.code || '',
    kind: coupon.kind || 'percent',
    value: coupon.value ?? '',
    currency: coupon.currency || 'EUR',
    minAmount: coupon.minAmount ?? 0,
    maxUses: coupon.maxUses ?? 0,
    validFrom: coupon.validFrom ? coupon.validFrom.slice(0, 10) : '',
    validUntil: coupon.validUntil ? coupon.validUntil.slice(0, 10) : '',
    active: coupon.active !== false,
    id: coupon.id,
  });
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.code || !form.value) return;
    onSave({
      ...form,
      value: parseFloat(form.value),
      minAmount: parseFloat(form.minAmount) || 0,
      maxUses: parseInt(form.maxUses) || 0,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
    });
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 12, padding: 24, width: '100%', maxWidth: 560 }}>
        <h3 style={{ marginTop: 0, color: G.white }}>{form.id ? 'Editar cupón' : 'Nuevo cupón'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Código</label>
            <input required value={form.code} onChange={e => set({ code: e.target.value.toUpperCase() })} placeholder="VERANO20" style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4, fontFamily: 'DM Mono, monospace', textTransform: 'uppercase' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Tipo</label>
            <select value={form.kind} onChange={e => set({ kind: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }}>
              <option value="percent">% Porcentaje</option>
              <option value="fixed">€ Fijo</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Valor</label>
            <input required type="number" step="0.01" min="0" value={form.value} onChange={e => set({ value: e.target.value })} placeholder={form.kind === 'percent' ? '20' : '10.00'} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Min. compra €</label>
            <input type="number" step="0.01" min="0" value={form.minAmount} onChange={e => set({ minAmount: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Max. usos</label>
            <input type="number" min="0" value={form.maxUses} onChange={e => set({ maxUses: e.target.value })} placeholder="0 = ilimitado" style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Válido desde</label>
            <input type="date" value={form.validFrom} onChange={e => set({ validFrom: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Válido hasta</label>
            <input type="date" value={form.validUntil} onChange={e => set({ validUntil: e.target.value })} style={{ width: '100%', padding: '8px 10px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 6, marginTop: 4 }} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, color: G.white, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} />
          Cupón activo (los clientes pueden usarlo)
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" onClick={onClose} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>Cancelar</button>
          <button type="submit" style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, cursor: 'pointer' }}>{form.id ? 'Guardar cambios' : 'Crear cupón'}</button>
        </div>
      </form>
    </div>
  );
}

// =============================================================================
// CHECKOUTS GALLERY (merchant dashboard → Configuración → Checkouts)
// =============================================================================
// Lets the merchant pick a checkout template ('mvp' | 'minimal' | 'conversion')
// and tweak the customization knobs that each theme exposes (CTA copy,
// trust badges, countdown, testimonials, colors, guarantee line).
//
// Important invariants:
//   • The 'mvp' template is the original 3462-line checkout.html — Apple
//     Pay + Google Pay + Stripe + Square + Collect.js all live there. It
//     stays the default and is NEVER edited from this UI.
//   • Themes are CSS+JS overlays loaded by the dispatcher at the top of
//     checkout.html (see commit 96b7f22+). Switching is instant — no
//     redeploy needed; the next session-fetch reads the new template.
//   • i18n keeps working: data-i18n hooks are preserved by every theme.
// 2026-06-07 — 8 checkout templates total, plan-gated.
//   • Bronze (2): mvp, minimal
//   • Silver (4): + conversion, premium
//   • Gold   (8): + two-step, express, carousel, mobile-bold
// `plan` is the minimum tier required. Customers below see a locked card
// with an upgrade CTA — they can still preview, just can't activate.
const CHECKOUT_TEMPLATES = [
  {
    id: 'mvp', plan: 'bronze',
    name: 'MVP',
    tagline: 'El checkout original validado',
    description: 'La versión que ya tienes funcionando. Apple Pay, Google Pay, Stripe, Square y Collect.js validados al 100%. Es el default — recomendado para empezar.',
    features: ['Apple Pay + Google Pay', 'Stripe + Square + NMI', 'Multi-idioma (i18n)', 'Mobile + Desktop'],
    swatch: 'linear-gradient(135deg,#7065f0 0%,#2563eb 100%)',
    textOn: '#fff',
  },
  {
    id: 'minimal', plan: 'bronze',
    name: 'Minimal',
    tagline: 'Inspirado en Stripe Checkout',
    description: 'Réplica del look-and-feel de Stripe Checkout: blanco puro, fuente Inter, púrpura #635bff, sombras suaves, columna única 440px. La estética que ha conquistado al mundo SaaS por su simplicidad.',
    features: ['Layout Stripe-style', 'Fuente Inter', 'Apple Pay + Google Pay', 'Sombras y focus rings'],
    swatch: 'linear-gradient(180deg,#ffffff 0%,#f6f9fc 100%)',
    textOn: '#0a2540',
  },
  {
    id: 'conversion', plan: 'silver',
    name: 'Conversion',
    tagline: 'Inspirado en ClickFunnels',
    description: 'DR sales-funnel agresivo: countdown rojo sticky, botón amarillo gigante con sombra hard, bordes negros marcados, badges de scarcity, testimonios con estrellas. Sube conversión por urgencia y prueba social.',
    features: ['Countdown sticky', 'CTA amarillo bold', 'Trust badges urgentes', 'Bordes hard-shadow'],
    swatch: 'linear-gradient(135deg,#fbbf24 0%,#dc2626 100%)',
    textOn: '#1a1a1a',
  },
  {
    id: 'premium', plan: 'silver',
    name: 'Premium',
    tagline: 'Inspirado en Apple Pay',
    description: 'Fondo negro puro, tipografía serif Cormorant Garamond para titulares, inputs sin caja (underline únicamente), CTA cápsula blanco con hover oro. Para productos de lujo, alto ticket o membresías premium.',
    features: ['Negro absoluto + serif', 'Inputs underline-only', 'CTA cápsula 28px radius', 'Acentos #c9a96e oro'],
    swatch: 'linear-gradient(135deg,#000000 0%,#c9a96e 100%)',
    textOn: '#fff',
  },
  {
    id: 'two-step', plan: 'gold',
    name: 'Two-step',
    tagline: 'Inspirado en Shopify Shop Pay',
    description: 'Estética Shop Pay: fondo lavanda, pills de progreso 1→2 con check verde, card blanca con sombra, púrpura #5a31f4, fuente Plus Jakarta Sans. Email primero (capturado) → resto después.',
    features: ['Fondo lavanda Shop Pay', 'Pills de progreso animadas', 'Captura email parcial', 'Plus Jakarta Sans'],
    swatch: 'linear-gradient(135deg,#f4f1fe 0%,#5a31f4 100%)',
    textOn: '#fff',
  },
  {
    id: 'express', plan: 'gold',
    name: 'Express',
    tagline: 'Inspirado en Amazon One-Click',
    description: 'Réplica visual de Amazon: card blanca minimalista, navy text, sección de wallets con badge naranja "COMPRA EN 1 CLICK", botón amarillo Amazon #FFD814 con gradiente y focus naranja. Total en rojo #B12704.',
    features: ['Amazon yellow CTA #FFD814', 'Wallet section naranja', 'Fuente Open Sans', 'Total en rojo'],
    swatch: 'linear-gradient(135deg,#FFD814 0%,#FF9900 100%)',
    textOn: '#0F1111',
  },
  {
    id: 'carousel', plan: 'gold',
    name: 'Carousel',
    tagline: 'Inspirado en Booking.com / Airbnb',
    description: 'Card Airbnb-style con productos en carousel scroll arriba, badge "TU SELECCIÓN" coral, CTA coral #ff385c gradiente. Layout limpio y profesional con bordes 16px, ideal para hotelería, viajes y productos visualmente atractivos.',
    features: ['Cards Airbnb-style', 'Carousel productos', 'CTA coral #ff385c', 'Bordes 16px rounded'],
    swatch: 'linear-gradient(135deg,#ff385c 0%,#bd1e59 100%)',
    textOn: '#fff',
  },
  {
    id: 'mobile-bold', plan: 'gold',
    name: 'Mobile Bold',
    tagline: 'Inspirado en Cash App / Venmo',
    description: 'Fondo verde Cash App #00d54b a pantalla completa, type Outfit weight 900, inputs con bordes 4px negros y sombra hard 6px, CTA con texto verde sobre fondo negro. Ultra mobile-first, gritón, Gen-Z friendly.',
    features: ['Verde brand full-bleed', 'Bordes 4px hard-shadow', 'Type 56px massive', 'CTA invertido negro/verde'],
    swatch: 'linear-gradient(180deg,#00d54b 0%,#001a08 100%)',
    textOn: '#fff',
  },
];

const PLAN_RANK_FOR_CHECKOUT = { bronze: 1, silver: 2, gold: 3, admin: 99 };

function CheckoutPreviewModal({ templateId, customization, onClose }) {
  // The dispatcher in checkout.html honours #zrpPreview= before doing the
  // session fetch, so we don't actually need a real session for the
  // visual preview. We try to create one in the background (for a more
  // accurate render including cart items), but we never block on it.
  const [demoSessionId, setDemoSessionId] = useState(() => sessionStorage.getItem('zrp_demo_session') || 'preview-' + Math.random().toString(36).slice(2, 10));
  const [device, setDevice] = useState('desktop');

  useEffect(() => {
    if (sessionStorage.getItem('zrp_demo_session')) return;
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/checkout/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        amount: 4999,
        currency: 'EUR',
        customerEmail: 'demo@example.com',
        metadata: { source: 'checkout-gallery-preview' },
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(s => {
        if (s?.sessionId || s?.id) {
          const sid = s.sessionId || s.id;
          sessionStorage.setItem('zrp_demo_session', sid);
          setDemoSessionId(sid);
        }
      })
      .catch(() => { /* dispatcher hash mode covers the visual preview */ });
  }, []);

  // Always pass the merchant's tweaks through the hash so the iframe
  // applies them instantly — independent of any real-session state.
  const previewParams = encodeURIComponent(JSON.stringify({ template: templateId, ...customization }));
  const checkoutOrigin = window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://app.zeroriskpay.com';
  const iframeUrl = `${checkoutOrigin}/checkout.html?session=${encodeURIComponent(demoSessionId)}#zrpPreview=${previewParams}`;

  const widths = { mobile: 390, tablet: 768, desktop: 1100 };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 14, width: '100%', maxWidth: 1200, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: G.muted, letterSpacing: 1.2, textTransform: 'uppercase' }}>Vista previa</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: G.white }}>{CHECKOUT_TEMPLATES.find(t => t.id === templateId)?.name || templateId}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {['mobile', 'tablet', 'desktop'].map(d => (
              <button key={d} onClick={() => setDevice(d)}
                style={{
                  padding: '5px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  border: `1px solid ${device === d ? G.gold : G.border}`,
                  background: device === d ? G.gold : 'transparent',
                  color: device === d ? G.black : G.muted,
                  borderRadius: 6, cursor: 'pointer',
                }}>
                {d === 'mobile' ? '📱 Mobile' : d === 'tablet' ? '▢ Tablet' : '🖥 Desktop'}
              </button>
            ))}
            <button onClick={onClose} style={{ marginLeft: 8, background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>✕ Cerrar</button>
          </div>
        </div>
        <div style={{ flex: 1, padding: 18, overflow: 'auto', background: '#1a1a22', display: 'flex', justifyContent: 'center' }}>
          <iframe
            title="checkout-preview"
            src={iframeUrl}
            style={{
              width: '100%', maxWidth: widths[device], minHeight: '70vh',
              background: '#fff', border: 'none', borderRadius: 10,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              transition: 'max-width 0.25s',
            }} />
        </div>
      </div>
    </div>
  );
}

function CheckoutCustomizationPanel({ customization, onChange }) {
  const set = (patch) => onChange({ ...customization, ...patch });
  const setTesti = (idx, patch) => {
    const list = [...(customization.testimonials || [])];
    list[idx] = { ...list[idx], ...patch };
    set({ testimonials: list });
  };
  const addTesti = () => set({ testimonials: [...(customization.testimonials || []), { name: '', country: '', text: '', rating: 5 }] });
  const rmTesti = (idx) => {
    const list = [...(customization.testimonials || [])];
    list.splice(idx, 1);
    set({ testimonials: list });
  };
  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18 }}>
      <h3 style={{ marginTop: 0, color: G.white, fontSize: 15 }}>Personalización</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Color primario (CTA)</label>
          <input type="color" value={customization.primaryColor || '#7065f0'} onChange={e => set({ primaryColor: e.target.value })} style={{ width: '100%', height: 38, border: `1px solid ${G.border}`, borderRadius: 6, background: 'transparent', marginTop: 4 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Color de acento (focus)</label>
          <input type="color" value={customization.accentColor || '#2563eb'} onChange={e => set({ accentColor: e.target.value })} style={{ width: '100%', height: 38, border: `1px solid ${G.border}`, borderRadius: 6, background: 'transparent', marginTop: 4 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Texto del botón (CTA)</label>
          <input type="text" placeholder="Pagar ahora" value={customization.ctaText || ''} onChange={e => set({ ctaText: e.target.value })} style={{ width: '100%', height: 38, padding: '6px 10px', border: `1px solid ${G.border}`, borderRadius: 6, background: G.bg, color: G.white, marginTop: 4 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Línea de garantía</label>
          <input type="text" placeholder="Garantía de devolución 30 días" value={customization.guaranteeText || ''} onChange={e => set({ guaranteeText: e.target.value })} style={{ width: '100%', height: 38, padding: '6px 10px', border: `1px solid ${G.border}`, borderRadius: 6, background: G.bg, color: G.white, marginTop: 4 }} />
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: G.white, cursor: 'pointer' }}>
          <input type="checkbox" checked={customization.showTrustBadges !== false} onChange={e => set({ showTrustBadges: e.target.checked })} />
          Mostrar badges de confianza
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: G.white, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!customization.showCountdown} onChange={e => set({ showCountdown: e.target.checked })} />
          Mostrar countdown
        </label>
        {customization.showCountdown && (
          <div>
            <label style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>Minutos del countdown</label>
            <input type="number" min={1} max={120} value={customization.countdownMinutes || 15} onChange={e => set({ countdownMinutes: +e.target.value || 15 })} style={{ width: '100%', height: 38, padding: '6px 10px', border: `1px solid ${G.border}`, borderRadius: 6, background: G.bg, color: G.white, marginTop: 4 }} />
          </div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: G.white, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!customization.showTestimonials} onChange={e => set({ showTestimonials: e.target.checked })} />
          Mostrar testimonios
        </label>
      </div>
      {customization.showTestimonials && (
        <div style={{ marginTop: 14, padding: 14, background: G.bg, borderRadius: 8, border: `1px solid ${G.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ color: G.white, fontSize: 13 }}>Testimonios ({(customization.testimonials || []).length})</strong>
            <button onClick={addTesti} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 5, padding: '4px 12px', fontWeight: 700, cursor: 'pointer', fontSize: 11 }}>+ Añadir</button>
          </div>
          {(customization.testimonials || []).map((t, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto auto', gap: 6, marginBottom: 6 }}>
              <input value={t.name || ''} onChange={e => setTesti(idx, { name: e.target.value })} placeholder="Nombre" style={{ padding: '5px 8px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }} />
              <input value={t.country || ''} onChange={e => setTesti(idx, { country: e.target.value })} placeholder="País" style={{ padding: '5px 8px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }} />
              <input value={t.text || ''} onChange={e => setTesti(idx, { text: e.target.value })} placeholder="Texto del testimonio" style={{ padding: '5px 8px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }} />
              <select value={t.rating || 5} onChange={e => setTesti(idx, { rating: +e.target.value })} style={{ padding: '5px 8px', background: G.card, border: `1px solid ${G.border}`, color: G.white, borderRadius: 4, fontSize: 12 }}>
                {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r}★</option>)}
              </select>
              <button onClick={() => rmTesti(idx)} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}50`, borderRadius: 4, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          {(customization.testimonials || []).length === 0 && <div style={{ color: G.dim, fontSize: 12 }}>Añade testimonios reales para el theme Conversion (máx. 4 visibles).</div>}
        </div>
      )}
    </div>
  );
}

function CheckoutsGallery({ showToast }) {
  const [active, setActive] = useState('mvp');
  const [userPlan, setUserPlan] = useState('bronze');
  const [isAdmin, setIsAdmin] = useState(false);
  const [customization, setCustomization] = useState({
    primaryColor: '#7065f0',
    accentColor: '#2563eb',
    showTrustBadges: true,
    showCountdown: false,
    countdownMinutes: 15,
    showTestimonials: false,
    testimonials: [],
    ctaText: '',
    guaranteeText: '',
  });
  const [previewId, setPreviewId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load current selection from backend settings on mount.
  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${BACKEND}/api/setup/settings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${BACKEND}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([s, me]) => {
      const c = s?.checkout || {};
      setActive(c.template || 'mvp');
      setCustomization({
        primaryColor: c.primaryColor || '#7065f0',
        accentColor: c.accentColor || '#2563eb',
        showTrustBadges: c.showTrustBadges !== false,
        showCountdown: !!c.showCountdown,
        countdownMinutes: c.countdownMinutes || 15,
        showTestimonials: !!c.showTestimonials,
        testimonials: Array.isArray(c.testimonials) ? c.testimonials : [],
        ctaText: c.ctaText || '',
        guaranteeText: c.guaranteeText || '',
      });
      setUserPlan((me?.plan || 'bronze').toLowerCase());
      setIsAdmin(me?.role === 'admin');
    }).finally(() => setLoading(false));
  }, []);

  const save = async (templateId) => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const cur = await fetch(`${BACKEND}/api/setup/settings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      const merged = { ...cur, checkout: { ...(cur.checkout || {}), ...customization, template: templateId } };
      const r = await fetch(`${BACKEND}/api/setup/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(merged),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setActive(templateId);
      showToast && showToast(`✓ Checkout "${CHECKOUT_TEMPLATES.find(t => t.id === templateId)?.name}" activado`, 'success');
    } catch (e) {
      showToast && showToast(`Error guardando: ${e.message}`, 'error');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 24, color: G.muted }}>Cargando…</div>;

  const planRank = PLAN_RANK_FOR_CHECKOUT[userPlan] || 1;
  const isUnlocked = (tpl) => isAdmin || (PLAN_RANK_FOR_CHECKOUT[tpl.plan] || 99) <= planRank;
  const counts = CHECKOUT_TEMPLATES.reduce((acc, t) => {
    acc[t.plan] = (acc[t.plan] || 0) + 1;
    return acc;
  }, {});
  const visibleForPlan = (plan) => {
    const r = PLAN_RANK_FOR_CHECKOUT[plan] || 99;
    return CHECKOUT_TEMPLATES.filter(t => (PLAN_RANK_FOR_CHECKOUT[t.plan] || 99) <= r).length;
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: G.white }}>Checkouts disponibles</h2>
        <p style={{ color: G.muted, fontSize: 13, marginTop: 4, maxWidth: 760 }}>
          Elige el diseño del checkout que verán tus clientes. Todos comparten <strong>Apple Pay + Google Pay + Stripe + Square + Collect.js</strong> — solo cambia la capa visual.
          Los idiomas (es / en / fr / de / it / pt) y los pixels (Meta / TikTok / GA) funcionan igual en todos.
        </p>
        {/* Plan summary strip */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          {['bronze', 'silver', 'gold'].map(plan => {
            const isUser = userPlan === plan;
            return (
              <div key={plan} style={{
                padding: '8px 14px', borderRadius: 8,
                background: isUser ? `${G.gold}18` : G.card,
                border: `1px solid ${isUser ? G.gold : G.border}`,
                fontSize: 12, color: G.white, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 14 }}>{plan === 'bronze' ? '🥉' : plan === 'silver' ? '🥈' : '🥇'}</span>
                <strong style={{ textTransform: 'capitalize' }}>{plan}</strong>
                <span style={{ color: G.muted }}>· {visibleForPlan(plan)} checkouts</span>
                {isUser && <span style={{ fontSize: 10, color: G.gold, fontWeight: 700, marginLeft: 4 }}>TU PLAN</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 16, marginBottom: 28 }}>
        {CHECKOUT_TEMPLATES.map(t => {
          const isActive = active === t.id;
          const unlocked = isUnlocked(t);
          const planEmoji = t.plan === 'bronze' ? '🥉' : t.plan === 'silver' ? '🥈' : '🥇';
          return (
            <div key={t.id} style={{
              background: G.card,
              border: `2px solid ${isActive ? G.gold : G.border}`,
              borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              opacity: unlocked ? 1 : 0.72,
              position: 'relative',
            }}>
              <div style={{ height: 120, background: t.swatch, position: 'relative', display: 'flex', alignItems: 'flex-end', padding: 14 }}>
                {isActive && (
                  <span style={{ position: 'absolute', top: 10, right: 10, background: G.gold, color: G.black, fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 4, letterSpacing: 0.5 }}>ACTIVO</span>
                )}
                {!unlocked && (
                  <span style={{ position: 'absolute', top: 10, right: 10, background: '#000000c0', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 4 }}>
                    🔒 {t.plan.toUpperCase()}
                  </span>
                )}
                <span style={{ position: 'absolute', top: 10, left: 10, background: '#0008', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {planEmoji} {t.plan}
                </span>
                <div style={{ fontSize: 22, fontWeight: 800, color: t.textOn || '#fff', textShadow: (t.textOn === '#111' || t.id === 'minimal') ? 'none' : '0 1px 6px rgba(0,0,0,0.35)' }}>{t.name}</div>
              </div>
              <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 12, color: G.muted, marginBottom: 8 }}>{t.tagline}</div>
                <div style={{ fontSize: 13, color: G.white, marginBottom: 12, lineHeight: 1.5 }}>{t.description}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', flex: 1 }}>
                  {t.features.map(f => (
                    <li key={f} style={{ fontSize: 12, color: G.dim, marginBottom: 4, display: 'flex', gap: 6 }}>
                      <span style={{ color: G.green }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setPreviewId(t.id)} style={{ flex: 1, background: 'transparent', color: G.white, border: `1px solid ${G.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    👁 Previsualizar
                  </button>
                  {unlocked ? (
                    <button disabled={saving || isActive} onClick={() => save(t.id)} style={{ flex: 1, background: isActive ? G.bg : G.gold, color: isActive ? G.muted : G.black, border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 800, cursor: isActive ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                      {isActive ? 'Activo' : 'Activar'}
                    </button>
                  ) : (
                    <button onClick={() => showToast && showToast(`Sube a plan ${t.plan} para activar este checkout`, 'info')} style={{ flex: 1, background: G.bg, color: G.gold, border: `1px dashed ${G.gold}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      🔒 Subir a {t.plan}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CheckoutCustomizationPanel customization={customization} onChange={setCustomization} />
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => save(active)} disabled={saving} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 800, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Guardando…' : '💾 Guardar personalización'}
        </button>
      </div>

      {previewId && (
        <CheckoutPreviewModal
          templateId={previewId}
          customization={customization}
          onClose={() => setPreviewId(null)} />
      )}
    </div>
  );
}

// =============================================================================
// SUPER-ADMIN PANEL (admin.zeroriskpay.com)
// =============================================================================
// Cross-tenant view for the SaaS operator. Lists every merchant in the
// system, their plan, gateways, products, revenue, MRR, recent payments
// and subscriptions. Reads /api/admin/* — backed by routes/admin.js which
// requires both the auth + adminOnly middleware. Non-admins landing on
// this host get a 403 page so the URL surface is contained.
// -----------------------------------------------------------------------------
// 2026-06-07 — Wrapper around fetch that retries up to 3 times with
// exponential backoff on TypeError "Failed to fetch". This is the
// signature of a Cloudflare cold-start where the first request arrives
// before DNS / TLS / Render warm-up finishes. The endpoint itself works
// instantly on retry — the cost is at most ~600ms on a cold panel load.
async function fetchAdminWithRetry(url, opts) {
  const delays = [0, 300, 800, 1500];
  let lastErr;
  for (let i = 0; i < delays.length; i++) {
    if (delays[i]) await new Promise(r => setTimeout(r, delays[i]));
    try {
      const r = await fetch(url, opts);
      return r;
    } catch (e) {
      lastErr = e;
      // Only retry the transient network error. Other failures bubble.
      if (e && (e.name === 'TypeError' || /failed to fetch/i.test(e.message || ''))) continue;
      throw e;
    }
  }
  throw lastErr;
}

// Tiny inline SVG line chart used in the platform "Evolución" tab.
function AdminLineChart({ points, height = 180, color = '#f5c842', label = '' }) {
  if (!points || !points.length) return <div style={{ color: G.dim, padding: 14 }}>Sin datos</div>;
  const w = 600;
  const h = height;
  const padX = 20, padY = 18;
  const max = Math.max(...points.map(p => p.value), 1);
  const dx = (w - 2 * padX) / Math.max(1, points.length - 1);
  const sy = (v) => h - padY - (v / max) * (h - 2 * padY);
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${padX + i * dx} ${sy(p.value)}`).join(' ');
  const area = `${path} L${padX + (points.length - 1) * dx} ${h - padY} L${padX} ${h - padY} Z`;
  const ticks = 4;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = (max / ticks) * i;
        const y = sy(v);
        return (
          <g key={i}>
            <line x1={padX} y1={y} x2={w - padX} y2={y} stroke={G.border} strokeWidth="0.5" />
            <text x={padX - 4} y={y + 3} fill={G.dim} fontSize="9" textAnchor="end">{v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}</text>
          </g>
        );
      })}
      <path d={area} fill={color} opacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={padX + i * dx} cy={sy(p.value)} r="2.5" fill={color}>
          <title>{p.date}: {p.value}</title>
        </circle>
      ))}
      {label && <text x={padX} y="12" fill={G.muted} fontSize="10">{label}</text>}
    </svg>
  );
}

function MiniSparkline({ data, color = '#f5c842', height = 24 }) {
  if (!data || !data.length) return null;
  const w = 80;
  const max = Math.max(...data.map(d => d.value), 1);
  const dx = w / Math.max(1, data.length - 1);
  const path = data.map((d, i) => `${i ? 'L' : 'M'}${i * dx} ${height - (d.value / max) * (height - 2) - 1}`).join(' ');
  const allZero = data.every(d => !d.value);
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ width: '100%', maxWidth: 80, height }}>
      {allZero
        ? <line x1="0" y1={height / 2} x2={w} y2={height / 2} stroke={G.border} strokeWidth="1" strokeDasharray="2 2" />
        : <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

function fmtMoney(n, sym = '€') { return `${sym}${Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtPctAdmin(n) { return n === null || n === undefined ? '—' : `${Number(n).toFixed(2)}%`; }
function flagFromCode(code) {
  if (!code || code.length !== 2) return '🏳️';
  const A = 0x1F1E6;
  const offset = (c) => A + c.toUpperCase().charCodeAt(0) - 65;
  try { return String.fromCodePoint(offset(code[0]), offset(code[1])); } catch { return '🏳️'; }
}

function AdminPlatformPanel() {
  const [data, setData] = useState(null);
  const [saas, setSaas] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('resumen');

  useEffect(() => {
    const token = localStorage.getItem('token');
    // F51b (2026-06-13) — auto-recovery: si recibimos 401 borramos el token
    // y recargamos. El navegador del operator tenía un token stale aunque
    // hubiera "logout/login" manualmente. Esto fuerza relogin limpio.
    const handleAuthFail = (status) => {
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
        return true;
      }
      return false;
    };
    fetchAdminWithRetry(`${BACKEND}/api/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) {
          if (handleAuthFail(r.status)) return null;
          return Promise.reject(r.status);
        }
        return r.json();
      })
      .then(d => d && setData(d))
      .catch(e => setErr(`Error ${e}`));
    fetchAdminWithRetry(`${BACKEND}/api/admin/saas-revenue`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setSaas)
      .catch(() => {});
  }, []);

  if (err) return <div style={{ padding: 24, color: G.red }}>No se pudo cargar la plataforma: {err}</div>;
  if (!data) return <div style={{ padding: 24, color: G.muted }}>Cargando métricas globales…</div>;

  const card = (label, value, sub, accent) => (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 10, letterSpacing: 1.4, color: G.muted, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || G.white, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: G.dim, marginTop: 3 }}>{sub}</div>}
    </div>
  );

  const tabs = [
    { id: 'resumen',    label: '📊 Resumen' },
    { id: 'evolucion',  label: '📈 Evolución 30d' },
    { id: 'pagos',      label: '💳 Pagos recientes' },
    { id: 'geografia',  label: '🌍 Geografía' },
    { id: 'gateways',   label: '⬡ Pasarelas' },
    { id: 'productos',  label: '📦 Top productos' },
    { id: 'alertas',    label: `⚠ Alertas${data.alerts?.length ? ' (' + data.alerts.length + ')' : ''}` },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0, marginBottom: 14, color: G.white }}>Plataforma — visión global</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20, borderBottom: `1px solid ${G.border}`, paddingBottom: 10 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: tab === t.id ? G.gold : 'transparent',
            color: tab === t.id ? G.black : G.white,
            border: `1px solid ${tab === t.id ? G.gold : G.border}`,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'resumen' && (
        <>
          {/* Top KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 18 }}>
            {card('MRR', fmtMoney(data.revenue.mrr), `ARR run-rate ${fmtMoney(data.revenue.arrRunRate)}`, G.gold)}
            {card('Revenue HOY',    fmtMoney(data.revenue.today),   `${data.payments.succeededToday || 0} pagos`)}
            {card('Revenue 7d',     fmtMoney(data.revenue.last7d),  `${data.payments.succeeded7d} pagos`)}
            {card('Revenue 30d',    fmtMoney(data.revenue.last30d), `${data.payments.succeeded30d} pagos`)}
            {card('Revenue total',  fmtMoney(data.revenue.lifetime),`${data.payments.succeededTotal} lifetime`)}
            {card('AOV',            fmtMoney(data.revenue.aov), 'Ticket medio')}
            {card('Clientes únicos',data.payments.uniqueCustomers || 0, 'Emails únicos pagados')}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 18 }}>
            {card('Tenants',   data.tenants.total, `+${data.tenants.new30d} en 30d`)}
            {saas && card('MRR SaaS (mes actual)', `€${saas.mrr.current.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, `Mes pasado: €${saas.mrr.lastMonth.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, '#C9A84C')}
            {saas && card('Setup recibido (total)', `€${saas.totals.setup.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, `${saas.counts.activePayingThisMonth} pagando este mes`)}
            {saas && card('Upsells (total)', `€${saas.totals.upsell.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, `Total recibido: €${saas.totals.all.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`)}
            {card('Subs activas', data.subscriptions.activeTotal, `${data.subscriptions.trialTotal || 0} en trial`)}
            {card('Conversión 30d', fmtPct(data.rates.conversion30d), `${data.infrastructure.checkoutSessions30d || 0} checkouts`)}
            {card('Refund 30d', fmtPct(data.rates.refund30d), `${data.payments.refundedTotal} totales`)}
            {card('Chargeback 30d', fmtPct(data.rates.chargeback30d), `${data.payments.chargedbackTotal} totales`, data.rates.chargeback30d > 1 ? G.red : null)}
            {card('Pasarelas activas', `${data.infrastructure.gatewaysActive}/${data.infrastructure.gatewaysTotal}`, 'Cross-tenant')}
            {card('Tiendas', data.infrastructure.shopsTotal, 'Total')}
          </div>

          <h3 style={{ color: G.white }}>Distribución por plan</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            {Object.entries(data.tenants.byPlan).map(([plan, count]) => (
              <div key={plan} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase' }}>{plan}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: G.white }}>{count}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'evolucion' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 10 }}>Revenue diario (€)</div>
            <AdminLineChart points={(data.dailySeries || []).map(d => ({ date: d.date, value: d.revenue }))} color="#f5c842" />
          </div>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 10 }}>Nº pagos diarios</div>
            <AdminLineChart points={(data.dailySeries || []).map(d => ({ date: d.date, value: d.payments }))} color="#4ade80" />
          </div>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 10 }}>Nuevos tenants por día</div>
            <AdminLineChart points={(data.dailySeries || []).map(d => ({ date: d.date, value: d.newTenants }))} color="#60a5fa" />
          </div>
        </div>
      )}

      {tab === 'pagos' && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', borderBottom: `1px solid ${G.border}`, textTransform: 'uppercase' }}>
                <th style={{ padding: 10 }}>Fecha</th>
                <th>Tenant</th>
                <th>Cliente</th>
                <th>Producto</th>
                <th>Gateway</th>
                <th>País</th>
                <th style={{ textAlign: 'right' }}>Importe</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(data.recentPayments || []).map(p => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                  <td style={{ padding: 8, whiteSpace: 'nowrap', fontSize: 11 }}>{p.createdAt ? new Date(p.createdAt).toLocaleString('es-ES') : '—'}</td>
                  <td style={{ fontSize: 11 }}>
                    <div style={{ fontWeight: 700 }}>{p.tenantName || '—'}</div>
                    <div style={{ color: G.muted }}>{p.tenantEmail || ''}</div>
                  </td>
                  <td style={{ fontSize: 11 }}>{p.customerEmail || '—'}</td>
                  <td style={{ fontSize: 11 }}>{p.productName || '—'}</td>
                  <td style={{ fontSize: 11 }}>{p.gateway || '—'}</td>
                  <td style={{ fontSize: 14 }}>{p.country ? flagFromCode(p.country) : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(p.amount)}</td>
                  <td>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase',
                      background: p.status === 'succeeded' ? `${G.green}22` : `${G.red}22`,
                      color: p.status === 'succeeded' ? G.green : G.red,
                    }}>{p.status}</span>
                  </td>
                </tr>
              ))}
              {(!data.recentPayments || data.recentPayments.length === 0) && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: G.muted }}>Sin pagos en el sistema.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'geografia' && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 14 }}>Top 15 países por revenue</div>
          {(data.topCountries || []).length === 0 ? (
            <div style={{ color: G.muted, fontSize: 13 }}>Sin pagos con país registrado todavía.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {data.topCountries.map(c => {
                  const max = Math.max(...data.topCountries.map(x => x.revenue), 1);
                  const pct = (c.revenue / max) * 100;
                  return (
                    <tr key={c.code} style={{ borderBottom: `1px solid ${G.border}30` }}>
                      <td style={{ padding: 8, fontSize: 18 }}>{flagFromCode(c.code)}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: G.muted, fontSize: 12 }}>{c.code}</td>
                      <td style={{ width: '50%' }}>
                        <div style={{ height: 8, background: G.bg, borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #f5c842, #fbbf24)' }} />
                        </div>
                      </td>
                      <td style={{ color: G.white, fontWeight: 700, textAlign: 'right' }}>{fmtMoney(c.revenue)}</td>
                      <td style={{ color: G.muted, fontSize: 11, textAlign: 'right' }}>{c.count} pagos</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'gateways' && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 14 }}>Top pasarelas por volumen</div>
          {(data.topGateways || []).length === 0 ? (
            <div style={{ color: G.muted, fontSize: 13 }}>Sin tráfico procesado todavía.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', textTransform: 'uppercase', borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ padding: 8 }}>Nombre</th>
                  <th style={{ textAlign: 'right' }}>Pagos</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                  <th style={{ textAlign: 'right' }}>AOV</th>
                </tr>
              </thead>
              <tbody>
                {data.topGateways.map(g => (
                  <tr key={g.name} style={{ borderBottom: `1px solid ${G.border}30`, color: G.white }}>
                    <td style={{ padding: 8 }}>{g.name}</td>
                    <td style={{ textAlign: 'right' }}>{g.count}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(g.revenue)}</td>
                    <td style={{ textAlign: 'right', color: G.muted }}>{g.count ? fmtMoney(g.revenue / g.count) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'productos' && (
        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, color: G.muted, textTransform: 'uppercase', marginBottom: 14 }}>Top productos cross-tenant</div>
          {(data.topProducts || []).length === 0 ? (
            <div style={{ color: G.muted, fontSize: 13 }}>Sin ventas todavía.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', textTransform: 'uppercase', borderBottom: `1px solid ${G.border}` }}>
                  <th style={{ padding: 8 }}>Producto</th>
                  <th style={{ textAlign: 'right' }}>Unidades</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.topProducts.map((p, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${G.border}30`, color: G.white }}>
                    <td style={{ padding: 8 }}>{p.name}</td>
                    <td style={{ textAlign: 'right' }}>{p.units}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'alertas' && (
        <div style={{ display: 'grid', gap: 10 }}>
          {(data.alerts || []).length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', background: G.card, border: `1px dashed ${G.border}`, borderRadius: 10, color: G.green }}>
              ✓ Sin alertas activas
            </div>
          ) : (
            data.alerts.map((a, i) => (
              <div key={i} style={{
                background: G.card,
                border: `1px solid ${a.severity === 'critical' ? G.red : a.severity === 'warn' ? G.orange : G.border}`,
                borderLeft: `4px solid ${a.severity === 'critical' ? G.red : a.severity === 'warn' ? G.orange : G.gold}`,
                borderRadius: 8, padding: 14,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 700, color: G.white }}>{a.message}</div>
                  <div style={{ fontSize: 11, color: G.muted, marginTop: 3 }}>Tenant: <strong>{a.tenant}</strong></div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '3px 10px', borderRadius: 4,
                  background: a.severity === 'critical' ? `${G.red}22` : a.severity === 'warn' ? `${G.orange}22` : `${G.gold}22`,
                  color: a.severity === 'critical' ? G.red : a.severity === 'warn' ? G.orange : G.gold,
                }}>{a.severity}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// 2026-06-11 — Sección de facturación SaaS del tenant: pagos manuales
// (monthly / setup / upsell), pausar/reactivar, % fee custom. Cada acción
// queda registrada en saasInvoices (sólo backend admin). Muestra historial.
function TenantSaasBilling({ tenantId, tenant, onTenantChanged }) {
  const [invoices, setInvoices] = useState(null);
  const [totals, setTotals] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // {type:'monthly'|'setup'|'upsell'|'tracking'}
  const [form, setForm] = useState({ amount: '', currency: 'EUR', periodFrom: '', periodTo: '', notes: '' });

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    fetchAdminWithRetry(`${BACKEND}/api/admin/tenants/${tenantId}/saas-invoices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setInvoices(d.invoices); setTotals(d.totals); })
      .catch(e => setErr(`Error ${e}`));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const openModal = (type) => {
    setForm({
      amount: type === 'setup' ? '2500' : type === 'monthly' ? (tenant?.plan === 'gold' ? '3000' : tenant?.plan === 'silver' ? '2000' : '1000') : '',
      currency: 'EUR',
      periodFrom: type === 'monthly' ? new Date().toISOString().slice(0, 7) + '-01' : '',
      periodTo: '',
      notes: '',
    });
    setModal({ type });
  };

  const submitInvoice = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { window.alert('Importe inválido'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/admin/tenants/${tenantId}/saas-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: modal.type, ...form, amount: parseFloat(form.amount) }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      setModal(null);
      load();
    } catch (e) { window.alert(`Error: ${e.message}`); }
    setBusy(false);
  };

  const toggleStatus = async () => {
    const newStatus = tenant?.status === 'paused' ? 'active' : 'paused';
    const verb = newStatus === 'paused' ? 'PAUSAR' : 'REACTIVAR';
    const tenantEmail = tenant?.email || tenantId;
    const txt = window.prompt(`${verb} tenant\n\n${tenantEmail}\n\n${newStatus === 'paused' ? 'Al pausar:\n- Bloquea su acceso INMEDIATO\n- Su MRR deja de contar para el SaaS\n- Datos quedan intactos\n\nEscribe el email para confirmar:' : 'Vas a reactivar este tenant. Volverá a tener acceso completo y a contar en MRR.\n\nEscribe el email para confirmar:'}`);
    if (txt !== tenantEmail) { if (txt !== null) window.alert('Email no coincide. Cancelado.'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/admin/tenants/${tenantId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      onTenantChanged && onTenantChanged();
    } catch (e) { window.alert(`Error: ${e.message}`); }
    setBusy(false);
  };

  const updateCustomFee = async () => {
    const current = tenant?.customTxFeePct ?? '';
    const input = window.prompt(`% fee personalizado para ${tenant?.email}\n\nDeja vacío para usar el del plan.\nActual: ${current === '' ? '(plan default)' : current + '%'}`, current);
    if (input === null) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/admin/tenants/${tenantId}/custom-fee`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customTxFeePct: input.trim() === '' ? null : parseFloat(input) }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      onTenantChanged && onTenantChanged();
    } catch (e) { window.alert(`Error: ${e.message}`); }
    setBusy(false);
  };

  const toggleTracking = async () => {
    const next = !(tenant?.trackingEnabled !== false);
    const ok = window.confirm(
      `${next ? 'ACTIVAR' : 'DESACTIVAR'} tracking para ${tenant?.email}\n\n` +
      (next
        ? 'Los próximos pagos de este cliente generarán tracking code y arrancarán la secuencia de eventos con los settings configurados.'
        : 'Los próximos pagos de este cliente NO generarán tracking code ni eventos.\nLos pagos siguen funcionando con normalidad.\nLos shipments ya existentes NO se tocan.') +
      '\n\nSolo afecta a este tenant.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/admin/tenants/${tenantId}/tracking-enabled`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      onTenantChanged && onTenantChanged();
    } catch (e) { window.alert(`Error: ${e.message}`); }
    setBusy(false);
  };

  // 2026-11-04 — Stripe Payment Element por tenant: el 3DS se confirma en el
  // navegador del cliente y sobrevive al navegador interno de Instagram/Facebook.
  const togglePaymentElement = async () => {
    const next = !(tenant?.paymentElement === true);
    const ok = window.confirm(
      `${next ? 'ACTIVAR' : 'DESACTIVAR'} Payment Element para ${tenant?.email}\n\n` +
      (next
        ? 'Los próximos checkouts con Stripe de este cliente usarán el formulario de pago nuevo de Stripe (3DS en el navegador, menos retos del banco y funciona dentro de Instagram/Facebook).'
        : 'Los próximos checkouts con Stripe de este cliente volverán al formulario de tarjeta de siempre.') +
      '\n\nNo cambia la plantilla del checkout ni las pasarelas que no son Stripe. Solo afecta a este tenant.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/admin/tenants/${tenantId}/payment-element`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
      onTenantChanged && onTenantChanged();
    } catch (e) { window.alert(`Error: ${e.message}`); }
    setBusy(false);
  };

  const paused = tenant?.status === 'paused';
  const trackingOn = tenant?.trackingEnabled !== false;
  const peOn = tenant?.paymentElement === true;
  const peLocked = tenant?.paymentElementForced === true;

  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
        <h3 style={{ color: G.white, margin: 0, fontSize: 15 }}>💼 Facturación SaaS · pagos manuales del tenant</h3>
        <span style={{ color: paused ? G.red : G.green, fontSize: 11, fontWeight: 700 }}>● {paused ? 'PAUSADO' : 'ACTIVO'}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
        <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase' }}>Total mensual</div>
          <div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{totals ? `€${totals.monthly?.toFixed(2) || '0.00'}` : '—'}</div>
        </div>
        <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase' }}>Setup</div>
          <div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{totals ? `€${totals.setup?.toFixed(2) || '0.00'}` : '—'}</div>
        </div>
        <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase' }}>Upsells</div>
          <div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{totals ? `€${totals.upsell?.toFixed(2) || '0.00'}` : '—'}</div>
        </div>
        <div style={{ background: G.black, border: `2px solid ${G.gold}80`, borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 9, color: G.gold, textTransform: 'uppercase', fontWeight: 700 }}>Total recibido</div>
          <div style={{ fontSize: 18, color: G.gold, fontWeight: 700 }}>{totals ? `€${totals.total?.toFixed(2) || '0.00'}` : '—'}</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <button onClick={() => openModal('monthly')} disabled={busy} style={{ background: G.gold, color: '#000', border: 'none', borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>💰 Registrar pago mensual</button>
        <button onClick={() => openModal('setup')} disabled={busy} style={{ background: 'transparent', color: G.gold, border: `1px solid ${G.gold}80`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>🔧 Registrar setup (€2.500)</button>
        <button onClick={() => openModal('upsell')} disabled={busy} style={{ background: 'transparent', color: G.dim, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✨ Registrar upsell / extra</button>
        <button onClick={() => openModal('tracking')} disabled={busy} style={{ background: 'transparent', color: G.dim, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>📦 Registrar tracking</button>
        <button onClick={updateCustomFee} disabled={busy} style={{ background: 'transparent', color: G.dim, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>% Fee custom {tenant?.customTxFeePct != null ? `(${tenant.customTxFeePct}%)` : ''}</button>
        <button onClick={toggleTracking} disabled={busy} title={trackingOn ? 'Apagar tracking SOLO para este tenant' : 'Encender tracking SOLO para este tenant'} style={{ background: trackingOn ? G.green + '20' : G.red + '20', color: trackingOn ? G.green : G.red, border: `1px solid ${trackingOn ? G.green : G.red}60`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{trackingOn ? '📦 Tracking ON' : '🚫 Tracking OFF'}</button>
        <button onClick={togglePaymentElement} disabled={busy || peLocked} title={peLocked ? 'Fijo en el código para esta cuenta' : peOn ? 'Volver al formulario de tarjeta de siempre (solo este tenant)' : 'Cobrar con Stripe Payment Element: 3DS en el navegador, funciona dentro de Instagram/Facebook (solo este tenant)'} style={{ background: peOn ? G.green + '20' : 'transparent', color: peOn ? G.green : G.dim, border: `1px solid ${peOn ? G.green + '60' : G.border}`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: peLocked ? 'default' : 'pointer' }}>{peOn ? '💳 Payment Element ON' : '💳 Payment Element OFF'}</button>
        <button onClick={toggleStatus} disabled={busy} style={{ background: paused ? G.green + '30' : G.red + '20', color: paused ? G.green : G.red, border: `1px solid ${paused ? G.green : G.red}60`, borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>{paused ? '▶ Reactivar tenant' : '⏸ Pausar tenant'}</button>
      </div>

      {err && <div style={{ color: G.red, fontSize: 12 }}>{err}</div>}
      {invoices && invoices.length > 0 && (
        <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${G.border}`, borderRadius: 8 }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: G.black, color: G.muted, fontSize: 10, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Tipo</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Importe</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Periodo</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Fecha</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Notas</th>
            </tr></thead>
            <tbody>
              {invoices.map(i => (
                <tr key={i.id} style={{ borderTop: `1px solid ${G.border}40`, color: G.dim }}>
                  <td style={{ padding: '5px 10px' }}><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: i.type === 'monthly' ? G.gold + '20' : i.type === 'setup' ? G.green + '20' : G.border, color: i.type === 'monthly' ? G.gold : i.type === 'setup' ? G.green : G.dim, fontWeight: 700, textTransform: 'uppercase' }}>{i.type}</span></td>
                  <td style={{ padding: '5px 10px', textAlign: 'right', color: G.white, fontWeight: 600 }}>{i.currency} {i.amount.toFixed(2)}</td>
                  <td style={{ padding: '5px 10px' }}>{i.periodFrom || '—'}{i.periodTo ? ` → ${i.periodTo}` : ''}</td>
                  <td style={{ padding: '5px 10px' }}>{i.paidAt ? new Date(i.paidAt).toLocaleDateString('es-ES') : '—'}</td>
                  <td style={{ padding: '5px 10px' }}>{i.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invoices && invoices.length === 0 && (
        <div style={{ color: G.muted, fontSize: 12 }}>Sin pagos registrados aún. Cuando recibas una transferencia, regístrala con los botones de arriba.</div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 20 }} onClick={() => !busy && setModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 22, maxWidth: 420, width: '100%' }}>
            <h3 style={{ color: G.white, margin: '0 0 14px', fontSize: 16 }}>
              {modal.type === 'monthly' ? '💰 Pago mensual' : modal.type === 'setup' ? '🔧 Setup' : '✨ Upsell'} — {tenant?.email}
            </h3>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Importe (EUR) *</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} step="0.01" style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 10px', color: G.white, boxSizing: 'border-box' }} />
            </div>
            {modal.type === 'monthly' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Periodo desde</label>
                  <input type="date" value={form.periodFrom} onChange={e => setForm(p => ({ ...p, periodFrom: e.target.value }))} style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 10px', color: G.white, boxSizing: 'border-box', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Hasta</label>
                  <input type="date" value={form.periodTo} onChange={e => setForm(p => ({ ...p, periodTo: e.target.value }))} style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 10px', color: G.white, boxSizing: 'border-box', colorScheme: 'dark' }} />
                </div>
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Notas (opcional)</label>
              <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Nº transferencia, referencia, etc." style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '7px 10px', color: G.white, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} disabled={busy} style={{ background: 'transparent', border: `1px solid ${G.border}`, color: G.muted, borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
              <button onClick={submitInvoice} disabled={busy} style={{ background: G.gold, color: '#000', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>{busy ? 'Guardando…' : 'OK — registrar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 2026-06-11 — Sección reutilizable: tracking emitido por un tenant en un
// rango de fechas. Se monta dentro de AdminTenantDrillDown y consulta
// /api/admin/tenants/:id/tracking-usage. Sirve para que el operator sepa
// cuánto facturar a fin de mes ($1/$0.75/$0.50 × nº trackings según plan).
function TenantTrackingUsage({ tenantId }) {
  const toIso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const [from, setFrom] = useState(toIso(monthStart));
  const [to, setTo] = useState(toIso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    setLoading(true); setErr(null);
    fetchAdminWithRetry(`${BACKEND}/api/admin/tenants/${tenantId}/tracking-usage?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(`Error ${e}`))
      .finally(() => setLoading(false));
  }, [tenantId, from, to]);
  useEffect(() => { load(); }, [load]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === 'mtd') {
      setFrom(toIso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))));
      setTo(toIso(now));
    } else if (preset === 'last-month') {
      const lm = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const lastDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      setFrom(toIso(lm)); setTo(toIso(lastDay));
    } else if (preset === '7d') {
      setFrom(toIso(new Date(now.getTime() - 7 * 86400000))); setTo(toIso(now));
    } else if (preset === '30d') {
      setFrom(toIso(new Date(now.getTime() - 30 * 86400000))); setTo(toIso(now));
    } else if (preset === 'ytd') {
      setFrom(toIso(new Date(Date.UTC(now.getUTCFullYear(), 0, 1))));
      setTo(toIso(now));
    }
  };

  const presets = [['mtd', 'Este mes'], ['last-month', 'Mes pasado'], ['7d', '7d'], ['30d', '30d'], ['ytd', 'Año']];

  return (
    <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
        <h3 style={{ color: G.white, margin: 0, fontSize: 15 }}>📦 Trackings emitidos · para facturar</h3>
        {data && <span style={{ color: G.muted, fontSize: 11 }}>Plan {data.tenant.plan.toUpperCase()} → ${data.trackingFee.toFixed(2)}/tracking</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '6px 10px', color: G.white, fontSize: 12, colorScheme: 'dark' }} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '6px 10px', color: G.white, fontSize: 12, colorScheme: 'dark' }} />
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {presets.map(([k, l]) => (
            <button key={k} onClick={() => setPreset(k)} style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '6px 10px', color: G.dim, fontSize: 11, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
      </div>

      {err && <div style={{ color: G.red, fontSize: 12 }}>{err}</div>}
      {loading && <div style={{ color: G.muted, fontSize: 12 }}>Cargando…</div>}

      {data && !loading && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Trackings emitidos</div>
              <div style={{ fontSize: 28, color: G.white, fontWeight: 700, lineHeight: 1.1, marginTop: 4 }}>{data.count}</div>
            </div>
            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Tarifa por tracking</div>
              <div style={{ fontSize: 28, color: G.white, fontWeight: 700, lineHeight: 1.1, marginTop: 4 }}>${data.trackingFee.toFixed(2)}</div>
            </div>
            <div style={{ background: G.black, border: `2px solid ${G.gold}80`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 9, color: G.gold, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>A facturar</div>
              <div style={{ fontSize: 28, color: G.gold, fontWeight: 700, lineHeight: 1.1, marginTop: 4 }}>${data.totalCost.toFixed(2)}</div>
            </div>
          </div>

          {data.items.length === 0 ? (
            <div style={{ color: G.muted, fontSize: 12, padding: '12px 0' }}>Sin trackings en este rango.</div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${G.border}`, borderRadius: 8 }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: G.black, color: G.muted, fontSize: 10, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '7px 12px', textAlign: 'left', letterSpacing: 1 }}>Código</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', letterSpacing: 1 }}>Fecha</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', letterSpacing: 1 }}>Estado</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', letterSpacing: 1 }}>Destino</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(it => (
                    <tr key={it.id} style={{ borderTop: `1px solid ${G.border}40`, color: G.dim }}>
                      <td style={{ padding: '6px 12px', fontFamily: 'DM Mono, monospace', color: G.white }}>{it.trackingCode || it.id.slice(0, 8)}</td>
                      <td style={{ padding: '6px 12px' }}>{it.createdAt ? new Date(it.createdAt).toLocaleDateString('es-ES', { timeZone: APP_TZ }) : '—'}</td>
                      <td style={{ padding: '6px 12px' }}>{it.status || '—'}</td>
                      <td style={{ padding: '6px 12px' }}>{it.destinationCountry || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.count > data.items.length && (
            <div style={{ color: G.muted, fontSize: 11, marginTop: 8 }}>Mostrando {data.items.length} de {data.count}. Acota el rango para ver el resto.</div>
          )}
        </>
      )}
    </div>
  );
}

function AdminTenantDrillDown({ tenantId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [payPage, setPayPage] = useState(0); // paginación de "Pagos recientes" (20/pág)
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetchAdminWithRetry(`${BACKEND}/api/admin/tenants/${tenantId}/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(`Error ${e}`));
  }, [tenantId]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'flex-end', zIndex: 999 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: G.black, borderLeft: `1px solid ${G.border}`, width: 'min(960px, 100%)', height: '100%', overflowY: 'auto', padding: 24 }}>
        <button onClick={onClose} style={{ float: 'right', background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}>✕ Cerrar</button>
        {err && <div style={{ color: G.red }}>Error {err}</div>}
        {!data && !err && <div style={{ color: G.muted }}>Cargando…</div>}
        {data && (
          <>
            <h2 style={{ color: G.white, marginTop: 0 }}>{data.tenant.name} <span style={{ fontSize: 13, color: G.muted, fontWeight: 400 }}>· {data.tenant.email}</span></h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <span style={{ padding: '3px 10px', borderRadius: 4, background: G.card, color: G.gold, fontSize: 11, fontWeight: 700 }}>PLAN {data.tenant.plan.toUpperCase()}</span>
              <span style={{ padding: '3px 10px', borderRadius: 4, background: G.card, color: G.muted, fontSize: 11 }}>Alta: {data.tenant.createdAt ? new Date(data.tenant.createdAt).toLocaleDateString() : '—'}</span>
              <span style={{ padding: '3px 10px', borderRadius: 4, background: G.card, color: G.muted, fontSize: 11 }}>ID: {data.tenant.id}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>MRR</div><div style={{ fontSize: 20, color: G.gold, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.mrr)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Hoy</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.today || 0)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Revenue 7d</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.last7d)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Revenue 30d</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.last30d)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Lifetime</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.lifetime)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>AOV</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.aov || 0)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>LTV</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{fmtMoney(data.tenant.revenue.ltv || 0)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Clientes</div><div style={{ fontSize: 20, color: G.white, fontWeight: 700 }}>{data.tenant.counts.customers || 0}</div></div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Conv. 30d</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.rates?.conversion30d === null || data.tenant.rates?.conversion30d === undefined ? '—' : `${data.tenant.rates.conversion30d}%`}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Refund 30d</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{fmtPct(data.tenant.rates?.refund30d || 0)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Chargeback 30d</div><div style={{ fontSize: 18, color: (data.tenant.rates?.chargeback30d || 0) > 1 ? G.red : G.white, fontWeight: 700 }}>{fmtPct(data.tenant.rates?.chargeback30d || 0)}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Pagos OK</div><div style={{ fontSize: 18, color: G.green, fontWeight: 700 }}>{data.tenant.counts.paymentsSucceeded || 0}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Refunds</div><div style={{ fontSize: 18, color: G.orange, fontWeight: 700 }}>{data.tenant.counts.paymentsRefunded || 0}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Chargebacks</div><div style={{ fontSize: 18, color: G.red, fontWeight: 700 }}>{data.tenant.counts.paymentsChargedback || 0}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Subs activas</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.counts.subscriptionsActive}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Subs trial</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.counts.subscriptionsTrial || 0}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Gateways</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.counts.gateways}/{data.tenant.counts.gatewaysTotal}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Tiendas</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.counts.shops}</div></div>
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 12 }}><div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Último pago</div><div style={{ fontSize: 18, color: G.white, fontWeight: 700 }}>{data.tenant.daysSinceLastPayment === null ? 'Nunca' : `${data.tenant.daysSinceLastPayment}d`}</div></div>
            </div>

            {data.dailyRevenue && data.dailyRevenue.length > 0 && (
              <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <div style={{ fontSize: 11, color: G.muted, textTransform: 'uppercase', marginBottom: 10 }}>Revenue diario (últimos 30 días)</div>
                <AdminLineChart points={data.dailyRevenue.map(d => ({ date: d.date, value: d.revenue }))} color="#f5c842" height={150} />
              </div>
            )}

            <TenantSaasBilling tenantId={tenantId} tenant={data.tenant} onTenantChanged={() => {
              const token = localStorage.getItem('token');
              fetchAdminWithRetry(`${BACKEND}/api/admin/tenants/${tenantId}/overview`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : Promise.reject(r.status))
                .then(setData)
                .catch(e => setErr(`Error ${e}`));
            }} />

            <TenantTrackingUsage tenantId={tenantId} />

            <h3 style={{ color: G.white }}>Pasarelas configuradas</h3>
            {data.tenant.gateways.length === 0 ? <div style={{ color: G.dim, fontSize: 13, marginBottom: 18 }}>Sin pasarelas configuradas.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 13 }}>
                <thead><tr style={{ color: G.muted, fontSize: 11, textAlign: 'left', borderBottom: `1px solid ${G.border}` }}><th style={{ padding: 6 }}>Nombre</th><th>Procesador</th><th>Estado</th></tr></thead>
                <tbody>{data.tenant.gateways.map(g => (
                  <tr key={g.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 6 }}>{g.name}</td><td>{g.processor}</td><td style={{ color: g.active ? G.green : G.red }}>{g.active ? '● Activo' : '○ Inactivo'}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}

            <h3 style={{ color: G.white }}>Top productos (por revenue)</h3>
            {data.topProducts.length === 0 ? <div style={{ color: G.dim, fontSize: 13, marginBottom: 18 }}>Sin ventas todavía.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 13 }}>
                <thead><tr style={{ color: G.muted, fontSize: 11, textAlign: 'left', borderBottom: `1px solid ${G.border}` }}><th style={{ padding: 6 }}>Producto</th><th>Unidades</th><th>Revenue</th></tr></thead>
                <tbody>{data.topProducts.map((p, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 6 }}>{p.name}</td><td>{p.units}</td><td>€{p.revenue.toLocaleString()}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}

            <h3 style={{ color: G.white }}>Pagos recientes</h3>
            {data.recentPayments.length === 0 ? <div style={{ color: G.dim, fontSize: 13, marginBottom: 18 }}>Sin pagos.</div> : (() => {
              const PAGE = 20;
              const total = data.recentPayments.length;
              const pages = Math.ceil(total / PAGE);
              const page = Math.min(payPage, pages - 1);
              const slice = data.recentPayments.slice(page * PAGE, page * PAGE + PAGE);
              const btn = (enabled) => ({ background: G.card, color: enabled ? G.white : G.dim, border: `1px solid ${G.border}`, borderRadius: 4, padding: '4px 10px', cursor: enabled ? 'pointer' : 'default' });
              return (
              <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
                <thead><tr style={{ color: G.muted, fontSize: 11, textAlign: 'left', borderBottom: `1px solid ${G.border}` }}><th style={{ padding: 6 }}>Fecha</th><th>Importe</th><th>Estado</th><th>Gateway</th><th>Cliente</th></tr></thead>
                <tbody>{slice.map(p => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 6 }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                    <td>{curSym(p.currency)}{p.amount.toLocaleString()}</td>
                    <td style={{ color: String(p.status).toUpperCase() === 'SUCCEEDED' ? G.green : String(p.status).toUpperCase() === 'PENDING' ? G.muted : G.red }}>{p.status}</td>
                    <td>{p.gateway || '—'}</td>
                    <td>{p.customerEmail || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, fontSize: 12, color: G.muted }}>
                <span>{page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} de {total} pagos</span>
                {pages > 1 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => setPayPage(Math.max(0, page - 1))} disabled={page === 0} style={btn(page > 0)}>‹ Anterior</button>
                    <span>Pág. {page + 1}/{pages}</span>
                    <button onClick={() => setPayPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1} style={btn(page < pages - 1)}>Siguiente ›</button>
                  </div>
                )}
              </div>
              </>
              );
            })()}

            <h3 style={{ color: G.white }}>Suscripciones</h3>
            {data.subscriptions.length === 0 ? <div style={{ color: G.dim, fontSize: 13 }}>Sin suscripciones.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ color: G.muted, fontSize: 11, textAlign: 'left', borderBottom: `1px solid ${G.border}` }}><th style={{ padding: 6 }}>Cliente</th><th>Importe</th><th>Estado</th><th>Próximo cobro</th></tr></thead>
                <tbody>{data.subscriptions.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                    <td style={{ padding: 6 }}>{s.customerEmail || '—'}</td>
                    <td>{curSym(s.currency)}{s.amount.toLocaleString()}</td>
                    <td style={{ color: String(s.status).toUpperCase() === 'ACTIVE' ? G.green : String(s.status).toUpperCase() === 'TRIAL' ? G.gold : G.muted }}>{s.status}</td>
                    <td>{s.nextChargeDate ? new Date(s.nextChargeDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}

            <h3 style={{ color: G.white, marginTop: 24 }}>Dominio personalizado del checkout</h3>
            <TenantCustomDomain tenantId={data.tenant.id} />
          </>
        )}
      </div>
    </div>
  );
}

// ── Custom domain manager for a single tenant (super-admin only) ───────────
// Operator activates here, then configures the CNAME on the tenant's DNS
// (Shopify Settings → Domains → DNS, or whatever provider they use).
// Apple Pay is auto-registered for the tenant's Stripe + Square gateways.
function TenantCustomDomain({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [domainInput, setDomainInput] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    fetch(`${BACKEND}/api/admin/custom-domain/${tenantId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) setError(j.error || 'Error al cargar');
        else setData(j);
        setLoading(false);
      })
      .catch((e) => { setError(String(e)); setLoading(false); });
  };

  useEffect(() => { load(); }, [tenantId]);

  const callApi = async (method, suffix = '', body = null) => {
    setBusy(true); setError(null);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND}/api/admin/custom-domain/${tenantId}${suffix}`, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || j.detail || `HTTP ${res.status}`);
      return j;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const addDomain = async () => {
    try {
      const j = await callApi('POST', '', { domain: domainInput.trim() });
      setData(j);
      setDomainInput('');
    } catch { /* error already in state */ }
  };

  const remove = async (domain) => {
    if (!window.confirm(`¿Quitar el dominio ${domain}? Esto lo desregistra de Cloudflare y de Apple Pay (Stripe/Square). Las demás tiendas del cliente no se tocan.`)) return;
    try { await callApi('DELETE', '', { domain }); load(); } catch { /* shown */ }
  };

  const refreshAP = async (domain) => {
    try { await callApi('POST', '/refresh-apple-pay', { domain }); load(); } catch { /* shown */ }
  };

  if (loading) return <div style={{ color: G.muted, fontSize: 13 }}>Cargando…</div>;

  // Tolera la forma nueva (data.domains[]) y la antigua (data.domain single),
  // por si en el gap de deploy el backend aún responde el formato viejo.
  const domains = Array.isArray(data?.domains)
    ? data.domains
    : (data?.domain
        ? [{ domain: data.domain, status: data.status, cnameInstructions: data.cnameInstructions, cloudflare: data.cloudflare, applePayResults: data.applePayResults, lastChecked: data.lastChecked }]
        : []);

  const renderDomainCard = (d) => {
    const statusColor = d.status === 'active' ? G.green : d.status === 'broken' ? G.red : G.orange;
    const statusLabel = d.status === 'active' ? 'Activo' : d.status === 'broken' ? 'Roto' : 'Pendiente DNS';
    return (
      <div key={d.domain} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ color: G.white, fontWeight: 700, fontSize: 15 }}>{d.domain}</div>
            <div style={{ fontSize: 11, color: statusColor, textTransform: 'uppercase', fontWeight: 700, marginTop: 4 }}>● {statusLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={load} disabled={busy} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>↻ Re-verificar</button>
            <button onClick={() => refreshAP(d.domain)} disabled={busy} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>Re-registrar Apple Pay</button>
            <button onClick={() => remove(d.domain)} disabled={busy} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}55`, padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>Eliminar</button>
          </div>
        </div>

        {d.cnameInstructions && (
          <div style={{ background: G.black, padding: 12, borderRadius: 4, fontSize: 12, color: G.white, marginBottom: 12, border: `1px solid ${G.border}` }}>
            <div style={{ color: G.muted, marginBottom: 6, fontSize: 11, textTransform: 'uppercase' }}>CNAME a configurar en el DNS del cliente</div>
            <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              <span style={{ color: G.gold }}>{d.cnameInstructions.type}</span>
              &nbsp;&nbsp;<strong>{d.cnameInstructions.name}</strong>
              &nbsp;&nbsp;→&nbsp;&nbsp;<strong>{d.cnameInstructions.target}</strong>
            </div>
          </div>
        )}

        {d.cloudflare && (
          <div style={{ fontSize: 11, color: G.muted, marginBottom: 12 }}>
            Cloudflare: <span style={{ color: d.cloudflare.status === 'active' ? G.green : G.orange }}>{d.cloudflare.status || '—'}</span>
            {d.cloudflare.certificate_authority && <> · CA: {d.cloudflare.certificate_authority}</>}
          </div>
        )}

        {d.applePayResults && d.applePayResults.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: G.muted, marginBottom: 6, textTransform: 'uppercase' }}>Apple Pay por pasarela</div>
            {d.applePayResults.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: r.status === 'ok' ? G.green : r.status === 'error' ? G.red : G.muted, padding: '2px 0' }}>
                {r.status === 'ok' ? '✓' : r.status === 'error' ? '✖' : '·'} <strong>{r.psp}</strong>
                {r.error && <span style={{ color: G.dim, marginLeft: 8 }}>{r.error}</span>}
                {r.reason && <span style={{ color: G.dim, marginLeft: 8 }}>{r.reason}</span>}
                {r.status === 'unsupported' && <span style={{ color: G.dim, marginLeft: 8 }}>(auto-register no implementado — tenant debe registrar manualmente en su panel del PSP)</span>}
              </div>
            ))}
          </div>
        )}

        {d.lastChecked && (
          <div style={{ fontSize: 10, color: G.dim, marginTop: 12 }}>
            Última verificación: {new Date(d.lastChecked).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ color: G.muted, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
        Dominios de checkout de este cliente (una tienda por dominio, ej.
        <code style={{ background: G.black, padding: '2px 6px', borderRadius: 3, margin: '0 4px' }}>checkout.tienda.com</code>).
        Todos usan <strong>las pasarelas que el cliente ya tiene añadidas</strong>. El cliente
        <strong> no toca nada</strong>: tras añadir un dominio aquí, configuras el CNAME
        en el DNS de esa tienda (Shopify → Settings → Domains → DNS, o el proveedor que use).
      </div>

      {domains.length > 0
        ? domains.map(renderDomainCard)
        : <div style={{ color: G.dim, fontSize: 12, marginBottom: 12 }}>Este cliente aún no tiene ningún dominio de checkout.</div>}

      {/* Añadir otro dominio — siempre visible */}
      <div style={{ background: G.card, border: `1px dashed ${G.border}`, borderRadius: 8, padding: 14 }}>
        <div style={{ color: G.muted, fontSize: 11, textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>Añadir dominio (otra tienda)</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="checkout.otra-tienda.com"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && domainInput.trim() && !busy) addDomain(); }}
            style={{ flex: 1, maxWidth: 320, padding: '8px 10px', borderRadius: 4, border: `1px solid ${G.border}`, background: G.black, color: G.white, fontSize: 13 }}
          />
          <button
            onClick={addDomain}
            disabled={busy || !domainInput.trim()}
            style={{ background: G.gold, color: G.black, fontWeight: 700, padding: '8px 18px', border: 0, borderRadius: 4, cursor: busy ? 'wait' : 'pointer', opacity: (busy || !domainInput.trim()) ? 0.6 : 1 }}
          >
            {busy ? 'Añadiendo…' : 'Añadir'}
          </button>
        </div>
      </div>

      {error && <div style={{ color: G.red, fontSize: 12, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

function AdminTenantsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');
  const [drillId, setDrillId] = useState(null);
  // 2026-06-10 — Crear tenant también desde admin.* (antes solo vivía en
  // <Clients> dentro del MerchantApp). Mismo endpoint POST /api/auth/register;
  // sólo replicamos el modal en estilo AdminPanel.
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', plan: 'bronze' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    fetchAdminWithRetry(`${BACKEND}/api/admin/tenants`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(`Error ${e}`));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTenant = async () => {
    if (!form.name || !form.email || !form.password) { window.alert('Completa nombre, email y contraseña'); return; }
    if (form.password.length < 8) { window.alert('La contraseña debe tener al menos 8 caracteres'); return; }
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND}/api/auth/register`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const created = await res.json();
      window.alert(`Cliente creado OK\n\nEmail: ${form.email}\nUserId: ${created.id}\nPlan: ${form.plan}\n\nDale las credenciales por canal privado.`);
      setModal(null);
      setForm({ name: '', email: '', password: '', plan: 'bronze' });
      load();
    } catch (e) {
      window.alert(`Error creando cliente: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  if (err) return <div style={{ padding: 24, color: G.red }}>Error: {err}</div>;
  if (!data) return <div style={{ padding: 24, color: G.muted }}>Cargando tenants…</div>;

  const rows = data.tenants.filter(t => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (t.name || '').toLowerCase().includes(f) || (t.email || '').toLowerCase().includes(f) || (t.plan || '').toLowerCase().includes(f);
  });

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ color: G.white, margin: 0 }}>Tenants <span style={{ color: G.muted, fontSize: 14, fontWeight: 400 }}>· {data.count} en total</span></h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtrar por nombre, email o plan…" style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 6, padding: '6px 12px', color: G.white, minWidth: 240 }} />
          <button onClick={() => setModal('create')} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Nuevo cliente</button>
        </div>
      </div>
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', textTransform: 'uppercase', borderBottom: `1px solid ${G.border}` }}>
              <th style={{ padding: 10 }}>Cliente</th>
              <th>Plan</th>
              <th>Salud</th>
              <th style={{ textAlign: 'right' }}>Hoy</th>
              <th style={{ textAlign: 'right' }}>7d</th>
              <th style={{ textAlign: 'right' }}>30d</th>
              <th style={{ textAlign: 'right' }}>MRR</th>
              <th style={{ textAlign: 'right' }}>Lifetime</th>
              <th style={{ textAlign: 'right' }}>AOV</th>
              <th style={{ textAlign: 'right' }}>LTV</th>
              <th style={{ textAlign: 'center' }}>Conv.</th>
              <th style={{ textAlign: 'center' }}>Subs</th>
              <th style={{ textAlign: 'center' }}>CB%</th>
              <th>Trend 14d</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={15} style={{ padding: 18, color: G.muted, textAlign: 'center' }}>Sin resultados.</td></tr>}
            {rows.map(t => {
              const healthOk = !t.health?.noGateway && !t.health?.noShop && !t.health?.stale && !t.health?.cbHigh;
              const cbColor = (t.rates?.chargeback30d || 0) > 1 ? G.red : G.white;
              return (
                <tr key={t.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: G.muted }}>{t.email}</div>
                  </td>
                  <td><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: G.black, color: G.gold, fontWeight: 700, textTransform: 'uppercase' }}>{t.plan}</span></td>
                  <td>
                    {healthOk ? (
                      <span style={{ color: G.green, fontSize: 14 }} title="Sin alertas">●</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {t.health?.noGateway && <span title="Sin pasarela" style={{ fontSize: 9, color: G.red }}>🚫 sin gw</span>}
                        {t.health?.noShop && <span title="Sin tienda" style={{ fontSize: 9, color: G.orange }}>📦 sin shop</span>}
                        {t.health?.stale && <span title={`Sin pagos ${t.daysSinceLastPayment}d`} style={{ fontSize: 9, color: G.orange }}>💤 {t.daysSinceLastPayment}d</span>}
                        {t.health?.cbHigh && <span title="Chargebacks > 1%" style={{ fontSize: 9, color: G.red }}>⚠ cb</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(t.revenue?.today || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(t.revenue?.last7d || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(t.revenue?.last30d || 0)}</td>
                  <td style={{ textAlign: 'right', color: G.gold, fontWeight: 700 }}>{fmtMoney(t.revenue?.mrr || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(t.revenue?.lifetime || 0)}</td>
                  <td style={{ textAlign: 'right', fontSize: 11, color: G.muted }}>{fmtMoney(t.revenue?.aov || 0)}</td>
                  <td style={{ textAlign: 'right', fontSize: 11, color: G.muted }}>{fmtMoney(t.revenue?.ltv || 0)}</td>
                  <td style={{ textAlign: 'center', fontSize: 11 }}>{t.rates?.conversion30d === null || t.rates?.conversion30d === undefined ? '—' : `${t.rates.conversion30d}%`}</td>
                  <td style={{ textAlign: 'center' }}>{t.counts?.subscriptionsActive || 0}</td>
                  <td style={{ textAlign: 'center', color: cbColor, fontWeight: 600 }}>{fmtPct(t.rates?.chargeback30d || 0)}</td>
                  <td style={{ minWidth: 80 }}>
                    {t.sparkline && <MiniSparkline data={t.sparkline} />}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => setDrillId(t.id)} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>VER →</button>
                      <button
                        title={`Eliminar tenant ${t.email}`}
                        onClick={async () => {
                          const confirmText = window.prompt(`⚠ ELIMINAR TENANT\n\nVas a borrar a ${t.name} (${t.email}).\n\nEsto:\n- Bloquea su acceso inmediatamente\n- Libera su email para re-registro\n- Sus payments/subs/vault quedan archivados (no se purgan)\n\nEscribe el email para confirmar:`);
                          if (confirmText !== t.email) {
                            if (confirmText !== null) window.alert('Email no coincide. Eliminación cancelada.');
                            return;
                          }
                          const token = localStorage.getItem('token');
                          try {
                            const res = await fetch(`${BACKEND}/api/auth/users/${t.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                            if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || `HTTP ${res.status}`); }
                            window.alert(`Tenant ${t.email} eliminado.`);
                            load();
                          } catch (e) {
                            window.alert(`Error: ${e.message}`);
                          }
                        }}
                        style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {drillId && <AdminTenantDrillDown tenantId={drillId} onClose={() => setDrillId(null)} />}

      {modal === 'create' && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
          onClick={() => !creating && setModal(null)}
        >
          <div
            style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 24, width: '100%', maxWidth: 460 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ color: G.white, margin: 0, fontSize: 17 }}>Nuevo cliente</h3>
              <button onClick={() => !creating && setModal(null)} style={{ background: 'none', border: 'none', color: G.muted, cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Nombre *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Juan García" style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, boxSizing: 'border-box', fontSize: 13 }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="juan@empresa.com" style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, boxSizing: 'border-box', fontSize: 13 }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Contraseña * (mín. 8 caracteres)</label>
              <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="········" style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, boxSizing: 'border-box', fontSize: 13 }} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 10, color: G.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Plan *</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))} style={{ width: '100%', background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 12px', color: G.white, boxSizing: 'border-box', fontSize: 13 }}>
                <option value="bronze">Bronce — 4 procesadores, retry básico</option>
                <option value="silver">Plata — 6 procesadores, AI routing, batch</option>
                <option value="gold">Oro — ilimitado, webhooks, vault</option>
              </select>
            </div>

            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 6, padding: 10, marginBottom: 18, fontSize: 11, color: G.muted, lineHeight: 1.6 }}>
              Tras crear: tendrás el <code style={{ color: G.gold }}>userId</code> para configurar el webhook Stripe del tenant.
              Recuerda darle credenciales por canal privado, NO por email cleartext.
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} disabled={creating} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 6, padding: '8px 16px', cursor: creating ? 'not-allowed' : 'pointer', fontSize: 12 }}>Cancelar</button>
              <button onClick={createTenant} disabled={creating} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, cursor: creating ? 'wait' : 'pointer', opacity: creating ? 0.6 : 1, fontSize: 12 }}>{creating ? 'Creando…' : 'Crear cliente'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ADMIN VAULT PANEL — ZeroRiskPay's own per-tenant card vault
// =============================================================================
// Two-pane view: tenant list → card list. Each card is masked by default;
// hitting "Revelar" calls POST /reveal which decrypts server-side and
// stamps the audit log on the record. The CVV column shows "(no CVV)"
// by default because we do NOT store CVV by default (PCI-DSS 3.2).
function AdminVaultPanel() {
  const [tenants, setTenants] = useState(null);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null); // userId of drill-down

  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetchAdminWithRetry(`${BACKEND}/api/admin/vault/tenants`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : Promise.reject(r.status)),
      fetchAdminWithRetry(`${BACKEND}/api/admin/vault/stats`,    { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ])
      .then(([t, s]) => { setTenants(t.tenants || []); setStats(s); })
      .catch(e => setErr(`Error ${e}`));
  }, []);

  if (err) return <div style={{ padding: 24, color: G.red }}>Vault: {err}</div>;
  if (!tenants) return <div style={{ padding: 24, color: G.muted }}>Cargando vault…</div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ color: G.white, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          🔐 Vault propio
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: `${G.red}22`, color: G.red, fontWeight: 700, letterSpacing: 0.5 }}>ADMIN ONLY</span>
        </h2>
        <p style={{ color: G.muted, fontSize: 13, marginTop: 6, maxWidth: 760 }}>
          Copia cifrada <strong>completa</strong> (AES-256-GCM) de las tarjetas que pasan por el orquestador. Se guarda <strong>una vez por tenant</strong> — los datos no se mezclan entre clientes.
          Cada registro lleva en plano solo lo necesario (last4, brand, expiry); el <strong>PAN + CVV</strong> van cifrados con la clave maestra de ZeroRiskPay para tener
          un backup real ante pérdida de datos en MongoDB.
        </p>
      </div>

      {/* Stats strip */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 22 }}>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Total cards</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: G.white }}>{stats.total}</div>
          </div>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Últimos 7d</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: G.white }}>{stats.last7d}</div>
          </div>
          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>Tenants con cards</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: G.white }}>{stats.tenants}</div>
          </div>
          {Object.entries(stats.brands || {}).map(([b, c]) => (
            <div key={b} style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase' }}>{b}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: G.white }}>{c}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tenant list */}
      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', textTransform: 'uppercase', borderBottom: `1px solid ${G.border}` }}>
              <th style={{ padding: 10 }}>Tenant</th>
              <th>Plan</th>
              <th style={{ textAlign: 'right' }}>Tarjetas en vault</th>
              <th>Última card guardada</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: G.muted }}>
                Aún no hay tarjetas guardadas en el vault. Se llenará automáticamente con cada pago exitoso que lleve PAN crudo.
              </td></tr>
            )}
            {tenants.map(t => (
              <tr key={t.userId} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                <td style={{ padding: 10 }}>
                  <div style={{ fontWeight: 600 }}>{t.tenantName}</div>
                  <div style={{ fontSize: 11, color: G.muted }}>{t.tenantEmail}</div>
                </td>
                <td><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: G.bg, color: G.gold, fontWeight: 700, textTransform: 'uppercase' }}>{t.tenantPlan || '—'}</span></td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{t.cards}</td>
                <td style={{ fontSize: 11, color: G.muted }}>{t.lastCreatedAt ? new Date(t.lastCreatedAt).toLocaleString('es-ES') : '—'}</td>
                <td>
                  <button onClick={() => setSelected(t.userId)} style={{ background: G.gold, color: G.black, border: 'none', borderRadius: 4, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    VER TARJETAS →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <AdminVaultDrillDown userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function AdminVaultDrillDown({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [revealed, setRevealed] = useState({}); // { cardId: { pan, cvv } }

  const reload = () => {
    const token = localStorage.getItem('token');
    return fetchAdminWithRetry(`${BACKEND}/api/admin/vault/tenants/${userId}/cards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(`Error ${e}`));
  };
  // reload reads userId from props; safe to re-fire whenever userId flips.
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const reveal = async (cardId) => {
    if (!window.confirm('Esta acción quedará registrada en el audit log. ¿Continuar?')) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${BACKEND}/api/admin/vault/cards/${cardId}/reveal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await r.json();
    if (!r.ok) { alert(body.error || 'Reveal failed'); return; }
    setRevealed(prev => ({ ...prev, [cardId]: { pan: body.pan, cvv: body.cvv } }));
    reload(); // refresh lastRevealedAt
  };

  const removeCard = async (cardId) => {
    if (!window.confirm('¿Eliminar esta tarjeta del vault? El cliente no se ve afectado (sus pagos siguen como están).')) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${BACKEND}/api/admin/vault/cards/${cardId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      setRevealed(prev => { const n = { ...prev }; delete n[cardId]; return n; });
      reload();
    }
  };

  const formatPan = (p) => p ? p.replace(/(.{4})/g, '$1 ').trim() : '';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', justifyContent: 'flex-end', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: G.black, borderLeft: `1px solid ${G.border}`, width: 'min(1000px, 100%)', height: '100%', overflowY: 'auto', padding: 24 }}>
        <button onClick={onClose} style={{ float: 'right', background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}>✕ Cerrar</button>
        {err && <div style={{ color: G.red, marginTop: 30 }}>Error {err}</div>}
        {!data && !err && <div style={{ color: G.muted, marginTop: 30 }}>Cargando…</div>}
        {data && (
          <>
            <h2 style={{ color: G.white, marginTop: 0 }}>
              🔐 {data.tenant.name}
              <span style={{ fontSize: 13, color: G.muted, fontWeight: 400, marginLeft: 10 }}>· {data.tenant.email}</span>
            </h2>
            <div style={{ background: `${G.red}11`, border: `1px solid ${G.red}40`, borderRadius: 8, padding: 12, marginBottom: 18, fontSize: 12, color: G.white }}>
              ⚠️ <strong>Datos sensibles</strong>. Solo revela una tarjeta cuando sea estrictamente necesario. Cada reveal queda en el audit log con tu usuario y timestamp. PCI-DSS exige justificación y minimización de acceso.
            </div>
            <div style={{ marginBottom: 12, color: G.muted, fontSize: 13 }}>{data.count} tarjeta(s) en el vault de este tenant</div>

            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: G.muted, fontSize: 10, textAlign: 'left', textTransform: 'uppercase', borderBottom: `1px solid ${G.border}` }}>
                    <th style={{ padding: 10 }}>Cliente</th>
                    <th>Cardholder</th>
                    <th>Brand</th>
                    <th>Tarjeta</th>
                    <th>CVV</th>
                    <th>Expira</th>
                    <th>Origen</th>
                    <th>Guardada</th>
                    <th>Último reveal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.cards.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: 30, textAlign: 'center', color: G.muted }}>Sin tarjetas guardadas para este tenant.</td></tr>
                  )}
                  {data.cards.map(c => {
                    const rev = revealed[c.id];
                    return (
                      <tr key={c.id} style={{ borderBottom: `1px solid ${G.border}40`, color: G.white }}>
                        <td style={{ padding: 8 }}>{c.customerEmail || '—'}</td>
                        <td>{c.cardholder || '—'}</td>
                        <td>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: G.bg, color: G.gold, fontWeight: 700, textTransform: 'uppercase' }}>{c.cardBrand}</span>
                        </td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                          {rev ? (
                            <span style={{ color: G.green }}>{formatPan(rev.pan)}</span>
                          ) : (
                            <span style={{ color: G.white }}>•••• •••• •••• {c.cardLast4}</span>
                          )}
                        </td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                          {rev ? (
                            <span style={{ color: G.green }}>{rev.cvv || '—'}</span>
                          ) : (
                            <span style={{ color: G.white }}>•••</span>
                          )}
                        </td>
                        <td style={{ fontFamily: 'DM Mono, monospace' }}>{c.cardExpiry || '—'}</td>
                        <td style={{ fontSize: 11, color: G.muted }}>{c.source}</td>
                        <td style={{ fontSize: 11, color: G.muted }}>{c.createdAt ? new Date(c.createdAt).toLocaleString('es-ES') : '—'}</td>
                        <td style={{ fontSize: 11, color: G.muted }}>{c.lastRevealedAt ? new Date(c.lastRevealedAt).toLocaleString('es-ES') : '—'}</td>
                        <td>
                          {!rev ? (
                            <button onClick={() => reveal(c.id)} style={{ background: G.red, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginRight: 4 }}>👁 Revelar</button>
                          ) : (
                            <button onClick={() => setRevealed(prev => { const n = { ...prev }; delete n[c.id]; return n; })} style={{ background: 'transparent', color: G.muted, border: `1px solid ${G.border}`, borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginRight: 4 }}>Ocultar</button>
                          )}
                          <button onClick={() => removeCard(c.id)} style={{ background: 'transparent', color: G.red, border: `1px solid ${G.red}40`, borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AdminPanel({ user, onLogout }) {
  const [tab, setTab] = useState('platform');
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: G.black, color: G.white, fontFamily: 'Syne, system-ui, sans-serif' }}>
      <aside style={{ width: 240, background: G.card, borderRight: `1px solid ${G.border}`, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
            <img src="/logo-icon-dark.png" alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            <div>
              <div style={{ fontWeight: 700 }}>ZeroRiskPay</div>
              <div style={{ fontSize: 10, color: G.gold, letterSpacing: 1.2, textTransform: 'uppercase' }}>Super Admin</div>
            </div>
          </div>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button onClick={() => setTab('platform')} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: tab === 'platform' ? G.gold : 'transparent', color: tab === 'platform' ? G.black : G.white, fontWeight: 600 }}>📊 Plataforma</button>
            <button onClick={() => setTab('tenants')} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: tab === 'tenants' ? G.gold : 'transparent', color: tab === 'tenants' ? G.black : G.white, fontWeight: 600 }}>👥 Tenants</button>
            <button onClick={() => setTab('leads')} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: tab === 'leads' ? G.gold : 'transparent', color: tab === 'leads' ? G.black : G.white, fontWeight: 600 }}>📥 Solicitudes</button>
            <button onClick={() => setTab('vault')} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: tab === 'vault' ? G.gold : 'transparent', color: tab === 'vault' ? G.black : G.white, fontWeight: 600 }}>🔐 Vault</button>
            <a href="https://app.zeroriskpay.com" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, color: G.muted, textDecoration: 'none', fontSize: 13, border: `1px dashed ${G.border}`, textAlign: 'center' }}>↗ Abrir vista cliente</a>
          </nav>
        </div>
        <div style={{ borderTop: `1px solid ${G.border}`, paddingTop: 12, fontSize: 12 }}>
          <div style={{ color: G.muted }}>Conectado como</div>
          <div style={{ fontWeight: 600 }}>{user.name || user.email}</div>
          <button onClick={onLogout} style={{ marginTop: 8, background: 'transparent', border: `1px solid ${G.border}`, color: G.muted, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Cerrar sesión</button>
        </div>
      </aside>
      <main style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'platform' && <AdminPlatformPanel />}
        {tab === 'tenants' && <AdminTenantsPanel />}
        {tab === 'leads' && <Leads showToast={showToast} />}
        {tab === 'vault' && <AdminVaultPanel />}
      </main>
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: toast.type === 'error' ? G.redDim : G.greenDim, color: toast.type === 'error' ? G.red : G.green, border: `1px solid ${toast.type === 'error' ? G.red : G.green}40`, borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 500, zIndex: 10000, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>{toast.msg}</div>
      )}
    </div>
  );
}

function NotAdminPage({ onLogout }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: G.black, color: G.white, fontFamily: 'Syne, sans-serif', padding: 24, textAlign: 'center' }}>
      <h1 style={{ color: G.gold }}>403 — Acceso restringido</h1>
      <p style={{ color: G.muted, maxWidth: 480 }}>Este panel es exclusivo para el equipo de ZeroRiskPay. Si eres un comerciante, vuelve a <a href="https://app.zeroriskpay.com" style={{ color: G.gold }}>app.zeroriskpay.com</a>.</p>
      <button onClick={onLogout} style={{ marginTop: 18, background: G.gold, color: G.black, border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, cursor: 'pointer' }}>Cerrar sesión</button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CLIENTES -- compradores agregados desde el histórico de pagos.
// Una fila por email único, con su gasto total y su historial resumido.
// -----------------------------------------------------------------------------

const CUSTOMER_COLS = [
  { key: 'name',          label: 'Nombre',        sortable: true },
  { key: 'email',         label: 'Email',         sortable: true },
  { key: 'phone',         label: 'Teléfono',      sortable: true },
  { key: 'city',          label: 'Ciudad',        sortable: true },
  { key: 'country',       label: 'País',          sortable: true },
  { key: 'totalSpent',    label: 'Gasto total',   sortable: true, num: true },
  { key: 'paymentsOk',    label: 'Pagos OK',      sortable: true, num: true },
  { key: 'totalPayments', label: 'Pagos totales', sortable: true, num: true },
  { key: 'lastPurchase',  label: 'Última compra', sortable: true },
  { key: 'registered',    label: 'Registrado',    sortable: true },
];

function Customers({ showToast }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtros aplicados (los que van al servidor) vs. borrador del formulario.
  const [applied, setApplied] = useState({ search: '', country: '', complaintsOnly: false });
  const [draft, setDraft] = useState({ search: '', country: '', complaintsOnly: false });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('lastPurchase');
  const [order, setOrder] = useState('desc');

  const [visibleCols, setVisibleCols] = useState(() => new Set(CUSTOMER_COLS.map(c => c.key)));
  const [colsOpen, setColsOpen] = useState(false);
  const [dense, setDense] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort, order };
      if (applied.search) params.search = applied.search;
      if (applied.country) params.country = applied.country;
      if (applied.complaintsOnly) params.complaintsOnly = '1';
      const data = await customersApi.getAll(params);
      setRows(data.customers || []);
      setTotal(data.total || 0);
    } catch (e) {
      showToast('No se pudieron cargar los clientes', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, order, applied, showToast]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(1); setApplied({ ...draft }); };
  const resetFilters = () => {
    const blank = { search: '', country: '', complaintsOnly: false };
    setDraft(blank); setApplied(blank); setPage(1);
  };

  const toggleSort = (key) => {
    if (sort === key) setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
    setPage(1);
  };

  const toggleCol = (key) => setVisibleCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const fmtDay = (iso) => {
    if (!iso) return '--';
    try { return new Date(iso).toLocaleDateString('es-ES', { timeZone: APP_TZ }); } catch { return '--'; }
  };

  const cell = (c, key) => {
    switch (key) {
      case 'totalSpent': return `${curSym('EUR')}${(c.totalSpent || 0).toFixed(2)}`;
      case 'lastPurchase': return fmtDay(c.lastPurchase);
      case 'registered': return fmtDay(c.registered);
      case 'country': return c.country || '--';
      // Un 0 en una columna numérica es un dato ("0 pagos OK"), no un vacío.
      default: return (typeof c[key] === 'number') ? c[key] : (c[key] || '--');
    }
  };

  // Exporta lo que el servidor tiene con los filtros actuales, no solo la
  // página visible — pide el máximo permitido y pagina hasta agotarlo.
  const exportAll = async (kind) => {
    try {
      const params = { limit: 100, sort, order };
      if (applied.search) params.search = applied.search;
      if (applied.country) params.country = applied.country;
      if (applied.complaintsOnly) params.complaintsOnly = '1';
      const all = [];
      for (let p = 1; ; p++) {
        const data = await customersApi.getAll({ ...params, page: p });
        const batch = data.customers || [];
        all.push(...batch);
        if (batch.length < 100 || all.length >= (data.total || 0)) break;
      }
      const cols = CUSTOMER_COLS.filter(c => visibleCols.has(c.key));
      const header = cols.map(c => c.label);
      const body = all.map(c => cols.map(col => {
        if (col.key === 'totalSpent') return (c.totalSpent || 0).toFixed(2);
        if (col.key === 'lastPurchase' || col.key === 'registered') return fmtDay(c[col.key]);
        return c[col.key] ?? '';
      }));
      const stamp = new Date().toISOString().split('T')[0];

      if (kind === 'csv') {
        const csv = [header, ...body]
          .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `clientes_${stamp}.csv`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
        XLSX.writeFile(wb, `clientes_${stamp}.xlsx`);
      }
      showToast(`${all.length} clientes exportados`, 'success');
    } catch (e) {
      showToast('Error al exportar', 'error');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const cols = CUSTOMER_COLS.filter(c => visibleCols.has(c.key));
  const pad = dense ? '7px 12px' : undefined;

  // KPIs de la página visible. El gasto agregado de TODA la base requeriría
  // recorrer todas las páginas, así que se etiqueta como "en esta página".
  const pageSpend = rows.reduce((a, c) => a + (c.totalSpent || 0), 0);
  const pageRepeat = rows.filter(c => (c.paymentsOk || 0) > 1).length;

  return (
    <div>
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Clientes</div>
          <div className="kpi-value">{total.toLocaleString('es-ES')}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>compradores únicos</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Gasto en esta página</div>
          <div className="kpi-value">{curSym('EUR')}{pageSpend.toFixed(2)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>{rows.length} de {total.toLocaleString('es-ES')}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Recurrentes</div>
          <div className="kpi-value">{pageRepeat}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>con más de 1 pago OK</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Ticket medio</div>
          <div className="kpi-value">{curSym('EUR')}{rows.length ? (pageSpend / rows.length).toFixed(2) : '0.00'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>en esta página</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Clientes</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refrescar</button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setColsOpen(o => !o)}>▤ Columnas</button>
              {colsOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 190, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                  {CUSTOMER_COLS.map(c => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px', fontSize: 12, color: G.white, cursor: 'pointer' }}>
                      <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                      {c.label}
                    </label>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(CUSTOMER_COLS.map(c => c.key)))}>Todas</button>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setColsOpen(false)}>Cerrar</button>
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setDense(d => !d)}>{dense ? '↕ Normal' : '↕ Compacta'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('csv')}>↓ CSV</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('xlsx')}>↓ Excel</button>
          </div>
        </div>

        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="filters">
            <input
              className="search"
              placeholder="Buscar por nombre, email o teléfono…"
              value={draft.search}
              onChange={e => setDraft(d => ({ ...d, search: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            />
            <input
              className="search"
              style={{ width: 150 }}
              placeholder="País (ES, DE, FR…)"
              value={draft.country}
              onChange={e => setDraft(d => ({ ...d, country: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: G.dim, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={draft.complaintsOnly}
                onChange={e => setDraft(d => ({ ...d, complaintsOnly: e.target.checked }))}
              />
              Solo con incidencias
            </label>
            <button className="btn btn-gold btn-sm" onClick={applyFilters}>Filtrar</button>
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Resetear</button>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cols.map(c => (
                  <th
                    key={c.key}
                    onClick={() => c.sortable && toggleSort(c.key)}
                    style={{ cursor: c.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', textAlign: c.num ? 'right' : 'left', padding: pad }}
                    title={c.sortable ? 'Ordenar' : undefined}
                  >
                    {c.label}
                    {sort === c.key && <span style={{ color: G.gold, marginLeft: 5 }}>{order === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 36, color: G.muted }}>Cargando…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 36, color: G.muted }}>
                  <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>Todavía no hay clientes</div>
                  <div style={{ fontSize: 12 }}>Los compradores de tus tiendas aparecerán aquí según vayan pagando.</div>
                </td></tr>
              )}
              {!loading && rows.map(c => (
                <tr key={c.email}>
                  {cols.map(col => (
                    <td
                      key={col.key}
                      style={{
                        padding: pad,
                        whiteSpace: 'nowrap',
                        textAlign: col.num ? 'right' : 'left',
                        fontFamily: col.num || col.key === 'phone' ? 'DM Mono, monospace' : undefined,
                        color: col.key === 'name' ? G.white : undefined,
                        fontWeight: col.key === 'name' ? 600 : undefined,
                      }}
                    >
                      {col.key === 'email'
                        ? <span style={{ color: G.dim }}>{c.email}{c.hasComplaint && <span title="Tiene reembolso o disputa" style={{ marginLeft: 6, color: G.orange }}>⚠</span>}</span>
                        : cell(c, col.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${G.border}`, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {total === 0 ? 'Sin registros' : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} de ${total.toLocaleString('es-ES')}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              className="form-input"
              style={{ width: 'auto', padding: '5px 28px 5px 10px', fontSize: 11 }}
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 11, color: G.muted }}>por página</span>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <span style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', minWidth: 62, textAlign: 'center' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ROOT APP -- diseño mejorado
// -----------------------------------------------------------------------------

export default function App() {
  const [user, setUser] = useState(() => { try { const s = localStorage.getItem('user'); return s ? JSON.parse(s) : null; } catch { return null; } });
  const [section, setSection] = useState('dashboard');
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showGS, setShowGS] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [liveVisitors, setLiveVisitors] = useState(0);
  const [, _tzTick] = useState(0);   // bump para repintar fechas cuando carga la zona horaria del tenant
  const showToast = useCallback((msg, type = 'success') => setToast({ msg, type }), []);

  // 2026-10-31 — Vuelta del OAuth de TikTok: TikTok redirige a la raíz con
  // ?auth_code=…&state=…. Solo se acepta si el state es el que guardamos al
  // pulsar "Conectar con TikTok"; luego se abre Beneficios → Anuncios.
  useEffect(() => {
    if (!user) return;
    let expected = null;
    try { expected = sessionStorage.getItem('zrp_tiktok_oauth_state'); } catch {}
    const q = new URLSearchParams(window.location.search);
    // 2026-11-02 — OAuth del MCP Server de TikTok: vuelve con ?code=&state=
    // (o ?error= si el usuario cancela). Se mantiene auth_code por compatibilidad.
    const authCode = q.get('code') || q.get('auth_code');
    const oauthError = q.get('error');
    if ((!authCode && !oauthError) || !expected) return;
    window.history.replaceState({}, document.title, window.location.pathname);
    try { sessionStorage.removeItem('zrp_tiktok_oauth_state'); } catch {}
    if (oauthError) { showToast('TikTok: ' + (q.get('error_description') || oauthError), 'error'); return; }
    const state = q.get('state');
    if (state !== expected) { showToast('TikTok: la respuesta no corresponde a esta conexión; vuelve a pulsar Conectar', 'error'); return; }
    beneficiosApi.tiktokCallback({ code: authCode, state })
      .then(() => {
        try { sessionStorage.setItem('zrp_open_tiktok', '1'); } catch {}
        showToast('TikTok conectado — elige la tienda de cada cuenta', 'success');
        setSection('beneficios');
      })
      .catch(e => showToast('TikTok: ' + (e.response?.data?.error || e.message), 'error'));
  }, [user, showToast]);

  // Live visitor counter — poll every 15s (app-level so it shows in topbar)
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchLive = () => fetch(`${BACKEND}/api/visits/live`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setLiveVisitors(d.total || 0)).catch(() => {});
    fetchLive();
    const iv = setInterval(fetchLive, 15000);
    return () => clearInterval(iv);
  }, [user]);

  // Init display-currency store from the user payload.
  // We use a 1:1 passthrough on purpose — ALL charges on this platform are
  // settled in USD by NMI/TailoredPay regardless of what the shop quotes in
  // EUR (wallet tokens require a byte-for-byte amount match, see CLAUDE.md).
  // So a "59.99€" subscription is really $59.99 on the acquirer side, and
  // the operator's books are kept in USD. Multiplying by an official FX rate
  // would inflate revenue by ~17% artificially. The toggle only flips the
  // currency SYMBOL and CODE, never the number.
  useEffect(() => {
    if (!user) return;
    const pref = user.displayCurrency || 'EUR';
    _initCurrency({ displayCurrency: pref, eurToUsdRate: 1 });
  }, [user]);

  // 2026-06-25 — Cargar la zona horaria del tenant (settings.timezone) y
  // aplicarla a todas las fechas/KPIs del panel (APP_TZ). Si difiere de la que
  // ya teníamos en localStorage (p.ej. login en dispositivo nuevo), forzamos un
  // re-render para que se repinten en su zona.
  useEffect(() => {
    if (!user) return;
    setupApi.getSettings().then(s => {
      const tz = s && s.timezone;
      if (tz && tz !== APP_TZ) { setAppTz(tz); _tzTick(n => n + 1); }
      else if (tz) { setAppTz(tz); }
    }).catch(() => {});
  }, [user]);

  const isAdmin = user?.role === 'admin';
  const userPlan = user?.plan || 'bronze';

  // 2026-06-11 — Onboarding wizard automático desactivado. El onboarding
  // lo hacemos 1:1 con cada cliente usando el PDF ONBOARDING_LIVE, no
  // un wizard genérico que les distraía al login.
  // El componente <GetStarted> se mantiene en el código por si en el
  // futuro queremos disparar el wizard manualmente desde algún botón.

  const goTo = sec => {
    if (!sec) return;
    // Check plan access
    if (!canAccessSection(userPlan, user?.role, sec)) {
      setUpgradeModal({ section: sec, requiredPlan: getRequiredPlan(sec) });
      return;
    }
    setSidebarOpen(false);
    setSection(sec);
    setShowGS(false);
    if (user) localStorage.setItem(`nlp_gs_done_${user.id}`, '1');
  };

  if (!user) return <Login onLogin={setUser} />;

  // 2026-06-11 — Tenant suspendido: el login backend devolvió 423 y el
  // Login frontend nos pasa un user con status='paused'. Render overlay
  // total — sin sidebar, sin dashboard. Cualquier API que intentara
  // llamar de todos modos devolvería 423 por el middleware auth().
  if (user.status === 'paused' && user.role !== 'admin') {
    const doLogout = () => { localStorage.clear(); setUser(null); };
    return <PausedOverlay user={user} onLogout={doLogout} />;
  }

  // 2026-06-07 — Super-admin host routing.
  // Cloudflare Pages serves this single bundle to app/admin/checkout/www/apex.
  // When the visitor is on admin.zeroriskpay.com we mount the cross-tenant
  // AdminPanel instead of the merchant dashboard. Non-admin accounts that
  // somehow land on this host get a 403 page (the auth/adminOnly guard on
  // /api/admin/* would 403 their fetches anyway — this just keeps the UI
  // honest about what URL surface they're allowed to see).
  const _hostIsAdmin = typeof window !== 'undefined'
    && window.location.hostname === 'admin.zeroriskpay.com';
  if (_hostIsAdmin) {
    const doLogout = () => { localStorage.clear(); setUser(null); };
    if (user.role !== 'admin') return <NotAdminPage onLogout={doLogout} />;
    return <AdminPanel user={user} onLogout={doLogout} />;
  }

  // 2026-06-10 — Sidebar items gated per-plan instead of admin-only.
  // Mirrors backend/src/config/plans.js. Bronce sees the baseline,
  // Plata adds smart-retry / AI routing / reconciliation / batch,
  // Oro adds webhooks API + cascades + vault. `adminOnly` is reserved
  // for the SaaS-operator console (Clientes, Leads). Each gated item
  // uses `requireFeature: '<plan-flag>'`; matching flags live in the
  // backend so the UI and the API stay in sync.
  const PLAN_FEATURES = {
    bronze: new Set([
      'subscriptions', 'paymentLinks', 'pixel', 'applePay', 'googlePay',
      'abandonedCart', 'expressCheckout', 'emailMarketing',
    ]),
    silver: new Set([
      'subscriptions', 'paymentLinks', 'pixel', 'applePay', 'googlePay',
      'abandonedCart', 'expressCheckout', 'emailMarketing',
      'smartRetry', 'retryCheckout', 'aiRouting', 'abTestRouting',
      'rotatorRouting', 'ghostRotator', 'reconciliation', 'batchPayments',
    ]),
    gold: new Set([
      'subscriptions', 'paymentLinks', 'pixel', 'applePay', 'googlePay',
      'abandonedCart', 'expressCheckout', 'emailMarketing',
      'smartRetry', 'retryCheckout', 'aiRouting', 'abTestRouting',
      'rotatorRouting', 'ghostRotator', 'reconciliation', 'batchPayments',
      'retryCascade', 'priceCascade', 'configurableRetry',
      'vaultPropio', 'telegramAlerts', 'supportBot', 'emailBot',
      'onePersonalSupport',
      'webhooksApi', 'clubModule', 'customTemplates', 'emailMarketingCustom',
      'customIntegration', 'reconciliation',
    ]),
  };
  const hasFeature = (plan, feature) =>
    !!PLAN_FEATURES[(plan || 'bronze').toLowerCase()]?.has(feature);

  const NAV_ITEMS = [
    // ── Principal — what every merchant lives in every day ──
    { id: 'dashboard',     icon: '◈',        label: 'Dashboard',      group: 'Principal' },
    { id: 'live-funnel',   icon: '\u{1F4E1}', label: 'En Vivo',        group: 'Principal' },
    { id: 'payments',      icon: '◎',        label: 'Pagos',          group: 'Principal' },
    { id: 'subscriptions', icon: '◌',        label: 'Suscripciones',  group: 'Principal' },
    { id: 'tracking',      icon: '\u{1F69A}', label: 'Tracking',       group: 'Principal' },

    // ── Comercial — selling tools the merchant configures ──
    { id: 'payment-links', icon: '🔗',       label: 'Payment Links',  group: 'Comercial' },
    { id: 'customers',     icon: '\u{1F464}', label: 'Clientes',       group: 'Comercial' },
    { id: 'products',      icon: '\u{1F4E6}', label: 'Productos',      group: 'Comercial' },
    { id: 'shops',         icon: '◫',        label: 'Tiendas',        group: 'Comercial' },
    { id: 'pixels',        icon: '◉',        label: 'Pixels',         group: 'Comercial' },
    { id: 'club',          icon: '\u{1F451}', label: 'Club Miembros',  group: 'Comercial', requireFeature: 'clubModule' },
    { id: 'batch',         icon: '⊞',       label: 'Batch Payment',  group: 'Comercial', requireFeature: 'batchPayments' },

    // ── Análisis — what the merchant looks at to take decisions ──
    { id: 'beneficios',    icon: '\u{1F4B0}', label: 'Beneficios',     group: 'Análisis' },
    { id: 'analytics',     icon: '◩',        label: 'Analíticas',     group: 'Análisis' },
    { id: 'chargebacks',   icon: '⚠',       label: 'Chargebacks',    group: 'Análisis' },
    { id: 'sub-stats',     icon: '▤',        label: 'Estadísticas Sub', group: 'Análisis', requireFeature: 'subscriptions' },
    { id: 'retry',         icon: '↺',        label: 'Retry Dashboard',  group: 'Análisis', requireFeature: 'retryCheckout' },
    { id: 'reconciliation',icon: '🔍',       label: 'Reconciliación',   group: 'Análisis', requireFeature: 'reconciliation' },

    // ── Configuración — what the merchant sets up once and forgets ──
    { id: 'gateways',      icon: '⬡',       label: 'Pasarelas',      group: 'Configuración' },
    { id: 'gateway-health',icon: '\u{1FA7A}', label: 'Salud pasarelas', group: 'Configuración' },

    // ── Marketing — mensajería a clientes ──
    { id: 'sms-templates', icon: '\u{1F4AC}', label: 'Plantillas SMS', group: 'Marketing' },
    { id: 'sms-campaigns', icon: '\u{1F4E3}', label: 'Campañas SMS',   group: 'Marketing' },
    { id: 'sms-history',   icon: '\u{1F4DC}', label: 'Historial SMS',  group: 'Marketing' },
    { id: 'checkouts',     icon: '🛒',       label: 'Checkouts',      group: 'Configuración' },
    { id: 'coupons',       icon: '🎟',       label: 'Cupones',        group: 'Configuración' },
    { id: 'bumps',         icon: '🎁',       label: 'Bumps & Upsells', group: 'Configuración' },
    { id: 'templates',     icon: '◻',       label: 'Templates',      group: 'Configuración' },
    { id: 'emails',        icon: '✉',       label: 'Emails',         group: 'Configuración' },
    { id: 'rules',         icon: '🎛',       label: 'Reglas Routing', group: 'Configuración', requireFeature: 'aiRouting' },
    { id: 'webhooks',      icon: '⊕',       label: 'Webhooks',       group: 'Configuración', requireFeature: 'webhooksApi' },
    { id: 'botEmail',      icon: '🤖',       label: 'Bot Email',      group: 'Configuración', requireFeature: 'emailBot' },
    { id: 'settings',      icon: '⚙',       label: 'Settings',       group: 'Configuración' },

    // ── Plan / Upgrade — visible para Bronce y Plata (Oro lo ve también
    //    como "tu plan actual"), oculto para super-admin que no es cliente.
    { id: 'upgrade',       icon: '🚀',       label: userPlan === 'gold' ? 'Tu plan' : 'Mejorar plan', group: 'Principal' },

    // ── Admin — SaaS operator console only ──
    { id: 'clients',       icon: '👥',       label: 'Clientes',       group: 'Admin', adminOnly: true },
    { id: 'leads',         icon: '@',        label: 'Leads',          group: 'Admin', adminOnly: true },
  ];

  // 2026-06-11 — Items con `requireFeature` (de planes superiores) YA NO se
  // ocultan: aparecen en el sidebar con candado 🔒 (ver render más abajo +
  // canAccessSection). Click → UpgradeModal con copy persuasivo por sección.
  // `adminOnly` sigue ocultando los items de la consola SaaS (Clientes /
  // Leads) para que un cliente no los vea.
  const _visibleNav = NAV_ITEMS.filter(i => !i.adminOnly || isAdmin);
  const NAV_GROUPS = ['Principal', 'Comercial', 'Marketing', 'Análisis', 'Configuración', 'Admin'];
  const navByGroup = NAV_GROUPS
    .map(g => ({ group: g, items: _visibleNav.filter(i => i.group === g) }))
    .filter(g => g.items.length > 0);

  // Keep `nav` defined for any downstream code that still expects a flat list.
  const nav = _visibleNav;

  // 2026-06-07 — Restored after Deploy F (68b3376) accidentally amputated
  // this map when it stripped the operator-legacy `dianxiaomi` section.
  // The topbar (page-title + breadcrumb) reads `titles[section]`, so a
  // missing object turns the whole dashboard into a ReferenceError.
  const titles = {
    dashboard:       'Dashboard',
    clients:         'Clientes',
    leads:           'Leads',
    payments:        'Pagos',
    'payment-links': 'Payment Links',
    customers:       'Clientes',
    'live-funnel':   'En Vivo',
    'gateway-health':'Salud de pasarelas',
    'sms-templates': 'Plantillas SMS',
    'sms-campaigns': 'Campañas SMS',
    'sms-history':   'Historial SMS',
    retry:           'Retry Dashboard',
    rules:           'Constructor de Reglas',
    reconciliation:  'Reconciliacion',
    subscriptions:   'Suscripciones',
    'sub-stats':     'Estadisticas Suscripciones',
    analytics:       'Analiticas',
    upgrade:         'Mejorar plan',
    botEmail:        'Bot Email',
    products:        'Productos Mas Vendidos',
    tracking:        'Tracking',
    beneficios:      'Beneficios (P&L)',
    chargebacks:     'Chargebacks / Disputas',
    batch:           'Batch Payment',
    gateways:        'Pasarelas',
    settings:        'Settings',
    webhooks:        'Webhooks',
    templates:       'Templates',
    emails:          'Emails / Deliverability',
    checkouts:       'Checkouts',
    coupons:         'Cupones de descuento',
    bumps:           'Bumps & Upsells',
  };

  return (
    <>
      <style>{css}</style>
      {showGS && <GetStarted onComplete={goTo} onClose={() => { setShowGS(false); if (user) localStorage.setItem(`nlp_gs_done_${user.id}`, '1'); }} />}
      <div className="layout">
        <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-top">
            <div className="logo-wrap">
              {/* 2026-06-07 — icon-only crop on transparent bg so the
                  square 36×36 sidebar slot doesn't smash the wordmark
                  or carry the legacy white card. */}
              <img src="/logo-icon-dark.png" alt="ZeroRiskPay" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'drop-shadow(0 1px 4px rgba(75,159,232,0.25))' }} />
              <div><div className="logo-name">ZeroRiskPay</div><div className="logo-sub">Orchestration Platform</div></div>
            </div>
          </div>
          <nav className="nav">
            {/* 2026-06-07 — Render the role-filtered groups built above
                (Principal / Comercial / Análisis / Configuración /
                optional Admin). One source of truth, no slice indices
                to drift, no leaks of admin-only items to client users. */}
            {navByGroup.map(({ group, items }, idx) => (
              <React.Fragment key={group}>
                <div className="nav-section-label" style={idx > 0 ? { marginTop: 8 } : undefined}>{group}</div>
                {items.map(item => {
                  const locked = !canAccessSection(userPlan, user?.role, item.id);
                  return (
                    <button key={item.id} className={`nav-item ${section === item.id ? 'active' : ''} ${locked ? 'locked' : ''}`} onClick={() => goTo(item.id)}>
                      <span className="nav-icon">{item.icon}</span>
                      <span>{item.label}</span>
                      {locked && <span className="nav-lock">🔒</span>}
                      {!locked && item.badge && <span style={{ marginLeft: 'auto', fontSize: 9, background: G.gold, color: '#000', borderRadius: 4, padding: '1px 5px', fontWeight: 800 }}>{item.badge}</span>}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            {/* 2026-06-11 — Botón "Ver guía de inicio" eliminado: el onboarding
                lo hacemos nosotros 1:1 con cada cliente, no via wizard. */}
          </nav>
          <div className="sidebar-user">
            <div className="user-avatar">{user.name?.[0]?.toUpperCase() || 'A'}</div>
            <div>
              <div className="user-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {user.name || 'Admin'}
                {user.plan && PLAN_TIERS[user.plan] && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700, background: PLAN_TIERS[user.plan].bg, color: PLAN_TIERS[user.plan].color, border: `1px solid ${PLAN_TIERS[user.plan].border}` }}>
                    {PLAN_TIERS[user.plan].emoji} {PLAN_TIERS[user.plan].name.replace('Plan ', '')}
                  </span>
                )}
              </div>
              <div className="user-role">{user.role === 'admin' ? '★ Administrador' : '◆ Usuario'}</div>
            </div>
            <button className="logout-btn" onClick={() => { localStorage.clear(); setUser(null); }} title="Cerrar sesión">⏻</button>
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            <div className="topbar-left">
              <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Abrir menu"><span /><span /><span /></button>
              <div style={{ minWidth: 0 }}>
                <div className="page-title">{titles[section]}</div>
                <div className="breadcrumb">ZeroRiskPay · {titles[section]} · {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: APP_TZ })}</div>
              </div>
            </div>
            <div className="topbar-right">
              <NYClock />
              <div
                className="topbar-badge topbar-visitors"
                style={{
                  color: liveVisitors > 0 ? G.green : G.red,
                  borderColor: liveVisitors > 0 ? G.green + '40' : G.red + '40',
                }}
                title={`${liveVisitors} visitante${liveVisitors !== 1 ? 's' : ''} en línea (checkout en vivo)`}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: liveVisitors > 0 ? G.green : G.red, display: 'inline-block', boxShadow: `0 0 6px ${liveVisitors > 0 ? G.green : G.red}`, animation: liveVisitors > 0 ? 'pulse 1.5s ease infinite' : 'none' }} />
                <span>{liveVisitors} visitante{liveVisitors !== 1 ? 's' : ''} en línea</span>
              </div>
              <div className="topbar-status topbar-badge" title="Estado del sistema">
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: G.green, display: 'inline-block', boxShadow: `0 0 6px ${G.green}` }} />
                <span>Sistema operativo</span>
              </div>
            </div>
          </div>

          <SiteDownBanner onNavigate={goTo} />
          {section === 'dashboard' && <Dashboard showToast={showToast} onNavigate={goTo} isAdmin={isAdmin} />}
          {section === 'upgrade' && <UpgradePage userPlan={userPlan} />}
          {section === 'botEmail' && <BotEmailPage showToast={showToast} />}
          {section === 'clients' && isAdmin && <Clients showToast={showToast} />}
          {section === 'leads' && isAdmin && <Leads showToast={showToast} />}
          {section === 'payments' && <Payments showToast={showToast} />}
          {section === 'payment-links' && <PaymentLinks showToast={showToast} />}
          {section === 'customers' && <Customers showToast={showToast} />}
          {section === 'live-funnel' && <LiveFunnel showToast={showToast} />}
          {section === 'sms-templates' && <SmsTemplates showToast={showToast} />}
          {section === 'sms-campaigns' && <SmsCampaigns showToast={showToast} />}
          {section === 'sms-history' && <SmsHistory showToast={showToast} />}
          {section === 'retry' && <RetryDashboard showToast={showToast} />}
          {section === 'rules' && <RuleBuilder showToast={showToast} />}
          {section === 'reconciliation' && <Reconciliation showToast={showToast} />}
          {section === 'subscriptions' && <Subscriptions showToast={showToast} user={user} />}
          {section === 'sub-stats' && <SubscriptionStats showToast={showToast} />}
          {section === 'analytics' && <Analytics showToast={showToast} />}
          {section === 'products' && <TopProducts showToast={showToast} />}
          {section === 'tracking' && <Tracking showToast={showToast} />}
          {section === 'beneficios' && <Beneficios showToast={showToast} />}
          {section === 'chargebacks' && <Chargebacks showToast={showToast} />}
          {section === 'batch' && <BatchPayment showToast={showToast} />}
          {section === 'gateways' && <GatewaysNew showToast={showToast} onNavigate={goTo} />}
          {section === 'gateway-health' && <GatewayHealth showToast={showToast} />}
          {section === 'checkouts' && <CheckoutsGallery showToast={showToast} />}
          {section === 'coupons' && <CouponsPanel showToast={showToast} />}
          {section === 'bumps' && <BumpsAndUpsellsPanel showToast={showToast} />}
          {section === 'settings' && <Settings showToast={showToast} user={user} />}
          {section === 'shops' && <ShopsWithScript showToast={showToast} />}
          {section === 'webhooks' && <Webhooks showToast={showToast} />}
          {section === 'pixels' && <PixelSettings showToast={showToast} />}
          {section === 'templates' && <EmailTemplates showToast={showToast} />}
          {section === 'emails' && <EmailDeliverability showToast={showToast} isAdmin={isAdmin} />}
          {section === 'club' && <ClubMembership showToast={showToast} />}
        </main>
      </div>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {upgradeModal && <UpgradeModal section={upgradeModal.section} requiredPlan={upgradeModal.requiredPlan} userPlan={userPlan} onClose={() => setUpgradeModal(null)} onNavigate={goTo} />}
    </>
  );
}
// v1.3 — apex landing is now served as static /landing.html
// via the bootstrap redirect in public/index.html. React only
// mounts on the dashboard hosts (app/admin/checkout/*.pages.dev).